/*!
 * Doka Image Editor 7.2.2
 * (c) 2018-2021 PQINA Inc. - All Rights Reserved
 * License: https://pqina.nl/doka/license/
 */
/* eslint-disable */

var createProgressEvent = (loaded, total = 1, lengthComputable = true) => {
    // 0 to 1 doesn't work
    try {
        return new ProgressEvent('progress', {
            loaded: loaded * 100,
            total: total * 100,
            lengthComputable,
        });
    }
    catch (err) {
        // ie11
        return {
            type: 'progress',
            loaded: loaded * 100,
            total: total * 100,
            lengthComputable,
        };
    }
};

var noop = (...args) => { };

const FileReaderDataFormat = {
    ArrayBuffer: 'readAsArrayBuffer',
};
var readFile = (file, onprogress = noop, options = {}) => new Promise((resolve, reject) => {
    const { dataFormat = FileReaderDataFormat.ArrayBuffer } = options;
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.onprogress = onprogress;
    reader[dataFormat](file);
});

const readExifOrientation = (view, offset) => {
    // Missing 0x45786966 Marker? No Exif Header, stop here
    if (view.getUint32((offset += 2), false) !== 0x45786966)
        return -1;
    // First 2bytes defines byte align of TIFF data.
    // If it is 0x4949="I I", it means "Intel" type byte align
    const intelByteAligned = view.getUint16((offset += 6), false) === 0x4949;
    offset += view.getUint32(offset + 4, intelByteAligned);
    const tags = view.getUint16(offset, intelByteAligned);
    offset += 2;
    // find Orientation tag
    for (let i = 0; i < tags; i++) {
        if (view.getUint16(offset + i * 12, intelByteAligned) === 0x0112)
            return view.getUint16(offset + i * 12 + 8, intelByteAligned);
    }
};
const readData = (data) => {
    const view = new DataView(data);
    // Every JPEG file starts from binary value '0xFFD8', so if it's not present, exit here
    if (view.getUint16(0, false) != 0xffd8)
        return null;
    const length = view.byteLength;
    let offset = 2;
    let marker;
    while (offset < length) {
        if (view.getUint16(offset + 2, false) <= 8)
            return -1;
        marker = view.getUint16(offset, false);
        offset += 2;
        // Exif Marker found
        if (marker === 0xffe1)
            return readExifOrientation(view, offset);
        // Invalid marker, exit here
        else if ((marker & 0xff00) !== 0xff00)
            return null;
        // Next!
        else
            offset += view.getUint16(offset, false);
    }
};
var getImageOrientationFromFile = async (file, onprogress = noop) => {
    onprogress(createProgressEvent(0, 1));
    const result = (await readFile(file.slice(0, 256 * 1024)));
    return readData(result) || 1;
};

let result = null;
var isBrowser = () => {
    if (result === null)
        result = typeof window !== 'undefined' && typeof window.document !== 'undefined';
    return result;
};

let result$1 = null;
var canOrientImages = () => new Promise((resolve) => {
    if (result$1 === null) {
        // 2x1 pixel image 90CW rotated with orientation EXIF header
        const testSrc = 'data:image/jpg;base64,/9j/4AAQSkZJRgABAQEASABIAAD/4QA6RXhpZgAATU0AKgAAAAgAAwESAAMAAAABAAYAAAEoAAMAAAABAAIAAAITAAMAAAABAAEAAAAAAAD/2wBDAP//////////////////////////////////////////////////////////////////////////////////////wAALCAABAAIBASIA/8QAJgABAAAAAAAAAAAAAAAAAAAAAxABAAAAAAAAAAAAAAAAAAAAAP/aAAgBAQAAPwBH/9k=';
        let testImage = isBrowser() ? new Image() : {};
        testImage.onload = () => {
            // should correct orientation if is presented in landscape,
            // in which case the browser doesn't autocorrect
            result$1 = testImage.naturalWidth === 1;
            testImage = undefined;
            resolve(result$1);
        };
        testImage.src = testSrc;
        return;
    }
    return resolve(result$1);
});

var canvasToImageData = (canvas) => {
    const imageData = canvas
        .getContext('2d')
        .getImageData(0, 0, canvas.width, canvas.height);
    return imageData;
};

var h = (name, attributes, children = []) => {
    const el = document.createElement(name);
    // @ts-ignore
    const descriptors = Object.getOwnPropertyDescriptors(el.__proto__);
    for (const key in attributes) {
        if (key === 'style') {
            el.style.cssText = attributes[key];
        }
        else if (descriptors[key] && descriptors[key].set) {
            el[key] = attributes[key];
        }
        else {
            el.setAttribute(key, attributes[key]);
        }
    }
    children.forEach((child) => el.appendChild(child));
    return el;
};

const MATRICES = {
    1: () => [1, 0, 0, 1, 0, 0],
    2: (width) => [-1, 0, 0, 1, width, 0],
    3: (width, height) => [-1, 0, 0, -1, width, height],
    4: (width, height) => [1, 0, 0, -1, 0, height],
    5: () => [0, 1, 1, 0, 0, 0],
    6: (width, height) => [0, 1, -1, 0, height, 0],
    7: (width, height) => [0, -1, -1, 0, height, width],
    8: (width) => [0, -1, 1, 0, 0, width],
};
var getImageOrientationMatrix = (width, height, orientation = -1) => {
    if (orientation === -1)
        orientation = 1;
    return MATRICES[orientation](width, height);
};

var releaseCanvas = (canvas) => {
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    ctx && ctx.clearRect(0, 0, 1, 1);
};

var isImageData = (obj) => 'data' in obj;

var imageDataToCanvas = async (imageData, orientation = 1) => {
    const [width, height] = (await canOrientImages()) || orientation < 5
        ? [imageData.width, imageData.height]
        : [imageData.height, imageData.width];
    const canvas = h('canvas', { width, height });
    const ctx = canvas.getContext('2d');
    // transform image data ojects into in memory canvas elements so we can transform them (putImageData isn't affect by transforms)
    if (isImageData(imageData) && !(await canOrientImages()) && orientation > 1) {
        const inMemoryCanvas = h('canvas', imageData);
        const ctx = inMemoryCanvas.getContext('2d');
        ctx.putImageData(imageData, 0, 0);
        imageData = inMemoryCanvas;
    }
    // get base transformation matrix
    if (!(await canOrientImages()) && orientation > 1) {
        ctx.transform.apply(ctx, getImageOrientationMatrix(imageData.width, imageData.height, orientation));
    }
    // can't test for instanceof ImageBitmap as Safari doesn't support it
    // if still imageData object by this point, we'll use put
    if (isImageData(imageData)) {
        ctx.putImageData(imageData, 0, 0);
    }
    else {
        ctx.drawImage(imageData, 0, 0);
    }
    // if image data is of type canvas, clean it up
    if (imageData instanceof HTMLCanvasElement)
        releaseCanvas(imageData);
    return canvas;
};

var orientImageData = async (imageData, orientation = 1) => {
    if (orientation === 1)
        return imageData;
    // correct image data for when the browser does not correctly read exif orientation headers
    if (!(await canOrientImages()))
        return canvasToImageData(await imageDataToCanvas(imageData, orientation));
    return imageData;
};

const imageToCanvas = (image, canvasMemoryLimit) => {
    // if these are 0 it's possible that we're trying to convert an SVG that doesn't have width or height attributes
    // https://bugzilla.mozilla.org/show_bug.cgi?id=1328124
    let canvasWidth = image.naturalWidth;
    let canvasHeight = image.naturalHeight;
    // determine if requires more memory than limit, if so limit target size
    const requiredCanvasMemory = canvasWidth * canvasHeight;
    if (canvasMemoryLimit && requiredCanvasMemory > canvasMemoryLimit) {
        const canvasScalar = Math.sqrt(canvasMemoryLimit) / Math.sqrt(requiredCanvasMemory);
        canvasWidth = Math.floor(canvasWidth * canvasScalar);
        canvasHeight = Math.floor(canvasHeight * canvasScalar);
    }
    // create new canvas element
    const canvas = h('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, 0, 0, canvasWidth, canvasHeight);
    return canvas;
};

// turns image into canvas only after it's fully loaded
var imageToCanvasSafe = (image, canvasMemoryLimit) => new Promise((resolve, reject) => {
    const ready = () => resolve(imageToCanvas(image, canvasMemoryLimit));
    if (image.complete && image.width) {
        // need to test for image.width, on ie11 it will be 0 for object urls
        ready();
    }
    else {
        image.onload = ready;
        image.onerror = reject;
    }
});

var blobToCanvas = async (imageBlob, canvasMemoryLimit) => {
    const imageElement = h('img', { src: URL.createObjectURL(imageBlob) });
    const canvas = await imageToCanvasSafe(imageElement, canvasMemoryLimit);
    URL.revokeObjectURL(imageElement.src);
    return canvas;
};

var blobToImageData = async (imageBlob, canvasMemoryLimit) => {
    const canvas = await blobToCanvas(imageBlob, canvasMemoryLimit);
    const imageData = canvasToImageData(canvas);
    releaseCanvas(canvas);
    return imageData;
};

var canvasToBlob = (canvas, mimeType = undefined, quality = undefined) => new Promise((resolve, reject) => {
    try {
        canvas.toBlob((blob) => {
            resolve(blob);
        }, mimeType, quality);
    }
    catch (err) {
        reject(err);
    }
});

var imageDataToBlob = async (imageData, mimeType, quality) => {
    try {
        const canvas = await imageDataToCanvas(imageData);
        const blob = await canvasToBlob(canvas, mimeType, quality);
        releaseCanvas(canvas);
        return blob;
    }
    catch (err) {
        throw err;
    }
};

var blobReadSection = async (blob, slice = [0, blob.size], onprogress) => (await readFile(blob.slice(...slice), onprogress));

var blobWriteSection = (blob, section, slice = [0, blob.size]) => {
    if (!section)
        return blob;
    return new Blob([section, blob.slice(...slice)], { type: blob.type });
};

var arrayBufferWriteExifOrientationTag = (data, value = 1) => {
    if (!data)
        return false;
    const view = new DataView(data);
    // Every JPEG file starts from binary value '0xFFD8', so if it's not present, exit here
    if (view.getUint16(0) !== 0xffd8)
        return false;
    let offset = 2; // Start at 2 as we skipped two bytes (FFD8)
    let marker;
    let markerLength;
    let orientationCorrected = false;
    while (offset < view.byteLength) {
        marker = view.getUint16(offset, false);
        markerLength = view.getUint16(offset + 2, false) + 2;
        // Test if is APP and COM markers
        const isData = (marker >= 0xffe0 && marker <= 0xffef) || marker === 0xfffe;
        if (!isData)
            break;
        if (!orientationCorrected)
            orientationCorrected = writeExifOrientation(view, offset, value);
        if (offset + markerLength > view.byteLength)
            break;
        offset += markerLength;
    }
    return orientationCorrected;
};
const writeExifOrientation = (view, offset, orientation) => {
    // Missing 0x45786966 Marker? No Exif Header, stop here
    if (view.getUint32(offset + 4, false) !== 0x45786966)
        return;
    // next byte!
    offset += 4;
    // First 2bytes defines byte align of TIFF data.
    // If it is 0x4949="I I", it means "Intel" type byte align
    const intelByteAligned = view.getUint16((offset += 6), false) === 0x4949;
    offset += view.getUint32(offset + 4, intelByteAligned);
    const tags = view.getUint16(offset, intelByteAligned);
    offset += 2;
    // find Orientation tag and overwrite with orientation value
    for (let i = 0; i < tags; i++) {
        if (view.getUint16(offset + i * 12, intelByteAligned) === 0x0112) {
            view.setUint16(offset + i * 12 + 8, orientation, intelByteAligned);
            return true;
        }
    }
    return false;
};

var getExtensionFromMimeType = (mimeType) => mimeType.match(/\/([a-z]+)/)[1];

var getFilenameWithoutExtension = (name) => name.substr(0, name.lastIndexOf('.')) || name;

var getExtensionFromFilename = (filename) => filename.split('.').pop();

const ImageExtensionsRegex = /avif|bmp|gif|jpg|jpeg|jpe|jif|jfif|png|svg|tiff|webp/;
/*
Support image mime types
- image/webp
- image/gif
- image/avif
- image/jpeg
- image/png
- image/bmp
- image/svg+xml
*/
var getMimeTypeFromExtension = (ext) => {
    // empty string returned if extension not found
    if (!ImageExtensionsRegex.test(ext))
        return '';
    // return MimeType for this extension
    return 'image/' + (/jfif|jif|jpe|jpg/.test(ext) ? 'jpeg' : ext === 'svg' ? 'svg+xml' : ext);
};

var getMimeTypeFromFilename = (name) => getMimeTypeFromExtension(getExtensionFromFilename(name).toLowerCase());

var matchFilenameToMimeType = (filename, mimeType) => {
    // get the mime type that matches this extension
    const fileMimeType = getMimeTypeFromFilename(filename);
    // test if type already matches current mime type, no need to change name
    if (fileMimeType === mimeType)
        return filename;
    // get the extension for this mimetype (gets all characters after the "image/" part)
    const targetMimeTypeExtension = getExtensionFromMimeType(mimeType);
    return `${getFilenameWithoutExtension(filename)}.${targetMimeTypeExtension}`;
};

var blobToFile = (blob, filename, mimetype) => {
    const lastModified = new Date().getTime();
    const blobHasMimeType = blob.type.length && blob.type !== 'null';
    const blobMimeType = blobHasMimeType ? blob.type : mimetype;
    const name = matchFilenameToMimeType(filename, blobMimeType);
    try {
        return new File([blob], name, {
            lastModified,
            type: blobHasMimeType ? blob.type : blobMimeType,
        });
    }
    catch (err) {
        const file = blobHasMimeType ? blob.slice() : blob.slice(0, blob.size, blobMimeType);
        file.lastModified = lastModified;
        file.name = name;
        return file;
    }
};

var getAspectRatio = (w, h) => w / h;

var passthrough = (v) => (v);

//
// generic
//
const scale = (value, scalar, pivot) => pivot + (value - pivot) * scalar;
const ellipseCreateFromRect = (rect) => ({
    x: rect.x + rect.width * 0.5,
    y: rect.y + rect.height * 0.5,
    rx: rect.width * 0.5,
    ry: rect.height * 0.5,
});
//
// vector
//
const vectorCreateEmpty = () => vectorCreate(0, 0);
const vectorCreate = (x, y) => ({ x, y });
const vectorCreateFromSize = (size) => vectorCreate(size.width, size.height);
const vectorCreateFromPointerEvent = (e) => vectorCreate(e.pageX, e.pageY);
const vectorCreateFromPointerEventOffset = (e) => vectorCreate(e.offsetX, e.offsetY);
const vectorClone = (v) => vectorCreate(v.x, v.y);
const vectorInvert = (v) => {
    v.x = -v.x;
    v.y = -v.y;
    return v;
};
const vectorRotate = (v, radians, pivot = vectorCreateEmpty()) => {
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const tx = v.x - pivot.x;
    const ty = v.y - pivot.y;
    v.x = pivot.x + cos * tx - sin * ty;
    v.y = pivot.y + sin * tx + cos * ty;
    return v;
};
const vectorLength = (v) => Math.sqrt(v.x * v.x + v.y * v.y);
const vectorNormalize = (v) => {
    const length = Math.sqrt(v.x * v.x + v.y * v.y);
    if (length === 0)
        return vectorCreateEmpty();
    v.x /= length;
    v.y /= length;
    return v;
};
const vectorAngle = (v) => Math.atan2(v.y, v.x);
const vectorAngleBetween = (a, b) => Math.atan2(b.y - a.y, b.x - a.x);
const vectorApply = (v, fn) => {
    v.x = fn(v.x);
    v.y = fn(v.y);
    return v;
};
const vectorAdd = (a, b) => {
    a.x += b.x;
    a.y += b.y;
    return a;
};
const vectorSubtract = (a, b) => {
    a.x -= b.x;
    a.y -= b.y;
    return a;
};
const vectorMultiply = (v, f) => {
    v.x *= f;
    v.y *= f;
    return v;
};
const vectorDot = (a, b) => a.x * b.x + a.y * b.y;
const vectorDistanceSquared = (a, b = vectorCreateEmpty()) => {
    const x = a.x - b.x;
    const y = a.y - b.y;
    return x * x + y * y;
};
const vectorDistance = (a, b = vectorCreateEmpty()) => Math.sqrt(vectorDistanceSquared(a, b));
const vectorCenter = (v) => {
    let x = 0;
    let y = 0;
    v.forEach((v) => {
        x += v.x;
        y += v.y;
    });
    return vectorCreate(x / v.length, y / v.length);
};
const vectorsFlip = (points, flipX, flipY, cx, cy) => {
    points.forEach((point) => {
        point.x = flipX ? cx - (point.x - cx) : point.x;
        point.y = flipY ? cy - (point.y - cy) : point.y;
    });
    return points;
};
const vectorsRotate = (points, angle, cx, cy) => {
    const s = Math.sin(angle);
    const c = Math.cos(angle);
    points.forEach((p) => {
        p.x -= cx;
        p.y -= cy;
        const rx = p.x * c - p.y * s;
        const ry = p.x * s + p.y * c;
        p.x = cx + rx;
        p.y = cy + ry;
    });
    return points;
};
//
// size
//
const toSize = (width, height) => ({ width, height });
const sizeClone = (size) => toSize(size.width, size.height);
const sizeCreateFromAny = (obj) => toSize(obj.width, obj.height);
const sizeCreateFromRect = (r) => toSize(r.width, r.height);
const sizeCreateFromArray = (a) => toSize(a[0], a[1]);
const sizeCreate = (width, height) => toSize(width, height);
const sizeEqual = (a, b, format = passthrough) => format(a.width) === format(b.width) && format(a.height) === format(b.height);
const sizeScale = (size, scalar) => {
    size.width *= scalar;
    size.height *= scalar;
    return size;
};
const sizeCenter = (size) => vectorCreate(size.width * 0.5, size.height * 0.5);
const sizeRotate = (size, radians) => {
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const w = cos * size.width + sin * size.height;
    const h = sin * size.width + cos * size.height;
    size.width = w;
    size.height = h;
    return size;
};
const sizeContains = (a, b) => a.width >= b.width && a.height >= b.height;
const sizeApply = (size, fn) => {
    size.width = fn(size.width);
    size.height = fn(size.height);
    return size;
};
const sizeHypotenuse = (size) => Math.sqrt(size.width * size.width + size.height * size.height);
const sizeMin = (a, b) => sizeCreate(Math.min(a.width, b.width), Math.min(a.height, b.height));
//
// line
//
const lineCreate = (start, end) => ({ start, end });
const lineClone = (line) => lineCreate(vectorClone(line.start), vectorClone(line.end));
const lineExtend = (line, amount) => {
    const v = vectorCreate(line.start.x - line.end.x, line.start.y - line.end.y);
    const vn = vectorNormalize(v);
    const vm = vectorMultiply(vn, amount);
    line.start.x += vm.x;
    line.start.y += vm.y;
    line.end.x -= vm.x;
    line.end.y -= vm.y;
    return line;
};
const lineMultiply = (line, amount) => {
    const v = vectorCreate(line.start.x - line.end.x, line.start.y - line.end.y);
    const vn = vectorNormalize(v);
    const vm = vectorMultiply(vn, amount);
    line.end.x += vm.x;
    line.end.y += vm.y;
    return line;
};
//
// rect
//
const CornerSigns = [
    vectorCreate(-1, -1),
    vectorCreate(-1, 1),
    vectorCreate(1, 1),
    vectorCreate(1, -1),
];
const toRect = (x, y, width, height) => ({
    x,
    y,
    width,
    height,
});
const rectClone = (rect) => toRect(rect.x, rect.y, rect.width, rect.height);
const rectCreateEmpty = () => toRect(0, 0, 0, 0);
const rectCreateFromDimensions = (width, height) => toRect(0, 0, width, height);
const rectCreateFromSize = (size) => toRect(0, 0, size.width, size.height);
const rectCreateFromAny = (obj) => toRect(obj.x || 0, obj.y || 0, obj.width || 0, obj.height || 0);
const rectCreateFromPoints = (...args) => {
    const pts = Array.isArray(args[0]) ? args[0] : args;
    let xMin = pts[0].x;
    let xMax = pts[0].x;
    let yMin = pts[0].y;
    let yMax = pts[0].y;
    pts.forEach((point) => {
        xMin = Math.min(xMin, point.x);
        xMax = Math.max(xMax, point.x);
        yMin = Math.min(yMin, point.y);
        yMax = Math.max(yMax, point.y);
    });
    return toRect(xMin, yMin, xMax - xMin, yMax - yMin);
};
const rectCreateFromEllipse = (ellipse) => rectCreate(ellipse.x - ellipse.rx, ellipse.y - ellipse.ry, ellipse.rx * 2, ellipse.ry * 2);
const rectCreateWithCenter = (center, size) => toRect(center.x - size.width * 0.5, center.y - size.height * 0.5, size.width, size.height);
const rectCreate = (x, y, width, height) => toRect(x, y, width, height);
const rectCenter = (rect) => vectorCreate(rect.x + rect.width * 0.5, rect.y + rect.height * 0.5);
const rectTranslate = (rect, t) => {
    rect.x += t.x;
    rect.y += t.y;
    return rect;
};
const rectScale = (rect, scalar, pivot) => {
    pivot = pivot || rectCenter(rect);
    rect.x = scalar * (rect.x - pivot.x) + pivot.x;
    rect.y = scalar * (rect.y - pivot.y) + pivot.y;
    rect.width = scalar * rect.width;
    rect.height = scalar * rect.height;
    return rect;
};
const rectMultiply = (rect, factor) => {
    rect.x *= factor;
    rect.y *= factor;
    rect.width *= factor;
    rect.height *= factor;
    return rect;
};
const rectDivide = (rect, factor) => {
    rect.x /= factor;
    rect.y /= factor;
    rect.width /= factor;
    rect.height /= factor;
    return rect;
};
const rectSubtract = (a, b) => {
    a.x -= b.x;
    a.y -= b.y;
    a.width -= b.width;
    a.height -= b.height;
    return a;
};
const rectAdd = (a, b) => {
    a.x += b.x;
    a.y += b.y;
    a.width += b.width;
    a.height += b.height;
    return a;
};
const rectEqual = (a, b, format = passthrough) => format(a.x) === format(b.x) &&
    format(a.y) === format(b.y) &&
    format(a.width) === format(b.width) &&
    format(a.height) === format(b.height);
const rectAspectRatio = (rect) => getAspectRatio(rect.width, rect.height);
const rectUpdate = (rect, x, y, width, height) => {
    rect.x = x;
    rect.y = y;
    rect.width = width;
    rect.height = height;
    return rect;
};
const rectUpdateWithRect = (a, b) => {
    a.x = b.x;
    a.y = b.y;
    a.width = b.width;
    a.height = b.height;
    return a;
};
const rectRotate = (rect, radians, pivot) => {
    if (!pivot)
        pivot = rectCenter(rect);
    return rectGetCorners(rect).map((vertex) => vectorRotate(vertex, radians, pivot));
};
const rectCenterRect = (a, b) => toRect(a.width * 0.5 - b.width * 0.5, a.height * 0.5 - b.height * 0.5, b.width, b.height);
const rectContainsPoint = (rect, point) => {
    if (point.x < rect.x)
        return false;
    if (point.y < rect.y)
        return false;
    if (point.x > rect.x + rect.width)
        return false;
    if (point.y > rect.y + rect.height)
        return false;
    return true;
};
const rectCoverRect = (rect, aspectRatio, offset = vectorCreateEmpty()) => {
    if (rect.width === 0 || rect.height === 0)
        return rectCreateEmpty();
    const inputAspectRatio = rectAspectRatio(rect);
    if (!aspectRatio)
        aspectRatio = inputAspectRatio;
    let width = rect.width;
    let height = rect.height;
    if (aspectRatio > inputAspectRatio) {
        // height remains the same, width is expanded
        width = height * aspectRatio;
    }
    else {
        // width remains the same, height is expanded
        height = width / aspectRatio;
    }
    return toRect(offset.x + (rect.width - width) * 0.5, offset.y + (rect.height - height) * 0.5, width, height);
};
const rectContainRect = (rect, aspectRatio = rectAspectRatio(rect), offset = vectorCreateEmpty()) => {
    if (rect.width === 0 || rect.height === 0)
        return rectCreateEmpty();
    let width = rect.width;
    let height = width / aspectRatio;
    if (height > rect.height) {
        height = rect.height;
        width = height * aspectRatio;
    }
    return toRect(offset.x + (rect.width - width) * 0.5, offset.y + (rect.height - height) * 0.5, width, height);
};
const rectToBounds = (rect) => [
    Math.min(rect.y, rect.y + rect.height),
    Math.max(rect.x, rect.x + rect.width),
    Math.max(rect.y, rect.y + rect.height),
    Math.min(rect.x, rect.x + rect.width),
];
const rectGetCorners = (rect) => [
    vectorCreate(rect.x, rect.y),
    vectorCreate(rect.x + rect.width, rect.y),
    vectorCreate(rect.x + rect.width, rect.y + rect.height),
    vectorCreate(rect.x, rect.y + rect.height),
];
const rectApply = (rect, fn) => {
    if (!rect)
        return;
    rect.x = fn(rect.x);
    rect.y = fn(rect.y);
    rect.width = fn(rect.width);
    rect.height = fn(rect.height);
    return rect;
};
const rectApplyPerspective = (rect, perspective, pivot = rectCenter(rect)) => rectGetCorners(rect).map((corner, index) => {
    const sign = CornerSigns[index];
    return vectorCreate(scale(corner.x, 1.0 + sign.x * perspective.x, pivot.x), scale(corner.y, 1.0 + sign.y * perspective.y, pivot.y));
});
const rectNormalizeOffset = (rect) => {
    rect.x = 0;
    rect.y = 0;
    return rect;
};
// https://stackoverflow.com/questions/9692448/how-can-you-find-the-centroid-of-a-concave-irregular-polygon-in-javascript
const quadCentroid = (points) => {
    const pts = points.map(vectorClone);
    const first = pts[0];
    const last = pts[pts.length - 1];
    if (first.x != last.x || first.y != last.y) {
        pts.push(first);
    }
    let twicearea = 0;
    let x = 0;
    let y = 0;
    let nPts = pts.length;
    let p1;
    let p2;
    let f;
    for (let i = 0, j = nPts - 1; i < nPts; j = i++) {
        p1 = pts[i];
        p2 = pts[j];
        f = (p1.y - first.y) * (p2.x - first.x) - (p2.y - first.y) * (p1.x - first.x);
        twicearea += f;
        x += (p1.x + p2.x - 2 * first.x) * f;
        y += (p1.y + p2.y - 2 * first.y) * f;
    }
    f = twicearea * 3;
    return vectorCreate(x / f + first.x, y / f + first.y);
};
const lineLineIntersection = (a, b) => _lineLineIntersection(a.start, a.end, b.start, b.end);
const _lineLineIntersection = (a, b, c, d) => {
    const uA = ((d.x - c.x) * (a.y - c.y) - (d.y - c.y) * (a.x - c.x)) /
        ((d.y - c.y) * (b.x - a.x) - (d.x - c.x) * (b.y - a.y));
    const uB = ((b.x - a.x) * (a.y - c.y) - (b.y - a.y) * (a.x - c.x)) /
        ((d.y - c.y) * (b.x - a.x) - (d.x - c.x) * (b.y - a.y));
    if (uA >= 0 && uA <= 1 && uB >= 0 && uB <= 1) {
        return vectorCreate(a.x + uA * (b.x - a.x), a.y + uA * (b.y - a.y));
    }
    return undefined;
};
const linePointsIntersection = (line, points) => {
    const vertices = [...points, points[0]];
    const l = vertices.length;
    const intersections = [];
    for (let i = 0; i < l - 1; i++) {
        const intersection = _lineLineIntersection(line.start, line.end, vectorCreate(vertices[i].x, vertices[i].y), vectorCreate(vertices[i + 1].x, vertices[i + 1].y));
        if (!intersection)
            continue;
        intersections.push(intersection);
    }
    return intersections.length ? intersections : undefined;
};
// First translate the polygon so that `point` is the origin. Then, for each
// edge, get the angle between two vectors: 1) the edge vector and 2) the
// vector of the first vertex of the edge. If all of the angles are the same
// sign (which is negative since they will be counter-clockwise) then the
// point is inside the polygon; otherwise, the point is outside.
const polyPointIntersectionConvex = (point, vertices) => {
    let i, a, b, ax, ay, bx, by, edgex, edgey, x;
    const l = vertices.length;
    for (i = 0; i < l; i++) {
        a = vertices[i];
        b = vertices[i + 1 > l - 1 ? 0 : i + 1];
        ax = a.x - point.x;
        ay = a.y - point.y;
        bx = b.x - point.x;
        by = b.y - point.y;
        edgex = ax - bx;
        edgey = ay - by;
        x = edgex * ay - edgey * ax;
        // 0 is ON the edge, but we check for -0.00001 to fix floating point errors
        if (x < -0.00001)
            return false;
    }
    return true;
};
const quadLines = (vertices) => {
    const arr = [];
    for (let i = 0; i < vertices.length; i++) {
        let next = i + 1;
        if (next === vertices.length)
            next = 0;
        arr.push(lineCreate(vectorClone(vertices[i]), vectorClone(vertices[next])));
    }
    return arr;
};

var getImageTransformedRect = (imageSize, imageRotation) => {
    const imageRect = rectCreateFromSize(imageSize);
    const imageCenter = rectCenter(imageRect);
    const imageTransformedVertices = rectRotate(imageRect, imageRotation, imageCenter);
    return rectNormalizeOffset(rectCreateFromPoints(imageTransformedVertices));
};

var isElement = (v, name) => v instanceof HTMLElement && (name ? new RegExp(`^${name}$`, 'i').test(v.nodeName) : true);

var isFile = (v) => v instanceof File;

var isString = (v) => typeof v === 'string';

var canvasToFile = async (canvas, mimeType, quality) => {
    const blob = await canvasToBlob(canvas, mimeType, quality);
    return blobToFile(blob, 'canvas');
};

var getFilenameFromURL = (url) => url
    .split('/')
    .pop()
    .split(/\?|\#/)
    .shift();

let isSafari = null;
var isSafari$1 = () => {
    if (isSafari === null)
        isSafari = isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    return isSafari;
};

var getImageElementSize = (imageElement) => new Promise((resolve, reject) => {
    let shouldAutoRemove = false;
    // test if image is attached to DOM, if not attached, attach so measurement is correct on Safari
    if (!imageElement.parentNode && isSafari$1()) {
        shouldAutoRemove = true;
        // has width 0 and height 0 to prevent rendering very big SVGs (without width and height) that will for one frame overflow the window and show a scrollbar
        imageElement.style.cssText = `position:absolute;visibility:hidden;pointer-events:none;left:0;top:0;width:0;height:0;`;
        document.body.appendChild(imageElement);
    }
    // start testing size
    const measure = () => {
        const width = imageElement.naturalWidth;
        const height = imageElement.naturalHeight;
        const hasSize = width && height;
        if (!hasSize)
            return;
        // clean up image if was attached for measuring
        if (shouldAutoRemove)
            imageElement.parentNode.removeChild(imageElement);
        clearInterval(intervalId);
        resolve({ width, height });
    };
    imageElement.onerror = (err) => {
        clearInterval(intervalId);
        reject(err);
    };
    const intervalId = setInterval(measure, 1);
    measure();
});

var getImageSize = async (image) => {
    // the image element we'll use to load the image
    let imageElement = image;
    // if is not an image element, it must be a valid image source
    if (!imageElement.src) {
        imageElement = new Image();
        imageElement.src = isString(image) ? image : URL.createObjectURL(image);
    }
    let size;
    try {
        size = await getImageElementSize(imageElement);
    }
    catch (err) {
        throw err;
    }
    finally {
        isFile(image) && URL.revokeObjectURL(imageElement.src);
    }
    return size;
};

const awaitComplete = (image) => new Promise((resolve, reject) => {
    if (image.complete)
        return resolve(image);
    image.onload = () => resolve(image);
    image.onerror = reject;
});
var imageToFile = async (imageElement) => {
    try {
        const size = await getImageSize(imageElement);
        const image = await awaitComplete(imageElement);
        const canvas = document.createElement('canvas');
        canvas.width = size.width;
        canvas.height = size.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, 0, 0);
        const blob = await canvasToBlob(canvas);
        return blobToFile(blob, getFilenameFromURL(image.src));
    }
    catch (err) {
        throw err;
    }
};

var isDataURI = (str) => /^data:/.test(str);

var isUserAgent = (test) => (isBrowser() ? RegExp(test).test(window.navigator.userAgent) : undefined);

let result$2 = null;
var isInternetExplorer = () => {
    if (result$2 === null)
        result$2 = isUserAgent(/MSIE|Trident/);
    return result$2;
};

// IE11 does not support getting blobs with fetch, so we use this fallback method
const dataURIToBlob = (dataURI) => {
    const parts = dataURI.split(',');
    const type = parts[0].substr(4);
    const byteString = atob(parts[1]);
    const l = byteString.length;
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (var i = 0; i < l; i++) {
        ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: type });
};
var dataURIToFile = async (dataURI, filename = 'data-uri', onprogress = noop) => {
    // basic loader, no size info
    onprogress(createProgressEvent(0, 1));
    let blob;
    if (isInternetExplorer()) {
        blob = dataURIToBlob(dataURI);
        onprogress(createProgressEvent(0.5, 1));
    }
    else {
        const res = await fetch(dataURI);
        onprogress(createProgressEvent(0.33, 1));
        blob = await res.blob();
        onprogress(createProgressEvent(0.66, 1));
    }
    const file = blobToFile(blob, filename);
    onprogress(createProgressEvent(1, 1));
    return file;
};

var getResponseHeader = (xhr, header, parse = (header) => header) => xhr.getAllResponseHeaders().indexOf(header) >= 0
    ? parse(xhr.getResponseHeader(header))
    : undefined;

var getFilenameFromContentDisposition = (header) => {
    if (!header)
        return null;
    const matches = header.split(/filename=|filename\*=.+''/)
        .splice(1)
        .map(name => name.trim().replace(/^["']|[;"']{0,2}$/g, ''))
        .filter(name => name.length);
    return matches.length ? decodeURI(matches[matches.length - 1]) : null;
};

const DokaErrorCode = {
    URL_REQUEST: 'URL_REQUEST',
};
class DokaError extends Error {
    constructor(message, code, metadata) {
        super(message);
        this.name = 'DokaError';
        this.code = code;
        this.metadata = metadata;
    }
}

var fetchFile = (url, onprogress) => new Promise((resolve, reject) => {
    const handleError = () => reject(new DokaError('Error fetching image', DokaErrorCode.URL_REQUEST, xhr));
    const xhr = new XMLHttpRequest();
    xhr.onprogress = onprogress;
    (xhr.onerror = handleError),
        (xhr.onload = () => {
            if (!xhr.response || xhr.status >= 300 || xhr.status < 200)
                return handleError();
            // we store the response mime type so we can add it to the blob later on, if it's missing (happens on Safari 10)
            const mimetype = getResponseHeader(xhr, 'Content-Type');
            // try to get filename and any file instructions as well
            const filename = getResponseHeader(xhr, 'Content-Disposition', getFilenameFromContentDisposition) || getFilenameFromURL(url);
            // convert to actual file if possible
            resolve(blobToFile(xhr.response, filename, mimetype || getMimeTypeFromFilename(filename)));
        });
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
});

var urlToFile = (url, onprogress) => {
    // use fetch to create blob from data uri
    if (isDataURI(url))
        return dataURIToFile(url, undefined, onprogress);
    // load file from url
    return fetchFile(url, onprogress);
};

var isBlob = (v) => v instanceof Blob && !(v instanceof File);

var srcToFile = async (src, onprogress) => {
    if (isFile(src) || isBlob(src))
        return src;
    else if (isString(src))
        return await urlToFile(src, onprogress);
    else if (isElement(src, 'canvas'))
        return await canvasToFile(src);
    else if (isElement(src, 'img'))
        return await imageToFile(src);
    else {
        throw new DokaError('Invalid image source', 'invalid-image-source');
    }
};

var orientImageSize = async (size, orientation = 1) => {
    // browser can handle image orientation
    if (await canOrientImages())
        return size;
    // no need to fix orientation
    if (orientation < 5)
        return size;
    // correct image size
    return sizeCreate(size.height, size.width);
};

const readExifOrientation$1 = (view, offset) => {
    // Missing 0x45786966 Marker? No Exif Header, stop here
    if (view.getUint32((offset += 2), false) !== 0x45786966)
        return -1;
    // First 2bytes defines byte align of TIFF data.
    // If it is 0x4949="I I", it means "Intel" type byte align
    const intelByteAligned = view.getUint16((offset += 6), false) === 0x4949;
    offset += view.getUint32(offset + 4, intelByteAligned);
    const tags = view.getUint16(offset, intelByteAligned);
    offset += 2;
    // find Orientation tag
    for (let i = 0; i < tags; i++) {
        if (view.getUint16(offset + i * 12, intelByteAligned) === 0x0112)
            return view.getUint16(offset + i * 12 + 8, intelByteAligned);
    }
};
var arrayBufferReadExifOrientationTag = (data) => {
    if (!data)
        return;
    const view = new DataView(data);
    // Every JPEG file starts from binary value '0xFFD8', so if it's not present, exit here
    if (view.getUint16(0, false) != 0xffd8)
        return;
    const length = view.byteLength;
    let offset = 2;
    let marker;
    while (offset < length) {
        if (view.getUint16(offset + 2, false) <= 8)
            return;
        marker = view.getUint16(offset, false);
        offset += 2;
        // Exif Marker found
        if (marker === 0xffe1)
            return readExifOrientation$1(view, offset);
        // Invalid marker, exit here
        else if ((marker & 0xff00) !== 0xff00)
            return;
        // Next!
        else
            offset += view.getUint16(offset, false);
    }
};

var isJPEG = (file) => /jpeg/.test(file.type);

var isPlainObject = (obj) => typeof obj == 'object' && obj.constructor == Object;

var stringify = (value) => (!isPlainObject(value) ? value : JSON.stringify(value));

var post = (url, dataset, options) => new Promise((resolve, reject) => {
    const { token = {}, beforeSend = noop, onprogress = noop } = options;
    token.cancel = () => request.abort();
    const request = new XMLHttpRequest();
    request.upload.onprogress = onprogress;
    request.onload = () => request.status >= 200 && request.status < 300 ? resolve(request) : reject(request);
    request.onerror = () => reject(request);
    request.ontimeout = () => reject(request);
    request.open('POST', encodeURI(url));
    beforeSend(request);
    request.send(dataset.reduce((formData, args) => {
        // @ts-ignore
        formData.append(...args.map(stringify));
        return formData;
    }, new FormData()));
});

var ctxRotate = (ctx, rotation = 0, pivot) => {
    if (rotation === 0)
        return ctx;
    ctx.translate(pivot.x, pivot.y);
    ctx.rotate(rotation);
    ctx.translate(-pivot.x, -pivot.y);
    return ctx;
};

var ctxTranslate = (ctx, x, y) => {
    ctx.translate(x, y);
    return ctx;
};

var ctxScale = (ctx, x, y) => {
    ctx.scale(x, y);
    return ctx;
};

var cropImageData = async (imageData, options = {}) => {
    const { flipX, flipY, rotation, crop } = options;
    const imageSize = sizeCreateFromAny(imageData);
    const shouldFlip = flipX || flipY;
    const shouldRotate = !!rotation;
    const cropDefined = crop && (crop.x || crop.y || crop.width || crop.height);
    const cropCoversImage = cropDefined && rectEqual(crop, rectCreateFromSize(imageSize));
    const shouldCrop = cropDefined && !cropCoversImage;
    // skip!
    if (!shouldFlip && !shouldRotate && !shouldCrop)
        return imageData;
    // create drawing context
    let imageDataOut;
    let image = h('canvas', {
        width: imageData.width,
        height: imageData.height,
    });
    image.getContext('2d').putImageData(imageData, 0, 0);
    // flip image data
    if (shouldFlip) {
        const ctx = h('canvas', {
            width: image.width,
            height: image.height,
        }).getContext('2d');
        ctxScale(ctx, flipX ? -1 : 1, flipY ? -1 : 1);
        ctx.drawImage(image, flipX ? -image.width : 0, flipY ? -image.height : 0);
        ctx.restore();
        releaseCanvas(image);
        image = ctx.canvas;
    }
    // rotate image data
    if (shouldRotate) {
        // if shouldRotate is true we also receive a crop rect
        const outputSize = sizeApply(sizeCreateFromRect(rectCreateFromPoints(rectRotate(rectCreateFromAny(image), rotation))), Math.floor);
        const ctx = h('canvas', {
            width: crop.width,
            height: crop.height,
        }).getContext('2d');
        ctxTranslate(ctx, -crop.x, -crop.y);
        ctxRotate(ctx, rotation, sizeCenter(outputSize));
        ctx.drawImage(image, (outputSize.width - image.width) * 0.5, (outputSize.height - image.height) * 0.5);
        ctx.restore();
        releaseCanvas(image);
        image = ctx.canvas;
    }
    // crop image data
    else if (shouldCrop) {
        const ctx = image.getContext('2d');
        imageDataOut = ctx.getImageData(crop.x, crop.y, crop.width, crop.height);
        releaseCanvas(image);
        return imageDataOut;
    }
    // done, return resulting image data
    const ctx = image.getContext('2d');
    imageDataOut = ctx.getImageData(0, 0, image.width, image.height);
    releaseCanvas(image);
    return imageDataOut;
};

var getUniqueId = () => Math.random()
    .toString(36)
    .substr(2, 9);

var functionToBlob = (fn) => new Blob(['(', typeof fn === 'function' ? fn.toString() : fn, ')()'], {
    type: 'application/javascript',
});

const wrapFunction = (fn) => `function () {self.onmessage = function (message) {(${fn.toString()}).apply(null, message.data.content.concat([function (err, response) {
    response = response || {};
    const transfer = 'data' in response ? [response.data.buffer] : 'width' in response ? [response] : [];
    return self.postMessage({ id: message.data.id, content: response, error: err }, transfer);
}]))}}`;
const workerPool = new Map();
var thread = (fn, args, transferList) => new Promise((resolve, reject) => {
    let pooledWorker = workerPool.get(fn);
    if (!pooledWorker) {
        // create worker for this function
        const workerFn = wrapFunction(fn);
        // create a new web worker
        const url = URL.createObjectURL(functionToBlob(workerFn));
        const messages = new Map();
        const worker = new Worker(url);
        // create a pooled worker, this object will contain the worker and active messages
        pooledWorker = {
            url,
            worker,
            messages,
            terminate: () => {
                pooledWorker.worker.terminate();
                URL.revokeObjectURL(url);
            },
        };
        // handle received messages
        worker.onmessage = function (e) {
            // should receive message id and message
            const { id, content, error } = e.data;
            // message route no longer valid
            if (!messages.has(id))
                return;
            // get related thread and resolve with returned content
            const message = messages.get(id);
            // remove thread from threads cache
            messages.delete(id);
            // resolve or reject message based on response from worker
            error != null ? message.reject(error) : message.resolve(content);
        };
        // pool this worker
        workerPool.set(fn, pooledWorker);
    }
    // we need a way to remember this message so we generate a unique id and use that as a key for this request, that way we can link the response back to request in the pooledWorker.onmessage handler
    const messageId = getUniqueId();
    pooledWorker.messages.set(messageId, { resolve, reject });
    // use pooled worker and await response
    pooledWorker.worker.postMessage({ id: messageId, content: args }, transferList);
});

var resizeTransform = (options, done) => {
    const { imageData, width, height } = options;
    const originWidth = imageData.width;
    const originHeight = imageData.height;
    const targetWidth = Math.round(width);
    const targetHeight = Math.round(height);
    const inputData = imageData.data;
    const outputData = new Uint8ClampedArray(targetWidth * targetHeight * 4);
    const ratioWidth = originWidth / targetWidth;
    const ratioHeight = originHeight / targetHeight;
    const ratioWidthHalf = Math.ceil(ratioWidth * 0.5);
    const ratioHeightHalf = Math.ceil(ratioHeight * 0.5);
    for (let j = 0; j < targetHeight; j++) {
        for (let i = 0; i < targetWidth; i++) {
            const x2 = (i + j * targetWidth) * 4;
            let weight = 0;
            let weights = 0;
            let weightsAlpha = 0;
            let r = 0;
            let g = 0;
            let b = 0;
            let a = 0;
            const centerY = (j + 0.5) * ratioHeight;
            for (let yy = Math.floor(j * ratioHeight); yy < (j + 1) * ratioHeight; yy++) {
                const dy = Math.abs(centerY - (yy + 0.5)) / ratioHeightHalf;
                const centerX = (i + 0.5) * ratioWidth;
                const w0 = dy * dy;
                for (let xx = Math.floor(i * ratioWidth); xx < (i + 1) * ratioWidth; xx++) {
                    let dx = Math.abs(centerX - (xx + 0.5)) / ratioWidthHalf;
                    const w = Math.sqrt(w0 + dx * dx);
                    if (w >= -1 && w <= 1) {
                        weight = 2 * w * w * w - 3 * w * w + 1;
                        if (weight > 0) {
                            dx = 4 * (xx + yy * originWidth);
                            const ref = inputData[dx + 3];
                            a += weight * ref;
                            weightsAlpha += weight;
                            if (ref < 255) {
                                weight = (weight * ref) / 250;
                            }
                            r += weight * inputData[dx];
                            g += weight * inputData[dx + 1];
                            b += weight * inputData[dx + 2];
                            weights += weight;
                        }
                    }
                }
            }
            outputData[x2] = r / weights;
            outputData[x2 + 1] = g / weights;
            outputData[x2 + 2] = b / weights;
            outputData[x2 + 3] = a / weightsAlpha;
        }
    }
    done(null, {
        data: outputData,
        width: targetWidth,
        height: targetHeight,
    });
};

var imageDataObjectToImageData = (obj) => {
    if (obj instanceof ImageData) {
        return obj;
    }
    let imageData;
    try {
        imageData = new ImageData(obj.width, obj.height);
    }
    catch (err) {
        // IE + Old EDGE (tested on 12)
        const canvas = h('canvas');
        imageData = canvas.getContext('2d').createImageData(obj.width, obj.height);
    }
    imageData.data.set(obj.data);
    return imageData;
};

var resizeImageData = async (imageData, options = {}) => {
    const { width, height, fit, upscale } = options;
    // no need to rescale
    if (!width && !height)
        return imageData;
    let targetWidth = width;
    let targetHeight = height;
    if (!width) {
        targetWidth = height;
    }
    else if (!height) {
        targetHeight = width;
    }
    if (fit !== 'force') {
        let scalarWidth = targetWidth / imageData.width;
        let scalarHeight = targetHeight / imageData.height;
        let scalar = 1;
        if (fit === 'cover') {
            scalar = Math.max(scalarWidth, scalarHeight);
        }
        else if (fit === 'contain') {
            scalar = Math.min(scalarWidth, scalarHeight);
        }
        // if image is too small, exit here with original image
        if (scalar > 1 && upscale === false)
            return imageData;
        targetWidth = Math.round(imageData.width * scalar);
        targetHeight = Math.round(imageData.height * scalar);
    }
    // no need to resize?
    if (imageData.width === targetWidth && imageData.height === targetHeight)
        return imageData;
    // let's resize!
    imageData = await thread(resizeTransform, [{ imageData: imageData, width: targetWidth, height: targetHeight }], [imageData.data.buffer]);
    // the resizer returns a plain object, not an actual image data object, lets create one
    return imageDataObjectToImageData(imageData);
};

var colorEffect = (options, done) => {
    const { imageData, matrix } = options;
    if (!matrix)
        return done(null, imageData);
    const outputData = new Uint8ClampedArray(imageData.width * imageData.height * 4);
    const data = imageData.data;
    const l = data.length;
    const m11 = matrix[0];
    const m12 = matrix[1];
    const m13 = matrix[2];
    const m14 = matrix[3];
    const m15 = matrix[4];
    const m21 = matrix[5];
    const m22 = matrix[6];
    const m23 = matrix[7];
    const m24 = matrix[8];
    const m25 = matrix[9];
    const m31 = matrix[10];
    const m32 = matrix[11];
    const m33 = matrix[12];
    const m34 = matrix[13];
    const m35 = matrix[14];
    const m41 = matrix[15];
    const m42 = matrix[16];
    const m43 = matrix[17];
    const m44 = matrix[18];
    const m45 = matrix[19];
    let index = 0;
    let r = 0.0;
    let g = 0.0;
    let b = 0.0;
    let a = 0.0;
    let mr = 0.0;
    let mg = 0.0;
    let mb = 0.0;
    let ma = 0.0;
    let or = 0.0;
    let og = 0.0;
    let ob = 0.0;
    for (; index < l; index += 4) {
        r = data[index] / 255;
        g = data[index + 1] / 255;
        b = data[index + 2] / 255;
        a = data[index + 3] / 255;
        mr = r * m11 + g * m12 + b * m13 + a * m14 + m15;
        mg = r * m21 + g * m22 + b * m23 + a * m24 + m25;
        mb = r * m31 + g * m32 + b * m33 + a * m34 + m35;
        ma = r * m41 + g * m42 + b * m43 + a * m44 + m45;
        or = Math.max(0, mr * ma) + (1.0 - ma);
        og = Math.max(0, mg * ma) + (1.0 - ma);
        ob = Math.max(0, mb * ma) + (1.0 - ma);
        outputData[index] = Math.max(0.0, Math.min(1.0, or)) * 255;
        outputData[index + 1] = Math.max(0.0, Math.min(1.0, og)) * 255;
        outputData[index + 2] = Math.max(0.0, Math.min(1.0, ob)) * 255;
        outputData[index + 3] = a * 255;
    }
    done(null, {
        data: outputData,
        width: imageData.width,
        height: imageData.height,
    });
};

var convolutionEffect = (options, done) => {
    const { imageData, matrix } = options;
    if (!matrix)
        return done(null, imageData);
    // calculate kernel weight
    let kernelWeight = matrix.reduce((prev, curr) => prev + curr);
    // input info
    const inputWidth = imageData.width;
    const inputHeight = imageData.height;
    const inputData = imageData.data;
    let i = 0;
    let x = 0;
    let y = 0;
    const side = Math.round(Math.sqrt(matrix.length));
    const sideHalf = Math.floor(side / 2);
    let r = 0, g = 0, b = 0, a = 0, cx = 0, cy = 0, scy = 0, scx = 0, srcOff = 0, weight = 0;
    const outputData = new Uint8ClampedArray(inputWidth * inputHeight * 4);
    for (y = 0; y < inputHeight; y++) {
        for (x = 0; x < inputWidth; x++) {
            // calculate the weighed sum of the source image pixels that
            // fall under the convolution matrix
            r = 0;
            g = 0;
            b = 0;
            a = 0;
            for (cy = 0; cy < side; cy++) {
                for (cx = 0; cx < side; cx++) {
                    scy = y + cy - sideHalf;
                    scx = x + cx - sideHalf;
                    if (scy < 0 || scy >= inputHeight || scx < 0 || scx >= inputWidth)
                        continue;
                    srcOff = (scy * inputWidth + scx) * 4;
                    weight = matrix[cy * side + cx];
                    r += inputData[srcOff] * weight;
                    g += inputData[srcOff + 1] * weight;
                    b += inputData[srcOff + 2] * weight;
                    a += inputData[srcOff + 3] * weight;
                }
            }
            outputData[i] = r / kernelWeight;
            outputData[i + 1] = g / kernelWeight;
            outputData[i + 2] = b / kernelWeight;
            outputData[i + 3] = a / kernelWeight;
            i += 4;
        }
    }
    done(null, {
        data: outputData,
        width: inputWidth,
        height: inputHeight,
    });
};

var vignetteEffect = (options, done) => {
    const { imageData, strength } = options;
    if (!strength)
        return done(null, imageData);
    const outputData = new Uint8ClampedArray(imageData.width * imageData.height * 4);
    const inputWidth = imageData.width;
    const inputHeight = imageData.height;
    const data = imageData.data;
    const dist = (x, y) => {
        dx = x - cx;
        dy = y - cy;
        return Math.sqrt(dx * dx + dy * dy);
    };
    let index = 0;
    let x = 0;
    let y = 0;
    let cx = inputWidth * 0.5;
    let cy = inputHeight * 0.5;
    let dx;
    let dy;
    let f;
    let dm = dist(0, 0);
    for (y = 0; y < inputHeight; y++) {
        for (x = 0; x < inputWidth; x++) {
            index = (x + y * inputWidth) * 4;
            f = (dist(x, y) * strength) / dm;
            if (strength > 0) {
                outputData[index] = data[index] * (1 - f);
                outputData[index + 1] = data[index + 1] * (1 - f);
                outputData[index + 2] = data[index + 2] * (1 - f);
            }
            else {
                outputData[index] = data[index] + f * -(255 - data[index]);
                outputData[index + 1] = data[index + 1] + f * -(255 - data[index + 1]);
                outputData[index + 2] = data[index + 2] + f * -(255 - data[index + 2]);
            }
            outputData[index + 3] = data[index + 3];
        }
    }
    done(null, {
        data: outputData,
        width: imageData.width,
        height: imageData.height,
    });
};

var noiseEffect = (options, done) => {
    const { imageData, level, monochrome = false } = options;
    if (!level)
        return done(null, imageData);
    const outputData = new Uint8ClampedArray(imageData.width * imageData.height * 4);
    const data = imageData.data;
    const l = data.length;
    let index = 0;
    let r;
    let g;
    let b;
    let average;
    const rand = () => (-1 + Math.random() * 2) * 255 * level;
    for (; index < l; index += 4) {
        if (monochrome) {
            average = rand();
            r = average;
            g = average;
            b = average;
        }
        else {
            r = rand();
            g = rand();
            b = rand();
        }
        outputData[index] = data[index] + r;
        outputData[index + 1] = data[index + 1] + g;
        outputData[index + 2] = data[index + 2] + b;
        outputData[index + 3] = data[index + 3];
    }
    done(null, {
        data: outputData,
        width: imageData.width,
        height: imageData.height,
    });
};

var gammaEffect = (options, done) => {
    const { imageData, level } = options;
    if (!level)
        return done(null, imageData);
    const outputData = new Uint8ClampedArray(imageData.width * imageData.height * 4);
    const data = imageData.data;
    const l = data.length;
    let index = 0;
    let r;
    let g;
    let b;
    for (; index < l; index += 4) {
        r = data[index] / 255;
        g = data[index + 1] / 255;
        b = data[index + 2] / 255;
        outputData[index] = Math.pow(r, level) * 255;
        outputData[index + 1] = Math.pow(g, level) * 255;
        outputData[index + 2] = Math.pow(b, level) * 255;
        outputData[index + 3] = data[index + 3];
    }
    done(null, {
        data: outputData,
        width: imageData.width,
        height: imageData.height,
    });
};

var isIdentityMatrix = (matrix) => {
    /*
    [
        1, 0, 0, 0, 0
        0, 1, 0, 0, 0
        0, 0, 1, 0, 0
        0, 0, 0, 1, 0
    ]
    */
    const l = matrix.length;
    let v;
    let s = l >= 20 ? 6 : l >= 16 ? 5 : 3;
    for (let i = 0; i < l; i++) {
        v = matrix[i];
        if (v === 1 && i % s !== 0)
            return false;
        else if (v !== 0 && v !== 1)
            return false;
    }
    return true;
};

var filterImageData = async (imageData, options = {}) => {
    const { colorMatrix, convolutionMatrix, gamma: gammaLevel, noise: noiseLevel, vignette: vignetteStrength, } = options;
    // filters
    const filters = [];
    // apply convolution matrix
    if (convolutionMatrix) {
        filters.push([convolutionEffect, { matrix: convolutionMatrix.clarity }]);
    }
    // apply noise
    if (gammaLevel > 0) {
        filters.push([gammaEffect, { level: 1.0 / gammaLevel }]);
    }
    // apply color matrix
    if (colorMatrix && !isIdentityMatrix(colorMatrix)) {
        filters.push([colorEffect, { matrix: colorMatrix }]);
    }
    // apply noise
    if (noiseLevel > 0 || noiseLevel < 0) {
        filters.push([noiseEffect, { level: noiseLevel }]);
    }
    // apply vignette
    if (vignetteStrength > 0 || vignetteStrength < 0) {
        filters.push([vignetteEffect, { strength: vignetteStrength }]);
    }
    // no changes
    if (!filters.length)
        return imageData;
    // builds effect chain
    const chain = (transforms, i) => `(err, imageData) => {
            (${transforms[i][0].toString()})(Object.assign({ imageData: imageData }, filterInstructions[${i}]), 
                ${transforms[i + 1] ? chain(transforms, i + 1) : 'done'})
        }`;
    const filterChain = `function (options, done) {
        const filterInstructions = options.filterInstructions;
        const imageData = options.imageData;
        (${chain(filters, 0)})(null, imageData)
    }`;
    imageData = await thread(filterChain, [
        {
            imageData: imageData,
            filterInstructions: filters.map((t) => t[1]),
        },
    ], [imageData.data.buffer]);
    // the resizer returns a plain object, not an actual image data object, lets create one
    return imageDataObjectToImageData(imageData);
};

var isNumber = (v) => typeof v === 'number';

var isEmoji = (str) => isString(str) &&
    str.match(/(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g) !== null;

var hasProp = (obj, key) => obj.hasOwnProperty(key);

var isObject = (v) => typeof v === 'object';

const copy = (val) => (isObject(val) ? deepCopy(val) : val);
const deepCopy = (src) => {
    let dst;
    if (Array.isArray(src)) {
        dst = [];
        src.forEach((val, i) => {
            dst[i] = copy(val);
        });
    }
    else {
        dst = {};
        Object.keys(src).forEach((key) => {
            const val = src[key];
            dst[key] = copy(val);
        });
    }
    return dst;
};

var isFunction = (v) => typeof v === 'function';

var isArray = (arr) => Array.isArray(arr);

let result$3 = null;
var isMac = () => {
    if (result$3 === null)
        result$3 = /^mac/i.test(navigator.platform);
    return result$3;
};

let result$4 = null;
var isIOS = () => {
    if (result$4 === null)
        result$4 = isUserAgent(/iPhone|iPad|iPod/);
    return result$4;
};

var isApple = () => isIOS() || isMac();

var isWindows = () => /^win/i.test(navigator.platform);

// macos:   font-size: 123, x: 63.5, y: 110
// windows: font-size: 112, x: 64, y: 103
// android: font-size: 112, x: 64, y: 102
let x = 64;
let y = 102;
let fontSize = 112;
let hasSetValues = false;
var getEmojiSVG = (emoji, alt) => {
    if (!hasSetValues && isBrowser()) {
        if (isWindows())
            y = 103;
        if (isApple()) {
            x = 63.5;
            y = 110;
            fontSize = 123;
        }
        hasSetValues = true;
    }
    return `<svg${alt ? ` aria-label="${alt}"` : ''} width="128" height="128" viewBox="0 0 128 128" preserveAspectRatio="xMinYMin meet" xmlns="http://www.w3.org/2000/svg"><text x="${x}" y="${y}" alignment-baseline="text-top" dominant-baseline="text-top" text-anchor="middle" font-size="${fontSize}px">${emoji}</text></svg>`;
};

var SVGToDataURL = (svg) => `data:image/svg+xml,${svg.replace('<', '%3C').replace('>', '%3E')}`;

var isBinary = (v) => v instanceof Blob;

var toPercentage = (value, total) => `${(value / total) * 100}%`;

var loadImage = (image, onsize = undefined) => new Promise((resolve, reject) => {
    // the image element we'll use to load the image
    let imageElement = image;
    let sizeCalculated = false;
    const reportSize = () => {
        if (sizeCalculated)
            return;
        sizeCalculated = true;
        isFunction(onsize) &&
            /* Use Promise.resolve to make async but place before resolve of parent promise */
            Promise.resolve().then(() => onsize(sizeCreate(imageElement.naturalWidth, imageElement.naturalHeight)));
    };
    // if is not an image element, it must be a valid image source
    if (!imageElement.src) {
        imageElement = new Image();
        imageElement.src = isString(image) ? image : URL.createObjectURL(image);
    }
    if (imageElement.complete) {
        reportSize();
        return resolve(imageElement);
    }
    // try to calculate size faster
    if (isFunction(onsize))
        getImageElementSize(imageElement).then(reportSize).catch(reject);
    imageElement.onload = () => {
        reportSize();
        resolve(imageElement);
    };
    imageElement.onerror = reject;
});

var colorArrayToRGBA = (color) => `rgba(${Math.round(color[0] * 255)}, ${Math.round(color[1] * 255)}, ${Math.round(color[2] * 255)}, ${isNumber(color[3]) ? color[3] : 1})`;

// font offset
// font size 16 -> 2, 4
// font size 32 -> 4, 6
// font size 64 -> 8, 12
// font size 128 -> 16, 24
// font size 256 -> 32, 48
let fontOffsetBrowser = undefined;
const getBrowserFontOffset = (fontSize) => {
    if (!fontOffsetBrowser) {
        // size
        const size = 32;
        // let's calculate it
        const ctx = createSimpleContext(size, size);
        updateTextContext(ctx, 100, 'sans-serif', 'left', [1, 1, 1]);
        ctx.fillText('F', 0, 0);
        // get pixel data so we can find the white pixels
        const data = ctx.getImageData(0, 0, size, size).data;
        // find x offset
        let p = 0;
        let step = 4;
        let to = data.length;
        let from = to - size * 4;
        for (p = from; p < to; p += step) {
            if (data[p])
                break;
        }
        const x = (p - from) / step;
        // find y offset
        from = (size - 1) * 4;
        step = size * 4;
        for (p = from; p < to; p += step) {
            if (data[p])
                break;
        }
        const y = (p - from) / step;
        fontOffsetBrowser = vectorCreate(x, y);
        // done with canvas
        releaseCanvas(ctx.canvas);
    }
    return vectorCreate(-fontOffsetBrowser.x * fontSize * 0.01, -fontOffsetBrowser.y * fontSize * 0.01);
};
const createSimpleContext = (width = 1, height = 1) => {
    const canvas = h('canvas');
    const ctx = canvas.getContext('2d');
    ctx.canvas.width = width;
    ctx.canvas.height = height;
    return ctx;
};
const createSimpleTextContext = (fontSize, fontFamily, textAlign = 'left', color) => {
    const ctx = createSimpleContext();
    updateTextContext(ctx, fontSize, fontFamily, textAlign, color);
    return ctx;
};
const updateTextContext = (ctx, fontSize, fontFamily, textAlign = 'left', color) => {
    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.textBaseline = 'top';
    ctx.textAlign = textAlign;
    ctx.fillStyle = color ? colorArrayToRGBA(color) : '#000';
};
const resizeContextToFitText = (ctx, text, fontSize, lineHeight) => {
    const { width, height } = measureTextContext(ctx, text, fontSize, lineHeight);
    ctx.canvas.width = Math.ceil(width);
    ctx.canvas.height = Math.ceil(height);
    return ctx;
};
const measureTextContext = (ctx, text, fontSize, lineHeight) => {
    // calculate width
    const lines = text.split('\n');
    const width = lines.reduce((prev, curr) => {
        const measure = ctx.measureText(curr);
        if (measure.width > prev) {
            prev = measure.width;
        }
        return prev;
    }, 1);
    // calculate height
    const height = (isFunction(lineHeight) ? lineHeight(fontSize) : lineHeight) * (lines.length + 1);
    return sizeCreate(Math.ceil(width), Math.ceil(height));
};
const textSize = (text, fontSize, fontFamily, lineHeight) => {
    const ctx = createSimpleTextContext(fontSize, fontFamily);
    const size = measureTextContext(ctx, text, fontSize, lineHeight);
    return size;
};
const wrapText = (ctx, text, lineWidth) => {
    // exit if no text
    if (text.length === 0)
        return '';
    const res = [];
    let lineBuffer = '';
    let lineIndex = 0;
    let measure;
    const paragraphs = text.split('\n\n');
    // draw the current line
    const pushLine = () => {
        if (!lineBuffer.length)
            return;
        if (!res[lineIndex]) {
            res[lineIndex] = [];
        }
        res[lineIndex].push(lineBuffer);
        // clear buffer
        lineBuffer = '';
    };
    const fitChar = (char) => {
        const testLine = lineBuffer + char;
        // measure width of entire line if adding these chars
        measure = ctx.measureText(testLine);
        // fits on line?
        if (measure.width < lineWidth) {
            lineBuffer = testLine;
        }
        else {
            // doesn't fit but line buffer is empty, just print the character and move to next line
            if (!lineBuffer.length) {
                lineBuffer = testLine;
                pushLine();
            }
            // fits, lets print current line and move char to next line
            else {
                pushLine();
                lineBuffer = char;
            }
            lineIndex++;
        }
    };
    const fitWord = (word) => {
        const testLine = lineBuffer.length ? lineBuffer + ' ' + word : word;
        // measure width of entire line if adding these chars
        measure = ctx.measureText(testLine);
        // fits on line?
        if (measure.width < lineWidth) {
            lineBuffer = testLine;
        }
        // wrap to next line
        else {
            // if line buffer is empty, whole word doesn't fit, need to cut it up
            if (!lineBuffer.length) {
                word.split('').forEach(fitChar);
            }
            // there are words in the buffer that do fit, let's draw the line and move this word to the next line
            else {
                // draw current buffer
                pushLine();
                lineIndex++;
                // retry to fit this word
                fitWord(word);
            }
        }
    };
    paragraphs.forEach((p) => {
        const lines = p.split('\n');
        lines.forEach((l) => {
            l.split(' ').forEach(fitWord);
            // end of line reached, if we have words in our buffer
            // at this point we need to draw them and then move to the next line
            if (lineBuffer.length)
                pushLine();
            // forced new line
            lineIndex++;
        });
        // forced new line
        lineIndex++;
    });
    return res.map((line) => line.join(' ')).join('\n');
};
const drawText = (ctx, text = '', options = {}) => {
    // exit if no text
    if (text.length === 0)
        return ctx;
    const { x = 0, y = 0, lineWidth = 0, textAlign, fontSize, lineHeight } = options;
    // determine where the browser will render the font and correct for browser differences
    const browserFontOffset = vectorAdd(getBrowserFontOffset(fontSize), vectorCreate(fontSize / 12, fontSize / 3.75));
    const fontOffsetX = x + browserFontOffset.x;
    const fontOffsetY = y + browserFontOffset.y;
    const lineHeightComputed = isFunction(lineHeight) ? lineHeight(fontSize) : lineHeight;
    let offset = textAlign === 'right' ? lineWidth : textAlign === 'center' ? lineWidth * 0.5 : 0;
    text.split('\n').forEach((line, i) => {
        ctx.fillText(line, fontOffsetX + offset, fontOffsetY + i * lineHeightComputed);
    });
    return ctx;
};

//#region shape generic
const shapeContain = (shape, parentRect) => {
    const shapeAspectRatio = shape.width / shape.height;
    const containedShape = rectContainRect(parentRect, shapeAspectRatio);
    if (shape.width > containedShape.width || shape.height > containedShape.height) {
        shape.width = containedShape.width;
        shape.height = containedShape.height;
    }
    return shape;
};
const shapeCenter = (shape, position) => {
    shape.x = position.x - shape.width * 0.5;
    shape.y = position.y - shape.height * 0.5;
    return shape;
};
const shapeDeepCopy = (shape) => {
    const copy = deepCopy(shape);
    // we don't want a million image elements so we'll reference the original one
    if (shape.backgroundImageElement)
        copy.backgroundImageElement = shape.backgroundImageElement;
    return copy;
};
const getContextSize = (context, size = {}) => {
    const contextAspectRatio = rectAspectRatio(context);
    let width;
    let height;
    if (size.width || size.height) {
        width = parseFloat(size.width || Number.MAX_SAFE_INTEGER);
        height = parseFloat(size.height || Number.MAX_SAFE_INTEGER);
        const min = Math.min(width, height);
        if (isString(size.width) || isString(size.height)) {
            width = `${min}%`;
            height = `${min * contextAspectRatio}%`;
        }
        else {
            width = min;
            height = min;
        }
    }
    else {
        const min = 10;
        width = `${min}%`;
        height = `${min * contextAspectRatio}%`;
    }
    return {
        width,
        height,
    };
};
const shapeCreateFromEmoji = (emoji, props = {}) => {
    return {
        width: undefined,
        height: undefined,
        ...props,
        aspectRatio: 1,
        backgroundImage: SVGToDataURL(getEmojiSVG(emoji)),
    };
};
const shapeCreateFromImage = (src, oncomplete, props = {}) => {
    const shape = {
        // required/default image shape props
        width: undefined,
        height: undefined,
        aspectRatio: undefined,
        backgroundColor: [0, 0, 0, 0],
        // custom props
        ...props,
        // set image
        backgroundImage: isString(src) ? src : URL.createObjectURL(src),
    };
    return shapeLoadResources(shape, oncomplete);
};
const shapeLoadResources = (shape, oncomplete = noop) => {
    if (shape.backgroundImage) {
        // done loading
        if (shape.isComplete) {
            oncomplete(shape.isError, shape);
            return shape;
        }
        // is still loading
        if (shape.isLoading)
            return shape;
        // default state before loading
        shape.isComplete = false;
        shape.isError = false;
        shape.isLoading = true;
        // updates the size of the shape relative to the image
        const updateShapeSize = (imageSize) => {
            // if background size is not set the shape is not locked to the image aspect ratio
            if (shape.backgroundSize)
                return;
            // always locked to aspect ratio
            const aspectRatio = imageSize.width / imageSize.height;
            // check if width has been defined, if so, we update height to match aspect ratio
            const width = shape.width || imageSize.width;
            const height = width / aspectRatio;
            // set final dimensions
            shape.width = width;
            shape.height = height;
            shape.aspectRatio = imageSize.width / imageSize.height;
        };
        // now loading
        loadImage(shape.backgroundImage, updateShapeSize)
            .then((image) => {
            shape.backgroundImageElement = image;
        })
            .catch((error) => {
            shape.isError = error;
        })
            .finally(() => {
            shape.isLoading = false;
            shape.isComplete = true;
            oncomplete(shape.isError, shape);
        });
    }
    return shape;
};
const shapeCreateFromPreset = (preset, parentRect, oncomplete) => {
    let shape;
    if (isString(preset) || isBinary(preset)) {
        // if is emoji, create default markup,
        if (isEmoji(preset)) {
            // create markup
            shape = shapeCreateFromEmoji(preset, getContextSize(parentRect));
            setTimeout(() => oncomplete(undefined, shape), 0);
        }
        // is URL, create default markup for image
        else {
            shape = shapeCreateFromImage(preset, oncomplete, {
                // disableStyle: true,
                ...getContextSize(parentRect),
            });
        }
    }
    else {
        // is using src shortcut
        if (preset.src) {
            // shape options
            const shapeOptions = {
                // default shape styles
                ...preset.shape,
                // precalcualte size of shape in context
                ...getContextSize(parentRect, preset.shape || preset),
            };
            // emoji markup
            if (isEmoji(preset.src)) {
                shape = shapeCreateFromEmoji(preset.src, shapeOptions);
                setTimeout(() => oncomplete(undefined, shape), 0);
            }
            // is url
            else if (isString(preset.src)) {
                shape = shapeCreateFromImage(preset.src, oncomplete, shapeOptions);
            }
        }
        // should have markup defined
        else if (preset.shape) {
            shape = shapeDeepCopy(preset.shape);
        }
    }
    if (hasProp(shape, 'backgroundImage')) {
        // set transparent background if no background color defined
        if (!hasProp(shape, 'backgroundColor')) {
            shape.backgroundColor = [0, 0, 0, 0];
        }
        // for image presets, disable styles by default
        if (!hasProp(shape, 'disableStyle')) {
            shape.disableStyle = true;
        }
        // don't allow flipping
        if (!hasProp(shape, 'disableFlip')) {
            shape.disableFlip = true;
        }
    }
    return parentRect ? shapeComputeDisplay(shape, parentRect) : shape;
};
const shapeLineGetStartPoint = (line) => vectorCreate(line.x1, line.y1);
const shapeLineGetEndPoint = (line) => vectorCreate(line.x2, line.y2);
const shapeTextUID = ({ text, textAlign, fontSize, fontFamily, lineHeight }) => `${text}_${textAlign}_${fontSize}_${fontFamily}_${isFunction(lineHeight) ? lineHeight(fontSize) : lineHeight}`;
//#endregion
//#region shape testing
// shape types
const shapeIsText = (shape) => hasProp(shape, 'text');
const shapeIsTextLine = (shape) => shapeIsText(shape) && !shapeHasComputedSize(shape);
const shapeIsTextBox = (shape) => shapeIsText(shape) && shapeHasComputedSize(shape);
const shapeIsRect = (shape) => !shapeIsText(shape) && shapeHasComputedSize(shape);
const shapeIsEllipse = (shape) => hasProp(shape, 'rx');
const shapeIsLine = (shape) => hasProp(shape, 'x1') && !shapeIsTriangle(shape);
const shapeIsTriangle = (shape) => hasProp(shape, 'x3');
const shapeIsPath = (shape) => hasProp(shape, 'points');
// shape state
const shapeIsTextEmpty = (shape) => shapeIsText(shape) && !shape.text.length;
const shapeIsVisible = (shape) => hasProp(shape, 'opacity') ? shape.opacity > 0 : true;
const shapeIsSelected = (shape) => shape.isSelected;
const shapeIsDraft = (shape) => shape.isDraft;
const shapeHasSize = (shape) => hasProp(shape, 'width') && hasProp(shape, 'height');
const shapeHasNumericStroke = (shape) => isNumber(shape.strokeWidth) && shape.strokeWidth > 0; // only relevant if is bigger than 0
const shapeHasRelativePosition = (shape) => {
    const hasRight = hasProp(shape, 'right');
    const hasBottom = hasProp(shape, 'bottom');
    return hasRight || hasBottom;
};
const shapeHasTexture = (shape) => hasProp(shape, 'backgroundImage') || hasProp(shape, 'text');
const shapeHasRelativeSize = (shape) => ((hasProp(shape, 'x') || hasProp(shape, 'left')) && hasProp(shape, 'right')) ||
    ((hasProp(shape, 'y') || hasProp(shape, 'top')) && hasProp(shape, 'bottom'));
const shapeHasComputedSize = (shape) => shapeHasSize(shape) || shapeHasRelativeSize(shape);
// actions
const shapeSelect = (shape) => {
    shape.isSelected = true;
    return shape;
};
const shapeMakeDraft = (shape) => {
    shape.isDraft = true;
    return shape;
};
// rights
const shapeCanStyle = (shape, style) => {
    if (shape.disableStyle === true)
        return false;
    if (isArray(shape.disableStyle)) {
        return !shape.disableStyle.includes(style);
    }
    return true;
};
const shapeCanErase = (shape) => shape.disableErase !== true;
const shapeCanSelect = (shape) => shape.disableSelect !== true && !shapeIsPath(shape) && !shapeIsTriangle(shape);
const shapeCanRemove = (shape) => shape.disableRemove !== true;
const shapeCanDuplicate = (shape) => shape.disableDuplicate !== true && shapeCanMove(shape);
const shapeCanReorder = (shape) => shape.disableReorder !== true;
const shapeCanFlip = (shape) => {
    if (shape.disableFlip)
        return false;
    if (shapeIsDraft(shape) || shapeHasRelativePosition(shape))
        return false;
    return shapeHasTexture(shape);
};
const shapeCanInput = (shape, input) => {
    if (!shapeIsText(shape))
        return false;
    if (shape.disableInput === true)
        return false;
    if (isFunction(shape.disableInput))
        return shape.disableInput(input != null ? input : shape.text);
    return input || true;
};
const shapeCanMove = (shape) => shape.disableMove !== true &&
    !shapeIsDraft(shape) &&
    !shapeIsPath(shape) &&
    !shapeHasRelativePosition(shape);
const shapeCanResize = (shape) => shape.disableResize !== true &&
    !shapeIsDraft(shape) &&
    (shapeHasSize(shape) || shapeIsEllipse(shape) || shapeIsLine(shape)) &&
    !shapeHasRelativePosition(shape) &&
    shapeCanMove(shape);
const shapeCanRotate = (shape) => shape.disableRotate !== true &&
    !shapeIsDraft(shape) &&
    (shapeHasSize(shape) || shapeIsTextLine(shape) || shapeIsEllipse(shape)) &&
    !shapeHasRelativePosition(shape);
//#endregion
//#region shape formatting
const shapeDeleteRelativeProps = (shape) => {
    delete shape.left;
    delete shape.right;
    delete shape.top;
    delete shape.bottom;
    return shape;
};
const shapeDeleteTransformProps = (shape) => {
    delete shape.rotation;
    return shape;
};
const shapeFormatStroke = (shape) => {
    shape.strokeWidth = shape.strokeWidth || 1;
    shape.strokeColor = shape.strokeColor || [0, 0, 0];
    return shape;
};
const shapeFormatFill = (shape) => {
    shape.backgroundColor = shape.backgroundColor
        ? shape.backgroundColor
        : shape.strokeWidth || shape.backgroundImage
            ? undefined
            : [0, 0, 0];
    return shape;
};
const autoLineHeight = (fontSize) => fontSize * 1.2;
const shapeFormatText = (shape) => {
    shape.fontSize = shape.fontSize || 16;
    shape.fontFamily = shape.fontFamily || 'sans-serif';
    shape.lineHeight = isNumber(shape.lineHeight) ? shape.lineHeight : autoLineHeight;
    shape.color = shape.color || [0, 0, 0];
    return shapeIsTextLine(shape) ? shapeFormatTextLine(shape) : shapeFormatTextBox(shape);
};
const shapeFormatTextLine = (shape) => {
    delete shape.textAlign;
    return shapeDeleteRelativeProps(shape);
};
const shapeFormatTextBox = (shape) => {
    shape.textAlign = shape.textAlign || 'left';
    return shape;
};
const shapeFormatRect = (shape) => {
    shape.cornerRadius = shape.cornerRadius || 0;
    shape.strokeWidth = shape.strokeWidth || 0;
    shape.strokeColor = shape.strokeColor || [0, 0, 0];
    return shapeFormatFill(shape);
};
const shapeFormatTriangle = (shape) => {
    shape.strokeWidth = shape.strokeWidth || 0;
    shape.strokeColor = shape.strokeColor || [0, 0, 0];
    shapeFormatFill(shape);
    return shapeDeleteRelativeProps(shape);
};
const shapeFormatEllipse = (shape) => {
    shape.strokeWidth = shape.strokeWidth || 0;
    shape.strokeColor = shape.strokeColor || [0, 0, 0];
    return shapeFormatFill(shape);
};
const shapeFormatPath = (shape) => {
    shapeFormatStroke(shape);
    shapeDeleteTransformProps(shape);
    return shapeDeleteRelativeProps(shape);
};
const shapeFormatLine = (shape) => {
    shapeFormatStroke(shape);
    shape.lineStart = shape.lineStart || undefined;
    shape.lineEnd = shape.lineEnd || undefined;
    shapeDeleteTransformProps(shape);
    return shapeDeleteRelativeProps(shape);
};
const shapeFormatDefaults = (shape) => {
    if (!isString(shape.id))
        shape.id = getUniqueId();
    if (!hasProp(shape, 'rotation'))
        shape.rotation = 0;
    if (!hasProp(shape, 'opacity'))
        shape.opacity = 1;
    if (!hasProp(shape, 'disableErase'))
        shape.disableErase = true;
};
const shapeFormat = (shape) => {
    shapeFormatDefaults(shape);
    if (shapeIsText(shape)) {
        shapeFormatText(shape);
    }
    else if (shapeIsRect(shape)) {
        shapeFormatRect(shape);
    }
    else if (shapeIsPath(shape)) {
        shapeFormatPath(shape);
    }
    else if (shapeIsLine(shape)) {
        shapeFormatLine(shape);
    }
    else if (shapeIsEllipse(shape)) {
        shapeFormatEllipse(shape);
    }
    else if (shapeIsTriangle(shape)) {
        shapeFormatTriangle(shape);
    }
    shapeLoadResources(shape);
    return shape;
};
const shapeGetDescription = (shape) => {
    if (shapeIsText(shape)) {
        return 'text';
    }
    else if (shapeIsRect(shape)) {
        return 'rectangle';
    }
    else if (shapeIsPath(shape)) {
        return 'path';
    }
    else if (shapeIsLine(shape)) {
        return 'line';
    }
    else if (shapeIsEllipse(shape)) {
        return 'ellipse';
    }
    else if (shapeIsTriangle(shape)) {
        return 'triangle';
    }
    return;
};
//#endregion
const toPixelValue = (percentage, total) => (parseFloat(percentage) / 100) * total;
//#region shape transforming
const shapeComputeValue = (shape, prop, { width, height }) => {
    if (!hasProp(shape, prop))
        return;
    let value = shape[prop];
    if (/points/.test(prop)) {
        const arr = shape[prop];
        if (!isString(arr[0].x))
            return;
        shape[prop] = arr.map((p) => vectorCreate(toPixelValue(p.x, width), toPixelValue(p.y, height)));
    }
    else if (isString(value)) {
        const f = parseFloat(value) / 100;
        if (/^x|left|width|rx|fontSize|cornerRadius|strokeWidth/.test(prop))
            shape[prop] = width * f;
        else if (/^right/.test(prop))
            shape[prop] = width - width * f;
        else if (/^y|top|height|ry/.test(prop))
            shape[prop] = height * f;
        else if (/^bottom/.test(prop))
            shape[prop] = height - height * f;
    }
};
const displayProps = [
    'width',
    'height',
    'left',
    'top',
    'right',
    'bottom',
    'x',
    'y',
    'rx',
    'ry',
    'points',
    'fontSize',
    'strokeWidth',
    'lineHeight',
    'x1',
    'y1',
    'x2',
    'y2',
    'x3',
    'y3',
];
const shapeComputeDisplay = (shape, parentRect) => {
    displayProps.forEach((prop) => shapeComputeValue(shape, prop, parentRect));
    shapeComputeRect(shape, parentRect);
    return shape;
};
const shapeGetPropPixelTotal = (prop, parentRect) => {
    let total;
    if (/^x|width|rx|fontSize|strokeWidth|cornerRadius/.test(prop)) {
        total = parentRect.width;
    }
    else if (/^y|height|ry/.test(prop)) {
        total = parentRect.height;
    }
    return total;
};
const shapeUpdateProp = (shape, prop, value, parentRect) => {
    if (!isString(shape[prop]))
        return (shape[prop] = value);
    let total = shapeGetPropPixelTotal(prop, parentRect);
    shape[prop] = toPercentage(value, total);
    return shape;
};
const shapeGetPropPixelValue = (shape, prop, parentRect) => {
    if (!isString(shape[prop]))
        return shape[prop];
    return toPixelValue(shape[prop], shapeGetPropPixelTotal(prop, parentRect));
};
const shapeGetPropsPixelValues = (shape, props, parentRect) => {
    return props.reduce((prev, prop) => {
        const value = shapeGetPropPixelValue(shape, prop, parentRect);
        prev[prop] = value;
        return prev;
    }, {});
};
const shapeUpdateProps = (shape, props, parentRect) => {
    Object.keys(props).forEach((key) => shapeUpdateProp(shape, key, props[key], parentRect));
    return shape;
};
const shapeComputeRect = (shape, parentRect) => {
    if (hasProp(shape, 'left'))
        shape.x = shape.left;
    if (hasProp(shape, 'right')) {
        const r = parentRect.width - shape.right;
        if (hasProp(shape, 'left')) {
            shape.x = shape.left;
            shape.width = Math.max(0, r - shape.left);
        }
        else if (hasProp(shape, 'width')) {
            shape.x = r - shape.width;
        }
    }
    if (hasProp(shape, 'top'))
        shape.y = shape.top;
    if (hasProp(shape, 'bottom')) {
        const b = parentRect.height - shape.bottom;
        if (hasProp(shape, 'top')) {
            shape.y = shape.top;
            shape.height = Math.max(0, b - shape.top);
        }
        else if (hasProp(shape, 'height')) {
            shape.y = b - shape.height;
        }
    }
    return shape;
};
const shapeComputeTransform = (shape, translate, scale) => {
    if (shapeIsPath(shape)) {
        shape.points
            .filter((point) => isNumber(point.x))
            .forEach((point) => {
            point.x *= scale;
            point.y *= scale;
            point.x += translate.x;
            point.y += translate.y;
        });
    }
    if (shapeIsTriangle(shape) && isNumber(shape.x1)) {
        shape.x1 *= scale;
        shape.y1 *= scale;
        shape.x2 *= scale;
        shape.y2 *= scale;
        shape.x3 *= scale;
        shape.y3 *= scale;
        shape.x1 += translate.x;
        shape.y1 += translate.y;
        shape.x2 += translate.x;
        shape.y2 += translate.y;
        shape.x3 += translate.x;
        shape.y3 += translate.y;
    }
    if (shapeIsLine(shape) && isNumber(shape.x1)) {
        shape.x1 *= scale;
        shape.y1 *= scale;
        shape.x2 *= scale;
        shape.y2 *= scale;
        shape.x1 += translate.x;
        shape.y1 += translate.y;
        shape.x2 += translate.x;
        shape.y2 += translate.y;
    }
    if (isNumber(shape.x) && isNumber(shape.y)) {
        shape.x *= scale;
        shape.y *= scale;
        shape.x += translate.x;
        shape.y += translate.y;
    }
    if (isNumber(shape.width) && isNumber(shape.height)) {
        shape.width *= scale;
        shape.height *= scale;
    }
    if (isNumber(shape.rx) && isNumber(shape.ry)) {
        shape.rx *= scale;
        shape.ry *= scale;
    }
    if (shapeHasNumericStroke(shape)) {
        shape.strokeWidth *= scale;
    }
    if (shapeIsText(shape) && isNumber(shape.fontSize)) {
        shape.fontSize *= scale;
    }
    if (hasProp(shape, 'cornerRadius') && isNumber(shape.cornerRadius)) {
        shape.cornerRadius *= scale;
    }
    return shape;
};
const shapeGetCenter = (shape) => {
    if (shapeIsRect(shape) || shapeIsTextBox(shape)) {
        return vectorCreate(shape.x + shape.width * 0.5, shape.y + shape.height * 0.5);
    }
    if (shapeIsEllipse(shape)) {
        return vectorCreate(shape.x, shape.y);
    }
    if (shapeIsTextLine(shape)) {
        const size = textSize(shape.text, shape.fontSize, shape.fontFamily, shape.lineHeight);
        return vectorCreate(shape.x + size.width * 0.5, shape.y + size.height * 0.5);
    }
    if (shapeIsPath(shape)) {
        return vectorCenter(shape.points);
    }
    if (shapeIsLine(shape)) {
        return vectorCenter([
            shapeLineGetStartPoint(shape),
            shapeLineGetEndPoint(shape),
        ]);
    }
    return undefined;
};
const shapeComputeLineEnd = (position, strokeWidth, color, style, direction) => {
    let shape = {};
    const scalar = 5;
    const isSolid = /solid/.test(style);
    const sizeMultiplier = isSolid ? 1.25 : 1;
    const scaledSize = scalar * strokeWidth * sizeMultiplier;
    const scaledSizeHalf = scaledSize * 0.5;
    const normal = vectorNormalize(direction);
    const offset = vectorMultiply(vectorClone(normal), scaledSizeHalf);
    const anchor = vectorClone(position);
    const positionUpdated = vectorClone(position);
    if (/arrow/.test(style)) {
        const tipOffset = vectorMultiply(vectorClone(normal), scaledSize);
        const inset = vectorCreate(position.x + tipOffset.x, position.y + tipOffset.y);
        if (isSolid) {
            shape = {
                points: [
                    vectorCreate(position.x, position.y),
                    vectorCreate(inset.x - tipOffset.y * 0.4, inset.y + tipOffset.x * 0.4),
                    vectorCreate(inset.x + tipOffset.y * 0.4, inset.y - tipOffset.x * 0.4),
                ],
                backgroundColor: color,
            };
            // move back so arrow triangle overlaps with end of line (otherwise line sticks out of triangle tip)
            positionUpdated.x += offset.x;
            positionUpdated.y += offset.y;
        }
        else {
            shape = {
                points: [
                    vectorCreate(inset.x + tipOffset.y * 0.4, inset.y - tipOffset.x * 0.4),
                    vectorClone(position),
                    vectorCreate(inset.x - tipOffset.y * 0.4, inset.y + tipOffset.x * 0.4),
                ],
                strokeWidth: strokeWidth,
                strokeColor: color,
            };
        }
    }
    else if (/circle/.test(style)) {
        // adjust line point offset
        positionUpdated.x += offset.x - normal.x;
        positionUpdated.y += offset.y - normal.y;
        // add line cap
        shape = {
            x: anchor.x,
            y: anchor.y,
            rx: scaledSizeHalf,
            ry: scaledSizeHalf,
            backgroundColor: isSolid ? color : undefined,
            strokeWidth: isSolid ? undefined : strokeWidth,
            strokeColor: isSolid ? undefined : color,
        };
    }
    else if (/square/.test(style)) {
        // adjust line point offset
        positionUpdated.x += offset.x - normal.x;
        positionUpdated.y += offset.y - normal.y;
        shape = {
            x: anchor.x - scaledSizeHalf,
            y: anchor.y - scaledSizeHalf,
            width: scaledSize,
            height: scaledSize,
            rotation: vectorAngle(normal),
            backgroundColor: isSolid ? color : undefined,
            strokeWidth: isSolid ? undefined : strokeWidth,
            strokeColor: isSolid ? undefined : color,
        };
    }
    else if (style === 'bar') {
        shape = {
            points: [
                vectorCreate(anchor.x - offset.y, anchor.y + offset.x),
                vectorCreate(anchor.x + offset.y, anchor.y - offset.x),
            ],
            strokeWidth: strokeWidth,
            strokeColor: color,
        };
    }
    return {
        position: positionUpdated,
        shape,
    };
};
//#endregion

var ctxRoundRect = (ctx, x, y, width, height, radius) => {
    if (width < 2 * radius)
        radius = width / 2;
    if (height < 2 * radius)
        radius = height / 2;
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + width, y, x + width, y + height, radius);
    ctx.arcTo(x + width, y + height, x, y + height, radius);
    ctx.arcTo(x, y + height, x, y, radius);
    ctx.arcTo(x, y, x + width, y, radius);
    ctx.closePath();
    return ctx;
};

const defineRectShape = (ctx, shape) => {
    shape.cornerRadius > 0
        ? ctxRoundRect(ctx, shape.x, shape.y, shape.width, shape.height, shape.cornerRadius)
        : ctx.rect(shape.x, shape.y, shape.width, shape.height);
    return ctx;
};
const fillRectShape = (ctx, shape) => {
    shape.backgroundColor && ctx.fill();
    return ctx;
};
const strokeRectShape = (ctx, shape) => {
    shape.strokeWidth && ctx.stroke();
    return ctx;
};
var drawRect = async (ctx, shape) => new Promise((resolve, reject) => {
    ctx.lineWidth = shape.strokeWidth ? shape.strokeWidth : 1; // 1 is default value for lineWidth prop
    ctx.strokeStyle = shape.strokeColor ? colorArrayToRGBA(shape.strokeColor) : 'none';
    ctx.fillStyle = shape.backgroundColor ? colorArrayToRGBA(shape.backgroundColor) : 'none';
    ctx.globalAlpha = shape.opacity;
    if (shape.backgroundImage) {
        shapeLoadResources(shape, (error, shape) => {
            if (error)
                return reject(error);
            let sX = 0;
            let sY = 0;
            let sWidth = shape.backgroundImageElement.naturalWidth;
            let sHeight = shape.backgroundImageElement.naturalHeight;
            let dX = shape.x;
            let dY = shape.y;
            let dWidth = shape.width;
            let dHeight = shape.height;
            if (shape.backgroundSize === 'contain') {
                const rect = rectContainRect(shape, sWidth / sHeight);
                dWidth = rect.width;
                dHeight = rect.height;
                dX += rect.x;
                dY += rect.y;
            }
            else if (shape.backgroundSize === 'cover') {
                const rect = rectContainRect(rectCreate(0, 0, sWidth, sHeight), dWidth / dHeight);
                sWidth = rect.width;
                sHeight = rect.height;
                sX = rect.x;
                sY = rect.y;
            }
            defineRectShape(ctx, shape);
            fillRectShape(ctx, shape);
            ctx.save();
            ctx.clip();
            ctx.drawImage(shape.backgroundImageElement, sX, sY, sWidth, sHeight, dX, dY, dWidth, dHeight);
            ctx.restore();
            strokeRectShape(ctx, shape);
            resolve(ctx);
        });
    }
    else {
        defineRectShape(ctx, shape);
        fillRectShape(ctx, shape);
        strokeRectShape(ctx, shape);
        resolve(ctx);
    }
});

var drawEllipse = async (ctx, shape) => new Promise((resolve, reject) => {
    ctx.lineWidth = shape.strokeWidth || 1; // 1 is default value for lineWidth prop
    ctx.strokeStyle = shape.strokeColor ? colorArrayToRGBA(shape.strokeColor) : 'none';
    ctx.fillStyle = shape.backgroundColor ? colorArrayToRGBA(shape.backgroundColor) : 'none';
    ctx.globalAlpha = shape.opacity;
    const x = shape.x - shape.rx;
    const y = shape.y - shape.ry;
    const w = shape.rx * 2;
    const h = shape.ry * 2;
    const kappa = 0.5522848;
    const ox = (w / 2) * kappa;
    const oy = (h / 2) * kappa;
    const xe = x + w;
    const ye = y + h;
    const xm = x + w / 2;
    const ym = y + h / 2;
    ctx.moveTo(x, ym);
    ctx.bezierCurveTo(x, ym - oy, xm - ox, y, xm, y);
    ctx.bezierCurveTo(xm + ox, y, xe, ym - oy, xe, ym);
    ctx.bezierCurveTo(xe, ym + oy, xm + ox, ye, xm, ye);
    ctx.bezierCurveTo(xm - ox, ye, x, ym + oy, x, ym);
    shape.backgroundColor && ctx.fill();
    if (shape.backgroundImage) {
        shapeLoadResources(shape, (error, image) => {
            if (error)
                return reject(error);
            ctx.drawImage(image.backgroundImageElement, shape.x, shape.y, shape.width, shape.height);
            shape.strokeWidth && ctx.stroke();
            resolve(ctx);
        });
    }
    else {
        shape.strokeWidth && ctx.stroke();
    }
    resolve(ctx);
});

var drawText$1 = async (ctx, shape) => {
    const size = shape.width
        ? sizeCreateFromAny(shape)
        : textSize(shape.text, shape.fontSize, shape.fontFamily, shape.lineHeight);
    const rect = {
        x: shape.x,
        y: shape.y,
        width: size.width,
        height: size.height,
    };
    drawRect(ctx, {
        ...shape,
        ...rect,
    });
    updateTextContext(ctx, shape.fontSize, shape.fontFamily, shape.textAlign, shape.color);
    ctx.rect(shape.x, shape.y, shape.width, shape.height);
    ctx.save();
    ctx.clip();
    drawText(ctx, shape.width ? wrapText(ctx, shape.text, shape.width) : shape.text, {
        x: shape.x,
        y: shape.y,
        fontSize: shape.fontSize,
        textAlign: shape.textAlign,
        lineHeight: shape.lineHeight,
        lineWidth: shape.width,
    });
    ctx.restore();
    return ctx;
};

var drawLine = async (ctx, shape, drawShape) => new Promise((resolve, reject) => {
    ctx.lineWidth = shape.strokeWidth || 1; // 1 is default value for lineWidth prop
    ctx.strokeStyle = shape.strokeColor ? colorArrayToRGBA(shape.strokeColor) : 'none';
    ctx.globalAlpha = shape.opacity;
    let lineStartPosition = shapeLineGetStartPoint(shape);
    let lineEndPosition = shapeLineGetEndPoint(shape);
    const lineStartDescription = shape.lineStart &&
        shapeComputeLineEnd(lineStartPosition, shape.strokeWidth, shape.strokeColor, shape.lineStart, vectorCreate(lineEndPosition.x - lineStartPosition.x, lineEndPosition.y - lineStartPosition.y));
    const lineEndDescription = shape.lineEnd &&
        shapeComputeLineEnd(lineEndPosition, shape.strokeWidth, shape.strokeColor, shape.lineEnd, vectorCreate(lineStartPosition.x - lineEndPosition.x, lineStartPosition.y - lineEndPosition.y));
    // update start and end position of line if required to draw caps
    lineStartPosition = lineStartDescription
        ? lineStartDescription.position
        : lineStartPosition;
    lineEndPosition = lineEndDescription ? lineEndDescription.position : lineEndPosition;
    // draw line
    ctx.moveTo(lineStartPosition.x, lineStartPosition.y);
    ctx.lineTo(lineEndPosition.x, lineEndPosition.y);
    shape.strokeWidth && ctx.stroke();
    ctx.beginPath();
    lineStartDescription && drawShape(ctx, lineStartDescription.shape);
    ctx.beginPath();
    lineEndDescription && drawShape(ctx, lineEndDescription.shape);
    resolve(ctx);
});

var drawPath = async (ctx, shape) => new Promise((resolve, reject) => {
    ctx.lineWidth = shape.strokeWidth || 1; // 1 is default value for lineWidth prop
    ctx.strokeStyle = shape.strokeColor ? colorArrayToRGBA(shape.strokeColor) : 'none';
    ctx.fillStyle = shape.backgroundColor ? colorArrayToRGBA(shape.backgroundColor) : 'none';
    ctx.globalAlpha = shape.opacity;
    // draw line
    const { points } = shape;
    ctx.moveTo(points[0].x, points[0].y);
    const l = points.length;
    for (let i = 1; i < l; i++) {
        ctx.lineTo(points[i].x, points[i].y);
    }
    shape.strokeWidth && ctx.stroke();
    shape.backgroundColor && ctx.fill();
    resolve(ctx);
});

var ctxFlip = (ctx, flipX, flipY, pivot) => {
    if (!flipX && !flipY)
        return ctx;
    ctx.translate(pivot.x, pivot.y);
    ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
    ctx.translate(-pivot.x, -pivot.y);
    return ctx;
};

const drawShape = async (ctx, shape) => {
    const center = shapeGetCenter(shape);
    // rotate context
    ctxRotate(ctx, shape.rotation, center);
    // flip context
    ctxFlip(ctx, shape.flipX, shape.flipY, center);
    let fn;
    if (shapeIsRect(shape)) {
        fn = drawRect;
    }
    else if (shapeIsEllipse(shape)) {
        fn = drawEllipse;
    }
    else if (shapeIsLine(shape)) {
        fn = drawLine;
    }
    else if (shapeIsPath(shape)) {
        fn = drawPath;
    }
    else if (shapeIsText(shape)) {
        fn = drawText$1;
    }
    return fn(ctx, shape, drawShape);
};

var drawImageData = async (imageData, options = {}) => {
    const { shapes = [], context = imageData, transform = noop } = options;
    // no shapes to draw
    if (!shapes.length)
        return imageData;
    // output
    let imageDataOut;
    // create drawing context
    const canvas = h('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    const ctx = canvas.getContext('2d');
    ctx.putImageData(imageData, 0, 0);
    // compute the position of all shapes
    const computedShapes = shapes.map(shapeDeepCopy).map((shape) => shapeComputeDisplay(shape, {
        x: 0,
        y: 0,
        width: context.width,
        height: context.height,
    }));
    // compute transforms for all shapes
    transform(ctx);
    for (const shape of computedShapes) {
        ctx.save();
        // each shape has its own path
        ctx.beginPath();
        // wait for shape to draw before drawing next shape
        await drawShape(ctx, shape);
        ctx.restore();
    }
    imageDataOut = ctx.getImageData(0, 0, canvas.width, canvas.height);
    releaseCanvas(canvas);
    return imageDataOut;
};

var fillImageData = async (imageData, options = {}) => {
    const { backgroundColor } = options;
    // no background color set or is fully transparent background color
    if (!backgroundColor || (backgroundColor && backgroundColor[3] === 0))
        return imageData;
    // fill
    let imageDataOut;
    let image = h('canvas');
    image.width = imageData.width;
    image.height = imageData.height;
    const ctx = image.getContext('2d');
    ctx.putImageData(imageData, 0, 0);
    // fill behind image
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = colorArrayToRGBA(backgroundColor);
    ctx.fillRect(0, 0, image.width, image.height);
    imageDataOut = ctx.getImageData(0, 0, image.width, image.height);
    releaseCanvas(image);
    return imageDataOut;
};

var dotColorMatrix = (a, b) => {
    const res = new Array(20);
    // R
    res[0] = a[0] * b[0] + a[1] * b[5] + a[2] * b[10] + a[3] * b[15];
    res[1] = a[0] * b[1] + a[1] * b[6] + a[2] * b[11] + a[3] * b[16];
    res[2] = a[0] * b[2] + a[1] * b[7] + a[2] * b[12] + a[3] * b[17];
    res[3] = a[0] * b[3] + a[1] * b[8] + a[2] * b[13] + a[3] * b[18];
    res[4] = a[0] * b[4] + a[1] * b[9] + a[2] * b[14] + a[3] * b[19] + a[4];
    // G
    res[5] = a[5] * b[0] + a[6] * b[5] + a[7] * b[10] + a[8] * b[15];
    res[6] = a[5] * b[1] + a[6] * b[6] + a[7] * b[11] + a[8] * b[16];
    res[7] = a[5] * b[2] + a[6] * b[7] + a[7] * b[12] + a[8] * b[17];
    res[8] = a[5] * b[3] + a[6] * b[8] + a[7] * b[13] + a[8] * b[18];
    res[9] = a[5] * b[4] + a[6] * b[9] + a[7] * b[14] + a[8] * b[19] + a[9];
    // B
    res[10] = a[10] * b[0] + a[11] * b[5] + a[12] * b[10] + a[13] * b[15];
    res[11] = a[10] * b[1] + a[11] * b[6] + a[12] * b[11] + a[13] * b[16];
    res[12] = a[10] * b[2] + a[11] * b[7] + a[12] * b[12] + a[13] * b[17];
    res[13] = a[10] * b[3] + a[11] * b[8] + a[12] * b[13] + a[13] * b[18];
    res[14] = a[10] * b[4] + a[11] * b[9] + a[12] * b[14] + a[13] * b[19] + a[14];
    // A
    res[15] = a[15] * b[0] + a[16] * b[5] + a[17] * b[10] + a[18] * b[15];
    res[16] = a[15] * b[1] + a[16] * b[6] + a[17] * b[11] + a[18] * b[16];
    res[17] = a[15] * b[2] + a[16] * b[7] + a[17] * b[12] + a[18] * b[17];
    res[18] = a[15] * b[3] + a[16] * b[8] + a[17] * b[13] + a[18] * b[18];
    res[19] = a[15] * b[4] + a[16] * b[9] + a[17] * b[14] + a[18] * b[19] + a[19];
    return res;
};

var getColorMatrixFromColorMatrices = (colorMatrices) => colorMatrices.length
    ? colorMatrices.reduce((previous, current) => dotColorMatrix([...previous], current), colorMatrices.shift())
    : [];

// connect function in process chain
const connect = (fn, getter = (...args) => args, setter) => async (state, options, onprogress) => {
    // will hold function result
    let res;
    // try to run the function
    try {
        res = await fn(...getter(state, options, onprogress));
    }
    catch (err) {
        throw err;
    }
    // a setter isn't required
    setter && setter(state, res);
    return state;
};
//
// Reader/Writer Presets
//
const AnyToFile = ({ srcProp = 'src', destProp = 'dest' } = {}) => [
    connect(srcToFile, (state, options, onprogress) => [state[srcProp], onprogress], (state, file) => (state[destProp] = file)),
    'any-to-file',
];
const BlobReadImageSize = ({ srcProp = 'src', destProp = 'size' } = {}) => [
    connect(getImageSize, (state, options) => [state[srcProp]], (state, size) => (state[destProp] = size)),
    'read-image-size',
];
const ImageSizeMatchOrientation = ({ srcSize = 'size', srcOrientation = 'orientation', destSize = 'size', } = {}) => [
    connect(orientImageSize, (state) => [state[srcSize], state[srcOrientation]], (state, size) => (state[destSize] = size)),
    'image-size-match-orientation',
];
const BlobReadImageHead = ({ srcProp = 'src', destProp = 'head' } = {}) => [
    connect((blob, slice) => (isJPEG(blob) ? blobReadSection(blob, slice) : undefined), 
    // 256 * 1024 should be enough bytes to find orientation header in next step
    (state) => [state[srcProp], [0, 256 * 1024], onprogress], (state, head) => (state[destProp] = head)),
    'read-image-head',
];
const ImageHeadReadExifOrientationTag = ({ srcProp = 'head', destProp = 'orientation', } = {}) => [
    connect(arrayBufferReadExifOrientationTag, (state) => [state[srcProp]], (state, orientation) => (state[destProp] = orientation)),
    'read-exif-orientation-tag',
];
const ImageHeadClearExifOrientationTag = ({ srcProp = 'head' } = {}) => [
    connect(arrayBufferWriteExifOrientationTag, (state) => [state[srcProp], 1]),
    'clear-exif-orientation-tag',
];
const ApplyCanvasScalar = ({ srcImageSize = 'size', srcCanvasSize = 'imageData', srcImageState = 'imageState', destImageSize = 'size', destScalar = 'scalar', } = {}) => [
    connect((naturalSize, canvasSize, imageState) => {
        // calculate canvas scalar
        const scalar = Math.min(canvasSize.width / naturalSize.width, canvasSize.height / naturalSize.height);
        // done because not scaling
        if (scalar !== 1) {
            const { crop, annotation, decoration } = imageState;
            // origin to scale to
            const origin = vectorCreateEmpty();
            // scale select.crop
            if (crop)
                imageState.crop = rectScale(crop, scalar, origin);
            // scale annotation
            const translate = vectorCreateEmpty();
            imageState.annotation = annotation.map((shape) => shapeComputeTransform(shape, translate, scalar));
            // scale decoration
            imageState.decoration = decoration.map((shape) => shapeComputeTransform(shape, translate, scalar));
        }
        return [scalar, sizeCreateFromAny(canvasSize)];
    }, (state) => [state[srcImageSize], state[srcCanvasSize], state[srcImageState]], (state, [scalar, imageSize]) => {
        state[destScalar] = scalar;
        state[destImageSize] = imageSize;
    }),
    'calculate-canvas-scalar',
];
const BlobToImageData = ({ srcProp = 'src', destProp = 'imageData', canvasMemoryLimit = undefined, }) => [
    connect(blobToImageData, (state) => [state[srcProp], canvasMemoryLimit], (state, imageData) => (state[destProp] = imageData)),
    'blob-to-image-data',
];
const ImageDataMatchOrientation = ({ srcImageData = 'imageData', srcOrientation = 'orientation', } = {}) => [
    connect(orientImageData, (state) => [state[srcImageData], state[srcOrientation]], (state, imageData) => (state.imageData = imageData)),
    'image-data-match-orientation',
];
const ImageDataFill = ({ srcImageData = 'imageData', srcImageState = 'imageState', } = {}) => [
    connect(fillImageData, (state) => [
        state[srcImageData],
        { backgroundColor: state[srcImageState].backgroundColor },
    ], (state, imageData) => (state.imageData = imageData)),
    'image-data-fill',
];
const ImageDataCrop = ({ srcImageData = 'imageData', srcImageState = 'imageState', } = {}) => [
    connect(cropImageData, (state) => [
        state[srcImageData],
        {
            crop: state[srcImageState].crop,
            rotation: state[srcImageState].rotation,
            flipX: state[srcImageState].flipX,
            flipY: state[srcImageState].flipY,
        },
    ], (state, imageData) => (state.imageData = imageData)),
    'image-data-crop',
];
const ImageDataResize = ({ resize = {
    width: undefined,
    height: undefined,
    fit: undefined,
    upscale: undefined,
}, srcProp = 'imageData', srcImageState = 'imageState', destImageScaledSize = 'imageScaledSize', }) => [
    connect(resizeImageData, (state) => [
        state[srcProp],
        {
            width: Math.min(resize.width || Number.MAX_SAFE_INTEGER, (state[srcImageState].targetSize && state[srcImageState].targetSize.width) ||
                Number.MAX_SAFE_INTEGER),
            height: Math.min(resize.height || Number.MAX_SAFE_INTEGER, (state[srcImageState].targetSize && state[srcImageState].targetSize.height) ||
                Number.MAX_SAFE_INTEGER),
            fit: resize.fit || 'contain',
            upscale: resize.upscale || false,
        },
    ], (state, imageData) => {
        if (!sizeEqual(state.imageData, imageData))
            state[destImageScaledSize] = sizeCreateFromAny(imageData);
        state.imageData = imageData;
    }),
    'image-data-resize',
];
const ImageDataFilter = ({ srcImageData = 'imageData', srcImageState = 'imageState', destImageData = 'imageData', } = {}) => [
    connect(filterImageData, (state) => {
        const { colorMatrix } = state[srcImageState];
        const colorMatrices = colorMatrix && Object.keys(colorMatrix).map((name) => colorMatrix[name]);
        return [
            state[srcImageData],
            {
                colorMatrix: colorMatrices && getColorMatrixFromColorMatrices(colorMatrices),
                convolutionMatrix: state[srcImageState].convolutionMatrix,
                gamma: state[srcImageState].gamma,
                noise: state[srcImageState].noise,
                vignette: state[srcImageState].vignette,
            },
        ];
    }, (state, imageData) => (state[destImageData] = imageData)),
    'image-data-filter',
];
const ImageDataAnnotate = ({ srcImageData = 'imageData', srcSize = 'size', srcImageState = 'imageState', destImageData = 'imageData', destImageScaledSize = 'imageScaledSize', } = {}) => [
    connect(drawImageData, (state) => [
        state[srcImageData],
        {
            shapes: state[srcImageState].annotation,
            context: state[srcSize],
            transform: (ctx) => {
                const imageSize = state[srcSize];
                const { crop = rectCreateFromSize(imageSize), rotation = 0, flipX, flipY, } = state[srcImageState];
                const rotatedRect = getImageTransformedRect(imageSize, rotation);
                const rotatedSize = {
                    width: rotatedRect.width,
                    height: rotatedRect.height,
                };
                // calculate image scalar so we can scale annotations accordingly
                const scaledSize = state[destImageScaledSize];
                const scalar = scaledSize
                    ? Math.min(scaledSize.width / crop.width, scaledSize.height / crop.height)
                    : 1;
                // calculate center
                const dx = imageSize.width * 0.5 - rotatedSize.width * 0.5;
                const dy = imageSize.height * 0.5 - rotatedSize.height * 0.5;
                const center = sizeCenter(imageSize);
                // image scalar
                ctx.scale(scalar, scalar);
                // offset
                ctx.translate(-dx, -dy);
                ctx.translate(-crop.x, -crop.y);
                // rotation
                ctx.translate(center.x, center.y);
                ctx.rotate(rotation);
                ctx.translate(-center.x, -center.y);
                // flipping
                ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
                ctx.translate(flipX ? -imageSize.width : 0, flipY ? -imageSize.height : 0);
                // annotations are clipped clip to image
                ctx.rect(0, 0, imageSize.width, imageSize.height);
                ctx.clip();
            },
        },
    ], (state, imageData) => (state[destImageData] = imageData)),
    'image-data-annotate',
];
const ImageDataDecorate = ({ srcImageData = 'imageData', srcImageState = 'imageState', destImageData = 'imageData', destImageScaledSize = 'imageScaledSize', } = {}) => [
    connect(drawImageData, (state) => [
        state[srcImageData],
        {
            shapes: state[srcImageState].decoration,
            context: state[srcImageState].crop,
            transform: (ctx) => {
                // calculate image scalar so we can scale decoration accordingly
                const { crop } = state.imageState;
                const scaledSize = state[destImageScaledSize];
                const scalar = scaledSize
                    ? Math.min(scaledSize.width / crop.width, scaledSize.height / crop.height)
                    : 1;
                ctx.scale(scalar, scalar);
            },
        },
    ], (state, imageData) => (state[destImageData] = imageData)),
    'image-data-decorate',
];
const ImageDataToBlob = ({ mimeType = undefined, quality = undefined, srcImageData = 'imageData', srcFile = 'src', destBlob = 'blob', } = {}) => [
    connect(imageDataToBlob, (state) => [
        state[srcImageData],
        mimeType || getMimeTypeFromFilename(state[srcFile].name),
        quality,
    ], (state, blob) => (state[destBlob] = blob)),
    'image-data-to-blob',
];
const BlobWriteImageHead = (srcBlob = 'blob', srcHead = 'head') => [
    connect(blobWriteSection, (state) => [
        state[srcBlob],
        state[srcHead],
        [20],
    ]),
    'blob-write-image-head',
];
const BlobToFile = ({ renameFile = undefined, srcBlob = 'blob', srcFile = 'src', destFile = 'dest', } = {}) => [
    connect(blobToFile, (state) => [
        state[srcBlob],
        renameFile ? renameFile(state[srcFile]) : state[srcFile].name,
    ], (state, file) => (state[destFile] = file)),
    'blob-to-file',
];
const Store = ({ store, dataset = (state) => [
    ['dest', state.dest, state.dest.name],
    ['imageState', state.imageState],
], destStore = 'store', }) => [
    connect(
    // upload function
    async (dataset, onprogress) => {
        try {
            return await post(store, dataset, { onprogress });
        }
        catch (err) {
            throw err;
        }
    }, 
    // get state values
    (state, options, onprogress) => [dataset(state), onprogress], 
    // set state values
    (state, xhr) => (state[destStore] = xhr) // logs XHR request returned by `post`
    ),
    'store',
];
const PropFilter = (allowlist) => [
    connect((state) => {
        // if no allowlist suppleid or is empty array we don't filter
        if (!allowlist || !allowlist.length)
            return state;
        // else we only allow the props defined in the list and delete non matching props
        Object.keys(state).forEach((key) => {
            if (allowlist.includes(key))
                return;
            delete state[key];
        });
        return state;
    }),
    'prop-filter',
];
// Generic image reader, suitable for most use cases
const createDefaultImageReader = (options = {}) => {
    const { orientImage = true, outputProps = ['src', 'dest', 'size'], preprocessImageFile, } = options;
    return [
        // can read most source files and turn them into blobs
        AnyToFile(),
        // TODO: test if supported mime/type
        // called when file created, can be used to read unrecognized files
        preprocessImageFile && [
            connect(preprocessImageFile, (state, options, onprogress) => [
                state.dest,
                options,
                onprogress,
            ], (state, file) => (state.dest = file)),
            'preprocess-image-file',
        ],
        // quickly read size (only reads first part of image)
        BlobReadImageSize({ srcProp: 'dest' }),
        // fix image orientation
        orientImage && BlobReadImageHead({ srcProp: 'dest' }),
        orientImage && ImageHeadReadExifOrientationTag(),
        orientImage && ImageSizeMatchOrientation(),
        // remove unwanted props
        PropFilter(outputProps),
    ].filter(Boolean);
};
// Generic image writer, suitable for most use cases
const createDefaultImageWriter = (options = {}) => {
    const { canvasMemoryLimit = isIOS() ? 4096 * 4096 : Infinity, orientImage = true, copyImageHead = true, mimeType = undefined, quality = undefined, renameFile = undefined, targetSize = undefined, store = undefined, outputProps = ['src', 'dest', 'imageState', 'store'], postprocessImageData, } = options;
    return [
        // get orientation info (if is jpeg)
        (orientImage || copyImageHead) && BlobReadImageHead(),
        orientImage && ImageHeadReadExifOrientationTag(),
        // get image size
        BlobReadImageSize(),
        // get image data
        BlobToImageData({ canvasMemoryLimit }),
        // get canvas scalar for when scaled down
        ApplyCanvasScalar(),
        // fix image orientation
        orientImage && ImageSizeMatchOrientation(),
        orientImage && ImageDataMatchOrientation(),
        // apply image state
        ImageDataCrop(),
        ImageDataResize({ resize: targetSize }),
        ImageDataFilter(),
        ImageDataFill(),
        ImageDataAnnotate(),
        ImageDataDecorate(),
        // run post processing on image data, for example to apply circular crop
        postprocessImageData && [
            connect(postprocessImageData, (state, options, onprogress) => [
                state.imageData,
                options,
                onprogress,
            ], (state, imageData) => (state.imageData = imageData)),
            'postprocess-image-data',
        ],
        // convert to file
        ImageDataToBlob({ mimeType, quality }),
        // we overwite the exif orientation tag so the image is oriented correctly
        orientImage && ImageHeadClearExifOrientationTag(),
        // we write the new image head to the target blob
        copyImageHead && BlobWriteImageHead(),
        // turn the image blob into a file, will also rename the file
        BlobToFile({ renameFile }),
        // upload or process data
        store && (isString(store) ? Store({ store }) : [store, 'store']),
        // remove unwanted props
        PropFilter(outputProps),
    ].filter(Boolean);
};

function noop$1() { }
const identity = x => x;
function assign(tar, src) {
    // @ts-ignore
    for (const k in src)
        tar[k] = src[k];
    return tar;
}
function run(fn) {
    return fn();
}
function blank_object() {
    return Object.create(null);
}
function run_all(fns) {
    fns.forEach(run);
}
function is_function(thing) {
    return typeof thing === 'function';
}
function safe_not_equal(a, b) {
    return a != a ? b == b : a !== b || ((a && typeof a === 'object') || typeof a === 'function');
}
function is_empty(obj) {
    return Object.keys(obj).length === 0;
}
function subscribe(store, ...callbacks) {
    if (store == null) {
        return noop$1;
    }
    const unsub = store.subscribe(...callbacks);
    return unsub.unsubscribe ? () => unsub.unsubscribe() : unsub;
}
function get_store_value(store) {
    let value;
    subscribe(store, _ => value = _)();
    return value;
}
function component_subscribe(component, store, callback) {
    component.$$.on_destroy.push(subscribe(store, callback));
}
function create_slot(definition, ctx, $$scope, fn) {
    if (definition) {
        const slot_ctx = get_slot_context(definition, ctx, $$scope, fn);
        return definition[0](slot_ctx);
    }
}
function get_slot_context(definition, ctx, $$scope, fn) {
    return definition[1] && fn
        ? assign($$scope.ctx.slice(), definition[1](fn(ctx)))
        : $$scope.ctx;
}
function get_slot_changes(definition, $$scope, dirty, fn) {
    if (definition[2] && fn) {
        const lets = definition[2](fn(dirty));
        if ($$scope.dirty === undefined) {
            return lets;
        }
        if (typeof lets === 'object') {
            const merged = [];
            const len = Math.max($$scope.dirty.length, lets.length);
            for (let i = 0; i < len; i += 1) {
                merged[i] = $$scope.dirty[i] | lets[i];
            }
            return merged;
        }
        return $$scope.dirty | lets;
    }
    return $$scope.dirty;
}
function update_slot(slot, slot_definition, ctx, $$scope, dirty, get_slot_changes_fn, get_slot_context_fn) {
    const slot_changes = get_slot_changes(slot_definition, $$scope, dirty, get_slot_changes_fn);
    if (slot_changes) {
        const slot_context = get_slot_context(slot_definition, ctx, $$scope, get_slot_context_fn);
        slot.p(slot_context, slot_changes);
    }
}
function exclude_internal_props(props) {
    const result = {};
    for (const k in props)
        if (k[0] !== '$')
            result[k] = props[k];
    return result;
}
function set_store_value(store, ret, value = ret) {
    store.set(value);
    return ret;
}
function action_destroyer(action_result) {
    return action_result && is_function(action_result.destroy) ? action_result.destroy : noop$1;
}

const is_client = typeof window !== 'undefined';
let now = is_client
    ? () => window.performance.now()
    : () => Date.now();
let raf = is_client ? cb => requestAnimationFrame(cb) : noop$1;

const tasks = new Set();
function run_tasks(now) {
    tasks.forEach(task => {
        if (!task.c(now)) {
            tasks.delete(task);
            task.f();
        }
    });
    if (tasks.size !== 0)
        raf(run_tasks);
}
/**
 * Creates a new task that runs on each raf frame
 * until it returns a falsy value or is aborted
 */
function loop(callback) {
    let task;
    if (tasks.size === 0)
        raf(run_tasks);
    return {
        promise: new Promise(fulfill => {
            tasks.add(task = { c: callback, f: fulfill });
        }),
        abort() {
            tasks.delete(task);
        }
    };
}

function append(target, node) {
    target.appendChild(node);
}
function insert(target, node, anchor) {
    target.insertBefore(node, anchor || null);
}
function detach(node) {
    node.parentNode.removeChild(node);
}
function element(name) {
    return document.createElement(name);
}
function svg_element(name) {
    return document.createElementNS('http://www.w3.org/2000/svg', name);
}
function text(data) {
    return document.createTextNode(data);
}
function space() {
    return text(' ');
}
function empty() {
    return text('');
}
function listen(node, event, handler, options) {
    node.addEventListener(event, handler, options);
    return () => node.removeEventListener(event, handler, options);
}
function prevent_default(fn) {
    return function (event) {
        event.preventDefault();
        // @ts-ignore
        return fn.call(this, event);
    };
}
function stop_propagation(fn) {
    return function (event) {
        event.stopPropagation();
        // @ts-ignore
        return fn.call(this, event);
    };
}
function attr(node, attribute, value) {
    if (value == null)
        node.removeAttribute(attribute);
    else if (node.getAttribute(attribute) !== value)
        node.setAttribute(attribute, value);
}
function children(element) {
    return Array.from(element.childNodes);
}
function set_data(text, data) {
    data = '' + data;
    if (text.wholeText !== data)
        text.data = data;
}
function set_input_value(input, value) {
    input.value = value == null ? '' : value;
}
function set_style(node, key, value, important) {
    node.style.setProperty(key, value, important ? 'important' : '');
}
function custom_event(type, detail) {
    const e = document.createEvent('CustomEvent');
    e.initCustomEvent(type, false, false, detail);
    return e;
}

const active_docs = new Set();
let active = 0;
// https://github.com/darkskyapp/string-hash/blob/master/index.js
function hash(str) {
    let hash = 5381;
    let i = str.length;
    while (i--)
        hash = ((hash << 5) - hash) ^ str.charCodeAt(i);
    return hash >>> 0;
}
function create_rule(node, a, b, duration, delay, ease, fn, uid = 0) {
    const step = 16.666 / duration;
    let keyframes = '{\n';
    for (let p = 0; p <= 1; p += step) {
        const t = a + (b - a) * ease(p);
        keyframes += p * 100 + `%{${fn(t, 1 - t)}}\n`;
    }
    const rule = keyframes + `100% {${fn(b, 1 - b)}}\n}`;
    const name = `__svelte_${hash(rule)}_${uid}`;
    const doc = node.ownerDocument;
    active_docs.add(doc);
    const stylesheet = doc.__svelte_stylesheet || (doc.__svelte_stylesheet = doc.head.appendChild(element('style')).sheet);
    const current_rules = doc.__svelte_rules || (doc.__svelte_rules = {});
    if (!current_rules[name]) {
        current_rules[name] = true;
        stylesheet.insertRule(`@keyframes ${name} ${rule}`, stylesheet.cssRules.length);
    }
    const animation = node.style.animation || '';
    node.style.animation = `${animation ? `${animation}, ` : ''}${name} ${duration}ms linear ${delay}ms 1 both`;
    active += 1;
    return name;
}
function delete_rule(node, name) {
    const previous = (node.style.animation || '').split(', ');
    const next = previous.filter(name
        ? anim => anim.indexOf(name) < 0 // remove specific animation
        : anim => anim.indexOf('__svelte') === -1 // remove all Svelte animations
    );
    const deleted = previous.length - next.length;
    if (deleted) {
        node.style.animation = next.join(', ');
        active -= deleted;
        if (!active)
            clear_rules();
    }
}
function clear_rules() {
    raf(() => {
        if (active)
            return;
        active_docs.forEach(doc => {
            const stylesheet = doc.__svelte_stylesheet;
            let i = stylesheet.cssRules.length;
            while (i--)
                stylesheet.deleteRule(i);
            doc.__svelte_rules = {};
        });
        active_docs.clear();
    });
}

let current_component;
function set_current_component(component) {
    current_component = component;
}
function get_current_component() {
    if (!current_component)
        throw new Error('Function called outside component initialization');
    return current_component;
}
function onMount(fn) {
    get_current_component().$$.on_mount.push(fn);
}
function afterUpdate(fn) {
    get_current_component().$$.after_update.push(fn);
}
function onDestroy(fn) {
    get_current_component().$$.on_destroy.push(fn);
}
function createEventDispatcher() {
    const component = get_current_component();
    return (type, detail) => {
        const callbacks = component.$$.callbacks[type];
        if (callbacks) {
            // TODO are there situations where events could be dispatched
            // in a server (non-DOM) environment?
            const event = custom_event(type, detail);
            callbacks.slice().forEach(fn => {
                fn.call(component, event);
            });
        }
    };
}
function setContext(key, context) {
    get_current_component().$$.context.set(key, context);
}
function getContext(key) {
    return get_current_component().$$.context.get(key);
}
// TODO figure out if we still want to support
// shorthand events, or if we want to implement
// a real bubbling mechanism
function bubble(component, event) {
    const callbacks = component.$$.callbacks[event.type];
    if (callbacks) {
        callbacks.slice().forEach(fn => fn(event));
    }
}

const dirty_components = [];
const binding_callbacks = [];
const render_callbacks = [];
const flush_callbacks = [];
const resolved_promise = Promise.resolve();
let update_scheduled = false;
function schedule_update() {
    if (!update_scheduled) {
        update_scheduled = true;
        resolved_promise.then(flush);
    }
}
function add_render_callback(fn) {
    render_callbacks.push(fn);
}
function add_flush_callback(fn) {
    flush_callbacks.push(fn);
}
let flushing = false;
const seen_callbacks = new Set();
function flush() {
    if (flushing)
        return;
    flushing = true;
    do {
        // first, call beforeUpdate functions
        // and update components
        for (let i = 0; i < dirty_components.length; i += 1) {
            const component = dirty_components[i];
            set_current_component(component);
            update(component.$$);
        }
        set_current_component(null);
        dirty_components.length = 0;
        while (binding_callbacks.length)
            binding_callbacks.pop()();
        // then, once components are updated, call
        // afterUpdate functions. This may cause
        // subsequent updates...
        for (let i = 0; i < render_callbacks.length; i += 1) {
            const callback = render_callbacks[i];
            if (!seen_callbacks.has(callback)) {
                // ...so guard against infinite loops
                seen_callbacks.add(callback);
                callback();
            }
        }
        render_callbacks.length = 0;
    } while (dirty_components.length);
    while (flush_callbacks.length) {
        flush_callbacks.pop()();
    }
    update_scheduled = false;
    flushing = false;
    seen_callbacks.clear();
}
function update($$) {
    if ($$.fragment !== null) {
        $$.update();
        run_all($$.before_update);
        const dirty = $$.dirty;
        $$.dirty = [-1];
        $$.fragment && $$.fragment.p($$.ctx, dirty);
        $$.after_update.forEach(add_render_callback);
    }
}

let promise;
function wait() {
    if (!promise) {
        promise = Promise.resolve();
        promise.then(() => {
            promise = null;
        });
    }
    return promise;
}
function dispatch(node, direction, kind) {
    node.dispatchEvent(custom_event(`${direction ? 'intro' : 'outro'}${kind}`));
}
const outroing = new Set();
let outros;
function group_outros() {
    outros = {
        r: 0,
        c: [],
        p: outros // parent group
    };
}
function check_outros() {
    if (!outros.r) {
        run_all(outros.c);
    }
    outros = outros.p;
}
function transition_in(block, local) {
    if (block && block.i) {
        outroing.delete(block);
        block.i(local);
    }
}
function transition_out(block, local, detach, callback) {
    if (block && block.o) {
        if (outroing.has(block))
            return;
        outroing.add(block);
        outros.c.push(() => {
            outroing.delete(block);
            if (callback) {
                if (detach)
                    block.d(1);
                callback();
            }
        });
        block.o(local);
    }
}
const null_transition = { duration: 0 };
function create_bidirectional_transition(node, fn, params, intro) {
    let config = fn(node, params);
    let t = intro ? 0 : 1;
    let running_program = null;
    let pending_program = null;
    let animation_name = null;
    function clear_animation() {
        if (animation_name)
            delete_rule(node, animation_name);
    }
    function init(program, duration) {
        const d = program.b - t;
        duration *= Math.abs(d);
        return {
            a: t,
            b: program.b,
            d,
            duration,
            start: program.start,
            end: program.start + duration,
            group: program.group
        };
    }
    function go(b) {
        const { delay = 0, duration = 300, easing = identity, tick = noop$1, css } = config || null_transition;
        const program = {
            start: now() + delay,
            b
        };
        if (!b) {
            // @ts-ignore todo: improve typings
            program.group = outros;
            outros.r += 1;
        }
        if (running_program || pending_program) {
            pending_program = program;
        }
        else {
            // if this is an intro, and there's a delay, we need to do
            // an initial tick and/or apply CSS animation immediately
            if (css) {
                clear_animation();
                animation_name = create_rule(node, t, b, duration, delay, easing, css);
            }
            if (b)
                tick(0, 1);
            running_program = init(program, duration);
            add_render_callback(() => dispatch(node, b, 'start'));
            loop(now => {
                if (pending_program && now > pending_program.start) {
                    running_program = init(pending_program, duration);
                    pending_program = null;
                    dispatch(node, running_program.b, 'start');
                    if (css) {
                        clear_animation();
                        animation_name = create_rule(node, t, running_program.b, running_program.duration, 0, easing, config.css);
                    }
                }
                if (running_program) {
                    if (now >= running_program.end) {
                        tick(t = running_program.b, 1 - t);
                        dispatch(node, running_program.b, 'end');
                        if (!pending_program) {
                            // we're done
                            if (running_program.b) {
                                // intro — we can tidy up immediately
                                clear_animation();
                            }
                            else {
                                // outro — needs to be coordinated
                                if (!--running_program.group.r)
                                    run_all(running_program.group.c);
                            }
                        }
                        running_program = null;
                    }
                    else if (now >= running_program.start) {
                        const p = now - running_program.start;
                        t = running_program.a + running_program.d * easing(p / running_program.duration);
                        tick(t, 1 - t);
                    }
                }
                return !!(running_program || pending_program);
            });
        }
    }
    return {
        run(b) {
            if (is_function(config)) {
                wait().then(() => {
                    // @ts-ignore
                    config = config();
                    go(b);
                });
            }
            else {
                go(b);
            }
        },
        end() {
            clear_animation();
            running_program = pending_program = null;
        }
    };
}

const globals = (typeof window !== 'undefined'
    ? window
    : typeof globalThis !== 'undefined'
        ? globalThis
        : global);

function destroy_block(block, lookup) {
    block.d(1);
    lookup.delete(block.key);
}
function outro_and_destroy_block(block, lookup) {
    transition_out(block, 1, 1, () => {
        lookup.delete(block.key);
    });
}
function update_keyed_each(old_blocks, dirty, get_key, dynamic, ctx, list, lookup, node, destroy, create_each_block, next, get_context) {
    let o = old_blocks.length;
    let n = list.length;
    let i = o;
    const old_indexes = {};
    while (i--)
        old_indexes[old_blocks[i].key] = i;
    const new_blocks = [];
    const new_lookup = new Map();
    const deltas = new Map();
    i = n;
    while (i--) {
        const child_ctx = get_context(ctx, list, i);
        const key = get_key(child_ctx);
        let block = lookup.get(key);
        if (!block) {
            block = create_each_block(key, child_ctx);
            block.c();
        }
        else if (dynamic) {
            block.p(child_ctx, dirty);
        }
        new_lookup.set(key, new_blocks[i] = block);
        if (key in old_indexes)
            deltas.set(key, Math.abs(i - old_indexes[key]));
    }
    const will_move = new Set();
    const did_move = new Set();
    function insert(block) {
        transition_in(block, 1);
        block.m(node, next);
        lookup.set(block.key, block);
        next = block.first;
        n--;
    }
    while (o && n) {
        const new_block = new_blocks[n - 1];
        const old_block = old_blocks[o - 1];
        const new_key = new_block.key;
        const old_key = old_block.key;
        if (new_block === old_block) {
            // do nothing
            next = new_block.first;
            o--;
            n--;
        }
        else if (!new_lookup.has(old_key)) {
            // remove old block
            destroy(old_block, lookup);
            o--;
        }
        else if (!lookup.has(new_key) || will_move.has(new_key)) {
            insert(new_block);
        }
        else if (did_move.has(old_key)) {
            o--;
        }
        else if (deltas.get(new_key) > deltas.get(old_key)) {
            did_move.add(new_key);
            insert(new_block);
        }
        else {
            will_move.add(old_key);
            o--;
        }
    }
    while (o--) {
        const old_block = old_blocks[o];
        if (!new_lookup.has(old_block.key))
            destroy(old_block, lookup);
    }
    while (n)
        insert(new_blocks[n - 1]);
    return new_blocks;
}

function get_spread_update(levels, updates) {
    const update = {};
    const to_null_out = {};
    const accounted_for = { $$scope: 1 };
    let i = levels.length;
    while (i--) {
        const o = levels[i];
        const n = updates[i];
        if (n) {
            for (const key in o) {
                if (!(key in n))
                    to_null_out[key] = 1;
            }
            for (const key in n) {
                if (!accounted_for[key]) {
                    update[key] = n[key];
                    accounted_for[key] = 1;
                }
            }
            levels[i] = n;
        }
        else {
            for (const key in o) {
                accounted_for[key] = 1;
            }
        }
    }
    for (const key in to_null_out) {
        if (!(key in update))
            update[key] = undefined;
    }
    return update;
}
function get_spread_object(spread_props) {
    return typeof spread_props === 'object' && spread_props !== null ? spread_props : {};
}

function bind(component, name, callback) {
    const index = component.$$.props[name];
    if (index !== undefined) {
        component.$$.bound[index] = callback;
        callback(component.$$.ctx[index]);
    }
}
function create_component(block) {
    block && block.c();
}
function mount_component(component, target, anchor) {
    const { fragment, on_mount, on_destroy, after_update } = component.$$;
    fragment && fragment.m(target, anchor);
    // onMount happens before the initial afterUpdate
    add_render_callback(() => {
        const new_on_destroy = on_mount.map(run).filter(is_function);
        if (on_destroy) {
            on_destroy.push(...new_on_destroy);
        }
        else {
            // Edge case - component was destroyed immediately,
            // most likely as a result of a binding initialising
            run_all(new_on_destroy);
        }
        component.$$.on_mount = [];
    });
    after_update.forEach(add_render_callback);
}
function destroy_component(component, detaching) {
    const $$ = component.$$;
    if ($$.fragment !== null) {
        run_all($$.on_destroy);
        $$.fragment && $$.fragment.d(detaching);
        // TODO null out other refs, including component.$$ (but need to
        // preserve final state?)
        $$.on_destroy = $$.fragment = null;
        $$.ctx = [];
    }
}
function make_dirty(component, i) {
    if (component.$$.dirty[0] === -1) {
        dirty_components.push(component);
        schedule_update();
        component.$$.dirty.fill(0);
    }
    component.$$.dirty[(i / 31) | 0] |= (1 << (i % 31));
}
function init(component, options, instance, create_fragment, not_equal, props, dirty = [-1]) {
    const parent_component = current_component;
    set_current_component(component);
    const prop_values = options.props || {};
    const $$ = component.$$ = {
        fragment: null,
        ctx: null,
        // state
        props,
        update: noop$1,
        not_equal,
        bound: blank_object(),
        // lifecycle
        on_mount: [],
        on_destroy: [],
        before_update: [],
        after_update: [],
        context: new Map(parent_component ? parent_component.$$.context : []),
        // everything else
        callbacks: blank_object(),
        dirty,
        skip_bound: false
    };
    let ready = false;
    $$.ctx = instance
        ? instance(component, prop_values, (i, ret, ...rest) => {
            const value = rest.length ? rest[0] : ret;
            if ($$.ctx && not_equal($$.ctx[i], $$.ctx[i] = value)) {
                if (!$$.skip_bound && $$.bound[i])
                    $$.bound[i](value);
                if (ready)
                    make_dirty(component, i);
            }
            return ret;
        })
        : [];
    $$.update();
    ready = true;
    run_all($$.before_update);
    // `false` as a special case of no DOM component
    $$.fragment = create_fragment ? create_fragment($$.ctx) : false;
    if (options.target) {
        if (options.hydrate) {
            const nodes = children(options.target);
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            $$.fragment && $$.fragment.l(nodes);
            nodes.forEach(detach);
        }
        else {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            $$.fragment && $$.fragment.c();
        }
        if (options.intro)
            transition_in(component.$$.fragment);
        mount_component(component, options.target, options.anchor);
        flush();
    }
    set_current_component(parent_component);
}
class SvelteComponent {
    $destroy() {
        destroy_component(this, 1);
        this.$destroy = noop$1;
    }
    $on(type, callback) {
        const callbacks = (this.$$.callbacks[type] || (this.$$.callbacks[type] = []));
        callbacks.push(callback);
        return () => {
            const index = callbacks.indexOf(callback);
            if (index !== -1)
                callbacks.splice(index, 1);
        };
    }
    $set($$props) {
        if (this.$$set && !is_empty($$props)) {
            this.$$.skip_bound = true;
            this.$$set($$props);
            this.$$.skip_bound = false;
        }
    }
}

const subscriber_queue = [];
/**
 * Creates a `Readable` store that allows reading by subscription.
 * @param value initial value
 * @param {StartStopNotifier}start start and stop notifications for subscriptions
 */
function readable(value, start) {
    return {
        subscribe: writable(value, start).subscribe
    };
}
/**
 * Create a `Writable` store that allows both updating and reading by subscription.
 * @param {*=}value initial value
 * @param {StartStopNotifier=}start start and stop notifications for subscriptions
 */
function writable(value, start = noop$1) {
    let stop;
    const subscribers = [];
    function set(new_value) {
        if (safe_not_equal(value, new_value)) {
            value = new_value;
            if (stop) { // store is ready
                const run_queue = !subscriber_queue.length;
                for (let i = 0; i < subscribers.length; i += 1) {
                    const s = subscribers[i];
                    s[1]();
                    subscriber_queue.push(s, value);
                }
                if (run_queue) {
                    for (let i = 0; i < subscriber_queue.length; i += 2) {
                        subscriber_queue[i][0](subscriber_queue[i + 1]);
                    }
                    subscriber_queue.length = 0;
                }
            }
        }
    }
    function update(fn) {
        set(fn(value));
    }
    function subscribe(run, invalidate = noop$1) {
        const subscriber = [run, invalidate];
        subscribers.push(subscriber);
        if (subscribers.length === 1) {
            stop = start(set) || noop$1;
        }
        run(value);
        return () => {
            const index = subscribers.indexOf(subscriber);
            if (index !== -1) {
                subscribers.splice(index, 1);
            }
            if (subscribers.length === 0) {
                stop();
                stop = null;
            }
        };
    }
    return { set, update, subscribe };
}
function derived(stores, fn, initial_value) {
    const single = !Array.isArray(stores);
    const stores_array = single
        ? [stores]
        : stores;
    const auto = fn.length < 2;
    return readable(initial_value, (set) => {
        let inited = false;
        const values = [];
        let pending = 0;
        let cleanup = noop$1;
        const sync = () => {
            if (pending) {
                return;
            }
            cleanup();
            const result = fn(single ? values[0] : values, set);
            if (auto) {
                set(result);
            }
            else {
                cleanup = is_function(result) ? result : noop$1;
            }
        };
        const unsubscribers = stores_array.map((store, i) => subscribe(store, (value) => {
            values[i] = value;
            pending &= ~(1 << i);
            if (inited) {
                sync();
            }
        }, () => {
            pending |= (1 << i);
        }));
        inited = true;
        sync();
        return function stop() {
            run_all(unsubscribers);
            cleanup();
        };
    });
}

var mergeObjects = (objects) => objects.reduce((prev, curr) => Object.assign(prev, curr), {});

// @ts-ignore
const UPDATE_VALUE = (updateValue) => ({ updateValue });
const DEFAULT_VALUE = (defaultValue) => ({ defaultValue });
const CUSTOM_STORE = (fn) => ({ store: fn });
// @ts-ignore
const DERIVED_STORE = (fn) => ({ store: (defaultValue, stores) => derived(...fn(stores)) });
const UNIQUE_DERIVED_STORE = (fn) => ({
    store: (defaultValue, stores) => {
        const [selectedStores, update, isEqual = () => false] = fn(stores);
        let isFirst = true;
        let currentValue;
        return derived(selectedStores, (storeValues, set) => {
            update(storeValues, (value) => {
                if (!isFirst && isEqual(currentValue, value))
                    return;
                currentValue = value;
                isFirst = false;
                set(value);
            });
        });
    },
});
const MAP_STORE = (fn) => ({
    store: (defaultValue, stores) => {
        const [valueMapper, paramStores = {}] = fn(stores);
        let storedItems = [];
        let $paramStores = {};
        const mapValue = (item) => valueMapper(item, $paramStores);
        // set default properties for each shape
        const setValue = (items) => {
            // was empty, still empty
            if (!storedItems.length && !items.length)
                return;
            // update value
            storedItems = items;
            updateValue();
        };
        const updateValue = () => {
            const mappedItems = storedItems.map(mapValue);
            storedItems = mappedItems;
            set(mappedItems);
        };
        Object.keys(paramStores).forEach((name) => {
            const store = paramStores[name];
            store.subscribe((value) => {
                $paramStores[name] = value;
                if (!value)
                    return;
                updateValue();
            });
        });
        const { subscribe, set } = writable(defaultValue);
        return {
            set: setValue,
            update: (fn) => setValue(fn(null)),
            subscribe,
        };
    },
});
const createStore = (accessors, stores, options) => {
    const { store = (defaultValue) => writable(defaultValue), defaultValue = noop, // should be a function returning the default value
    updateValue = undefined, } = options;
    // create our private store
    const storeInstance = store(defaultValue(), stores, accessors);
    const { subscribe, update = noop } = storeInstance; // update = noop because not all stores can be updated
    // on update private store
    let unsub;
    const onUpdate = (cb) => {
        let ignoreFirstCallback = true;
        if (unsub)
            unsub();
        unsub = subscribe((value) => {
            // need to ignore first callback because that returns current value
            if (ignoreFirstCallback)
                return (ignoreFirstCallback = false);
            // now we have the newly assigned value
            cb(value);
            unsub();
            unsub = undefined;
        });
    };
    // create the value updater function, needs access to stores so can read all store values
    const updateStoreValue = updateValue ? updateValue(accessors) : passthrough;
    // set and validate value
    storeInstance.set = (nextValue) => update((previousValue) => updateStoreValue(nextValue, previousValue, onUpdate));
    // set default value for external reference
    storeInstance.defaultValue = defaultValue;
    // expose store api
    return storeInstance;
};
var createStores = (props) => {
    const stores = {};
    const accessors = {};
    props.forEach(([name, ...options]) => {
        const opts = mergeObjects(options);
        const store = (stores[name] = createStore(accessors, stores, opts));
        const property = {
            get: () => get_store_value(store),
            set: store.set,
        };
        Object.defineProperty(accessors, name, property);
    });
    return {
        stores,
        accessors,
    };
};

var props = [
    // io
    ['src'],
    ['imageReader'],
    ['imageWriter'],
    // current images
    ['images', DEFAULT_VALUE(() => [])],
];

var pubsub = () => {
    let subs = [];
    return {
        sub: (event, callback) => {
            subs.push({ event, callback });
            return () => (subs = subs.filter((subscriber) => subscriber.event !== event || subscriber.callback !== callback));
        },
        pub: (event, value) => {
            subs
                .filter((sub) => sub.event === event)
                .forEach((sub) => sub.callback(value));
        }
    };
};

var capitalizeFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1);

var defineMethods = (object, api) => {
    Object.keys(api).forEach((name) => {
        const descriptor = isFunction(api[name])
            ? {
                value: api[name],
                writable: false,
            }
            : api[name];
        Object.defineProperty(object, name, descriptor);
    });
};

const PI = Math.PI;
const HALF_PI = Math.PI / 2;
const QUART_PI = HALF_PI / 2;

var fixPrecision = (value, precision = 12) => parseFloat(value.toFixed(precision));

const scalar = 10000;
var offsetRectToFitPolygon = (rect, poly) => {
    const polyLines = quadLines(poly);
    const offset = vectorCreateEmpty();
    const rectVertexes = rectGetCorners(rect);
    // we can fit it
    rectVertexes.forEach((vertex) => {
        // we update each corner by adding the current offset
        vectorAdd(vertex, offset);
        // test if point lies in polygon, if so, all is fine and we can exit
        if (polyPointIntersectionConvex(vertex, poly))
            return;
        polyLines.forEach((line) => {
            // get angle of edge and draw a ray from the corner perpendicular to the edge
            const a = Math.atan2(line.start.y - line.end.y, line.start.x - line.end.x);
            const x = Math.sin(Math.PI - a) * scalar;
            const y = Math.cos(Math.PI - a) * scalar;
            const ray = vectorCreate(vertex.x + x, vertex.y + y);
            // extend the poly line so even if we overshoot the polygon we hit it
            const lineExtended = lineExtend(lineClone(line), scalar);
            // get the resulting intersection (there's always an intersection)
            const intersection = lineLineIntersection(lineCreate(vertex, ray), lineExtended);
            // no intersection, no need to do anything
            if (!intersection)
                return;
            // update offset to move towards image
            vectorAdd(offset, vectorSubtract(vectorClone(intersection), vertex));
        });
    });
    // test if any vertexes still fall outside of poly, if so, we can't fit the rect
    const rectOffset = rectClone(rect);
    vectorAdd(rectOffset, offset);
    const rectOffsetVertices = rectGetCorners(rectOffset);
    const fits = rectOffsetVertices.every((vertex) => polyPointIntersectionConvex(vertex, poly));
    if (fits) {
        rectUpdateWithRect(rect, rectOffset);
        return true;
    }
    return false;
};

var limitCropRectToImage = (rect, poly) => {
    // get crop rect polygon vertexes
    const rectVertexes = rectGetCorners(rect);
    // if we end up here it doesn't fit, we might need to adjust
    const polyLines = quadLines(poly);
    const rectCenterPosition = rectCenter(rect);
    const intersections = [];
    rectVertexes.forEach((rectVertex) => {
        const ray = lineMultiply(lineCreate(vectorClone(rectCenterPosition), vectorClone(rectVertex)), 1000000);
        let intersectionFound = false;
        polyLines.forEach((line) => {
            const intersection = lineLineIntersection(ray, line);
            if (!intersection || intersectionFound)
                return;
            intersections.push(intersection);
            intersectionFound = true;
        });
    });
    // top left -> bottom right
    const tlbr = vectorDistance(intersections[0], intersections[2]);
    // top right -> bottom left
    const trbl = vectorDistance(intersections[1], intersections[3]);
    // calculate smallest rectangle we can make, use that
    const rectLimitedVertices = tlbr < trbl ? [intersections[0], intersections[2]] : [intersections[1], intersections[3]];
    const rectLimitedToImage = rectCreateFromPoints(rectLimitedVertices);
    // only use our fitted crop rectangle if it's smaller than our current rectangle,
    // this would mean that our current rectangle couldn't be moved to make it fit
    if (rectLimitedToImage.width < rect.width) {
        // need to center on previous rect
        rectUpdateWithRect(rect, rectLimitedToImage);
        return true;
    }
    return false;
};

var getImagePolygon = (image, imageRotation, imagePerspective = { x: 0, y: 0 }) => {
    const imageRect = rectCreateFromSize(image);
    const imageCenter = rectCenter(imageRect);
    const imagePoly = rectApplyPerspective(imageRect, imagePerspective, imageCenter).map((imageVertex) => vectorRotate(imageVertex, imageRotation, imageCenter));
    // get image poly bounds, we need this to offset the poly vertices from 0,0
    const imagePolyBounds = rectCreateFromPoints(imagePoly);
    // get image polygon vertexes
    return imagePoly.map((imageVertex) => vectorSubtract(imageVertex, imagePolyBounds));
};

var getMaxSizeInRect = (size, rotation = 0, aspectRatio = rectAspectRatio(size)) => {
    let width;
    let height;
    if (rotation !== 0) {
        const innerAngle = Math.atan2(1, aspectRatio);
        const rotationSigned = Math.sign(rotation) * rotation;
        const rotationSignedMod = rotationSigned % Math.PI;
        const rotationSignedModHalf = rotationSigned % HALF_PI;
        // determine if is turned on side
        let hyp;
        let r;
        if (rotationSignedMod > QUART_PI && rotationSignedMod < HALF_PI + QUART_PI) {
            r = rotationSignedModHalf > QUART_PI ? rotationSigned : HALF_PI - rotationSignedModHalf;
        }
        else {
            r = rotationSignedModHalf > QUART_PI ? HALF_PI - rotationSignedModHalf : rotationSigned;
        }
        hyp = Math.min(Math.abs(size.height / Math.sin(innerAngle + r)), Math.abs(size.width / Math.cos(innerAngle - r)));
        width = Math.cos(innerAngle) * hyp;
        height = width / aspectRatio;
    }
    else {
        width = size.width;
        height = width / aspectRatio;
        if (height > size.height) {
            height = size.height;
            width = height * aspectRatio;
        }
    }
    return sizeCreate(width, height);
};

var limitRectToImage = (rect, imageSize, imageRotation = 0, imagePerspective = vectorCreateEmpty(), minSize) => {
    // rotation and/or perspective, let's use the "advanced" collision detection method
    if ((isNumber(imageRotation) && imageRotation !== 0) ||
        imagePerspective.x ||
        imagePerspective.y) {
        const inputAspectRatio = rectAspectRatio(rect);
        // test if crop can fit image, if it can, offset the crop so it fits
        const imagePolygon = getImagePolygon(imageSize, imageRotation, imagePerspective);
        const maxSizeInRect = getMaxSizeInRect(imageSize, imageRotation, inputAspectRatio);
        const canFit = rect.width < maxSizeInRect.width && rect.height < maxSizeInRect.height;
        if (!canFit) {
            const dx = rect.width * 0.5 - maxSizeInRect.width * 0.5;
            const dy = rect.height * 0.5 - maxSizeInRect.height * 0.5;
            // adjust crop rect to max size
            if (rect.width > maxSizeInRect.width) {
                rect.width = maxSizeInRect.width;
                rect.x += dx;
            }
            if (rect.height > maxSizeInRect.height) {
                rect.height = maxSizeInRect.height;
                rect.y += dy;
            }
            // test if has exceeded min size, if so we need to limit the size and recalculate the other edge
            /*
                        -\
                       /  ---\
                      h2      ---\
                     /            ---\
                    +--------w---------+\
                   /|                  | ---\
                  / |                  |     ---\
                 /  |                  |         ---\
                /   |                  |             --
               h1   |                  |             /
              /     |                  |            /
             /      |                  |           /
            -\      |                  |          /
              ---\  |                  |         /
                  --+------------------+        /
                      ---\                     /
                          --\                 /
                             ---\            /
                                 ---\       /
                                     ---\  /
                                         --
            */
        }
        offsetRectToFitPolygon(rect, imagePolygon);
        const wasLimited = limitCropRectToImage(rect, imagePolygon);
        // this makes sure that after limiting the size, the crop rect is moved to a position that is inside the image
        if (wasLimited)
            offsetRectToFitPolygon(rect, imagePolygon);
    }
    // no rotation, no perspective, use simple bounds method
    else {
        // remember intended aspect ratio so we can try and recreate it
        let intendedAspectRatio = rectAspectRatio(rect);
        // limit to image size first, can never exceed that
        rect.width = Math.min(rect.width, imageSize.width);
        rect.height = Math.min(rect.height, imageSize.height);
        // reposition rect so it's always inside image bounds
        rect.x = Math.max(rect.x, 0);
        if (rect.x + rect.width > imageSize.width) {
            rect.x -= rect.x + rect.width - imageSize.width;
        }
        rect.y = Math.max(rect.y, 0);
        if (rect.y + rect.height > imageSize.height) {
            rect.y -= rect.y + rect.height - imageSize.height;
        }
        // we get the center of the current rect so we can center the contained rect to it
        const intendedCenter = rectCenter(rect);
        // make sure still adheres to aspect ratio
        const containedRect = rectContainRect(rect, intendedAspectRatio);
        containedRect.width = Math.max(minSize.width, containedRect.width);
        containedRect.height = Math.max(minSize.height, containedRect.height);
        containedRect.x = intendedCenter.x - containedRect.width * 0.5;
        containedRect.y = intendedCenter.y - containedRect.height * 0.5;
        rectUpdateWithRect(rect, containedRect);
    }
};

var applyCropRectAction = (cropRectPrevious, cropRectNext, imageSize, imageRotation, imagePerspective, cropLimitToImageBounds, cropMinSize, cropMaxSize) => {
    // clone
    const minSize = sizeClone(cropMinSize);
    // set upper bounds to crop max size
    const maxSize = sizeClone(cropMaxSize);
    // limit max size (more important that min size is respected so first limit max size)
    const maxScalar = fixPrecision(Math.max(cropRectNext.width / maxSize.width, cropRectNext.height / maxSize.height));
    const minScalar = fixPrecision(Math.min(cropRectNext.width / minSize.width, cropRectNext.height / minSize.height));
    // clone for resulting crop rect
    const cropRectOut = rectClone(cropRectNext);
    //
    // if exceeds min or max scale correct next crop rectangle to conform to bounds
    //
    if (minScalar < 1 || maxScalar > 1) {
        // center of both previous and next crop rects
        const previousCropRectCenter = rectCenter(cropRectPrevious);
        const nextCropRectCenter = rectCenter(cropRectNext);
        // calculate scales
        const scalar = minScalar < 1 ? minScalar : maxScalar;
        const cx = (nextCropRectCenter.x + previousCropRectCenter.x) / 2;
        const cy = (nextCropRectCenter.y + previousCropRectCenter.y) / 2;
        const cw = cropRectOut.width / scalar;
        const ch = cropRectOut.height / scalar;
        rectUpdate(cropRectOut, cx - cw * 0.5, cy - ch * 0.5, cw, ch);
    }
    // no need to limit to bounds, let's go!
    if (!cropLimitToImageBounds)
        return {
            crop: cropRectOut,
        };
    //
    // make sure the crop is made inside the bounds of the image
    //
    limitRectToImage(cropRectOut, imageSize, imageRotation, imagePerspective, minSize);
    return {
        crop: cropRectOut,
    };
};

var getBaseCropRect = (imageSize, transformedCropRect, imageRotation, imagePerspective = { x: 0, y: 0 }) => {
    const imageRect = rectCreateFromSize(imageSize);
    const imageCenter = rectCenter(imageRect);
    const imageTransformedVertices = rectRotate(imageRect, imageRotation, imageCenter);
    // get the rotated image bounds center (offset isn't relevant as crop is relative to top left image position)
    const imageRotatedBoundsCenter = rectCenter(rectNormalizeOffset(rectCreateFromPoints(imageTransformedVertices)));
    // get the center of the crop inside the rotated image
    const cropCenterInTransformedImage = rectCenter(transformedCropRect);
    // invert the rotation of the crop center around the rotated image center
    const deRotatedCropCenter = vectorRotate(cropCenterInTransformedImage, -imageRotation, imageRotatedBoundsCenter);
    // calculate crop distance from rotated image center
    const cropFromCenterOfTransformedImage = vectorSubtract(deRotatedCropCenter, imageRotatedBoundsCenter);
    // calculate original crop offset (from untransformed image)
    const originalCropCenterOffset = vectorApply(vectorAdd(imageCenter, cropFromCenterOfTransformedImage), fixPrecision);
    return rectCreate(originalCropCenterOffset.x - transformedCropRect.width * 0.5, originalCropCenterOffset.y - transformedCropRect.height * 0.5, transformedCropRect.width, transformedCropRect.height);
};

var clamp = (value, min, max) => Math.max(min, Math.min(value, max));

var applyRotationAction = (imageRotationPrevious, imageRotation, imageRotationRange, cropRect, imageSize, imagePerspective, cropLimitToImageBounds, cropRectOrigin, cropMinSize, cropMaxSize) => {
    // clone
    const minSize = sizeClone(cropMinSize);
    // set upper bounds to crop max size if image is bigger than max size,
    //  else if should limit to image bounds use image size as limit
    const maxSize = cropLimitToImageBounds && sizeContains(imageSize, cropMaxSize)
        ? sizeClone(cropMaxSize)
        : imageSize;
    let didAttemptDoubleTurn = false;
    const rotate = (rotationPrevious, rotation) => {
        // get the base crop rect (position of crop rect in untransformed image)
        // if we have the base crop rect we can apply the new rotation to it
        const cropRectBase = getBaseCropRect(imageSize, cropRect, rotationPrevious, null);
        // calculate transforms based on new rotation and base crop rect
        const imageRect = rectCreateFromSize(imageSize);
        const imageCenter = rectCenter(imageRect);
        const imageTransformedCorners = rectApplyPerspective(imageRect, imagePerspective, imageCenter);
        // need this to correct for perspective centroid displacement
        const perspectiveOffset = vectorSubtract(vectorClone(imageCenter), quadCentroid(imageTransformedCorners));
        // rotate around center of image
        const cropCenter = vectorRotate(rectCenter(cropRectBase), rotation, imageCenter);
        const rotateCropOffset = vectorSubtract(vectorClone(imageCenter), cropCenter);
        // get center of image bounds and move to correct position
        imageTransformedCorners.forEach((imageVertex) => vectorRotate(imageVertex, rotation, imageCenter));
        const imageBoundsRect = rectCreateFromPoints(imageTransformedCorners);
        const imageCentroid = quadCentroid(imageTransformedCorners);
        const cropOffset = vectorAdd(vectorSubtract(vectorSubtract(imageCentroid, rotateCropOffset), imageBoundsRect), perspectiveOffset);
        // create output cropRect
        const cropRectOut = rectCreate(cropOffset.x - cropRectBase.width * 0.5, cropOffset.y - cropRectBase.height * 0.5, cropRectBase.width, cropRectBase.height);
        // if has size target, scale croprect to target size
        if (cropRectOrigin) {
            rectScale(cropRectOut, cropRectOrigin.width / cropRectOut.width);
        }
        // if should limit to image bounds
        if (cropLimitToImageBounds) {
            const imagePoly = getImagePolygon(imageSize, rotation, imagePerspective);
            // offsetRectToFitPolygon(cropRectOut, imagePoly);
            // commenting this fixes poly sliding problem when adjusting rotation
            limitCropRectToImage(cropRectOut, imagePoly);
        }
        //#region if exceeds min or max adjust rotation to conform to bounds
        const minScalar = Math.min(cropRectOut.width / minSize.width, cropRectOut.height / minSize.height);
        const maxScalar = Math.max(cropRectOut.width / maxSize.width, cropRectOut.height / maxSize.height);
        if (minScalar < 1 || maxScalar > 1) {
            // determine if is full image turn
            const isTurn = fixPrecision(Math.abs(rotation - rotationPrevious)) === fixPrecision(Math.PI / 2);
            // try another turn if is turning image
            if (isTurn && !didAttemptDoubleTurn) {
                didAttemptDoubleTurn = true;
                return rotate(imageRotationPrevious, imageRotationPrevious + Math.sign(rotation - rotationPrevious) * Math.PI);
            }
        }
        //#endregion
        return {
            rotation,
            crop: cropRectOut,
        };
    };
    // amount of turns applied, we need this to correctly determine the allowed rotation range
    const imageTurns = Math.sign(imageRotation) * Math.round(Math.abs(imageRotation) / HALF_PI) * HALF_PI;
    const imageRotationClamped = clamp(imageRotation, imageTurns + imageRotationRange[0], imageTurns + imageRotationRange[1]);
    // set new crop position
    return rotate(imageRotationPrevious, imageRotationClamped);
};

const ORDERED_STATE_PROPS = [
    // requirements
    'cropLimitToImage',
    'cropMinSize',
    'cropMaxSize',
    'cropAspectRatio',
    // selection -> flip -> rotate -> perspective -> crop
    'flipX',
    'flipY',
    'rotation',
    'perspectiveX',
    'perspectiveY',
    'crop',
    // effects
    'colorMatrix',
    'convolutionMatrix',
    'gamma',
    'noise',
    'vignette',
    // shapes
    'annotation',
    'decoration',
    // other
    'backgroundColor',
    'targetSize',
];
const clone = (value) => {
    if (isArray(value)) {
        return value.map(clone);
    }
    else if (isObject(value)) {
        return { ...value };
    }
    return value;
};
/* TODO: keep this state outside of the shape array? */
const filterShapeState = (shapes) => shapes.forEach((shape) => {
    delete shape.isFormatted;
    delete shape.isComplete;
    delete shape.isError;
    delete shape.isLoading;
});
var stateStore = (defaultValue, stores, accessors) => {
    const observedStores = ORDERED_STATE_PROPS.map((key) => stores[key]);
    // can only subscribe, setting is done directly through store accessors
    // @ts-ignore
    const { subscribe } = derived(observedStores, (values, set) => {
        const state = ORDERED_STATE_PROPS.reduce((prev, curr, i) => {
            prev[curr] = clone(values[i]);
            return prev;
        }, {});
        // round crop if defined
        state.crop && rectApply(state.crop, Math.round);
        // remove state props from decoration and annotation
        state.annotation && filterShapeState(state.annotation);
        state.decoration && filterShapeState(state.decoration);
        set(state);
    });
    const setState = (state) => {
        // requires at least some state to be supplied
        if (!state)
            return;
        // make sure crop origin is reset
        accessors.cropOrigin = undefined;
        // apply new values
        ORDERED_STATE_PROPS
            // remove keys that weren't set
            .filter((key) => state.hasOwnProperty(key))
            // apply each key in order
            .forEach((key) => {
            accessors[key] = clone(state[key]);
        });
    };
    return {
        set: setState,
        update: (fn) => setState(fn(null)),
        subscribe,
    };
};

var toNumericAspectRatio = (v) => {
    if (!v)
        return undefined;
    if (/:/.test(v)) {
        const [w, h] = v.split(':');
        return w / h;
    }
    return parseFloat(v);
};

var arrayEqual = (a, b) => {
    if (a.length !== b.length)
        return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i])
            return false;
    }
    return true;
};

let result$5 = null;
var isAndroid = () => {
    if (result$5 === null)
        result$5 = isUserAgent(/Android/);
    return result$5;
};

let result$6 = null;
var isFirefox = () => {
    if (result$6 === null)
        result$6 = isUserAgent(/Firefox/);
    return result$6;
};

let result$7 = null;
var isEdge = () => {
    if (result$7 === null)
        result$7 = isUserAgent(/Edge/);
    return result$7;
};

let vendor;
var getBrowserVendor = () => {
    if (vendor)
        return vendor;
    vendor = isIOS()
        ? 'ios'
        : isAndroid()
            ? 'android'
            : isFirefox()
                ? 'firefox'
                : isInternetExplorer()
                    ? 'ie'
                    : isEdge()
                        ? 'edge'
                        : 'chrome';
    return vendor;
};

// https://github.com/jhildenbiddle/canvas-size#test-results
let maxCanvasSze;
var getMaxCanvasSize = () => {
    if (!maxCanvasSze) {
        const sizes = {
            android: [32767, 32767, 10836],
            ios: [4194303, 8388607, 16777216],
            chrome: [32767, 32767, 268435456],
            firefox: [32767, 32767, 124992400],
            edge: [16384, 16384, 268435456],
            ie: [16384, 16384, 67108864],
        };
        maxCanvasSze = sizes[getBrowserVendor()] || sizes['chrome'];
    }
    const [width, height, density] = maxCanvasSze;
    return {
        width,
        height,
        density,
    };
};

var isRotatedSideways = (a) => {
    const rotationLimited = Math.abs(a) % Math.PI;
    return rotationLimited > QUART_PI && rotationLimited < Math.PI - QUART_PI;
};

var padColorArray = (color = [0, 0, 0, 0], opacity = 1.0) => color.length === 4 ? color : [...color, opacity];

//
// constants
//
const MIN_ROTATION = -QUART_PI;
const MAX_ROTATION = QUART_PI;
//
// updater methods
//
const updateCropRect = (props) => (cropNext, cropPrevious = cropNext) => {
    // wait for image to load
    const { loadState, size, cropMinSize, cropMaxSize, cropLimitToImage, rotation, perspective, } = props;
    // image hasn't loaded yet, use supplied crop rect
    if (!cropNext || !loadState || !loadState.beforeComplete)
        return cropNext;
    // apply the action
    const res = applyCropRectAction(cropPrevious, cropNext, size, rotation, perspective, cropLimitToImage, cropMinSize, cropMaxSize);
    const cropOut = rectApply(res.crop, fixPrecision);
    return cropOut;
};
const updateCropAspectRatio = (props) => (aspectRatioNext) => {
    const { loadState, crop, size } = props;
    const aspectRatio = toNumericAspectRatio(aspectRatioNext);
    // no aspect ratio means custom aspect ratio so set to undefined
    if (!aspectRatio)
        return undefined;
    // can't update crop if not defined yet
    if (!loadState || !loadState.beforeComplete)
        return aspectRatio;
    // get center position of crop
    const cropCenter = vectorApply(rectCenter(crop), Math.round);
    const imageCenter = vectorApply(sizeCenter(size), Math.round);
    if (cropCenter.x === imageCenter.x && cropCenter.y === imageCenter.y) {
        props.crop = rectApply(rectContainRect(rectCreateFromSize(size), aspectRatioNext), fixPrecision);
    }
    else {
        const cropSize = {
            width: crop.height * aspectRatio,
            height: crop.height,
        };
        const tx = (crop.width - cropSize.width) * 0.5;
        const ty = (crop.height - cropSize.height) * 0.5;
        props.crop = rectApply(rectCreate(crop.x + tx, crop.y + ty, cropSize.width, cropSize.height), fixPrecision);
    }
    return aspectRatio;
};
const updateCropLimitToImage = (props) => (limitToImageNext, limitToImagePrevious, onUpdate) => {
    // if was not limiting previously and now set limiting make sure crop fits bounds
    if (!limitToImagePrevious && limitToImageNext) {
        onUpdate(() => (props.crop = rectClone(props.crop)));
    }
    return limitToImageNext;
};
const updateRotation = (props) => (rotationNext, rotationPrevious, onUpdate) => {
    // when image rotation is updated we need to adjust the
    // cropRect offset so rotation happens from cropRect center
    // no change
    if (rotationNext === rotationPrevious)
        return rotationNext;
    // get relevant data from store state
    const { loadState, size, rotationRange, cropMinSize, cropMaxSize, crop, perspective, cropLimitToImage, cropOrigin, } = props;
    // image not ready, exit!
    if (!loadState || !loadState.beforeComplete)
        return rotationNext;
    // get new state
    const res = applyRotationAction(rotationPrevious, rotationNext, rotationRange, crop, size, perspective, cropLimitToImage, cropOrigin, cropMinSize, cropMaxSize);
    // return validated rotation value, then, after we assign that value, we update the crop rect
    // we may only call onUpdate if a change was made
    onUpdate(() => {
        props.crop = rectApply(res.crop, fixPrecision);
    });
    // return result rotation (might have been rotated twice to fit crop rectangle)
    return res.rotation;
};
// updates the range of valid rotation input
const updateRotationRange = (imageSize, imageIsRotatedSideways, cropMinSize, cropSize) => {
    /*
    - 'a' is angle between diagonal and image height
    - 'b' is angle between diagonal and crop height
    - 'c' is angle between diagonal and image width
    - resulting range is then a - b
    +----------/\------------------------+
    |         / \ \                      |
    |        /   \  \                    |
    |       /     \   \                  |
    |       \      \    \                |
    |         \     \     \              |
    |           \    \      \            |
    |             \   \       /          |
    |               \  \     /           |
    |                 \ \   /            |
    |                   \\a/b            |
    +---------------------\--------------+
    */
    const scalar = Math.max(cropMinSize.width / cropSize.width, cropMinSize.height / cropSize.height);
    const minSize = sizeCreate(cropSize.width * scalar, cropSize.height * scalar);
    // the hypotenus is the length of the diagonal of the min crop size
    const requiredSpace = sizeHypotenuse(minSize);
    // minimum space available in horizontal / vertical direction
    const availableSpace = Math.min(imageSize.width, imageSize.height);
    // if there's enough space available, we can return the max range
    if (requiredSpace < availableSpace)
        return [MIN_ROTATION, MAX_ROTATION];
    // can't fully rotate the image
    // if the image is turned we need to swap the width and height
    const imageWidth = imageIsRotatedSideways ? imageSize.height : imageSize.width;
    const imageHeight = imageIsRotatedSideways ? imageSize.width : imageSize.height;
    // subtracting the angle between the hypotenuse and the crop itself
    const a = Math.acos(minSize.height / requiredSpace);
    const b = Math.acos(imageHeight / requiredSpace);
    const c = Math.asin(imageWidth / requiredSpace);
    const rangeHorizontal = a - b;
    const rangeVertical = c - a;
    // range is not a number, it means we can rotate as much as we want
    if (Number.isNaN(rangeHorizontal) && Number.isNaN(rangeVertical))
        return [MIN_ROTATION, MAX_ROTATION];
    // get minimum range
    const range = Number.isNaN(rangeHorizontal)
        ? rangeVertical
        : Number.isNaN(rangeVertical)
            ? rangeHorizontal
            : Math.min(rangeHorizontal, rangeVertical);
    // if not, limit range to min and max rotation
    const rangeMin = Math.max(-range, MIN_ROTATION);
    const rangeMax = Math.min(range, MAX_ROTATION);
    return [rangeMin, rangeMax];
};
// updates the range of valid crop rectangle input
const updateCropRange = (imageSize, rotation, cropAspectRatio, cropMinSize, cropMaxSize, cropLimitToImage) => {
    // ! rotation doesn't affect min size, only max size
    // set lower bounds to crop min size
    let minSize = sizeClone(cropMinSize);
    // set upper bounds to crop max size
    let maxSize = sizeClone(cropMaxSize);
    // now we got basic bounds, let's see if we should limit to the image bounds, else we done
    if (!cropLimitToImage)
        return [minSize, maxSize];
    return [minSize, sizeApply(getMaxSizeInRect(imageSize, rotation, cropAspectRatio), Math.round)];
};
const prepareShape = (shape, options) => {
    // only auto-format once
    if (!shape.isFormatted) {
        shape = shapeFormat(shape);
        shape.isFormatted = true;
    }
    // we need to make sure shape is still correctly positioned relative to parent
    // draft cannot be relative
    // if context changed
    // if has left top right or bottom
    if (!shape.isDraft &&
        shapeHasRelativeSize(shape) &&
        (!shape.context || !rectEqual(options.context, shape.context))) {
        shape = shapeComputeRect(shape, options.context);
        shape.context = { ...options.context };
    }
    return shape;
};
var imageProps = [
    // image info received from read
    ['file'],
    ['size'],
    // loading and processing state
    ['loadState'],
    ['processState'],
    // derived info
    [
        'aspectRatio',
        DERIVED_STORE(({ size }) => [
            size,
            ($size) => ($size ? rectAspectRatio($size) : undefined),
        ]),
    ],
    // image modifications
    ['perspectiveX', DEFAULT_VALUE(() => 0)],
    ['perspectiveY', DEFAULT_VALUE(() => 0)],
    [
        'perspective',
        DERIVED_STORE(({ perspectiveX, perspectiveY }) => [
            [perspectiveX, perspectiveY],
            ([x, y]) => ({ x, y }),
        ]),
    ],
    ['rotation', DEFAULT_VALUE(() => 0), UPDATE_VALUE(updateRotation)],
    ['flipX', DEFAULT_VALUE(() => false)],
    ['flipY', DEFAULT_VALUE(() => false)],
    ['flip', DERIVED_STORE(({ flipX, flipY }) => [[flipX, flipY], ([x, y]) => ({ x, y })])],
    [
        'isRotatedSideways',
        UNIQUE_DERIVED_STORE(({ rotation }) => [
            [rotation],
            ([$rotation], set) => set(isRotatedSideways($rotation)),
            (prevValue, nextValue) => prevValue !== nextValue,
        ]),
    ],
    ['crop', UPDATE_VALUE(updateCropRect)],
    ['cropAspectRatio', UPDATE_VALUE(updateCropAspectRatio)],
    ['cropOrigin'],
    ['cropMinSize', DEFAULT_VALUE(() => ({ width: 1, height: 1 }))],
    ['cropMaxSize', DEFAULT_VALUE(() => sizeClone(getMaxCanvasSize()))],
    ['cropLimitToImage', DEFAULT_VALUE(() => true), UPDATE_VALUE(updateCropLimitToImage)],
    [
        'cropSize',
        UNIQUE_DERIVED_STORE(({ crop }) => [
            [crop],
            ([$crop], set) => {
                if (!$crop)
                    return;
                set(sizeCreate($crop.width, $crop.height));
            },
            // if is same as previous size, don't trigger update (happens when updating only the crop offset)
            (prevValue, nextValue) => sizeEqual(prevValue, nextValue),
        ]),
    ],
    [
        'cropRectAspectRatio',
        DERIVED_STORE(({ cropSize }) => [
            [cropSize],
            ([$cropSize], set) => {
                if (!$cropSize)
                    return;
                set(fixPrecision(rectAspectRatio($cropSize), 5));
            },
        ]),
    ],
    [
        'cropRange',
        UNIQUE_DERIVED_STORE(({ size, rotation, cropRectAspectRatio, cropMinSize, cropMaxSize, cropLimitToImage, }) => [
            [size, rotation, cropRectAspectRatio, cropMinSize, cropMaxSize, cropLimitToImage],
            ([$size, $rotation, $cropRectAspectRatio, $cropMinSize, $cropMaxSize, $cropLimitToImage,], set) => {
                // wait for image size
                if (!$size)
                    return;
                const range = updateCropRange($size, $rotation, $cropRectAspectRatio, $cropMinSize, $cropMaxSize, $cropLimitToImage);
                set(range);
            },
            // if is same range as previous range, don't trigger update
            (prevRange, nextRange) => arrayEqual(prevRange, nextRange),
        ]),
    ],
    [
        'rotationRange',
        UNIQUE_DERIVED_STORE(({ size, isRotatedSideways, cropMinSize, cropSize }) => [
            [size, isRotatedSideways, cropMinSize, cropSize],
            ([$size, $isRotatedSideways, $cropMinSize, $cropSize], set) => {
                // wait for image size
                if (!$size || !$cropSize)
                    return;
                const range = updateRotationRange($size, $isRotatedSideways, $cropMinSize, $cropSize);
                set(range);
            },
            // if is same range as previous range, don't trigger update
            (prevRange, nextRange) => arrayEqual(prevRange, nextRange),
        ]),
    ],
    // canvas
    ['backgroundColor', UPDATE_VALUE(() => (color) => padColorArray(color))],
    // size
    ['targetSize'],
    // effects
    ['colorMatrix'],
    ['convolutionMatrix'],
    ['gamma'],
    ['noise'],
    ['vignette'],
    // annotations live in image space
    [
        'annotation',
        MAP_STORE(({ size }) => [prepareShape, { context: size }]),
        DEFAULT_VALUE(() => []),
    ],
    // decorations live in crop space
    [
        'decoration',
        MAP_STORE(({ crop }) => [prepareShape, { context: crop }]),
        DEFAULT_VALUE(() => []),
    ],
    // state of image, used to restore a previous state or request the current state
    ['state', CUSTOM_STORE(stateStore)],
];

var process = async (value, chainTasks, chainOptions = {}, options) => {
    // options relevant to the process method itself
    const { ontaskstart, ontaskprogress, ontaskend, token } = options;
    // has been cancelled
    let cancelled = false;
    // set cancel handler method
    token.cancel = () => {
        // cancel called from outside of the process method
        cancelled = true;
    };
    // step through chain
    for (const [index, task] of chainTasks.entries()) {
        // exit when cancelled
        if (cancelled)
            return;
        // get the task function and the id so we can notify the callee of the task that is being started
        const [fn, id] = task;
        // start task
        ontaskstart(index, id);
        try {
            value = await fn(value, { ...chainOptions }, (event) => ontaskprogress(index, id, event));
        }
        catch (err) {
            // stop processing more items in the chain
            cancelled = true;
            // pass error back to parent
            throw err;
        }
        ontaskend(index, id);
    }
    return value;
};

var createImage = () => {
    // create default store
    const { stores, accessors } = createStores(imageProps);
    // pub/sub
    const { pub, sub } = pubsub();
    // processing handler
    const createProcessingHandler = (stateProp, eventKey) => {
        const getStore = () => accessors[stateProp] || {};
        const setStore = (obj) => (accessors[stateProp] = {
            ...getStore(),
            ...obj,
            timeStamp: Date.now(),
        });
        const hasError = () => getStore().error;
        const handleError = (error) => {
            if (hasError())
                return;
            setStore({
                error: error,
            });
            pub(`${eventKey}error`, { ...getStore() });
        };
        return {
            start() {
                pub(`${eventKey}start`);
            },
            onprogress(event) {
                if (hasError())
                    return;
                setStore({
                    progress: event.loaded / event.total,
                });
                pub(`${eventKey}progress`, { ...getStore() });
            },
            onabort() {
                pub(`${eventKey}abort`);
            },
            ontaskstart(index, id) {
                if (hasError())
                    return;
                setStore({
                    index,
                    task: id,
                    taskProgress: undefined,
                });
                pub(`${eventKey}taskstart`, { ...getStore() });
            },
            ontaskprogress(index, id, event) {
                if (hasError())
                    return;
                setStore({
                    index,
                    task: id,
                    taskProgress: event.loaded / event.total,
                });
                pub(`${eventKey}taskprogress`, { ...getStore() });
            },
            ontaskend(index, id) {
                if (hasError())
                    return;
                const prevProgress = getStore().taskProgress;
                const nextProgress = isNumber(prevProgress)
                    ? getStore().taskProgress === Infinity
                        ? Infinity
                        : 1
                    : undefined;
                setStore({
                    index,
                    task: id,
                    taskProgress: nextProgress,
                });
                // publish start
                pub(`${eventKey}taskend`, { ...getStore() });
            },
            ontaskerror(error) {
                handleError(error);
            },
            error(error) {
                handleError(error);
            },
            beforeComplete(data) {
                if (hasError())
                    return;
                setStore({ beforeComplete: true });
                pub(`before${eventKey}`, data);
            },
            complete(data) {
                if (hasError())
                    return;
                setStore({ complete: true });
                pub(eventKey, data);
            },
        };
    };
    //#region read image
    const read = (src, { reader }) => {
        // exit if no reader supplied
        if (!reader)
            return;
        // reset file data to undefined as we're loading a new image
        Object.assign(accessors, {
            file: undefined,
            size: undefined,
            loadState: undefined,
        });
        // our cancel token so we can abort load if needed, cancel will be set by process
        let imageReadToken = { cancel: noop };
        let imageReadCancelled = false;
        const imageReadHandler = createProcessingHandler('loadState', 'load');
        const processOptions = {
            token: imageReadToken,
            ...imageReadHandler,
        };
        const readerState = {
            src,
            size: undefined,
            dest: undefined,
        };
        const readerOptions = {};
        // start reading image
        (async () => {
            try {
                imageReadHandler.start();
                const output = (await process(readerState, reader, readerOptions, processOptions));
                // was cancelled
                if (imageReadCancelled)
                    return imageReadHandler.onabort();
                // get shortcuts for validation
                const { size, dest } = output || {};
                // if we don't have a size
                if (!size || !size.width || !size.height)
                    throw new DokaError('Image size not received', 'IMAGE_SIZE_MISSING', output);
                // update internal data
                Object.assign(accessors, {
                    size: size,
                    file: dest,
                });
                // set base crop rect if not already has crop defined
                if (!accessors.crop) {
                    accessors.crop = rectContainRect(rectCreateFromSize(size), accessors.cropAspectRatio);
                }
                // apply current image state in correct order,
                // there's a difference between the actions `rotate -> crop` and `crop -> rotate`
                // this makes sure the initial state is always `rotate -> crop`
                accessors.state = accessors.state;
                // before load complete
                imageReadHandler.beforeComplete(output);
                // done loading image
                imageReadHandler.complete(output);
            }
            catch (err) {
                imageReadHandler.error(err);
            }
            finally {
                imageReadToken = undefined;
            }
        })();
        // call to abort load
        return () => {
            imageReadCancelled = true;
            imageReadToken && imageReadToken.cancel();
        };
    };
    //#endregion
    //#region write image
    const write = (writer) => {
        // not ready to start processing
        if (!accessors.loadState.complete)
            return;
        // reset process state to undefined
        accessors.processState = undefined;
        // we need this token to be a blet to cancel the processing operation
        let imageWriteToken = { cancel: noop };
        const imageWriteHandler = createProcessingHandler('processState', 'process');
        const writerState = {
            src: accessors.file,
            imageState: accessors.state,
            dest: undefined,
        };
        const writerOptions = {};
        const processOptions = {
            token: imageWriteToken,
            ...imageWriteHandler,
        };
        if (!writer) {
            imageWriteHandler.start();
            imageWriteHandler.complete(writerState);
            return;
        }
        // start writing image
        (async () => {
            imageWriteHandler.start();
            try {
                const output = (await process(writerState, writer, writerOptions, processOptions));
                imageWriteHandler.complete(output);
            }
            catch (err) {
                imageWriteHandler.error(err);
            }
            finally {
                imageWriteToken = undefined;
            }
        })();
        // call to abort processing
        return () => imageWriteToken && imageWriteToken.cancel();
    };
    //#endregion
    //#region api
    defineMethods(accessors, {
        read,
        write,
        on: sub,
    });
    //#endregion
    // expose store API
    return {
        accessors,
        stores,
    };
};

// @ts-ignore
const editorEventsToBubble = [
    'loadstart',
    'loadabort',
    'loaderror',
    'loadprogress',
    'load',
    'processstart',
    'processabort',
    'processerror',
    'processprogress',
    'process',
];
const imagePrivateProps = [
    'flip',
    'cropOrigin',
    'isRotatedSideways',
    'perspective',
    'perspectiveX',
    'perspectiveY',
    'cropRange',
];
const editorPrivateProps = ['images'];
const getImagePropGroupedName = (prop) => `image${capitalizeFirstLetter(prop)}`;
const getEditorProps = () => {
    const imageProperties = getImagePublicPropNames(imageProps).map(getImagePropGroupedName);
    const editorProperties = props
        .map(([prop]) => prop)
        .filter((prop) => !editorPrivateProps.includes(prop));
    return imageProperties.concat(editorProperties);
};
const getImagePublicPropNames = (imageProps) => imageProps.map(([prop]) => prop).filter((prop) => !imagePrivateProps.includes(prop));
var createImageEditor = () => {
    // create default stores
    const { stores, accessors } = createStores(props);
    // set up pub/sub for the app layer
    const { sub, pub } = pubsub();
    const bubble = (name) => (value) => pub(name, value);
    // helper method
    const getImageObjSafe = () => (accessors.images ? accessors.images[0] : {});
    // create shortcuts to image props : `crop` -> `imageCrop`
    let initialImageProps = {};
    getImagePublicPropNames(imageProps).forEach((prop) => {
        Object.defineProperty(accessors, getImagePropGroupedName(prop), {
            get: () => {
                const image = getImageObjSafe();
                if (!image)
                    return;
                return image.accessors[prop];
            },
            set: (value) => {
                const image = getImageObjSafe();
                if (!image) {
                    initialImageProps[prop] = value;
                    return;
                }
                image.accessors[prop] = value;
            },
        });
    });
    // handling loading an image if a src is set
    const unsubSrc = stores.src.subscribe((src) => {
        // no image set, means clear active image
        if (!src)
            return (accessors.images = []);
        // exit here if we don't have an imageReader we'll wait for an imageReader to be defined
        if (!accessors.imageReader)
            return;
        // this is the first image so let's use `loadSrc` so we can also apply initial props
        if (!accessors.images.length)
            return loadSrc(src);
        loadImage(src);
    });
    const unsubReader = stores.imageReader.subscribe((reader) => {
        // can't do anything without an image reader
        if (!reader)
            return;
        // an image has already been loaded no need to load images that were set earlier
        if (accessors.images.length)
            return;
        // no image to load, we'll wait for images to be set to the `src` prop
        if (!accessors.src)
            return;
        // src is waiting to be loaded so let's pick it up,
        // push it back a tick so we know initialImageProps are set
        loadSrc(accessors.src);
    });
    // loads the initial source, differs from loadImage as this method picks up any set image props
    const loadSrc = (src) => Promise.resolve().then(() => loadImage(src, initialImageProps));
    // internal helper method to get active image
    const getImage = () => accessors.images && accessors.images[0];
    //#endregion
    //#region public method (note that these are also called from UI, name of method is name of dispatched event in UI)
    // load image, resolve when image is loaded
    let imageLoadAbort;
    const loadImage = (src, options = {}) => new Promise((resolve, reject) => {
        // if already has image, remove existing image
        if (getImage())
            removeImage();
        // access image props and stores
        const image = createImage();
        editorEventsToBubble.map((event) => image.accessors.on(event, bubble(event)));
        // done, clean up listeners
        const done = () => {
            initialImageProps = {};
            unsubReject();
            unsubResolve();
        };
        const unsubReject = image.accessors.on('loaderror', (error) => {
            done();
            reject(error);
        });
        image.accessors.on('beforeload', () => {
            // normalize all prefixed options (from imageRotation -> rotation)
            const normalizedOptions = getImagePublicPropNames(imageProps).reduce((prev, prop) => {
                // test if already has image prop (rotation)
                if (prop in options) {
                    prev[prop] = options[prop];
                    return prev;
                }
                // test if has prefixed image prop (imageRotation)
                else {
                    const groupedPropName = getImagePropGroupedName(prop);
                    if (groupedPropName in options) {
                        prev[prop] = options[groupedPropName];
                    }
                }
                return prev;
            }, {});
            // if set as initial props we apply in most logical order
            ['cropLimitToImage', 'crop', 'cropAspectRatio', 'rotation'].forEach((prop) => {
                if (!(prop in normalizedOptions))
                    return;
                // asign to `image`
                image.accessors[prop] = normalizedOptions[prop];
                // remove from normalizedOptions
                delete normalizedOptions[prop];
            });
            // assign remaining options to `image`
            Object.assign(image.accessors, normalizedOptions);
        });
        const unsubResolve = image.accessors.on('load', (output) => {
            imageLoadAbort = undefined;
            done();
            resolve(output);
        });
        // set new image
        accessors.images = [image];
        // assign passed options to editor accessors, we ignore 'src'
        if (options.imageReader)
            accessors.imageReader = options.imageReader;
        if (options.imageWriter)
            accessors.imageWriter = options.imageWriter;
        // start reading image
        imageLoadAbort = image.accessors.read(src, { reader: accessors.imageReader });
    });
    // start processing a loaded image, resolve when image is processed
    let imageProcessAbort;
    const processImage = (src, options) => {
        return new Promise(async (resolve, reject) => {
            // if src supplied, first load src, then process
            src && (await loadImage(src, options));
            // get current active image
            const image = getImage();
            // needs image for processing
            if (!image)
                return reject('no image');
            // done, clean up listeners
            const done = () => {
                imageProcessAbort = undefined;
                unsubReject();
                unsubResolve();
            };
            const unsubReject = image.accessors.on('processerror', (error) => {
                done();
                reject(error);
            });
            const unsubResolve = image.accessors.on('process', (output) => {
                done();
                resolve(output);
            });
            imageProcessAbort = image.accessors.write(accessors.imageWriter);
        });
    };
    const abortProcessImage = () => {
        const image = getImage();
        if (!image)
            return;
        if (imageProcessAbort)
            imageProcessAbort();
        image.accessors.processState = undefined;
    };
    // edit image, loads an image and resolve when image is processed
    const editImage = (src, options) => new Promise((resolve, reject) => {
        loadImage(src, options)
            .then(() => {
            // access image props and stores
            const { images } = accessors;
            const image = images[0];
            // done, clean up listeners
            const done = () => {
                unsubReject();
                unsubResolve();
            };
            const unsubReject = image.accessors.on('processerror', (error) => {
                done();
                reject(error);
            });
            const unsubResolve = image.accessors.on('process', (output) => {
                done();
                resolve(output);
            });
        })
            .catch(reject);
    });
    const removeImage = () => {
        // no images, nothing to remove
        const image = getImage();
        if (!image)
            return;
        // try to abort image load
        if (imageLoadAbort)
            imageLoadAbort();
        image.accessors.loadState = undefined;
        // clear images
        accessors.images = [];
    };
    //#endregion
    Object.defineProperty(accessors, 'stores', {
        get: () => stores,
    });
    //#region API
    defineMethods(accessors, {
        on: sub,
        loadImage,
        editImage,
        removeImage,
        processImage,
        abortProcessImage,
        destroy: () => {
            unsubSrc();
            unsubReader();
        },
    });
    return accessors;
    //#endregion
};

var getComponentExportedProps = (Component) => {
    const descriptors = Object.getOwnPropertyDescriptors(Component.prototype);
    return Object.keys(descriptors).filter((key) => !!descriptors[key]['get']);
};

function circOut(t) {
    return Math.sqrt(1 - --t * t);
}

function is_date(obj) {
    return Object.prototype.toString.call(obj) === '[object Date]';
}

function get_interpolator(a, b) {
    if (a === b || a !== a)
        return () => a;
    const type = typeof a;
    if (type !== typeof b || Array.isArray(a) !== Array.isArray(b)) {
        throw new Error('Cannot interpolate values of different type');
    }
    if (Array.isArray(a)) {
        const arr = b.map((bi, i) => {
            return get_interpolator(a[i], bi);
        });
        return t => arr.map(fn => fn(t));
    }
    if (type === 'object') {
        if (!a || !b)
            throw new Error('Object cannot be null');
        if (is_date(a) && is_date(b)) {
            a = a.getTime();
            b = b.getTime();
            const delta = b - a;
            return t => new Date(a + t * delta);
        }
        const keys = Object.keys(b);
        const interpolators = {};
        keys.forEach(key => {
            interpolators[key] = get_interpolator(a[key], b[key]);
        });
        return t => {
            const result = {};
            keys.forEach(key => {
                result[key] = interpolators[key](t);
            });
            return result;
        };
    }
    if (type === 'number') {
        const delta = b - a;
        return t => a + t * delta;
    }
    throw new Error(`Cannot interpolate ${type} values`);
}
function tweened(value, defaults = {}) {
    const store = writable(value);
    let task;
    let target_value = value;
    function set(new_value, opts) {
        if (value == null) {
            store.set(value = new_value);
            return Promise.resolve();
        }
        target_value = new_value;
        let previous_task = task;
        let started = false;
        let { delay = 0, duration = 400, easing = identity, interpolate = get_interpolator } = assign(assign({}, defaults), opts);
        if (duration === 0) {
            if (previous_task) {
                previous_task.abort();
                previous_task = null;
            }
            store.set(value = target_value);
            return Promise.resolve();
        }
        const start = now() + delay;
        let fn;
        task = loop(now => {
            if (now < start)
                return true;
            if (!started) {
                fn = interpolate(value, new_value);
                if (typeof duration === 'function')
                    duration = duration(value, new_value);
                started = true;
            }
            if (previous_task) {
                previous_task.abort();
                previous_task = null;
            }
            const elapsed = now - start;
            if (elapsed > duration) {
                store.set(value = new_value);
                return false;
            }
            // @ts-ignore
            store.set(value = fn(easing(elapsed / duration)));
            return true;
        });
        return task.promise;
    }
    return {
        set,
        update: (fn, opts) => set(fn(target_value, value), opts),
        subscribe: store.subscribe
    };
}

function tick_spring(ctx, last_value, current_value, target_value) {
    if (typeof current_value === 'number') {
        // @ts-ignore
        const delta = target_value - current_value;
        // @ts-ignore
        const velocity = (current_value - last_value) / (ctx.dt || 1 / 60); // guard div by 0
        const spring = ctx.opts.stiffness * delta;
        const damper = ctx.opts.damping * velocity;
        const acceleration = (spring - damper) * ctx.inv_mass;
        const d = (velocity + acceleration) * ctx.dt;
        if (Math.abs(d) < ctx.opts.precision && Math.abs(delta) < ctx.opts.precision) {
            return target_value; // settled
        }
        else {
            ctx.settled = false; // signal loop to keep ticking
            // @ts-ignore
            return current_value + d;
        }
    }
    else if (isArray(current_value)) {
        // @ts-ignore
        return current_value.map((_, i) => tick_spring(ctx, last_value[i], current_value[i], target_value[i]));
    }
    else if (typeof current_value === 'object') {
        const next_value = {};
        // @ts-ignore
        for (const k in current_value) {
            // @ts-ignore
            next_value[k] = tick_spring(ctx, last_value[k], current_value[k], target_value[k]);
        }
        // @ts-ignore
        return next_value;
    }
    else {
        throw new Error(`Cannot spring ${typeof current_value} values`);
    }
}
function spring(value, opts = {}) {
    const store = writable(value);
    const { stiffness = 0.15, damping = 0.8, precision = 0.01 } = opts;
    let last_time;
    let task;
    let current_token;
    let last_value = value;
    let target_value = value;
    let inv_mass = 1;
    let inv_mass_recovery_rate = 0;
    let cancel_task = false;
    function set(new_value, opts = {}) {
        target_value = new_value;
        const token = (current_token = {});
        if (value == null || opts.hard || (spring.stiffness >= 1 && spring.damping >= 1)) {
            cancel_task = true; // cancel any running animation
            last_time = null;
            last_value = new_value;
            store.set((value = target_value));
            return Promise.resolve();
        }
        else if (opts.soft) {
            const rate = opts.soft === true ? 0.5 : +opts.soft;
            inv_mass_recovery_rate = 1 / (rate * 60);
            inv_mass = 0; // infinite mass, unaffected by spring forces
        }
        if (!task) {
            last_time = null;
            cancel_task = false;
            const ctx = {
                inv_mass: undefined,
                opts: spring,
                settled: true,
                dt: undefined,
            };
            task = loop((now) => {
                if (last_time === null)
                    last_time = now;
                if (cancel_task) {
                    cancel_task = false;
                    task = null;
                    return false;
                }
                inv_mass = Math.min(inv_mass + inv_mass_recovery_rate, 1);
                // altered so doesn't create a new object
                ctx.inv_mass = inv_mass;
                ctx.opts = spring;
                ctx.settled = true; // tick_spring may signal false
                ctx.dt = ((now - last_time) * 60) / 1000;
                const next_value = tick_spring(ctx, last_value, value, target_value);
                last_time = now;
                last_value = value;
                store.set((value = next_value));
                if (ctx.settled)
                    task = null;
                return !ctx.settled;
            });
        }
        return new Promise((fulfil) => {
            task.promise.then(() => {
                if (token === current_token)
                    fulfil();
            });
        });
    }
    const spring = {
        set,
        update: (fn, opts) => set(fn(target_value, value), opts),
        subscribe: store.subscribe,
        stiffness,
        damping,
        precision,
    };
    return spring;
}

var prefersReducedMotion = readable(false, set => {
    const mql = window.matchMedia('(prefers-reduced-motion:reduce)');
    set(mql.matches);
    mql.onchange = () => set(mql.matches);
});

var hasResizeObserver = () => 'ResizeObserver' in window;

//
const rectNext = rectCreateEmpty();
const updateNodeRect = (node, x, y, width, height) => {
    if (!node.rect)
        node.rect = rectCreateEmpty();
    const rect = node.rect;
    rectUpdate(rectNext, x, y, width, height);
    if (rectEqual(rect, rectNext))
        return;
    rectUpdateWithRect(rect, rectNext);
    node.dispatchEvent(new CustomEvent('measure', { detail: rect }));
};
// measures the element
const r = Math.round;
const measureViewRect = (node) => {
    const clientRect = node.getBoundingClientRect();
    updateNodeRect(node, r(clientRect.x), r(clientRect.y), r(clientRect.width), r(clientRect.height));
};
const measureOffset = (node) => updateNodeRect(node, node.offsetLeft, node.offsetTop, node.offsetWidth, node.offsetHeight);
// holds all the elements to measure using requestAnimationFrame
const elements = [];
// draw loop
let frame = null;
function tick() {
    if (!elements.length) {
        frame = null;
        return;
    }
    elements.forEach((node) => node.measure(node));
    frame = requestAnimationFrame(tick);
}
let observer; // ResizeObserver API not known by TypeScript
var measurable = (node, options = {}) => {
    const { observePosition = false, observeViewRect = false, once = false, disabled = false, } = options;
    // exit
    if (disabled)
        return;
    // use resize observe if available
    if (hasResizeObserver() && !observePosition && !observeViewRect) {
        // we only create one observer, it will observe all registered elements
        if (!observer) {
            // @ts-ignore: [2020-02-20] ResizeObserver API not known by TypeScript
            observer = new ResizeObserver((entries) => {
                entries.forEach((entry) => measureOffset(entry.target));
            });
        }
        // start observing this node
        observer.observe(node);
        // measure our node for the first time
        measureOffset(node);
        // if should only measure once, remove now
        if (once)
            observer.unobserve(node);
        // and we done, need to return a clean up method for when our node is destroyed
        return {
            destroy() {
                // already unobserved this node
                if (once)
                    return;
                observer.unobserve(node);
                // TODO: test if all nodes have been removed, if so, remove observer
            },
        };
    }
    // set measure function
    node.measure = observeViewRect ? measureViewRect : measureOffset;
    // add so the element is measured
    elements.push(node);
    // start measuring on next frame, we set up a single measure loop,
    // the loop will check if there's still elements that need to be measured,
    // else it will stop running
    if (!frame)
        frame = requestAnimationFrame(tick);
    // measure this element now
    node.measure(node);
    // remove method
    return {
        destroy() {
            const index = elements.indexOf(node);
            elements.splice(index, 1);
        },
    };
};

var focusvisible = (element) => {
    let isKeyboardInteraction = false;
    const handlePointerdown = () => {
        isKeyboardInteraction = false;
    };
    const handleKeydown = () => {
        isKeyboardInteraction = true;
    };
    const handleKeyup = () => {
        isKeyboardInteraction = false;
    };
    const handleFocus = (e) => {
        if (!isKeyboardInteraction)
            return;
        e.target.dataset.focusVisible = '';
    };
    const handleBlur = (e) => {
        delete e.target.dataset.focusVisible;
    };
    const map = {
        pointerdown: handlePointerdown,
        keydown: handleKeydown,
        keyup: handleKeyup,
        focus: handleFocus,
        blur: handleBlur,
    };
    Object.keys(map).forEach((event) => element.addEventListener(event, map[event], true));
    return () => {
        Object.keys(map).forEach((event) => element.removeEventListener(event, map[event], true));
    };
};

let result$8 = null;
var supportsWebGL2 = () => {
    if (result$8 === null) {
        if ('WebGL2RenderingContext' in window) {
            let canvas;
            try {
                canvas = h('canvas');
                result$8 = !!canvas.getContext('webgl2');
            }
            catch (err) {
                result$8 = false;
            }
            canvas && releaseCanvas(canvas);
        }
        else {
            result$8 = false;
        }
    }
    return result$8;
};

var isPowerOf2 = (value) => (value & (value - 1)) === 0;

var stringReplace = (str, entries, prefix = '') => Object.keys(entries).reduce((prev, curr) => prev.replace(new RegExp(prefix + curr), entries[curr]), str);

var SHADER_FRAG_HEAD = "#version 300 es\nprecision highp float;\n\nout vec4 fragColor;"; // eslint-disable-line

var SHADER_FRAG_INIT = "\nfloat a=1.0;vec4 fillColor=uColor;vec4 textureColor=texture(uTexture,vTexCoord);textureColor*=(1.0-step(1.0,vTexCoord.y))*step(0.0,vTexCoord.y)*(1.0-step(1.0,vTexCoord.x))*step(0.0,vTexCoord.x);"; // eslint-disable-line

var SHADER_FRAG_MASK = "\nuniform float uMaskFeather[8];uniform float uMaskBounds[4];uniform float uMaskOpacity;float mask(float x,float y,float bounds[4],float opacity){return 1.0-(1.0-(smoothstep(bounds[3],bounds[3]+1.0,x)*(1.0-smoothstep(bounds[1]-1.0,bounds[1],x))*(1.0-step(bounds[0],y))*step(bounds[2],y)))*(1.0-opacity);}"; // eslint-disable-line

var SHADER_FRAG_MASK_APPLY = "\nfloat m=mask(gl_FragCoord.x,gl_FragCoord.y,uMaskBounds,uMaskOpacity);"; // eslint-disable-line

var SHADER_FRAG_MASK_FEATHER_APPLY = "\nfloat leftFeatherOpacity=step(uMaskFeather[1],gl_FragCoord.x)*uMaskFeather[0]+((1.0-uMaskFeather[0])*smoothstep(uMaskFeather[1],uMaskFeather[3],gl_FragCoord.x));float rightFeatherOpacity=(1.0-step(uMaskFeather[7],gl_FragCoord.x))*uMaskFeather[4]+((1.0-uMaskFeather[4])*smoothstep(uMaskFeather[7],uMaskFeather[5],gl_FragCoord.x));a*=leftFeatherOpacity*rightFeatherOpacity;"; // eslint-disable-line

var SHADER_FRAG_RECT_AA = "\nvec2 scaledPoint=vec2(vRectCoord.x*uSize.x,vRectCoord.y*uSize.y);a*=smoothstep(0.0,1.0,uSize.x-scaledPoint.x);a*=smoothstep(0.0,1.0,uSize.y-scaledPoint.y);a*=smoothstep(0.0,1.0,scaledPoint.x);a*=smoothstep(0.0,1.0,scaledPoint.y);"; // eslint-disable-line

var SHADER_FRAG_CORNER_RADIUS = "\nvec2 s=(uSize-2.0)*.5;vec2 r=(vRectCoord*uSize);vec2 p=r-(uSize*.5);float cornerRadius=uCornerRadius[0];bool left=r.x<s.x;bool top=r.y<s.x;if(!left&&top){cornerRadius=uCornerRadius[1];}if(!left&&!top){cornerRadius=uCornerRadius[3];}if(left&&!top){cornerRadius=uCornerRadius[2];}a*=1.0-clamp(length(max(abs(p)-(s-cornerRadius),0.0))-cornerRadius,0.0,1.0);"; // eslint-disable-line

var SHADER_FRAG_SHAPE_BLEND_COLOR = "\nfillColor.a*=a;fillColor.rgb*=fillColor.a;fillColor.rgb*=m;fillColor.rgb+=(1.0-m)*(uCanvasColor.rgb*fillColor.a);textureColor.a*=a;textureColor.rgb*=m*a;textureColor.rgb+=(1.0-m)*(uCanvasColor.rgb*textureColor.a);fragColor=textureColor+(fillColor*(1.0-textureColor.a));"; // eslint-disable-line

var SHADER_FRAG_TEXTURE_COLORIZE = "\nif(uTextureColor.a>0.0){textureColor.rgb=uTextureColor.rgb*textureColor.a;textureColor*=uTextureColor.a;}"; // eslint-disable-line

var SHADER_VERT_HEAD = "#version 300 es\n\nin vec4 aPosition;uniform mat4 uMatrix;"; // eslint-disable-line

var SHADER_VERT_MULTIPLY_MATRUX = "\ngl_Position=uMatrix*vec4(aPosition.x,aPosition.y,0,1);"; // eslint-disable-line

var SHADER_VERT_TEXTURE = "\nin vec2 aTexCoord;out vec2 vTexCoord;"; // eslint-disable-line

const SHADER_VERT_SNIPPETS = {
    head: SHADER_VERT_HEAD,
    text: SHADER_VERT_TEXTURE,
    matrix: SHADER_VERT_MULTIPLY_MATRUX,
};
const SHADER_FRAG_SNIPPETS = {
    head: SHADER_FRAG_HEAD,
    mask: SHADER_FRAG_MASK,
    init: SHADER_FRAG_INIT,
    colorize: SHADER_FRAG_TEXTURE_COLORIZE,
    maskapply: SHADER_FRAG_MASK_APPLY,
    maskfeatherapply: SHADER_FRAG_MASK_FEATHER_APPLY,
    edgeaa: SHADER_FRAG_RECT_AA,
    cornerradius: SHADER_FRAG_CORNER_RADIUS,
    fragcolor: SHADER_FRAG_SHAPE_BLEND_COLOR,
};
const transpileShader = (gl, src, type) => {
    src = stringReplace(src, type === gl.VERTEX_SHADER ? SHADER_VERT_SNIPPETS : SHADER_FRAG_SNIPPETS, '##').trim();
    // ready if supports webgl
    if (supportsWebGL2())
        return src;
    src = src.replace(/#version.+/gm, '').trim();
    src = src.replace(/^\/\/\#/gm, '#');
    if (type === gl.VERTEX_SHADER) {
        src = src.replace(/in /gm, 'attribute ').replace(/out /g, 'varying ');
    }
    if (type === gl.FRAGMENT_SHADER) {
        src = src
            .replace(/in /gm, 'varying ')
            .replace(/out.*?;/gm, '')
            .replace(/texture\(/g, 'texture2D(')
            .replace(/fragColor/g, 'gl_FragColor');
    }
    return `${src}`;
};
const compileShader = (gl, src, type) => {
    const shader = gl.createShader(type);
    const transpiledSrc = transpileShader(gl, src, type);
    gl.shaderSource(shader, transpiledSrc);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error(gl.getShaderInfoLog(shader));
    }
    return shader;
};
const createShader = (gl, vertexShader, fragmentShader, attribs, uniforms) => {
    const program = gl.createProgram();
    gl.attachShader(program, compileShader(gl, vertexShader, gl.VERTEX_SHADER));
    gl.attachShader(program, compileShader(gl, fragmentShader, gl.FRAGMENT_SHADER));
    gl.linkProgram(program);
    const locations = {};
    attribs.forEach((name) => {
        locations[name] = gl.getAttribLocation(program, name);
    });
    uniforms.forEach((name) => {
        locations[name] = gl.getUniformLocation(program, name);
    });
    return {
        program,
        locations,
    };
};
const canMipMap = (source) => {
    if (supportsWebGL2())
        return true;
    return isPowerOf2(source.width) && isPowerOf2(source.height);
};
const applyTextureProperties = (gl, source) => {
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, canMipMap(source) ? gl.LINEAR_MIPMAP_LINEAR : gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    if (canMipMap(source)) {
        gl.generateMipmap(gl.TEXTURE_2D);
    }
};
const createTexture = (gl, source) => {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    applyTextureProperties(gl, source);
    gl.bindTexture(gl.TEXTURE_2D, null);
    return texture;
};
const applyOpacity = (color, opacity = 1) => color
    ? [color[0], color[1], color[2], isNumber(color[3]) ? opacity * color[3] : opacity]
    : [0, 0, 0, 0];

const mat4Create = () => {
    const mat = new Float32Array(16);
    mat[0] = 1;
    mat[5] = 1;
    mat[10] = 1;
    mat[15] = 1;
    return mat;
};
const mat4Perspective = (mat, fovy, aspect, near, far) => {
    const f = 1.0 / Math.tan(fovy / 2);
    const nf = 1 / (near - far);
    mat[0] = f / aspect;
    mat[1] = 0;
    mat[2] = 0;
    mat[3] = 0;
    mat[4] = 0;
    mat[5] = f;
    mat[6] = 0;
    mat[7] = 0;
    mat[8] = 0;
    mat[9] = 0;
    mat[10] = (far + near) * nf;
    mat[11] = -1;
    mat[12] = 0;
    mat[13] = 0;
    mat[14] = 2 * far * near * nf;
    mat[15] = 0;
};
const mat4Ortho = (mat, left, right, bottom, top, near, far) => {
    const lr = 1 / (left - right);
    const bt = 1 / (bottom - top);
    const nf = 1 / (near - far);
    mat[0] = -2 * lr;
    mat[1] = 0;
    mat[2] = 0;
    mat[3] = 0;
    mat[4] = 0;
    mat[5] = -2 * bt;
    mat[6] = 0;
    mat[7] = 0;
    mat[8] = 0;
    mat[9] = 0;
    mat[10] = 2 * nf;
    mat[11] = 0;
    mat[12] = (left + right) * lr;
    mat[13] = (top + bottom) * bt;
    mat[14] = (far + near) * nf;
    mat[15] = 1;
};
const mat4Translate = (mat, x, y, z) => {
    mat[12] = mat[0] * x + mat[4] * y + mat[8] * z + mat[12];
    mat[13] = mat[1] * x + mat[5] * y + mat[9] * z + mat[13];
    mat[14] = mat[2] * x + mat[6] * y + mat[10] * z + mat[14];
    mat[15] = mat[3] * x + mat[7] * y + mat[11] * z + mat[15];
};
const mat4Scale = (mat, s) => {
    mat[0] = mat[0] * s;
    mat[1] = mat[1] * s;
    mat[2] = mat[2] * s;
    mat[3] = mat[3] * s;
    mat[4] = mat[4] * s;
    mat[5] = mat[5] * s;
    mat[6] = mat[6] * s;
    mat[7] = mat[7] * s;
    mat[8] = mat[8] * s;
    mat[9] = mat[9] * s;
    mat[10] = mat[10] * s;
    mat[11] = mat[11] * s;
};
const mat4ScaleX = (mat, s) => {
    mat[0] = mat[0] * s;
    mat[1] = mat[1] * s;
    mat[2] = mat[2] * s;
    mat[3] = mat[3] * s;
};
const mat4ScaleY = (mat, s) => {
    mat[4] = mat[4] * s;
    mat[5] = mat[5] * s;
    mat[6] = mat[6] * s;
    mat[7] = mat[7] * s;
};
const mat4RotateX = (mat, rad) => {
    const s = Math.sin(rad);
    const c = Math.cos(rad);
    const a10 = mat[4];
    const a11 = mat[5];
    const a12 = mat[6];
    const a13 = mat[7];
    const a20 = mat[8];
    const a21 = mat[9];
    const a22 = mat[10];
    const a23 = mat[11];
    mat[4] = a10 * c + a20 * s;
    mat[5] = a11 * c + a21 * s;
    mat[6] = a12 * c + a22 * s;
    mat[7] = a13 * c + a23 * s;
    mat[8] = a20 * c - a10 * s;
    mat[9] = a21 * c - a11 * s;
    mat[10] = a22 * c - a12 * s;
    mat[11] = a23 * c - a13 * s;
};
const mat4RotateY = (mat, rad) => {
    const s = Math.sin(rad);
    const c = Math.cos(rad);
    const a00 = mat[0];
    const a01 = mat[1];
    const a02 = mat[2];
    const a03 = mat[3];
    const a20 = mat[8];
    const a21 = mat[9];
    const a22 = mat[10];
    const a23 = mat[11];
    mat[0] = a00 * c - a20 * s;
    mat[1] = a01 * c - a21 * s;
    mat[2] = a02 * c - a22 * s;
    mat[3] = a03 * c - a23 * s;
    mat[8] = a00 * s + a20 * c;
    mat[9] = a01 * s + a21 * c;
    mat[10] = a02 * s + a22 * c;
    mat[11] = a03 * s + a23 * c;
};
const mat4RotateZ = (mat, rad) => {
    const s = Math.sin(rad);
    const c = Math.cos(rad);
    const a00 = mat[0];
    const a01 = mat[1];
    const a02 = mat[2];
    const a03 = mat[3];
    const a10 = mat[4];
    const a11 = mat[5];
    const a12 = mat[6];
    const a13 = mat[7];
    mat[0] = a00 * c + a10 * s;
    mat[1] = a01 * c + a11 * s;
    mat[2] = a02 * c + a12 * s;
    mat[3] = a03 * c + a13 * s;
    mat[4] = a10 * c - a00 * s;
    mat[5] = a11 * c - a01 * s;
    mat[6] = a12 * c - a02 * s;
    mat[7] = a13 * c - a03 * s;
};

var degToRad = (degrees) => degrees * Math.PI / 180;

var previewFragmentShader = "\n##head\nin vec2 vTexCoord;uniform sampler2D uTexture;uniform sampler2D uTextureMarkup;uniform vec2 uTextureSize;uniform float uOpacity;uniform vec4 uFillColor;uniform vec4 uOverlayColor;uniform mat4 uColorMatrix;uniform vec4 uColorOffset;uniform float uClarityKernel[9];uniform float uClarityKernelWeight;uniform float uColorGamma;uniform float uColorVignette;uniform float uMaskClip;uniform float uMaskOpacity;uniform float uMaskBounds[4];uniform float uMaskCornerRadius[4];uniform float uMaskFeather[8];vec4 applyGamma(vec4 c,float g){c.r=pow(c.r,g);c.g=pow(c.g,g);c.b=pow(c.b,g);return c;}vec4 applyColorMatrix(vec4 c,mat4 m,vec4 o){vec4 cM=(c*m)+o;cM*=cM.a;return cM;}vec4 applyConvolutionMatrix(vec4 c,float k0,float k1,float k2,float k3,float k4,float k5,float k6,float k7,float k8,float w){if(w<=0.0)return c;vec2 pixel=vec2(1)/uTextureSize;vec4 colorSum=texture(uTexture,vTexCoord+pixel*vec2(-1.0,-1.0))*k0+texture(uTexture,vTexCoord+pixel*vec2(0.0,-1.0))*k1+texture(uTexture,vTexCoord+pixel*vec2(1.0,-1.0))*k2+texture(uTexture,vTexCoord+pixel*vec2(-1.0,0.0))*k3+texture(uTexture,vTexCoord+pixel*vec2(0.0,0.0))*k4+texture(uTexture,vTexCoord+pixel*vec2(1.0,0.0))*k5+texture(uTexture,vTexCoord+pixel*vec2(-1.0,1.0))*k6+texture(uTexture,vTexCoord+pixel*vec2(0.0,1.0))*k7+texture(uTexture,vTexCoord+pixel*vec2(1.0,1.0))*k8;vec4 color=vec4((colorSum/w).rgb,c.a);color.rgb=clamp(color.rgb,0.0,1.0);return color;}vec4 applyVignette(vec4 c,vec2 pos,vec2 center,float v){float d=distance(pos,center)/length(center);float f=1.0-(d*abs(v));if(v>0.0){c.rgb*=f;}else if(v<0.0){c.rgb+=(1.0-f)*(1.0-c.rgb);}return c;}vec4 blendPremultipliedAlpha(vec4 back,vec4 front){return front+(back*(1.0-front.a));}void main(){float x=gl_FragCoord.x;float y=gl_FragCoord.y;float a=1.0;float maskTop=uMaskBounds[0];float maskRight=uMaskBounds[1];float maskBottom=uMaskBounds[2];float maskLeft=uMaskBounds[3];float leftFeatherOpacity=step(uMaskFeather[1],x)*uMaskFeather[0]+((1.0-uMaskFeather[0])*smoothstep(uMaskFeather[1],uMaskFeather[3],x));float rightFeatherOpacity=(1.0-step(uMaskFeather[7],x))*uMaskFeather[4]+((1.0-uMaskFeather[4])*smoothstep(uMaskFeather[7],uMaskFeather[5],x));a*=leftFeatherOpacity*rightFeatherOpacity;float overlayColorAlpha=(smoothstep(maskLeft,maskLeft+1.0,x)*(1.0-smoothstep(maskRight-1.0,maskRight,x))*(1.0-step(maskTop,y))*step(maskBottom,y));if(uOverlayColor.a==0.0){a*=overlayColorAlpha;}vec2 offset=vec2(maskLeft,maskBottom);vec2 size=vec2(maskRight-maskLeft,maskTop-maskBottom)*.5;vec2 center=offset.xy+size.xy;int pixelX=int(step(center.x,x));int pixelY=int(step(y,center.y));float cornerRadius=0.0;if(pixelX==0&&pixelY==0)cornerRadius=uMaskCornerRadius[0];if(pixelX==1&&pixelY==0)cornerRadius=uMaskCornerRadius[1];if(pixelX==0&&pixelY==1)cornerRadius=uMaskCornerRadius[2];if(pixelX==1&&pixelY==1)cornerRadius=uMaskCornerRadius[3];float cornerOffset=sign(cornerRadius)*length(max(abs(gl_FragCoord.xy-size-offset)-size+cornerRadius,0.0))-cornerRadius;float cornerOpacity=1.0-smoothstep(0.0,1.0,cornerOffset);a*=cornerOpacity;vec2 scaledPoint=vec2(vTexCoord.x*uTextureSize.x,vTexCoord.y*uTextureSize.y);a*=smoothstep(0.0,1.0,uTextureSize.x-scaledPoint.x);a*=smoothstep(0.0,1.0,uTextureSize.y-scaledPoint.y);a*=smoothstep(0.0,1.0,scaledPoint.x);a*=smoothstep(0.0,1.0,scaledPoint.y);vec4 color=texture(uTexture,vTexCoord);color=applyConvolutionMatrix(color,uClarityKernel[0],uClarityKernel[1],uClarityKernel[2],uClarityKernel[3],uClarityKernel[4],uClarityKernel[5],uClarityKernel[6],uClarityKernel[7],uClarityKernel[8],uClarityKernelWeight);color=applyGamma(color,uColorGamma);color=applyColorMatrix(color,uColorMatrix,uColorOffset);color=blendPremultipliedAlpha(uFillColor,color);color*=a;color=blendPremultipliedAlpha(color,texture(uTextureMarkup,vTexCoord));if(uColorVignette!=0.0){vec2 pos=gl_FragCoord.xy-offset;color=applyVignette(color,pos,center-offset,uColorVignette);}vec4 overlayColor=uOverlayColor*(1.0-overlayColorAlpha);overlayColor.rgb*=overlayColor.a;color=blendPremultipliedAlpha(color,overlayColor);if(uOverlayColor.a>0.0&&color.a<1.0&&uFillColor.a>0.0){color=blendPremultipliedAlpha(uFillColor,overlayColor);}color*=uOpacity;fragColor=color;}"; // eslint-disable-line

var previewVertexShader = "\n##head\n##text\nvoid main(){vTexCoord=aTexCoord;gl_Position=uMatrix*aPosition;}"; // eslint-disable-line

var pathVertexShader = "#version 300 es\n\nin vec4 aPosition;in vec2 aNormal;in float aMiter;out vec2 vNormal;out float vMiter;uniform float uWidth;uniform mat4 uMatrix;void main(){vMiter=aMiter;vNormal=aNormal;float w=uWidth;gl_Position=uMatrix*vec4(aPosition.x+(aNormal.x*w*.5*aMiter),aPosition.y+(aNormal.y*w*.5*aMiter),0,1);}"; // eslint-disable-line

var pathFragmentShader = "\n##head\n##mask\nin vec2 vNormal;in float vMiter;uniform float uWidth;uniform vec4 uColor;uniform vec4 uCanvasColor;void main(){vec4 fillColor=uColor;float w=(uWidth-1.0)*.5;float d=1.0-abs(vMiter);float m=mask(gl_FragCoord.x,gl_FragCoord.y,uMaskBounds,uMaskOpacity);fillColor.a*=clamp(smoothstep(0.25,1.0,d*w),0.0,1.0);fillColor.rgb*=fillColor.a;fillColor.rgb*=m;fillColor.rgb+=(1.0-m)*(uCanvasColor.rgb*fillColor.a);fragColor=fillColor;}"; // eslint-disable-line

var rectVertexShader = "\n##head\n##text\nin vec2 aRectCoord;out vec2 vRectCoord;void main(){vTexCoord=aTexCoord;vRectCoord=aRectCoord;\n##matrix\n}"; // eslint-disable-line

var rectFragmentShader = "\n##head\n##mask\nin vec2 vTexCoord;in vec2 vRectCoord;uniform sampler2D uTexture;uniform vec4 uTextureColor;uniform vec4 uColor;uniform float uCornerRadius[4];uniform vec2 uSize;uniform vec2 uPosition;uniform vec4 uCanvasColor;void main(){\n##init\n##colorize\n##edgeaa\n##cornerradius\n##maskfeatherapply\n##maskapply\n##fragcolor\n}"; // eslint-disable-line

var ellipseVertexShader = "\n##head\n##text\nvoid main(){vTexCoord=vec2(aTexCoord.x*2.0-1.0,aTexCoord.y*2.0-1.0);\n##matrix\n}"; // eslint-disable-line

var ellipseFragmentShader = "\n##head\n##mask\nin vec2 vTexCoord;uniform sampler2D uTexture;uniform vec2 uRadius;uniform vec4 uColor;uniform int uInverted;uniform vec4 uCanvasColor;void main(){\n##init\nfloat ar=uRadius.x/uRadius.y;vec2 rAA=vec2(uRadius.x-1.0,uRadius.y-(1.0/ar));vec2 scaledPointSq=vec2((vTexCoord.x*uRadius.x)*(vTexCoord.x*uRadius.x),(vTexCoord.y*uRadius.y)*(vTexCoord.y*uRadius.y));float p=(scaledPointSq.x/(uRadius.x*uRadius.x))+(scaledPointSq.y/(uRadius.y*uRadius.y));float pAA=(scaledPointSq.x/(rAA.x*rAA.x))+(scaledPointSq.y/(rAA.y*rAA.y));a=smoothstep(1.0,p/pAA,p);if(uInverted==1)a=1.0-a;\n##maskapply\n##fragcolor\n}"; // eslint-disable-line

var triangleVertexShader = "\n##head\nvoid main(){\n##matrix\n}"; // eslint-disable-line

var triangleFragmentShader = "\n##head\n##mask\nuniform vec4 uColor;uniform vec4 uCanvasColor;void main(){vec4 fillColor=uColor;\n##maskapply\nfillColor.rgb*=fillColor.a;fillColor.rgb*=m;fillColor.rgb+=(1.0-m)*(uCanvasColor.rgb*fillColor.a);fragColor=fillColor;}"; // eslint-disable-line

const createPathSegment = (vertices, index, a, b, c) => {
    let ab;
    let bc;
    ab = vectorNormalize(vectorCreate(b.x - a.x, b.y - a.y));
    bc = vectorNormalize(vectorCreate(c.x - b.x, c.y - b.y));
    const tangent = vectorNormalize(vectorCreate(ab.x + bc.x, ab.y + bc.y));
    const miter = vectorCreate(-tangent.y, tangent.x);
    const normal = vectorCreate(-ab.y, ab.x);
    // TODO: The Math.min is a "dirty" hack to prevent miter spikes, should bevel line edges
    // const dist = Math.min(1, 1 / vectorDot(miter, normal));
    const miterLength = 1 / vectorDot(miter, normal);
    vertices[index] = b.x;
    vertices[index + 1] = b.y;
    vertices[index + 2] = miter.x;
    vertices[index + 3] = miter.y;
    vertices[index + 4] = -miterLength;
    vertices[index + 5] = b.x;
    vertices[index + 6] = b.y;
    vertices[index + 7] = miter.x;
    vertices[index + 8] = miter.y;
    vertices[index + 9] = miterLength;
};
const createPathVertices = (points, close) => {
    let a, b, c, i = 0;
    const l = points.length;
    const stride = 10;
    const vertices = new Float32Array((close ? l + 1 : l) * stride);
    const first = points[0];
    const last = points[l - 1];
    for (i = 0; i < l; i++) {
        a = points[i - 1];
        b = points[i];
        c = points[i + 1];
        // if previous point not available use inverse vector to next point
        if (!a)
            a = close ? last : vectorCreate(b.x + (b.x - c.x), b.y + (b.y - c.y));
        // if next point not available use inverse vector from previous point
        if (!c)
            c = close ? first : vectorCreate(b.x + (b.x - a.x), b.y + (b.y - a.y));
        createPathSegment(vertices, i * stride, a, b, c);
    }
    if (close)
        createPathSegment(vertices, l * stride, last, first, points[1]);
    return vertices;
};
const rectPointsToVertices = (points) => {
    // [tl, tr, br, bl]
    // B   D
    // | \ |
    // A  C
    const vertices = new Float32Array(8);
    vertices[0] = points[3].x;
    vertices[1] = points[3].y;
    vertices[2] = points[0].x;
    vertices[3] = points[0].y;
    vertices[4] = points[2].x;
    vertices[5] = points[2].y;
    vertices[6] = points[1].x;
    vertices[7] = points[1].y;
    return vertices;
};
const trianglePointToVertices = (points) => {
    const vertices = new Float32Array(6);
    vertices[0] = points[0].x;
    vertices[1] = points[0].y;
    vertices[2] = points[1].x;
    vertices[3] = points[1].y;
    vertices[4] = points[2].x;
    vertices[5] = points[2].y;
    return vertices;
};
const createRectPoints = (rect, rotation = 0, flipX, flipY) => {
    const corners = rectGetCorners(rect);
    const cx = rect.x + rect.width * 0.5;
    const cy = rect.y + rect.height * 0.5;
    if (flipX || flipY)
        vectorsFlip(corners, flipX, flipY, cx, cy);
    if (rotation !== 0)
        vectorsRotate(corners, rotation, cx, cy);
    return corners;
};
const createEllipseOutline = (x, y, width, height, rotation, flipX, flipY) => {
    const points = [];
    // decide how much points we need to describe the circle
    const size = Math.abs(width) + Math.abs(height);
    const precision = Math.max(20, Math.round(size / 6));
    const w = width * 0.5;
    const h = height * 0.5;
    const cx = x + w;
    const cy = y + h;
    for (let i = 0; i < precision; i++) {
        points.push(vectorCreate(cx + w * Math.cos((i * (Math.PI * 2)) / precision), cy + h * Math.sin((i * (Math.PI * 2)) / precision)));
    }
    if (flipX || flipY)
        vectorsFlip(points, flipX, flipY, cx, cy);
    if (rotation)
        vectorsRotate(points, rotation, cx, cy);
    return points;
};
const createRectOutline = (x, y, width, height, rotation, cornerRadius, flipX, flipY) => {
    const points = [];
    if (cornerRadius.every((v) => v === 0)) {
        points.push(vectorCreate(x, y), // top left corner
        vectorCreate(x + width, y), // top right corner
        vectorCreate(x + width, y + height), // bottom right corner
        vectorCreate(x, y + height) // bottom left corner
        );
    }
    else {
        const [tl, tr, bl, br] = cornerRadius;
        const l = x;
        const r = x + width;
        const t = y;
        const b = y + height;
        // start at end of top left corner
        points.push(vectorCreate(l + tl, t));
        pushRectCornerPoints(points, r - tr, t + tr, tr, -1);
        // move to bottom right corner
        points.push(vectorCreate(r, t + tr));
        pushRectCornerPoints(points, r - br, b - br, br, 0);
        // move to bottom left corner
        points.push(vectorCreate(r - br, b));
        pushRectCornerPoints(points, l + bl, b - bl, bl, 1);
        // move to top left corner
        points.push(vectorCreate(l, b - bl));
        pushRectCornerPoints(points, l + tl, t + tl, tl, 2);
    }
    if (flipX || flipY)
        vectorsFlip(points, flipX, flipY, x + width * 0.5, y + height * 0.5);
    if (rotation)
        vectorsRotate(points, rotation, x + width * 0.5, y + height * 0.5);
    return points;
};
const pushRectCornerPoints = (points, x, y, radius, offset) => {
    const precision = Math.min(20, Math.max(4, Math.round(radius / 2)));
    let p = 0;
    let s = 0;
    let rx = 0;
    let ry = 0;
    let i = 0;
    for (; i < precision; i++) {
        p = i / precision;
        s = offset * HALF_PI + p * HALF_PI;
        rx = radius * Math.cos(s);
        ry = radius * Math.sin(s);
        points.push(vectorCreate(x + rx, y + ry));
    }
};

var getWebGLContext = (canvas, attrs) => canvas.getContext('webgl2', attrs) ||
    canvas.getContext('webgl', attrs) ||
    canvas.getContext('experimental-webgl', attrs);

// prettier-ignore
// B   D
// | \ |
// A  C
const rectUV = new Float32Array([
    0.0, 1.0,
    0.0, 0.0,
    1.0, 1.0,
    1.0, 0.0,
]);
var createGLImageDrawer = (canvas, imageData, imageSize, pixelDensity) => {
    let viewSize = { width: 0, height: 0 };
    let viewSizeVisual = { width: 0, height: 0 };
    let viewAspectRatio;
    let viewPixelDensity;
    let viewZ;
    let markupMatrixCanvas;
    let markupMatrixFrameBuffer;
    let markupMatrix;
    let imagePositions;
    let maskTop;
    let maskRight;
    let maskBottom;
    let maskLeft;
    let maskOpacity;
    let maskBounds;
    let canvasColor = [0, 0, 0];
    //#region texture handling
    // offset +   0 : white
    // offset +   1 : transparent
    // offset +   2 : preview markup
    // offset +   3 : preview
    // offset + >=4 : markup
    const TEXTURE_WHITE_INDEX = 0;
    const TEXTURE_TRANSPARENT_INDEX = 1;
    const TEXTURE_PREVIEW_MARKUP_INDEX = 2;
    const TEXTURE_PREVIEW_INDEX = 3;
    const TEXTURE_OFFSET = 4;
    const textures = [];
    const setTextureObject = (index, texture, source = undefined) => {
        const storedTexture = textures[index];
        if (storedTexture)
            gl.deleteTexture(storedTexture.texture);
        textures[index] = {
            texture,
            source,
            textureAspectRatio: source ? getAspectRatio(source.width, source.height) : undefined,
        };
        return index;
    };
    const getTextureSize = (index) => {
        const source = textures[index].source;
        return (source && {
            width: source.width,
            height: source.height,
        });
    };
    const getAvailableTextureIndex = () => {
        const index = textures.findIndex((value) => value === undefined);
        return index < TEXTURE_OFFSET ? textures.length : index;
    };
    const addTextureObject = (texture, source = undefined) => setTextureObject(getAvailableTextureIndex(), texture, source);
    const addTexture = (source) => addTextureObject(createTexture(gl, source), source);
    const setTexture = (index, source) => {
        setTextureObject(index, createTexture(gl, source), source);
    };
    const reserveTexture = (index) => {
        textures[index] = {};
    };
    const removeTexture = (index) => {
        const item = textures[index];
        gl.deleteTexture(item.texture);
        if (item.source instanceof HTMLCanvasElement)
            releaseCanvas(item.source);
        textures[index] = undefined;
    };
    //#endregion
    // resize view
    const resize = (width, height, pixelDensity) => {
        // density
        viewPixelDensity = pixelDensity;
        // visual size
        viewSizeVisual.width = width;
        viewSizeVisual.height = height;
        // size
        viewSize.width = width * pixelDensity;
        viewSize.height = height * pixelDensity;
        // calculate the aspect ratio, we use this to determine quad size
        viewAspectRatio = getAspectRatio(viewSize.width, viewSize.height);
        // sync dimensions with image data
        canvas.width = viewSize.width;
        canvas.height = viewSize.height;
        // redefine matrices
        markupMatrixCanvas = mat4Create();
        mat4Ortho(markupMatrixCanvas, 0, viewSize.width, viewSize.height, 0, -1, 1);
        markupMatrixFrameBuffer = mat4Create();
        const w = imageData.width * pixelDensity;
        const h = imageData.height * pixelDensity;
        mat4Ortho(markupMatrixFrameBuffer, 0, w, h, 0, -1, 1);
        mat4Translate(markupMatrixFrameBuffer, 0, h, 0);
        mat4ScaleX(markupMatrixFrameBuffer, 1);
        mat4ScaleY(markupMatrixFrameBuffer, -1);
        // update preview markup texture
        gl.bindTexture(gl.TEXTURE_2D, textures[TEXTURE_PREVIEW_MARKUP_INDEX].texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, imageData.width, imageData.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        // set the filtering, we don't need mips
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, textures[TEXTURE_PREVIEW_MARKUP_INDEX].texture, 0);
        // update image texture
        const imageWidth = imageSize.width * pixelDensity;
        const imageHeight = imageSize.height * pixelDensity;
        const l = imageWidth * -0.5;
        const t = imageHeight * 0.5;
        const r = imageWidth * 0.5;
        const b = imageHeight * -0.5;
        // prettier-ignore
        // B   D
        // | \ |
        // A  C
        imagePositions = new Float32Array([
            l, b, 0,
            l, t, 0,
            r, b, 0,
            r, t, 0,
        ]);
        gl.bindBuffer(gl.ARRAY_BUFFER, imagePositionsBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, imagePositions, gl.STATIC_DRAW);
        // move image backwards so it's presented in actual pixel size
        viewZ = // 1. we calculate the z offset required to have the
            //    image height match the view height
            /*        /|
                     / |
                    /  | height / 2
                   /   |
            f / 2 /__z_|
                  \    |
                   \   |
                    \  |
                     \ |
                      \|
            */
            (imageSize.height / 2 / FOV_TAN_HALF) *
                // 2. we want to render the image at the actual height, viewsize / height gets us results in a 1:1 presentation
                (viewSize.height / imageSize.height) *
                // 3. z has to be negative, therefor multiply by -1
                -1;
    };
    // fov is fixed
    const FOV = degToRad(30);
    const FOV_TAN_HALF = Math.tan(FOV / 2);
    // get gl drawing context
    const gl = getWebGLContext(canvas, {
        antialias: false,
        alpha: false,
        premultipliedAlpha: true,
    });
    // no drawing context received, exit
    if (!gl)
        return;
    // enable derivatives
    gl.getExtension('OES_standard_derivatives');
    // toggle gl settings
    gl.disable(gl.DEPTH_TEST);
    // set blend mode, we need it for alpha blending
    gl.enable(gl.BLEND);
    /*
    https://webglfundamentals.org/webgl/lessons/webgl-and-alpha.html
    most if not all Canvas 2D implementations work with pre-multiplied alpha.
    That means when you transfer them to WebGL and UNPACK_PREMULTIPLY_ALPHA_WEBGL
    is false WebGL will convert them back to un-premultipiled.
    With pre-multiplied alpha on, [1, .5, .5, 0] does not exist, it's always [0, 0, 0, 0]
    */
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    // something to look into:
    // gl.UNPACK_COLORSPACE_CONVERSION_WEBGL
    // create white base shape texture
    const whiteTexture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, whiteTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, // width
    1, // height
    0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([255, 255, 255, 255]) // white background
    );
    setTextureObject(TEXTURE_WHITE_INDEX, whiteTexture);
    const transparentTexture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, transparentTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, // width
    1, // height
    0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 0]) // transparent background
    );
    setTextureObject(TEXTURE_TRANSPARENT_INDEX, transparentTexture);
    // create image markup texture and framebuffer
    const imageMarkupTexture = gl.createTexture();
    setTextureObject(TEXTURE_PREVIEW_MARKUP_INDEX, imageMarkupTexture);
    const fb = gl.createFramebuffer();
    // #region image
    // create preview texture
    setTexture(TEXTURE_PREVIEW_INDEX, imageData);
    const imagePositionsBuffer = gl.createBuffer();
    const texturePositionsBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, texturePositionsBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, rectUV, gl.STATIC_DRAW);
    // prettier-ignore
    // B   D
    // | \ |
    // A  C
    const imageCoordinateCount = 4;
    // create default pixel drawing program, supports what we need
    const previewShader = createShader(gl, previewVertexShader, previewFragmentShader, ['aPosition', 'aTexCoord'], [
        'uMatrix',
        'uTexture',
        'uTextureMarkup',
        'uTextureSize',
        'uColorGamma',
        'uColorVignette',
        'uColorOffset',
        'uColorMatrix',
        'uClarityKernel',
        'uClarityKernelWeight',
        'uOpacity',
        'uMaskOpacity',
        'uMaskBounds',
        'uMaskCornerRadius',
        'uMaskFeather',
        'uFillColor',
        'uOverlayColor',
    ]);
    // tell canvas context to use program
    gl.useProgram(previewShader.program);
    // transform vars
    let matrix;
    const drawPreviewImage = (originX, originY, translateX, translateY, rotateX, rotateY, rotateZ, scale, colorMatrix, opacity = 1, clarity, gamma = 1, vignette = 0, maskFeather = [1, 0, 1, 0, 1, viewSizeVisual.width, 1, viewSizeVisual.width], maskCornerRadius = [0, 0, 0, 0], imageBackgroundColor = [0, 0, 0, 0], imageOverlayColor = [0, 0, 0, 0], enableMarkup = true) => {
        const { program, locations } = previewShader;
        // convert to pixel density
        translateX *= viewPixelDensity;
        translateY *= viewPixelDensity;
        originX *= viewPixelDensity;
        originY *= viewPixelDensity;
        // apply
        matrix = mat4Create();
        mat4Perspective(matrix, FOV, viewAspectRatio, 1, -viewZ * 2);
        // move image
        mat4Translate(matrix, translateX, -translateY, viewZ);
        // set rotation origin in view
        mat4Translate(matrix, originX, -originY, 0);
        // rotate image
        mat4RotateZ(matrix, -rotateZ);
        // resize
        mat4Scale(matrix, scale);
        // reset rotation origin
        mat4Translate(matrix, -originX, originY, 0);
        // flip
        mat4RotateY(matrix, rotateY);
        mat4RotateX(matrix, rotateX);
        //
        // tell context to draw preview
        //
        gl.useProgram(program);
        gl.enableVertexAttribArray(locations.aPosition);
        gl.enableVertexAttribArray(locations.aTexCoord);
        // set up texture
        const previewTexture = textures[TEXTURE_PREVIEW_INDEX];
        gl.uniform1i(locations.uTexture, TEXTURE_PREVIEW_INDEX);
        gl.uniform2f(locations.uTextureSize, imageData.width, imageData.height);
        gl.activeTexture(gl.TEXTURE0 + TEXTURE_PREVIEW_INDEX);
        gl.bindTexture(gl.TEXTURE_2D, previewTexture.texture);
        // set up markup texture
        const textureIndex = enableMarkup
            ? TEXTURE_PREVIEW_MARKUP_INDEX
            : TEXTURE_TRANSPARENT_INDEX;
        const markupTexture = textures[textureIndex];
        gl.uniform1i(locations.uTextureMarkup, textureIndex);
        gl.activeTexture(gl.TEXTURE0 + textureIndex);
        gl.bindTexture(gl.TEXTURE_2D, markupTexture.texture);
        // set up buffers
        gl.bindBuffer(gl.ARRAY_BUFFER, imagePositionsBuffer);
        gl.vertexAttribPointer(locations.aPosition, 3, gl.FLOAT, false, 0, 0);
        gl.bindBuffer(gl.ARRAY_BUFFER, texturePositionsBuffer);
        gl.vertexAttribPointer(locations.aTexCoord, 2, gl.FLOAT, false, 0, 0);
        // update matrix
        gl.uniformMatrix4fv(locations.uMatrix, false, matrix);
        // overlay color
        gl.uniform4fv(locations.uOverlayColor, imageOverlayColor);
        gl.uniform4fv(locations.uFillColor, imageBackgroundColor);
        // convolution
        let clarityWeight;
        if (!clarity) {
            clarity = [0, 0, 0, 0, 1, 0, 0, 0, 0];
            clarityWeight = -1;
        }
        else {
            clarityWeight = clarity.reduce((prev, curr) => prev + curr, 0);
            clarityWeight = clarityWeight <= 0 ? 1 : clarityWeight;
        }
        gl.uniform1fv(locations.uClarityKernel, clarity);
        gl.uniform1f(locations.uClarityKernelWeight, clarityWeight);
        gl.uniform1f(locations.uColorGamma, 1.0 / gamma);
        gl.uniform1f(locations.uColorVignette, vignette);
        // set color matrix values
        gl.uniform4f(locations.uColorOffset, colorMatrix[4], colorMatrix[9], colorMatrix[14], colorMatrix[19]);
        gl.uniformMatrix4fv(locations.uColorMatrix, false, [
            colorMatrix[0],
            colorMatrix[1],
            colorMatrix[2],
            colorMatrix[3],
            colorMatrix[5],
            colorMatrix[6],
            colorMatrix[7],
            colorMatrix[8],
            colorMatrix[10],
            colorMatrix[11],
            colorMatrix[12],
            colorMatrix[13],
            colorMatrix[15],
            colorMatrix[16],
            colorMatrix[17],
            colorMatrix[18],
        ]);
        // opacity level
        gl.uniform1f(locations.uOpacity, opacity);
        // mask
        gl.uniform1f(locations.uMaskOpacity, maskOpacity);
        gl.uniform1fv(locations.uMaskBounds, maskBounds);
        gl.uniform1fv(locations.uMaskCornerRadius, maskCornerRadius.map((v) => v * pixelDensity));
        gl.uniform1fv(locations.uMaskFeather, maskFeather.map((v, i) => (i % 2 === 0 ? v : v * pixelDensity)));
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, imageCoordinateCount);
        gl.disableVertexAttribArray(locations.aPosition);
        gl.disableVertexAttribArray(locations.aTexCoord);
    };
    //#endregion
    // #region path
    const pathShader = createShader(gl, pathVertexShader, pathFragmentShader, ['aPosition', 'aNormal', 'aMiter'], ['uColor', 'uCanvasColor', 'uMatrix', 'uWidth', 'uMaskBounds', 'uMaskOpacity']);
    const pathBuffer = gl.createBuffer();
    const strokePath = (points, width, color, close) => {
        const { program, locations } = pathShader;
        gl.useProgram(program);
        gl.enableVertexAttribArray(locations.aPosition);
        gl.enableVertexAttribArray(locations.aNormal);
        gl.enableVertexAttribArray(locations.aMiter);
        const vertices = createPathVertices(points, close);
        const stride = Float32Array.BYTES_PER_ELEMENT * 5;
        const normalOffset = Float32Array.BYTES_PER_ELEMENT * 2; // at position 2
        const miterOffset = Float32Array.BYTES_PER_ELEMENT * 4; // at position 4
        gl.uniform1f(locations.uWidth, width + 2.0);
        gl.uniform4fv(locations.uColor, color);
        gl.uniformMatrix4fv(locations.uMatrix, false, markupMatrix);
        gl.uniform4f(locations.uCanvasColor, canvasColor[0], canvasColor[1], canvasColor[2], 1);
        gl.uniform1fv(locations.uMaskBounds, maskBounds);
        gl.uniform1f(locations.uMaskOpacity, maskOpacity);
        gl.bindBuffer(gl.ARRAY_BUFFER, pathBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
        gl.vertexAttribPointer(locations.aPosition, 2, gl.FLOAT, false, stride, 0);
        gl.vertexAttribPointer(locations.aNormal, 2, gl.FLOAT, false, stride, normalOffset);
        gl.vertexAttribPointer(locations.aMiter, 1, gl.FLOAT, false, stride, miterOffset);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, vertices.length / 5);
        gl.disableVertexAttribArray(locations.aPosition);
        gl.disableVertexAttribArray(locations.aNormal);
        gl.disableVertexAttribArray(locations.aMiter);
    };
    //#endregion
    // #region triangle
    const triangleShader = createShader(gl, triangleVertexShader, triangleFragmentShader, ['aPosition'], ['uColor', 'uCanvasColor', 'uMatrix', 'uMaskBounds', 'uMaskOpacity']);
    const triangleBuffer = gl.createBuffer();
    const fillTriangle = (vertices, backgroundColor) => {
        const { program, locations } = triangleShader;
        gl.useProgram(program);
        gl.enableVertexAttribArray(locations.aPosition);
        gl.uniform4fv(locations.uColor, backgroundColor);
        gl.uniformMatrix4fv(locations.uMatrix, false, markupMatrix);
        gl.uniform1fv(locations.uMaskBounds, maskBounds);
        gl.uniform1f(locations.uMaskOpacity, maskOpacity);
        gl.uniform4f(locations.uCanvasColor, canvasColor[0], canvasColor[1], canvasColor[2], 1);
        gl.bindBuffer(gl.ARRAY_BUFFER, triangleBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
        gl.vertexAttribPointer(locations.aPosition, 2, gl.FLOAT, false, 0, 0);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, vertices.length / 2);
        gl.disableVertexAttribArray(locations.aPosition);
        return vertices;
    };
    //#endregion
    // #region rect
    const rectShaderAttributes = ['aPosition', 'aTexCoord', 'aRectCoord'];
    const rectShaderUniforms = [
        'uTexture',
        'uColor',
        'uMatrix',
        'uCanvasColor',
        'uTextureColor',
        'uPosition',
        'uSize',
        'uMaskBounds',
        'uMaskOpacity',
        'uMaskFeather',
        'uCornerRadius',
    ];
    const rectShader = createShader(gl, rectVertexShader, rectFragmentShader, rectShaderAttributes, rectShaderUniforms);
    const rectBuffer = gl.createBuffer();
    const rectTextureBuffer = gl.createBuffer();
    const rectCornerBuffer = gl.createBuffer();
    const rectTextureColorFilter = [0, 0, 0, 0];
    const fillRect = (vertices, width, height, cornerRadius, backgroundColor, backgroundImage, colorFilter = rectTextureColorFilter, uv = rectUV, maskFeather = [
        1,
        0,
        1,
        0,
        1,
        Math.max(viewSize.width, imageData.width),
        1,
        Math.max(viewSize.width, imageData.width),
    ]) => {
        const { program, locations } = rectShader;
        gl.useProgram(program);
        gl.enableVertexAttribArray(locations.aPosition);
        gl.enableVertexAttribArray(locations.aTexCoord);
        gl.enableVertexAttribArray(locations.aRectCoord);
        gl.uniform4fv(locations.uColor, backgroundColor);
        gl.uniform4fv(locations.uTextureColor, colorFilter);
        gl.uniform2fv(locations.uSize, [width, height]);
        gl.uniform2fv(locations.uPosition, [vertices[2], vertices[3]]);
        gl.uniform1fv(locations.uCornerRadius, cornerRadius);
        gl.uniform4f(locations.uCanvasColor, canvasColor[0], canvasColor[1], canvasColor[2], 1);
        // mask
        gl.uniform1fv(locations.uMaskFeather, maskFeather.map((v, i) => (i % 2 === 0 ? v : v * pixelDensity)));
        gl.uniform1fv(locations.uMaskBounds, maskBounds);
        gl.uniform1f(locations.uMaskOpacity, maskOpacity);
        gl.uniformMatrix4fv(locations.uMatrix, false, markupMatrix);
        const textureIndex = textures[backgroundImage] && textures[backgroundImage].texture
            ? backgroundImage
            : TEXTURE_TRANSPARENT_INDEX;
        gl.uniform1i(locations.uTexture, textureIndex);
        gl.activeTexture(gl.TEXTURE0 + textureIndex);
        gl.bindTexture(gl.TEXTURE_2D, textures[textureIndex].texture);
        gl.bindBuffer(gl.ARRAY_BUFFER, rectTextureBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, uv, gl.STATIC_DRAW);
        gl.vertexAttribPointer(locations.aTexCoord, 2, gl.FLOAT, false, 0, 0);
        // we use these coordinates combined with the size of the rect to interpolate and alias edges
        gl.bindBuffer(gl.ARRAY_BUFFER, rectCornerBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, rectUV, gl.STATIC_DRAW);
        gl.vertexAttribPointer(locations.aRectCoord, 2, gl.FLOAT, false, 0, 0);
        gl.bindBuffer(gl.ARRAY_BUFFER, rectBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
        gl.vertexAttribPointer(locations.aPosition, 2, gl.FLOAT, false, 0, 0);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, vertices.length / 2);
        gl.disableVertexAttribArray(locations.aPosition);
        gl.disableVertexAttribArray(locations.aTexCoord);
        gl.disableVertexAttribArray(locations.aRectCoord);
        return vertices;
    };
    //#endregion
    // #region ellipse
    const ellipseShader = createShader(gl, ellipseVertexShader, ellipseFragmentShader, ['aPosition', 'aTexCoord'], [
        'uTexture',
        'uColor',
        'uCanvasColor',
        'uMatrix',
        'uRadius',
        'uInverted',
        'uMaskBounds',
        'uMaskOpacity',
    ]);
    const ellipseBuffer = gl.createBuffer();
    const ellipseTextureBuffer = gl.createBuffer();
    const fillEllipse = (vertices, width, height, backgroundColor, backgroundImage = TEXTURE_TRANSPARENT_INDEX, inverted) => {
        const { program, locations } = ellipseShader;
        gl.useProgram(program);
        gl.enableVertexAttribArray(locations.aPosition);
        gl.enableVertexAttribArray(locations.aTexCoord);
        gl.uniform2fv(locations.uRadius, [width * 0.5, height * 0.5]);
        gl.uniform1i(locations.uInverted, inverted ? 1 : 0);
        gl.uniform4fv(locations.uColor, backgroundColor);
        gl.uniformMatrix4fv(locations.uMatrix, false, markupMatrix);
        gl.uniform4f(locations.uCanvasColor, canvasColor[0], canvasColor[1], canvasColor[2], 1);
        gl.uniform1fv(locations.uMaskBounds, maskBounds);
        gl.uniform1f(locations.uMaskOpacity, maskOpacity);
        gl.uniform1i(locations.uTexture, backgroundImage);
        gl.activeTexture(gl.TEXTURE0 + backgroundImage);
        gl.bindTexture(gl.TEXTURE_2D, textures[backgroundImage].texture);
        gl.bindBuffer(gl.ARRAY_BUFFER, ellipseTextureBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, rectUV, gl.STATIC_DRAW);
        gl.vertexAttribPointer(locations.aTexCoord, 2, gl.FLOAT, false, 0, 0);
        gl.bindBuffer(gl.ARRAY_BUFFER, ellipseBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
        gl.vertexAttribPointer(locations.aPosition, 2, gl.FLOAT, false, 0, 0);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, vertices.length / 2);
        gl.disableVertexAttribArray(locations.aPosition);
        gl.disableVertexAttribArray(locations.aTexCoord);
    };
    //#endregion
    //
    // draw calls
    //
    const drawPath = (points, strokeWidth, strokeColor, opacity) => {
        // is no line
        if (points.length < 2)
            return;
        strokePath(points.map((p) => ({
            x: p.x * pixelDensity,
            y: p.y * pixelDensity,
        })), strokeWidth * pixelDensity, applyOpacity(strokeColor, opacity), false);
    };
    const drawTriangle = (points, rotation = 0, flipX = false, flipY = false, backgroundColor, opacity) => {
        if (!backgroundColor)
            return;
        const clonedPoints = points.map((p) => ({
            x: p.x * pixelDensity,
            y: p.y * pixelDensity,
        }));
        const center = quadCentroid(clonedPoints);
        if (flipX || flipY)
            vectorsFlip(clonedPoints, flipX, flipY, center.x, center.y);
        vectorsRotate(clonedPoints, rotation, center.x, center.y);
        const vertices = trianglePointToVertices(clonedPoints);
        fillTriangle(vertices, applyOpacity(backgroundColor, opacity));
    };
    const getBackgroundUV = (width, height, backgroundSize, backgroundPosition) => {
        if (!backgroundSize && !backgroundPosition)
            return rectUV;
        let x = backgroundPosition.x / backgroundSize.width;
        let y = backgroundPosition.y / backgroundSize.height;
        let w = width / backgroundSize.width / pixelDensity;
        let h = height / backgroundSize.height / pixelDensity;
        w -= x;
        h -= y;
        // prettier-ignore
        // B   D
        // | \ |
        // A  C
        // bottom left
        const ax = -x;
        const ay = h;
        // top left
        const bx = -x;
        const by = -y;
        // bottom right
        const cx = w;
        const cy = h;
        // top right
        const dx = w;
        const dy = -y;
        return new Float32Array([
            ax,
            ay,
            bx,
            by,
            cx,
            cy,
            dx,
            dy,
        ]);
    };
    const limitCornerRadius = (r, size) => Math.floor(clamp(r, 0, Math.min((size.width - 1) * 0.5, (size.height - 1) * 0.5)));
    const drawRect = (rect, rotation = 0, flipX = false, flipY = false, cornerRadius, backgroundColor, backgroundImage, backgroundSize = undefined, backgroundPosition = undefined, strokeWidth, strokeColor, opacity, maskFeather = undefined, colorize) => {
        // clone first
        let rectOut = rectMultiply(rectClone(rect), pixelDensity);
        // has radius, doesn't matter for coordinates
        const cornerRadiusOut = cornerRadius
            .map((r) => limitCornerRadius(r, rect))
            .map((r) => r * viewPixelDensity);
        // should fill
        if (backgroundColor || backgroundImage) {
            // adjust for edge anti-aliasing, if we don't do this the
            // visible rectangle will be 1 pixel smaller than the actual rectangle
            const rectFill = rectClone(rectOut);
            rectFill.x -= 0.5;
            rectFill.y -= 0.5;
            rectFill.width += 1.0;
            rectFill.height += 1.0;
            const points = createRectPoints(rectFill, rotation, flipX, flipY);
            const vertices = rectPointsToVertices(points);
            fillRect(vertices, rectFill.width, rectFill.height, cornerRadiusOut, applyOpacity(backgroundColor, opacity), backgroundImage, colorize && applyOpacity(colorize), getBackgroundUV(rectFill.width, rectFill.height, backgroundSize, backgroundPosition), maskFeather);
        }
        // should draw outline
        if (strokeWidth) {
            // fixes issue where stroke would render weirdly
            strokeWidth = Math.min(strokeWidth, rectOut.width, rectOut.height);
            strokePath(
            // rect out is already multiplied by pixel density
            createRectOutline(rectOut.x, rectOut.y, rectOut.width, rectOut.height, rotation, cornerRadiusOut, flipX, flipY), strokeWidth * pixelDensity, applyOpacity(strokeColor, opacity), true);
        }
    };
    const drawEllipse = (center, rx, ry, rotation, flipX, flipY, backgroundColor, backgroundImage, strokeWidth, strokeColor, opacity, inverted) => {
        let rectOut = rectMultiply(rectCreate(center.x - rx, center.y - ry, rx * 2, ry * 2), pixelDensity);
        if (backgroundColor || backgroundImage) {
            // adjust for edge anti-aliasing, if we don't do this the
            // visible rectangle will be 1 pixel smaller than the actual rectangle
            const rectFill = rectClone(rectOut);
            rectFill.x -= 0.5;
            rectFill.y -= 0.5;
            rectFill.width += 1.0;
            rectFill.height += 1.0;
            const points = createRectPoints(rectFill, rotation, flipX, flipY);
            const vertices = rectPointsToVertices(points);
            fillEllipse(vertices, rectFill.width, rectFill.height, applyOpacity(backgroundColor, opacity), backgroundImage, inverted);
        }
        if (strokeWidth)
            strokePath(
            // rect out is already multiplied by pixeldensity
            createEllipseOutline(rectOut.x, rectOut.y, rectOut.width, rectOut.height, rotation, flipX, flipY), strokeWidth * pixelDensity, applyOpacity(strokeColor, opacity), true);
    };
    //#endregion
    return {
        // draw api
        drawPath,
        drawTriangle,
        drawRect,
        drawEllipse,
        drawPreviewImage,
        setCanvasColor(color) {
            canvasColor = color;
        },
        drawToCanvas() {
            gl.bindFramebuffer(gl.FRAMEBUFFER, null);
            // switch transform matrix
            markupMatrix = markupMatrixCanvas;
            // tell webgl about the viewport
            gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
            // black (or other color depending on background)
            gl.colorMask(true, true, true, false);
            gl.clearColor(canvasColor[0], canvasColor[1], canvasColor[2], 1);
            // gl.clearColor(0.25, 0.25, 0.25, 1); // for debugging
            gl.clear(gl.COLOR_BUFFER_BIT);
        },
        drawToFramebuffer() {
            // is twice as big as the loaded image
            gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
            // switch transform matrix
            markupMatrix = markupMatrixFrameBuffer;
            // tell webgl about the "viewport"
            gl.viewport(0, 0, imageData.width, imageData.height);
            // always transparent
            gl.colorMask(true, true, true, true);
            gl.clearColor(0, 0, 0, 0);
            gl.clear(gl.COLOR_BUFFER_BIT);
        },
        // texture management
        addTexture,
        setTexture,
        reserveTexture,
        removeTexture,
        getTextureSize,
        getAvailableTextureIndex,
        // set mask
        enableMask(rect, opacity) {
            const maskX = rect.x * viewPixelDensity;
            const maskY = rect.y * viewPixelDensity;
            const maskWidth = rect.width * viewPixelDensity;
            const maskHeight = rect.height * viewPixelDensity;
            maskLeft = maskX;
            maskRight = maskLeft + maskWidth;
            maskTop = viewSize.height - maskY;
            maskBottom = viewSize.height - (maskY + maskHeight);
            maskOpacity = 1.0 - opacity;
            maskBounds = [maskTop, maskRight, maskBottom, maskLeft];
        },
        disableMask() {
            maskLeft = 0;
            maskRight = viewSize.width;
            maskTop = viewSize.height;
            maskBottom = 0;
            maskOpacity = 1;
            maskBounds = [maskTop, maskRight, maskBottom, maskLeft];
        },
        // canvas
        resize,
        release() {
            canvas.width = 1;
            canvas.height = 1;
        },
    };
};

/* src/native/ui/components/Canvas.svelte generated by Svelte v3.29.4 */

function create_fragment(ctx) {
	let div;
	let canvas_1;
	let measurable_action;
	let mounted;
	let dispose;

	return {
		c() {
			div = element("div");
			canvas_1 = element("canvas");
			attr(div, "class", "DokaCanvas");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			append(div, canvas_1);
			/*canvas_1_binding*/ ctx[15](canvas_1);

			if (!mounted) {
				dispose = [
					listen(canvas_1, "measure", /*handleSizeChange*/ ctx[4]),
					action_destroyer(measurable_action = measurable.call(null, canvas_1))
				];

				mounted = true;
			}
		},
		p: noop$1,
		i: noop$1,
		o: noop$1,
		d(detaching) {
			if (detaching) detach(div);
			/*canvas_1_binding*/ ctx[15](null);
			mounted = false;
			run_all(dispose);
		}
	};
}

function instance($$self, $$props, $$invalidate) {
	let $imageMain;
	let $background;
	let $mask;
	const dispatch = createEventDispatcher();

	// fallback matrix
	const COLOR_MATRIX_IDENTITY = [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0];

	let { animate } = $$props;
	let { imageData } = $$props;
	let { imageSize } = $$props;
	let { imageProps } = $$props;
	let { imagePreviews = [] } = $$props;
	let { maskRect } = $$props;
	let { pixelRatio = 1 } = $$props;
	let { willRender = passthrough } = $$props;
	let { loadImageData = passthrough } = $$props;
	let { backgroundColor } = $$props;

	//
	// springyness for main preview
	//
	const updateSpring = (spring, value) => spring.set(value, { hard: !animate });

	const blendWithCanvasBackground = (back, front) => {
		const [bR, bG, bB] = back;
		const [fR, fG, fB, fA] = front;
		return [fR * fA + bR * (1 - fA), fG * fA + bG * (1 - fA), fB * fA + bB * (1 - fA), 1];
	};

	const SPRING_PROPS = { precision: 0.0001 };
	const SPRING_PROPS_FRACTION = { precision: SPRING_PROPS.precision * 0.01 };
	const background = tweened(undefined, { duration: 250 });
	component_subscribe($$self, background, value => $$invalidate(22, $background = value));
	const translation = spring(undefined, SPRING_PROPS);
	const rotation = spring(undefined, SPRING_PROPS_FRACTION);
	const origin = spring(undefined, SPRING_PROPS);
	const perspective = spring(undefined, SPRING_PROPS_FRACTION);
	const scale = spring(undefined, SPRING_PROPS_FRACTION);
	const gamma = spring(undefined, SPRING_PROPS_FRACTION);
	const vignette = spring(undefined, SPRING_PROPS_FRACTION);
	const colorMatrix = spring([...COLOR_MATRIX_IDENTITY], SPRING_PROPS);
	const clarity = writable(undefined);
	const opacity = spring(0, SPRING_PROPS_FRACTION);
	const maskOpacity = spring(1, SPRING_PROPS_FRACTION);
	const mask = writable();
	component_subscribe($$self, mask, value => $$invalidate(24, $mask = value));
	const imageBackgroundColor = writable();

	const imageMain = derived(
		[
			origin,
			translation,
			perspective,
			rotation,
			scale,
			opacity,
			colorMatrix,
			mask,
			maskOpacity,
			clarity,
			gamma,
			vignette,
			imageBackgroundColor,
			background
		],
		([
				$origin,
				$translation,
				$perspective,
				$rotation,
				$scale,
				$opacity,
				$colorMatrix,
				$mask,
				$maskOpacity,
				$clarity,
				$gamma,
				$vignette,
				$imageBackgroundColor,
				$background
			], set) => {
			if (!imageProps || !$background) return set(undefined);
			const maskOpacityClamped = clamp($maskOpacity, 0, 1);

			set({
				origin: $origin,
				translation: $translation,
				perspective: $perspective,
				rotation: $rotation,
				scale: $scale,
				colorMatrix: $colorMatrix,
				opacity: clamp($opacity, 0, 1),
				clarity: $clarity,
				gamma: $gamma,
				vignette: $vignette,
				mask: $mask,
				maskOpacity: maskOpacityClamped,
				overlayColor: [$background[0], $background[1], $background[2], maskOpacityClamped],
				backgroundColor: $imageBackgroundColor
			});
		}
	);

	component_subscribe($$self, imageMain, value => $$invalidate(21, $imageMain = value));

	//
	// rendering
	//
	let canvas;

	let webGLImageDrawer = null;
	let width = null;
	let height = null;

	const drawPreviewImage = ({ origin, translation, rotation, scale, colorMatrix, opacity, clarity, gamma, vignette, offset, maskFeather, maskCornerRadius, backgroundColor, overlayColor, enableShapes }) => {
		let tx = translation.x;
		let ty = translation.y;

		if (offset) {
			tx += offset.x - width * 0.5;
			ty += offset.y - height * 0.5;
		}

		// calculate opaque backgroundColor if is background color is transparent and visible
		if (backgroundColor[3] < 1 && backgroundColor[3] > 0) {
			backgroundColor = blendWithCanvasBackground($background, backgroundColor);
		}

		webGLImageDrawer.drawPreviewImage(origin.x, origin.y, tx, ty, rotation.x, rotation.y, rotation.z, scale, colorMatrix || COLOR_MATRIX_IDENTITY, opacity, clarity, gamma, vignette, maskFeather, maskCornerRadius, backgroundColor, overlayColor, enableShapes);
	};

	// shape texture cache
	const TextureUnits = new Map([]);

	const getShapeImageTexture = ({ backgroundImage }) => {
		// get texture unit assigned to this image
		let texture = TextureUnits.get(backgroundImage);

		// texture already available, we done
		if (texture) return texture;

		// reserve texture unit
		texture = webGLImageDrawer.getAvailableTextureIndex();

		webGLImageDrawer.reserveTexture(texture);

		// this background is now available in this texture unit
		TextureUnits.set(backgroundImage, texture);

		// load the new background image data
		loadImageData(backgroundImage).then(imageData => {
			// create new texture
			webGLImageDrawer.setTexture(texture, imageData);

			// need to redraw because texture is now available
			requestAnimationFrame(drawUpdate);
		}).catch(err => {
			console.error(err);
		});

		return texture;
	};

	const textMeasureContext = createSimpleContext();

	const getShapeTextTexture = shape => {
		let { text, textAlign, fontFamily, fontSize, lineHeight, width } = shape;

		// we need this context to correctly wrap text
		updateTextContext(textMeasureContext, fontSize, fontFamily, textAlign);

		// wrap the text
		const textString = width ? wrapText(textMeasureContext, text, width) : text;

		// create UID for this texture so we can cache it and fetch it later on
		const textUID = shapeTextUID({ ...shape, text: textString });

		// get texture unit assigned to this specific text shape
		let texture = TextureUnits.get(textUID);

		if (texture) return texture;

		// we need to create a new texture
		const ctx = createSimpleContext();

		updateTextContext(ctx, fontSize, fontFamily, textAlign);

		// calculate canvas height
		resizeContextToFitText(ctx, textString, fontSize, lineHeight);

		// context resized, we now need to re-apply style
		updateTextContext(ctx, fontSize, fontFamily, textAlign);

		// if so, draw text and update texture
		drawText(ctx, textString, {
			fontSize,
			textAlign,
			lineHeight,
			lineWidth: ctx.canvas.width
		});

		// TODO: Create power of 2 texture and update texture instead of delete -> replace
		// add this texture
		texture = webGLImageDrawer.addTexture(ctx.canvas);

		// Store reference to the texture
		TextureUnits.set(textUID, texture);

		return texture;
	};

	const getShapeTexture = shape => {
		let texture;

		// let's create textures for backgrounds and texts
		if (shape.backgroundImage) {
			texture = getShapeImageTexture(shape);
		} else if (isString(shape.text)) {
			if (shape.width && shape.width < 1 || shape.height && shape.height < 1) return undefined;
			texture = getShapeTextTexture(shape);
		}

		return texture;
	};

	const drawShapes = (shapes = []) => shapes.map(shape => {
		let texture = getShapeTexture(shape);

		if (isArray(shape.points)) {
			// is triangle
			if (shape.points.length === 3 && shape.backgroundColor) {
				webGLImageDrawer.drawTriangle(shape.points, shape.rotation, shape.flipX, shape.flipY, shape.backgroundColor, shape.strokeWidth, shape.strokeColor, shape.opacity);
			} else // is normal path
			{
				webGLImageDrawer.drawPath(shape.points, shape.strokeWidth, shape.strokeColor, shape.opacity);
			}
		} else // is ellipse 
		if (isNumber(shape.rx)) {
			webGLImageDrawer.drawEllipse(shape, shape.rx, shape.ry, shape.rotation, shape.flipX, shape.flipY, shape.backgroundColor, texture, shape.strokeWidth, shape.strokeColor, shape.opacity, shape.inverted);
		} else // is rect
		if (isString(shape.text) && texture || shape.width) {
			const textureSize = texture && webGLImageDrawer.getTextureSize(texture);
			let colorize = undefined;

			let shapeRect = shape.width
			? shape
			: { x: shape.x, y: shape.y, ...textureSize };

			let backgroundSize;
			let backgroundPosition;

			if (textureSize) {
				// background should be scaled
				if (shape.backgroundImage && shape.backgroundSize) {
					// always respect texture aspect ratio
					const textureAspectRatio = getAspectRatio(textureSize.width, textureSize.height);

					// adjust position of background
					if (shape.backgroundSize === "contain") {
						const rect = rectContainRect(shape, textureAspectRatio, shapeRect);
						backgroundSize = sizeCreateFromRect(rect);
						backgroundPosition = vectorCreate((shape.width - backgroundSize.width) * 0.5, (shape.height - backgroundSize.height) * 0.5);
					} else if (shape.backgroundSize === "cover") {
						const rect = rectCoverRect(shape, textureAspectRatio, shapeRect);
						backgroundSize = sizeCreateFromRect(rect);
						backgroundPosition = vectorCreate(rect.x, rect.y);
						backgroundPosition = vectorCreate((shape.width - backgroundSize.width) * 0.5, (shape.height - backgroundSize.height) * 0.5);
					}
				} else // is text, "background" should be texture size and be positioned based on alignment
				if (shape.text && shape.width) {
					// position texture based on text alignment
					backgroundSize = textureSize;

					backgroundPosition = vectorCreate(0, 0);

					if (shape.textAlign === "center") {
						backgroundPosition.x = (shape.width - textureSize.width) * 0.5;
					}

					if (shape.textAlign === "right") {
						backgroundPosition.x = shape.width - textureSize.width;
					}

					colorize = shape.color;
				}
			}

			webGLImageDrawer.drawRect(
				shapeRect,
				shape.rotation,
				shape.flipX,
				shape.flipY,
				[
					shape.cornerRadius,
					shape.cornerRadius,
					shape.cornerRadius,
					shape.cornerRadius
				],
				shape.backgroundColor,
				texture,
				backgroundSize,
				backgroundPosition,
				shape.strokeWidth,
				shape.strokeColor,
				shape.opacity,
				undefined,
				colorize
			);
		}

		return texture ? [shape.id, texture] : undefined;
	}).filter(Boolean);

	// redraws state
	const redraw = () => {
		// allow dev to inject more shapes
		const canvas = willRender({
			opacity: $imageMain.opacity, // opacity of main image, can be used to change opacity of other elements
			rotation: $imageMain.rotation,
			scale: $imageMain.scale,
			size: canvasSize,
			backgroundColor: [...$background]
		});

		canvas.backgroundColor = [...$background];
		canvas.image = $imageMain;
		canvas.imageMask = $imageMain.mask;
		canvas.imageMaskOpacity = $imageMain.maskOpacity;
		canvas.imagePreviews = imagePreviews;

		// no need to draw to framebuffer is no annotations
		const hasAnnotations = canvas.annotationShapes.length > 0;

		// if image has background color
		const hasImageBackgroundColor = $imageMain.backgroundColor[3] > 0;

		// if the overlay is transparent so we can see the canvas
		const hasTransparentOverlay = $imageMain.maskOpacity < 1;

		// array of textures used in this draw call
		const usedTextures = [];

		// set canvas background color to image background color if is defined
		if (hasTransparentOverlay && hasImageBackgroundColor) {
			const backR = canvas.backgroundColor[0];
			const backG = canvas.backgroundColor[1];
			const backB = canvas.backgroundColor[2];
			const frontA = 1 - $imageMain.maskOpacity;
			const frontR = $imageMain.backgroundColor[0] * frontA;
			const frontG = $imageMain.backgroundColor[1] * frontA;
			const frontB = $imageMain.backgroundColor[2] * frontA;
			const fA = 1 - frontA;
			canvas.backgroundColor = [frontR + backR * fA, frontG + backG * fA, frontB + backB * fA, 1];
		}

		webGLImageDrawer.setCanvasColor(canvas.backgroundColor);

		// if has annotations draw annotation shapes to framebuffer
		// TODO: only run this if annotations have changed
		if (hasAnnotations) {
			webGLImageDrawer.disableMask();
			webGLImageDrawer.drawToFramebuffer();
			usedTextures.push(...drawShapes(canvas.annotationShapes));
		}

		// switch to canvas drawing for other elements
		webGLImageDrawer.drawToCanvas();

		webGLImageDrawer.enableMask(canvas.imageMask, canvas.imageMaskOpacity);

		// draw a colored rectangle behind main preview image
		if (hasImageBackgroundColor) {
			webGLImageDrawer.drawRect($mask, 0, false, false, [0, 0, 0, 0], blendWithCanvasBackground($background, $imageMain.backgroundColor));
		}

		// draw main preview image
		canvas.image.enableShapes = hasAnnotations;

		drawPreviewImage(canvas.image);

		// TODO: move vignette here (draw with colorized circular gradient texture instead of in shader)
		// draw decorations shapes relative to crop
		usedTextures.push(...drawShapes(canvas.decorationShapes));

		// crop mask not used for interface
		webGLImageDrawer.disableMask();

		// draw custom interface shapes
		usedTextures.push(...drawShapes(canvas.interfaceShapes));

		canvas.imagePreviews.forEach(image => {
			webGLImageDrawer.enableMask(image.mask, image.maskOpacity);

			// draw background fill
			if (hasImageBackgroundColor) {
				webGLImageDrawer.drawRect(image.mask, 0, false, false, image.maskCornerRadius, $imageMain.backgroundColor, undefined, undefined, undefined, undefined, undefined, image.opacity, image.maskFeather);
			}

			drawPreviewImage({
				...image,
				backgroundColor: $imageMain.backgroundColor,
				overlayColor: [0, 0, 0, 0],
				enableShapes: false
			});
		});

		webGLImageDrawer.disableMask();

		// determine which textures can be dropped
		TextureUnits.forEach((registeredTextureUnit, key, map) => {
			const isUsed = !!usedTextures.find(([id, usedTextureUnit]) => usedTextureUnit === registeredTextureUnit);

			// stil used, no need to release
			if (isUsed) return;

			// remove this texture
			TextureUnits.delete(key);

			webGLImageDrawer.removeTexture(registeredTextureUnit);
		});
	};

	//
	// events
	//
	// auto update when size changes
	const handleSizeChange = e => {
		$$invalidate(17, width = e.detail.width);
		$$invalidate(18, height = e.detail.height);
		dispatch("measure", { width, height });
	};

	//
	// hooks
	//
	// after DOM has been altered, draw to canvas
	afterUpdate(() => drawUpdate());

	// hook up canvas and image
	onMount(() => $$invalidate(16, webGLImageDrawer = createGLImageDrawer(canvas, imageData, imageSize, pixelRatio)));

	// clean up canvas
	onDestroy(() => {
		// done drawing
		webGLImageDrawer.release();

		// force release canvas for Safari
		releaseCanvas(textMeasureContext.canvas);
	});

	function canvas_1_binding($$value) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			canvas = $$value;
			$$invalidate(0, canvas);
		});
	}

	$$self.$$set = $$props => {
		if ("animate" in $$props) $$invalidate(5, animate = $$props.animate);
		if ("imageData" in $$props) $$invalidate(6, imageData = $$props.imageData);
		if ("imageSize" in $$props) $$invalidate(7, imageSize = $$props.imageSize);
		if ("imageProps" in $$props) $$invalidate(8, imageProps = $$props.imageProps);
		if ("imagePreviews" in $$props) $$invalidate(9, imagePreviews = $$props.imagePreviews);
		if ("maskRect" in $$props) $$invalidate(10, maskRect = $$props.maskRect);
		if ("pixelRatio" in $$props) $$invalidate(11, pixelRatio = $$props.pixelRatio);
		if ("willRender" in $$props) $$invalidate(12, willRender = $$props.willRender);
		if ("loadImageData" in $$props) $$invalidate(13, loadImageData = $$props.loadImageData);
		if ("backgroundColor" in $$props) $$invalidate(14, backgroundColor = $$props.backgroundColor);
	};

	let canvasSize;
	let canDraw;
	let drawUpdate;

	$$self.$$.update = () => {
		if ($$self.$$.dirty[0] & /*backgroundColor*/ 16384) {
			// canvas style
			 backgroundColor && updateSpring(background, backgroundColor);
		}

		if ($$self.$$.dirty[0] & /*imageProps*/ 256) {
			// image transforms
			 imageProps && updateSpring(origin, imageProps.origin);
		}

		if ($$self.$$.dirty[0] & /*imageProps*/ 256) {
			 imageProps && updateSpring(translation, imageProps.translation);
		}

		if ($$self.$$.dirty[0] & /*imageProps*/ 256) {
			 imageProps && updateSpring(perspective, imageProps.perspective);
		}

		if ($$self.$$.dirty[0] & /*imageProps*/ 256) {
			 imageProps && updateSpring(rotation, imageProps.rotation);
		}

		if ($$self.$$.dirty[0] & /*imageProps*/ 256) {
			 imageProps && updateSpring(scale, imageProps.scale);
		}

		if ($$self.$$.dirty[0] & /*imageProps*/ 256) {
			 imageProps && updateSpring(colorMatrix, imageProps.colorMatrix || [...COLOR_MATRIX_IDENTITY]);
		}

		if ($$self.$$.dirty[0] & /*imageProps*/ 256) {
			 imageProps && updateSpring(vignette, imageProps.vignette || 0);
		}

		if ($$self.$$.dirty[0] & /*imageProps*/ 256) {
			 imageProps && updateSpring(gamma, imageProps.gamma || 1);
		}

		if ($$self.$$.dirty[0] & /*imageProps*/ 256) {
			 imageProps && clarity.set(imageProps.convolutionMatrix && imageProps.convolutionMatrix.clarity || undefined);
		}

		if ($$self.$$.dirty[0] & /*imageProps*/ 256) {
			// image ui style
			 imageProps && updateSpring(opacity, 1);
		}

		if ($$self.$$.dirty[0] & /*imageProps*/ 256) {
			 imageProps && updateSpring(maskOpacity, isNumber(imageProps.maskOpacity)
			? imageProps.maskOpacity
			: 1);
		}

		if ($$self.$$.dirty[0] & /*maskRect*/ 1024) {
			 maskRect && mask.set(maskRect);
		}

		if ($$self.$$.dirty[0] & /*imageProps*/ 256) {
			 imageProps && imageBackgroundColor.set(imageProps.backgroundColor);
		}

		if ($$self.$$.dirty[0] & /*width, height*/ 393216) {
			// canvas size
			 canvasSize = { width, height };
		}

		if ($$self.$$.dirty[0] & /*webGLImageDrawer, width, height, $imageMain*/ 2555904) {
			// can draw view
			 $$invalidate(20, canDraw = webGLImageDrawer && width && height && $imageMain);
		}

		if ($$self.$$.dirty[0] & /*width, height, webGLImageDrawer, pixelRatio*/ 460800) {
			// observe width and height changes and resize the canvas proportionally
			 width && height && webGLImageDrawer && webGLImageDrawer.resize(width, height, pixelRatio);
		}

		if ($$self.$$.dirty[0] & /*canDraw*/ 1048576) {
			// switch to draw method when can draw
			 drawUpdate = canDraw ? redraw : noop;
		}
	};

	return [
		canvas,
		background,
		mask,
		imageMain,
		handleSizeChange,
		animate,
		imageData,
		imageSize,
		imageProps,
		imagePreviews,
		maskRect,
		pixelRatio,
		willRender,
		loadImageData,
		backgroundColor,
		canvas_1_binding
	];
}

class Canvas extends SvelteComponent {
	constructor(options) {
		super();

		init(
			this,
			options,
			instance,
			create_fragment,
			safe_not_equal,
			{
				animate: 5,
				imageData: 6,
				imageSize: 7,
				imageProps: 8,
				imagePreviews: 9,
				maskRect: 10,
				pixelRatio: 11,
				willRender: 12,
				loadImageData: 13,
				backgroundColor: 14
			},
			[-1, -1]
		);
	}
}

var arrayJoin = (arr, filter = Boolean, str = ' ') => arr.filter(filter).join(str);

/* src/native/ui/components/TabList.svelte generated by Svelte v3.29.4 */
const get_default_slot_changes = dirty => ({ tab: dirty & /*tabNodes*/ 4 });
const get_default_slot_context = ctx => ({ tab: /*tab*/ ctx[15] });

function get_each_context(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[15] = list[i];
	return child_ctx;
}

// (63:4) {#each tabNodes as tab (tab.id) }
function create_each_block(key_1, ctx) {
	let li;
	let button;
	let button_aria_controls_value;
	let button_id_value;
	let button_aria_selected_value;
	let t;
	let current;
	let mounted;
	let dispose;
	const default_slot_template = /*#slots*/ ctx[9].default;
	const default_slot = create_slot(default_slot_template, ctx, /*$$scope*/ ctx[8], get_default_slot_context);

	function keydown_handler(...args) {
		return /*keydown_handler*/ ctx[10](/*tab*/ ctx[15], ...args);
	}

	function click_handler(...args) {
		return /*click_handler*/ ctx[11](/*tab*/ ctx[15], ...args);
	}

	return {
		key: key_1,
		first: null,
		c() {
			li = element("li");
			button = element("button");
			if (default_slot) default_slot.c();
			t = space();
			attr(button, "role", "tab");
			attr(button, "aria-controls", button_aria_controls_value = /*tab*/ ctx[15].href);
			attr(button, "id", button_id_value = /*tab*/ ctx[15].tabId);
			attr(button, "aria-selected", button_aria_selected_value = /*tab*/ ctx[15].selected);
			this.first = li;
		},
		m(target, anchor) {
			insert(target, li, anchor);
			append(li, button);

			if (default_slot) {
				default_slot.m(button, null);
			}

			append(li, t);
			current = true;

			if (!mounted) {
				dispose = [
					listen(button, "keydown", keydown_handler),
					listen(button, "click", click_handler)
				];

				mounted = true;
			}
		},
		p(new_ctx, dirty) {
			ctx = new_ctx;

			if (default_slot) {
				if (default_slot.p && dirty & /*$$scope, tabNodes*/ 260) {
					update_slot(default_slot, default_slot_template, ctx, /*$$scope*/ ctx[8], dirty, get_default_slot_changes, get_default_slot_context);
				}
			}

			if (!current || dirty & /*tabNodes*/ 4 && button_aria_controls_value !== (button_aria_controls_value = /*tab*/ ctx[15].href)) {
				attr(button, "aria-controls", button_aria_controls_value);
			}

			if (!current || dirty & /*tabNodes*/ 4 && button_id_value !== (button_id_value = /*tab*/ ctx[15].tabId)) {
				attr(button, "id", button_id_value);
			}

			if (!current || dirty & /*tabNodes*/ 4 && button_aria_selected_value !== (button_aria_selected_value = /*tab*/ ctx[15].selected)) {
				attr(button, "aria-selected", button_aria_selected_value);
			}
		},
		i(local) {
			if (current) return;
			transition_in(default_slot, local);
			current = true;
		},
		o(local) {
			transition_out(default_slot, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(li);
			if (default_slot) default_slot.d(detaching);
			mounted = false;
			run_all(dispose);
		}
	};
}

function create_fragment$1(ctx) {
	let ul;
	let each_blocks = [];
	let each_1_lookup = new Map();
	let ul_class_value;
	let current;
	let each_value = /*tabNodes*/ ctx[2];
	const get_key = ctx => /*tab*/ ctx[15].id;

	for (let i = 0; i < each_value.length; i += 1) {
		let child_ctx = get_each_context(ctx, each_value, i);
		let key = get_key(child_ctx);
		each_1_lookup.set(key, each_blocks[i] = create_each_block(key, child_ctx));
	}

	return {
		c() {
			ul = element("ul");

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			attr(ul, "class", ul_class_value = arrayJoin(["DokaTabList", /*klass*/ ctx[0]]));
			attr(ul, "role", "tablist");
		},
		m(target, anchor) {
			insert(target, ul, anchor);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].m(ul, null);
			}

			/*ul_binding*/ ctx[12](ul);
			current = true;
		},
		p(ctx, [dirty]) {
			if (dirty & /*tabNodes, handleKeyTab, handleClickTab, $$scope*/ 284) {
				const each_value = /*tabNodes*/ ctx[2];
				group_outros();
				each_blocks = update_keyed_each(each_blocks, dirty, get_key, 1, ctx, each_value, each_1_lookup, ul, outro_and_destroy_block, create_each_block, null, get_each_context);
				check_outros();
			}

			if (!current || dirty & /*klass*/ 1 && ul_class_value !== (ul_class_value = arrayJoin(["DokaTabList", /*klass*/ ctx[0]]))) {
				attr(ul, "class", ul_class_value);
			}
		},
		i(local) {
			if (current) return;

			for (let i = 0; i < each_value.length; i += 1) {
				transition_in(each_blocks[i]);
			}

			current = true;
		},
		o(local) {
			for (let i = 0; i < each_blocks.length; i += 1) {
				transition_out(each_blocks[i]);
			}

			current = false;
		},
		d(detaching) {
			if (detaching) detach(ul);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].d();
			}

			/*ul_binding*/ ctx[12](null);
		}
	};
}

function instance$1($$self, $$props, $$invalidate) {
	let { $$slots: slots = {}, $$scope } = $$props;
	let root;
	let { class: klass = undefined } = $$props;
	let { name } = $$props;
	let { selected } = $$props;
	let { tabs = [] } = $$props;
	const dispatch = createEventDispatcher();

	const focusTab = index => {
		const tab = root.querySelectorAll("button[role=\"tab\"]")[index];
		if (!tab) return;
		tab.focus();
	};

	const handleClickTab = (e, id) => {
		e.preventDefault();
		e.stopPropagation();
		dispatch("select", id);
	};

	const handleKeyTab = ({ key }, id) => {
		if (!(/arrow/i).test(key)) return;
		const index = tabs.findIndex(tab => tab.id === id);

		// next
		if ((/right|down/i).test(key)) return focusTab(index < tabs.length - 1 ? index + 1 : 0);

		// prev
		if ((/left|up/i).test(key)) return focusTab(index > 0 ? index - 1 : tabs.length - 1);
	};

	const keydown_handler = (tab, e) => handleKeyTab(e, tab.id);
	const click_handler = (tab, e) => handleClickTab(e, tab.id);

	function ul_binding($$value) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			root = $$value;
			$$invalidate(1, root);
		});
	}

	$$self.$$set = $$props => {
		if ("class" in $$props) $$invalidate(0, klass = $$props.class);
		if ("name" in $$props) $$invalidate(5, name = $$props.name);
		if ("selected" in $$props) $$invalidate(6, selected = $$props.selected);
		if ("tabs" in $$props) $$invalidate(7, tabs = $$props.tabs);
		if ("$$scope" in $$props) $$invalidate(8, $$scope = $$props.$$scope);
	};

	let tabNodes;

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*tabs, selected, name*/ 224) {
			 $$invalidate(2, tabNodes = tabs.map(tab => {
				const isActive = tab.id === selected;

				return {
					...tab,
					tabId: `doka-tab-${name}-${tab.id}`,
					href: `#doka-${name}-${tab.id}`,
					selected: isActive
				};
			}));
		}
	};

	return [
		klass,
		root,
		tabNodes,
		handleClickTab,
		handleKeyTab,
		name,
		selected,
		tabs,
		$$scope,
		slots,
		keydown_handler,
		click_handler,
		ul_binding
	];
}

class TabList extends SvelteComponent {
	constructor(options) {
		super();
		init(this, options, instance$1, create_fragment$1, safe_not_equal, { class: 0, name: 5, selected: 6, tabs: 7 });
	}
}

/* src/native/ui/components/TabPanels.svelte generated by Svelte v3.29.4 */
const get_default_slot_changes$1 = dirty => ({ panel: dirty & /*panelNodes*/ 8 });
const get_default_slot_context$1 = ctx => ({ panel: /*id*/ ctx[10] });

function get_each_context$1(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[10] = list[i].id;
	child_ctx[11] = list[i].panelId;
	child_ctx[12] = list[i].tabindex;
	child_ctx[13] = list[i].labelledBy;
	child_ctx[14] = list[i].hidden;
	child_ctx[2] = list[i].visible;
	return child_ctx;
}

// (60:4) {#each panelNodes as { id, panelId, tabindex, labelledBy, hidden, visible }
function create_each_block$1(key_1, ctx) {
	let div;
	let t;
	let div_class_value;
	let div_hidden_value;
	let div_id_value;
	let div_tabindex_value;
	let div_aria_labelledby_value;
	let div_data_inert_value;
	let current;
	const default_slot_template = /*#slots*/ ctx[8].default;
	const default_slot = create_slot(default_slot_template, ctx, /*$$scope*/ ctx[7], get_default_slot_context$1);

	return {
		key: key_1,
		first: null,
		c() {
			div = element("div");
			if (default_slot) default_slot.c();
			t = space();
			attr(div, "class", div_class_value = arrayJoin(["DokaTabPanel", /*panelClass*/ ctx[1]]));
			div.hidden = div_hidden_value = /*hidden*/ ctx[14];
			attr(div, "id", div_id_value = /*panelId*/ ctx[11]);
			attr(div, "tabindex", div_tabindex_value = /*tabindex*/ ctx[12]);
			attr(div, "aria-labelledby", div_aria_labelledby_value = /*labelledBy*/ ctx[13]);
			attr(div, "data-inert", div_data_inert_value = !/*visible*/ ctx[2]);
			this.first = div;
		},
		m(target, anchor) {
			insert(target, div, anchor);

			if (default_slot) {
				default_slot.m(div, null);
			}

			append(div, t);
			current = true;
		},
		p(ctx, dirty) {
			if (default_slot) {
				if (default_slot.p && dirty & /*$$scope, panelNodes*/ 136) {
					update_slot(default_slot, default_slot_template, ctx, /*$$scope*/ ctx[7], dirty, get_default_slot_changes$1, get_default_slot_context$1);
				}
			}

			if (!current || dirty & /*panelClass*/ 2 && div_class_value !== (div_class_value = arrayJoin(["DokaTabPanel", /*panelClass*/ ctx[1]]))) {
				attr(div, "class", div_class_value);
			}

			if (!current || dirty & /*panelNodes*/ 8 && div_hidden_value !== (div_hidden_value = /*hidden*/ ctx[14])) {
				div.hidden = div_hidden_value;
			}

			if (!current || dirty & /*panelNodes*/ 8 && div_id_value !== (div_id_value = /*panelId*/ ctx[11])) {
				attr(div, "id", div_id_value);
			}

			if (!current || dirty & /*panelNodes*/ 8 && div_tabindex_value !== (div_tabindex_value = /*tabindex*/ ctx[12])) {
				attr(div, "tabindex", div_tabindex_value);
			}

			if (!current || dirty & /*panelNodes*/ 8 && div_aria_labelledby_value !== (div_aria_labelledby_value = /*labelledBy*/ ctx[13])) {
				attr(div, "aria-labelledby", div_aria_labelledby_value);
			}

			if (!current || dirty & /*panelNodes*/ 8 && div_data_inert_value !== (div_data_inert_value = !/*visible*/ ctx[2])) {
				attr(div, "data-inert", div_data_inert_value);
			}
		},
		i(local) {
			if (current) return;
			transition_in(default_slot, local);
			current = true;
		},
		o(local) {
			transition_out(default_slot, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			if (default_slot) default_slot.d(detaching);
		}
	};
}

function create_fragment$2(ctx) {
	let div;
	let each_blocks = [];
	let each_1_lookup = new Map();
	let div_class_value;
	let measurable_action;
	let current;
	let mounted;
	let dispose;
	let each_value = /*panelNodes*/ ctx[3];
	const get_key = ctx => /*id*/ ctx[10];

	for (let i = 0; i < each_value.length; i += 1) {
		let child_ctx = get_each_context$1(ctx, each_value, i);
		let key = get_key(child_ctx);
		each_1_lookup.set(key, each_blocks[i] = create_each_block$1(key, child_ctx));
	}

	return {
		c() {
			div = element("div");

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			attr(div, "class", div_class_value = arrayJoin(["DokaTabPanels", /*klass*/ ctx[0]]));
		},
		m(target, anchor) {
			insert(target, div, anchor);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].m(div, null);
			}

			current = true;

			if (!mounted) {
				dispose = [
					listen(div, "measure", /*measure_handler*/ ctx[9]),
					action_destroyer(measurable_action = measurable.call(null, div))
				];

				mounted = true;
			}
		},
		p(ctx, [dirty]) {
			if (dirty & /*arrayJoin, panelClass, panelNodes, $$scope*/ 138) {
				const each_value = /*panelNodes*/ ctx[3];
				group_outros();
				each_blocks = update_keyed_each(each_blocks, dirty, get_key, 1, ctx, each_value, each_1_lookup, div, outro_and_destroy_block, create_each_block$1, null, get_each_context$1);
				check_outros();
			}

			if (!current || dirty & /*klass*/ 1 && div_class_value !== (div_class_value = arrayJoin(["DokaTabPanels", /*klass*/ ctx[0]]))) {
				attr(div, "class", div_class_value);
			}
		},
		i(local) {
			if (current) return;

			for (let i = 0; i < each_value.length; i += 1) {
				transition_in(each_blocks[i]);
			}

			current = true;
		},
		o(local) {
			for (let i = 0; i < each_blocks.length; i += 1) {
				transition_out(each_blocks[i]);
			}

			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].d();
			}

			mounted = false;
			run_all(dispose);
		}
	};
}

function instance$2($$self, $$props, $$invalidate) {
	let { $$slots: slots = {}, $$scope } = $$props;
	let { class: klass = undefined } = $$props;
	let { name } = $$props;
	let { selected } = $$props;
	let { visible = undefined } = $$props;
	let { panelClass = undefined } = $$props;
	let { panels = [] } = $$props;

	function measure_handler(event) {
		bubble($$self, event);
	}

	$$self.$$set = $$props => {
		if ("class" in $$props) $$invalidate(0, klass = $$props.class);
		if ("name" in $$props) $$invalidate(4, name = $$props.name);
		if ("selected" in $$props) $$invalidate(5, selected = $$props.selected);
		if ("visible" in $$props) $$invalidate(2, visible = $$props.visible);
		if ("panelClass" in $$props) $$invalidate(1, panelClass = $$props.panelClass);
		if ("panels" in $$props) $$invalidate(6, panels = $$props.panels);
		if ("$$scope" in $$props) $$invalidate(7, $$scope = $$props.$$scope);
	};

	let panelNodes;

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*panels, selected, visible, name*/ 116) {
			 $$invalidate(3, panelNodes = panels.map(id => {
				const isActive = id === selected;
				const isVisible = visible ? visible.indexOf(id) !== -1 : true;

				return {
					id,
					panelId: `doka-${name}_${id}`,
					labelledBy: `doka-tab-${name}_${id}`,
					hidden: !isActive,
					visible: isVisible,
					tabindex: isActive ? 0 : -1
				};
			}));
		}
	};

	return [
		klass,
		panelClass,
		visible,
		panelNodes,
		name,
		selected,
		panels,
		$$scope,
		slots,
		measure_handler
	];
}

class TabPanels extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$2, create_fragment$2, safe_not_equal, {
			class: 0,
			name: 4,
			selected: 5,
			visible: 2,
			panelClass: 1,
			panels: 6
		});
	}
}

/* src/native/ui/components/Panel.svelte generated by Svelte v3.29.4 */

function create_fragment$3(ctx) {
	let div;
	let switch_instance;
	let updating_name;
	let div_class_value;
	let current;
	const switch_instance_spread_levels = [/*componentProps*/ ctx[7]];

	function switch_instance_name_binding(value) {
		/*switch_instance_name_binding*/ ctx[17].call(null, value);
	}

	var switch_value = /*componentView*/ ctx[11];

	function switch_props(ctx) {
		let switch_instance_props = {};

		for (let i = 0; i < switch_instance_spread_levels.length; i += 1) {
			switch_instance_props = assign(switch_instance_props, switch_instance_spread_levels[i]);
		}

		if (/*panelName*/ ctx[3] !== void 0) {
			switch_instance_props.name = /*panelName*/ ctx[3];
		}

		return { props: switch_instance_props };
	}

	if (switch_value) {
		switch_instance = new switch_value(switch_props(ctx));
		binding_callbacks.push(() => bind(switch_instance, "name", switch_instance_name_binding));
		/*switch_instance_binding*/ ctx[18](switch_instance);
		switch_instance.$on("measure", /*measure_handler*/ ctx[19]);
	}

	return {
		c() {
			div = element("div");
			if (switch_instance) create_component(switch_instance.$$.fragment);
			attr(div, "data-util", /*panelName*/ ctx[3]);
			attr(div, "class", div_class_value = arrayJoin(["DokaPanel", /*klass*/ ctx[1]]));
			attr(div, "style", /*style*/ ctx[6]);
		},
		m(target, anchor) {
			insert(target, div, anchor);

			if (switch_instance) {
				mount_component(switch_instance, div, null);
			}

			current = true;
		},
		p(ctx, [dirty]) {
			const switch_instance_changes = (dirty & /*componentProps*/ 128)
			? get_spread_update(switch_instance_spread_levels, [get_spread_object(/*componentProps*/ ctx[7])])
			: {};

			if (!updating_name && dirty & /*panelName*/ 8) {
				updating_name = true;
				switch_instance_changes.name = /*panelName*/ ctx[3];
				add_flush_callback(() => updating_name = false);
			}

			if (switch_value !== (switch_value = /*componentView*/ ctx[11])) {
				if (switch_instance) {
					group_outros();
					const old_component = switch_instance;

					transition_out(old_component.$$.fragment, 1, 0, () => {
						destroy_component(old_component, 1);
					});

					check_outros();
				}

				if (switch_value) {
					switch_instance = new switch_value(switch_props(ctx));
					binding_callbacks.push(() => bind(switch_instance, "name", switch_instance_name_binding));
					/*switch_instance_binding*/ ctx[18](switch_instance);
					switch_instance.$on("measure", /*measure_handler*/ ctx[19]);
					create_component(switch_instance.$$.fragment);
					transition_in(switch_instance.$$.fragment, 1);
					mount_component(switch_instance, div, null);
				} else {
					switch_instance = null;
				}
			} else if (switch_value) {
				switch_instance.$set(switch_instance_changes);
			}

			if (!current || dirty & /*panelName*/ 8) {
				attr(div, "data-util", /*panelName*/ ctx[3]);
			}

			if (!current || dirty & /*klass*/ 2 && div_class_value !== (div_class_value = arrayJoin(["DokaPanel", /*klass*/ ctx[1]]))) {
				attr(div, "class", div_class_value);
			}

			if (!current || dirty & /*style*/ 64) {
				attr(div, "style", /*style*/ ctx[6]);
			}
		},
		i(local) {
			if (current) return;
			if (switch_instance) transition_in(switch_instance.$$.fragment, local);
			current = true;
		},
		o(local) {
			if (switch_instance) transition_out(switch_instance.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			/*switch_instance_binding*/ ctx[18](null);
			if (switch_instance) destroy_component(switch_instance);
		}
	};
}

function instance$3($$self, $$props, $$invalidate) {
	let $opacityClamped;
	let $isActivePrivateStore;
	const dispatch = createEventDispatcher();
	let { isActive = true } = $$props;
	let { stores } = $$props;
	let { content } = $$props;
	let { component } = $$props;
	let { locale } = $$props;
	let { class: klass = undefined } = $$props;

	// we remember the view rect in this variable
	let rect;

	const opacity = spring(0);
	const opacityClamped = derived(opacity, $opacity => clamp($opacity, 0, 1));
	component_subscribe($$self, opacityClamped, value => $$invalidate(22, $opacityClamped = value));

	// throw hide / show events
	let isHidden = !isActive;

	// create active store so can be used in derived stores
	const isActivePrivateStore = writable(isActive);

	component_subscribe($$self, isActivePrivateStore, value => $$invalidate(23, $isActivePrivateStore = value));

	const stateProps = {
		isActive: derived(isActivePrivateStore, $isActivePrivateStore => $isActivePrivateStore),
		isActiveFraction: derived(opacityClamped, $opacityClamped => $opacityClamped),
		isVisible: derived(opacityClamped, $opacityClamped => $opacityClamped > 0)
	};

	// build the component props
	const componentView = content.view;

	const componentExportedProps = getComponentExportedProps(componentView);

	const componentComputedProps = Object.keys(content.props || {}).reduce(
		(computedProps, key) => {
			if (!componentExportedProps.includes(key)) return computedProps;
			computedProps[key] = content.props[key];
			return computedProps;
		},
		{}
	);

	const componentComputedStateProps = Object.keys(stateProps).reduce(
		(computedStateProps, key) => {
			if (!componentExportedProps.includes(key)) return computedStateProps;
			computedStateProps[key] = stateProps[key];
			return computedStateProps;
		},
		{}
	);

	// class used on panel element
	let panelName;

	// we use the `hasBeenMounted` bool to block rect updates until the entire panel is ready
	let hasBeenMounted = false;

	let preMountRect;
	let mountRectTimeout;

	onMount(() => {
		$$invalidate(4, hasBeenMounted = true);

		// this fixes a weird issue where the measure event isn't fired 
		// when no components active in subview
		clearTimeout(mountRectTimeout);

		mountRectTimeout = setTimeout(
			() => {
				if (rect || !preMountRect) return;
				dispatch("measure", { ...preMountRect });
			},
			0
		);
	});

	function switch_instance_name_binding(value) {
		panelName = value;
		$$invalidate(3, panelName);
	}

	function switch_instance_binding($$value) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			component = $$value;
			$$invalidate(0, component);
		});
	}

	const measure_handler = e => {
		$$invalidate(5, preMountRect = { ...e.detail });
		if (!hasBeenMounted) return;
		$$invalidate(2, rect = e.detail);
		dispatch("measure", { ...rect });
	};

	$$self.$$set = $$props => {
		if ("isActive" in $$props) $$invalidate(12, isActive = $$props.isActive);
		if ("stores" in $$props) $$invalidate(13, stores = $$props.stores);
		if ("content" in $$props) $$invalidate(14, content = $$props.content);
		if ("component" in $$props) $$invalidate(0, component = $$props.component);
		if ("locale" in $$props) $$invalidate(15, locale = $$props.locale);
		if ("class" in $$props) $$invalidate(1, klass = $$props.class);
	};

	let style;
	let componentProps;

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*rect, isActive, component*/ 4101) {
			// when the view rect changes and the panel is in active state or became active, dispatch measure event
			 if (rect && isActive && component) dispatch("measure", rect);
		}

		if ($$self.$$.dirty & /*isActive*/ 4096) {
			 opacity.set(isActive ? 1 : 0);
		}

		if ($$self.$$.dirty & /*$opacityClamped, isHidden*/ 5242880) {
			 if ($opacityClamped <= 0 && !isHidden) {
				$$invalidate(20, isHidden = true);
				dispatch("hide");
			} else if ($opacityClamped > 0 && isHidden) {
				$$invalidate(20, isHidden = false);
				dispatch("show");
			}
		}

		if ($$self.$$.dirty & /*$opacityClamped*/ 4194304) {
			 dispatch("fade", $opacityClamped);
		}

		if ($$self.$$.dirty & /*$opacityClamped*/ 4194304) {
			// only set opacity prop if is below 0
			 $$invalidate(6, style = $opacityClamped < 1
			? `opacity: ${$opacityClamped}`
			: undefined);
		}

		if ($$self.$$.dirty & /*isActive*/ 4096) {
			 set_store_value(isActivePrivateStore, $isActivePrivateStore = isActive, $isActivePrivateStore);
		}

		if ($$self.$$.dirty & /*stores, locale*/ 40960) {
			 $$invalidate(7, componentProps = {
				...componentComputedProps,
				...componentComputedStateProps,
				stores,
				locale
			});
		}
	};

	return [
		component,
		klass,
		rect,
		panelName,
		hasBeenMounted,
		preMountRect,
		style,
		componentProps,
		dispatch,
		opacityClamped,
		isActivePrivateStore,
		componentView,
		isActive,
		stores,
		content,
		locale,
		opacity,
		switch_instance_name_binding,
		switch_instance_binding,
		measure_handler
	];
}

class Panel extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$3, create_fragment$3, safe_not_equal, {
			isActive: 12,
			stores: 13,
			content: 14,
			component: 0,
			locale: 15,
			class: 1,
			opacity: 16
		});
	}

	get opacity() {
		return this.$$.ctx[16];
	}
}

/* src/native/ui/components/Icon.svelte generated by Svelte v3.29.4 */

function create_fragment$4(ctx) {
	let svg;
	let svg_viewBox_value;
	let current;
	const default_slot_template = /*#slots*/ ctx[4].default;
	const default_slot = create_slot(default_slot_template, ctx, /*$$scope*/ ctx[3], null);

	return {
		c() {
			svg = svg_element("svg");
			if (default_slot) default_slot.c();
			attr(svg, "style", /*style*/ ctx[2]);
			attr(svg, "width", /*width*/ ctx[0]);
			attr(svg, "height", /*height*/ ctx[1]);
			attr(svg, "viewBox", svg_viewBox_value = "0 0 " + /*width*/ ctx[0] + "\n    " + /*height*/ ctx[1]);
			attr(svg, "xmlns", "http://www.w3.org/2000/svg");
			attr(svg, "aria-hidden", "true");
			attr(svg, "focusable", "false");
			attr(svg, "stroke-linecap", "round");
			attr(svg, "stroke-linejoin", "round");
		},
		m(target, anchor) {
			insert(target, svg, anchor);

			if (default_slot) {
				default_slot.m(svg, null);
			}

			current = true;
		},
		p(ctx, [dirty]) {
			if (default_slot) {
				if (default_slot.p && dirty & /*$$scope*/ 8) {
					update_slot(default_slot, default_slot_template, ctx, /*$$scope*/ ctx[3], dirty, null, null);
				}
			}

			if (!current || dirty & /*style*/ 4) {
				attr(svg, "style", /*style*/ ctx[2]);
			}

			if (!current || dirty & /*width*/ 1) {
				attr(svg, "width", /*width*/ ctx[0]);
			}

			if (!current || dirty & /*height*/ 2) {
				attr(svg, "height", /*height*/ ctx[1]);
			}

			if (!current || dirty & /*width, height*/ 3 && svg_viewBox_value !== (svg_viewBox_value = "0 0 " + /*width*/ ctx[0] + "\n    " + /*height*/ ctx[1])) {
				attr(svg, "viewBox", svg_viewBox_value);
			}
		},
		i(local) {
			if (current) return;
			transition_in(default_slot, local);
			current = true;
		},
		o(local) {
			transition_out(default_slot, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(svg);
			if (default_slot) default_slot.d(detaching);
		}
	};
}

function instance$4($$self, $$props, $$invalidate) {
	let { $$slots: slots = {}, $$scope } = $$props;
	let { width = 24 } = $$props;
	let { height = 24 } = $$props;
	let { style = undefined } = $$props;

	$$self.$$set = $$props => {
		if ("width" in $$props) $$invalidate(0, width = $$props.width);
		if ("height" in $$props) $$invalidate(1, height = $$props.height);
		if ("style" in $$props) $$invalidate(2, style = $$props.style);
		if ("$$scope" in $$props) $$invalidate(3, $$scope = $$props.$$scope);
	};

	return [width, height, style, $$scope, slots];
}

class Icon extends SvelteComponent {
	constructor(options) {
		super();
		init(this, options, instance$4, create_fragment$4, safe_not_equal, { width: 0, height: 1, style: 2 });
	}
}

/* src/native/ui/components/Button.svelte generated by Svelte v3.29.4 */

function create_if_block_1(ctx) {
	let icon_1;
	let current;

	icon_1 = new Icon({
			props: {
				$$slots: { default: [create_default_slot] },
				$$scope: { ctx }
			}
		});

	return {
		c() {
			create_component(icon_1.$$.fragment);
		},
		m(target, anchor) {
			mount_component(icon_1, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const icon_1_changes = {};

			if (dirty & /*$$scope, icon*/ 32770) {
				icon_1_changes.$$scope = { dirty, ctx };
			}

			icon_1.$set(icon_1_changes);
		},
		i(local) {
			if (current) return;
			transition_in(icon_1.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(icon_1.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(icon_1, detaching);
		}
	};
}

// (27:16) <Icon>
function create_default_slot(ctx) {
	let g;

	return {
		c() {
			g = svg_element("g");
		},
		m(target, anchor) {
			insert(target, g, anchor);
			g.innerHTML = /*icon*/ ctx[1];
		},
		p(ctx, dirty) {
			if (dirty & /*icon*/ 2) g.innerHTML = /*icon*/ ctx[1];		},
		d(detaching) {
			if (detaching) detach(g);
		}
	};
}

// (33:12) {#if label}
function create_if_block(ctx) {
	let span;
	let t;

	return {
		c() {
			span = element("span");
			t = text(/*label*/ ctx[0]);
			attr(span, "class", /*elLabelClass*/ ctx[9]);
		},
		m(target, anchor) {
			insert(target, span, anchor);
			append(span, t);
		},
		p(ctx, dirty) {
			if (dirty & /*label*/ 1) set_data(t, /*label*/ ctx[0]);

			if (dirty & /*elLabelClass*/ 512) {
				attr(span, "class", /*elLabelClass*/ ctx[9]);
			}
		},
		d(detaching) {
			if (detaching) detach(span);
		}
	};
}

// (24:10)          
function fallback_block(ctx) {
	let span;
	let t;
	let current;
	let if_block0 = /*icon*/ ctx[1] && create_if_block_1(ctx);
	let if_block1 = /*label*/ ctx[0] && create_if_block(ctx);

	return {
		c() {
			span = element("span");
			if (if_block0) if_block0.c();
			t = space();
			if (if_block1) if_block1.c();
			attr(span, "class", /*elButtonInnerClass*/ ctx[7]);
		},
		m(target, anchor) {
			insert(target, span, anchor);
			if (if_block0) if_block0.m(span, null);
			append(span, t);
			if (if_block1) if_block1.m(span, null);
			current = true;
		},
		p(ctx, dirty) {
			if (/*icon*/ ctx[1]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty & /*icon*/ 2) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_1(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(span, t);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (/*label*/ ctx[0]) {
				if (if_block1) {
					if_block1.p(ctx, dirty);
				} else {
					if_block1 = create_if_block(ctx);
					if_block1.c();
					if_block1.m(span, null);
				}
			} else if (if_block1) {
				if_block1.d(1);
				if_block1 = null;
			}

			if (!current || dirty & /*elButtonInnerClass*/ 128) {
				attr(span, "class", /*elButtonInnerClass*/ ctx[7]);
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(span);
			if (if_block0) if_block0.d();
			if (if_block1) if_block1.d();
		}
	};
}

function create_fragment$5(ctx) {
	let button;
	let current;
	let mounted;
	let dispose;
	const default_slot_template = /*#slots*/ ctx[14].default;
	const default_slot = create_slot(default_slot_template, ctx, /*$$scope*/ ctx[15], null);
	const default_slot_or_fallback = default_slot || fallback_block(ctx);

	return {
		c() {
			button = element("button");
			if (default_slot_or_fallback) default_slot_or_fallback.c();
			attr(button, "class", /*elButtonClass*/ ctx[8]);
			attr(button, "type", /*type*/ ctx[4]);
			attr(button, "title", /*label*/ ctx[0]);
			attr(button, "style", /*style*/ ctx[2]);
			button.disabled = /*disabled*/ ctx[3];
		},
		m(target, anchor) {
			insert(target, button, anchor);

			if (default_slot_or_fallback) {
				default_slot_or_fallback.m(button, null);
			}

			current = true;

			if (!mounted) {
				dispose = [
					listen(button, "keydown", function () {
						if (is_function(/*onkeydown*/ ctx[6])) /*onkeydown*/ ctx[6].apply(this, arguments);
					}),
					listen(button, "click", function () {
						if (is_function(/*onclick*/ ctx[5])) /*onclick*/ ctx[5].apply(this, arguments);
					})
				];

				mounted = true;
			}
		},
		p(new_ctx, [dirty]) {
			ctx = new_ctx;

			if (default_slot) {
				if (default_slot.p && dirty & /*$$scope*/ 32768) {
					update_slot(default_slot, default_slot_template, ctx, /*$$scope*/ ctx[15], dirty, null, null);
				}
			} else {
				if (default_slot_or_fallback && default_slot_or_fallback.p && dirty & /*elButtonInnerClass, elLabelClass, label, icon*/ 643) {
					default_slot_or_fallback.p(ctx, dirty);
				}
			}

			if (!current || dirty & /*elButtonClass*/ 256) {
				attr(button, "class", /*elButtonClass*/ ctx[8]);
			}

			if (!current || dirty & /*type*/ 16) {
				attr(button, "type", /*type*/ ctx[4]);
			}

			if (!current || dirty & /*label*/ 1) {
				attr(button, "title", /*label*/ ctx[0]);
			}

			if (!current || dirty & /*style*/ 4) {
				attr(button, "style", /*style*/ ctx[2]);
			}

			if (!current || dirty & /*disabled*/ 8) {
				button.disabled = /*disabled*/ ctx[3];
			}
		},
		i(local) {
			if (current) return;
			transition_in(default_slot_or_fallback, local);
			current = true;
		},
		o(local) {
			transition_out(default_slot_or_fallback, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(button);
			if (default_slot_or_fallback) default_slot_or_fallback.d(detaching);
			mounted = false;
			run_all(dispose);
		}
	};
}

function instance$5($$self, $$props, $$invalidate) {
	let { $$slots: slots = {}, $$scope } = $$props;
	let { class: klass = undefined } = $$props;
	let { label = undefined } = $$props;
	let { labelClass = undefined } = $$props;
	let { innerClass = undefined } = $$props;
	let { hideLabel = false } = $$props;
	let { icon = undefined } = $$props;
	let { style = undefined } = $$props;
	let { disabled = undefined } = $$props;
	let { type = "button" } = $$props;
	let { onclick = undefined } = $$props;
	let { onkeydown = undefined } = $$props;

	$$self.$$set = $$props => {
		if ("class" in $$props) $$invalidate(10, klass = $$props.class);
		if ("label" in $$props) $$invalidate(0, label = $$props.label);
		if ("labelClass" in $$props) $$invalidate(11, labelClass = $$props.labelClass);
		if ("innerClass" in $$props) $$invalidate(12, innerClass = $$props.innerClass);
		if ("hideLabel" in $$props) $$invalidate(13, hideLabel = $$props.hideLabel);
		if ("icon" in $$props) $$invalidate(1, icon = $$props.icon);
		if ("style" in $$props) $$invalidate(2, style = $$props.style);
		if ("disabled" in $$props) $$invalidate(3, disabled = $$props.disabled);
		if ("type" in $$props) $$invalidate(4, type = $$props.type);
		if ("onclick" in $$props) $$invalidate(5, onclick = $$props.onclick);
		if ("onkeydown" in $$props) $$invalidate(6, onkeydown = $$props.onkeydown);
		if ("$$scope" in $$props) $$invalidate(15, $$scope = $$props.$$scope);
	};

	let elButtonInnerClass;
	let elButtonClass;
	let elLabelClass;

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*innerClass*/ 4096) {
			 $$invalidate(7, elButtonInnerClass = arrayJoin(["DokaButtonInner", innerClass]));
		}

		if ($$self.$$.dirty & /*hideLabel, klass*/ 9216) {
			 $$invalidate(8, elButtonClass = arrayJoin(["DokaButton", hideLabel && "DokaButtonIconOnly", klass]));
		}

		if ($$self.$$.dirty & /*hideLabel, labelClass*/ 10240) {
			 $$invalidate(9, elLabelClass = arrayJoin([hideLabel ? "implicit" : "DokaButtonLabel", labelClass]));
		}
	};

	return [
		label,
		icon,
		style,
		disabled,
		type,
		onclick,
		onkeydown,
		elButtonInnerClass,
		elButtonClass,
		elLabelClass,
		klass,
		labelClass,
		innerClass,
		hideLabel,
		slots,
		$$scope
	];
}

class Button extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$5, create_fragment$5, safe_not_equal, {
			class: 10,
			label: 0,
			labelClass: 11,
			innerClass: 12,
			hideLabel: 13,
			icon: 1,
			style: 2,
			disabled: 3,
			type: 4,
			onclick: 5,
			onkeydown: 6
		});
	}
}

var arrayRemove = (array, predicate) => {
    const index = array.findIndex(predicate);
    if (index >= 0)
        return array.splice(index, 1);
    return undefined;
};

// svelte
// constants
const INERTIA_THRESHOLD = 0.25; // when force of velocity exceeds this value we drift
const INERTIA_DISTANCE_MULTIPLIER = 50;
const INERTIA_DURATION_MULTIPLIER = 80;
const TAP_DURATION_MAX = 300;
const TAP_DISTANCE_MAX = 64;
const DOUBLE_TAP_DURATION_MAX = 700;
const DOUBLE_TAP_DISTANCE_MAX = 128;
const isContextMenuAction = (e) => isNumber(e.button) && e.button !== 0;
const getPinchScalar = (offset, current) => {
    return (current - offset) / offset;
};
var interactable = (node, options = {}) => {
    // set defaults
    const { inertia = false, matchTarget = false, pinch = false, getEventPosition = (e) => vectorCreate(e.clientX, e.clientY), } = options;
    //
    // helpers
    //
    function dispatch(type, detail) {
        node.dispatchEvent(new CustomEvent(type, { detail }));
    }
    function resetInertia() {
        if (inertiaTweenUnsubscribe)
            inertiaTweenUnsubscribe();
        inertiaTweenUnsubscribe = undefined;
    }
    //#region pointer registry
    const pointers = [];
    const addPointer = (e) => {
        const pointer = {
            timeStamp: e.timeStamp,
            timeStampInitial: e.timeStamp,
            position: getEventPosition(e),
            origin: getEventPosition(e),
            velocity: vectorCreateEmpty(),
            translation: vectorCreateEmpty(),
            interactionState: undefined,
            event: e,
        };
        pointers.push(pointer);
        pointer.interactionState = getInteractionState(pointers);
    };
    const removePointer = (e) => {
        const pointer = arrayRemove(pointers, (pointer) => pointer.event.pointerId === e.pointerId);
        if (pointer)
            return pointer[0];
    };
    const getPointerIndex = (e) => pointers.findIndex((pointer) => pointer.event.pointerId === e.pointerId);
    const flattenPointerOrigin = (pointer) => {
        pointer.origin.x = pointer.position.x;
        pointer.origin.y = pointer.position.y;
        pointer.translation.x = 0;
        pointer.translation.y = 0;
    };
    const updatePointer = (e) => {
        const pointer = getPointer(e);
        if (!pointer)
            return;
        const { timeStamp } = e;
        // position
        const eventPosition = getEventPosition(e);
        // duration between previous interaction and new interaction, an interaction duration cannot be faster than 1 millisecond
        const interactionDuration = Math.max(1, timeStamp - pointer.timeStamp);
        // calculate velocity
        pointer.velocity.x = (eventPosition.x - pointer.position.x) / interactionDuration;
        pointer.velocity.y = (eventPosition.y - pointer.position.y) / interactionDuration;
        // update the translation
        pointer.translation.x = eventPosition.x - pointer.origin.x;
        pointer.translation.y = eventPosition.y - pointer.origin.y;
        // set new state
        pointer.timeStamp = timeStamp;
        pointer.position.x = eventPosition.x;
        pointer.position.y = eventPosition.y;
        pointer.event = e;
    };
    const getPointer = (e) => {
        const i = getPointerIndex(e);
        if (i < 0)
            return;
        return pointers[i];
    };
    const isSingleTouching = () => pointers.length === 1;
    const isMultiTouching = () => pointers.length === 2;
    const getDistance = (pointers, position) => {
        const distanceTotal = pointers.reduce((prev, curr) => {
            prev += vectorDistance(position, curr.position);
            return prev;
        }, 0);
        return distanceTotal / pointers.length;
    };
    const getInteractionState = (pointers) => {
        const center = vectorCenter(pointers.map((pointer) => pointer.position));
        const distance = getDistance(pointers, center);
        return {
            center,
            distance,
            velocity: vectorCenter(pointers.map((pointer) => pointer.velocity)),
            translation: vectorCenter(pointers.map((pointer) => pointer.translation)),
        };
    };
    //#endregion
    let inertiaTween;
    let inertiaTweenUnsubscribe;
    let pinchOffset;
    let currentTranslation;
    let currentScale;
    let isGesture;
    let lastTapTimeStamp = 0;
    let lastTapPosition = undefined;
    // start handling interactions
    node.addEventListener('pointerdown', handlePointerdown);
    function handlePointerdown(e) {
        // ignore more than two pointers for now
        if (isMultiTouching())
            return;
        // not interested in context menu
        if (isContextMenuAction(e))
            return;
        // target should equal node, if it doesn't user might have clicked one of the nodes children
        if (matchTarget && e.target !== node)
            return;
        // stop any previous inertia tweens
        resetInertia();
        // register this pointer
        addPointer(e);
        // if is first pointer we need to init the drag gesture
        if (isSingleTouching()) {
            // handle pointer events
            document.documentElement.addEventListener('pointermove', handlePointermove);
            document.documentElement.addEventListener('pointerup', handlePointerup);
            document.documentElement.addEventListener('pointercancel', handlePointerup);
            // clear vars
            isGesture = false;
            currentScale = 0;
            currentTranslation = vectorCreateEmpty();
            pinchOffset = undefined;
            dispatch('interactionstart', {
                origin: vectorClone(getPointer(e).origin),
            });
        }
        else if (pinch) {
            isGesture = true;
            pinchOffset = vectorDistance(pointers[0].position, pointers[1].position);
            currentTranslation.x += pointers[0].translation.x;
            currentTranslation.y += pointers[0].translation.y;
            flattenPointerOrigin(pointers[0]);
        }
    }
    //
    // pointer move can only be a primary event (other pointers are not handled)
    //
    function handlePointermove(e) {
        // prevent selection of text (Safari)
        e.preventDefault();
        // update pointer state
        updatePointer(e);
        let scalar = currentScale;
        let translation = vectorClone(pointers[0].translation);
        if (isMultiTouching()) {
            // current offset
            translation.x += pointers[1].translation.x;
            translation.y += pointers[1].translation.y;
            // current pinch distance
            const pinchCurrent = vectorDistance(pointers[0].position, pointers[1].position);
            // to find out scalar we calculate the difference between the pinch offset and the new pinch
            scalar += getPinchScalar(pinchOffset, pinchCurrent);
        }
        translation.x += currentTranslation.x;
        translation.y += currentTranslation.y;
        dispatch('interactionupdate', {
            translation,
            scalar: pinch ? scalar : undefined,
        });
    }
    //
    // pointer up can only be a primary event (other pointers are not handled)
    //
    function handlePointerup(e) {
        // remove pointer from active pointers array
        const removedPointer = removePointer(e);
        // store current size
        if (isSingleTouching()) {
            // calculate current scale
            const pinchCurrent = vectorDistance(pointers[0].position, removedPointer.position);
            currentScale = (currentScale || 0) + getPinchScalar(pinchOffset, pinchCurrent);
            currentTranslation.x += pointers[0].translation.x + removedPointer.translation.x;
            currentTranslation.y += pointers[0].translation.y + removedPointer.translation.y;
            flattenPointerOrigin(pointers[0]);
        }
        // check if this was a tap
        let isTap = false;
        let isDoubleTap = false;
        if (!isGesture) {
            const interactionEnd = performance.now();
            const interactionDuration = interactionEnd - removedPointer.timeStampInitial;
            const interactionDistanceSquared = vectorDistanceSquared(removedPointer.translation);
            isTap =
                interactionDistanceSquared < TAP_DISTANCE_MAX &&
                    interactionDuration < TAP_DURATION_MAX;
            isDoubleTap = !!(lastTapPosition &&
                isTap &&
                interactionEnd - lastTapTimeStamp < DOUBLE_TAP_DURATION_MAX &&
                vectorDistanceSquared(lastTapPosition, removedPointer.position) <
                    DOUBLE_TAP_DISTANCE_MAX);
            if (isTap) {
                lastTapPosition = vectorClone(removedPointer.position);
                lastTapTimeStamp = interactionEnd;
            }
        }
        // we wait till last multi-touch interaction is finished, all pointers need to be de-registered before proceeding
        if (pointers.length > 0)
            return;
        // stop listening
        document.documentElement.removeEventListener('pointermove', handlePointermove);
        document.documentElement.removeEventListener('pointerup', handlePointerup);
        document.documentElement.removeEventListener('pointercancel', handlePointerup);
        const translation = vectorClone(removedPointer.translation);
        const velocity = vectorClone(removedPointer.velocity);
        // allows cancelling inertia from release handler
        let inertiaPrevented = false;
        // user has released interaction
        dispatch('interactionrelease', {
            isTap,
            isDoubleTap,
            translation,
            scalar: currentScale,
            preventInertia: () => (inertiaPrevented = true),
        });
        // stop intantly if not a lot of force applied
        const force = vectorDistance(velocity);
        if (inertiaPrevented || !inertia || force < INERTIA_THRESHOLD) {
            return handleEnd(translation, { isTap, isDoubleTap });
        }
        // drift
        inertiaTween = tweened(vectorClone(translation), {
            easing: circOut,
            duration: force * INERTIA_DURATION_MULTIPLIER,
        });
        inertiaTween
            .set({
            x: translation.x + velocity.x * INERTIA_DISTANCE_MULTIPLIER,
            y: translation.y + velocity.y * INERTIA_DISTANCE_MULTIPLIER,
        })
            .then(() => {
            // if has unsubscribed (tween was reset)
            if (!inertiaTweenUnsubscribe)
                return;
            // go!
            handleEnd(get_store_value(inertiaTween), { isTap, isDoubleTap });
        });
        inertiaTweenUnsubscribe = inertiaTween.subscribe(handleInertiaUpdate);
    }
    function handleInertiaUpdate(inertiaTranslation) {
        // if is same as previous position, ignore
        if (!inertiaTranslation)
            return; // || vectorEqual(inertiaTranslation, translation)) return;
        // this will handle drift interactions
        dispatch('interactionupdate', {
            translation: inertiaTranslation,
            scalar: pinch ? currentScale : undefined,
        });
    }
    function handleEnd(translation, tapState) {
        resetInertia();
        dispatch('interactionend', {
            ...tapState,
            translation,
            scalar: pinch ? currentScale : undefined,
        });
    }
    return {
        destroy() {
            resetInertia();
            node.removeEventListener('pointerdown', handlePointerdown);
        },
    };
};

var nudgeable = (element, options = {}) => {
    // if added as action on non focusable element you should add tabindex=0 attribute
    const { direction = undefined, shiftMultiplier = 10, bubbles = false, stopKeydownPropagation = true, } = options;
    const isHorizontalDirection = direction === 'horizontal';
    const isVerticalDirection = direction === 'vertical';
    const handleKeydown = (e) => {
        const { key } = e;
        const isShift = e.shiftKey;
        const isHorizontalAction = /up|down/i.test(key);
        const isVerticalAction = /left|right/i.test(key);
        // no directional key
        if (!isHorizontalAction && !isVerticalAction)
            return;
        // is horizontal but up or down pressed
        if (isHorizontalDirection && isVerticalAction)
            return;
        // is vertical but left or right pressed
        if (isVerticalDirection && isHorizontalAction)
            return;
        // if holding shift move by a factor 10
        const multiplier = isShift ? shiftMultiplier : 1;
        if (stopKeydownPropagation)
            e.stopPropagation();
        element.dispatchEvent(new CustomEvent('nudge', {
            bubbles,
            detail: vectorCreate((/left/i.test(key) ? -1 : /right/i.test(key) ? 1 : 0) * multiplier, (/up/i.test(key) ? -1 : /down/i.test(key) ? 1 : 0) * multiplier),
        }));
    };
    element.addEventListener('keydown', handleKeydown);
    return () => {
        element.removeEventListener('keydown', handleKeydown);
    };
};

function elastify(translation, dist) {
    return dist * Math.sign(translation) * Math.log10(1 + Math.abs(translation) / dist);
}
const elastifyRects = (a, b, dist) => {
    if (!b)
        return rectClone(a);
    const left = a.x + elastify(b.x - a.x, dist);
    const right = a.x + a.width + elastify(b.x + b.width - (a.x + a.width), dist);
    const top = a.y + elastify(b.y - a.y, dist);
    const bottom = a.y + a.height + elastify(b.y + b.height - (a.y + a.height), dist);
    return {
        x: left,
        y: top,
        width: right - left,
        height: bottom - top,
    };
};

var unitToPixels = (value, element) => {
    if (!value)
        return;
    if (/em/.test(value))
        return parseInt(value, 10) * 16;
    if (/px/.test(value))
        return parseInt(value, 10);
};

var getWheelDelta = (e) => {
    let d = e.detail || 0;
    // @ts-ignore
    if (e.wheelDelta || e.wheelDeltaY)
        d = (-e.wheelDelta || -e.wheelDeltaY) / 120;
    if (!d)
        d = e.deltaY;
    return d;
};

/* src/native/ui/components/Scrollable.svelte generated by Svelte v3.29.4 */

function create_fragment$6(ctx) {
	let div1;
	let div0;
	let interactable_action;
	let measurable_action;
	let div1_class_value;
	let measurable_action_1;
	let nudgeable_action;
	let current;
	let mounted;
	let dispose;
	const default_slot_template = /*#slots*/ ctx[28].default;
	const default_slot = create_slot(default_slot_template, ctx, /*$$scope*/ ctx[27], null);

	return {
		c() {
			div1 = element("div");
			div0 = element("div");
			if (default_slot) default_slot.c();
			attr(div0, "style", /*childStyle*/ ctx[6]);
			attr(div1, "class", div1_class_value = arrayJoin(["DokaScrollable", /*klass*/ ctx[0]]));
			attr(div1, "style", /*overflowStyle*/ ctx[4]);
			attr(div1, "data-direction", /*scrollDirection*/ ctx[1]);
			attr(div1, "data-state", /*containerState*/ ctx[5]);
		},
		m(target, anchor) {
			insert(target, div1, anchor);
			append(div1, div0);

			if (default_slot) {
				default_slot.m(div0, null);
			}

			/*div1_binding*/ ctx[30](div1);
			current = true;

			if (!mounted) {
				dispose = [
					listen(div0, "interactionstart", /*handleDragStart*/ ctx[9]),
					listen(div0, "interactionupdate", /*handleDragMove*/ ctx[11]),
					listen(div0, "interactionend", /*handleDragEnd*/ ctx[12]),
					listen(div0, "interactionrelease", /*handleDragRelease*/ ctx[10]),
					action_destroyer(interactable_action = interactable.call(null, div0, { inertia: true })),
					listen(div0, "measure", /*measure_handler*/ ctx[29]),
					action_destroyer(measurable_action = measurable.call(null, div0)),
					listen(div1, "wheel", /*handleWheel*/ ctx[14]),
					listen(div1, "scroll", /*handleScroll*/ ctx[16]),
					listen(div1, "focusin", /*handleFocus*/ ctx[15]),
					listen(div1, "nudge", /*handleNudge*/ ctx[17]),
					listen(div1, "measure", /*handleResizeScrollContainer*/ ctx[13]),
					action_destroyer(measurable_action_1 = measurable.call(null, div1, { observePosition: true })),
					action_destroyer(nudgeable_action = nudgeable.call(null, div1, {
						direction: /*scrollDirection*/ ctx[1] === "x"
						? "horizontal"
						: "vertical",
						stopKeydownPropagation: false
					}))
				];

				mounted = true;
			}
		},
		p(ctx, dirty) {
			if (default_slot) {
				if (default_slot.p && dirty[0] & /*$$scope*/ 134217728) {
					update_slot(default_slot, default_slot_template, ctx, /*$$scope*/ ctx[27], dirty, null, null);
				}
			}

			if (!current || dirty[0] & /*childStyle*/ 64) {
				attr(div0, "style", /*childStyle*/ ctx[6]);
			}

			if (!current || dirty[0] & /*klass*/ 1 && div1_class_value !== (div1_class_value = arrayJoin(["DokaScrollable", /*klass*/ ctx[0]]))) {
				attr(div1, "class", div1_class_value);
			}

			if (!current || dirty[0] & /*overflowStyle*/ 16) {
				attr(div1, "style", /*overflowStyle*/ ctx[4]);
			}

			if (!current || dirty[0] & /*scrollDirection*/ 2) {
				attr(div1, "data-direction", /*scrollDirection*/ ctx[1]);
			}

			if (!current || dirty[0] & /*containerState*/ 32) {
				attr(div1, "data-state", /*containerState*/ ctx[5]);
			}

			if (nudgeable_action && is_function(nudgeable_action.update) && dirty[0] & /*scrollDirection*/ 2) nudgeable_action.update.call(null, {
				direction: /*scrollDirection*/ ctx[1] === "x"
				? "horizontal"
				: "vertical",
				stopKeydownPropagation: false
			});
		},
		i(local) {
			if (current) return;
			transition_in(default_slot, local);
			current = true;
		},
		o(local) {
			transition_out(default_slot, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div1);
			if (default_slot) default_slot.d(detaching);
			/*div1_binding*/ ctx[30](null);
			mounted = false;
			run_all(dispose);
		}
	};
}

function instance$6($$self, $$props, $$invalidate) {
	let $scrollOffset;
	let $keysPressedStore;
	let { $$slots: slots = {}, $$scope } = $$props;
	const dispatch = createEventDispatcher();
	const keysPressedStore = getContext("keysPressed");
	component_subscribe($$self, keysPressedStore, value => $$invalidate(44, $keysPressedStore = value));
	let scrollState = "idle";
	let scrollOrigin;
	let scrollRect;
	let scrollContainerRect;
	let scrollReleased;
	let scrollOffset = spring(0);
	component_subscribe($$self, scrollOffset, value => $$invalidate(42, $scrollOffset = value));
	let { class: klass = undefined } = $$props;
	let { scrollBlockInteractionDist = 5 } = $$props;
	let { scrollStep = 10 } = $$props; // the distance multiplier for each mouse scroll interaction (delta)
	let { scrollFocusMargin = 64 } = $$props; // the margin used around elements to decided where to move the focus so elements are positioned into view with some spacing around them, this allows peaking at next/previous elements
	let { scrollDirection = "x" } = $$props;
	let { scrollAutoCancel = false } = $$props;
	let { elasticity = 0 } = $$props;
	let { onscroll = noop } = $$props;
	let { maskFeatherSize = undefined } = $$props;
	let { maskFeatherStartOpacity = undefined } = $$props;
	let { maskFeatherEndOpacity = undefined } = $$props;

	// logic
	let container;

	let overflowStyle = "";

	// triggers onscroll callback
	scrollOffset.subscribe(value => {
		const pos = vectorCreateEmpty();
		pos[scrollDirection] = value;
		onscroll(pos);
	});

	const limitOffsetToContainer = offset => Math.max(Math.min(0, offset), scrollContainerRect[size] - scrollRect[size]);
	let scrollFirstMove;
	let scrollCancelled;
	let scrollTranslationPrev;

	const isHorizontalTranslation = translation => {
		const velocity = vectorApply(vectorCreate(translation.x - scrollTranslationPrev.x, translation.y - scrollTranslationPrev.y), Math.abs);
		scrollTranslationPrev = vectorClone(translation);
		const speed = vectorDistanceSquared(velocity);
		const diff = velocity.x - velocity.y;
		return !(speed > 1 && diff < -0.5);
	};

	const handleDragStart = () => {
		// not overflowing so no need to handle
		if (!overflows) return;

		scrollCancelled = false;
		scrollFirstMove = true;
		scrollTranslationPrev = vectorCreate(0, 0);
		scrollReleased = false;
		$$invalidate(31, scrollState = "idle");
		scrollOrigin = get_store_value(scrollOffset);
	};

	const handleDragRelease = ({ detail }) => {
		if (!overflows) return;
		scrollReleased = true;
		$$invalidate(31, scrollState = "idle");
	};

	const handleDragMove = ({ detail }) => {
		if (!overflows) return;
		if (scrollCancelled) return;

		// fixes problem with single move event fired when clicking
		if (scrollFirstMove) {
			scrollFirstMove = false;
			if (vectorDistanceSquared(detail.translation) < 0.1) return;
		}

		if (scrollAutoCancel && scrollDirection === "x" && !isHorizontalTranslation(detail.translation)) {
			scrollCancelled = true;
			return;
		}

		setScrollOffset(scrollOrigin + detail.translation[scrollDirection], { elastic: true });
	};

	const handleDragEnd = ({ detail }) => {
		if (!overflows) return;
		if (scrollCancelled) return;
		const offset = scrollOrigin + detail.translation[scrollDirection];
		const offsetLimited = limitOffsetToContainer(offset);
		scrollOffset.set(offsetLimited);
	};

	const handleResizeScrollContainer = ({ detail }) => {
		$$invalidate(33, scrollContainerRect = detail);

		dispatch("measure", {
			x: detail.x,
			y: detail.y,
			width: detail.width,
			height: detail.height
		});
	};

	const setScrollOffset = (offset, options = {}) => {
		const { elastic = false, animate = false } = options;

		// prevents clicks on child elements if the container is being scrolled
		if (Math.abs(offset) > scrollBlockInteractionDist && scrollState === "idle" && !scrollReleased) {
			$$invalidate(31, scrollState = "scrolling");
		}

		const offsetLimited = limitOffsetToContainer(offset);

		const offsetVisual = elastic && elasticity && !scrollReleased
		? offsetLimited + elastify(offset - offsetLimited, elasticity)
		: offsetLimited;

		scrollOffset.set(offsetVisual, { hard: animate ? false : !scrollReleased });
	};

	const handleWheel = e => {
		// don't do anything if isn't overflowing
		if (!overflows) return;

		// scroll down -> move to right/down
		// scroll up -> move to left/up
		// don't run default actions, prevent other actions from running
		e.preventDefault();

		e.stopPropagation();

		// apply wheel delta to offset
		const delta = getWheelDelta(e);

		const offset = get_store_value(scrollOffset);
		setScrollOffset(offset + delta * scrollStep, { animate: true });
	};

	const handleFocus = e => {
		// don't do anything if isn't overflowing
		if (!overflows) return;

		// ignore this handler if is dragging
		if (!scrollReleased && !$keysPressedStore.length) return;

		let target = e.target;

		// when a target is marked as implicit we use its parent elemetn
		if (e.target.classList.contains("implicit")) target = target.parentNode;

		// get bounds
		const start = target[scrollDirection === "x" ? "offsetLeft" : "offsetTop"]; //.offsetLeft;

		const space = target[scrollDirection === "x" ? "offsetWidth" : "offsetHeight"]; //.offsetWidth;
		const end = start + space;

		// we need to know the current offset of the scroll so we can determine if the target is in view
		const currentScrollOffset = get_store_value(scrollOffset);

		// the margin around elements to keep in mind when focussing items
		const margin = scrollFocusMargin + maskFeatherSize;

		if (currentScrollOffset + start < margin) {
			setScrollOffset(-start + margin);
		} else if (currentScrollOffset + end > scrollContainerRect[size] - margin) {
			setScrollOffset(scrollContainerRect[size] - end - margin, { animate: true });
		}
	};

	const handleScroll = () => {
		// the scroll handler corrects auto browser scroll, 
		// is triggered when browser tries to focus an 
		// element outside of the scrollcontiner
		$$invalidate(3, container[scrollDirection === "x" ? "scrollLeft" : "scrollTop"] = 0, container);
	};

	const handleNudge = ({ detail }) => {
		const delta = -2 * detail[scrollDirection];
		const offset = get_store_value(scrollOffset);
		setScrollOffset(offset + delta * scrollStep, { animate: true });
	};

	const measure_handler = e => $$invalidate(2, scrollRect = e.detail);

	function div1_binding($$value) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			container = $$value;
			$$invalidate(3, container);
		});
	}

	$$self.$$set = $$props => {
		if ("class" in $$props) $$invalidate(0, klass = $$props.class);
		if ("scrollBlockInteractionDist" in $$props) $$invalidate(21, scrollBlockInteractionDist = $$props.scrollBlockInteractionDist);
		if ("scrollStep" in $$props) $$invalidate(22, scrollStep = $$props.scrollStep);
		if ("scrollFocusMargin" in $$props) $$invalidate(23, scrollFocusMargin = $$props.scrollFocusMargin);
		if ("scrollDirection" in $$props) $$invalidate(1, scrollDirection = $$props.scrollDirection);
		if ("scrollAutoCancel" in $$props) $$invalidate(24, scrollAutoCancel = $$props.scrollAutoCancel);
		if ("elasticity" in $$props) $$invalidate(25, elasticity = $$props.elasticity);
		if ("onscroll" in $$props) $$invalidate(26, onscroll = $$props.onscroll);
		if ("maskFeatherSize" in $$props) $$invalidate(18, maskFeatherSize = $$props.maskFeatherSize);
		if ("maskFeatherStartOpacity" in $$props) $$invalidate(19, maskFeatherStartOpacity = $$props.maskFeatherStartOpacity);
		if ("maskFeatherEndOpacity" in $$props) $$invalidate(20, maskFeatherEndOpacity = $$props.maskFeatherEndOpacity);
		if ("$$scope" in $$props) $$invalidate(27, $$scope = $$props.$$scope);
	};

	let size;
	let axis;
	let containerStyle;
	let containerFeatherSize;
	let overflows;
	let containerState;
	let childStyle;

	$$self.$$.update = () => {
		if ($$self.$$.dirty[0] & /*scrollDirection*/ 2) {
			 $$invalidate(38, size = scrollDirection === "x" ? "width" : "height");
		}

		if ($$self.$$.dirty[0] & /*scrollDirection*/ 2) {
			 $$invalidate(39, axis = scrollDirection.toUpperCase());
		}

		if ($$self.$$.dirty[0] & /*container*/ 8) {
			 $$invalidate(40, containerStyle = container && getComputedStyle(container));
		}

		if ($$self.$$.dirty[0] & /*container*/ 8 | $$self.$$.dirty[1] & /*containerStyle*/ 512) {
			 $$invalidate(41, containerFeatherSize = containerStyle && unitToPixels(containerStyle.getPropertyValue("--scrollable-feather-size")));
		}

		if ($$self.$$.dirty[0] & /*scrollRect, maskFeatherStartOpacity, maskFeatherEndOpacity*/ 1572868 | $$self.$$.dirty[1] & /*$scrollOffset, scrollContainerRect, containerFeatherSize, size*/ 3204) {
			 if ($scrollOffset != null && scrollContainerRect && containerFeatherSize != null && scrollRect) {
				const startOffset = -$scrollOffset / containerFeatherSize;
				const endOffset = -(scrollContainerRect[size] - scrollRect[size] - $scrollOffset) / containerFeatherSize;
				$$invalidate(19, maskFeatherStartOpacity = clamp(1 - startOffset, 0, 1));
				$$invalidate(20, maskFeatherEndOpacity = clamp(1 - endOffset, 0, 1));
				$$invalidate(18, maskFeatherSize = containerFeatherSize);
				$$invalidate(4, overflowStyle = `--scrollable-feather-start-opacity: ${maskFeatherStartOpacity};--scrollable-feather-end-opacity: ${maskFeatherEndOpacity}`);
			}
		}

		if ($$self.$$.dirty[0] & /*scrollRect*/ 4 | $$self.$$.dirty[1] & /*scrollContainerRect, size*/ 132) {
			 $$invalidate(43, overflows = scrollContainerRect && scrollRect
			? scrollRect[size] > scrollContainerRect[size]
			: undefined);
		}

		if ($$self.$$.dirty[1] & /*scrollState, overflows*/ 4097) {
			 $$invalidate(5, containerState = arrayJoin([scrollState, overflows ? "overflows" : undefined]));
		}

		if ($$self.$$.dirty[1] & /*overflows, axis, $scrollOffset*/ 6400) {
			 $$invalidate(6, childStyle = overflows
			? `transform: translate${axis}(${$scrollOffset}px)`
			: undefined);
		}
	};

	return [
		klass,
		scrollDirection,
		scrollRect,
		container,
		overflowStyle,
		containerState,
		childStyle,
		keysPressedStore,
		scrollOffset,
		handleDragStart,
		handleDragRelease,
		handleDragMove,
		handleDragEnd,
		handleResizeScrollContainer,
		handleWheel,
		handleFocus,
		handleScroll,
		handleNudge,
		maskFeatherSize,
		maskFeatherStartOpacity,
		maskFeatherEndOpacity,
		scrollBlockInteractionDist,
		scrollStep,
		scrollFocusMargin,
		scrollAutoCancel,
		elasticity,
		onscroll,
		$$scope,
		slots,
		measure_handler,
		div1_binding
	];
}

class Scrollable extends SvelteComponent {
	constructor(options) {
		super();

		init(
			this,
			options,
			instance$6,
			create_fragment$6,
			safe_not_equal,
			{
				class: 0,
				scrollBlockInteractionDist: 21,
				scrollStep: 22,
				scrollFocusMargin: 23,
				scrollDirection: 1,
				scrollAutoCancel: 24,
				elasticity: 25,
				onscroll: 26,
				maskFeatherSize: 18,
				maskFeatherStartOpacity: 19,
				maskFeatherEndOpacity: 20
			},
			[-1, -1]
		);
	}
}

function fade(node, { delay = 0, duration = 400, easing = identity }) {
    const o = +getComputedStyle(node).opacity;
    return {
        delay,
        duration,
        easing,
        css: t => `opacity: ${t * o}`
    };
}

/* src/native/ui/components/StatusMessage.svelte generated by Svelte v3.29.4 */

function create_fragment$7(ctx) {
	let span;
	let t;
	let measure_action;
	let span_transition;
	let current;
	let mounted;
	let dispose;

	return {
		c() {
			span = element("span");
			t = text(/*text*/ ctx[0]);
			attr(span, "class", "DokaStatusMessage");
		},
		m(target, anchor) {
			insert(target, span, anchor);
			append(span, t);
			current = true;

			if (!mounted) {
				dispose = [
					listen(span, "measure", function () {
						if (is_function(/*onmeasure*/ ctx[1])) /*onmeasure*/ ctx[1].apply(this, arguments);
					}),
					action_destroyer(measure_action = measurable.call(null, span))
				];

				mounted = true;
			}
		},
		p(new_ctx, [dirty]) {
			ctx = new_ctx;
			if (!current || dirty & /*text*/ 1) set_data(t, /*text*/ ctx[0]);
		},
		i(local) {
			if (current) return;

			add_render_callback(() => {
				if (!span_transition) span_transition = create_bidirectional_transition(span, fade, {}, true);
				span_transition.run(1);
			});

			current = true;
		},
		o(local) {
			if (!span_transition) span_transition = create_bidirectional_transition(span, fade, {}, false);
			span_transition.run(0);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(span);
			if (detaching && span_transition) span_transition.end();
			mounted = false;
			run_all(dispose);
		}
	};
}

function instance$7($$self, $$props, $$invalidate) {
	let { text } = $$props;
	let { onmeasure = noop } = $$props;

	$$self.$$set = $$props => {
		if ("text" in $$props) $$invalidate(0, text = $$props.text);
		if ("onmeasure" in $$props) $$invalidate(1, onmeasure = $$props.onmeasure);
	};

	return [text, onmeasure];
}

class StatusMessage extends SvelteComponent {
	constructor(options) {
		super();
		init(this, options, instance$7, create_fragment$7, safe_not_equal, { text: 0, onmeasure: 1 });
	}
}

/* src/native/ui/components/ProgressIndicator.svelte generated by Svelte v3.29.4 */

function create_fragment$8(ctx) {
	let span1;
	let svg;
	let g;
	let circle0;
	let circle1;
	let t0;
	let span0;
	let t1;

	return {
		c() {
			span1 = element("span");
			svg = svg_element("svg");
			g = svg_element("g");
			circle0 = svg_element("circle");
			circle1 = svg_element("circle");
			t0 = space();
			span0 = element("span");
			t1 = text(/*formattedValue*/ ctx[0]);
			attr(circle0, "class", "DokaProgressIndicatorBar");
			attr(circle0, "r", "8.5");
			attr(circle0, "cx", "10");
			attr(circle0, "cy", "10");
			attr(circle0, "stroke-linecap", "round");
			attr(circle0, "opacity", ".25");
			attr(circle1, "class", "DokaProgressIndicatorFill");
			attr(circle1, "r", "8.5");
			attr(circle1, "stroke-dasharray", /*circleValue*/ ctx[1]);
			attr(circle1, "cx", "10");
			attr(circle1, "cy", "10");
			attr(circle1, "transform", "rotate(-90) translate(-20)");
			attr(g, "fill", "none");
			attr(g, "stroke", "currentColor");
			attr(g, "stroke-width", "2.5");
			attr(g, "stroke-linecap", "round");
			attr(g, "opacity", /*circleOpacity*/ ctx[2]);
			attr(svg, "width", "20");
			attr(svg, "height", "20");
			attr(svg, "viewBox", "0 0 20 20");
			attr(svg, "xmlns", "http://www.w3.org/2000/svg");
			attr(svg, "aria-hidden", "true");
			attr(svg, "focusable", "false");
			attr(span0, "class", "implicit");
			attr(span1, "class", "DokaProgressIndicator");
			attr(span1, "data-status", /*status*/ ctx[3]);
		},
		m(target, anchor) {
			insert(target, span1, anchor);
			append(span1, svg);
			append(svg, g);
			append(g, circle0);
			append(g, circle1);
			append(span1, t0);
			append(span1, span0);
			append(span0, t1);
		},
		p(ctx, [dirty]) {
			if (dirty & /*circleValue*/ 2) {
				attr(circle1, "stroke-dasharray", /*circleValue*/ ctx[1]);
			}

			if (dirty & /*circleOpacity*/ 4) {
				attr(g, "opacity", /*circleOpacity*/ ctx[2]);
			}

			if (dirty & /*formattedValue*/ 1) set_data(t1, /*formattedValue*/ ctx[0]);

			if (dirty & /*status*/ 8) {
				attr(span1, "data-status", /*status*/ ctx[3]);
			}
		},
		i: noop$1,
		o: noop$1,
		d(detaching) {
			if (detaching) detach(span1);
		}
	};
}

function instance$8($$self, $$props, $$invalidate) {
	let $animatedProgressClamped;
	const dispatch = createEventDispatcher();
	let { progress } = $$props;
	let { min = 0 } = $$props;
	let { max = 100 } = $$props;
	let { labelBusy = "Busy" } = $$props;
	const animatedValue = spring(0, { precision: 0.01 });
	const animatedProgressClamped = derived([animatedValue], $animatedValue => clamp($animatedValue, min, max));
	component_subscribe($$self, animatedProgressClamped, value => $$invalidate(9, $animatedProgressClamped = value));

	animatedProgressClamped.subscribe(value => {
		if (progress === 1 && Math.round(value) >= 100) dispatch("complete");
	});

	$$self.$$set = $$props => {
		if ("progress" in $$props) $$invalidate(5, progress = $$props.progress);
		if ("min" in $$props) $$invalidate(6, min = $$props.min);
		if ("max" in $$props) $$invalidate(7, max = $$props.max);
		if ("labelBusy" in $$props) $$invalidate(8, labelBusy = $$props.labelBusy);
	};

	let formattedValue;
	let circleValue;
	let circleOpacity;
	let status;

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*progress*/ 32) {
			 progress && progress !== Infinity && animatedValue.set(progress * 100);
		}

		if ($$self.$$.dirty & /*progress, labelBusy, $animatedProgressClamped*/ 800) {
			 $$invalidate(0, formattedValue = progress === Infinity
			? labelBusy
			: `${Math.round($animatedProgressClamped)}%`);
		}

		if ($$self.$$.dirty & /*progress, $animatedProgressClamped*/ 544) {
			 $$invalidate(1, circleValue = progress === Infinity
			? "26.5 53"
			: `${$animatedProgressClamped / 100 * 53} 53`);
		}

		if ($$self.$$.dirty & /*progress, $animatedProgressClamped*/ 544) {
			 $$invalidate(2, circleOpacity = Math.min(1, progress === Infinity
			? 1
			: $animatedProgressClamped / 10));
		}

		if ($$self.$$.dirty & /*progress*/ 32) {
			 $$invalidate(3, status = progress === Infinity ? "busy" : "loading");
		}
	};

	return [
		formattedValue,
		circleValue,
		circleOpacity,
		status,
		animatedProgressClamped,
		progress,
		min,
		max,
		labelBusy
	];
}

class ProgressIndicator extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$8, create_fragment$8, safe_not_equal, {
			progress: 5,
			min: 6,
			max: 7,
			labelBusy: 8
		});
	}
}

/* src/native/ui/components/StatusProgress.svelte generated by Svelte v3.29.4 */

function create_fragment$9(ctx) {
	let span;
	let progressindicator;
	let current;
	progressindicator = new ProgressIndicator({ props: { progress: /*progress*/ ctx[0] } });

	progressindicator.$on("complete", function () {
		if (is_function(/*oncomplete*/ ctx[1])) /*oncomplete*/ ctx[1].apply(this, arguments);
	});

	return {
		c() {
			span = element("span");
			create_component(progressindicator.$$.fragment);
			attr(span, "class", "DokaStatusProgress");
			attr(span, "style", /*style*/ ctx[2]);
		},
		m(target, anchor) {
			insert(target, span, anchor);
			mount_component(progressindicator, span, null);
			current = true;
		},
		p(new_ctx, [dirty]) {
			ctx = new_ctx;
			const progressindicator_changes = {};
			if (dirty & /*progress*/ 1) progressindicator_changes.progress = /*progress*/ ctx[0];
			progressindicator.$set(progressindicator_changes);

			if (!current || dirty & /*style*/ 4) {
				attr(span, "style", /*style*/ ctx[2]);
			}
		},
		i(local) {
			if (current) return;
			transition_in(progressindicator.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(progressindicator.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(span);
			destroy_component(progressindicator);
		}
	};
}

function instance$9($$self, $$props, $$invalidate) {
	let { offset = 0 } = $$props;
	let { visible = true } = $$props;
	let { progress } = $$props;
	let { oncomplete = noop } = $$props;

	$$self.$$set = $$props => {
		if ("offset" in $$props) $$invalidate(3, offset = $$props.offset);
		if ("visible" in $$props) $$invalidate(4, visible = $$props.visible);
		if ("progress" in $$props) $$invalidate(0, progress = $$props.progress);
		if ("oncomplete" in $$props) $$invalidate(1, oncomplete = $$props.oncomplete);
	};

	let style;

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*offset, visible*/ 24) {
			 $$invalidate(2, style = `transform: translateX(${offset}px); opacity: ${visible ? 1 : 0}`);
		}
	};

	return [progress, oncomplete, style, offset, visible];
}

class StatusProgress extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$9, create_fragment$9, safe_not_equal, {
			offset: 3,
			visible: 4,
			progress: 0,
			oncomplete: 1
		});
	}
}

const ImageStoreProps = [
    'file',
    'size',
    'loadState',
    'processState',
    'cropAspectRatio',
    'cropLimitToImage',
    'crop',
    'cropMinSize',
    'cropMaxSize',
    'cropRange',
    'cropOrigin',
    'cropRectAspectRatio',
    'rotation',
    'rotationRange',
    'targetSize',
    'flipX',
    'flipY',
    'perspectiveX',
    'perspectiveY',
    'perspective',
    'colorMatrix',
    'convolutionMatrix',
    'gamma',
    'vignette',
    'noise',
    'decoration',
    'annotation',
    'backgroundColor',
    'state',
];
const proxy = function (get, set, update) {
    let subscribers = [];
    return {
        set,
        update,
        publish: (value) => {
            subscribers.forEach((cb) => cb(value));
        },
        subscribe: (cb) => {
            subscribers.push(cb);
            get(cb);
            return () => {
                subscribers = subscribers.filter((item) => item !== cb);
            };
        },
    };
};
var createImageProxy = () => {
    let unsubs;
    let image;
    const proxyStores = ImageStoreProps.reduce((prev, curr) => {
        prev[curr] = proxy(
        // getter
        (cb) => {
            // subscribe
            if (!image)
                return cb();
            const unsub = image.stores[curr].subscribe(cb);
            unsub();
        }, 
        // setter
        (value) => {
            // set value on actual store if is defined
            if (!image)
                return;
            image.stores[curr].set(value);
        }, 
        // updater
        (cb) => {
            if (!image)
                return;
            image.stores[curr].update(cb);
        });
        return prev;
    }, {});
    const update = (newImage) => {
        image = newImage;
        if (unsubs) {
            // remove subscribers
            unsubs.forEach((unsub) => unsub());
            unsubs = undefined;
        }
        if (!newImage) {
            // need to reset load state
            proxyStores['file'].publish(undefined);
            proxyStores['loadState'].publish(undefined);
            return;
        }
        unsubs = ImageStoreProps.map((prop) => newImage.stores[prop].subscribe((value) => proxyStores[prop].publish(value)));
    };
    return {
        update,
        stores: proxyStores,
    };
};

var matchMedia$1 = (query, cb) => {
    const mql = matchMedia(query);
    mql.addListener(cb);
    cb(mql);
    return {
        get matches() { return mql.matches; },
        destroy: () => mql.removeListener(cb)
    };
};

var mediaQueryStore = (query, formatValue = passthrough) => {
    const { subscribe, set } = writable(undefined);
    const mm = matchMedia$1(query, ({ matches }) => set(formatValue(matches)));
    return {
        subscribe,
        unsubscribe: mm.destroy
    };
};

var canPreventNavSwipe = () => {
    // if not iOS we can't prevent swipe because it probably isn't a thing
    if (!isIOS())
        return false;
    // extract version number
    const [, major, minor] = navigator.userAgent
        .match(/OS (\d+)_(\d+)_?(\d+)?/i)
        .map((v) => parseInt(v, 10) || 0);
    // is atleast version 13.4+
    return major > 13 || (major === 13 && minor >= 4);
};

var calculateImageTransforms = (stageRect, rootRect, imageSize, cropRect, imageSelectionRect, imageScale, imagePerspectiveX, imagePerspectiveY, imageRotation, imageFlipX, imageFlipY) => {
    if (!stageRect || !rootRect || !imageSize || !cropRect || !imageScale)
        return undefined;
    const viewRect = rectNormalizeOffset(rectClone(rootRect));
    const viewCenter = rectCenter(viewRect);
    const stageCenter = rectCenter(stageRect);
    const imageRect = rectCreateFromSize(imageSize);
    const imageCenter = rectCenter(imageRect);
    const imagePerspective = vectorCreate(imagePerspectiveX, imagePerspectiveY);
    // get base crop rect so we can correctly apply transforms
    const cropRectBase = getBaseCropRect(imageSize, cropRect, imageRotation, imagePerspective);
    const cropRectBaseCenter = rectCenter(cropRectBase);
    const imageTranslation = vectorSubtract(vectorClone(imageCenter), cropRectBaseCenter);
    // calculate stage center offset from view center
    const imageOffset = vectorSubtract(vectorClone(stageCenter), viewCenter);
    // correct for stage offset
    imageTranslation.x += imageOffset.x;
    imageTranslation.y += imageOffset.y;
    // set origin of translation (so rotates around center of selection)
    const imageOrigin = vectorInvert(vectorClone(imageTranslation));
    // correct for stage offset
    imageOrigin.x += imageOffset.x;
    imageOrigin.y += imageOffset.y;
    // correct for image selection offset relative to view
    const imageSelectionCenter = rectCenter(rectTranslate(rectClone(imageSelectionRect), stageRect));
    const imageSelectionOffset = vectorSubtract(imageSelectionCenter, stageCenter);
    vectorAdd(imageTranslation, imageSelectionOffset);
    return {
        // offset: imageOffset,
        origin: imageOrigin,
        translation: imageTranslation,
        rotation: {
            x: imageFlipY ? Math.PI : 0,
            y: imageFlipX ? Math.PI : 0,
            z: imageRotation,
        },
        perspective: imagePerspective,
        scale: imageScale,
    };
};

// @ts-ignore
var historyCreate = (getState, setState) => {
    // set up pub/sub for history object
    const { sub, pub } = pubsub();
    // current history state
    let baseState;
    const entries = [];
    const index = writable(-1);
    const subs = [];
    const updateSubs = () => subs.forEach((cb) => cb({ index: get_store_value(index), length: entries.length }));
    const history = {
        get index() {
            return get_store_value(index);
        },
        set index(i) {
            // validate new index
            i = Number.isInteger(i) ? i : -1;
            i = clamp(i, -1, entries.length - 1);
            // remember
            index.set(i);
            setState(entries[history.index] || baseState);
            // update subs
            updateSubs();
        },
        get state() {
            return entries[entries.length - 1] || baseState;
        },
        length() {
            return entries.length;
        },
        undo() {
            const newIndex = history.index--;
            pub('undo', newIndex);
            return newIndex;
        },
        redo() {
            const newIndex = history.index++;
            pub('redo', history.index);
            return newIndex;
        },
        revert() {
            entries.length = 0;
            history.index = -1;
            pub('revert');
        },
        write() {
            // reset length to current index
            entries.length = history.index + 1;
            // add new entry
            entries.push(getState());
            // move pointer to last added index
            index.set(entries.length - 1);
            updateSubs();
        },
        set(state = {}) {
            baseState = state;
        },
        subscribe(cb) {
            subs.push(cb);
            cb({ index: history.index, length: entries.length });
            return () => subs.splice(subs.indexOf(cb), 1);
        },
        on: sub,
    };
    return history;
};

var canCreateImageBitmap = () => 'createImageBitmap' in window;

var isChrome = () => isBrowser() && !!window['chrome'];

var imageBitmapToImageData = async (imageBitmap) => canvasToImageData(await imageDataToCanvas(imageBitmap));

var blobToImageBitmap = (file, ImageOrienter) => new Promise(async (resolve, reject) => {
    // get orientation of image, should return 1 for normal orientation
    const orientation = await ImageOrienter.read(file);
    // helper method to apply orientation fix if needed
    const toImageData = (file) => blobToImageData(file)
        .then((imageData) => ImageOrienter.apply(imageData, orientation))
        .then(resolve)
        .catch(reject);
    // cannot create image bitmaps in worker
    if (!canCreateImageBitmap())
        return toImageData(file);
    // create image bitmap in thread
    let imageBitmap;
    try {
        imageBitmap = await thread((file, done) => createImageBitmap(file)
            .then((bitmap) => done(null, bitmap))
            .catch(() => {
            // fail silently
            done(null);
        }), [file]);
    }
    catch (err) {
        // always fail silently, we'll try to turn the blob into image data in the main thread
    }
    // no bitmap returned, must be something wrong,
    if (!imageBitmap || !imageBitmap.width)
        return toImageData(file);
    // if the browser can't orient images, we need to correct the orientation now
    if (!(await canOrientImages()))
        return resolve(ImageOrienter.apply(imageBitmap, orientation));
    // need to convert so oriented image can be used as WebGL texture
    // converting ImageBitmap to <canvas> (and then optionally to image data) fixes a Chrome render bug where the
    // ImageBitmap when used in WebGL doesn't render in the correct orientation
    // https://bugs.chromium.org/p/chromium/issues/detail?id=1082451&q=orientation%20imagebitmap&can=2
    if (isChrome() && orientation > 1)
        return resolve(await imageBitmapToImageData(imageBitmap));
    // yay we got our bitmap
    resolve(imageBitmap);
});

var imageDataContain = (imageData, size) => new Promise(async (resolve) => {
    if (imageData.width < size.width && imageData.height < size.height)
        return resolve(imageData);
    const scalar = Math.min(size.width / imageData.width, size.height / imageData.height);
    // scale
    const targetWidth = scalar * imageData.width;
    const targetHeight = scalar * imageData.height;
    const canvas = h('canvas', {
        width: targetWidth,
        height: targetHeight,
    });
    const ctx = canvas.getContext('2d');
    const data = isImageData(imageData)
        ? (await imageDataToCanvas(imageData))
        : imageData;
    ctx.drawImage(data, 0, 0, targetWidth, targetHeight);
    resolve(canvasToImageData(canvas));
});

let limit;
var getWebGLTextureSizeLimit = () => {
    if (limit)
        return limit;
    const canvas = h('canvas');
    const gl = getWebGLContext(canvas);
    limit = gl.getParameter(gl.MAX_TEXTURE_SIZE);
    releaseCanvas(canvas);
    return limit;
};

var getDevicePixelRatio = () => (isBrowser() && window.devicePixelRatio) || 1;

var hexToRGB = (str) => {
    const [, q, w, e] = str.split('');
    str = str.length === 4 ? `#${q}${q}${w}${w}${e}${e}` : str;
    const [, r, g, b] = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(str);
    return [r, g, b].map((v) => parseInt(v, 16) / 255);
};

var colorStringToColorArray = (color) => {
    // if rgba(128, 128, 128, .5)
    if (/^rgba/.test(color)) {
        return color
            .substr(5)
            .split(',')
            .map(parseFloat)
            .map((v, i) => v / (i === 3 ? 1 : 255));
    }
    // if rgb(128, 128, 128)
    if (/^rgb/.test(color)) {
        return color
            .substr(4)
            .split(',')
            .map(parseFloat)
            .map((v) => v / 255);
    }
    // if #777
    if (/^#/.test(color)) {
        return hexToRGB(color);
    }
};

var isDarkColor = (color) => {
    return color[0] < 0.25 && color[1] < 0.25 && color[2] < 0.25;
};

var localize = (prop, locale, params) => isFunction(prop) ? prop(locale, params) : prop;

/* src/native/ui/index.svelte generated by Svelte v3.29.4 */

function get_each_context$2(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[245] = list[i];
	return child_ctx;
}

function get_each_context_1(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[248] = list[i];
	return child_ctx;
}

// (1370:1) {#if canRender}
function create_if_block$1(ctx) {
	let t;
	let if_block1_anchor;
	let current;
	let if_block0 = /*isStatusVisible*/ ctx[53] && create_if_block_11(ctx);
	let if_block1 = /*$imagePreview*/ ctx[44] && create_if_block_1$1(ctx);

	return {
		c() {
			if (if_block0) if_block0.c();
			t = space();
			if (if_block1) if_block1.c();
			if_block1_anchor = empty();
		},
		m(target, anchor) {
			if (if_block0) if_block0.m(target, anchor);
			insert(target, t, anchor);
			if (if_block1) if_block1.m(target, anchor);
			insert(target, if_block1_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			if (/*isStatusVisible*/ ctx[53]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty[1] & /*isStatusVisible*/ 4194304) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_11(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(t.parentNode, t);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (/*$imagePreview*/ ctx[44]) {
				if (if_block1) {
					if_block1.p(ctx, dirty);

					if (dirty[1] & /*$imagePreview*/ 8192) {
						transition_in(if_block1, 1);
					}
				} else {
					if_block1 = create_if_block_1$1(ctx);
					if_block1.c();
					transition_in(if_block1, 1);
					if_block1.m(if_block1_anchor.parentNode, if_block1_anchor);
				}
			} else if (if_block1) {
				group_outros();

				transition_out(if_block1, 1, 1, () => {
					if_block1 = null;
				});

				check_outros();
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);
			transition_in(if_block1);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			transition_out(if_block1);
			current = false;
		},
		d(detaching) {
			if (if_block0) if_block0.d(detaching);
			if (detaching) detach(t);
			if (if_block1) if_block1.d(detaching);
			if (detaching) detach(if_block1_anchor);
		}
	};
}

// (1372:2) {#if isStatusVisible}
function create_if_block_11(ctx) {
	let div;
	let p;
	let current_block_type_index;
	let if_block;
	let p_style_value;
	let div_style_value;
	let current;
	const if_block_creators = [create_if_block_12, create_if_block_14];
	const if_blocks = [];

	function select_block_type(ctx, dirty) {
		if (/*isWaitingForImage*/ ctx[40] || /*isImageLoadError*/ ctx[39] || /*isLoadingImageData*/ ctx[42] || /*isCreatingImagePreview*/ ctx[43]) return 0;
		if (/*isProcessingImage*/ ctx[45] && /*imageProcessStatusLabel*/ ctx[48]) return 1;
		return -1;
	}

	if (~(current_block_type_index = select_block_type(ctx))) {
		if_block = if_blocks[current_block_type_index] = if_block_creators[current_block_type_index](ctx);
	}

	return {
		c() {
			div = element("div");
			p = element("p");
			if (if_block) if_block.c();
			attr(p, "style", p_style_value = `transform: translateX(${/*$statusOffsetX*/ ctx[54]}px)`);
			attr(div, "class", "DokaStatus");
			attr(div, "style", div_style_value = `opacity: ${/*$statusOpacity*/ ctx[52]}`);
		},
		m(target, anchor) {
			insert(target, div, anchor);
			append(div, p);

			if (~current_block_type_index) {
				if_blocks[current_block_type_index].m(p, null);
			}

			current = true;
		},
		p(ctx, dirty) {
			let previous_block_index = current_block_type_index;
			current_block_type_index = select_block_type(ctx);

			if (current_block_type_index === previous_block_index) {
				if (~current_block_type_index) {
					if_blocks[current_block_type_index].p(ctx, dirty);
				}
			} else {
				if (if_block) {
					group_outros();

					transition_out(if_blocks[previous_block_index], 1, 1, () => {
						if_blocks[previous_block_index] = null;
					});

					check_outros();
				}

				if (~current_block_type_index) {
					if_block = if_blocks[current_block_type_index];

					if (!if_block) {
						if_block = if_blocks[current_block_type_index] = if_block_creators[current_block_type_index](ctx);
						if_block.c();
					}

					transition_in(if_block, 1);
					if_block.m(p, null);
				} else {
					if_block = null;
				}
			}

			if (!current || dirty[1] & /*$statusOffsetX*/ 8388608 && p_style_value !== (p_style_value = `transform: translateX(${/*$statusOffsetX*/ ctx[54]}px)`)) {
				attr(p, "style", p_style_value);
			}

			if (!current || dirty[1] & /*$statusOpacity*/ 2097152 && div_style_value !== (div_style_value = `opacity: ${/*$statusOpacity*/ ctx[52]}`)) {
				attr(div, "style", div_style_value);
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block);
			current = true;
		},
		o(local) {
			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);

			if (~current_block_type_index) {
				if_blocks[current_block_type_index].d();
			}
		}
	};
}

// (1387:58) 
function create_if_block_14(ctx) {
	let statusmessage;
	let t0;
	let statusprogress;
	let t1;
	let if_block_anchor;
	let current;

	statusmessage = new StatusMessage({
			props: {
				text: /*imageProcessStatusLabel*/ ctx[48],
				onmeasure: /*offsetProgress*/ ctx[114]
			}
		});

	statusprogress = new StatusProgress({
			props: {
				offset: /*$progressOffsetX*/ ctx[55],
				visible: /*imageProcessShowProgressIndicator*/ ctx[50],
				progress: /*imageProcessProgress*/ ctx[49]
			}
		});

	let if_block = /*isImageProcessingError*/ ctx[51] && create_if_block_15(ctx);

	return {
		c() {
			create_component(statusmessage.$$.fragment);
			t0 = space();
			create_component(statusprogress.$$.fragment);
			t1 = space();
			if (if_block) if_block.c();
			if_block_anchor = empty();
		},
		m(target, anchor) {
			mount_component(statusmessage, target, anchor);
			insert(target, t0, anchor);
			mount_component(statusprogress, target, anchor);
			insert(target, t1, anchor);
			if (if_block) if_block.m(target, anchor);
			insert(target, if_block_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const statusmessage_changes = {};
			if (dirty[1] & /*imageProcessStatusLabel*/ 131072) statusmessage_changes.text = /*imageProcessStatusLabel*/ ctx[48];
			statusmessage.$set(statusmessage_changes);
			const statusprogress_changes = {};
			if (dirty[1] & /*$progressOffsetX*/ 16777216) statusprogress_changes.offset = /*$progressOffsetX*/ ctx[55];
			if (dirty[1] & /*imageProcessShowProgressIndicator*/ 524288) statusprogress_changes.visible = /*imageProcessShowProgressIndicator*/ ctx[50];
			if (dirty[1] & /*imageProcessProgress*/ 262144) statusprogress_changes.progress = /*imageProcessProgress*/ ctx[49];
			statusprogress.$set(statusprogress_changes);

			if (/*isImageProcessingError*/ ctx[51]) {
				if (if_block) {
					if_block.p(ctx, dirty);

					if (dirty[1] & /*isImageProcessingError*/ 1048576) {
						transition_in(if_block, 1);
					}
				} else {
					if_block = create_if_block_15(ctx);
					if_block.c();
					transition_in(if_block, 1);
					if_block.m(if_block_anchor.parentNode, if_block_anchor);
				}
			} else if (if_block) {
				group_outros();

				transition_out(if_block, 1, 1, () => {
					if_block = null;
				});

				check_outros();
			}
		},
		i(local) {
			if (current) return;
			transition_in(statusmessage.$$.fragment, local);
			transition_in(statusprogress.$$.fragment, local);
			transition_in(if_block);
			current = true;
		},
		o(local) {
			transition_out(statusmessage.$$.fragment, local);
			transition_out(statusprogress.$$.fragment, local);
			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			destroy_component(statusmessage, detaching);
			if (detaching) detach(t0);
			destroy_component(statusprogress, detaching);
			if (detaching) detach(t1);
			if (if_block) if_block.d(detaching);
			if (detaching) detach(if_block_anchor);
		}
	};
}

// (1376:3) {#if isWaitingForImage || isImageLoadError || isLoadingImageData || isCreatingImagePreview}
function create_if_block_12(ctx) {
	let statusmessage;
	let t0;
	let statusprogress;
	let t1;
	let if_block_anchor;
	let current;

	statusmessage = new StatusMessage({
			props: {
				text: /*imageLoadStatusLabel*/ ctx[47],
				onmeasure: /*offsetProgress*/ ctx[114]
			}
		});

	statusprogress = new StatusProgress({
			props: {
				offset: /*$progressOffsetX*/ ctx[55],
				visible: /*imageLoadShowProgressIndicator*/ ctx[46],
				progress: /*imageLoadProgress*/ ctx[41]
			}
		});

	let if_block = /*isImageLoadError*/ ctx[39] && create_if_block_13(ctx);

	return {
		c() {
			create_component(statusmessage.$$.fragment);
			t0 = space();
			create_component(statusprogress.$$.fragment);
			t1 = space();
			if (if_block) if_block.c();
			if_block_anchor = empty();
		},
		m(target, anchor) {
			mount_component(statusmessage, target, anchor);
			insert(target, t0, anchor);
			mount_component(statusprogress, target, anchor);
			insert(target, t1, anchor);
			if (if_block) if_block.m(target, anchor);
			insert(target, if_block_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const statusmessage_changes = {};
			if (dirty[1] & /*imageLoadStatusLabel*/ 65536) statusmessage_changes.text = /*imageLoadStatusLabel*/ ctx[47];
			statusmessage.$set(statusmessage_changes);
			const statusprogress_changes = {};
			if (dirty[1] & /*$progressOffsetX*/ 16777216) statusprogress_changes.offset = /*$progressOffsetX*/ ctx[55];
			if (dirty[1] & /*imageLoadShowProgressIndicator*/ 32768) statusprogress_changes.visible = /*imageLoadShowProgressIndicator*/ ctx[46];
			if (dirty[1] & /*imageLoadProgress*/ 1024) statusprogress_changes.progress = /*imageLoadProgress*/ ctx[41];
			statusprogress.$set(statusprogress_changes);

			if (/*isImageLoadError*/ ctx[39]) {
				if (if_block) {
					if_block.p(ctx, dirty);

					if (dirty[1] & /*isImageLoadError*/ 256) {
						transition_in(if_block, 1);
					}
				} else {
					if_block = create_if_block_13(ctx);
					if_block.c();
					transition_in(if_block, 1);
					if_block.m(if_block_anchor.parentNode, if_block_anchor);
				}
			} else if (if_block) {
				group_outros();

				transition_out(if_block, 1, 1, () => {
					if_block = null;
				});

				check_outros();
			}
		},
		i(local) {
			if (current) return;
			transition_in(statusmessage.$$.fragment, local);
			transition_in(statusprogress.$$.fragment, local);
			transition_in(if_block);
			current = true;
		},
		o(local) {
			transition_out(statusmessage.$$.fragment, local);
			transition_out(statusprogress.$$.fragment, local);
			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			destroy_component(statusmessage, detaching);
			if (detaching) detach(t0);
			destroy_component(statusprogress, detaching);
			if (detaching) detach(t1);
			if (if_block) if_block.d(detaching);
			if (detaching) detach(if_block_anchor);
		}
	};
}

// (1392:4) {#if isImageProcessingError}
function create_if_block_15(ctx) {
	let span;
	let button;
	let span_style_value;
	let current;

	button = new Button({
			props: {
				onclick: /*handleCloseImageProcessError*/ ctx[116],
				label: /*locale*/ ctx[2].statusLabelButtonClose,
				icon: /*locale*/ ctx[2].statusIconButtonClose,
				hideLabel: true
			}
		});

	return {
		c() {
			span = element("span");
			create_component(button.$$.fragment);
			attr(span, "class", "DokaStatusButton");
			attr(span, "style", span_style_value = `transform: translateX(${/*$progressOffsetX*/ ctx[55]}px)`);
		},
		m(target, anchor) {
			insert(target, span, anchor);
			mount_component(button, span, null);
			current = true;
		},
		p(ctx, dirty) {
			const button_changes = {};
			if (dirty[0] & /*locale*/ 4) button_changes.label = /*locale*/ ctx[2].statusLabelButtonClose;
			if (dirty[0] & /*locale*/ 4) button_changes.icon = /*locale*/ ctx[2].statusIconButtonClose;
			button.$set(button_changes);

			if (!current || dirty[1] & /*$progressOffsetX*/ 16777216 && span_style_value !== (span_style_value = `transform: translateX(${/*$progressOffsetX*/ ctx[55]}px)`)) {
				attr(span, "style", span_style_value);
			}
		},
		i(local) {
			if (current) return;
			transition_in(button.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(button.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(span);
			destroy_component(button);
		}
	};
}

// (1381:4) {#if isImageLoadError}
function create_if_block_13(ctx) {
	let span;
	let button;
	let span_style_value;
	let current;

	button = new Button({
			props: {
				onclick: /*handleCloseImageLoadError*/ ctx[115],
				label: /*locale*/ ctx[2].statusLabelButtonClose,
				icon: /*locale*/ ctx[2].statusIconButtonClose,
				hideLabel: true
			}
		});

	return {
		c() {
			span = element("span");
			create_component(button.$$.fragment);
			attr(span, "class", "DokaStatusButton");
			attr(span, "style", span_style_value = `transform: translateX(${/*$progressOffsetX*/ ctx[55]}px)`);
		},
		m(target, anchor) {
			insert(target, span, anchor);
			mount_component(button, span, null);
			current = true;
		},
		p(ctx, dirty) {
			const button_changes = {};
			if (dirty[0] & /*locale*/ 4) button_changes.label = /*locale*/ ctx[2].statusLabelButtonClose;
			if (dirty[0] & /*locale*/ 4) button_changes.icon = /*locale*/ ctx[2].statusIconButtonClose;
			button.$set(button_changes);

			if (!current || dirty[1] & /*$progressOffsetX*/ 16777216 && span_style_value !== (span_style_value = `transform: translateX(${/*$progressOffsetX*/ ctx[55]}px)`)) {
				attr(span, "style", span_style_value);
			}
		},
		i(local) {
			if (current) return;
			transition_in(button.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(button.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(span);
			destroy_component(button);
		}
	};
}

// (1403:2) {#if $imagePreview}
function create_if_block_1$1(ctx) {
	let t0;
	let t1;
	let current_block_type_index;
	let if_block2;
	let t2;
	let canvas;
	let current;
	let if_block0 = /*enableToolbar*/ ctx[9] && create_if_block_4(ctx);
	let if_block1 = /*shouldRenderTabs*/ ctx[18] && /*showUtils*/ ctx[16] && create_if_block_3(ctx);
	const if_block_creators = [create_if_block_2, create_else_block];
	const if_blocks = [];

	function select_block_type_1(ctx, dirty) {
		if (/*shouldRenderTabs*/ ctx[18]) return 0;
		return 1;
	}

	current_block_type_index = select_block_type_1(ctx);
	if_block2 = if_blocks[current_block_type_index] = if_block_creators[current_block_type_index](ctx);

	canvas = new Canvas({
			props: {
				animate: /*shouldAnimate*/ ctx[20],
				pixelRatio: /*$pixelRatio*/ ctx[62],
				backgroundColor: /*$rootBackgroundColor*/ ctx[36],
				maskRect: /*$imageSelectionRectPresentation*/ ctx[38],
				imageSize: /*$imageSize*/ ctx[19],
				imageData: /*$imagePreview*/ ctx[44],
				imageProps: /*imageProps*/ ctx[37],
				imagePreviews: /*$imagePreviews*/ ctx[63],
				loadImageData: /*imageSourceToImageData*/ ctx[11],
				willRender: /*func_2*/ ctx[153]
			}
		});

	return {
		c() {
			if (if_block0) if_block0.c();
			t0 = space();
			if (if_block1) if_block1.c();
			t1 = space();
			if_block2.c();
			t2 = space();
			create_component(canvas.$$.fragment);
		},
		m(target, anchor) {
			if (if_block0) if_block0.m(target, anchor);
			insert(target, t0, anchor);
			if (if_block1) if_block1.m(target, anchor);
			insert(target, t1, anchor);
			if_blocks[current_block_type_index].m(target, anchor);
			insert(target, t2, anchor);
			mount_component(canvas, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			if (/*enableToolbar*/ ctx[9]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty[0] & /*enableToolbar*/ 512) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_4(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(t0.parentNode, t0);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (/*shouldRenderTabs*/ ctx[18] && /*showUtils*/ ctx[16]) {
				if (if_block1) {
					if_block1.p(ctx, dirty);

					if (dirty[0] & /*shouldRenderTabs, showUtils*/ 327680) {
						transition_in(if_block1, 1);
					}
				} else {
					if_block1 = create_if_block_3(ctx);
					if_block1.c();
					transition_in(if_block1, 1);
					if_block1.m(t1.parentNode, t1);
				}
			} else if (if_block1) {
				group_outros();

				transition_out(if_block1, 1, 1, () => {
					if_block1 = null;
				});

				check_outros();
			}

			let previous_block_index = current_block_type_index;
			current_block_type_index = select_block_type_1(ctx);

			if (current_block_type_index === previous_block_index) {
				if_blocks[current_block_type_index].p(ctx, dirty);
			} else {
				group_outros();

				transition_out(if_blocks[previous_block_index], 1, 1, () => {
					if_blocks[previous_block_index] = null;
				});

				check_outros();
				if_block2 = if_blocks[current_block_type_index];

				if (!if_block2) {
					if_block2 = if_blocks[current_block_type_index] = if_block_creators[current_block_type_index](ctx);
					if_block2.c();
				}

				transition_in(if_block2, 1);
				if_block2.m(t2.parentNode, t2);
			}

			const canvas_changes = {};
			if (dirty[0] & /*shouldAnimate*/ 1048576) canvas_changes.animate = /*shouldAnimate*/ ctx[20];
			if (dirty[2] & /*$pixelRatio*/ 1) canvas_changes.pixelRatio = /*$pixelRatio*/ ctx[62];
			if (dirty[1] & /*$rootBackgroundColor*/ 32) canvas_changes.backgroundColor = /*$rootBackgroundColor*/ ctx[36];
			if (dirty[1] & /*$imageSelectionRectPresentation*/ 128) canvas_changes.maskRect = /*$imageSelectionRectPresentation*/ ctx[38];
			if (dirty[0] & /*$imageSize*/ 524288) canvas_changes.imageSize = /*$imageSize*/ ctx[19];
			if (dirty[1] & /*$imagePreview*/ 8192) canvas_changes.imageData = /*$imagePreview*/ ctx[44];
			if (dirty[1] & /*imageProps*/ 64) canvas_changes.imageProps = /*imageProps*/ ctx[37];
			if (dirty[2] & /*$imagePreviews*/ 2) canvas_changes.imagePreviews = /*$imagePreviews*/ ctx[63];
			if (dirty[0] & /*imageSourceToImageData*/ 2048) canvas_changes.loadImageData = /*imageSourceToImageData*/ ctx[11];
			if (dirty[0] & /*willRenderCanvas*/ 32 | dirty[2] & /*$imageDecoration, $imageAnnotation, $imageOverlay*/ 28) canvas_changes.willRender = /*func_2*/ ctx[153];
			canvas.$set(canvas_changes);
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);
			transition_in(if_block1);
			transition_in(if_block2);
			transition_in(canvas.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			transition_out(if_block1);
			transition_out(if_block2);
			transition_out(canvas.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (if_block0) if_block0.d(detaching);
			if (detaching) detach(t0);
			if (if_block1) if_block1.d(detaching);
			if (detaching) detach(t1);
			if_blocks[current_block_type_index].d(detaching);
			if (detaching) detach(t2);
			destroy_component(canvas, detaching);
		}
	};
}

// (1405:3) {#if enableToolbar}
function create_if_block_4(ctx) {
	let div;
	let t0;
	let t1;
	let measurable_action;
	let current;
	let mounted;
	let dispose;
	let if_block0 = (/*navButtonsAlpha*/ ctx[57].length || /*enableButtonRevert*/ ctx[7]) && create_if_block_9(ctx);
	let if_block1 = /*enableNavigateHistory*/ ctx[8] && create_if_block_6(ctx);
	let if_block2 = /*enableButtonExport*/ ctx[6] && create_if_block_5(ctx);

	return {
		c() {
			div = element("div");
			if (if_block0) if_block0.c();
			t0 = space();
			if (if_block1) if_block1.c();
			t1 = space();
			if (if_block2) if_block2.c();
			attr(div, "class", "DokaNav DokaNavTools");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			if (if_block0) if_block0.m(div, null);
			append(div, t0);
			if (if_block1) if_block1.m(div, null);
			append(div, t1);
			if (if_block2) if_block2.m(div, null);
			current = true;

			if (!mounted) {
				dispose = [
					listen(div, "measure", /*measure_handler*/ ctx[138]),
					action_destroyer(measurable_action = measurable.call(null, div))
				];

				mounted = true;
			}
		},
		p(ctx, dirty) {
			if (/*navButtonsAlpha*/ ctx[57].length || /*enableButtonRevert*/ ctx[7]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty[0] & /*enableButtonRevert*/ 128 | dirty[1] & /*navButtonsAlpha*/ 67108864) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_9(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(div, t0);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (/*enableNavigateHistory*/ ctx[8]) {
				if (if_block1) {
					if_block1.p(ctx, dirty);

					if (dirty[0] & /*enableNavigateHistory*/ 256) {
						transition_in(if_block1, 1);
					}
				} else {
					if_block1 = create_if_block_6(ctx);
					if_block1.c();
					transition_in(if_block1, 1);
					if_block1.m(div, t1);
				}
			} else if (if_block1) {
				group_outros();

				transition_out(if_block1, 1, 1, () => {
					if_block1 = null;
				});

				check_outros();
			}

			if (/*enableButtonExport*/ ctx[6]) {
				if (if_block2) {
					if_block2.p(ctx, dirty);

					if (dirty[0] & /*enableButtonExport*/ 64) {
						transition_in(if_block2, 1);
					}
				} else {
					if_block2 = create_if_block_5(ctx);
					if_block2.c();
					transition_in(if_block2, 1);
					if_block2.m(div, null);
				}
			} else if (if_block2) {
				group_outros();

				transition_out(if_block2, 1, 1, () => {
					if_block2 = null;
				});

				check_outros();
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);
			transition_in(if_block1);
			transition_in(if_block2);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			transition_out(if_block1);
			transition_out(if_block2);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			if (if_block0) if_block0.d();
			if (if_block1) if_block1.d();
			if (if_block2) if_block2.d();
			mounted = false;
			run_all(dispose);
		}
	};
}

// (1408:4) {#if navButtonsAlpha.length || enableButtonRevert}
function create_if_block_9(ctx) {
	let div1;
	let div0;
	let each_blocks = [];
	let each_1_lookup = new Map();
	let t;
	let current;
	let each_value_1 = /*navButtonsAlpha*/ ctx[57];
	const get_key = ctx => /*button*/ ctx[248].id;

	for (let i = 0; i < each_value_1.length; i += 1) {
		let child_ctx = get_each_context_1(ctx, each_value_1, i);
		let key = get_key(child_ctx);
		each_1_lookup.set(key, each_blocks[i] = create_each_block_1(key, child_ctx));
	}

	let if_block = /*enableButtonRevert*/ ctx[7] && create_if_block_10(ctx);

	return {
		c() {
			div1 = element("div");
			div0 = element("div");

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			t = space();
			if (if_block) if_block.c();
			attr(div0, "class", "DokaNavSet");
			attr(div1, "class", "DokaNavGroup");
		},
		m(target, anchor) {
			insert(target, div1, anchor);
			append(div1, div0);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].m(div0, null);
			}

			append(div0, t);
			if (if_block) if_block.m(div0, null);
			current = true;
		},
		p(ctx, dirty) {
			if (dirty[0] & /*locale*/ 4 | dirty[1] & /*navButtonsAlpha*/ 67108864) {
				const each_value_1 = /*navButtonsAlpha*/ ctx[57];
				group_outros();
				each_blocks = update_keyed_each(each_blocks, dirty, get_key, 1, ctx, each_value_1, each_1_lookup, div0, outro_and_destroy_block, create_each_block_1, t, get_each_context_1);
				check_outros();
			}

			if (/*enableButtonRevert*/ ctx[7]) {
				if (if_block) {
					if_block.p(ctx, dirty);

					if (dirty[0] & /*enableButtonRevert*/ 128) {
						transition_in(if_block, 1);
					}
				} else {
					if_block = create_if_block_10(ctx);
					if_block.c();
					transition_in(if_block, 1);
					if_block.m(div0, null);
				}
			} else if (if_block) {
				group_outros();

				transition_out(if_block, 1, 1, () => {
					if_block = null;
				});

				check_outros();
			}
		},
		i(local) {
			if (current) return;

			for (let i = 0; i < each_value_1.length; i += 1) {
				transition_in(each_blocks[i]);
			}

			transition_in(if_block);
			current = true;
		},
		o(local) {
			for (let i = 0; i < each_blocks.length; i += 1) {
				transition_out(each_blocks[i]);
			}

			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div1);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].d();
			}

			if (if_block) if_block.d();
		}
	};
}

// (1411:5) {#each navButtonsAlpha as button (button.id) }
function create_each_block_1(key_1, ctx) {
	let first;
	let button;
	let current;

	const button_spread_levels = [
		/*button*/ ctx[248],
		{
			label: localize(/*button*/ ctx[248].label, /*locale*/ ctx[2])
		},
		{
			icon: localize(/*button*/ ctx[248].icon, /*locale*/ ctx[2])
		}
	];

	let button_props = {};

	for (let i = 0; i < button_spread_levels.length; i += 1) {
		button_props = assign(button_props, button_spread_levels[i]);
	}

	button = new Button({ props: button_props });

	return {
		key: key_1,
		first: null,
		c() {
			first = empty();
			create_component(button.$$.fragment);
			this.first = first;
		},
		m(target, anchor) {
			insert(target, first, anchor);
			mount_component(button, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const button_changes = (dirty[0] & /*locale*/ 4 | dirty[1] & /*navButtonsAlpha*/ 67108864)
			? get_spread_update(button_spread_levels, [
					dirty[1] & /*navButtonsAlpha*/ 67108864 && get_spread_object(/*button*/ ctx[248]),
					{
						label: localize(/*button*/ ctx[248].label, /*locale*/ ctx[2])
					},
					{
						icon: localize(/*button*/ ctx[248].icon, /*locale*/ ctx[2])
					}
				])
			: {};

			button.$set(button_changes);
		},
		i(local) {
			if (current) return;
			transition_in(button.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(button.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(first);
			destroy_component(button, detaching);
		}
	};
}

// (1414:5) {#if enableButtonRevert}
function create_if_block_10(ctx) {
	let button;
	let current;

	button = new Button({
			props: {
				label: /*locale*/ ctx[2].labelButtonRevert,
				hideLabel: true,
				icon: /*locale*/ ctx[2].iconButtonRevert,
				disabled: !/*canUndo*/ ctx[22],
				onclick: /*revert*/ ctx[106]
			}
		});

	return {
		c() {
			create_component(button.$$.fragment);
		},
		m(target, anchor) {
			mount_component(button, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const button_changes = {};
			if (dirty[0] & /*locale*/ 4) button_changes.label = /*locale*/ ctx[2].labelButtonRevert;
			if (dirty[0] & /*locale*/ 4) button_changes.icon = /*locale*/ ctx[2].iconButtonRevert;
			if (dirty[0] & /*canUndo*/ 4194304) button_changes.disabled = !/*canUndo*/ ctx[22];
			button.$set(button_changes);
		},
		i(local) {
			if (current) return;
			transition_in(button.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(button.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(button, detaching);
		}
	};
}

// (1421:4) {#if enableNavigateHistory}
function create_if_block_6(ctx) {
	let div;
	let t;
	let div_class_value;
	let current;
	let if_block0 = /*enableNavigateHistory*/ ctx[8] && create_if_block_8(ctx);
	let if_block1 = /*shouldRenderUtilTools*/ ctx[35] && create_if_block_7(ctx);

	return {
		c() {
			div = element("div");
			if (if_block0) if_block0.c();
			t = space();
			if (if_block1) if_block1.c();

			attr(div, "class", div_class_value = arrayJoin([
				"DokaNavGroup",
				/*shouldRenderUtilTools*/ ctx[35] && /*isPortrait*/ ctx[33]
				? undefined
				: "DokaNavGroupFloat"
			]));
		},
		m(target, anchor) {
			insert(target, div, anchor);
			if (if_block0) if_block0.m(div, null);
			append(div, t);
			if (if_block1) if_block1.m(div, null);
			current = true;
		},
		p(ctx, dirty) {
			if (/*enableNavigateHistory*/ ctx[8]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty[0] & /*enableNavigateHistory*/ 256) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_8(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(div, t);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (/*shouldRenderUtilTools*/ ctx[35]) {
				if (if_block1) {
					if_block1.p(ctx, dirty);

					if (dirty[1] & /*shouldRenderUtilTools*/ 16) {
						transition_in(if_block1, 1);
					}
				} else {
					if_block1 = create_if_block_7(ctx);
					if_block1.c();
					transition_in(if_block1, 1);
					if_block1.m(div, null);
				}
			} else if (if_block1) {
				group_outros();

				transition_out(if_block1, 1, 1, () => {
					if_block1 = null;
				});

				check_outros();
			}

			if (!current || dirty[1] & /*shouldRenderUtilTools, isPortrait*/ 20 && div_class_value !== (div_class_value = arrayJoin([
				"DokaNavGroup",
				/*shouldRenderUtilTools*/ ctx[35] && /*isPortrait*/ ctx[33]
				? undefined
				: "DokaNavGroupFloat"
			]))) {
				attr(div, "class", div_class_value);
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);
			transition_in(if_block1);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			transition_out(if_block1);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			if (if_block0) if_block0.d();
			if (if_block1) if_block1.d();
		}
	};
}

// (1423:5) {#if enableNavigateHistory}
function create_if_block_8(ctx) {
	let div;
	let button0;
	let t;
	let button1;
	let current;

	button0 = new Button({
			props: {
				label: /*locale*/ ctx[2].labelButtonUndo,
				hideLabel: true,
				icon: /*locale*/ ctx[2].iconButtonUndo,
				disabled: !/*canUndo*/ ctx[22],
				onclick: /*history*/ ctx[12].undo
			}
		});

	button1 = new Button({
			props: {
				label: /*locale*/ ctx[2].labelButtonRedo,
				hideLabel: true,
				icon: /*locale*/ ctx[2].iconButtonRedo,
				disabled: !/*canRedo*/ ctx[23],
				onclick: /*history*/ ctx[12].redo
			}
		});

	return {
		c() {
			div = element("div");
			create_component(button0.$$.fragment);
			t = space();
			create_component(button1.$$.fragment);
			attr(div, "class", "DokaNavSet");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			mount_component(button0, div, null);
			append(div, t);
			mount_component(button1, div, null);
			current = true;
		},
		p(ctx, dirty) {
			const button0_changes = {};
			if (dirty[0] & /*locale*/ 4) button0_changes.label = /*locale*/ ctx[2].labelButtonUndo;
			if (dirty[0] & /*locale*/ 4) button0_changes.icon = /*locale*/ ctx[2].iconButtonUndo;
			if (dirty[0] & /*canUndo*/ 4194304) button0_changes.disabled = !/*canUndo*/ ctx[22];
			button0.$set(button0_changes);
			const button1_changes = {};
			if (dirty[0] & /*locale*/ 4) button1_changes.label = /*locale*/ ctx[2].labelButtonRedo;
			if (dirty[0] & /*locale*/ 4) button1_changes.icon = /*locale*/ ctx[2].iconButtonRedo;
			if (dirty[0] & /*canRedo*/ 8388608) button1_changes.disabled = !/*canRedo*/ ctx[23];
			button1.$set(button1_changes);
		},
		i(local) {
			if (current) return;
			transition_in(button0.$$.fragment, local);
			transition_in(button1.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(button0.$$.fragment, local);
			transition_out(button1.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			destroy_component(button0);
			destroy_component(button1);
		}
	};
}

// (1429:5) {#if shouldRenderUtilTools}
function create_if_block_7(ctx) {
	let div;
	let each_blocks = [];
	let each_1_lookup = new Map();
	let current;
	let each_value = /*utilTools*/ ctx[26];
	const get_key = ctx => /*tool*/ ctx[245].id;

	for (let i = 0; i < each_value.length; i += 1) {
		let child_ctx = get_each_context$2(ctx, each_value, i);
		let key = get_key(child_ctx);
		each_1_lookup.set(key, each_blocks[i] = create_each_block$2(key, child_ctx));
	}

	return {
		c() {
			div = element("div");

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			attr(div, "class", "DokaNavSet");
		},
		m(target, anchor) {
			insert(target, div, anchor);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].m(div, null);
			}

			current = true;
		},
		p(ctx, dirty) {
			if (dirty[0] & /*utilTools*/ 67108864) {
				const each_value = /*utilTools*/ ctx[26];
				group_outros();
				each_blocks = update_keyed_each(each_blocks, dirty, get_key, 1, ctx, each_value, each_1_lookup, div, outro_and_destroy_block, create_each_block$2, null, get_each_context$2);
				check_outros();
			}
		},
		i(local) {
			if (current) return;

			for (let i = 0; i < each_value.length; i += 1) {
				transition_in(each_blocks[i]);
			}

			current = true;
		},
		o(local) {
			for (let i = 0; i < each_blocks.length; i += 1) {
				transition_out(each_blocks[i]);
			}

			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].d();
			}
		}
	};
}

// (1431:6) {#each utilTools as tool (tool.id) }
function create_each_block$2(key_1, ctx) {
	let first;
	let switch_instance;
	let switch_instance_anchor;
	let current;
	const switch_instance_spread_levels = [/*tool*/ ctx[245].props, { hideLabel: true }];
	var switch_value = /*tool*/ ctx[245].component;

	function switch_props(ctx) {
		let switch_instance_props = {};

		for (let i = 0; i < switch_instance_spread_levels.length; i += 1) {
			switch_instance_props = assign(switch_instance_props, switch_instance_spread_levels[i]);
		}

		return { props: switch_instance_props };
	}

	if (switch_value) {
		switch_instance = new switch_value(switch_props());
	}

	return {
		key: key_1,
		first: null,
		c() {
			first = empty();
			if (switch_instance) create_component(switch_instance.$$.fragment);
			switch_instance_anchor = empty();
			this.first = first;
		},
		m(target, anchor) {
			insert(target, first, anchor);

			if (switch_instance) {
				mount_component(switch_instance, target, anchor);
			}

			insert(target, switch_instance_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const switch_instance_changes = (dirty[0] & /*utilTools*/ 67108864)
			? get_spread_update(switch_instance_spread_levels, [
					get_spread_object(/*tool*/ ctx[245].props),
					switch_instance_spread_levels[1]
				])
			: {};

			if (switch_value !== (switch_value = /*tool*/ ctx[245].component)) {
				if (switch_instance) {
					group_outros();
					const old_component = switch_instance;

					transition_out(old_component.$$.fragment, 1, 0, () => {
						destroy_component(old_component, 1);
					});

					check_outros();
				}

				if (switch_value) {
					switch_instance = new switch_value(switch_props());
					create_component(switch_instance.$$.fragment);
					transition_in(switch_instance.$$.fragment, 1);
					mount_component(switch_instance, switch_instance_anchor.parentNode, switch_instance_anchor);
				} else {
					switch_instance = null;
				}
			} else if (switch_value) {
				switch_instance.$set(switch_instance_changes);
			}
		},
		i(local) {
			if (current) return;
			if (switch_instance) transition_in(switch_instance.$$.fragment, local);
			current = true;
		},
		o(local) {
			if (switch_instance) transition_out(switch_instance.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(first);
			if (detaching) detach(switch_instance_anchor);
			if (switch_instance) destroy_component(switch_instance, detaching);
		}
	};
}

// (1439:4) {#if enableButtonExport}
function create_if_block_5(ctx) {
	let div;
	let button;
	let current;

	button = new Button({
			props: {
				class: "DokaButtonExport",
				hideLabel: /*isNarrowViewport*/ ctx[34],
				icon: /*isNarrowViewport*/ ctx[34] && /*locale*/ ctx[2].iconButtonExport,
				label: /*locale*/ ctx[2].labelButtonExport,
				onclick: /*handleExport*/ ctx[108]
			}
		});

	return {
		c() {
			div = element("div");
			create_component(button.$$.fragment);
			attr(div, "class", "DokaNavGroup");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			mount_component(button, div, null);
			current = true;
		},
		p(ctx, dirty) {
			const button_changes = {};
			if (dirty[1] & /*isNarrowViewport*/ 8) button_changes.hideLabel = /*isNarrowViewport*/ ctx[34];
			if (dirty[0] & /*locale*/ 4 | dirty[1] & /*isNarrowViewport*/ 8) button_changes.icon = /*isNarrowViewport*/ ctx[34] && /*locale*/ ctx[2].iconButtonExport;
			if (dirty[0] & /*locale*/ 4) button_changes.label = /*locale*/ ctx[2].labelButtonExport;
			button.$set(button_changes);
		},
		i(local) {
			if (current) return;
			transition_in(button.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(button.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			destroy_component(button);
		}
	};
}

// (1448:3) {#if shouldRenderTabs && showUtils }
function create_if_block_3(ctx) {
	let div;
	let scrollable;
	let current;

	scrollable = new Scrollable({
			props: {
				elasticity: /*elasticityMultiplier*/ ctx[4] * scrollElasticity,
				scrollDirection: /*isLandscape*/ ctx[32] ? "y" : "x",
				$$slots: { default: [create_default_slot_1] },
				$$scope: { ctx }
			}
		});

	return {
		c() {
			div = element("div");
			create_component(scrollable.$$.fragment);
			attr(div, "class", "DokaNav DokaNavMain");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			mount_component(scrollable, div, null);
			current = true;
		},
		p(ctx, dirty) {
			const scrollable_changes = {};
			if (dirty[0] & /*elasticityMultiplier*/ 16) scrollable_changes.elasticity = /*elasticityMultiplier*/ ctx[4] * scrollElasticity;
			if (dirty[1] & /*isLandscape*/ 2) scrollable_changes.scrollDirection = /*isLandscape*/ ctx[32] ? "y" : "x";

			if (dirty[0] & /*tabsConfig, tabs, utilSelected*/ 838860800 | dirty[8] & /*$$scope*/ 8) {
				scrollable_changes.$$scope = { dirty, ctx };
			}

			scrollable.$set(scrollable_changes);
		},
		i(local) {
			if (current) return;
			transition_in(scrollable.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(scrollable.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			destroy_component(scrollable);
		}
	};
}

// (1452:6) <Icon>
function create_default_slot_3(ctx) {
	let g;
	let raw_value = /*tab*/ ctx[244].icon + "";

	return {
		c() {
			g = svg_element("g");
		},
		m(target, anchor) {
			insert(target, g, anchor);
			g.innerHTML = raw_value;
		},
		p(ctx, dirty) {
			if (dirty[7] & /*tab*/ 134217728 && raw_value !== (raw_value = /*tab*/ ctx[244].icon + "")) g.innerHTML = raw_value;		},
		d(detaching) {
			if (detaching) detach(g);
		}
	};
}

// (1451:5) <TabList {...tabsConfig} {tabs} on:select={({ detail }) => utilSelected = detail} let:tab={tab}>
function create_default_slot_2(ctx) {
	let icon;
	let t0;
	let span;
	let t1_value = /*tab*/ ctx[244].label + "";
	let t1;
	let current;

	icon = new Icon({
			props: {
				$$slots: { default: [create_default_slot_3] },
				$$scope: { ctx }
			}
		});

	return {
		c() {
			create_component(icon.$$.fragment);
			t0 = space();
			span = element("span");
			t1 = text(t1_value);
		},
		m(target, anchor) {
			mount_component(icon, target, anchor);
			insert(target, t0, anchor);
			insert(target, span, anchor);
			append(span, t1);
			current = true;
		},
		p(ctx, dirty) {
			const icon_changes = {};

			if (dirty[7] & /*tab*/ 134217728 | dirty[8] & /*$$scope*/ 8) {
				icon_changes.$$scope = { dirty, ctx };
			}

			icon.$set(icon_changes);
			if ((!current || dirty[7] & /*tab*/ 134217728) && t1_value !== (t1_value = /*tab*/ ctx[244].label + "")) set_data(t1, t1_value);
		},
		i(local) {
			if (current) return;
			transition_in(icon.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(icon.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(icon, detaching);
			if (detaching) detach(t0);
			if (detaching) detach(span);
		}
	};
}

// (1450:4) <Scrollable elasticity={elasticityMultiplier * scrollElasticity} scrollDirection={isLandscape ? 'y' : 'x'}>
function create_default_slot_1(ctx) {
	let tablist;
	let current;
	const tablist_spread_levels = [/*tabsConfig*/ ctx[28], { tabs: /*tabs*/ ctx[29] }];

	let tablist_props = {
		$$slots: {
			default: [
				create_default_slot_2,
				({ tab }) => ({ 244: tab }),
				({ tab }) => [0, 0, 0, 0, 0, 0, 0, tab ? 134217728 : 0]
			]
		},
		$$scope: { ctx }
	};

	for (let i = 0; i < tablist_spread_levels.length; i += 1) {
		tablist_props = assign(tablist_props, tablist_spread_levels[i]);
	}

	tablist = new TabList({ props: tablist_props });
	tablist.$on("select", /*select_handler*/ ctx[139]);

	return {
		c() {
			create_component(tablist.$$.fragment);
		},
		m(target, anchor) {
			mount_component(tablist, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const tablist_changes = (dirty[0] & /*tabsConfig, tabs*/ 805306368)
			? get_spread_update(tablist_spread_levels, [
					dirty[0] & /*tabsConfig*/ 268435456 && get_spread_object(/*tabsConfig*/ ctx[28]),
					dirty[0] & /*tabs*/ 536870912 && { tabs: /*tabs*/ ctx[29] }
				])
			: {};

			if (dirty[7] & /*tab*/ 134217728 | dirty[8] & /*$$scope*/ 8) {
				tablist_changes.$$scope = { dirty, ctx };
			}

			tablist.$set(tablist_changes);
		},
		i(local) {
			if (current) return;
			transition_in(tablist.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(tablist.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(tablist, detaching);
		}
	};
}

// (1472:3) {:else}
function create_else_block(ctx) {
	let panel;
	let updating_component;
	let current;

	function panel_component_binding_1(value) {
		/*panel_component_binding_1*/ ctx[148].call(null, value);
	}

	let panel_props = {
		class: "DokaMain",
		content: {
			.../*utilsMerged*/ ctx[24].find(/*func_1*/ ctx[147]),
			props: /*pluginOptions*/ ctx[10][/*utilSelected*/ ctx[25]]
		},
		locale: /*locale*/ ctx[2],
		stores: /*utilStores*/ ctx[109]
	};

	if (/*pluginInterface*/ ctx[0][/*utilSelected*/ ctx[25]] !== void 0) {
		panel_props.component = /*pluginInterface*/ ctx[0][/*utilSelected*/ ctx[25]];
	}

	panel = new Panel({ props: panel_props });
	binding_callbacks.push(() => bind(panel, "component", panel_component_binding_1));
	panel.$on("measure", /*measure_handler_3*/ ctx[149]);
	panel.$on("show", /*show_handler_1*/ ctx[150]);
	panel.$on("hide", /*hide_handler_1*/ ctx[151]);
	panel.$on("fade", /*fade_handler_1*/ ctx[152]);

	return {
		c() {
			create_component(panel.$$.fragment);
		},
		m(target, anchor) {
			mount_component(panel, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const panel_changes = {};

			if (dirty[0] & /*utilsMerged, utilSelected, pluginOptions*/ 50332672) panel_changes.content = {
				.../*utilsMerged*/ ctx[24].find(/*func_1*/ ctx[147]),
				props: /*pluginOptions*/ ctx[10][/*utilSelected*/ ctx[25]]
			};

			if (dirty[0] & /*locale*/ 4) panel_changes.locale = /*locale*/ ctx[2];

			if (!updating_component && dirty[0] & /*pluginInterface, utilSelected*/ 33554433) {
				updating_component = true;
				panel_changes.component = /*pluginInterface*/ ctx[0][/*utilSelected*/ ctx[25]];
				add_flush_callback(() => updating_component = false);
			}

			panel.$set(panel_changes);
		},
		i(local) {
			if (current) return;
			transition_in(panel.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(panel.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(panel, detaching);
		}
	};
}

// (1459:3) {#if shouldRenderTabs}
function create_if_block_2(ctx) {
	let tabpanels;
	let current;

	const tabpanels_spread_levels = [
		{ class: "DokaMain" },
		{ visible: /*utilsVisible*/ ctx[15] },
		/*tabsConfig*/ ctx[28],
		{ panels: /*panels*/ ctx[30] }
	];

	let tabpanels_props = {
		$$slots: {
			default: [
				create_default_slot$1,
				({ panel }) => ({ 243: panel }),
				({ panel }) => [0, 0, 0, 0, 0, 0, 0, panel ? 67108864 : 0]
			]
		},
		$$scope: { ctx }
	};

	for (let i = 0; i < tabpanels_spread_levels.length; i += 1) {
		tabpanels_props = assign(tabpanels_props, tabpanels_spread_levels[i]);
	}

	tabpanels = new TabPanels({ props: tabpanels_props });
	tabpanels.$on("measure", /*measure_handler_2*/ ctx[146]);

	return {
		c() {
			create_component(tabpanels.$$.fragment);
		},
		m(target, anchor) {
			mount_component(tabpanels, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const tabpanels_changes = (dirty[0] & /*utilsVisible, tabsConfig, panels*/ 1342210048)
			? get_spread_update(tabpanels_spread_levels, [
					tabpanels_spread_levels[0],
					dirty[0] & /*utilsVisible*/ 32768 && { visible: /*utilsVisible*/ ctx[15] },
					dirty[0] & /*tabsConfig*/ 268435456 && get_spread_object(/*tabsConfig*/ ctx[28]),
					dirty[0] & /*panels*/ 1073741824 && { panels: /*panels*/ ctx[30] }
				])
			: {};

			if (dirty[0] & /*utilsMerged, pluginOptions, locale, utilSelected, pluginInterface, utilsVisible, utilsVisibleFraction*/ 184583173 | dirty[1] & /*$utilRect*/ 1073741824 | dirty[7] & /*panel*/ 67108864 | dirty[8] & /*$$scope*/ 8) {
				tabpanels_changes.$$scope = { dirty, ctx };
			}

			tabpanels.$set(tabpanels_changes);
		},
		i(local) {
			if (current) return;
			transition_in(tabpanels.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(tabpanels.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(tabpanels, detaching);
		}
	};
}

// (1460:3) <TabPanels class="DokaMain" visible={utilsVisible} {...tabsConfig} {panels} let:panel on:measure={e => $tabRect = e.detail}>
function create_default_slot$1(ctx) {
	let panel;
	let updating_component;
	let current;

	function func(...args) {
		return /*func*/ ctx[140](/*panel*/ ctx[243], ...args);
	}

	function panel_component_binding(value) {
		/*panel_component_binding*/ ctx[141].call(null, value, /*panel*/ ctx[243]);
	}

	function show_handler(...args) {
		return /*show_handler*/ ctx[143](/*panel*/ ctx[243], ...args);
	}

	function hide_handler(...args) {
		return /*hide_handler*/ ctx[144](/*panel*/ ctx[243], ...args);
	}

	function fade_handler(...args) {
		return /*fade_handler*/ ctx[145](/*panel*/ ctx[243], ...args);
	}

	let panel_props = {
		content: {
			.../*utilsMerged*/ ctx[24].find(func),
			props: /*pluginOptions*/ ctx[10][/*panel*/ ctx[243]]
		},
		locale: /*locale*/ ctx[2],
		isActive: /*panel*/ ctx[243] === /*utilSelected*/ ctx[25],
		stores: /*utilStores*/ ctx[109]
	};

	if (/*pluginInterface*/ ctx[0][/*panel*/ ctx[243]] !== void 0) {
		panel_props.component = /*pluginInterface*/ ctx[0][/*panel*/ ctx[243]];
	}

	panel = new Panel({ props: panel_props });
	binding_callbacks.push(() => bind(panel, "component", panel_component_binding));
	panel.$on("measure", /*measure_handler_1*/ ctx[142]);
	panel.$on("show", show_handler);
	panel.$on("hide", hide_handler);
	panel.$on("fade", fade_handler);

	return {
		c() {
			create_component(panel.$$.fragment);
		},
		m(target, anchor) {
			mount_component(panel, target, anchor);
			current = true;
		},
		p(new_ctx, dirty) {
			ctx = new_ctx;
			const panel_changes = {};

			if (dirty[0] & /*utilsMerged, pluginOptions*/ 16778240 | dirty[7] & /*panel*/ 67108864) panel_changes.content = {
				.../*utilsMerged*/ ctx[24].find(func),
				props: /*pluginOptions*/ ctx[10][/*panel*/ ctx[243]]
			};

			if (dirty[0] & /*locale*/ 4) panel_changes.locale = /*locale*/ ctx[2];
			if (dirty[0] & /*utilSelected*/ 33554432 | dirty[7] & /*panel*/ 67108864) panel_changes.isActive = /*panel*/ ctx[243] === /*utilSelected*/ ctx[25];

			if (!updating_component && dirty[0] & /*pluginInterface*/ 1 | dirty[7] & /*panel*/ 67108864) {
				updating_component = true;
				panel_changes.component = /*pluginInterface*/ ctx[0][/*panel*/ ctx[243]];
				add_flush_callback(() => updating_component = false);
			}

			panel.$set(panel_changes);
		},
		i(local) {
			if (current) return;
			transition_in(panel.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(panel.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(panel, detaching);
		}
	};
}

function create_fragment$a(ctx) {
	let div;
	let measurable_action;
	let focusvisible_action;
	let current;
	let mounted;
	let dispose;
	add_render_callback(/*onwindowresize*/ ctx[137]);
	let if_block = /*canRender*/ ctx[58] && create_if_block$1(ctx);

	return {
		c() {
			div = element("div");
			if (if_block) if_block.c();
			attr(div, "id", /*id*/ ctx[3]);
			attr(div, "class", /*className*/ ctx[31]);
			attr(div, "data-env", /*$env*/ ctx[59]);
		},
		m(target, anchor) {
			insert(target, div, anchor);
			if (if_block) if_block.m(div, null);
			/*div_binding*/ ctx[154](div);
			current = true;

			if (!mounted) {
				dispose = [
					listen(window, "keydown", /*handleKeydown*/ ctx[118]),
					listen(window, "keyup", /*handleKeyup*/ ctx[119]),
					listen(window, "blur", /*handleWindowBlur*/ ctx[120]),
					listen(window, "resize", /*onwindowresize*/ ctx[137]),
					listen(div, "contextmenu", /*handleContextMenu*/ ctx[121]),
					listen(div, "touchstart", function () {
						if (is_function(/*handleTouchStart*/ ctx[56])) /*handleTouchStart*/ ctx[56].apply(this, arguments);
					}),
					listen(div, "transitionend", /*handleTransitionEnd*/ ctx[110]),
					listen(div, "measure", /*measure_handler_4*/ ctx[155]),
					action_destroyer(measurable_action = measurable.call(null, div, { observeViewRect: true })),
					action_destroyer(focusvisible_action = focusvisible.call(null, div))
				];

				mounted = true;
			}
		},
		p(new_ctx, dirty) {
			ctx = new_ctx;

			if (/*canRender*/ ctx[58]) {
				if (if_block) {
					if_block.p(ctx, dirty);

					if (dirty[1] & /*canRender*/ 134217728) {
						transition_in(if_block, 1);
					}
				} else {
					if_block = create_if_block$1(ctx);
					if_block.c();
					transition_in(if_block, 1);
					if_block.m(div, null);
				}
			} else if (if_block) {
				group_outros();

				transition_out(if_block, 1, 1, () => {
					if_block = null;
				});

				check_outros();
			}

			if (!current || dirty[0] & /*id*/ 8) {
				attr(div, "id", /*id*/ ctx[3]);
			}

			if (!current || dirty[1] & /*className*/ 1) {
				attr(div, "class", /*className*/ ctx[31]);
			}

			if (!current || dirty[1] & /*$env*/ 268435456) {
				attr(div, "data-env", /*$env*/ ctx[59]);
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block);
			current = true;
		},
		o(local) {
			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			if (if_block) if_block.d();
			/*div_binding*/ ctx[154](null);
			mounted = false;
			run_all(dispose);
		}
	};
}

const imageCropRectElasticity = 5;

// updating crop selection / the intended selection rect, combined with actual selection rect, used to render elasticity visualisation
const imageSelectionRectElasticity = 1;

const scrollElasticity = 10;
const rangeInputElasticity = 5;

function instance$a($$self, $$props, $$invalidate) {
	let $images;
	let $clientRect;
	let $rootRect;
	let $imageState;
	let $imageLoadState;
	let $imageCropRectSnapshot;
	let $imageCropRectIntent;
	let $previewShouldUpscale;
	let $imageCropRect;
	let $isInteracting;
	let $imageSelectionRect;
	let $imageSelectionRectIntent;
	let $stageRect;
	let $stageScalar;
	let $imageSize;
	let $presentationScalar;
	let $imageSelectionRectSnapshot;
	let $prefersReducedMotion;
	let $tabRect;

	let $history,
		$$unsubscribe_history = noop$1,
		$$subscribe_history = () => ($$unsubscribe_history(), $$unsubscribe_history = subscribe(history, $$value => $$invalidate(180, $history = $$value)), history);

	let $imageProcessingPreparing;
	let $rootBackgroundColor;
	let $pointerAccuracy;
	let $pointerHoverable;
	let $imageTransforms;
	let $imagePreviewModifiers;
	let $imageEffects;
	let $imageBackgroundColor;
	let $imageSelectionRectPresentation;
	let $imageVisualLoadComplete;
	let $imageFile;
	let $imagePreview;
	let $imageProcessState;
	let $statusOpacity;
	let $statusOffsetX;
	let $progressOffsetX;
	let $pressedKeysStore;
	let $isInteractingFraction;
	let $env;
	let $toolRect;
	let $utilRect;
	let $pixelRatio;
	let $imagePreviews;
	let $imageDecoration;
	let $imageAnnotation;
	let $imageOverlay;
	component_subscribe($$self, prefersReducedMotion, $$value => $$invalidate(179, $prefersReducedMotion = $$value));
	$$self.$$.on_destroy.push(() => $$unsubscribe_history());
	const dispatch = createEventDispatcher();
	

	//
	// View Props
	//
	let { class: klass = undefined } = $$props;

	let { layout: layoutMode = undefined } = $$props;
	let { stores } = $$props;
	let { locale = undefined } = $$props;
	let { id = undefined } = $$props;
	let { util = undefined } = $$props;
	let { utils = undefined } = $$props; // which utils are active (list of ids), or is derived from plugins automatically
	let { animations = "auto" } = $$props;
	let { previewUpscale = false } = $$props;
	let { elasticityMultiplier = 10 } = $$props;
	let { willRenderCanvas = passthrough } = $$props;
	let { willRevert = () => Promise.resolve(true) } = $$props;
	let { enableButtonExport = true } = $$props;
	let { enableButtonRevert = true } = $$props;
	let { enableNavigateHistory = true } = $$props;
	let { enableToolbar = true } = $$props;
	let { enableUtils = true } = $$props;
	let { previewImageDataMaxSize = undefined } = $$props;
	let { imageOrienter = { read: () => 1, apply: v => v } } = $$props;
	let { pluginComponents = undefined } = $$props;
	let { pluginOptions = {} } = $$props;
	const pluginInterface = {};
	let { root } = $$props;
	let registeredPluginsComponents = [];

	// this method is used to read image resources (preview image / shape images)
	const maxTextureSize = sizeCreate(getWebGLTextureSizeLimit(), getWebGLTextureSizeLimit());

	let { imageSourceToImageData = src => isString(src)
	? fetch(src).then(res => res.blob()).then(blob => blobToImageBitmap(blob, imageOrienter)).then(imageData => imageDataContain(imageData, maxImageDataSize))
	: blobToImageBitmap(src, imageOrienter).then(imageData => imageDataContain(imageData, maxImageDataSize)) } = $$props;

	const imageProxy = createImageProxy();
	const { file: imageFile, size: imageSize, loadState: imageLoadState, processState: imageProcessState, cropAspectRatio: imageCropAspectRatio, cropLimitToImage: imageCropLimitToImage, crop: imageCropRect, cropMinSize: imageCropMinSize, cropMaxSize: imageCropMaxSize, cropRange: imageCropRange, cropOrigin: imageCropRectOrigin, cropRectAspectRatio: imageCropRectAspectRatio, rotation: imageRotation, rotationRange: imageRotationRange, targetSize: imageOutputSize, flipX: imageFlipX, flipY: imageFlipY, backgroundColor: imageBackgroundColor, colorMatrix: imageColorMatrix, convolutionMatrix: imageConvolutionMatrix, gamma: imageGamma, vignette: imageVignette, noise: imageNoise, decoration: imageDecoration, annotation: imageAnnotation, state: imageState } = imageProxy.stores;
	component_subscribe($$self, imageFile, value => $$invalidate(199, $imageFile = value));
	component_subscribe($$self, imageSize, value => $$invalidate(19, $imageSize = value));
	component_subscribe($$self, imageLoadState, value => $$invalidate(165, $imageLoadState = value));
	component_subscribe($$self, imageProcessState, value => $$invalidate(200, $imageProcessState = value));
	component_subscribe($$self, imageCropRect, value => $$invalidate(169, $imageCropRect = value));
	component_subscribe($$self, imageBackgroundColor, value => $$invalidate(197, $imageBackgroundColor = value));
	component_subscribe($$self, imageDecoration, value => $$invalidate(64, $imageDecoration = value));
	component_subscribe($$self, imageAnnotation, value => $$invalidate(65, $imageAnnotation = value));
	component_subscribe($$self, imageState, value => $$invalidate(164, $imageState = value));
	const { images, imageReader } = stores;
	component_subscribe($$self, images, value => $$invalidate(162, $images = value));

	//
	// handles the view rect size, makes sure it is offset from the top
	//
	// root element reference used to read styles
	const rootBackgroundColor = writable([0, 0, 0]);

	component_subscribe($$self, rootBackgroundColor, value => $$invalidate(36, $rootBackgroundColor = value));

	// client rect is the doka editor rect excluding scroll offset
	const clientRect = writable();

	component_subscribe($$self, clientRect, value => $$invalidate(17, $clientRect = value));

	// root rect is the doka editor rect including scroll offset
	const rootRect = writable();

	component_subscribe($$self, rootRect, value => $$invalidate(163, $rootRect = value));

	// when in overlay mode force aspect ratio to aspect ratio of editor root
	const syncRootAspectRatio = () => {
		// set aspect ratio
		imageCropAspectRatio.set(rectAspectRatio($rootRect));

		// need to set history 0 point to this state
		history.set($imageState);
	};

	const tabRect = writable(rectCreateEmpty());
	component_subscribe($$self, tabRect, value => $$invalidate(21, $tabRect = value));
	const toolRect = writable(rectCreateEmpty());
	component_subscribe($$self, toolRect, value => $$invalidate(60, $toolRect = value));
	const utilRect = writable(); // is undefined because we wait till util is set before defining stage rect
	component_subscribe($$self, utilRect, value => $$invalidate(61, $utilRect = value));

	const stageRect = derived([utilRect, tabRect, toolRect], ([$utilRect, $tabRect, $toolRect], set) => {
		if (!$utilRect) return set(undefined);
		const utiloffsetX = $tabRect.x;

		const utilOffsetY = $tabRect.y + (shouldRenderTabs && showUtils || isOverlayModeEnabled
		? 0
		: $toolRect.height);

		set(rectCreate(utiloffsetX + $utilRect.x, utilOffsetY + $utilRect.y, $utilRect.width, $utilRect.height));
	});

	component_subscribe($$self, stageRect, value => $$invalidate(173, $stageRect = value));

	//
	// environment
	//
	const pointerAccuracy = mediaQueryStore("(pointer: fine)", matches => matches ? "pointer-fine" : "pointer-coarse");

	component_subscribe($$self, pointerAccuracy, value => $$invalidate(192, $pointerAccuracy = value));
	const pointerHoverable = mediaQueryStore("(hover: hover)", matches => matches ? "pointer-hover" : "pointer-no-hover");
	component_subscribe($$self, pointerHoverable, value => $$invalidate(193, $pointerHoverable = value));

	//
	// app API
	//
	const isInteracting = writable(false);

	component_subscribe($$self, isInteracting, value => $$invalidate(170, $isInteracting = value));

	const isInteractingFraction = readable(undefined, set => {
		const animator = spring(0);

		const updater = value => {
			animator.set(value ? 1 : 0);
		};

		const subs = [isInteracting.subscribe(updater), animator.subscribe(set)];

		// destroy subs
		return () => subs.forEach(unsub => unsub());
	});

	component_subscribe($$self, isInteractingFraction, value => $$invalidate(203, $isInteractingFraction = value));
	const previewShouldUpscale = writable(previewUpscale);
	component_subscribe($$self, previewShouldUpscale, value => $$invalidate(168, $previewShouldUpscale = value));

	//
	// image related
	//
	const stageScalar = derived([stageRect, imageCropRect], ([$stageRect, $imageCropRect], set) => {
		const isManipulatingImageCropRect = !!($imageCropRectSnapshot || $imageCropRectIntent);

		// update stage scalar, the crop output size relative to the stage, if image fits it's 1
		if (!$stageRect || !$imageCropRect || isManipulatingImageCropRect) return;

		// calculate scale factor needed to fit crop rect to stage
		const scalar = Math.min($stageRect.width / $imageCropRect.width, $stageRect.height / $imageCropRect.height);

		// always scaled down to fit stage, if is allowed to upscale, zoom to fit stage
		const scale = $previewShouldUpscale ? scalar : Math.min(1, scalar);

		set(scale);
	});

	component_subscribe($$self, stageScalar, value => $$invalidate(174, $stageScalar = value));
	const imageCropRectSnapshot = writable();
	component_subscribe($$self, imageCropRectSnapshot, value => $$invalidate(166, $imageCropRectSnapshot = value));
	const imageCropRectIntent = writable(); // should always be set before setting `imageCropRect`
	component_subscribe($$self, imageCropRectIntent, value => $$invalidate(167, $imageCropRectIntent = value));

	const imageCropRectPresentation = readable(undefined, set => {
		const animator = spring(undefined, { precision: 0.0001 });

		const update = () => {
			if (!$imageCropRect) return;
			const instantUpdate = $imageCropRectIntent === undefined || $isInteracting;
			const elasticRect = elastifyRects($imageCropRect, $imageCropRectIntent, imageCropRectElasticity * elasticityMultiplier);
			animator.set(elasticRect, { hard: instantUpdate });
		};

		const subs = [
			// need to update presentation rect when crop rect is updated
			imageCropRect.subscribe(update),
			// update parent store
			animator.subscribe(set)
		];

		return () => subs.forEach(unsub => unsub());
	});

	const imageSelectionRect = writable();
	component_subscribe($$self, imageSelectionRect, value => $$invalidate(171, $imageSelectionRect = value));
	const imageSelectionRectSnapshot = writable();
	component_subscribe($$self, imageSelectionRectSnapshot, value => $$invalidate(176, $imageSelectionRectSnapshot = value));
	const imageSelectionRectIntent = writable(undefined); // should always be set before setting `imageSelectionRect`
	component_subscribe($$self, imageSelectionRectIntent, value => $$invalidate(172, $imageSelectionRectIntent = value));

	//
	// Image selection
	//
	const imageSelectionRectPresentation = readable(undefined, set => {
		const animator = spring(undefined, { precision: 0.0001 });

		const updater = () => {
			if (!$imageSelectionRect) return;
			const instantUpdate = $isInteracting;
			const elasticRect = elastifyRects($imageSelectionRect, $imageSelectionRectIntent, imageSelectionRectElasticity * elasticityMultiplier);

			// prevent negative size (can happen because of elastics)
			if (elasticRect.width < 0) {
				elasticRect.width = 0;
				elasticRect.x = $imageSelectionRect.x;
			}

			if (elasticRect.height < 0) {
				elasticRect.height = 0;
				elasticRect.y = $imageSelectionRect.y;
			}

			// translate elastic rect x,y by stage rect x,y
			rectTranslate(elasticRect, $stageRect);

			animator.set(elasticRect, { hard: instantUpdate });
		};

		const subs = [
			// need to update selection rect when stage is resized
			stageRect.subscribe(updater),
			// listen for selection rect changes (as is assigned rect will always trigger, even if assigned same rect, this is needed to also update when intent changes)
			imageSelectionRect.subscribe(updater),
			// update parent store
			animator.subscribe(set)
		];

		// destroy subs
		return () => subs.forEach(unsub => unsub());
	});

	component_subscribe($$self, imageSelectionRectPresentation, value => $$invalidate(38, $imageSelectionRectPresentation = value));

	// when scaling the stage we need to recenter the image selection
	stageRect.subscribe(() => {
		if (!$stageRect || !$imageCropRect) return;
		const imageCropRectFitsStage = $imageCropRect.width <= $stageRect.width && $imageCropRect.height <= $stageRect.height;

		const centeredImageSelectionRect = // if we have a crop rect and it fits the stage, center it
		imageCropRectFitsStage
		? // center the crop rectangle to the stage
			rectCenterRect($stageRect, rectMultiply(rectClone($imageCropRect), $stageScalar || 1))
		: // render a rectangle based on the fixed crop aspect ratio, or the aspect ratio of the current crop rectangle, or as a last resort the input image
			rectContainRect($stageRect, rectAspectRatio($imageCropRect || $imageSize));

		imageSelectionRect.set(centeredImageSelectionRect);
	});

	// if we've taken a snapshot of the selection, also take a snapshot of the crop rect and the presentationScalar
	// - cropRect so we can adjust it based on the adjustments to the imageSelectionRect
	// - presentationScalar so we can use the snapshot for the calculation of the cropRect scale relative to the original imageSelectionRect (if we use the live $presentationScalar small rounding errors of the scalar result in weird glitches)
	let presentationScalarSnapshot;

	imageSelectionRectSnapshot.subscribe(rect => {
		if (!rect) {
			presentationScalarSnapshot = undefined;
			set_store_value(imageCropRectSnapshot, $imageCropRectSnapshot = undefined, $imageCropRectSnapshot);
			return;
		}

		presentationScalarSnapshot = $presentationScalar;
		const cropRectClone = rectClone($imageCropRect);
		imageCropRectSnapshot.set(cropRectClone);
	});

	// if image selection rect is being manipulated, and imageSelectionRectSnapshot has been defined, we also need to update the crop rect
	imageSelectionRect.subscribe(rect => {
		// only sync crop rect with image selection rect when no longer manipulating
		if (!rect || !$imageSelectionRectSnapshot) return;

		const offset = rectSubtract(rectClone(rect), $imageSelectionRectSnapshot);
		rectDivide(offset, presentationScalarSnapshot);
		const crop = rectAdd(rectClone($imageCropRectSnapshot), offset);
		imageCropRect.set(crop);
	});

	imageCropRect.subscribe(rect => {
		// don't update image selection rect while we're intertacting with the view, this prevents feedback loop from cropRect model updates
		if ($isInteracting || $imageSelectionRectSnapshot || $imageCropRectIntent) return;

		// if no rect, we can't do anything
		// if no selection rect yet, we wait for a selection rect to be loaded
		if (!rect || !$imageSelectionRect) return;

		// only if crop aspect ratio differs from image selection aspect ratio we update image selection rectangle
		const imageSelectionRectAspectRatio = rectAspectRatio($imageSelectionRect);

		const cropRectAspectRatio = rectAspectRatio(rect);
		if (fixPrecision(imageSelectionRectAspectRatio) === fixPrecision(cropRectAspectRatio)) return;

		// TEMP DUPLICATE OF STAGESCALAR DERIVED STORE TO FIX ZOOM OUT AFTER WHEEL ISSUE
		// reproduce -> crop view => resize selection rect vertically, tap recenter, zoom out with scroll wheel
		const scalar = Math.min($stageRect.width / $imageCropRect.width, $stageRect.height / $imageCropRect.height);

		// const scale = $previewShouldUpscale ? scalar : Math.min(1, scalar);
		const size = sizeCreate(rect.width * scalar, rect.height * scalar);

		const tx = ($imageSelectionRect.width - size.width) * 0.5;
		const ty = ($imageSelectionRect.height - size.height) * 0.5;
		const selectionRect = rectCreate($imageSelectionRect.x + tx, $imageSelectionRect.y + ty, size.width, size.height);
		imageSelectionRect.set(selectionRect);
	});

	const imageScalar = derived([stageScalar, imageCropRect, imageSelectionRect], ([$stageScalar, $imageCropRect, $imageSelectionRect], set) => {
		// update the image scalar, the image zoom calculated by the image selection and the actual crop coordinates
		if (!$stageScalar || !$imageCropRect || !$imageSelectionRect) return;

		const selectionScaledWidth = $imageSelectionRect.width / $imageCropRect.width;
		const selectionScaledHeight = $imageSelectionRect.height / $imageCropRect.height;

		// need to correct for scale of stage
		const scalar = Math.max(selectionScaledWidth, selectionScaledHeight) / $stageScalar;

		set(scalar);
	});

	const presentationScalar = derived([stageScalar, imageScalar], ([$stageScalar, $imageScalar], set) => {
		// can't scale presentation if no image scalar defined
		if (!$imageScalar) return;

		const scalar = $stageScalar * $imageScalar;
		set(scalar);
	});

	component_subscribe($$self, presentationScalar, value => $$invalidate(175, $presentationScalar = value));

	//
	// UI Elements
	//
	// guides and edges rendered on top of image
	const imageSelectionGuides = derived(
		[
			rootBackgroundColor,
			isInteracting,
			imageSelectionRectPresentation,
			isInteractingFraction
		],
		([$rootBackgroundColor, $isInteracting, $rect, $opacity], set) => {
			if (!$rect) return set([]);
			const { x, y, width, height } = $rect;
			const showGrid = $opacity > 0;
			const rows = showGrid ? 3 : 1;
			const cols = showGrid ? 3 : 1;
			const w = width / cols;
			const h = height / rows;
			const guides = [];

			const strokeColor = isDarkColor($rootBackgroundColor)
			? [1, 1, 1]
			: [0, 0, 0];

			// rows
			for (let r = 0; r <= rows; r++) {
				let isEdge = r === 0 || r === rows;
				if (isOverlayModeEnabled && isEdge) continue;
				let o = isEdge ? 1 : $opacity * 0.25;
				let yo = y + h * r;

				guides.push({
					id: `image-selection-guide-row-${r}`,
					points: [vectorCreate(x, yo), vectorCreate(x + width, yo)],
					opacity: o,
					strokeWidth: 1,
					strokeColor
				});
			}

			// cols
			for (let c = 0; c <= cols; c++) {
				let isEdge = c === 0 || c === cols;
				if (isOverlayModeEnabled && isEdge) continue;
				let o = isEdge ? 1 : $opacity * 0.25;
				let xo = x + w * c;

				guides.push({
					id: `image-selection-guide-col-${c}`,
					points: [vectorCreate(xo, y), vectorCreate(xo, y + height)],
					opacity: o,
					strokeWidth: 1,
					strokeColor
				});
			}

			set(guides);
		}
	);

	// custom markup rendered on top of image
	const imageOverlayMarkup = writable([]);

	// the resulting overlay markup
	const imageOverlay = derived([imageSelectionGuides, imageOverlayMarkup], ([$imageSelectionGuides, $imageOverlayMarkup], set) => {
		set($imageSelectionGuides.concat($imageOverlayMarkup));
	});

	component_subscribe($$self, imageOverlay, value => $$invalidate(66, $imageOverlay = value));

	//
	// Loading the preview
	//
	const imageVisualLoadComplete = writable(false);

	component_subscribe($$self, imageVisualLoadComplete, value => $$invalidate(198, $imageVisualLoadComplete = value));
	let imagePreviewLoaderCancelToken;

	const createImagePreviewLoader = (file, token) => new Promise((resolve, reject) => {
			let cancelled = false;
			let timer;
			token.cancel = () => cancelled = true;
			const now = Date.now();

			imageSourceToImageData(file).then(imageData => {
				const dist = Date.now() - now;
				clearTimeout(timer);

				timer = setTimeout(
					() => {
						if (cancelled) return;
						resolve(imageData);
					},
					Math.max(0, 1000 - dist)
				);
			}).catch(reject);
		});

	const imagePreview = derived([imageVisualLoadComplete, imageFile], ([$imageVisualLoadComplete, $imageFile], set) => {
		// cancel existing loader
		if (imagePreviewLoaderCancelToken) imagePreviewLoaderCancelToken.cancel();

		// no data, reset preview data
		if (!$imageVisualLoadComplete || !$imageFile) return set(undefined);

		imagePreviewLoaderCancelToken = { cancel: noop };
		createImagePreviewLoader($imageFile, imagePreviewLoaderCancelToken).then(set).catch(err => set_store_value(imageLoadState, $imageLoadState.error = err, $imageLoadState));
	});

	component_subscribe($$self, imagePreview, value => $$invalidate(44, $imagePreview = value));

	//
	// Calculates the image transforms and effects necessary for the preview
	//
	const imagePreviewModifiers = writable({});

	component_subscribe($$self, imagePreviewModifiers, value => $$invalidate(195, $imagePreviewModifiers = value));
	const imagePreviews = writable([]);
	component_subscribe($$self, imagePreviews, value => $$invalidate(63, $imagePreviews = value));

	const imageTransforms = derived(
		[
			stageRect,
			rootRect,
			imageSize,
			imageCropRectPresentation,
			imageSelectionRect,
			presentationScalar,
			imageRotation,
			imageFlipX,
			imageFlipY
		],
		([
				$stageRect,
				$rootRect,
				$imageSize,
				$imageCropRectPresentation,
				$imageSelectionRect,
				$presentationScalar,
				$imageRotation,
				$imageFlipX,
				$imageFlipY
			], set) => {
			set(calculateImageTransforms($stageRect, $rootRect, $imageSize, $imageCropRectPresentation, $imageSelectionRect, $presentationScalar, 0, 0, $imageRotation, $imageFlipX, $imageFlipY));
		}
	);

	component_subscribe($$self, imageTransforms, value => $$invalidate(194, $imageTransforms = value));

	const imageEffects = derived(
		[
			imageColorMatrix,
			imageConvolutionMatrix,
			imageGamma,
			imageVignette,
			imageNoise
		],
		([$colorMatrix, $convolutionMatrix, $gamma, $vignette, $noise], set) => {
			const colorMatrices = $colorMatrix && Object.keys($colorMatrix).map(name => $colorMatrix[name]).filter(Boolean);

			const effects = {
				gamma: $gamma || undefined,
				vignette: $vignette || undefined,
				noise: $noise || undefined,
				convolutionMatrix: $convolutionMatrix || undefined,
				colorMatrix: colorMatrices && colorMatrices.length && getColorMatrixFromColorMatrices(colorMatrices)
			};

			set(effects);
		}
	);

	component_subscribe($$self, imageEffects, value => $$invalidate(196, $imageEffects = value));

	//
	// current environment variables
	//
	let windowWidth;

	let windowHeight;
	const shouldPreventSwipe = canPreventNavSwipe();
	const env = writable();
	component_subscribe($$self, env, value => $$invalidate(59, $env = value));
	const initialPixelRatio = getDevicePixelRatio();

	const pixelRatio = readable(initialPixelRatio, set => {
		const handleResolutionChange = () => set(getDevicePixelRatio());
		const resolutionObserver = matchMedia(`(resolution: ${initialPixelRatio}dppx)`);
		resolutionObserver.addListener(handleResolutionChange);
		return () => resolutionObserver.removeListener(handleResolutionChange);
	});

	component_subscribe($$self, pixelRatio, value => $$invalidate(62, $pixelRatio = value));

	const history = historyCreate(() => $imageState, state => {
		// set new state from history, $imageState will store a clone of the received state
		set_store_value(imageState, $imageState = state, $imageState); // image state will return a clone of the current state

		// trigger recenter of image selection rect
		tabRect.set($tabRect);
	});

	$$subscribe_history();

	imageLoadState.subscribe(({ complete } = {}) => {
		if (!complete) return;
		history.set($imageState);
	});

	const revert = () => willRevert().then(shouldReset => shouldReset && history.revert());

	//
	// Visual processing of the image
	//
	const imageProcessingPreparing = writable(false);

	component_subscribe($$self, imageProcessingPreparing, value => $$invalidate(181, $imageProcessingPreparing = value));

	const handleExport = () => {
		// this will trigger status overlay fade in
		set_store_value(imageProcessingPreparing, $imageProcessingPreparing = true, $imageProcessingPreparing);

		// wait for status to be done fading in, then requests image writing
		const unsub = statusOpacity.subscribe(value => {
			if (value !== 1) return;

			// stop listening for
			unsub();

			// request write image
			dispatch("processImage");
		});
	};

	imageProcessState.subscribe(state => {
		if (!state) return;
		set_store_value(imageProcessingPreparing, $imageProcessingPreparing = true, $imageProcessingPreparing);
		const { task, error, complete } = state;
		if (complete) set_store_value(imageProcessingPreparing, $imageProcessingPreparing = false, $imageProcessingPreparing);
	});

	//
	// Configure the available views
	//
	const utilStores = {
		// model stores
		...stores,
		// image
		imageFile,
		imageSize,
		imageCropAspectRatio,
		imageCropMinSize,
		imageCropMaxSize,
		imageCropLimitToImage,
		imageCropRect,
		imageCropRectOrigin,
		imageCropRectSnapshot,
		imageCropRectAspectRatio,
		imageCropRange,
		imageRotation,
		imageRotationRange,
		imageFlipX,
		imageFlipY,
		imageOutputSize,
		// effects
		imageColorMatrix,
		imageConvolutionMatrix,
		imageGamma,
		imageVignette,
		imageNoise,
		// markup
		imageDecoration,
		imageAnnotation,
		// image preview for utils that need access to pixel data
		imagePreview,
		// top left position of image preview
		imageTransforms,
		// allows utils to control how the preview is presented (for example more opacity for mask)
		imagePreviewModifiers,
		// history state and update
		history,
		// static env info
		pixelRatio,
		elasticityMultiplier,
		scrollElasticity,
		rangeInputElasticity,
		// dynamic env info
		pointerAccuracy,
		pointerHoverable,
		env,
		rootRect,
		stageRect,
		stageScalar,
		utilRect,
		presentationScalar,
		rootBackgroundColor,
		// (write) add guides to ui (for example is used by markup util to add lines for shape manipulator)
		imageOverlayMarkup,
		// (write) previews to render
		imagePreviews,
		// (write) set to true to disable animations
		isInteracting,
		// (write) the current intended crop rect
		imageCropRectIntent,
		// (read) the current presented crop rect
		imageCropRectPresentation,
		// (write) the current limited size of the image selection rect
		imageSelectionRect,
		// (write) the inteded rectangle by the user
		imageSelectionRectIntent,
		// (read) the current presentation of the image selection rect, includes elasticity etc.
		imageSelectionRectPresentation,
		// (read) a snapshot of the image selection rectangle, use to store the rectangle before modification so alterations to the rectangle can be applied to the snapshot
		imageSelectionRectSnapshot,
		// scalar of image in view
		imageScalar
	};

	// don't expose image store
	delete utilStores.image;

	const utilsUniqueId = `util-${getUniqueId()}`;
	let utilsVisible = [];

	// env
	let iOS = isIOS();

	//
	// canvas background color
	//
	const syncBackgroundColor = () => {
		const colorString = rootElementComputedStyle.getPropertyValue("background-color");
		const colorArray = colorStringToColorArray(colorString);

		// hide transparent color
		if (colorArray[3] === 0) return;

		// limit to opaque colors
		colorArray.length = 3;

		// update background
		set_store_value(rootBackgroundColor, $rootBackgroundColor = colorArray, $rootBackgroundColor);
	};

	const handleTransitionEnd = ({ target, propertyName }) => {
		if (target !== root || propertyName !== "background-color") return;
		syncBackgroundColor();
	};

	const shapeApplyLineEnd = (position, size, color, style, direction) => {
		const shapes = [];
		const isSolid = (/solid/).test(style);
		const scaledSize = 6 * size;
		const s = isSolid ? scaledSize : scaledSize - 1;
		const sh = s * 0.5;
		const normal = vectorNormalize(direction);
		const offset = vectorMultiply(vectorClone(normal), sh);
		const anchor = vectorClone(position);

		if ((/arrow/).test(style)) {
			const tipOffset = vectorMultiply(vectorClone(normal), s);
			const inset = vectorCreate(position.x + tipOffset.x, position.y + tipOffset.y);
			const tx = tipOffset.x * 0.55;
			const ty = tipOffset.y * 0.55;

			if (isSolid) {
				shapes.push({
					points: [
						vectorClone(position),
						vectorCreate(inset.x - ty, inset.y + tx),
						vectorCreate(inset.x + ty, inset.y - tx)
					],
					backgroundColor: color
				});

				// move back so arrow triangle overlaps with end of line (otherwise line sticks out of triangle tip)
				position.x += offset.x;

				position.y += offset.y;
			} else {
				// line
				shapes.push({
					points: [vectorCreate(inset.x + ty, inset.y - tx), vectorClone(position)],
					strokeWidth: size,
					strokeColor: color
				});

				// line
				shapes.push({
					points: [vectorClone(position), vectorCreate(inset.x - ty, inset.y + tx)],
					strokeWidth: size,
					strokeColor: color
				});

				// tip
				shapes.push({
					rx: size * 0.5,
					ry: size * 0.5,
					x: position.x,
					y: position.y,
					backgroundColor: color
				});
			}
		} else if ((/circle/).test(style)) {
			// adjust line point offset
			position.x += offset.x - normal.x;

			position.y += offset.y - normal.y;

			// add line cap
			shapes.push({
				x: anchor.x,
				y: anchor.y,
				rx: sh,
				ry: sh,
				backgroundColor: isSolid ? color : undefined,
				strokeWidth: isSolid ? undefined : size,
				strokeColor: isSolid ? undefined : color
			});
		} else if ((/square/).test(style)) {
			// adjust line point offset
			position.x += offset.x - normal.x;

			position.y += offset.y - normal.y;

			shapes.push({
				x: anchor.x - sh,
				y: anchor.y - sh,
				width: s,
				height: s,
				rotation: vectorAngle(normal),
				backgroundColor: isSolid ? color : undefined,
				strokeWidth: isSolid ? undefined : size,
				strokeColor: isSolid ? undefined : color
			});
		} else if (style === "bar") {
			shapes.push({
				points: [
					vectorCreate(anchor.x - offset.y, anchor.y + offset.x),
					vectorCreate(anchor.x + offset.y, anchor.y - offset.x)
				],
				strokeWidth: size,
				strokeColor: color
			});
		}

		return shapes;
	};

	// test if some shapes have left/top/bottom/right offsets, if so, convert to crop space
	// updates original shape (which at this point is a flattened shape (clone))
	const positionDecorationShape = (shape, canvasState) => shapeComputeDisplay(shape, {
		x: $imageSelectionRectPresentation.x / canvasState.scale,
		y: $imageSelectionRectPresentation.y / canvasState.scale,
		width: $imageSelectionRectPresentation.width / canvasState.scale,
		height: $imageSelectionRectPresentation.height / canvasState.scale
	});

	// decoration is drawn relative to view space, so we need to translate relative to crop presentation rect
	const transformDecorationShape = (shape, canvasState) => {
		shapeComputeTransform(shape, $imageSelectionRectPresentation, canvasState.scale, canvasState.size);
		return shape;
	};

	const flattenShapes = shapes => {
		const flattenedShapes = [];
		shapes.forEach(shape => flattenedShapes.push(...flattenShape(shape)));
		return flattenedShapes.filter(Boolean);
	};

	const flattenShape = shape => {
		// the shapes to draw, can be multiple shapes
		const drawableShapes = [shape];

		if (shapeIsLine(shape)) {
			const start = vectorCreate(shape.x1, shape.y1);
			const end = vectorCreate(shape.x2, shape.y2);

			if (shape.lineStart) {
				drawableShapes.push(...shapeApplyLineEnd(start, shape.strokeWidth, shape.strokeColor, shape.lineStart, vectorCreate(end.x - start.x, end.y - start.y)));
			}

			if (shape.lineEnd) {
				drawableShapes.push(...shapeApplyLineEnd(end, shape.strokeWidth, shape.strokeColor, shape.lineEnd, vectorCreate(start.x - end.x, start.y - end.y)));
			}

			shape.points = [start, end];
		} else if (shapeIsTriangle(shape)) {
			shape.points = [
				vectorCreate(shape.x1, shape.y1),
				vectorCreate(shape.x2, shape.y2),
				vectorCreate(shape.x3, shape.y3)
			];
		}

		// is empty text
		if (shapeIsTextEmpty(shape)) {
			// make sure shape is still visible
			if (shapeIsTextLine(shape)) {
				shape.width = 5;

				shape.height = isFunction(shape.lineHeight)
				? shape.lineHeight(shape.fontSize)
				: shape.lineHeight;
			}

			// set to empty text style
			shape.strokeWidth = 1;

			shape.strokeColor = [1, 1, 1, 0.5];
			shape.backgroundColor = [0, 0, 0, 0.1];
		}

		return drawableShapes;
	};

	const statusOpacity = tweened(undefined, { duration: 500 });
	component_subscribe($$self, statusOpacity, value => $$invalidate(52, $statusOpacity = value));
	let loadTimer;

	const statusOffsetX = spring(undefined, {
		stiffness: 0.02,
		damping: 0.5,
		precision: 0.25
	});

	component_subscribe($$self, statusOffsetX, value => $$invalidate(54, $statusOffsetX = value));

	const progressOffsetX = spring(undefined, {
		stiffness: 0.03,
		damping: 0.4,
		precision: 0.25
	});

	component_subscribe($$self, progressOffsetX, value => $$invalidate(55, $progressOffsetX = value));

	const hideStatus = () => {
		statusOffsetX.set(undefined, { hard: true });
		progressOffsetX.set(undefined, { hard: true });
	};

	const offsetProgress = e => {
		set_store_value(statusOffsetX, $statusOffsetX = Math.round(-e.detail.width * 0.5) - 16, $statusOffsetX); // -16 offsets text so it's better centered to the viewport
		set_store_value(progressOffsetX, $progressOffsetX = e.detail.width, $progressOffsetX);
	};

	const handleCloseImageLoadError = () => {
		dispatch("abortLoadImage");
	};

	const handleCloseImageProcessError = () => {
		dispatch("abortProcessImage");
		set_store_value(imageProcessingPreparing, $imageProcessingPreparing = false, $imageProcessingPreparing);
	};

	let { navButtons = [] } = $$props;

	const mapToButton = button => {
		const btn = { ...button };
		delete btn.slot;
		return btn;
	};

	// context for children to know if a key is down
	const pressedKeysStore = writable([]);

	component_subscribe($$self, pressedKeysStore, value => $$invalidate(202, $pressedKeysStore = value));
	setContext("keysPressed", pressedKeysStore);

	const handleKeydown = e => {
		const { keyCode } = e;
		const keySet = new Set([...$pressedKeysStore, keyCode]);
		pressedKeysStore.set(Array.from(keySet));
	};

	const handleKeyup = ({ keyCode }) => {
		pressedKeysStore.set($pressedKeysStore.filter(pressedKey => pressedKey !== keyCode));
	};

	// so no keys get stuck when user cmd-tabs out of the window, or when dev-tools open by key-press
	const handleWindowBlur = () => {
		pressedKeysStore.set([]);
	};

	// block context menu on everything but text input fields
	const handleContextMenu = e => {
		if ((/textarea/i).test(e.target.nodeName) || (/text/).test(e.target.type)) return;
		e.preventDefault();
	};

	// canvas drawing
	const getStageState = () => ({
		// add information on which util is active
		utilVisibility: { ...utilsVisibleFraction },
		// used to fade in elements when interacting
		isInteracting: $isInteracting,
		isInteractingFraction: $isInteractingFraction,
		// add the root rectangle so will render can use width/height of root element
		rootRect: rectClone($rootRect),
		// add the stage rectangle so will render knows where stage starts and ends
		stageRect: rectClone($stageRect),
		// preview selection rect
		selectionRect: rectClone($imageSelectionRectPresentation)
	});

	const createCanvasState = (canvasState, annotationShapes, decorationShapes, interfaceShapes) => ({
		annotationShapes: flattenShapes(annotationShapes.filter(shapeIsVisible).map(shapeDeepCopy).map(shape => shapeComputeDisplay(shape, $imageSize))),
		decorationShapes: flattenShapes(decorationShapes.filter(shapeIsVisible).map(shapeDeepCopy).map(shape => positionDecorationShape(shape, canvasState)).map(shape => transformDecorationShape(shape, canvasState))),
		interfaceShapes: flattenShapes(interfaceShapes.filter(shapeIsVisible))
	});

	function onwindowresize() {
		$$invalidate(13, windowWidth = window.innerWidth);
		$$invalidate(14, windowHeight = window.innerHeight);
	}

	const measure_handler = e => set_store_value(toolRect, $toolRect = e.detail, $toolRect);
	const select_handler = ({ detail }) => $$invalidate(25, utilSelected = detail);
	const func = (panel, util) => util.id === panel;

	function panel_component_binding(value, panel) {
		pluginInterface[panel] = value;
		(($$invalidate(0, pluginInterface), $$invalidate(10, pluginOptions)), $$invalidate(135, pluginComponents));
	}

	const measure_handler_1 = e => set_store_value(utilRect, $utilRect = e.detail, $utilRect);
	const show_handler = panel => $$invalidate(15, utilsVisible = utilsVisible.concat(panel));
	const hide_handler = panel => $$invalidate(15, utilsVisible = utilsVisible.filter(util => util !== panel));
	const fade_handler = (panel, { detail }) => $$invalidate(27, utilsVisibleFraction[panel] = detail, utilsVisibleFraction);
	const measure_handler_2 = e => set_store_value(tabRect, $tabRect = e.detail, $tabRect);
	const func_1 = util => util.id === utilSelected;

	function panel_component_binding_1(value) {
		pluginInterface[utilSelected] = value;
		(($$invalidate(0, pluginInterface), $$invalidate(10, pluginOptions)), $$invalidate(135, pluginComponents));
	}

	const measure_handler_3 = e => set_store_value(utilRect, $utilRect = e.detail, $utilRect);
	const show_handler_1 = () => $$invalidate(15, utilsVisible = utilsVisible.concat(utilSelected));
	const hide_handler_1 = () => $$invalidate(15, utilsVisible = utilsVisible.filter(util => util !== utilSelected));
	const fade_handler_1 = ({ detail }) => $$invalidate(27, utilsVisibleFraction[utilSelected] = detail, utilsVisibleFraction);

	const func_2 = canvasState => {
		// current draw state
		const drawState = { ...canvasState, ...getStageState() };

		// allow dev to add custom overlay to `imageOverlay`
		const { annotationShapes, decorationShapes, interfaceShapes } = willRenderCanvas(
			{
				decorationShapes: $imageDecoration,
				annotationShapes: $imageAnnotation,
				interfaceShapes: $imageOverlay
			},
			drawState
		);

		// need to map shapes
		return createCanvasState(drawState, annotationShapes, decorationShapes, interfaceShapes);
	};

	function div_binding($$value) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			root = $$value;
			$$invalidate(1, root);
		});
	}

	const measure_handler_4 = e => set_store_value(clientRect, $clientRect = e.detail, $clientRect);

	$$self.$$set = $$props => {
		if ("class" in $$props) $$invalidate(124, klass = $$props.class);
		if ("layout" in $$props) $$invalidate(125, layoutMode = $$props.layout);
		if ("stores" in $$props) $$invalidate(126, stores = $$props.stores);
		if ("locale" in $$props) $$invalidate(2, locale = $$props.locale);
		if ("id" in $$props) $$invalidate(3, id = $$props.id);
		if ("util" in $$props) $$invalidate(127, util = $$props.util);
		if ("utils" in $$props) $$invalidate(128, utils = $$props.utils);
		if ("animations" in $$props) $$invalidate(129, animations = $$props.animations);
		if ("previewUpscale" in $$props) $$invalidate(130, previewUpscale = $$props.previewUpscale);
		if ("elasticityMultiplier" in $$props) $$invalidate(4, elasticityMultiplier = $$props.elasticityMultiplier);
		if ("willRenderCanvas" in $$props) $$invalidate(5, willRenderCanvas = $$props.willRenderCanvas);
		if ("willRevert" in $$props) $$invalidate(131, willRevert = $$props.willRevert);
		if ("enableButtonExport" in $$props) $$invalidate(6, enableButtonExport = $$props.enableButtonExport);
		if ("enableButtonRevert" in $$props) $$invalidate(7, enableButtonRevert = $$props.enableButtonRevert);
		if ("enableNavigateHistory" in $$props) $$invalidate(8, enableNavigateHistory = $$props.enableNavigateHistory);
		if ("enableToolbar" in $$props) $$invalidate(9, enableToolbar = $$props.enableToolbar);
		if ("enableUtils" in $$props) $$invalidate(132, enableUtils = $$props.enableUtils);
		if ("previewImageDataMaxSize" in $$props) $$invalidate(133, previewImageDataMaxSize = $$props.previewImageDataMaxSize);
		if ("imageOrienter" in $$props) $$invalidate(134, imageOrienter = $$props.imageOrienter);
		if ("pluginComponents" in $$props) $$invalidate(135, pluginComponents = $$props.pluginComponents);
		if ("pluginOptions" in $$props) $$invalidate(10, pluginOptions = $$props.pluginOptions);
		if ("root" in $$props) $$invalidate(1, root = $$props.root);
		if ("imageSourceToImageData" in $$props) $$invalidate(11, imageSourceToImageData = $$props.imageSourceToImageData);
		if ("navButtons" in $$props) $$invalidate(136, navButtons = $$props.navButtons);
	};

	let isOverlayModeEnabled;
	let showUtils;
	let maxImageDataSize;
	let canAnimate;
	let acceptsAnimations;
	let shouldAnimate;
	let canUndo;
	let canRedo;
	let utilsFiltered;
	let utilsAvailable;
	let utilsDefined;
	let utilsMerged;
	let utilSelected;
	let utilTools;
	let utilsVisibleFraction;
	let tabsConfig;
	let tabs;
	let panels;
	let shouldRenderTabs;
	let className;
	let orientation;
	let isLandscape;
	let isPortrait;
	let horizontalSpace;
	let limitedSpace;
	let verticalSpace;
	let isNarrowViewport;
	let genericSpace;
	let swipeNavigation;
	let shouldRenderUtilTools;
	let rootElementComputedStyle;
	let imageProps;
	let isImageLoadError;
	let isWaitingForImage;
	let imageLoadProgress;
	let isLoadingImageData;
	let isCreatingImagePreview;
	let isProcessingImage;
	let imageLoadShowProgressIndicator;
	let imageLoadStatusLabel;
	let imageProcessStatusLabel;
	let imageProcessProgress;
	let imageProcessShowProgressIndicator;
	let isImageProcessingError;
	let isStatusVisible;
	let handleTouchStart;
	let navButtonsAlpha;
	let hasClientRect;
	let canRender;

	$$self.$$.update = () => {
		if ($$self.$$.dirty[4] & /*layoutMode*/ 2) {
			 $$invalidate(160, isOverlayModeEnabled = layoutMode === "overlay");
		}

		if ($$self.$$.dirty[4] & /*enableUtils*/ 256 | $$self.$$.dirty[5] & /*isOverlayModeEnabled*/ 32) {
			 $$invalidate(16, showUtils = enableUtils && !isOverlayModeEnabled);
		}

		if ($$self.$$.dirty[0] & /*pluginOptions, pluginInterface*/ 1025) {
			// map plugin options to plugin interface
			 if (pluginOptions) {
				// for every plugin in plugin options
				Object.keys(pluginOptions).forEach(plugin => {
					// for every prop defined for this plugin
					Object.keys(pluginOptions[plugin]).forEach(prop => {
						// set value to interface
						if (!pluginInterface[plugin]) return;

						// set prop value
						$$invalidate(0, pluginInterface[plugin][prop] = pluginOptions[plugin][prop], pluginInterface);
					});
				});
			}
		}

		if ($$self.$$.dirty[0] & /*pluginInterface*/ 1 | $$self.$$.dirty[4] & /*pluginComponents*/ 2048) {
			 {
				let changed = false;

				pluginComponents.forEach(([key]) => {
					if (pluginInterface[key]) return;
					$$invalidate(0, pluginInterface[key] = {}, pluginInterface);
					changed = true;
				});

				if (changed) {
					$$invalidate(156, registeredPluginsComponents = [...pluginComponents]);
				}
			}
		}

		if ($$self.$$.dirty[4] & /*previewImageDataMaxSize*/ 512) {
			 maxImageDataSize = previewImageDataMaxSize
			? sizeMin(previewImageDataMaxSize, maxTextureSize)
			: maxTextureSize;
		}

		if ($$self.$$.dirty[5] & /*$images*/ 128) {
			 imageProxy.update($images[0]);
		}

		if ($$self.$$.dirty[0] & /*$clientRect*/ 131072) {
			 $clientRect && rootRect.set(rectCreate($clientRect.x, $clientRect.y, $clientRect.width, $clientRect.height));
		}

		if ($$self.$$.dirty[5] & /*$rootRect, isOverlayModeEnabled, $imageLoadState*/ 1312) {
			 $rootRect && isOverlayModeEnabled && ($imageLoadState && $imageLoadState.complete) && syncRootAspectRatio();
		}

		if ($$self.$$.dirty[4] & /*previewUpscale*/ 64 | $$self.$$.dirty[5] & /*isOverlayModeEnabled*/ 32) {
			 previewShouldUpscale.set(previewUpscale || isOverlayModeEnabled);
		}

		if ($$self.$$.dirty[5] & /*$isInteracting*/ 32768) {
			//
			// Animations based on prefers-reduced-motion, automatically checks if user prefers reduced animations
			//
			 $$invalidate(177, canAnimate = !$isInteracting);
		}

		if ($$self.$$.dirty[5] & /*$prefersReducedMotion*/ 16777216) {
			 $$invalidate(178, acceptsAnimations = !$prefersReducedMotion);
		}

		if ($$self.$$.dirty[4] & /*animations*/ 32 | $$self.$$.dirty[5] & /*canAnimate, acceptsAnimations*/ 12582912) {
			 $$invalidate(20, shouldAnimate = animations === "always"
			? canAnimate
			: animations === "never"
				? false
				: canAnimate && acceptsAnimations);
		}

		if ($$self.$$.dirty[5] & /*$history*/ 33554432) {
			 $$invalidate(22, canUndo = $history.index > -1);
		}

		if ($$self.$$.dirty[5] & /*$history*/ 33554432) {
			 $$invalidate(23, canRedo = $history.index < $history.length - 1);
		}

		if ($$self.$$.dirty[0] & /*locale*/ 4 | $$self.$$.dirty[4] & /*utils*/ 16 | $$self.$$.dirty[5] & /*registeredPluginsComponents*/ 2) {
			 $$invalidate(182, utilsFiltered = locale && registeredPluginsComponents.length
			? utils || registeredPluginsComponents.map(([id]) => id)
			: []);
		}

		if ($$self.$$.dirty[5] & /*registeredPluginsComponents, utilsFiltered*/ 134217730) {
			 $$invalidate(183, utilsAvailable = registeredPluginsComponents.filter(([id]) => utilsFiltered.includes(id)));
		}

		if ($$self.$$.dirty[5] & /*utilsAvailable*/ 268435456) {
			 $$invalidate(184, utilsDefined = utilsAvailable.length);
		}

		if ($$self.$$.dirty[0] & /*locale*/ 4 | $$self.$$.dirty[5] & /*utilsFiltered, utilsAvailable*/ 402653184) {
			 $$invalidate(24, utilsMerged = utilsFiltered.map(utilId => {
				const util = utilsAvailable.find(([id]) => utilId === id); // [id, view]
				if (!util) return;

				return {
					id: utilId,
					view: util[1],
					tabIcon: locale[`${utilId}Icon`],
					tabLabel: locale[`${utilId}Label`]
				};
			}).filter(Boolean) || []);
		}

		if ($$self.$$.dirty[4] & /*util*/ 8 | $$self.$$.dirty[5] & /*utilsFiltered, utilsDefined*/ 671088640) {
			 $$invalidate(25, utilSelected = util && typeof util === "string" && utilsFiltered.includes(util)
			? util
			: utilsDefined && utilsFiltered[0]);
		}

		if ($$self.$$.dirty[0] & /*utilSelected, pluginInterface*/ 33554433) {
			 $$invalidate(26, utilTools = utilSelected && pluginInterface[utilSelected].tools || []);
		}

		if ($$self.$$.dirty[0] & /*utilsMerged, utilsVisibleFraction*/ 150994944) {
			 $$invalidate(27, utilsVisibleFraction = utilsMerged.reduce(
				(prev, curr) => {
					prev[curr.id] = utilsVisibleFraction && utilsVisibleFraction[curr.id] || 0;
					return prev;
				},
				{}
			));
		}

		if ($$self.$$.dirty[0] & /*utilSelected*/ 33554432) {
			 $$invalidate(28, tabsConfig = {
				name: utilsUniqueId,
				selected: utilSelected
			});
		}

		if ($$self.$$.dirty[0] & /*utilsMerged*/ 16777216) {
			 $$invalidate(29, tabs = utilsMerged.map(util => ({
				id: util.id,
				icon: util.tabIcon,
				label: util.tabLabel
			})));
		}

		if ($$self.$$.dirty[0] & /*utilsMerged*/ 16777216) {
			 $$invalidate(30, panels = utilsMerged.map(util => util.id));
		}

		if ($$self.$$.dirty[5] & /*utilsFiltered*/ 134217728) {
			 $$invalidate(18, shouldRenderTabs = utilsFiltered.length > 1);
		}

		if ($$self.$$.dirty[4] & /*klass*/ 1) {
			 $$invalidate(31, className = arrayJoin(["DokaRoot", klass]));
		}

		if ($$self.$$.dirty[5] & /*$rootRect*/ 256) {
			 $$invalidate(185, orientation = $rootRect && ($rootRect.width > $rootRect.height
			? "landscape"
			: "portrait"));
		}

		if ($$self.$$.dirty[5] & /*orientation*/ 1073741824) {
			 $$invalidate(32, isLandscape = orientation === "landscape");
		}

		if ($$self.$$.dirty[1] & /*isLandscape*/ 2) {
			 $$invalidate(33, isPortrait = !isLandscape);
		}

		if ($$self.$$.dirty[5] & /*$rootRect*/ 256) {
			 $$invalidate(186, horizontalSpace = $rootRect && ($rootRect.width > 1000
			? "wide"
			: $rootRect.width < 600 ? "narrow" : undefined));
		}

		if ($$self.$$.dirty[5] & /*$rootRect*/ 256) {
			 $$invalidate(187, limitedSpace = $rootRect && ($rootRect.width <= 320 || $rootRect.height <= 460) && "cramped");
		}

		if ($$self.$$.dirty[5] & /*$rootRect*/ 256) {
			 $$invalidate(188, verticalSpace = $rootRect && ($rootRect.height > 1000
			? "tall"
			: $rootRect.height < 600 ? "short" : undefined));
		}

		if ($$self.$$.dirty[6] & /*horizontalSpace*/ 1) {
			 $$invalidate(34, isNarrowViewport = horizontalSpace === "narrow");
		}

		if ($$self.$$.dirty[6] & /*horizontalSpace, verticalSpace*/ 5) {
			 $$invalidate(189, genericSpace = horizontalSpace === "narrow" || verticalSpace === "short"
			? "compact"
			: undefined);
		}

		if ($$self.$$.dirty[0] & /*windowWidth*/ 8192 | $$self.$$.dirty[5] & /*$rootRect*/ 256) {
			 $$invalidate(190, swipeNavigation = iOS && ($rootRect && windowWidth === $rootRect.width) && !shouldPreventSwipe
			? "has-swipe-navigation"
			: undefined);
		}

		if ($$self.$$.dirty[0] & /*utilTools*/ 67108864 | $$self.$$.dirty[6] & /*verticalSpace*/ 4) {
			 $$invalidate(35, shouldRenderUtilTools = utilTools.length && verticalSpace === "short");
		}

		if ($$self.$$.dirty[0] & /*root*/ 2) {
			// will update when root element is available
			 $$invalidate(191, rootElementComputedStyle = root && getComputedStyle(root));
		}

		if ($$self.$$.dirty[6] & /*rootElementComputedStyle*/ 32) {
			// sync for first time
			 if (rootElementComputedStyle) syncBackgroundColor();
		}

		if ($$self.$$.dirty[0] & /*enableToolbar, shouldRenderTabs, showUtils*/ 328192 | $$self.$$.dirty[4] & /*layoutMode*/ 2 | $$self.$$.dirty[5] & /*orientation*/ 1073741824 | $$self.$$.dirty[6] & /*horizontalSpace, verticalSpace, genericSpace, limitedSpace, swipeNavigation, $pointerAccuracy, $pointerHoverable*/ 223) {
			 env.set(arrayJoin([
				orientation,
				horizontalSpace,
				verticalSpace,
				genericSpace,
				limitedSpace,
				swipeNavigation,
				$pointerAccuracy,
				$pointerHoverable,
				layoutMode,
				iOS && "ios",
				enableToolbar && "has-toolbar",
				shouldRenderTabs && showUtils && "has-navigation"
			]));
		}

		if ($$self.$$.dirty[5] & /*$imageCropRect*/ 16384 | $$self.$$.dirty[6] & /*$imageTransforms, $imagePreviewModifiers, $imageEffects, $imageBackgroundColor*/ 3840) {
			 $$invalidate(37, imageProps = $imageTransforms
			? {
					// modifiers
					...Object.keys($imagePreviewModifiers).filter(key => $imagePreviewModifiers[key] != null).reduce(
						(prev, curr) => {
							prev = { ...prev, ...$imagePreviewModifiers[curr] };
							return prev;
						},
						{}
					),
					// default props
					...$imageTransforms,
					...$imageEffects,
					backgroundColor: $imageBackgroundColor,
					size: sizeCreateFromRect($imageCropRect)
				}
			: undefined);
		}

		if ($$self.$$.dirty[5] & /*$imageLoadState*/ 1024) {
			//
			// loading status
			//
			 $$invalidate(39, isImageLoadError = $imageLoadState && !!$imageLoadState.error);
		}

		if ($$self.$$.dirty[5] & /*$imageLoadState*/ 1024) {
			 $$invalidate(40, isWaitingForImage = !$imageLoadState || !$imageLoadState.complete && $imageLoadState.task === undefined);
		}

		if ($$self.$$.dirty[5] & /*$imageLoadState*/ 1024) {
			 $$invalidate(41, imageLoadProgress = $imageLoadState && ($imageLoadState.taskProgress || Infinity));
		}

		if ($$self.$$.dirty[5] & /*$imageLoadState, loadTimer*/ 1040) {
			 if ($imageLoadState && $imageLoadState.complete) {
				// TODO: derive visual load complete from interface rest state instead of arbitrary timer
				clearTimeout(loadTimer);

				$$invalidate(159, loadTimer = setTimeout(
					() => {
						set_store_value(imageVisualLoadComplete, $imageVisualLoadComplete = true, $imageVisualLoadComplete);
					},
					500
				));
			} else if (!$imageLoadState) {
				set_store_value(imageVisualLoadComplete, $imageVisualLoadComplete = false, $imageVisualLoadComplete);
			}
		}

		if ($$self.$$.dirty[1] & /*isImageLoadError, isWaitingForImage*/ 768 | $$self.$$.dirty[5] & /*$imageLoadState*/ 1024 | $$self.$$.dirty[6] & /*$imageVisualLoadComplete*/ 4096) {
			 $$invalidate(42, isLoadingImageData = $imageLoadState && !isImageLoadError && !isWaitingForImage && !$imageVisualLoadComplete);
		}

		if ($$self.$$.dirty[1] & /*$imagePreview*/ 8192 | $$self.$$.dirty[6] & /*$imageFile*/ 8192) {
			 $$invalidate(43, isCreatingImagePreview = !!($imageFile && !$imagePreview));
		}

		if ($$self.$$.dirty[5] & /*$imageProcessingPreparing*/ 67108864 | $$self.$$.dirty[6] & /*$imageProcessState*/ 16384) {
			//
			// processing status
			//
			 $$invalidate(45, isProcessingImage = $imageProcessingPreparing || $imageProcessState && $imageProcessState.progress !== undefined && !$imageProcessState.complete);
		}

		if ($$self.$$.dirty[1] & /*isWaitingForImage*/ 512 | $$self.$$.dirty[5] & /*$imageLoadState*/ 1024) {
			 $$invalidate(46, imageLoadShowProgressIndicator = $imageLoadState && !($imageLoadState.error || isWaitingForImage));
		}

		if ($$self.$$.dirty[0] & /*locale*/ 4 | $$self.$$.dirty[5] & /*$imageLoadState*/ 1024) {
			 $$invalidate(47, imageLoadStatusLabel = locale && (!$imageLoadState
			? locale.statusLabelLoadImage($imageLoadState)
			: !$imageLoadState.complete
				? locale.statusLabelLoadImage($imageLoadState)
				: locale.statusLabelLoadImage({
						progress: Infinity,
						task: "blob-to-bitmap"
					})));
		}

		if ($$self.$$.dirty[0] & /*locale*/ 4 | $$self.$$.dirty[6] & /*$imageProcessState*/ 16384) {
			 $$invalidate(48, imageProcessStatusLabel = $imageProcessState && locale && locale.statusLabelProcessImage($imageProcessState));
		}

		if ($$self.$$.dirty[6] & /*$imageProcessState*/ 16384) {
			 $$invalidate(49, imageProcessProgress = $imageProcessState && $imageProcessState.taskProgress || Infinity);
		}

		if ($$self.$$.dirty[6] & /*$imageProcessState*/ 16384) {
			 $$invalidate(50, imageProcessShowProgressIndicator = $imageProcessState && !$imageProcessState.error);
		}

		if ($$self.$$.dirty[6] & /*$imageProcessState*/ 16384) {
			 $$invalidate(51, isImageProcessingError = $imageProcessState && $imageProcessState.error);
		}

		if ($$self.$$.dirty[1] & /*isWaitingForImage, isImageLoadError, isLoadingImageData, isCreatingImagePreview, isProcessingImage*/ 23296) {
			 set_store_value(
				statusOpacity,
				$statusOpacity = isWaitingForImage || isImageLoadError || isLoadingImageData || isCreatingImagePreview || isProcessingImage
				? 1
				: 0,
				$statusOpacity
			);
		}

		if ($$self.$$.dirty[1] & /*$statusOpacity*/ 2097152) {
			 $$invalidate(53, isStatusVisible = $statusOpacity > 0);
		}

		if ($$self.$$.dirty[1] & /*$statusOpacity*/ 2097152) {
			 if ($statusOpacity <= 0) hideStatus();
		}

		if ($$self.$$.dirty[0] & /*windowWidth*/ 8192) {
			 $$invalidate(56, handleTouchStart = shouldPreventSwipe && (e => {
				// get touch or event itself (fixes issue with chrome dev tools)
				const event = e.touches ? e.touches[0] : e;

				// if is not an attempt to swipe back or forward
				if (event.pageX > 10 && event.pageX < windowWidth - 10) return;

				// stop the navigation attempt
				e.preventDefault();
			}));
		}

		if ($$self.$$.dirty[4] & /*navButtons*/ 4096) {
			 $$invalidate(57, navButtonsAlpha = navButtons.filter(button => button.slot === "alpha").map(mapToButton));
		}

		if ($$self.$$.dirty[0] & /*$clientRect*/ 131072) {
			 $$invalidate(201, hasClientRect = $clientRect && $clientRect.width > 0 && $clientRect.height > 0);
		}

		if ($$self.$$.dirty[0] & /*locale*/ 4 | $$self.$$.dirty[5] & /*utilsDefined*/ 536870912 | $$self.$$.dirty[6] & /*hasClientRect*/ 32768) {
			 $$invalidate(58, canRender = hasClientRect && locale && utilsDefined);
		}
	};

	return [
		pluginInterface,
		root,
		locale,
		id,
		elasticityMultiplier,
		willRenderCanvas,
		enableButtonExport,
		enableButtonRevert,
		enableNavigateHistory,
		enableToolbar,
		pluginOptions,
		imageSourceToImageData,
		history,
		windowWidth,
		windowHeight,
		utilsVisible,
		showUtils,
		$clientRect,
		shouldRenderTabs,
		$imageSize,
		shouldAnimate,
		$tabRect,
		canUndo,
		canRedo,
		utilsMerged,
		utilSelected,
		utilTools,
		utilsVisibleFraction,
		tabsConfig,
		tabs,
		panels,
		className,
		isLandscape,
		isPortrait,
		isNarrowViewport,
		shouldRenderUtilTools,
		$rootBackgroundColor,
		imageProps,
		$imageSelectionRectPresentation,
		isImageLoadError,
		isWaitingForImage,
		imageLoadProgress,
		isLoadingImageData,
		isCreatingImagePreview,
		$imagePreview,
		isProcessingImage,
		imageLoadShowProgressIndicator,
		imageLoadStatusLabel,
		imageProcessStatusLabel,
		imageProcessProgress,
		imageProcessShowProgressIndicator,
		isImageProcessingError,
		$statusOpacity,
		isStatusVisible,
		$statusOffsetX,
		$progressOffsetX,
		handleTouchStart,
		navButtonsAlpha,
		canRender,
		$env,
		$toolRect,
		$utilRect,
		$pixelRatio,
		$imagePreviews,
		$imageDecoration,
		$imageAnnotation,
		$imageOverlay,
		imageFile,
		imageSize,
		imageLoadState,
		imageProcessState,
		imageCropRect,
		imageBackgroundColor,
		imageDecoration,
		imageAnnotation,
		imageState,
		images,
		rootBackgroundColor,
		clientRect,
		rootRect,
		tabRect,
		toolRect,
		utilRect,
		stageRect,
		pointerAccuracy,
		pointerHoverable,
		isInteracting,
		isInteractingFraction,
		previewShouldUpscale,
		stageScalar,
		imageCropRectSnapshot,
		imageCropRectIntent,
		imageSelectionRect,
		imageSelectionRectSnapshot,
		imageSelectionRectIntent,
		imageSelectionRectPresentation,
		presentationScalar,
		imageOverlay,
		imageVisualLoadComplete,
		imagePreview,
		imagePreviewModifiers,
		imagePreviews,
		imageTransforms,
		imageEffects,
		env,
		pixelRatio,
		revert,
		imageProcessingPreparing,
		handleExport,
		utilStores,
		handleTransitionEnd,
		statusOpacity,
		statusOffsetX,
		progressOffsetX,
		offsetProgress,
		handleCloseImageLoadError,
		handleCloseImageProcessError,
		pressedKeysStore,
		handleKeydown,
		handleKeyup,
		handleWindowBlur,
		handleContextMenu,
		getStageState,
		createCanvasState,
		klass,
		layoutMode,
		stores,
		util,
		utils,
		animations,
		previewUpscale,
		willRevert,
		enableUtils,
		previewImageDataMaxSize,
		imageOrienter,
		pluginComponents,
		navButtons,
		onwindowresize,
		measure_handler,
		select_handler,
		func,
		panel_component_binding,
		measure_handler_1,
		show_handler,
		hide_handler,
		fade_handler,
		measure_handler_2,
		func_1,
		panel_component_binding_1,
		measure_handler_3,
		show_handler_1,
		hide_handler_1,
		fade_handler_1,
		func_2,
		div_binding,
		measure_handler_4
	];
}

class Ui extends SvelteComponent {
	constructor(options) {
		super();

		init(
			this,
			options,
			instance$a,
			create_fragment$a,
			safe_not_equal,
			{
				class: 124,
				layout: 125,
				stores: 126,
				locale: 2,
				id: 3,
				util: 127,
				utils: 128,
				animations: 129,
				previewUpscale: 130,
				elasticityMultiplier: 4,
				willRenderCanvas: 5,
				willRevert: 131,
				enableButtonExport: 6,
				enableButtonRevert: 7,
				enableNavigateHistory: 8,
				enableToolbar: 9,
				enableUtils: 132,
				previewImageDataMaxSize: 133,
				imageOrienter: 134,
				pluginComponents: 135,
				pluginOptions: 10,
				pluginInterface: 0,
				root: 1,
				imageSourceToImageData: 11,
				history: 12,
				navButtons: 136
			},
			[-1, -1, -1, -1, -1, -1, -1, -1, -1]
		);
	}

	get class() {
		return this.$$.ctx[124];
	}

	set class(klass) {
		this.$set({ class: klass });
		flush();
	}

	get layout() {
		return this.$$.ctx[125];
	}

	set layout(layoutMode) {
		this.$set({ layout: layoutMode });
		flush();
	}

	get stores() {
		return this.$$.ctx[126];
	}

	set stores(stores) {
		this.$set({ stores });
		flush();
	}

	get locale() {
		return this.$$.ctx[2];
	}

	set locale(locale) {
		this.$set({ locale });
		flush();
	}

	get id() {
		return this.$$.ctx[3];
	}

	set id(id) {
		this.$set({ id });
		flush();
	}

	get util() {
		return this.$$.ctx[127];
	}

	set util(util) {
		this.$set({ util });
		flush();
	}

	get utils() {
		return this.$$.ctx[128];
	}

	set utils(utils) {
		this.$set({ utils });
		flush();
	}

	get animations() {
		return this.$$.ctx[129];
	}

	set animations(animations) {
		this.$set({ animations });
		flush();
	}

	get previewUpscale() {
		return this.$$.ctx[130];
	}

	set previewUpscale(previewUpscale) {
		this.$set({ previewUpscale });
		flush();
	}

	get elasticityMultiplier() {
		return this.$$.ctx[4];
	}

	set elasticityMultiplier(elasticityMultiplier) {
		this.$set({ elasticityMultiplier });
		flush();
	}

	get willRenderCanvas() {
		return this.$$.ctx[5];
	}

	set willRenderCanvas(willRenderCanvas) {
		this.$set({ willRenderCanvas });
		flush();
	}

	get willRevert() {
		return this.$$.ctx[131];
	}

	set willRevert(willRevert) {
		this.$set({ willRevert });
		flush();
	}

	get enableButtonExport() {
		return this.$$.ctx[6];
	}

	set enableButtonExport(enableButtonExport) {
		this.$set({ enableButtonExport });
		flush();
	}

	get enableButtonRevert() {
		return this.$$.ctx[7];
	}

	set enableButtonRevert(enableButtonRevert) {
		this.$set({ enableButtonRevert });
		flush();
	}

	get enableNavigateHistory() {
		return this.$$.ctx[8];
	}

	set enableNavigateHistory(enableNavigateHistory) {
		this.$set({ enableNavigateHistory });
		flush();
	}

	get enableToolbar() {
		return this.$$.ctx[9];
	}

	set enableToolbar(enableToolbar) {
		this.$set({ enableToolbar });
		flush();
	}

	get enableUtils() {
		return this.$$.ctx[132];
	}

	set enableUtils(enableUtils) {
		this.$set({ enableUtils });
		flush();
	}

	get previewImageDataMaxSize() {
		return this.$$.ctx[133];
	}

	set previewImageDataMaxSize(previewImageDataMaxSize) {
		this.$set({ previewImageDataMaxSize });
		flush();
	}

	get imageOrienter() {
		return this.$$.ctx[134];
	}

	set imageOrienter(imageOrienter) {
		this.$set({ imageOrienter });
		flush();
	}

	get pluginComponents() {
		return this.$$.ctx[135];
	}

	set pluginComponents(pluginComponents) {
		this.$set({ pluginComponents });
		flush();
	}

	get pluginOptions() {
		return this.$$.ctx[10];
	}

	set pluginOptions(pluginOptions) {
		this.$set({ pluginOptions });
		flush();
	}

	get pluginInterface() {
		return this.$$.ctx[0];
	}

	get root() {
		return this.$$.ctx[1];
	}

	set root(root) {
		this.$set({ root });
		flush();
	}

	get imageSourceToImageData() {
		return this.$$.ctx[11];
	}

	set imageSourceToImageData(imageSourceToImageData) {
		this.$set({ imageSourceToImageData });
		flush();
	}

	get history() {
		return this.$$.ctx[12];
	}

	get navButtons() {
		return this.$$.ctx[136];
	}

	set navButtons(navButtons) {
		this.$set({ navButtons });
		flush();
	}
}

// which props to filter out of exported props
const utilPrivateProps = ['klass', 'stores', 'isVisible', 'isActive', 'isActiveFraction', 'locale'];
const viewPrivateProps = [
    // methods
    'history',
    // props
    'klass',
    'stores',
    'navButtons',
    'pluginComponents',
    'pluginInterface',
    'pluginOptions',
];
// view options array
let editorProps;
const pluginProps = new Set([]);
const propPluginRef = {};
// loops over plugins and registers all available options so we can define getters/setters
const pluginComponents = new Map();
const setEditorViewPlugins = (...args) => {
    args.forEach((plugin) => {
        const [id, Component] = plugin.util;
        pluginComponents.set(id, Component);
        getComponentExportedProps(Component)
            .filter((prop) => !utilPrivateProps.includes(prop))
            .forEach((prop) => {
            pluginProps.add(prop);
            propPluginRef[prop] = id;
        });
    });
};
const getEditorViewProps = () => {
    editorProps = new Set(getComponentExportedProps(Ui).filter((prop) => !viewPrivateProps.includes(prop)));
    return [...editorProps, ...pluginProps];
};
const attachEditorView = (target, stores, props = {}) => {
    const accessors = {};
    const editor = new Ui({
        target,
        props: {
            ...props,
            stores,
            pluginComponents: Array.from(pluginComponents),
        },
    });
    // set up accessors for editor props
    if (!editorProps)
        editorProps = new Set(getComponentExportedProps(Ui).filter((prop) => !viewPrivateProps.includes(prop)));
    editorProps.forEach((prop) => {
        Object.defineProperty(accessors, prop, {
            get: () => editor.prop,
            set: (value) => (editor[prop] = value),
        });
    });
    // set up accessors for plugin props
    pluginProps.forEach((prop) => {
        const plugin = propPluginRef[prop];
        Object.defineProperty(accessors, prop, {
            get: () => editor.pluginInterface[plugin][prop],
            set: (value) => {
                editor.pluginOptions = {
                    ...editor.pluginOptions,
                    [plugin]: {
                        ...editor.pluginOptions[plugin],
                        [prop]: value,
                    },
                };
            },
        });
    });
    // add element root query
    Object.defineProperty(accessors, 'element', {
        get: () => editor.root,
        set: () => undefined,
    });
    defineMethods(accessors, {
        on: (event, cb) => {
            if (/undo|redo|revert/.test(event))
                return editor.history.on(event, cb);
            return editor.$on(event, cb);
        },
        undo: () => editor.history.undo(),
        redo: () => editor.history.redo(),
        revert: () => editor.history.clear(),
        destroy: () => editor.$destroy(),
    });
    // clean up on window unload
    if (isBrowser())
        window.addEventListener('unload', () => editor && editor.$destroy());
    return accessors;
};

var linkAccessors = (destination, origin) => {
    const descriptors = Object.getOwnPropertyDescriptors(destination);
    Object.keys(descriptors).forEach((key) => {
        // props, set new setter and getter
        if (!!descriptors[key]['get']) {
            Object.defineProperty(origin, key, {
                get: () => destination[key],
                set: (value) => (destination[key] = value),
            });
        }
        // method, copy reference
        else {
            origin[key] = destination[key];
        }
    });
};

var initEditorView = (viewProps = {}) => (target) => {
    const accessors = {};
    const { sub, pub } = pubsub();
    // create editor core
    const core = createImageEditor();
    linkAccessors(core, accessors);
    // create editor view
    const view = attachEditorView(target, core.stores, viewProps);
    linkAccessors(view, accessors);
    // listen for UI interaction and link to editor API
    const unsubs = ['processImage', 'abortProcessImage', 'abortLoadImage'].map((event) => view.on(event, () => core[event]()));
    // set up new api
    defineMethods(accessors, {
        on: (event, cb) => {
            const unsubInit = sub(event, cb);
            const unsubCore = core.on(event, cb);
            const unsubView = view.on(event, cb);
            return () => {
                unsubInit();
                unsubCore();
                unsubView();
            };
        },
        destroy: () => {
            // unsub
            unsubs.forEach((unsub) => unsub());
            // destroy
            view.destroy();
            core.destroy();
            // destroyed
            pub('destroy');
        },
    });
    return accessors;
};

var appendEditor = (target, options = {}) => {
    const element = isString(target) ? document.querySelector(target) : target;
    if (!element)
        return undefined;
    options.class = options.class ? `doka-image-editor ${options.class}` : 'doka-image-editor';
    const accessors = initEditorView()(element);
    return Object.assign(accessors, options);
};

var appendEditors = (targets, options = {}) => {
    const elements = isString(targets)
        ? Array.from(document.querySelectorAll(targets))
        : targets;
    return elements.filter(Boolean).map((element) => appendEditor(element, deepCopy(options)));
};

/* src/native/ui/modal/index.svelte generated by Svelte v3.29.4 */

function create_fragment$b(ctx) {
	let div;
	let mounted;
	let dispose;
	add_render_callback(/*onwindowresize*/ ctx[11]);

	return {
		c() {
			div = element("div");
			attr(div, "class", "doka-image-editor DokaModal");
			attr(div, "data-env", /*env*/ ctx[3]);
			attr(div, "style", /*style*/ ctx[2]);
		},
		m(target, anchor) {
			insert(target, div, anchor);
			/*div_binding*/ ctx[12](div);

			if (!mounted) {
				dispose = [
					listen(window, "keydown", /*handleKey*/ ctx[6]),
					listen(window, "orientationchange", /*spawnMeasureElement*/ ctx[7]),
					listen(window, "resize", /*onwindowresize*/ ctx[11]),
					listen(div, "wheel", /*handleWheel*/ ctx[5])
				];

				mounted = true;
			}
		},
		p(ctx, [dirty]) {
			if (dirty & /*env*/ 8) {
				attr(div, "data-env", /*env*/ ctx[3]);
			}

			if (dirty & /*style*/ 4) {
				attr(div, "style", /*style*/ ctx[2]);
			}
		},
		i: noop$1,
		o: noop$1,
		d(detaching) {
			if (detaching) detach(div);
			/*div_binding*/ ctx[12](null);
			mounted = false;
			run_all(dispose);
		}
	};
}

function instance$b($$self, $$props, $$invalidate) {
	let $opacity;
	const dispatch = createEventDispatcher();
	let { root } = $$props;
	let { preventZoomViewport = true } = $$props;
	let hidden = true;
	let hiding = false;
	let showing = false;
	const opacity = spring(0, { precision: 0.001, damping: 0.5 });
	component_subscribe($$self, opacity, value => $$invalidate(21, $opacity = value));

	// animate hide and show
	const unsub = opacity.subscribe(o => {
		if (showing && o >= 1) {
			showing = false; // now shown
			hidden = false;
			dispatch("show");
		} else if (hiding && o <= 0) {
			hiding = false; // now hidden
			hidden = true;
			dispatch("hide");
		}
	});

	// clean up when removed
	onDestroy(() => unsub());

	let viewportDefaults = undefined;
	let showTimeoutId = undefined;

	const show = () => {
		// is visible, or busy becoming visible
		if (showing || !hidden) return;

		// now in 'showing' transition
		showing = true;

		// store default viewport element values
		const viewportElement = document.querySelector("meta[name=viewport]") || h("meta", { name: "viewport" });

		viewportDefaults = !viewportDefaults && viewportElement.getAttribute("content");

		viewportElement.setAttribute("content", viewportRules + ((/cover/).test(viewportDefaults)
		? ",viewport-fit=cover"
		: ""));

		if (!viewportElement.parentNode) document.head.appendChild(viewportElement);
		clearTimeout(showTimeoutId);
		showTimeoutId = setTimeout(() => set_store_value(opacity, $opacity = 1, $opacity), 250);
	};

	const hide = () => {
		// is hiding or already hidden
		if (hiding || hidden) return;

		// if previous command was show, we need to prevent the timeout from kicking off
		clearTimeout(showTimeoutId);

		// now in 'hiding' transition
		hiding = true;

		// get a reference to the viewport element, it should be there as we created it earlier, we restore defaults if the viewport metatag was defined, if not, we destroy it
		const viewportElement = document.querySelector("meta[name=viewport]");

		if (viewportDefaults) viewportElement.setAttribute("content", viewportDefaults); else document.head.removeChild(viewportElement);

		// let's hide!
		set_store_value(opacity, $opacity = 0, $opacity);
	};

	// prevent mousewheel from scrolling content behind modal
	const handleWheel = e => e.preventDefault();

	// handle escape to close modal
	const handleKey = e => {
		// get key type
		const { key } = e;

		// only deal with escape key
		if (!(/escape/i).test(key)) return;

		// don't close when in an input or textuarea
		const targetElement = e.target;

		if (targetElement && (/input|textarea/i).test(targetElement.nodeName)) return;
		hide();
	};

	// this logic scales modal to fit viewport height
	let windowHeight = 0;

	// iOS measure actual page height so we can make optional use of available space
	let measureElement;

	let measureElementHeight = undefined;

	const spawnMeasureElement = () => {
		// can't spawn more than one
		if (measureElement) return;

		measureElement = h("div", {
			style: "position:fixed;height:100vh;top:0"
		});

		document.body.appendChild(measureElement);
	};

	onMount(() => {
		// no need to run this logic if is not on iOS
		if (!isIOS()) return;

		// will be used in first read to determine page height and bottom padding
		spawnMeasureElement();
	});

	afterUpdate(() => {
		// will only run on iOS
		if (!measureElement) return;

		// get height and store
		$$invalidate(19, measureElementHeight = measureElement.offsetHeight);

		// remove measure element
		measureElement.parentNode.removeChild(measureElement);

		measureElement = undefined;
	});

	function onwindowresize() {
		$$invalidate(1, windowHeight = window.innerHeight);
	}

	function div_binding($$value) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			root = $$value;
			$$invalidate(0, root);
		});
	}

	$$self.$$set = $$props => {
		if ("root" in $$props) $$invalidate(0, root = $$props.root);
		if ("preventZoomViewport" in $$props) $$invalidate(8, preventZoomViewport = $$props.preventZoomViewport);
	};

	let viewportRules;
	let computedStyle;
	let insetTop;
	let insetBottom;
	let modalHeight;
	let style;
	let iOSFooterVisible;
	let env;

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*preventZoomViewport*/ 256) {
			 viewportRules = "width=device-width,height=device-height,initial-scale=1" + (preventZoomViewport
			? ",maximum-scale=1,user-scalable=0"
			: "");
		}

		if ($$self.$$.dirty & /*root*/ 1) {
			 $$invalidate(22, computedStyle = root && getComputedStyle(root));
		}

		if ($$self.$$.dirty & /*computedStyle*/ 4194304) {
			 $$invalidate(23, insetTop = computedStyle && parseInt(computedStyle.getPropertyValue("--safe-area-inset-top"), 10));
		}

		if ($$self.$$.dirty & /*computedStyle*/ 4194304) {
			 $$invalidate(24, insetBottom = computedStyle && parseInt(computedStyle.getPropertyValue("--safe-area-inset-bottom"), 10));
		}

		if ($$self.$$.dirty & /*windowHeight, insetTop, insetBottom*/ 25165826) {
			 $$invalidate(25, modalHeight = windowHeight + insetTop + insetBottom);
		}

		if ($$self.$$.dirty & /*modalHeight, $opacity*/ 35651584) {
			 $$invalidate(2, style = `height:${modalHeight}px;opacity:${$opacity}`);
		}

		if ($$self.$$.dirty & /*measureElementHeight, windowHeight*/ 524290) {
			 $$invalidate(26, iOSFooterVisible = isIOS() && measureElementHeight > windowHeight);
		}

		if ($$self.$$.dirty & /*iOSFooterVisible*/ 67108864) {
			 $$invalidate(3, env = arrayJoin([isIOS() && "ios", iOSFooterVisible && "has-footer"]));
		}
	};

	return [
		root,
		windowHeight,
		style,
		env,
		opacity,
		handleWheel,
		handleKey,
		spawnMeasureElement,
		preventZoomViewport,
		show,
		hide,
		onwindowresize,
		div_binding
	];
}

class Modal extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$b, create_fragment$b, safe_not_equal, {
			root: 0,
			preventZoomViewport: 8,
			show: 9,
			hide: 10
		});
	}

	get root() {
		return this.$$.ctx[0];
	}

	set root(root) {
		this.$set({ root });
		flush();
	}

	get preventZoomViewport() {
		return this.$$.ctx[8];
	}

	set preventZoomViewport(preventZoomViewport) {
		this.$set({ preventZoomViewport });
		flush();
	}

	get show() {
		return this.$$.ctx[9];
	}

	get hide() {
		return this.$$.ctx[10];
	}
}

// @ts-ignore
// we export a function that renders the view as to not export svelte component related code to outside the ui dir
var createModal = (options = {}, parent) => new Modal({
    target: parent || document.body,
    props: {
        preventZoomViewport: options.preventZoomViewport,
    },
});

var openEditor = (options = {}, parent) => {
    // set up pub/sub for the app layer
    const { sub, pub } = pubsub();
    const accessors = {};
    const modal = createModal(options, parent);
    // public methods
    const close = () => {
        pub('close');
        hide();
    };
    const hide = () => {
        modal.hide();
    };
    const show = () => {
        modal.show();
    };
    // create instance
    const createEditorView = initEditorView({
        navButtons: [
            {
                label: (locale) => locale.labelClose,
                icon: (locale) => locale.iconButtonClose,
                hideLabel: true,
                onclick: () => close(),
                slot: 'alpha',
            },
        ],
    });
    const view = createEditorView(modal.root);
    linkAccessors(view, accessors);
    // cleans up view
    const destroy = () => {
        modal.hide();
        modal.$destroy();
        view.destroy();
    };
    defineMethods(accessors, {
        on: (event, cb) => {
            // capture modal related events
            if (/show|hide|close/.test(event))
                return sub(event, cb);
            // route rest of events to view
            return view.on(event, cb);
        },
        destroy,
        hide,
        show,
        close,
    });
    // add element root query
    Object.defineProperty(accessors, 'modal', {
        get: () => modal.root,
        set: () => undefined,
    });
    // destroy when hidden
    modal.$on('show', () => pub('show'));
    modal.$on('hide', () => {
        pub('hide');
        destroy();
    });
    // modal behavior
    view.on('process', hide);
    view.on('loadstart', show);
    // update props
    Object.assign(accessors, options);
    return accessors;
};

var overlayEditor = (target, options) => {
    const editor = appendEditor(target, {
        ...options,
        layout: 'overlay',
    });
    return editor;
};

const EVENTS_TO_BUBBLE = [
    ...editorEventsToBubble,
    'undo',
    'redo',
    'revert',
    'destroy',
    'show',
    'hide',
    'close',
];
const dispatchElementEvent = (target, event, detail) => target.dispatchEvent(new CustomEvent(event, { detail, bubbles: true, cancelable: true }));
var dispatchEditorEvents = (editor, handler, options = {}) => {
    const { prefix = 'doka:' } = options;
    return EVENTS_TO_BUBBLE.map((event) => editor.on(event, (value) => isElement(handler)
        ? dispatchElementEvent(handler, `${prefix}${event}`, value)
        : handler(event, value)));
};

const TAG = 'doka-image-editor';
var customElement = () => new Promise((resolve) => {
    if (!DokaImageEditorElement)
        return resolve([]);
    customElements.define(TAG, DokaImageEditorElement);
    customElements.whenDefined(TAG).then(() => resolve(document.querySelectorAll(TAG)));
});
const DokaImageEditorElement = isBrowser() &&
    class extends HTMLElement {
        constructor() {
            super();
            this._editor = undefined;
            this._unsubs = undefined;
        }
        static get observedAttributes() {
            return ['src'];
        }
        attributeChangedCallback(attrName, oldValue, newValue) {
            this[attrName] = newValue;
        }
        connectedCallback() {
            this._editor = initEditorView()(this);
            linkAccessors(this._editor, this);
            this._editor.src = this.getAttribute('src');
            this._unsubs = dispatchEditorEvents(this._editor, this);
        }
        disconnectedCallback() {
            this._editor.destroy();
            this._unsubs.forEach((unsub) => unsub());
        }
    };

const IconCross = '<g fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor" stroke-width=".125em"><path d="M18 6L6 18M6 6l12 12"></path></path></g>';
var en_gb = {
    // generic
    labelReset: 'Reset',
    labelDefault: 'Default',
    labelAuto: 'Auto',
    labelNone: 'None',
    labelEdit: 'Edit',
    labelClose: 'Close',
    // sizes
    labelSizeExtraSmall: 'Extra small',
    labelSizeSmall: 'Small',
    labelSizeMediumSmall: 'Medium small',
    labelSizeMedium: 'Medium',
    labelSizeMediumLarge: 'Medium large',
    labelSizeLarge: 'Large',
    labelSizeExtraLarge: 'Extra large',
    // unused?
    labelButtonRevert: 'Revert',
    labelButtonCancel: 'Cancel',
    labelButtonUndo: 'Undo',
    labelButtonRedo: 'Redo',
    labelButtonExport: 'Done',
    iconButtonClose: IconCross,
    iconButtonRevert: `<g fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor" stroke-width=".125em"><path d="M7.388 18.538a8 8 0 10-2.992-9.03"/><path fill="currentColor" d="M2.794 11.696L2.37 6.714l5.088 3.18z"/><path d="M12 8v4M12 12l4 2"/></g>`,
    iconButtonUndo: `<g fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor" stroke-width=".125em"><path d="M10 8h4c2.485 0 5 2 5 5s-2.515 5-5 5h-4"/><path fill="currentColor" d="M5 8l4-3v6z"/></g>`,
    iconButtonRedo: `<g fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor" stroke-width=".125em"><path d="M14 8h-4c-2.485 0-5 2-5 5s2.515 5 5 5h4"/><path fill="currentColor" d="M19 8l-4-3v6z"/></g>`,
    iconButtonExport: `<polyline points="20 6 9 17 4 12" fill="none" stroke="currentColor" stroke-width=".125em"></polyline>`,
    // status
    statusLabelButtonClose: 'Close',
    statusIconButtonClose: IconCross,
    statusLabelLoadImage: (state) => {
        if (!state || !state.task)
            return 'Waiting for image';
        if (state.error)
            return 'Error loading image';
        if (state.task === 'blob-to-bitmap')
            return 'Creating preview…';
        return 'Loading image…';
    },
    // processing status message
    statusLabelProcessImage: (state) => {
        if (!state || !state.task)
            return undefined;
        if (state.task === 'store') {
            if (state.error)
                return 'Error uploading image';
            return 'Uploading image…';
        }
        if (state.error)
            return 'Error processing image';
        return 'Processing image…';
    },
};
const ShapeEditor = {
    shapeLabelButtonSelectSticker: 'Select image',
    shapeIconButtonSelectSticker: `<g><circle fill="currentColor" cx="5" cy="6" r="1.333"/><g fill="none" stroke="currentColor" stroke-width="0.0625em" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="1" width="14" height="14" rx="2"/><path d="M15 13l-5-6-5 8"/></g></g>`,
    shapeIconButtonFlipHorizontal: `<svg width="24" height="24" xmlns="http://www.w3.org/2000/svg"><g stroke="#FFF" stroke-width="2" fill="none" fill-rule="evenodd"><path d="M6 6.5h5v11H6z"/><path fill="#FFF" d="M15 6.5h3v11h-3z"/><path d="M11 4v16" fill="#fff"/></g></svg>`,
    shapeIconButtonFlipVertical: `<g stroke="currentColor" stroke-width=".125em"><rect x="7" y="8" width="11" height="5" fill="none"/><rect x="7" y="17" width="11" height="2" fill="currentColor"/><line x1="5" y1="13" x2="20" y2="13"/></g>`,
    shapeIconButtonRemove: `<g fill="none" fill-rule="evenodd"><path stroke="#FFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M7.5 7h9z"/><path d="M7.916 9h8.168a1 1 0 01.99 1.14l-.972 6.862a2 2 0 01-1.473 1.653c-.877.23-1.753.345-2.629.345-.876 0-1.752-.115-2.628-.345a2 2 0 01-1.473-1.653l-.973-6.862A1 1 0 017.916 9z" fill="#FFF"/><rect fill="#FFF" x="10" y="5" width="4" height="3" rx="1"/></g>`,
    shapeIconButtonDuplicate: `
    <g fill="none" fill-rule="evenodd"><path d="M15 13.994V16a2 2 0 01-2 2H8a2 2 0 01-2-2v-5a2 2 0 012-2h2.142" stroke="currentColor" stroke-width=".125em"/><path d="M15 9V8a1 1 0 00-2 0v1h-1a1 1 0 000 2h1v1a1 1 0 002 0v-1h1a1 1 0 000-2h-1zm-4-4h6a2 2 0 012 2v6a2 2 0 01-2 2h-6a2 2 0 01-2-2V7a2 2 0 012-2z" fill="currentColor"/></g>`,
    shapeIconButtonMoveToFront: `
    <g fill="none" fill-rule="evenodd"><rect fill="currentColor" x="11" y="13" width="8" height="2" rx="1"/><rect fill="currentColor" x="9" y="17" width="10" height="2" rx="1"/><path d="M11.364 8H10a5 5 0 000 10M12 6.5L14.5 8 12 9.5z" stroke="currentColor" stroke-width=".125em" stroke-linecap="round"/></g>`,
    shapeTitleButtonFlipHorizontal: 'Flip Horizontal',
    shapeTitleButtonFlipVertical: 'Flip Vertical',
    shapeTitleButtonRemove: 'Remove',
    shapeTitleButtonDuplicate: 'Duplicate',
    shapeTitleButtonMoveToFront: 'Move to front',
    shapeLabelInputText: 'Edit text',
    shapeIconInputCancel: '<g fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor" stroke-width=".125em"><path d="M18 6L6 18M6 6l12 12"/></g>',
    shapeIconInputConfirm: '<g fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor" stroke-width=".125em"><polyline points="20 6 9 17 4 12"/></g>',
    shapeLabelInputCance: 'Cancel',
    shapeLabelInputConfirm: 'Confirm',
    shapeLabelStrokeNone: 'No outline',
    shapeTitleBackgroundColor: 'Fill color',
    shapeTitleFontFamily: 'Font',
    shapeTitleFontSize: 'Font size',
    shapeTitleLineHeight: 'Leading',
    shapeTitleLineStart: 'Start',
    shapeTitleLineEnd: 'End',
    shapeTitleStrokeWidth: 'Line width',
    shapeTitleStrokeColor: 'Line color',
    shapeTitleLineDecorationBar: 'Bar',
    shapeTitleLineDecorationCircle: 'Circle',
    shapeTitleLineDecorationSquare: 'Square',
    shapeTitleLineDecorationArrow: 'Arrow',
    shapeTitleLineDecorationCircleSolid: 'Circle solid',
    shapeTitleLineDecorationSquareSolid: 'Square solid',
    shapeTitleLineDecorationArrowSolid: 'Arrow solid',
    shapeTitleColorTransparent: 'Transparent',
    shapeTitleColorWhite: 'White',
    shapeTitleColorSilver: 'Silver',
    shapeTitleColorGray: 'Gray',
    shapeTitleColorBlack: 'Black',
    shapeTitleColorNavy: 'Navy',
    shapeTitleColorBlue: 'Blue',
    shapeTitleColorAqua: 'Aqua',
    shapeTitleColorTeal: 'Teal',
    shapeTitleColorOlive: 'Olive',
    shapeTitleColorGreen: 'Green',
    shapeTitleColorYellow: 'Yellow',
    shapeTitleColorOrange: 'Orange',
    shapeTitleColorRed: 'Red',
    shapeTitleColorMaroon: 'Maroon',
    shapeTitleColorFuchsia: 'Fuchsia',
    shapeTitleColorPurple: 'Purple',
    shapeTitleTextColor: 'Font color',
    shapeTitleTextAlign: 'Text align',
    shapeTitleTextAlignLeft: 'Left align text',
    shapeTitleTextAlignCenter: 'Center align text',
    shapeTitleTextAlignRight: 'Right align text',
    shapeIconTextAlignLeft: '<g stroke-width=".125em" stroke="currentColor"><line x1="5" y1="8" x2="15" y2="8"/><line x1="5" y1="12" x2="19" y2="12"/><line x1="5" y1="16" x2="14" y2="16"/></g>',
    shapeIconTextAlignCenter: '<g stroke-width=".125em" stroke="currentColor"><line x1="7" y1="8" x2="17" y2="8"/><line x1="5" y1="12" x2="19" y2="12"/><line x1="8" y1="16" x2="16" y2="16"/></g>',
    shapeIconTextAlignRight: '<g stroke-width=".125em" stroke="currentColor"><line x1="9" y1="8" x2="19" y2="8"/><line x1="5" y1="12" x2="19" y2="12"/><line x1="11" y1="16" x2="19" y2="16"/></g>',
    shapeLabelToolSharpie: 'Sharpie',
    shapeLabelToolEraser: 'Eraser',
    shapeLabelToolRectangle: 'Rectangle',
    shapeLabelToolEllipse: 'Ellipse',
    shapeLabelToolArrow: 'Arrow',
    shapeLabelToolLine: 'Line',
    shapeLabelToolText: 'Text',
    shapeLabelToolPreset: 'Stickers',
    shapeIconToolSharpie: '<g stroke-width=".125em" stroke="currentColor" fill="none"><path d="M2.025 5c5.616-2.732 8.833-3.857 9.65-3.374C12.903 2.351.518 12.666 2.026 14 3.534 15.334 16.536.566 17.73 2.566 18.924 4.566 3.98 17.187 4.831 18c.851.813 9.848-6 11.643-6 1.087 0-2.53 5.11-2.92 7-.086.41 3.323-1.498 4.773-1 .494.17.64 2.317 1.319 3 .439.443 1.332.776 2.679 1" stroke="currentColor" stroke-width=".125em" fill="none" fill-rule="evenodd" stroke-linejoin="round"/></g>',
    shapeIconToolEraser: '<g stroke-width=".125em" stroke="currentColor" stroke-linecap="round" fill="none"><g transform="translate(3, 15) rotate(-45)"><rect x="0" y="0" width="18" height="10" rx="3"/></g><line x1="11" y1="21" x2="18" y2="21"/><line x1="20" y1="21" x2="22" y2="21"/></g>',
    shapeIconToolRectangle: '<g stroke-width=".125em" stroke="currentColor" fill="none"><rect x="2" y="2" width="20" height="20" rx="3"/></g>',
    shapeIconToolEllipse: '<g stroke-width=".125em" stroke="currentColor" fill="none"><circle cx="12" cy="12" r="11"/></g>',
    shapeIconToolArrow: '<g stroke-width=".125em" stroke="currentColor" fill="none"><line x1="20" y1="3" x2="6" y2="21"/><path d="m10 5 L22 1 L21 13" fill="currentColor" stroke="none"/></g>',
    shapeIconToolLine: '<g stroke-width=".125em" stroke="currentColor" fill="none"><line x1="20" y1="3" x2="6" y2="21"/></g>',
    shapeIconToolText: '<g stroke="none" fill="currentColor" transform="translate(6,0)"><path d="M8.14 20.085c.459 0 .901-.034 1.329-.102a8.597 8.597 0 001.015-.21v1.984c-.281.135-.695.247-1.242.336a9.328 9.328 0 01-1.477.133c-3.312 0-4.968-1.745-4.968-5.235V6.804H.344v-1.25l2.453-1.078L3.89.819h1.5v3.97h4.97v2.015H5.39v10.078c0 1.031.245 1.823.735 2.375s1.161.828 2.015.828z"/>',
    shapeIconToolPreset: '<g fill="none" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor" stroke-width=".125em"><path d="M12 22c2.773 0 1.189-5.177 3-7 1.796-1.808 7-.25 7-3 0-5.523-4.477-10-10-10S2 6.477 2 12s4.477 10 10 10z"></path><path d="M20 17c-3 3-5 5-8 5"></path></g>',
};

//#region markup
const propertyMap = {
    borderColor: 'strokeColor',
    borderWidth: 'strokeWidth',
    lineWidth: 'strokeWidth',
    fontColor: 'color',
    lineColor: 'strokeColor',
    src: 'backgroundImage',
    fit: 'backgroundSize',
};
const convertValue = (value) => {
    if (value === 0)
        return 0;
    if (value === '0%')
        return 0;
    if (value === '0px')
        return 0;
    if (/px$/.test(value))
        return parseInt(value, 10);
    if (/\%$/.test(value))
        return value;
    if (value <= 1)
        return `${value * 100}%`;
};
const addValues = (a, b) => {
    let _a, _b;
    if (/%$/.test(a)) {
        _a = parseFloat(a);
        _b = parseFloat(b);
        return `${_a + _b}%`;
    }
    if (/px$/.test(a)) {
        _a = parseInt(a, 10);
        _b = parseInt(b, 10);
        return `${_a + _b}px`;
    }
};
const shapeFromLegacyMarkup = (crop, type, style) => {
    const shape = Object.keys(style).reduce((res, prop) => {
        let value = style[prop];
        // rename prop
        prop = propertyMap[prop] || prop;
        // if is px value
        if (/px$/.test(value)) {
            value = convertValue(value);
        }
        // if is number convert to percentage
        else if (/^(?:x|y|left|right|top|bottom|width|height|fontSize|strokeWidth)$/.test(prop) &&
            typeof value === 'number') {
            if (/strokeWidth/.test(prop))
                value *= 2;
            value = convertValue(value);
        }
        // if is color value
        if (/color/i.test(prop)) {
            value = colorStringToColorArray(value);
        }
        if (value === null)
            value = undefined;
        res[prop] = value;
        return res;
    }, {});
    if (type === 'line') {
        if (shape.lineDecoration.length === 1)
            shape.lineEnd = 'arrow';
        if (shape.lineDecoration.length === 2)
            shape.lineStart = 'arrow';
        shape.x1 = shape.x;
        shape.y1 = shape.y;
        shape.x2 = addValues(shape.x, shape.width);
        shape.y2 = addValues(shape.y, shape.height);
        delete shape.x;
        delete shape.y;
        delete shape.width;
        delete shape.height;
        delete shape.lineDecoration;
        delete shape.lineStyle;
    }
    if (type === 'text') {
        shape.y = addValues(shape.y, '-' + shape.fontSize);
        delete shape.width;
        delete shape.height;
        delete shape.borderStyle;
    }
    if (type === 'ellipse') {
        let w, h;
        // calculate `rx` and `ry` based on `width` and `h`eight`
        if (/%$/.test(shape.width)) {
            w = parseFloat(shape.width) / 100;
            h = parseFloat(shape.height) / 100;
            const widthAbsolute = w * crop.width;
            const heightAbsolute = h * crop.height;
            w = (widthAbsolute / crop.width) * 100;
            h = (heightAbsolute / crop.height) * 100;
        }
        else {
            w = shape.width;
            h = shape.height;
        }
        shape.rx = w * 0.5;
        shape.ry = h * 0.5;
        if (/%$/.test(shape.width)) {
            shape.rx += '%';
            shape.ry += '%';
        }
        shape.x = addValues(shape.x, shape.rx);
        shape.y = addValues(shape.y, shape.ry);
        delete shape.width;
        delete shape.height;
        delete shape.borderStyle;
    }
    if (type === 'rect') {
        delete shape.borderStyle;
    }
    if (type === 'path') {
        shape.points = shape.points.map((point) => {
            return {
                x: convertValue(point.x),
                y: convertValue(point.y),
            };
        });
    }
    return shape;
};
//#endregion
//#region crop
const getOffsetPointOnEdge = (length, rotation) => {
    const a = length;
    const A = 1.5707963267948966;
    const B = rotation;
    const C = 1.5707963267948966 - rotation;
    const sinA = Math.sin(A);
    const sinB = Math.sin(B);
    const sinC = Math.sin(C);
    const cosC = Math.cos(C);
    const ratio = a / sinA;
    const b = ratio * sinB;
    const c = ratio * sinC;
    return vectorCreate(cosC * b, cosC * c);
};
const getRotatedRectSize = (rect, rotation) => {
    const w = rect.width;
    const h = rect.height;
    const hor = getOffsetPointOnEdge(w, rotation);
    const ver = getOffsetPointOnEdge(h, rotation);
    const tl = vectorCreate(rect.x + Math.abs(hor.x), rect.y - Math.abs(hor.y));
    const tr = vectorCreate(rect.x + rect.width + Math.abs(ver.y), rect.y + Math.abs(ver.x));
    const bl = vectorCreate(rect.x - Math.abs(ver.y), rect.y + rect.height - Math.abs(ver.x));
    return {
        width: vectorDistance(tl, tr),
        height: vectorDistance(tl, bl),
    };
};
const getBoundsAroundCenter = (imageSize, center) => {
    const cx = center.x > 0.5 ? 1 - center.x : center.x;
    const cy = center.y > 0.5 ? 1 - center.y : center.y;
    return sizeCreate(cx * 2 * imageSize.width, cy * 2 * imageSize.height);
};
const getCanvasSize = (imageSize, canvasAspectRatio, zoom = 1) => {
    const imageAspectRatio = imageSize.height / imageSize.width;
    // determine actual pixels on x and y axis
    let canvasWidth = 1;
    let canvasHeight = canvasAspectRatio;
    let imgWidth = 1;
    let imgHeight = imageAspectRatio;
    if (imgHeight > canvasHeight) {
        imgHeight = canvasHeight;
        imgWidth = imgHeight / imageAspectRatio;
    }
    const scalar = Math.max(canvasWidth / imgWidth, canvasHeight / imgHeight);
    const width = imageSize.width / (zoom * scalar * imgWidth);
    const height = width * canvasAspectRatio;
    return {
        width: width,
        height: height,
    };
};
const getCenteredCropRect = (imageSize, aspectRatio) => {
    let width = imageSize.width;
    let height = width * aspectRatio;
    if (height > imageSize.height) {
        height = imageSize.height;
        width = height / aspectRatio;
    }
    const x = (imageSize.width - width) * 0.5;
    const y = (imageSize.height - height) * 0.5;
    return rectCreate(x, y, width, height);
};
const getCorrectedLegacyAspectRatio = (imageSize, aspectRatio) => aspectRatio != null
    ? // fox the aspect ratio
        1 / aspectRatio
    : // set image aspect ratio
        imageSize.width / imageSize.height;
const imagePropertiesFromLegacyCrop = (imageSize, { flip, aspectRatio, rotation, center, zoom, scaleToFit }) => {
    const res = {};
    // is centered crop
    const isCenteredCrop = !center || (center && center.x === 0.5 && center.y === 0.5);
    // handle basic props
    if (flip && flip.horizontal)
        res.flipHorizontal = flip.horizontal;
    if (flip && flip.vertical)
        res.flipVertical = flip.vertical;
    // fix aspect ratio, in FilePond and Doka classic it's height/width instead of width/height
    const correctedAspectRatio = getCorrectedLegacyAspectRatio(imageSize, aspectRatio);
    // image bounds
    const cropLimit = !(scaleToFit === false);
    const canvasSize = getCanvasSize(imageSize, aspectRatio, zoom);
    const cropSize = isCenteredCrop
        ? imageSize
        : getBoundsAroundCenter(imageSize, cropLimit ? center : { x: 0.5, y: 0.5 });
    const cropCentered = getCenteredCropRect(imageSize, aspectRatio);
    // has different aspect ratio than image
    if (aspectRatio || !isCenteredCrop || zoom) {
        res.crop = rectContainRect(rectCreateFromSize(cropSize), correctedAspectRatio);
    }
    if (typeof rotation === 'number' && rotation !== null) {
        if (rotation != null)
            res.rotation = rotation;
        const rotatedCropSize = getRotatedRectSize(cropCentered, rotation);
        const scalar = Math.max(rotatedCropSize.width / cropSize.width, rotatedCropSize.height / cropSize.height);
        // window.clear();
        // crop position in non-rotated image
        const cropCenter = vectorCreate(center.x * imageSize.width, center.y * imageSize.height);
        // const imageCenter = sizeCenter(imageSize);
        // const imageCenterToCropCenter = vectorCreate(
        //     imageCenter.x - cropCenter.x,
        //     imageCenter.y - cropCenter.y
        // );
        // get rotated image center
        const rotatedImageSize = getImageTransformedRect(imageSize, rotation);
        const rotatedImageCenter = sizeCenter(rotatedImageSize);
        const rotatedImageOffset = vectorCreate((rotatedImageSize.width - imageSize.width) * 0.5, (rotatedImageSize.height - imageSize.height) * 0.5);
        // window.draw(rotatedImageCenter, 'red');
        // window.draw(rotatedImageSize, 'red');
        // window.draw(
        //     {
        //         x: rotatedImageOffset.x,
        //         y: rotatedImageOffset.y,
        //         width: imageSize.width,
        //         height: imageSize.height,
        //     },
        //     'cyan'
        // );
        // const imagePoints = rectRotate(
        //     rectCreate(
        //         rotatedImageOffset.x,
        //         rotatedImageOffset.y,
        //         imageSize.width,
        //         imageSize.height
        //     ),
        //     rotation
        // );
        // window.draw(imagePoints, 'orange');
        const cropPoints = rectRotate({
            x: rotatedImageOffset.x + cropCenter.x - (canvasSize.width / scalar) * 0.5,
            y: rotatedImageOffset.y + cropCenter.y - (canvasSize.height / scalar) * 0.5,
            width: canvasSize.width / scalar,
            height: canvasSize.height / scalar,
        }, rotation);
        // window.draw(cropPoints, 'cyan');
        const cropBoundsPoints = vectorsRotate(cropPoints.map(vectorClone), rotation, rotatedImageCenter.x, rotatedImageCenter.y);
        // window.draw(cropBoundsPoints, 'orange');
        const cropBoundsPointsCenter = rectCenter(rectCreateFromPoints(cropBoundsPoints));
        // window.draw(cropBoundsPointsCenter, 'green');
        const deRotatedCropBoundsPoints = vectorsRotate(cropBoundsPoints.map(vectorClone), -(rotation * 2), cropBoundsPointsCenter.x, cropBoundsPointsCenter.y);
        // window.draw(deRotatedCropBoundsPoints, 'green');
        res.crop = rectCreateFromPoints(deRotatedCropBoundsPoints);
    }
    else if (zoom != null) {
        rectScale(res.crop, 1 / zoom);
    }
    if (!cropLimit) {
        res.cropLimitToImage = false;
    }
    return res;
};
//#endregion
const isLegacyData = (data = {}) => {
    if ('markup' in data || 'color' in data || 'filter' in data)
        return true;
    const { crop } = data;
    if (crop && ('flip' in crop || 'center' in crop || 'aspectRatio' in crop || 'rotation' in crop))
        return true;
    return false;
};
var legacyDataToImageState = (editor, imageSize, data = {}) => {
    const res = {};
    // test if isn't legacy data
    if (!isLegacyData(data))
        return data;
    if (data.crop) {
        Object.assign(res, imagePropertiesFromLegacyCrop(imageSize, data.crop));
    }
    if (data.markup) {
        res.decoration = data.markup
            .map((markup) => shapeFromLegacyMarkup(res.crop || imageSize, markup[0], markup[1]))
            .sort((a, b) => {
            if (a.zIndex < b.zIndex)
                return -1;
            if (a.zIndex > b.zIndex)
                return 1;
            return 0;
        })
            .map((shape) => {
            delete shape.zIndex;
            return shape;
        });
    }
    if (data.color) {
        res.colorMatrix = {
            brightness: data.color.brightness.matrix,
            contrast: data.color.contrast.matrix,
            exposure: data.color.exposure.matrix,
            saturation: data.color.saturation.matrix,
        };
    }
    if (data.filter) {
        if (!res.colorMatrix)
            res.colorMatrix = {};
        res.colorMatrix.filter =
            typeof data.filter === 'string' && editor['filterFunctions'][data.filter]
                ? editor['filterFunctions'][data.filter]()
                : data.filter.matrix;
    }
    return res;
};

const isOperaMini = () => Object.prototype.toString.call(window['operamini']) === '[object OperaMini]';
const hasPromises = () => 'Promise' in window;
const hasCreateObjectURL = () => 'URL' in window && 'createObjectURL' in window.URL;
const hasVisibility = () => 'visibilityState' in document;
const hasTiming = () => 'performance' in window; // iOS 8.x
const hasFileConstructor = () => 'File' in window; // excludes IE11
let result$9 = null;
var isModernBrowser = () => {
    if (result$9 === null)
        result$9 =
            isBrowser() &&
                // Can't run on Opera Mini due to lack of everything
                !isOperaMini() &&
                // Require these APIs to feature detect a modern browser
                hasVisibility() &&
                hasPromises() &&
                hasFileConstructor() &&
                hasCreateObjectURL() &&
                hasTiming();
    return result$9;
};

/* src/native/ui/components/Measure.svelte generated by Svelte v3.29.4 */

function create_fragment$c(ctx) {
	let div;
	let measurable_action;
	let current;
	let mounted;
	let dispose;
	const default_slot_template = /*#slots*/ ctx[3].default;
	const default_slot = create_slot(default_slot_template, ctx, /*$$scope*/ ctx[2], null);

	return {
		c() {
			div = element("div");
			if (default_slot) default_slot.c();
			attr(div, "class", /*klass*/ ctx[0]);
		},
		m(target, anchor) {
			insert(target, div, anchor);

			if (default_slot) {
				default_slot.m(div, null);
			}

			current = true;

			if (!mounted) {
				dispose = [
					listen(div, "measure", /*handleResize*/ ctx[1]),
					action_destroyer(measurable_action = measurable.call(null, div))
				];

				mounted = true;
			}
		},
		p(ctx, [dirty]) {
			if (default_slot) {
				if (default_slot.p && dirty & /*$$scope*/ 4) {
					update_slot(default_slot, default_slot_template, ctx, /*$$scope*/ ctx[2], dirty, null, null);
				}
			}

			if (!current || dirty & /*klass*/ 1) {
				attr(div, "class", /*klass*/ ctx[0]);
			}
		},
		i(local) {
			if (current) return;
			transition_in(default_slot, local);
			current = true;
		},
		o(local) {
			transition_out(default_slot, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			if (default_slot) default_slot.d(detaching);
			mounted = false;
			run_all(dispose);
		}
	};
}

function instance$c($$self, $$props, $$invalidate) {
	let { $$slots: slots = {}, $$scope } = $$props;
	const dispatch = createEventDispatcher();
	let { class: klass = null } = $$props;
	const handleResize = ({ detail }) => dispatch("measure", detail);

	$$self.$$set = $$props => {
		if ("class" in $$props) $$invalidate(0, klass = $$props.class);
		if ("$$scope" in $$props) $$invalidate(2, $$scope = $$props.$$scope);
	};

	return [klass, handleResize, $$scope, slots];
}

class Measure extends SvelteComponent {
	constructor(options) {
		super();
		init(this, options, instance$c, create_fragment$c, safe_not_equal, { class: 0 });
	}
}

/* src/native/ui/components/Util.svelte generated by Svelte v3.29.4 */
const get_footer_slot_changes = dirty => ({});
const get_footer_slot_context = ctx => ({});
const get_main_slot_changes = dirty => ({});
const get_main_slot_context = ctx => ({});
const get_header_slot_changes = dirty => ({});
const get_header_slot_context = ctx => ({});

// (159:0) {#if hasHeader}
function create_if_block_2$1(ctx) {
	let div;
	let current;
	const header_slot_template = /*#slots*/ ctx[4].header;
	const header_slot = create_slot(header_slot_template, ctx, /*$$scope*/ ctx[3], get_header_slot_context);

	return {
		c() {
			div = element("div");
			if (header_slot) header_slot.c();
			attr(div, "class", "DokaUtilHeader");
		},
		m(target, anchor) {
			insert(target, div, anchor);

			if (header_slot) {
				header_slot.m(div, null);
			}

			current = true;
		},
		p(ctx, dirty) {
			if (header_slot) {
				if (header_slot.p && dirty & /*$$scope*/ 8) {
					update_slot(header_slot, header_slot_template, ctx, /*$$scope*/ ctx[3], dirty, get_header_slot_changes, get_header_slot_context);
				}
			}
		},
		i(local) {
			if (current) return;
			transition_in(header_slot, local);
			current = true;
		},
		o(local) {
			transition_out(header_slot, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			if (header_slot) header_slot.d(detaching);
		}
	};
}

// (166:22) <Measure class="DokaStage" on:measure/>
function fallback_block$1(ctx) {
	let measure;
	let current;
	measure = new Measure({ props: { class: "DokaStage" } });
	measure.$on("measure", /*measure_handler*/ ctx[5]);

	return {
		c() {
			create_component(measure.$$.fragment);
		},
		m(target, anchor) {
			mount_component(measure, target, anchor);
			current = true;
		},
		p: noop$1,
		i(local) {
			if (current) return;
			transition_in(measure.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(measure.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(measure, detaching);
		}
	};
}

// (169:0) {#if hasFooter}
function create_if_block_1$2(ctx) {
	let div;
	let current;
	const footer_slot_template = /*#slots*/ ctx[4].footer;
	const footer_slot = create_slot(footer_slot_template, ctx, /*$$scope*/ ctx[3], get_footer_slot_context);

	return {
		c() {
			div = element("div");
			if (footer_slot) footer_slot.c();
			attr(div, "class", "DokaUtilFooter");
		},
		m(target, anchor) {
			insert(target, div, anchor);

			if (footer_slot) {
				footer_slot.m(div, null);
			}

			current = true;
		},
		p(ctx, dirty) {
			if (footer_slot) {
				if (footer_slot.p && dirty & /*$$scope*/ 8) {
					update_slot(footer_slot, footer_slot_template, ctx, /*$$scope*/ ctx[3], dirty, get_footer_slot_changes, get_footer_slot_context);
				}
			}
		},
		i(local) {
			if (current) return;
			transition_in(footer_slot, local);
			current = true;
		},
		o(local) {
			transition_out(footer_slot, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			if (footer_slot) footer_slot.d(detaching);
		}
	};
}

function create_fragment$d(ctx) {
	let t0;
	let div;
	let t1;
	let t2;
	let if_block2_anchor;
	let current;
	let if_block0 = /*hasHeader*/ ctx[1] && create_if_block_2$1(ctx);
	const main_slot_template = /*#slots*/ ctx[4].main;
	const main_slot = create_slot(main_slot_template, ctx, /*$$scope*/ ctx[3], get_main_slot_context);
	const main_slot_or_fallback = main_slot || fallback_block$1(ctx);
	let if_block1 = /*hasFooter*/ ctx[2] && create_if_block_1$2(ctx);
	let if_block2 = false ;

	return {
		c() {
			if (if_block0) if_block0.c();
			t0 = space();
			div = element("div");
			if (main_slot_or_fallback) main_slot_or_fallback.c();
			t1 = space();
			if (if_block1) if_block1.c();
			t2 = space();
			if_block2_anchor = empty();
			attr(div, "class", "DokaUtilMain");
		},
		m(target, anchor) {
			if (if_block0) if_block0.m(target, anchor);
			insert(target, t0, anchor);
			insert(target, div, anchor);

			if (main_slot_or_fallback) {
				main_slot_or_fallback.m(div, null);
			}

			/*div_binding*/ ctx[6](div);
			insert(target, t1, anchor);
			if (if_block1) if_block1.m(target, anchor);
			insert(target, t2, anchor);
			insert(target, if_block2_anchor, anchor);
			current = true;
		},
		p(ctx, [dirty]) {
			if (/*hasHeader*/ ctx[1]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty & /*hasHeader*/ 2) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_2$1(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(t0.parentNode, t0);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (main_slot) {
				if (main_slot.p && dirty & /*$$scope*/ 8) {
					update_slot(main_slot, main_slot_template, ctx, /*$$scope*/ ctx[3], dirty, get_main_slot_changes, get_main_slot_context);
				}
			}

			if (/*hasFooter*/ ctx[2]) {
				if (if_block1) {
					if_block1.p(ctx, dirty);

					if (dirty & /*hasFooter*/ 4) {
						transition_in(if_block1, 1);
					}
				} else {
					if_block1 = create_if_block_1$2(ctx);
					if_block1.c();
					transition_in(if_block1, 1);
					if_block1.m(t2.parentNode, t2);
				}
			} else if (if_block1) {
				group_outros();

				transition_out(if_block1, 1, 1, () => {
					if_block1 = null;
				});

				check_outros();
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);
			transition_in(main_slot_or_fallback, local);
			transition_in(if_block1);
			transition_in(if_block2);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			transition_out(main_slot_or_fallback, local);
			transition_out(if_block1);
			transition_out(if_block2);
			current = false;
		},
		d(detaching) {
			if (if_block0) if_block0.d(detaching);
			if (detaching) detach(t0);
			if (detaching) detach(div);
			if (main_slot_or_fallback) main_slot_or_fallback.d(detaching);
			/*div_binding*/ ctx[6](null);
			if (detaching) detach(t1);
			if (if_block1) if_block1.d(detaching);
			if (detaching) detach(t2);
			if (detaching) detach(if_block2_anchor);
		}
	};
}

function instance$d($$self, $$props, $$invalidate) {
	let { $$slots: slots = {}, $$scope } = $$props;
	let { hasHeader = !!$$props.$$slots.header } = $$props;
	let { hasFooter = !!$$props.$$slots.footer } = $$props;
	let { root = undefined } = $$props;

	function measure_handler(event) {
		bubble($$self, event);
	}

	function div_binding($$value) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			root = $$value;
			$$invalidate(0, root);
		});
	}

	$$self.$$set = $$new_props => {
		$$invalidate(7, $$props = assign(assign({}, $$props), exclude_internal_props($$new_props)));
		if ("hasHeader" in $$new_props) $$invalidate(1, hasHeader = $$new_props.hasHeader);
		if ("hasFooter" in $$new_props) $$invalidate(2, hasFooter = $$new_props.hasFooter);
		if ("root" in $$new_props) $$invalidate(0, root = $$new_props.root);
		if ("$$scope" in $$new_props) $$invalidate(3, $$scope = $$new_props.$$scope);
	};

	$$props = exclude_internal_props($$props);
	return [root, hasHeader, hasFooter, $$scope, slots, measure_handler, div_binding];
}

class Util extends SvelteComponent {
	constructor(options) {
		super();
		init(this, options, instance$d, create_fragment$d, safe_not_equal, { hasHeader: 1, hasFooter: 2, root: 0 });
	}
}

// if this is a non retina display snap to pixel
let fn = null;
var snapToPixel = (v) => {
    if (fn === null)
        fn = getDevicePixelRatio() === 1 ? (v) => Math.round(v) : (v) => v;
    return fn(v);
};

/* src/native/ui/components/Details.svelte generated by Svelte v3.29.4 */
const get_details_slot_changes = dirty => ({});
const get_details_slot_context = ctx => ({});
const get_label_slot_changes = dirty => ({});
const get_label_slot_context = ctx => ({});

// (191:4) <Button class={arrayJoin(['DokaDetailsButton', toggleClass])} onkeydown={handleButtonKeydown} onclick={handleClick}>
function create_default_slot$2(ctx) {
	let current;
	const label_slot_template = /*#slots*/ ctx[22].label;
	const label_slot = create_slot(label_slot_template, ctx, /*$$scope*/ ctx[27], get_label_slot_context);

	return {
		c() {
			if (label_slot) label_slot.c();
		},
		m(target, anchor) {
			if (label_slot) {
				label_slot.m(target, anchor);
			}

			current = true;
		},
		p(ctx, dirty) {
			if (label_slot) {
				if (label_slot.p && dirty[0] & /*$$scope*/ 134217728) {
					update_slot(label_slot, label_slot_template, ctx, /*$$scope*/ ctx[27], dirty, get_label_slot_changes, get_label_slot_context);
				}
			}
		},
		i(local) {
			if (current) return;
			transition_in(label_slot, local);
			current = true;
		},
		o(local) {
			transition_out(label_slot, local);
			current = false;
		},
		d(detaching) {
			if (label_slot) label_slot.d(detaching);
		}
	};
}

// (195:4) {#if isVisible}
function create_if_block_1$3(ctx) {
	let div;
	let div_class_value;
	let measure_action;
	let current;
	let mounted;
	let dispose;
	const details_slot_template = /*#slots*/ ctx[22].details;
	const details_slot = create_slot(details_slot_template, ctx, /*$$scope*/ ctx[27], get_details_slot_context);

	return {
		c() {
			div = element("div");
			if (details_slot) details_slot.c();
			attr(div, "tabindex", "-1");
			attr(div, "style", /*style*/ ctx[7]);
			attr(div, "class", div_class_value = arrayJoin(["DokaDetailsPanel", /*klass*/ ctx[1]]));
		},
		m(target, anchor) {
			insert(target, div, anchor);

			if (details_slot) {
				details_slot.m(div, null);
			}

			/*div_binding*/ ctx[23](div);
			current = true;

			if (!mounted) {
				dispose = [
					listen(div, "measure", /*measure_handler*/ ctx[24]),
					action_destroyer(measure_action = measurable.call(null, div))
				];

				mounted = true;
			}
		},
		p(ctx, dirty) {
			if (details_slot) {
				if (details_slot.p && dirty[0] & /*$$scope*/ 134217728) {
					update_slot(details_slot, details_slot_template, ctx, /*$$scope*/ ctx[27], dirty, get_details_slot_changes, get_details_slot_context);
				}
			}

			if (!current || dirty[0] & /*style*/ 128) {
				attr(div, "style", /*style*/ ctx[7]);
			}

			if (!current || dirty[0] & /*klass*/ 2 && div_class_value !== (div_class_value = arrayJoin(["DokaDetailsPanel", /*klass*/ ctx[1]]))) {
				attr(div, "class", div_class_value);
			}
		},
		i(local) {
			if (current) return;
			transition_in(details_slot, local);
			current = true;
		},
		o(local) {
			transition_out(details_slot, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			if (details_slot) details_slot.d(detaching);
			/*div_binding*/ ctx[23](null);
			mounted = false;
			run_all(dispose);
		}
	};
}

function create_fragment$e(ctx) {
	let t0;
	let div;
	let button;
	let t1;
	let div_class_value;
	let measure_action;
	let t2;
	let if_block1_anchor;
	let current;
	let mounted;
	let dispose;

	button = new Button({
			props: {
				class: arrayJoin(["DokaDetailsButton", /*toggleClass*/ ctx[0]]),
				onkeydown: /*handleButtonKeydown*/ ctx[14],
				onclick: /*handleClick*/ ctx[13],
				$$slots: { default: [create_default_slot$2] },
				$$scope: { ctx }
			}
		});

	let if_block0 = /*isVisible*/ ctx[6] && create_if_block_1$3(ctx);
	let if_block1 = false ;

	return {
		c() {
			t0 = space();
			div = element("div");
			create_component(button.$$.fragment);
			t1 = space();
			if (if_block0) if_block0.c();
			t2 = space();
			if_block1_anchor = empty();
			attr(div, "class", div_class_value = arrayJoin(["DokaDetails", /*klass*/ ctx[1]]));
		},
		m(target, anchor) {
			insert(target, t0, anchor);
			insert(target, div, anchor);
			mount_component(button, div, null);
			append(div, t1);
			if (if_block0) if_block0.m(div, null);
			/*div_binding_1*/ ctx[25](div);
			insert(target, t2, anchor);
			insert(target, if_block1_anchor, anchor);
			current = true;

			if (!mounted) {
				dispose = [
					listen(document.body, "pointerdown", function () {
						if (is_function(/*handleDown*/ ctx[8])) /*handleDown*/ ctx[8].apply(this, arguments);
					}),
					listen(document.body, "pointerup", function () {
						if (is_function(/*handleUp*/ ctx[9])) /*handleUp*/ ctx[9].apply(this, arguments);
					}),
					listen(div, "keydown", /*handleComponentKeydown*/ ctx[15]),
					listen(div, "measure", /*measure_handler_1*/ ctx[26]),
					action_destroyer(measure_action = measurable.call(null, div, { observePosition: true }))
				];

				mounted = true;
			}
		},
		p(new_ctx, dirty) {
			ctx = new_ctx;
			const button_changes = {};
			if (dirty[0] & /*toggleClass*/ 1) button_changes.class = arrayJoin(["DokaDetailsButton", /*toggleClass*/ ctx[0]]);

			if (dirty[0] & /*$$scope*/ 134217728) {
				button_changes.$$scope = { dirty, ctx };
			}

			button.$set(button_changes);

			if (/*isVisible*/ ctx[6]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty[0] & /*isVisible*/ 64) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_1$3(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(div, null);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (!current || dirty[0] & /*klass*/ 2 && div_class_value !== (div_class_value = arrayJoin(["DokaDetails", /*klass*/ ctx[1]]))) {
				attr(div, "class", div_class_value);
			}
		},
		i(local) {
			if (current) return;
			transition_in(button.$$.fragment, local);
			transition_in(if_block0);
			transition_in(if_block1);
			current = true;
		},
		o(local) {
			transition_out(button.$$.fragment, local);
			transition_out(if_block0);
			transition_out(if_block1);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(t0);
			if (detaching) detach(div);
			destroy_component(button);
			if (if_block0) if_block0.d();
			/*div_binding_1*/ ctx[25](null);
			if (detaching) detach(t2);
			if (detaching) detach(if_block1_anchor);
			mounted = false;
			run_all(dispose);
		}
	};
}

let panelMargin = 8;

function instance$e($$self, $$props, $$invalidate) {
	let $position;
	let $offset;
	let $opacity;
	let { $$slots: slots = {}, $$scope } = $$props;
	let { isActive = false } = $$props;
	let { viewBounds = undefined } = $$props;
	let { viewOffset = vectorCreateEmpty() } = $$props;
	let { portalOffset = vectorCreateEmpty() } = $$props;
	let { portal = undefined } = $$props;
	let { toggleClass = undefined } = $$props;
	let { onshow = ({ panel }) => panel.focus() } = $$props;
	let { class: klass = undefined } = $$props;
	let parentRect;
	let panelSize;
	let dir = vectorCreateEmpty();
	let element = undefined;
	let opacity = spring(0);
	component_subscribe($$self, opacity, value => $$invalidate(34, $opacity = value));
	const position = writable({ x: 0, y: 0 });
	component_subscribe($$self, position, value => $$invalidate(31, $position = value));
	const offset = spring(-5);
	component_subscribe($$self, offset, value => $$invalidate(33, $offset = value));

	const isTargetSelf = e => {
		return portal && portal.contains(e.target) || element.contains(e.target) || element === e.target;
	};

	let downOutsidePanel = false;

	// decide if to use portal
	let detailPanel;

	let trigger;

	// test keydown press to open
	const handleClick = e => {
		$$invalidate(30, trigger = e);
		$$invalidate(16, isActive = !isActive);
	};

	const handleButtonKeydown = e => {
		if (!(/down/i).test(e.key)) return;
		$$invalidate(16, isActive = true);
		$$invalidate(30, trigger = e);
	};

	const handleComponentKeydown = e => {
		if (!(/esc/i).test(e.key)) return;
		$$invalidate(16, isActive = false);
		element.querySelector(".DokaDetailsButton").focus();
	};

	// clean up panel if it was appended to a portal
	onDestroy(() => {
		if (!portal || !detailPanel) return;
		portal.removeChild(detailPanel);
	});

	function div_binding($$value) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			detailPanel = $$value;
			$$invalidate(5, detailPanel);
		});
	}

	const measure_handler = e => $$invalidate(3, panelSize = sizeCreateFromAny(e.detail));

	function div_binding_1($$value) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			element = $$value;
			$$invalidate(4, element);
		});
	}

	const measure_handler_1 = e => $$invalidate(2, parentRect = e.detail);

	$$self.$$set = $$props => {
		if ("isActive" in $$props) $$invalidate(16, isActive = $$props.isActive);
		if ("viewBounds" in $$props) $$invalidate(17, viewBounds = $$props.viewBounds);
		if ("viewOffset" in $$props) $$invalidate(18, viewOffset = $$props.viewOffset);
		if ("portalOffset" in $$props) $$invalidate(19, portalOffset = $$props.portalOffset);
		if ("portal" in $$props) $$invalidate(20, portal = $$props.portal);
		if ("toggleClass" in $$props) $$invalidate(0, toggleClass = $$props.toggleClass);
		if ("onshow" in $$props) $$invalidate(21, onshow = $$props.onshow);
		if ("class" in $$props) $$invalidate(1, klass = $$props.class);
		if ("$$scope" in $$props) $$invalidate(27, $$scope = $$props.$$scope);
	};

	let transform;
	let isVisible;
	let isAnimating;
	let style;
	let handleDown;
	let handleUp;

	$$self.$$.update = () => {
		if ($$self.$$.dirty[0] & /*isActive, downOutsidePanel*/ 536936448) {
			 $$invalidate(9, handleUp = isActive
			? e => {
					if (!downOutsidePanel) return;
					$$invalidate(29, downOutsidePanel = false);
					if (isTargetSelf(e)) return;
					$$invalidate(16, isActive = false);
				}
			: undefined);
		}

		if ($$self.$$.dirty[0] & /*isActive*/ 65536) {
			 opacity.set(isActive ? 1 : 0);
		}

		if ($$self.$$.dirty[0] & /*isActive*/ 65536) {
			 offset.set(isActive ? 0 : -5);
		}

		if ($$self.$$.dirty[0] & /*parentRect, panelSize, viewBounds, viewOffset, portalOffset*/ 917516) {
			 if (parentRect && panelSize && viewBounds) {
				// parentRect is position of parent container on page
				// viewOffset is position in parent view (for when can't position with offsetRect)
				// panelSize is size of panel view, used for centering panel
				// portalOffset is offset of portal relative to viewBounds
				// render to right and bottom of parent by default
				let x = viewOffset.x + parentRect.x + parentRect.width;

				let y = viewOffset.y + parentRect.y + parentRect.height;
				const panelRight = x + panelSize.width;
				const panelBottom = y + panelSize.height;
				const viewLeft = viewBounds.x;
				const viewTop = viewBounds.y;
				const viewRight = viewBounds.x + viewBounds.width;
				const viewBottom = viewBounds.y + viewBounds.height;

				// for now always center horizontally
				$$invalidate(28, dir.x = 0, dir);

				x -= parentRect.width * 0.5 + panelSize.width * 0.5;

				// would move out of view on the left
				if (panelSize.width / viewBounds.width > 0.75) {
					x = viewLeft + viewBounds.width * 0.5 - panelSize.width * 0.5;
				} else {
					if (x < viewLeft) {
						x = viewLeft;
					}

					// would move out of view on the right
					if (x + panelSize.width > viewRight) {
						x = viewRight - panelSize.width;
					}
				}

				if (panelBottom > viewBottom) {
					$$invalidate(28, dir.y = -1, dir);

					// doesn't fit vertically, push up
					// const totalHeightSmallerThanView = panelSize.height + parentRect.height < viewBounds.height;
					const topOffset = y - (panelSize.height + parentRect.height);

					const fitsAboveParent = topOffset > viewTop;

					if (fitsAboveParent) {
						// move to top of parent
						y -= panelSize.height + parentRect.height;
					} else {
						// overlap with button
						y -= panelBottom - viewBottom;
					}
				} else {
					$$invalidate(28, dir.y = 1, dir);
				}

				set_store_value(position, $position = vectorApply(vectorCreate(x - portalOffset.x, y - portalOffset.y), snapToPixel), $position);
			}
		}

		if ($$self.$$.dirty[0] & /*dir*/ 268435456 | $$self.$$.dirty[1] & /*$position, $offset*/ 5) {
			 $$invalidate(32, transform = `translateX(${$position.x + dir.x * panelMargin}px) translateY(${$position.y + dir.y * panelMargin + dir.y * $offset}px)`);
		}

		if ($$self.$$.dirty[1] & /*$opacity*/ 8) {
			 $$invalidate(6, isVisible = $opacity > 0);
		}

		if ($$self.$$.dirty[1] & /*$opacity*/ 8) {
			 $$invalidate(35, isAnimating = $opacity < 1);
		}

		if ($$self.$$.dirty[1] & /*isAnimating, $opacity, transform*/ 26) {
			 $$invalidate(7, style = isAnimating
			? `opacity: ${$opacity}; pointer-events: ${$opacity < 1 ? "none" : "all"}; transform: ${transform};`
			: `transform: ${transform}`);
		}

		if ($$self.$$.dirty[0] & /*isActive*/ 65536) {
			 $$invalidate(8, handleDown = isActive
			? e => {
					if (isTargetSelf(e)) return;
					$$invalidate(29, downOutsidePanel = true);
				}
			: undefined);
		}

		if ($$self.$$.dirty[0] & /*isVisible, portal, detailPanel*/ 1048672) {
			 if (isVisible && portal && detailPanel && detailPanel.parentNode !== portal) {
				portal.appendChild(detailPanel);
			}
		}

		if ($$self.$$.dirty[0] & /*isActive*/ 65536) {
			 if (!isActive) {
				$$invalidate(30, trigger = undefined);
			}
		}

		if ($$self.$$.dirty[0] & /*isVisible, detailPanel, onshow, trigger*/ 1075839072) {
			 if (isVisible && detailPanel) {
				onshow({ e: trigger, panel: detailPanel });
			}
		}
	};

	return [
		toggleClass,
		klass,
		parentRect,
		panelSize,
		element,
		detailPanel,
		isVisible,
		style,
		handleDown,
		handleUp,
		opacity,
		position,
		offset,
		handleClick,
		handleButtonKeydown,
		handleComponentKeydown,
		isActive,
		viewBounds,
		viewOffset,
		portalOffset,
		portal,
		onshow,
		slots,
		div_binding,
		measure_handler,
		div_binding_1,
		measure_handler_1,
		$$scope
	];
}

class Details extends SvelteComponent {
	constructor(options) {
		super();

		init(
			this,
			options,
			instance$e,
			create_fragment$e,
			safe_not_equal,
			{
				isActive: 16,
				viewBounds: 17,
				viewOffset: 18,
				portalOffset: 19,
				portal: 20,
				toggleClass: 0,
				onshow: 21,
				class: 1
			},
			[-1, -1]
		);
	}
}

/* src/native/ui/components/RadioItem.svelte generated by Svelte v3.29.4 */

function create_fragment$f(ctx) {
	let li;
	let input;
	let t;
	let label_1;
	let li_class_value;
	let current;
	let mounted;
	let dispose;
	const default_slot_template = /*#slots*/ ctx[14].default;
	const default_slot = create_slot(default_slot_template, ctx, /*$$scope*/ ctx[13], null);

	return {
		c() {
			li = element("li");
			input = element("input");
			t = space();
			label_1 = element("label");
			if (default_slot) default_slot.c();
			attr(input, "type", "radio");
			attr(input, "class", "implicit");
			attr(input, "id", /*inputId*/ ctx[6]);
			attr(input, "name", /*name*/ ctx[0]);
			input.value = /*value*/ ctx[3];
			input.disabled = /*disabled*/ ctx[5];
			input.checked = /*checked*/ ctx[4];
			attr(label_1, "for", /*inputId*/ ctx[6]);
			attr(label_1, "title", /*label*/ ctx[2]);
			attr(li, "class", li_class_value = arrayJoin(["DokaRadioGroupOption", /*klass*/ ctx[1]]));
			attr(li, "data-disabled", /*disabled*/ ctx[5]);
			attr(li, "data-selected", /*checked*/ ctx[4]);
		},
		m(target, anchor) {
			insert(target, li, anchor);
			append(li, input);
			append(li, t);
			append(li, label_1);

			if (default_slot) {
				default_slot.m(label_1, null);
			}

			current = true;

			if (!mounted) {
				dispose = [
					listen(input, "change", stop_propagation(/*change_handler*/ ctx[15])),
					listen(input, "keydown", /*handleKeydown*/ ctx[8]),
					listen(input, "click", /*handleClick*/ ctx[9])
				];

				mounted = true;
			}
		},
		p(ctx, [dirty]) {
			if (!current || dirty & /*inputId*/ 64) {
				attr(input, "id", /*inputId*/ ctx[6]);
			}

			if (!current || dirty & /*name*/ 1) {
				attr(input, "name", /*name*/ ctx[0]);
			}

			if (!current || dirty & /*value*/ 8) {
				input.value = /*value*/ ctx[3];
			}

			if (!current || dirty & /*disabled*/ 32) {
				input.disabled = /*disabled*/ ctx[5];
			}

			if (!current || dirty & /*checked*/ 16) {
				input.checked = /*checked*/ ctx[4];
			}

			if (default_slot) {
				if (default_slot.p && dirty & /*$$scope*/ 8192) {
					update_slot(default_slot, default_slot_template, ctx, /*$$scope*/ ctx[13], dirty, null, null);
				}
			}

			if (!current || dirty & /*inputId*/ 64) {
				attr(label_1, "for", /*inputId*/ ctx[6]);
			}

			if (!current || dirty & /*label*/ 4) {
				attr(label_1, "title", /*label*/ ctx[2]);
			}

			if (!current || dirty & /*klass*/ 2 && li_class_value !== (li_class_value = arrayJoin(["DokaRadioGroupOption", /*klass*/ ctx[1]]))) {
				attr(li, "class", li_class_value);
			}

			if (!current || dirty & /*disabled*/ 32) {
				attr(li, "data-disabled", /*disabled*/ ctx[5]);
			}

			if (!current || dirty & /*checked*/ 16) {
				attr(li, "data-selected", /*checked*/ ctx[4]);
			}
		},
		i(local) {
			if (current) return;
			transition_in(default_slot, local);
			current = true;
		},
		o(local) {
			transition_out(default_slot, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(li);
			if (default_slot) default_slot.d(detaching);
			mounted = false;
			run_all(dispose);
		}
	};
}

function instance$f($$self, $$props, $$invalidate) {
	let $keysPressedStored;
	let { $$slots: slots = {}, $$scope } = $$props;
	let { name } = $$props;
	let { class: klass = undefined } = $$props;
	let { label } = $$props;
	let { id } = $$props;
	let { value } = $$props;
	let { checked } = $$props;
	let { onkeydown } = $$props;
	let { onclick } = $$props;
	let { disabled = false } = $$props;
	const keysPressedStored = getContext("keysPressed");
	component_subscribe($$self, keysPressedStored, value => $$invalidate(16, $keysPressedStored = value));

	const handleKeydown = e => {
		onkeydown(e);
	};

	const handleClick = e => {
		if ($keysPressedStored.length) return;
		onclick(e);
	};

	function change_handler(event) {
		bubble($$self, event);
	}

	$$self.$$set = $$props => {
		if ("name" in $$props) $$invalidate(0, name = $$props.name);
		if ("class" in $$props) $$invalidate(1, klass = $$props.class);
		if ("label" in $$props) $$invalidate(2, label = $$props.label);
		if ("id" in $$props) $$invalidate(10, id = $$props.id);
		if ("value" in $$props) $$invalidate(3, value = $$props.value);
		if ("checked" in $$props) $$invalidate(4, checked = $$props.checked);
		if ("onkeydown" in $$props) $$invalidate(11, onkeydown = $$props.onkeydown);
		if ("onclick" in $$props) $$invalidate(12, onclick = $$props.onclick);
		if ("disabled" in $$props) $$invalidate(5, disabled = $$props.disabled);
		if ("$$scope" in $$props) $$invalidate(13, $$scope = $$props.$$scope);
	};

	let inputId;

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*name, id*/ 1025) {
			 $$invalidate(6, inputId = `${name}-${id}`);
		}
	};

	return [
		name,
		klass,
		label,
		value,
		checked,
		disabled,
		inputId,
		keysPressedStored,
		handleKeydown,
		handleClick,
		id,
		onkeydown,
		onclick,
		$$scope,
		slots,
		change_handler
	];
}

class RadioItem extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$f, create_fragment$f, safe_not_equal, {
			name: 0,
			class: 1,
			label: 2,
			id: 10,
			value: 3,
			checked: 4,
			onkeydown: 11,
			onclick: 12,
			disabled: 5
		});
	}
}

var flattenOptions = (options = []) => options.reduce((prev, current) => {
    const isGroup = isArray(current) ? isArray(current[1]) : !!current.options;
    if (isGroup) {
        return prev.concat(isArray(current) ? current[1] : current.options);
    }
    prev.push(current);
    return prev;
}, []);

const mapOption = (option, index, optionMapper) => {
    let mappedOption;
    if (isArray(option)) {
        mappedOption = {
            id: index,
            value: option[0],
            label: option[1],
            ...(option[2] || {}),
        };
    }
    else {
        mappedOption = option;
        mappedOption.id = mappedOption.id != null ? mappedOption.id : index;
    }
    return optionMapper ? optionMapper(mappedOption) : mappedOption;
};
var mapOptions = (options = [], optionMapper) => {
    let index = 0;
    return options.map((option) => {
        index++;
        if (isArray(option)) {
            // is either [label, options] or [value, label]
            if (isArray(option[1])) {
                return {
                    id: index,
                    label: option[0],
                    options: option[1].map((option) => mapOption(option, ++index, optionMapper)),
                };
            }
            return mapOption(option, index, optionMapper);
        }
        else {
            // is either { id?, label, options } or { id?, value, label }
            if (option.options) {
                return {
                    id: option.id || index,
                    label: option.label,
                    options: option.options.map((option) => mapOption(option, ++index, optionMapper)),
                };
            }
            return mapOption(option, index, optionMapper);
        }
    });
};

var opop = (fn, ...args) => fn && fn(...args);

const localizeOptions = (options, locale) => options.map(([value, label, props]) => {
    if (isArray(label)) {
        return [localize(value, locale), localizeOptions(label, locale)];
    }
    else {
        const res = [value, localize(label, locale)];
        if (props && props.icon) {
            res.push({
                ...props,
                icon: localize(props.icon, locale),
            });
        }
        return res;
    }
});
var localizeOptions$1 = (options, locale) => localizeOptions(options, locale);

/* src/native/ui/components/RadioGroup.svelte generated by Svelte v3.29.4 */

const get_option_slot_changes_1 = dirty => ({
	option: dirty[0] & /*mappedOptions*/ 2048
});

const get_option_slot_context_1 = ctx => ({ option: /*option*/ ctx[27] });

const get_option_slot_changes = dirty => ({
	option: dirty[0] & /*mappedOptions*/ 2048
});

const get_option_slot_context = ctx => ({ option: /*option*/ ctx[27] });

function get_each_context_1$1(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[27] = list[i];
	return child_ctx;
}

const get_group_slot_changes = dirty => ({
	option: dirty[0] & /*mappedOptions*/ 2048
});

const get_group_slot_context = ctx => ({ option: /*option*/ ctx[27] });

function get_each_context$3(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[27] = list[i];
	return child_ctx;
}

// (83:0) {#if localizedOptions.length}
function create_if_block_1$4(ctx) {
	let fieldset;
	let t;
	let ul;
	let each_blocks = [];
	let each_1_lookup = new Map();
	let fieldset_class_value;
	let current;
	let if_block = /*label*/ ctx[1] && create_if_block_7$1(ctx);
	let each_value = /*mappedOptions*/ ctx[11];
	const get_key = ctx => /*option*/ ctx[27].id;

	for (let i = 0; i < each_value.length; i += 1) {
		let child_ctx = get_each_context$3(ctx, each_value, i);
		let key = get_key(child_ctx);
		each_1_lookup.set(key, each_blocks[i] = create_each_block$3(key, child_ctx));
	}

	return {
		c() {
			fieldset = element("fieldset");
			if (if_block) if_block.c();
			t = space();
			ul = element("ul");

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			attr(ul, "class", "DokaRadioGroupOptions");
			attr(fieldset, "class", fieldset_class_value = arrayJoin(["DokaRadioGroup", /*klass*/ ctx[3]]));
			attr(fieldset, "data-layout", /*layout*/ ctx[5]);
			attr(fieldset, "title", /*title*/ ctx[7]);
		},
		m(target, anchor) {
			insert(target, fieldset, anchor);
			if (if_block) if_block.m(fieldset, null);
			append(fieldset, t);
			append(fieldset, ul);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].m(ul, null);
			}

			current = true;
		},
		p(ctx, dirty) {
			if (/*label*/ ctx[1]) {
				if (if_block) {
					if_block.p(ctx, dirty);
				} else {
					if_block = create_if_block_7$1(ctx);
					if_block.c();
					if_block.m(fieldset, t);
				}
			} else if (if_block) {
				if_block.d(1);
				if_block = null;
			}

			if (dirty[0] & /*optionGroupClass, mappedOptions, name, optionClass, getOptionIndex, selectedIndex, handleRadioKeydown, handleRadioClick, optionLabelClass, $$scope*/ 4225873) {
				const each_value = /*mappedOptions*/ ctx[11];
				group_outros();
				each_blocks = update_keyed_each(each_blocks, dirty, get_key, 1, ctx, each_value, each_1_lookup, ul, outro_and_destroy_block, create_each_block$3, null, get_each_context$3);
				check_outros();
			}

			if (!current || dirty[0] & /*klass*/ 8 && fieldset_class_value !== (fieldset_class_value = arrayJoin(["DokaRadioGroup", /*klass*/ ctx[3]]))) {
				attr(fieldset, "class", fieldset_class_value);
			}

			if (!current || dirty[0] & /*layout*/ 32) {
				attr(fieldset, "data-layout", /*layout*/ ctx[5]);
			}

			if (!current || dirty[0] & /*title*/ 128) {
				attr(fieldset, "title", /*title*/ ctx[7]);
			}
		},
		i(local) {
			if (current) return;

			for (let i = 0; i < each_value.length; i += 1) {
				transition_in(each_blocks[i]);
			}

			current = true;
		},
		o(local) {
			for (let i = 0; i < each_blocks.length; i += 1) {
				transition_out(each_blocks[i]);
			}

			current = false;
		},
		d(detaching) {
			if (detaching) detach(fieldset);
			if (if_block) if_block.d();

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].d();
			}
		}
	};
}

// (85:4) {#if label}
function create_if_block_7$1(ctx) {
	let legend;
	let t;
	let legend_class_value;

	return {
		c() {
			legend = element("legend");
			t = text(/*label*/ ctx[1]);
			attr(legend, "class", legend_class_value = /*hideLabel*/ ctx[2] && "implicit");
		},
		m(target, anchor) {
			insert(target, legend, anchor);
			append(legend, t);
		},
		p(ctx, dirty) {
			if (dirty[0] & /*label*/ 2) set_data(t, /*label*/ ctx[1]);

			if (dirty[0] & /*hideLabel*/ 4 && legend_class_value !== (legend_class_value = /*hideLabel*/ ctx[2] && "implicit")) {
				attr(legend, "class", legend_class_value);
			}
		},
		d(detaching) {
			if (detaching) detach(legend);
		}
	};
}

// (115:8) {:else}
function create_else_block$1(ctx) {
	let radioitem;
	let current;

	radioitem = new RadioItem({
			props: {
				name: /*name*/ ctx[4],
				label: /*option*/ ctx[27].label,
				id: /*option*/ ctx[27].id,
				value: /*option*/ ctx[27].value,
				disabled: /*option*/ ctx[27].disabled,
				class: /*optionClass*/ ctx[8],
				checked: /*getOptionIndex*/ ctx[12](/*option*/ ctx[27]) === /*selectedIndex*/ ctx[0],
				onkeydown: /*handleRadioKeydown*/ ctx[13](/*option*/ ctx[27]),
				onclick: /*handleRadioClick*/ ctx[14](/*option*/ ctx[27]),
				$$slots: { default: [create_default_slot_2$1] },
				$$scope: { ctx }
			}
		});

	return {
		c() {
			create_component(radioitem.$$.fragment);
		},
		m(target, anchor) {
			mount_component(radioitem, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const radioitem_changes = {};
			if (dirty[0] & /*name*/ 16) radioitem_changes.name = /*name*/ ctx[4];
			if (dirty[0] & /*mappedOptions*/ 2048) radioitem_changes.label = /*option*/ ctx[27].label;
			if (dirty[0] & /*mappedOptions*/ 2048) radioitem_changes.id = /*option*/ ctx[27].id;
			if (dirty[0] & /*mappedOptions*/ 2048) radioitem_changes.value = /*option*/ ctx[27].value;
			if (dirty[0] & /*mappedOptions*/ 2048) radioitem_changes.disabled = /*option*/ ctx[27].disabled;
			if (dirty[0] & /*optionClass*/ 256) radioitem_changes.class = /*optionClass*/ ctx[8];
			if (dirty[0] & /*mappedOptions, selectedIndex*/ 2049) radioitem_changes.checked = /*getOptionIndex*/ ctx[12](/*option*/ ctx[27]) === /*selectedIndex*/ ctx[0];
			if (dirty[0] & /*mappedOptions*/ 2048) radioitem_changes.onkeydown = /*handleRadioKeydown*/ ctx[13](/*option*/ ctx[27]);
			if (dirty[0] & /*mappedOptions*/ 2048) radioitem_changes.onclick = /*handleRadioClick*/ ctx[14](/*option*/ ctx[27]);

			if (dirty[0] & /*$$scope, optionLabelClass, mappedOptions*/ 4196416) {
				radioitem_changes.$$scope = { dirty, ctx };
			}

			radioitem.$set(radioitem_changes);
		},
		i(local) {
			if (current) return;
			transition_in(radioitem.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(radioitem.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(radioitem, detaching);
		}
	};
}

// (90:8) {#if option.options}
function create_if_block_2$2(ctx) {
	let li;
	let t0;
	let ul;
	let each_blocks = [];
	let each_1_lookup = new Map();
	let t1;
	let li_class_value;
	let current;
	const group_slot_template = /*#slots*/ ctx[21].group;
	const group_slot = create_slot(group_slot_template, ctx, /*$$scope*/ ctx[22], get_group_slot_context);
	const group_slot_or_fallback = group_slot || fallback_block_1(ctx);
	let each_value_1 = /*option*/ ctx[27].options;
	const get_key = ctx => /*option*/ ctx[27].id;

	for (let i = 0; i < each_value_1.length; i += 1) {
		let child_ctx = get_each_context_1$1(ctx, each_value_1, i);
		let key = get_key(child_ctx);
		each_1_lookup.set(key, each_blocks[i] = create_each_block_1$1(key, child_ctx));
	}

	return {
		c() {
			li = element("li");
			if (group_slot_or_fallback) group_slot_or_fallback.c();
			t0 = space();
			ul = element("ul");

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			t1 = space();
			attr(ul, "class", "DokaRadioGroupOptions");
			attr(li, "class", li_class_value = arrayJoin(["DokaRadioGroupOptionGroup", /*optionGroupClass*/ ctx[9]]));
		},
		m(target, anchor) {
			insert(target, li, anchor);

			if (group_slot_or_fallback) {
				group_slot_or_fallback.m(li, null);
			}

			append(li, t0);
			append(li, ul);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].m(ul, null);
			}

			append(li, t1);
			current = true;
		},
		p(ctx, dirty) {
			if (group_slot) {
				if (group_slot.p && dirty[0] & /*$$scope, mappedOptions*/ 4196352) {
					update_slot(group_slot, group_slot_template, ctx, /*$$scope*/ ctx[22], dirty, get_group_slot_changes, get_group_slot_context);
				}
			} else {
				if (group_slot_or_fallback && group_slot_or_fallback.p && dirty[0] & /*mappedOptions*/ 2048) {
					group_slot_or_fallback.p(ctx, dirty);
				}
			}

			if (dirty[0] & /*name, mappedOptions, optionClass, getOptionIndex, selectedIndex, handleRadioKeydown, handleRadioClick, optionLabelClass, $$scope*/ 4225361) {
				const each_value_1 = /*option*/ ctx[27].options;
				group_outros();
				each_blocks = update_keyed_each(each_blocks, dirty, get_key, 1, ctx, each_value_1, each_1_lookup, ul, outro_and_destroy_block, create_each_block_1$1, null, get_each_context_1$1);
				check_outros();
			}

			if (!current || dirty[0] & /*optionGroupClass*/ 512 && li_class_value !== (li_class_value = arrayJoin(["DokaRadioGroupOptionGroup", /*optionGroupClass*/ ctx[9]]))) {
				attr(li, "class", li_class_value);
			}
		},
		i(local) {
			if (current) return;
			transition_in(group_slot_or_fallback, local);

			for (let i = 0; i < each_value_1.length; i += 1) {
				transition_in(each_blocks[i]);
			}

			current = true;
		},
		o(local) {
			transition_out(group_slot_or_fallback, local);

			for (let i = 0; i < each_blocks.length; i += 1) {
				transition_out(each_blocks[i]);
			}

			current = false;
		},
		d(detaching) {
			if (detaching) detach(li);
			if (group_slot_or_fallback) group_slot_or_fallback.d(detaching);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].d();
			}
		}
	};
}

// (126:16) {#if option.icon}
function create_if_block_6$1(ctx) {
	let icon;
	let current;

	icon = new Icon({
			props: {
				$$slots: { default: [create_default_slot_3$1] },
				$$scope: { ctx }
			}
		});

	return {
		c() {
			create_component(icon.$$.fragment);
		},
		m(target, anchor) {
			mount_component(icon, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const icon_changes = {};

			if (dirty[0] & /*$$scope, mappedOptions*/ 4196352) {
				icon_changes.$$scope = { dirty, ctx };
			}

			icon.$set(icon_changes);
		},
		i(local) {
			if (current) return;
			transition_in(icon.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(icon.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(icon, detaching);
		}
	};
}

// (127:16) <Icon>
function create_default_slot_3$1(ctx) {
	let g;
	let raw_value = /*option*/ ctx[27].icon + "";

	return {
		c() {
			g = svg_element("g");
		},
		m(target, anchor) {
			insert(target, g, anchor);
			g.innerHTML = raw_value;
		},
		p(ctx, dirty) {
			if (dirty[0] & /*mappedOptions*/ 2048 && raw_value !== (raw_value = /*option*/ ctx[27].icon + "")) g.innerHTML = raw_value;		},
		d(detaching) {
			if (detaching) detach(g);
		}
	};
}

// (129:16) {#if !option.hideLabel}
function create_if_block_5$1(ctx) {
	let span;
	let t_value = /*option*/ ctx[27].label + "";
	let t;

	return {
		c() {
			span = element("span");
			t = text(t_value);
			attr(span, "class", /*optionLabelClass*/ ctx[6]);
		},
		m(target, anchor) {
			insert(target, span, anchor);
			append(span, t);
		},
		p(ctx, dirty) {
			if (dirty[0] & /*mappedOptions*/ 2048 && t_value !== (t_value = /*option*/ ctx[27].label + "")) set_data(t, t_value);

			if (dirty[0] & /*optionLabelClass*/ 64) {
				attr(span, "class", /*optionLabelClass*/ ctx[6]);
			}
		},
		d(detaching) {
			if (detaching) detach(span);
		}
	};
}

// (125:76)                  
function fallback_block_2(ctx) {
	let t0;
	let t1;
	let current;
	let if_block0 = /*option*/ ctx[27].icon && create_if_block_6$1(ctx);
	let if_block1 = !/*option*/ ctx[27].hideLabel && create_if_block_5$1(ctx);

	return {
		c() {
			if (if_block0) if_block0.c();
			t0 = space();
			if (if_block1) if_block1.c();
			t1 = space();
		},
		m(target, anchor) {
			if (if_block0) if_block0.m(target, anchor);
			insert(target, t0, anchor);
			if (if_block1) if_block1.m(target, anchor);
			insert(target, t1, anchor);
			current = true;
		},
		p(ctx, dirty) {
			if (/*option*/ ctx[27].icon) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty[0] & /*mappedOptions*/ 2048) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_6$1(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(t0.parentNode, t0);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (!/*option*/ ctx[27].hideLabel) {
				if (if_block1) {
					if_block1.p(ctx, dirty);
				} else {
					if_block1 = create_if_block_5$1(ctx);
					if_block1.c();
					if_block1.m(t1.parentNode, t1);
				}
			} else if (if_block1) {
				if_block1.d(1);
				if_block1 = null;
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			current = false;
		},
		d(detaching) {
			if (if_block0) if_block0.d(detaching);
			if (detaching) detach(t0);
			if (if_block1) if_block1.d(detaching);
			if (detaching) detach(t1);
		}
	};
}

// (116:8) <RadioItem              {name}              label={option.label}             id={option.id}             value={option.value}             disabled={option.disabled}             class={optionClass}             checked={getOptionIndex(option) === selectedIndex}             onkeydown={handleRadioKeydown(option)}             onclick={handleRadioClick(option)}>
function create_default_slot_2$1(ctx) {
	let current;
	const option_slot_template = /*#slots*/ ctx[21].option;
	const option_slot = create_slot(option_slot_template, ctx, /*$$scope*/ ctx[22], get_option_slot_context_1);
	const option_slot_or_fallback = option_slot || fallback_block_2(ctx);

	return {
		c() {
			if (option_slot_or_fallback) option_slot_or_fallback.c();
		},
		m(target, anchor) {
			if (option_slot_or_fallback) {
				option_slot_or_fallback.m(target, anchor);
			}

			current = true;
		},
		p(ctx, dirty) {
			if (option_slot) {
				if (option_slot.p && dirty[0] & /*$$scope, mappedOptions*/ 4196352) {
					update_slot(option_slot, option_slot_template, ctx, /*$$scope*/ ctx[22], dirty, get_option_slot_changes_1, get_option_slot_context_1);
				}
			} else {
				if (option_slot_or_fallback && option_slot_or_fallback.p && dirty[0] & /*optionLabelClass, mappedOptions*/ 2112) {
					option_slot_or_fallback.p(ctx, dirty);
				}
			}
		},
		i(local) {
			if (current) return;
			transition_in(option_slot_or_fallback, local);
			current = true;
		},
		o(local) {
			transition_out(option_slot_or_fallback, local);
			current = false;
		},
		d(detaching) {
			if (option_slot_or_fallback) option_slot_or_fallback.d(detaching);
		}
	};
}

// (92:47) <span class="DokaRadioGroupOptionGroupLabel">
function fallback_block_1(ctx) {
	let span;
	let t_value = /*option*/ ctx[27].label + "";
	let t;

	return {
		c() {
			span = element("span");
			t = text(t_value);
			attr(span, "class", "DokaRadioGroupOptionGroupLabel");
		},
		m(target, anchor) {
			insert(target, span, anchor);
			append(span, t);
		},
		p(ctx, dirty) {
			if (dirty[0] & /*mappedOptions*/ 2048 && t_value !== (t_value = /*option*/ ctx[27].label + "")) set_data(t, t_value);
		},
		d(detaching) {
			if (detaching) detach(span);
		}
	};
}

// (105:24) {#if option.icon}
function create_if_block_4$1(ctx) {
	let icon;
	let current;

	icon = new Icon({
			props: {
				$$slots: { default: [create_default_slot_1$1] },
				$$scope: { ctx }
			}
		});

	return {
		c() {
			create_component(icon.$$.fragment);
		},
		m(target, anchor) {
			mount_component(icon, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const icon_changes = {};

			if (dirty[0] & /*$$scope, mappedOptions*/ 4196352) {
				icon_changes.$$scope = { dirty, ctx };
			}

			icon.$set(icon_changes);
		},
		i(local) {
			if (current) return;
			transition_in(icon.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(icon.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(icon, detaching);
		}
	};
}

// (106:24) <Icon>
function create_default_slot_1$1(ctx) {
	let g;
	let raw_value = /*option*/ ctx[27].icon + "";

	return {
		c() {
			g = svg_element("g");
		},
		m(target, anchor) {
			insert(target, g, anchor);
			g.innerHTML = raw_value;
		},
		p(ctx, dirty) {
			if (dirty[0] & /*mappedOptions*/ 2048 && raw_value !== (raw_value = /*option*/ ctx[27].icon + "")) g.innerHTML = raw_value;		},
		d(detaching) {
			if (detaching) detach(g);
		}
	};
}

// (108:24) {#if !option.hideLabel}
function create_if_block_3$1(ctx) {
	let span;
	let t_value = /*option*/ ctx[27].label + "";
	let t;

	return {
		c() {
			span = element("span");
			t = text(t_value);
			attr(span, "class", /*optionLabelClass*/ ctx[6]);
		},
		m(target, anchor) {
			insert(target, span, anchor);
			append(span, t);
		},
		p(ctx, dirty) {
			if (dirty[0] & /*mappedOptions*/ 2048 && t_value !== (t_value = /*option*/ ctx[27].label + "")) set_data(t, t_value);

			if (dirty[0] & /*optionLabelClass*/ 64) {
				attr(span, "class", /*optionLabelClass*/ ctx[6]);
			}
		},
		d(detaching) {
			if (detaching) detach(span);
		}
	};
}

// (104:84)                          
function fallback_block$2(ctx) {
	let t0;
	let t1;
	let current;
	let if_block0 = /*option*/ ctx[27].icon && create_if_block_4$1(ctx);
	let if_block1 = !/*option*/ ctx[27].hideLabel && create_if_block_3$1(ctx);

	return {
		c() {
			if (if_block0) if_block0.c();
			t0 = space();
			if (if_block1) if_block1.c();
			t1 = space();
		},
		m(target, anchor) {
			if (if_block0) if_block0.m(target, anchor);
			insert(target, t0, anchor);
			if (if_block1) if_block1.m(target, anchor);
			insert(target, t1, anchor);
			current = true;
		},
		p(ctx, dirty) {
			if (/*option*/ ctx[27].icon) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty[0] & /*mappedOptions*/ 2048) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_4$1(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(t0.parentNode, t0);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (!/*option*/ ctx[27].hideLabel) {
				if (if_block1) {
					if_block1.p(ctx, dirty);
				} else {
					if_block1 = create_if_block_3$1(ctx);
					if_block1.c();
					if_block1.m(t1.parentNode, t1);
				}
			} else if (if_block1) {
				if_block1.d(1);
				if_block1 = null;
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			current = false;
		},
		d(detaching) {
			if (if_block0) if_block0.d(detaching);
			if (detaching) detach(t0);
			if (if_block1) if_block1.d(detaching);
			if (detaching) detach(t1);
		}
	};
}

// (95:16) <RadioItem                      {name}                      label={option.label}                     id={option.id}                     value={option.value}                     disabled={option.disabled}                     class={optionClass}                     checked={getOptionIndex(option) === selectedIndex}                     onkeydown={handleRadioKeydown(option)}                     onclick={handleRadioClick(option)}>
function create_default_slot$3(ctx) {
	let current;
	const option_slot_template = /*#slots*/ ctx[21].option;
	const option_slot = create_slot(option_slot_template, ctx, /*$$scope*/ ctx[22], get_option_slot_context);
	const option_slot_or_fallback = option_slot || fallback_block$2(ctx);

	return {
		c() {
			if (option_slot_or_fallback) option_slot_or_fallback.c();
		},
		m(target, anchor) {
			if (option_slot_or_fallback) {
				option_slot_or_fallback.m(target, anchor);
			}

			current = true;
		},
		p(ctx, dirty) {
			if (option_slot) {
				if (option_slot.p && dirty[0] & /*$$scope, mappedOptions*/ 4196352) {
					update_slot(option_slot, option_slot_template, ctx, /*$$scope*/ ctx[22], dirty, get_option_slot_changes, get_option_slot_context);
				}
			} else {
				if (option_slot_or_fallback && option_slot_or_fallback.p && dirty[0] & /*optionLabelClass, mappedOptions*/ 2112) {
					option_slot_or_fallback.p(ctx, dirty);
				}
			}
		},
		i(local) {
			if (current) return;
			transition_in(option_slot_or_fallback, local);
			current = true;
		},
		o(local) {
			transition_out(option_slot_or_fallback, local);
			current = false;
		},
		d(detaching) {
			if (option_slot_or_fallback) option_slot_or_fallback.d(detaching);
		}
	};
}

// (94:16) {#each option.options as option (option.id) }
function create_each_block_1$1(key_1, ctx) {
	let first;
	let radioitem;
	let current;

	radioitem = new RadioItem({
			props: {
				name: /*name*/ ctx[4],
				label: /*option*/ ctx[27].label,
				id: /*option*/ ctx[27].id,
				value: /*option*/ ctx[27].value,
				disabled: /*option*/ ctx[27].disabled,
				class: /*optionClass*/ ctx[8],
				checked: /*getOptionIndex*/ ctx[12](/*option*/ ctx[27]) === /*selectedIndex*/ ctx[0],
				onkeydown: /*handleRadioKeydown*/ ctx[13](/*option*/ ctx[27]),
				onclick: /*handleRadioClick*/ ctx[14](/*option*/ ctx[27]),
				$$slots: { default: [create_default_slot$3] },
				$$scope: { ctx }
			}
		});

	return {
		key: key_1,
		first: null,
		c() {
			first = empty();
			create_component(radioitem.$$.fragment);
			this.first = first;
		},
		m(target, anchor) {
			insert(target, first, anchor);
			mount_component(radioitem, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const radioitem_changes = {};
			if (dirty[0] & /*name*/ 16) radioitem_changes.name = /*name*/ ctx[4];
			if (dirty[0] & /*mappedOptions*/ 2048) radioitem_changes.label = /*option*/ ctx[27].label;
			if (dirty[0] & /*mappedOptions*/ 2048) radioitem_changes.id = /*option*/ ctx[27].id;
			if (dirty[0] & /*mappedOptions*/ 2048) radioitem_changes.value = /*option*/ ctx[27].value;
			if (dirty[0] & /*mappedOptions*/ 2048) radioitem_changes.disabled = /*option*/ ctx[27].disabled;
			if (dirty[0] & /*optionClass*/ 256) radioitem_changes.class = /*optionClass*/ ctx[8];
			if (dirty[0] & /*mappedOptions, selectedIndex*/ 2049) radioitem_changes.checked = /*getOptionIndex*/ ctx[12](/*option*/ ctx[27]) === /*selectedIndex*/ ctx[0];
			if (dirty[0] & /*mappedOptions*/ 2048) radioitem_changes.onkeydown = /*handleRadioKeydown*/ ctx[13](/*option*/ ctx[27]);
			if (dirty[0] & /*mappedOptions*/ 2048) radioitem_changes.onclick = /*handleRadioClick*/ ctx[14](/*option*/ ctx[27]);

			if (dirty[0] & /*$$scope, optionLabelClass, mappedOptions*/ 4196416) {
				radioitem_changes.$$scope = { dirty, ctx };
			}

			radioitem.$set(radioitem_changes);
		},
		i(local) {
			if (current) return;
			transition_in(radioitem.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(radioitem.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(first);
			destroy_component(radioitem, detaching);
		}
	};
}

// (89:8) {#each mappedOptions as option (option.id) }
function create_each_block$3(key_1, ctx) {
	let first;
	let current_block_type_index;
	let if_block;
	let if_block_anchor;
	let current;
	const if_block_creators = [create_if_block_2$2, create_else_block$1];
	const if_blocks = [];

	function select_block_type(ctx, dirty) {
		if (/*option*/ ctx[27].options) return 0;
		return 1;
	}

	current_block_type_index = select_block_type(ctx);
	if_block = if_blocks[current_block_type_index] = if_block_creators[current_block_type_index](ctx);

	return {
		key: key_1,
		first: null,
		c() {
			first = empty();
			if_block.c();
			if_block_anchor = empty();
			this.first = first;
		},
		m(target, anchor) {
			insert(target, first, anchor);
			if_blocks[current_block_type_index].m(target, anchor);
			insert(target, if_block_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			let previous_block_index = current_block_type_index;
			current_block_type_index = select_block_type(ctx);

			if (current_block_type_index === previous_block_index) {
				if_blocks[current_block_type_index].p(ctx, dirty);
			} else {
				group_outros();

				transition_out(if_blocks[previous_block_index], 1, 1, () => {
					if_blocks[previous_block_index] = null;
				});

				check_outros();
				if_block = if_blocks[current_block_type_index];

				if (!if_block) {
					if_block = if_blocks[current_block_type_index] = if_block_creators[current_block_type_index](ctx);
					if_block.c();
				}

				transition_in(if_block, 1);
				if_block.m(if_block_anchor.parentNode, if_block_anchor);
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block);
			current = true;
		},
		o(local) {
			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(first);
			if_blocks[current_block_type_index].d(detaching);
			if (detaching) detach(if_block_anchor);
		}
	};
}

function create_fragment$g(ctx) {
	let t;
	let if_block1_anchor;
	let current;
	let if_block0 = /*localizedOptions*/ ctx[10].length && create_if_block_1$4(ctx);
	let if_block1 = false ;

	return {
		c() {
			if (if_block0) if_block0.c();
			t = space();
			if_block1_anchor = empty();
		},
		m(target, anchor) {
			if (if_block0) if_block0.m(target, anchor);
			insert(target, t, anchor);
			insert(target, if_block1_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			if (/*localizedOptions*/ ctx[10].length) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty[0] & /*localizedOptions*/ 1024) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_1$4(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(t.parentNode, t);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);
			transition_in(if_block1);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			transition_out(if_block1);
			current = false;
		},
		d(detaching) {
			if (if_block0) if_block0.d(detaching);
			if (detaching) detach(t);
			if (detaching) detach(if_block1_anchor);
		}
	};
}

function instance$g($$self, $$props, $$invalidate) {
	let { $$slots: slots = {}, $$scope } = $$props;
	const dispatch = createEventDispatcher();
	let { label = undefined } = $$props;
	let { hideLabel = true } = $$props;
	let { class: klass = undefined } = $$props;
	let { name = `doka-radio-group-${getUniqueId()}` } = $$props;
	let { selectedIndex = -1 } = $$props;
	let { options = [] } = $$props;
	let { onchange = undefined } = $$props;
	let { layout = undefined } = $$props;
	let { optionMapper = undefined } = $$props;
	let { optionFilter = undefined } = $$props;
	let { value = undefined } = $$props;
	let { optionLabelClass = undefined } = $$props;
	let { title = undefined } = $$props;
	let { locale = undefined } = $$props;
	let { optionClass = undefined } = $$props;
	let { optionGroupClass = undefined } = $$props;
	const getOptionIndex = option => flattenedOptions.findIndex(flattenedOption => flattenedOption.id === option.id);
	const isConfirm = key => (/enter| /i).test(key);

	const changeSelection = option => {
		$$invalidate(0, selectedIndex = getOptionIndex(option));
		const payload = { index: selectedIndex, ...option };
		opop(onchange, payload);
		dispatch("change", payload);
	};

	const handleRadioKeydown = option => e => {
		// is confirm key ([enter] or [space])
		if (!isConfirm(e.key)) return;

		changeSelection(option);
	};

	const handleRadioClick = option => e => {
		changeSelection(option);
	};

	$$self.$$set = $$props => {
		if ("label" in $$props) $$invalidate(1, label = $$props.label);
		if ("hideLabel" in $$props) $$invalidate(2, hideLabel = $$props.hideLabel);
		if ("class" in $$props) $$invalidate(3, klass = $$props.class);
		if ("name" in $$props) $$invalidate(4, name = $$props.name);
		if ("selectedIndex" in $$props) $$invalidate(0, selectedIndex = $$props.selectedIndex);
		if ("options" in $$props) $$invalidate(15, options = $$props.options);
		if ("onchange" in $$props) $$invalidate(16, onchange = $$props.onchange);
		if ("layout" in $$props) $$invalidate(5, layout = $$props.layout);
		if ("optionMapper" in $$props) $$invalidate(17, optionMapper = $$props.optionMapper);
		if ("optionFilter" in $$props) $$invalidate(18, optionFilter = $$props.optionFilter);
		if ("value" in $$props) $$invalidate(19, value = $$props.value);
		if ("optionLabelClass" in $$props) $$invalidate(6, optionLabelClass = $$props.optionLabelClass);
		if ("title" in $$props) $$invalidate(7, title = $$props.title);
		if ("locale" in $$props) $$invalidate(20, locale = $$props.locale);
		if ("optionClass" in $$props) $$invalidate(8, optionClass = $$props.optionClass);
		if ("optionGroupClass" in $$props) $$invalidate(9, optionGroupClass = $$props.optionGroupClass);
		if ("$$scope" in $$props) $$invalidate(22, $$scope = $$props.$$scope);
	};

	let localizedOptions;
	let mappedOptions;
	let flattenedOptions;

	$$self.$$.update = () => {
		if ($$self.$$.dirty[0] & /*optionFilter, options, locale*/ 1343488) {
			 $$invalidate(10, localizedOptions = localizeOptions$1(optionFilter ? options.filter(optionFilter) : options, locale));
		}

		if ($$self.$$.dirty[0] & /*localizedOptions, optionMapper*/ 132096) {
			 $$invalidate(11, mappedOptions = mapOptions(localizedOptions, optionMapper));
		}

		if ($$self.$$.dirty[0] & /*mappedOptions*/ 2048) {
			 $$invalidate(23, flattenedOptions = flattenOptions(mappedOptions));
		}

		if ($$self.$$.dirty[0] & /*value, selectedIndex, flattenedOptions*/ 8912897) {
			// can optionally pass value to have radio button group try to auto-select the right option
			 if (value && selectedIndex < 0) {
				$$invalidate(0, selectedIndex = flattenedOptions.findIndex(option => option.value === value));
			}
		}
	};

	return [
		selectedIndex,
		label,
		hideLabel,
		klass,
		name,
		layout,
		optionLabelClass,
		title,
		optionClass,
		optionGroupClass,
		localizedOptions,
		mappedOptions,
		getOptionIndex,
		handleRadioKeydown,
		handleRadioClick,
		options,
		onchange,
		optionMapper,
		optionFilter,
		value,
		locale,
		slots,
		$$scope
	];
}

class RadioGroup extends SvelteComponent {
	constructor(options) {
		super();

		init(
			this,
			options,
			instance$g,
			create_fragment$g,
			safe_not_equal,
			{
				label: 1,
				hideLabel: 2,
				class: 3,
				name: 4,
				selectedIndex: 0,
				options: 15,
				onchange: 16,
				layout: 5,
				optionMapper: 17,
				optionFilter: 18,
				value: 19,
				optionLabelClass: 6,
				title: 7,
				locale: 20,
				optionClass: 8,
				optionGroupClass: 9
			},
			[-1, -1]
		);
	}
}

/* src/native/ui/components/Dropdown.svelte generated by Svelte v3.29.4 */

function create_if_block_2$3(ctx) {
	let icon_1;
	let current;

	icon_1 = new Icon({
			props: {
				$$slots: { default: [create_default_slot_3$2] },
				$$scope: { ctx }
			}
		});

	return {
		c() {
			create_component(icon_1.$$.fragment);
		},
		m(target, anchor) {
			mount_component(icon_1, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const icon_1_changes = {};

			if (dirty & /*$$scope, icon*/ 268435520) {
				icon_1_changes.$$scope = { dirty, ctx };
			}

			icon_1.$set(icon_1_changes);
		},
		i(local) {
			if (current) return;
			transition_in(icon_1.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(icon_1.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(icon_1, detaching);
		}
	};
}

// (147:8) <Icon>
function create_default_slot_3$2(ctx) {
	let g;

	return {
		c() {
			g = svg_element("g");
		},
		m(target, anchor) {
			insert(target, g, anchor);
			g.innerHTML = /*icon*/ ctx[6];
		},
		p(ctx, dirty) {
			if (dirty & /*icon*/ 64) g.innerHTML = /*icon*/ ctx[6];		},
		d(detaching) {
			if (detaching) detach(g);
		}
	};
}

// (149:8) {#if !hideLabel}
function create_if_block_1$5(ctx) {
	let span;
	let t_value = (/*label*/ ctx[2] || /*selectedLabel*/ ctx[19]) + "";
	let t;
	let span_class_value;

	return {
		c() {
			span = element("span");
			t = text(t_value);
			attr(span, "class", span_class_value = arrayJoin(["DokaButtonLabel", /*labelClass*/ ctx[3]]));
		},
		m(target, anchor) {
			insert(target, span, anchor);
			append(span, t);
		},
		p(ctx, dirty) {
			if (dirty & /*label, selectedLabel*/ 524292 && t_value !== (t_value = (/*label*/ ctx[2] || /*selectedLabel*/ ctx[19]) + "")) set_data(t, t_value);

			if (dirty & /*labelClass*/ 8 && span_class_value !== (span_class_value = arrayJoin(["DokaButtonLabel", /*labelClass*/ ctx[3]]))) {
				attr(span, "class", span_class_value);
			}
		},
		d(detaching) {
			if (detaching) detach(span);
		}
	};
}

// (145:4) <span slot="label" title={localize(title, locale)} class={arrayJoin(['DokaButtonInner', innerClass])} >
function create_label_slot(ctx) {
	let span;
	let t;
	let span_title_value;
	let span_class_value;
	let current;
	let if_block0 = /*icon*/ ctx[6] && create_if_block_2$3(ctx);
	let if_block1 = !/*hideLabel*/ ctx[5] && create_if_block_1$5(ctx);

	return {
		c() {
			span = element("span");
			if (if_block0) if_block0.c();
			t = space();
			if (if_block1) if_block1.c();
			attr(span, "slot", "label");
			attr(span, "title", span_title_value = localize(/*title*/ ctx[1], /*locale*/ ctx[15]));
			attr(span, "class", span_class_value = arrayJoin(["DokaButtonInner", /*innerClass*/ ctx[4]]));
		},
		m(target, anchor) {
			insert(target, span, anchor);
			if (if_block0) if_block0.m(span, null);
			append(span, t);
			if (if_block1) if_block1.m(span, null);
			current = true;
		},
		p(ctx, dirty) {
			if (/*icon*/ ctx[6]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty & /*icon*/ 64) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_2$3(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(span, t);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (!/*hideLabel*/ ctx[5]) {
				if (if_block1) {
					if_block1.p(ctx, dirty);
				} else {
					if_block1 = create_if_block_1$5(ctx);
					if_block1.c();
					if_block1.m(span, null);
				}
			} else if (if_block1) {
				if_block1.d(1);
				if_block1 = null;
			}

			if (!current || dirty & /*title, locale*/ 32770 && span_title_value !== (span_title_value = localize(/*title*/ ctx[1], /*locale*/ ctx[15]))) {
				attr(span, "title", span_title_value);
			}

			if (!current || dirty & /*innerClass*/ 16 && span_class_value !== (span_class_value = arrayJoin(["DokaButtonInner", /*innerClass*/ ctx[4]]))) {
				attr(span, "class", span_class_value);
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(span);
			if (if_block0) if_block0.d();
			if (if_block1) if_block1.d();
		}
	};
}

// (166:12) <span slot="group">
function create_group_slot(ctx) {
	let span;
	let t_value = /*option*/ ctx[27].label + "";
	let t;

	return {
		c() {
			span = element("span");
			t = text(t_value);
			attr(span, "slot", "group");
		},
		m(target, anchor) {
			insert(target, span, anchor);
			append(span, t);
		},
		p(ctx, dirty) {
			if (dirty & /*option*/ 134217728 && t_value !== (t_value = /*option*/ ctx[27].label + "")) set_data(t, t_value);
		},
		d(detaching) {
			if (detaching) detach(span);
		}
	};
}

// (168:16) {#if option.icon}
function create_if_block$2(ctx) {
	let icon_1;
	let current;

	icon_1 = new Icon({
			props: {
				style: isFunction(/*optionIconStyle*/ ctx[13])
				? /*optionIconStyle*/ ctx[13](/*option*/ ctx[27].value)
				: /*optionIconStyle*/ ctx[13],
				$$slots: { default: [create_default_slot_2$2] },
				$$scope: { ctx }
			}
		});

	return {
		c() {
			create_component(icon_1.$$.fragment);
		},
		m(target, anchor) {
			mount_component(icon_1, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const icon_1_changes = {};

			if (dirty & /*optionIconStyle, option*/ 134225920) icon_1_changes.style = isFunction(/*optionIconStyle*/ ctx[13])
			? /*optionIconStyle*/ ctx[13](/*option*/ ctx[27].value)
			: /*optionIconStyle*/ ctx[13];

			if (dirty & /*$$scope, option*/ 402653184) {
				icon_1_changes.$$scope = { dirty, ctx };
			}

			icon_1.$set(icon_1_changes);
		},
		i(local) {
			if (current) return;
			transition_in(icon_1.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(icon_1.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(icon_1, detaching);
		}
	};
}

// (169:16) <Icon style={isFunction(optionIconStyle) ? optionIconStyle(option.value) : optionIconStyle}>
function create_default_slot_2$2(ctx) {
	let g;
	let raw_value = /*option*/ ctx[27].icon + "";

	return {
		c() {
			g = svg_element("g");
		},
		m(target, anchor) {
			insert(target, g, anchor);
			g.innerHTML = raw_value;
		},
		p(ctx, dirty) {
			if (dirty & /*option*/ 134217728 && raw_value !== (raw_value = /*option*/ ctx[27].icon + "")) g.innerHTML = raw_value;		},
		d(detaching) {
			if (detaching) detach(g);
		}
	};
}

// (167:12) <span slot="option">
function create_option_slot(ctx) {
	let span0;
	let t0;
	let span1;
	let t1_value = /*option*/ ctx[27].label + "";
	let t1;
	let span1_style_value;
	let span1_class_value;
	let current;
	let if_block = /*option*/ ctx[27].icon && create_if_block$2(ctx);

	return {
		c() {
			span0 = element("span");
			if (if_block) if_block.c();
			t0 = space();
			span1 = element("span");
			t1 = text(t1_value);

			attr(span1, "style", span1_style_value = isFunction(/*optionLabelStyle*/ ctx[14])
			? /*optionLabelStyle*/ ctx[14](/*option*/ ctx[27].value)
			: /*optionLabelStyle*/ ctx[14]);

			attr(span1, "class", span1_class_value = arrayJoin(["DokaDropdownOptionLabel", /*optionLabelClass*/ ctx[10]]));
			attr(span0, "slot", "option");
		},
		m(target, anchor) {
			insert(target, span0, anchor);
			if (if_block) if_block.m(span0, null);
			append(span0, t0);
			append(span0, span1);
			append(span1, t1);
			current = true;
		},
		p(ctx, dirty) {
			if (/*option*/ ctx[27].icon) {
				if (if_block) {
					if_block.p(ctx, dirty);

					if (dirty & /*option*/ 134217728) {
						transition_in(if_block, 1);
					}
				} else {
					if_block = create_if_block$2(ctx);
					if_block.c();
					transition_in(if_block, 1);
					if_block.m(span0, t0);
				}
			} else if (if_block) {
				group_outros();

				transition_out(if_block, 1, 1, () => {
					if_block = null;
				});

				check_outros();
			}

			if ((!current || dirty & /*option*/ 134217728) && t1_value !== (t1_value = /*option*/ ctx[27].label + "")) set_data(t1, t1_value);

			if (!current || dirty & /*optionLabelStyle, option*/ 134234112 && span1_style_value !== (span1_style_value = isFunction(/*optionLabelStyle*/ ctx[14])
			? /*optionLabelStyle*/ ctx[14](/*option*/ ctx[27].value)
			: /*optionLabelStyle*/ ctx[14])) {
				attr(span1, "style", span1_style_value);
			}

			if (!current || dirty & /*optionLabelClass*/ 1024 && span1_class_value !== (span1_class_value = arrayJoin(["DokaDropdownOptionLabel", /*optionLabelClass*/ ctx[10]]))) {
				attr(span1, "class", span1_class_value);
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block);
			current = true;
		},
		o(local) {
			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(span0);
			if (if_block) if_block.d();
		}
	};
}

// (154:8) <RadioGroup              {name}             {value}             {selectedIndex}             {optionFilter}             {optionMapper}             optionLabelClass={arrayJoin(['DokaDropdownOptionLabel', optionLabelClass])}             optionGroupClass="DokaDropdownOptionGroup"             optionClass="DokaDropdownOption"             options={localizedOptions}             onchange={handleSelect}             let:option>
function create_default_slot_1$2(ctx) {
	let t;

	return {
		c() {
			t = space();
		},
		m(target, anchor) {
			insert(target, t, anchor);
		},
		p: noop$1,
		i: noop$1,
		o: noop$1,
		d(detaching) {
			if (detaching) detach(t);
		}
	};
}

// (153:4) <div slot="details" on:keydown={handleKeydown}>
function create_details_slot(ctx) {
	let div;
	let radiogroup;
	let current;
	let mounted;
	let dispose;

	radiogroup = new RadioGroup({
			props: {
				name: /*name*/ ctx[7],
				value: /*value*/ ctx[9],
				selectedIndex: /*selectedIndex*/ ctx[8],
				optionFilter: /*optionFilter*/ ctx[11],
				optionMapper: /*optionMapper*/ ctx[12],
				optionLabelClass: arrayJoin(["DokaDropdownOptionLabel", /*optionLabelClass*/ ctx[10]]),
				optionGroupClass: "DokaDropdownOptionGroup",
				optionClass: "DokaDropdownOption",
				options: /*localizedOptions*/ ctx[18],
				onchange: /*handleSelect*/ ctx[20],
				$$slots: {
					default: [
						create_default_slot_1$2,
						({ option }) => ({ 27: option }),
						({ option }) => option ? 134217728 : 0
					],
					option: [
						create_option_slot,
						({ option }) => ({ 27: option }),
						({ option }) => option ? 134217728 : 0
					],
					group: [
						create_group_slot,
						({ option }) => ({ 27: option }),
						({ option }) => option ? 134217728 : 0
					]
				},
				$$scope: { ctx }
			}
		});

	return {
		c() {
			div = element("div");
			create_component(radiogroup.$$.fragment);
			attr(div, "slot", "details");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			mount_component(radiogroup, div, null);
			current = true;

			if (!mounted) {
				dispose = listen(div, "keydown", /*handleKeydown*/ ctx[22]);
				mounted = true;
			}
		},
		p(ctx, dirty) {
			const radiogroup_changes = {};
			if (dirty & /*name*/ 128) radiogroup_changes.name = /*name*/ ctx[7];
			if (dirty & /*value*/ 512) radiogroup_changes.value = /*value*/ ctx[9];
			if (dirty & /*selectedIndex*/ 256) radiogroup_changes.selectedIndex = /*selectedIndex*/ ctx[8];
			if (dirty & /*optionFilter*/ 2048) radiogroup_changes.optionFilter = /*optionFilter*/ ctx[11];
			if (dirty & /*optionMapper*/ 4096) radiogroup_changes.optionMapper = /*optionMapper*/ ctx[12];
			if (dirty & /*optionLabelClass*/ 1024) radiogroup_changes.optionLabelClass = arrayJoin(["DokaDropdownOptionLabel", /*optionLabelClass*/ ctx[10]]);
			if (dirty & /*localizedOptions*/ 262144) radiogroup_changes.options = /*localizedOptions*/ ctx[18];

			if (dirty & /*$$scope, optionLabelStyle, option, optionLabelClass, optionIconStyle*/ 402678784) {
				radiogroup_changes.$$scope = { dirty, ctx };
			}

			radiogroup.$set(radiogroup_changes);
		},
		i(local) {
			if (current) return;
			transition_in(radiogroup.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(radiogroup.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			destroy_component(radiogroup);
			mounted = false;
			dispose();
		}
	};
}

// (144:0) <Details bind:isActive={dropdownVisible} {...detailPanel} onshow={handleShowPanel} class={arrayJoin(['DokaDropdown', klass, hideLabel && 'DokaDropdownIconOnly'])} toggleClass="DokaDropdownToggle">
function create_default_slot$4(ctx) {
	let t;

	return {
		c() {
			t = space();
		},
		m(target, anchor) {
			insert(target, t, anchor);
		},
		p: noop$1,
		i: noop$1,
		o: noop$1,
		d(detaching) {
			if (detaching) detach(t);
		}
	};
}

function create_fragment$h(ctx) {
	let details;
	let updating_isActive;
	let current;

	const details_spread_levels = [
		/*detailPanel*/ ctx[16],
		{ onshow: /*handleShowPanel*/ ctx[21] },
		{
			class: arrayJoin([
				"DokaDropdown",
				/*klass*/ ctx[0],
				/*hideLabel*/ ctx[5] && "DokaDropdownIconOnly"
			])
		},
		{ toggleClass: "DokaDropdownToggle" }
	];

	function details_isActive_binding(value) {
		/*details_isActive_binding*/ ctx[25].call(null, value);
	}

	let details_props = {
		$$slots: {
			default: [create_default_slot$4],
			details: [create_details_slot],
			label: [create_label_slot]
		},
		$$scope: { ctx }
	};

	for (let i = 0; i < details_spread_levels.length; i += 1) {
		details_props = assign(details_props, details_spread_levels[i]);
	}

	if (/*dropdownVisible*/ ctx[17] !== void 0) {
		details_props.isActive = /*dropdownVisible*/ ctx[17];
	}

	details = new Details({ props: details_props });
	binding_callbacks.push(() => bind(details, "isActive", details_isActive_binding));

	return {
		c() {
			create_component(details.$$.fragment);
		},
		m(target, anchor) {
			mount_component(details, target, anchor);
			current = true;
		},
		p(ctx, [dirty]) {
			const details_changes = (dirty & /*detailPanel, handleShowPanel, arrayJoin, klass, hideLabel*/ 2162721)
			? get_spread_update(details_spread_levels, [
					dirty & /*detailPanel*/ 65536 && get_spread_object(/*detailPanel*/ ctx[16]),
					dirty & /*handleShowPanel*/ 2097152 && { onshow: /*handleShowPanel*/ ctx[21] },
					dirty & /*arrayJoin, klass, hideLabel*/ 33 && {
						class: arrayJoin([
							"DokaDropdown",
							/*klass*/ ctx[0],
							/*hideLabel*/ ctx[5] && "DokaDropdownIconOnly"
						])
					},
					details_spread_levels[3]
				])
			: {};

			if (dirty & /*$$scope, name, value, selectedIndex, optionFilter, optionMapper, optionLabelClass, localizedOptions, optionLabelStyle, optionIconStyle, title, locale, innerClass, labelClass, label, selectedLabel, hideLabel, icon*/ 269287422) {
				details_changes.$$scope = { dirty, ctx };
			}

			if (!updating_isActive && dirty & /*dropdownVisible*/ 131072) {
				updating_isActive = true;
				details_changes.isActive = /*dropdownVisible*/ ctx[17];
				add_flush_callback(() => updating_isActive = false);
			}

			details.$set(details_changes);
		},
		i(local) {
			if (current) return;
			transition_in(details.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(details.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(details, detaching);
		}
	};
}

function instance$h($$self, $$props, $$invalidate) {
	let { class: klass = undefined } = $$props;
	let { title = undefined } = $$props;
	let { label = undefined } = $$props;
	let { labelClass = undefined } = $$props;
	let { innerClass = undefined } = $$props;
	let { hideLabel = false } = $$props;
	let { icon = undefined } = $$props;
	let { name = undefined } = $$props;
	let { options = [] } = $$props;
	let { selectedIndex = -1 } = $$props;
	let { value = undefined } = $$props;
	let { optionLabelClass = undefined } = $$props;
	let { optionFilter = undefined } = $$props;
	let { optionMapper = undefined } = $$props;
	let { optionIconStyle = undefined } = $$props;
	let { optionLabelStyle = undefined } = $$props;
	let { onchange = undefined } = $$props;
	let { locale = undefined } = $$props;
	let { detailPanel = {} } = $$props;

	const getUndefinedOptionLabel = options => {
		const option = localizedOptions.find(option => option[0] === undefined);
		if (!option) return undefined;
		return option[1];
	};

	let dropdownVisible;

	const handleSelect = detail => {
		// triggers label update
		$$invalidate(19, selectedLabel = detail.value);

		// update value
		onchange(detail);

		// hide details
		$$invalidate(17, dropdownVisible = false);
	};

	const handleShowPanel = ({ e, panel }) => {
		if (e && e.key && (/up|down/i).test(e.key)) return panel.querySelector("input:not([disabled])").focus();
		panel.querySelector("fieldset").focus();
	};

	const handleKeydown = e => {
		// don't allow tabbing ([tab] is also blocked in normal <select>)
		if ((/tab/i).test(e.key)) e.preventDefault();
	};

	function details_isActive_binding(value) {
		dropdownVisible = value;
		$$invalidate(17, dropdownVisible);
	}

	$$self.$$set = $$props => {
		if ("class" in $$props) $$invalidate(0, klass = $$props.class);
		if ("title" in $$props) $$invalidate(1, title = $$props.title);
		if ("label" in $$props) $$invalidate(2, label = $$props.label);
		if ("labelClass" in $$props) $$invalidate(3, labelClass = $$props.labelClass);
		if ("innerClass" in $$props) $$invalidate(4, innerClass = $$props.innerClass);
		if ("hideLabel" in $$props) $$invalidate(5, hideLabel = $$props.hideLabel);
		if ("icon" in $$props) $$invalidate(6, icon = $$props.icon);
		if ("name" in $$props) $$invalidate(7, name = $$props.name);
		if ("options" in $$props) $$invalidate(23, options = $$props.options);
		if ("selectedIndex" in $$props) $$invalidate(8, selectedIndex = $$props.selectedIndex);
		if ("value" in $$props) $$invalidate(9, value = $$props.value);
		if ("optionLabelClass" in $$props) $$invalidate(10, optionLabelClass = $$props.optionLabelClass);
		if ("optionFilter" in $$props) $$invalidate(11, optionFilter = $$props.optionFilter);
		if ("optionMapper" in $$props) $$invalidate(12, optionMapper = $$props.optionMapper);
		if ("optionIconStyle" in $$props) $$invalidate(13, optionIconStyle = $$props.optionIconStyle);
		if ("optionLabelStyle" in $$props) $$invalidate(14, optionLabelStyle = $$props.optionLabelStyle);
		if ("onchange" in $$props) $$invalidate(24, onchange = $$props.onchange);
		if ("locale" in $$props) $$invalidate(15, locale = $$props.locale);
		if ("detailPanel" in $$props) $$invalidate(16, detailPanel = $$props.detailPanel);
	};

	let localizedOptions;
	let selectedLabel;

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*options, locale*/ 8421376) {
			 $$invalidate(18, localizedOptions = localizeOptions$1(options, locale));
		}

		if ($$self.$$.dirty & /*localizedOptions, value*/ 262656) {
			 $$invalidate(19, selectedLabel = localizedOptions.reduce(
				(prev, curr) => {
					if (prev) return prev;
					const [optionValue, optionLabel] = curr;
					if (optionValue === value) return optionLabel;
				},
				undefined
			) || getUndefinedOptionLabel());
		}
	};

	return [
		klass,
		title,
		label,
		labelClass,
		innerClass,
		hideLabel,
		icon,
		name,
		selectedIndex,
		value,
		optionLabelClass,
		optionFilter,
		optionMapper,
		optionIconStyle,
		optionLabelStyle,
		locale,
		detailPanel,
		dropdownVisible,
		localizedOptions,
		selectedLabel,
		handleSelect,
		handleShowPanel,
		handleKeydown,
		options,
		onchange,
		details_isActive_binding
	];
}

class Dropdown extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$h, create_fragment$h, safe_not_equal, {
			class: 0,
			title: 1,
			label: 2,
			labelClass: 3,
			innerClass: 4,
			hideLabel: 5,
			icon: 6,
			name: 7,
			options: 23,
			selectedIndex: 8,
			value: 9,
			optionLabelClass: 10,
			optionFilter: 11,
			optionMapper: 12,
			optionIconStyle: 13,
			optionLabelStyle: 14,
			onchange: 24,
			locale: 15,
			detailPanel: 16
		});
	}
}

var toFraction = (value, min, max) => (value - min) / (max - min);

/* src/native/ui/components/RangeInput.svelte generated by Svelte v3.29.4 */

function create_if_block$3(ctx) {
	let div;
	let div_style_value;

	return {
		c() {
			div = element("div");
			attr(div, "class", "DokaRangeInputMeter");
			attr(div, "style", div_style_value = `transform: translateX(${/*$position*/ ctx[9].x - /*svgPadding*/ ctx[10].x}px) translateY(${/*$position*/ ctx[9].y - /*svgPadding*/ ctx[10].y}px)`);
		},
		m(target, anchor) {
			insert(target, div, anchor);
			div.innerHTML = /*svg*/ ctx[6];
		},
		p(ctx, dirty) {
			if (dirty[0] & /*svg*/ 64) div.innerHTML = /*svg*/ ctx[6];
			if (dirty[0] & /*$position*/ 512 && div_style_value !== (div_style_value = `transform: translateX(${/*$position*/ ctx[9].x - /*svgPadding*/ ctx[10].x}px) translateY(${/*$position*/ ctx[9].y - /*svgPadding*/ ctx[10].y}px)`)) {
				attr(div, "style", div_style_value);
			}
		},
		d(detaching) {
			if (detaching) detach(div);
		}
	};
}

function create_fragment$i(ctx) {
	let div1;
	let span;
	let t0;
	let t1;
	let button;
	let t2;
	let button_disabled_value;
	let t3;
	let div0;
	let interactable_action;
	let measurable_action;
	let nudgeable_action;
	let mounted;
	let dispose;
	let if_block = /*$position*/ ctx[9] && create_if_block$3(ctx);

	return {
		c() {
			div1 = element("div");
			span = element("span");
			t0 = text(/*valueLabel*/ ctx[4]);
			t1 = space();
			button = element("button");
			t2 = text(/*labelReset*/ ctx[1]);
			t3 = space();
			div0 = element("div");
			if (if_block) if_block.c();
			attr(span, "class", "DokaRangeInputValue");
			attr(button, "class", "DokaRangeInputReset");
			attr(button, "type", "button");
			button.disabled = button_disabled_value = /*value*/ ctx[0] === /*base*/ ctx[3];
			attr(div0, "class", "DokaRangeInputInner");
			attr(div0, "style", /*rangeMask*/ ctx[8]);
			attr(div0, "data-value-limited", /*isLimited*/ ctx[7]);
			attr(div1, "class", "DokaRangeInput");
			attr(div1, "tabindex", "0");
		},
		m(target, anchor) {
			insert(target, div1, anchor);
			append(div1, span);
			append(span, t0);
			append(div1, t1);
			append(div1, button);
			append(button, t2);
			append(div1, t3);
			append(div1, div0);
			if (if_block) if_block.m(div0, null);

			if (!mounted) {
				dispose = [
					listen(button, "click", /*handleReset*/ ctx[15]),
					listen(div0, "interactionstart", /*handleDragStart*/ ctx[11]),
					listen(div0, "interactionupdate", /*handleDragMove*/ ctx[13]),
					listen(div0, "interactionend", /*handleDragEnd*/ ctx[14]),
					listen(div0, "interactionrelease", /*handleDragRelease*/ ctx[12]),
					action_destroyer(interactable_action = interactable.call(null, div0, { inertia: true })),
					listen(div0, "measure", /*measure_handler*/ ctx[27]),
					action_destroyer(measurable_action = measurable.call(null, div0)),
					listen(div1, "wheel", /*handleWheel*/ ctx[17]),
					listen(div1, "nudge", /*handleNudge*/ ctx[18]),
					action_destroyer(nudgeable_action = nudgeable.call(null, div1, /*direction*/ ctx[2] = "horizontal"))
				];

				mounted = true;
			}
		},
		p(ctx, dirty) {
			if (dirty[0] & /*valueLabel*/ 16) set_data(t0, /*valueLabel*/ ctx[4]);
			if (dirty[0] & /*labelReset*/ 2) set_data(t2, /*labelReset*/ ctx[1]);

			if (dirty[0] & /*value, base*/ 9 && button_disabled_value !== (button_disabled_value = /*value*/ ctx[0] === /*base*/ ctx[3])) {
				button.disabled = button_disabled_value;
			}

			if (/*$position*/ ctx[9]) {
				if (if_block) {
					if_block.p(ctx, dirty);
				} else {
					if_block = create_if_block$3(ctx);
					if_block.c();
					if_block.m(div0, null);
				}
			} else if (if_block) {
				if_block.d(1);
				if_block = null;
			}

			if (dirty[0] & /*rangeMask*/ 256) {
				attr(div0, "style", /*rangeMask*/ ctx[8]);
			}

			if (dirty[0] & /*isLimited*/ 128) {
				attr(div0, "data-value-limited", /*isLimited*/ ctx[7]);
			}

			if (nudgeable_action && is_function(nudgeable_action.update) && dirty[0] & /*direction*/ 4) nudgeable_action.update.call(null, /*direction*/ ctx[2] = "horizontal");
		},
		i: noop$1,
		o: noop$1,
		d(detaching) {
			if (detaching) detach(div1);
			if (if_block) if_block.d();
			mounted = false;
			run_all(dispose);
		}
	};
}

const radiusBig = 2;
const radiusSmall = 0.75;
const indicatorSpacing = 10;
const indicatorInterval = 5;
const indicatorCount = 40; // must be even

function instance$i($$self, $$props, $$invalidate) {
	let $position;
	let { labelReset = "Reset" } = $$props;
	let { direction = "x" } = $$props;
	let { min = 0 } = $$props;
	let { max = 1 } = $$props;
	let { base = min } = $$props;
	let { value = 0 } = $$props;
	let { valueLabel = 0 } = $$props;
	let { valueMin = undefined } = $$props;
	let { valueMax = undefined } = $$props;
	let { oninputstart = noop } = $$props;
	let { oninputmove = noop } = $$props;
	let { oninputend = noop } = $$props;
	let { elasticity = 0 } = $$props;
	const round = (number, increment, offset) => Math.ceil((number - offset) / increment) * increment + offset;
	let size;
	let svg;
	let svgSize;
	let svgPadding = { x: 2, y: 0 };

	function getMask(limitedMin, limitedMax) {
		const step = 1 / indicatorCount;
		const from = toFraction(limitedMin, min, max);
		const to = toFraction(limitedMax, min, max);
		const fromSnapped = fixPrecision(round(from, step, 0) - step * 0.5);
		const toSnapped = fixPrecision(round(to, step, 0) - step * 0.5);
		return `--range-mask-from:${fromSnapped * 100}%;--range-mask-to:${toSnapped * 100}%`;
	}

	//
	// Dragging related
	//
	let interactionOrigin = undefined;

	let interactionReleased = false;
	let interactionRange;
	let interactionOptions = { snap: false, elastic: false };

	const handleDragStart = () => {
		interactionReleased = false;
		interactionOrigin = get_store_value(position);

		interactionRange = [
			valueToPosition(valueMin != null ? valueMin : min, direction),
			valueToPosition(valueMax != null ? valueMax : max, direction)
		];

		oninputstart();
	};

	const handleDragRelease = () => {
		interactionReleased = true;
	};

	const handleDragMove = ({ detail }) => {
		interactionOptions.snap = !interactionReleased;
		interactionOptions.elastic = !interactionReleased;
		translatePosition(interactionOrigin, detail.translation, interactionOptions);
	};

	const handleDragEnd = ({ detail }) => {
		interactionOptions.snap = false;
		interactionOptions.elastic = false;
		const valueAtPosition = translatePosition(interactionOrigin, detail.translation, interactionOptions);
		interactionOrigin = undefined;
		interactionRange = undefined;

		// if is near base position, set to base
		if (Math.abs(valueAtPosition - base) < 0.01) return oninputend(base);

		oninputend(valueAtPosition);
	};

	const translatePosition = (origin, translation, options) => {
		const target = origin[direction] + translation[direction];
		const targetLimited = clamp(target, interactionRange[1][direction], interactionRange[0][direction]);

		const targetElastic = elasticity
		? targetLimited + elastify(target - targetLimited, elasticity)
		: targetLimited;

		const targetVisual = options.elastic ? targetElastic : targetLimited;

		// set position
		const targetValid = vectorCreate(0, 0);

		targetValid[direction] = targetVisual;
		position.set(targetValid, { hard: options.snap });
		return clamp(positionToValue(targetValid, direction), min, max);
	};

	const handleReset = () => {
		$$invalidate(0, value = base);
		oninputstart();
		oninputend(value);
	};

	//
	// Meter position and value updates
	//
	const position = spring();

	component_subscribe($$self, position, value => $$invalidate(9, $position = value));

	const valueToPosition = (value, axis) => {
		const offset = (size[axis] - barSize[axis]) * 0.5;
		const fraction = toFraction(value, min, max);
		const v = fraction * barSize[axis] - barSize[axis] * 0.5;
		const pos = offset - v;

		return {
			x: axis === "x" ? pos : 0,
			y: axis === "y" ? pos : 0
		};
	};

	const positionToValue = (position, axis) => {
		const dist = position[axis] - size[axis] * 0.5;
		const fraction = -(dist / barSize[axis]);
		return min + fraction * range;
	};

	position.subscribe(currentPosition => {
		// if not positioned yet or is not interacting we're not interested in this event
		if (!currentPosition || !interactionOrigin) return;

		oninputmove(clamp(positionToValue(currentPosition, direction), min, max));
	});

	const updatePosition = value => {
		const interactionRange = [
			valueToPosition(valueMin != null ? valueMin : min, direction),
			valueToPosition(valueMax != null ? valueMax : max, direction)
		];

		const target = {
			x: direction === "x" ? $position.x + value : 0,
			y: direction === "y" ? $position.y + value : 0
		};

		const targetLimited = clamp(target[direction], interactionRange[1][direction], interactionRange[0][direction]);
		const positionTarget = { ...$position, [direction]: targetLimited };
		set_store_value(position, $position = positionTarget, $position);
		const result = clamp(positionToValue(positionTarget, direction), min, max);
		oninputstart();
		oninputmove(result);
		oninputend(result);
	};

	const handleWheel = e => {
		// don't run default actions, prevent other actions from running
		e.preventDefault();

		e.stopPropagation();

		// apply wheel delta to offset
		const step = 8;

		const delta = getWheelDelta(e) * step;
		updatePosition(delta);
	};

	const handleNudge = ({ detail }) => {
		updatePosition(detail[direction] * 8);
	};

	const measure_handler = e => $$invalidate(5, size = vectorCreateFromSize(e.detail));

	$$self.$$set = $$props => {
		if ("labelReset" in $$props) $$invalidate(1, labelReset = $$props.labelReset);
		if ("direction" in $$props) $$invalidate(2, direction = $$props.direction);
		if ("min" in $$props) $$invalidate(19, min = $$props.min);
		if ("max" in $$props) $$invalidate(20, max = $$props.max);
		if ("base" in $$props) $$invalidate(3, base = $$props.base);
		if ("value" in $$props) $$invalidate(0, value = $$props.value);
		if ("valueLabel" in $$props) $$invalidate(4, valueLabel = $$props.valueLabel);
		if ("valueMin" in $$props) $$invalidate(21, valueMin = $$props.valueMin);
		if ("valueMax" in $$props) $$invalidate(22, valueMax = $$props.valueMax);
		if ("oninputstart" in $$props) $$invalidate(23, oninputstart = $$props.oninputstart);
		if ("oninputmove" in $$props) $$invalidate(24, oninputmove = $$props.oninputmove);
		if ("oninputend" in $$props) $$invalidate(25, oninputend = $$props.oninputend);
		if ("elasticity" in $$props) $$invalidate(26, elasticity = $$props.elasticity);
	};

	let range;
	let valueMinLimited;
	let valueMaxLimited;
	let barSize;
	let isLimited;
	let rangeMask;

	$$self.$$.update = () => {
		if ($$self.$$.dirty[0] & /*max, min*/ 1572864) {
			//
			// derived
			//
			 $$invalidate(33, range = max - min);
		}

		if ($$self.$$.dirty[0] & /*valueMin, min*/ 2621440) {
			 $$invalidate(34, valueMinLimited = valueMin != null ? Math.max(valueMin, min) : min);
		}

		if ($$self.$$.dirty[0] & /*valueMax, max*/ 5242880) {
			 $$invalidate(35, valueMaxLimited = valueMax != null ? Math.min(valueMax, max) : max);
		}

		if ($$self.$$.dirty[0] & /*size*/ 32) {
			 if (size) {
				const centerY = size.y * 0.5;
				let indicators = "";
				let svgWidth;
				let svgHeight = size.y;

				for (let i = 0; i <= indicatorCount; i++) {
					const x = svgPadding.x + i * indicatorSpacing;
					const y = centerY;
					const r = i % indicatorInterval === 0 ? radiusBig : radiusSmall;
					indicators += `<circle cx="${x}" cy="${y}" r="${r}"/>`;
					svgWidth = x + svgPadding.x;
				}

				$$invalidate(6, svg = `<svg width="${svgWidth}" height="${svgHeight}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svgWidth} ${svgHeight}" aria-hidden="true" focusable="false"><g fill="currentColor">${indicators}<rect rx="4" ry="4" y="${centerY - 4}"" height="8"></rect></g></svg>`);

				$$invalidate(28, svgSize = {
					x: svgWidth - svgPadding.x * 2,
					y: svgHeight
				});
			}
		}

		if ($$self.$$.dirty[0] & /*size, svgSize*/ 268435488) {
			 barSize = size && svgSize;
		}

		if ($$self.$$.dirty[0] & /*min, max*/ 1572864 | $$self.$$.dirty[1] & /*valueMinLimited, valueMaxLimited*/ 24) {
			 $$invalidate(7, isLimited = valueMinLimited !== min || valueMaxLimited !== max);
		}

		if ($$self.$$.dirty[0] & /*isLimited*/ 128 | $$self.$$.dirty[1] & /*valueMinLimited, valueMaxLimited*/ 24) {
			 $$invalidate(8, rangeMask = isLimited
			? getMask(valueMinLimited, valueMaxLimited)
			: "");
		}

		if ($$self.$$.dirty[0] & /*size, value, direction*/ 37 | $$self.$$.dirty[1] & /*range*/ 4) {
			// if has size (range is in here so the position is updated if the range is changed)
			 if (range && size && size.x && size.y) {
				position.set(valueToPosition(value, direction));
			}
		}
	};

	return [
		value,
		labelReset,
		direction,
		base,
		valueLabel,
		size,
		svg,
		isLimited,
		rangeMask,
		$position,
		svgPadding,
		handleDragStart,
		handleDragRelease,
		handleDragMove,
		handleDragEnd,
		handleReset,
		position,
		handleWheel,
		handleNudge,
		min,
		max,
		valueMin,
		valueMax,
		oninputstart,
		oninputmove,
		oninputend,
		elasticity,
		measure_handler
	];
}

class RangeInput extends SvelteComponent {
	constructor(options) {
		super();

		init(
			this,
			options,
			instance$i,
			create_fragment$i,
			safe_not_equal,
			{
				labelReset: 1,
				direction: 2,
				min: 19,
				max: 20,
				base: 3,
				value: 0,
				valueLabel: 4,
				valueMin: 21,
				valueMax: 22,
				oninputstart: 23,
				oninputmove: 24,
				oninputend: 25,
				elasticity: 26
			},
			[-1, -1]
		);
	}
}

/* src/native/ui/components/Toolbar.svelte generated by Svelte v3.29.4 */

function create_fragment$j(ctx) {
	let div1;
	let div0;
	let measurable_action;
	let measurable_action_1;
	let current;
	let mounted;
	let dispose;
	const default_slot_template = /*#slots*/ ctx[5].default;
	const default_slot = create_slot(default_slot_template, ctx, /*$$scope*/ ctx[4], null);

	return {
		c() {
			div1 = element("div");
			div0 = element("div");
			if (default_slot) default_slot.c();
			attr(div0, "class", "DokaToolbarInner");
			attr(div1, "class", "DokaToolbar");
			attr(div1, "data-layout", /*layout*/ ctx[1]);
			attr(div1, "data-overflow", /*overflow*/ ctx[0]);
		},
		m(target, anchor) {
			insert(target, div1, anchor);
			append(div1, div0);

			if (default_slot) {
				default_slot.m(div0, null);
			}

			current = true;

			if (!mounted) {
				dispose = [
					listen(div0, "measure", /*handleChildResizeEvent*/ ctx[3]),
					action_destroyer(measurable_action = measurable.call(null, div0)),
					listen(div1, "measure", /*handleParentResizeEvent*/ ctx[2]),
					action_destroyer(measurable_action_1 = measurable.call(null, div1))
				];

				mounted = true;
			}
		},
		p(ctx, [dirty]) {
			if (default_slot) {
				if (default_slot.p && dirty & /*$$scope*/ 16) {
					update_slot(default_slot, default_slot_template, ctx, /*$$scope*/ ctx[4], dirty, null, null);
				}
			}

			if (!current || dirty & /*layout*/ 2) {
				attr(div1, "data-layout", /*layout*/ ctx[1]);
			}

			if (!current || dirty & /*overflow*/ 1) {
				attr(div1, "data-overflow", /*overflow*/ ctx[0]);
			}
		},
		i(local) {
			if (current) return;
			transition_in(default_slot, local);
			current = true;
		},
		o(local) {
			transition_out(default_slot, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div1);
			if (default_slot) default_slot.d(detaching);
			mounted = false;
			run_all(dispose);
		}
	};
}

function instance$j($$self, $$props, $$invalidate) {
	let { $$slots: slots = {}, $$scope } = $$props;
	let childWidth = 0;
	let childMaxWidth = 0;
	let parentWidth = 0;
	let overflow;

	const testOverflow = () => {
		$$invalidate(0, overflow = layout === "compact" && childWidth > parentWidth
		? "overflow"
		: undefined);
	};

	const handleParentResizeEvent = ({ detail }) => {
		const { width, height } = detail;
		$$invalidate(8, parentWidth = width);
		testOverflow();
	};

	const handleChildResizeEvent = ({ detail }) => {
		const { width, height } = detail;

		if (width > childMaxWidth) {
			$$invalidate(7, childMaxWidth = width);
		}

		childWidth = width;
		if (!overflow) testOverflow();
	};

	$$self.$$set = $$props => {
		if ("$$scope" in $$props) $$invalidate(4, $$scope = $$props.$$scope);
	};

	let layout;

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*childMaxWidth, parentWidth*/ 384) {
			 $$invalidate(1, layout = childMaxWidth > parentWidth ? "compact" : "default");
		}
	};

	return [
		overflow,
		layout,
		handleParentResizeEvent,
		handleChildResizeEvent,
		$$scope,
		slots
	];
}

class Toolbar extends SvelteComponent {
	constructor(options) {
		super();
		init(this, options, instance$j, create_fragment$j, safe_not_equal, {});
	}
}

// enum Direction {
//     Top = 't',
//     Right = 'r',
//     Bottom = 'b',
//     Left = 'l',
//     TopLeft = 'tl',
//     TopRight = 'tr',
//     BottomRight = 'br',
//     BottomLeft = 'bl',
// }
// export const { Top, Right, Bottom, Left, TopLeft, TopRight, BottomRight, BottomLeft } = Direction;
// export default Direction;
const Direction = {
    Top: 't',
    Right: 'r',
    Bottom: 'b',
    Left: 'l',
    TopLeft: 'tl',
    TopRight: 'tr',
    BottomRight: 'br',
    BottomLeft: 'bl',
};
const { Top, Right, Bottom, Left, TopLeft, TopRight, BottomRight, BottomLeft } = Direction;

var DirectionRectMap = {
    [Top]: (rect) => ({ x: rect.x, y: rect.y }),
    [TopRight]: (rect) => ({ x: rect.x + rect.width, y: rect.y }),
    [Right]: (rect) => ({ x: rect.x + rect.width, y: rect.y }),
    [BottomRight]: (rect) => ({ x: rect.x + rect.width, y: rect.y + rect.height }),
    [Bottom]: (rect) => ({ x: rect.x, y: rect.y + rect.height }),
    [BottomLeft]: (rect) => ({ x: rect.x, y: rect.y + rect.height }),
    [Left]: (rect) => ({ x: rect.x, y: rect.y }),
    [TopLeft]: (rect) => ({ x: rect.x, y: rect.y }),
};

/* src/native/ui/components/RectManipulator.svelte generated by Svelte v3.29.4 */

function get_each_context$4(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[11] = list[i].key;
	child_ctx[12] = list[i].translate;
	child_ctx[13] = list[i].scale;
	child_ctx[14] = list[i].type;
	child_ctx[15] = list[i].opacity;
	return child_ctx;
}

// (210:0) {#each mappedDirections as { key, translate, scale, type, opacity }
function create_each_block$4(key_1, ctx) {
	let div;
	let div_tabindex_value;
	let div_data_direction_value;
	let div_data_shape_value;
	let div_style_value;
	let nudgeable_action;
	let interactable_action;
	let mounted;
	let dispose;

	return {
		key: key_1,
		first: null,
		c() {
			div = element("div");
			attr(div, "role", "button");
			attr(div, "tabindex", div_tabindex_value = /*type*/ ctx[14] === "edge" ? -1 : 0);
			attr(div, "class", "DokaRectManipulator");
			attr(div, "data-direction", div_data_direction_value = /*key*/ ctx[11]);

			attr(div, "data-shape", div_data_shape_value = `${/*type*/ ctx[14] === "edge"
			? "edge"
			: `${/*style*/ ctx[0]}`}`);

			attr(div, "style", div_style_value = `transform: translate3d(${/*translate*/ ctx[12].x}px, ${/*translate*/ ctx[12].y}px, 0) scale(${/*scale*/ ctx[13].x}, ${/*scale*/ ctx[13].y}); opacity: ${/*opacity*/ ctx[15]}`);
			this.first = div;
		},
		m(target, anchor) {
			insert(target, div, anchor);

			if (!mounted) {
				dispose = [
					listen(div, "nudge", function () {
						if (is_function(/*nudge*/ ctx[5](/*key*/ ctx[11]))) /*nudge*/ ctx[5](/*key*/ ctx[11]).apply(this, arguments);
					}),
					action_destroyer(nudgeable_action = nudgeable.call(null, div)),
					listen(div, "interactionstart", function () {
						if (is_function(/*route*/ ctx[4]("resizestart", /*key*/ ctx[11]))) /*route*/ ctx[4]("resizestart", /*key*/ ctx[11]).apply(this, arguments);
					}),
					listen(div, "interactionupdate", function () {
						if (is_function(/*route*/ ctx[4]("resizemove", /*key*/ ctx[11]))) /*route*/ ctx[4]("resizemove", /*key*/ ctx[11]).apply(this, arguments);
					}),
					listen(div, "interactionend", function () {
						if (is_function(/*route*/ ctx[4]("resizeend", /*key*/ ctx[11]))) /*route*/ ctx[4]("resizeend", /*key*/ ctx[11]).apply(this, arguments);
					}),
					action_destroyer(interactable_action = interactable.call(null, div))
				];

				mounted = true;
			}
		},
		p(new_ctx, dirty) {
			ctx = new_ctx;

			if (dirty & /*mappedDirections*/ 2 && div_tabindex_value !== (div_tabindex_value = /*type*/ ctx[14] === "edge" ? -1 : 0)) {
				attr(div, "tabindex", div_tabindex_value);
			}

			if (dirty & /*mappedDirections*/ 2 && div_data_direction_value !== (div_data_direction_value = /*key*/ ctx[11])) {
				attr(div, "data-direction", div_data_direction_value);
			}

			if (dirty & /*mappedDirections, style*/ 3 && div_data_shape_value !== (div_data_shape_value = `${/*type*/ ctx[14] === "edge"
			? "edge"
			: `${/*style*/ ctx[0]}`}`)) {
				attr(div, "data-shape", div_data_shape_value);
			}

			if (dirty & /*mappedDirections*/ 2 && div_style_value !== (div_style_value = `transform: translate3d(${/*translate*/ ctx[12].x}px, ${/*translate*/ ctx[12].y}px, 0) scale(${/*scale*/ ctx[13].x}, ${/*scale*/ ctx[13].y}); opacity: ${/*opacity*/ ctx[15]}`)) {
				attr(div, "style", div_style_value);
			}
		},
		d(detaching) {
			if (detaching) detach(div);
			mounted = false;
			run_all(dispose);
		}
	};
}

function create_fragment$k(ctx) {
	let each_blocks = [];
	let each_1_lookup = new Map();
	let each_1_anchor;
	let each_value = /*mappedDirections*/ ctx[1];
	const get_key = ctx => /*key*/ ctx[11];

	for (let i = 0; i < each_value.length; i += 1) {
		let child_ctx = get_each_context$4(ctx, each_value, i);
		let key = get_key(child_ctx);
		each_1_lookup.set(key, each_blocks[i] = create_each_block$4(key, child_ctx));
	}

	return {
		c() {
			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			each_1_anchor = empty();
		},
		m(target, anchor) {
			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].m(target, anchor);
			}

			insert(target, each_1_anchor, anchor);
		},
		p(ctx, [dirty]) {
			if (dirty & /*mappedDirections, style, nudge, route*/ 51) {
				const each_value = /*mappedDirections*/ ctx[1];
				each_blocks = update_keyed_each(each_blocks, dirty, get_key, 1, ctx, each_value, each_1_lookup, each_1_anchor.parentNode, destroy_block, create_each_block$4, each_1_anchor, get_each_context$4);
			}
		},
		i: noop$1,
		o: noop$1,
		d(detaching) {
			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].d(detaching);
			}

			if (detaching) detach(each_1_anchor);
		}
	};
}

function instance$k($$self, $$props, $$invalidate) {
	let $selectionScale;
	let $selectionOpacity;
	let { rect = null } = $$props;
	let { visible = false } = $$props;
	let { style = undefined } = $$props;

	// is `undefined` to prevent bounce when first rendering view
	const selectionScale = spring(undefined, {
		precision: 0.0001,
		stiffness: 0.2,
		damping: 0.4
	});

	component_subscribe($$self, selectionScale, value => $$invalidate(8, $selectionScale = value));
	const selectionOpacity = spring(0, { precision: 0.001 });
	component_subscribe($$self, selectionOpacity, value => $$invalidate(9, $selectionOpacity = value));

	//
	// dragging
	//
	const dispatch = createEventDispatcher();

	const route = (type, direction) => ({ detail }) => dispatch(type, {
		direction,
		translation: detail && detail.translation
	});

	const nudge = direction => ({ detail }) => {
		dispatch(`resizestart`, { direction, translation: { x: 0, y: 0 } });
		dispatch(`resizemove`, { direction, translation: detail });
		dispatch(`resizeend`, { direction, translation: { x: 0, y: 0 } });
	};

	$$self.$$set = $$props => {
		if ("rect" in $$props) $$invalidate(6, rect = $$props.rect);
		if ("visible" in $$props) $$invalidate(7, visible = $$props.visible);
		if ("style" in $$props) $$invalidate(0, style = $$props.style);
	};

	let mappedDirections;

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*visible*/ 128) {
			 selectionScale.set(visible ? 1 : 0.5);
		}

		if ($$self.$$.dirty & /*visible*/ 128) {
			 selectionOpacity.set(visible ? 1 : 0);
		}

		if ($$self.$$.dirty & /*rect, $selectionScale, $selectionOpacity*/ 832) {
			//
			// rendering
			//
			 $$invalidate(1, mappedDirections = Object.keys(Direction).map((key, i) => {
				// get direction enum
				const direction = Direction[key];

				// get position from direction
				const position = DirectionRectMap[direction](rect);

				// corner or edge
				const type = direction.length === 1 ? "edge" : "corner";

				const isCorner = type === "corner";

				return {
					key: direction,
					type,
					scale: {
						x: (/^(t|b)$/).test(direction)
						? rect.width
						: isCorner ? clamp($selectionScale, 0.5, 1.25) : 1,
						y: (/^(r|l)$/).test(direction)
						? rect.height
						: isCorner ? clamp($selectionScale, 0.5, 1.25) : 1
					},
					translate: { x: position.x, y: position.y },
					opacity: $selectionOpacity
				};
			}));
		}
	};

	return [
		style,
		mappedDirections,
		selectionScale,
		selectionOpacity,
		route,
		nudge,
		rect,
		visible
	];
}

class RectManipulator extends SvelteComponent {
	constructor(options) {
		super();
		init(this, options, instance$k, create_fragment$k, safe_not_equal, { rect: 6, visible: 7, style: 0 });
	}
}

var getEventPositionInViewport = (e) => vectorCreate(e.clientX, e.clientY);

var getEventPositionInStage = (e, viewOffset, stageOffset) => {
    const positionInViewport = getEventPositionInViewport(e);
    return vectorSubtract(vectorSubtract(positionInViewport, viewOffset), stageOffset);
};

var DirectionInversionTable = {
    [Top]: Bottom,
    [Right]: Left,
    [Bottom]: Top,
    [Left]: Right,
    [TopLeft]: BottomRight,
    [TopRight]: BottomLeft,
    [BottomRight]: TopLeft,
    [BottomLeft]: TopRight,
};

var DirectionCoordinateTable = {
    [Top]: [0.5, 0],
    [Right]: [1, 0.5],
    [Bottom]: [0.5, 1],
    [Left]: [0, 0.5],
    [TopLeft]: [0, 0],
    [TopRight]: [1, 0],
    [BottomRight]: [1, 1],
    [BottomLeft]: [0, 1],
};

var getTranslationInfo = (target) => {
    const translateToRight = target === Right || target === TopRight || target === BottomRight;
    const translateToLeft = target === Left || target === BottomLeft || target === TopLeft;
    const translateToTop = target === Top || target === TopRight || target === TopLeft;
    const translateToBottom = target === Bottom || target === BottomRight || target === BottomLeft;
    const translateHorizontally = target === Left || target === Right;
    const translateVertically = target === Top || target === Bottom;
    const translateAxis = translateHorizontally || translateVertically;
    return [
        translateToRight,
        translateToLeft,
        translateToTop,
        translateToBottom,
        translateHorizontally,
        translateVertically,
        translateAxis,
    ];
};

var limitRectDirectionTranslation = (rect, transform, bounds, options = {}) => {
    // the transforms to apply
    const { target, translate } = transform;
    // the transform requirements
    const { aspectRatio, minSize, maxSize } = options;
    // get anchor coordinates and x,y position we need this for relative scaling of the view rectangle
    const anchor = DirectionInversionTable[target];
    const anchorDirectionCoordinates = DirectionCoordinateTable[anchor];
    const anchorPosition = vectorAdd(vectorCreate(rect.x, rect.y), vectorCreate(anchorDirectionCoordinates[0] * rect.width, anchorDirectionCoordinates[1] * rect.height));
    // get coordinate of direction
    const targetDirectionCoordinates = DirectionCoordinateTable[target];
    const targetPosition = vectorAdd(rectClone(rect), vectorCreate(targetDirectionCoordinates[0] * rect.width, targetDirectionCoordinates[1] * rect.height));
    // bools to determine which direction the interaction is moving in
    const [translateToRight, translateToLeft, translateToTop, translateToBottom, translateHorizontally, translateVertically, translateAxis,] = getTranslationInfo(target);
    let tx = translate.x;
    let ty = translate.y;
    if (translateHorizontally)
        ty = 0;
    else if (translateVertically)
        tx = 0;
    const interactionBounds = getInteractionBounds(anchorPosition, target, bounds, {
        aspectRatio,
        minSize,
        maxSize,
    });
    // current bounds
    let [t, r, b, l] = rectToBounds(rect);
    // update view bounds with anchor based on translation direction (one side is always locked)
    if (translateToRight)
        l = anchorPosition.x;
    else if (translateToLeft)
        r = anchorPosition.x;
    if (translateToBottom)
        t = anchorPosition.y;
    else if (translateToTop)
        b = anchorPosition.y;
    // update view bounds with interaction limits
    if (translateToRight) {
        const innerR = interactionBounds.inner.x + interactionBounds.inner.width;
        const outerR = interactionBounds.outer.x + interactionBounds.outer.width;
        r = clamp(targetPosition.x + tx, innerR, outerR);
    }
    else if (translateToLeft) {
        const innerL = interactionBounds.outer.x;
        const outerL = interactionBounds.inner.x;
        l = clamp(targetPosition.x + tx, innerL, outerL);
    }
    if (translateToBottom) {
        const innerB = interactionBounds.inner.y + interactionBounds.inner.height;
        const outerB = interactionBounds.outer.y + interactionBounds.outer.height;
        b = clamp(targetPosition.y + ty, innerB, outerB);
    }
    else if (translateToTop) {
        const innerT = interactionBounds.outer.y;
        const outerT = interactionBounds.inner.y;
        t = clamp(targetPosition.y + ty, innerT, outerT);
    }
    // if aspect ratio is set we need to scale both axis
    if (aspectRatio) {
        // if translating over horizontal or vertical axis we need to update the other axis based on the aspect ratio as well
        if (translateAxis) {
            let dx = r - l;
            let dy = b - t;
            if (translateHorizontally) {
                dy = dx / aspectRatio;
                t = anchorPosition.y - dy * 0.5;
                b = anchorPosition.y + dy * 0.5;
            }
            else if (translateVertically) {
                dx = dy * aspectRatio;
                l = anchorPosition.x - dx * 0.5;
                r = anchorPosition.x + dx * 0.5;
            }
        }
        // we're translating one of the corners in both the x and y direction, need to make sure it conforms to aspect ratio
        else {
            const pointer = vectorCreate(targetPosition.x + tx - anchorPosition.x, targetPosition.y + ty - anchorPosition.y);
            // translations cannot be inverted, limited by anchor position
            if (target === TopRight) {
                pointer.x = Math.max(0, pointer.x);
                pointer.y = Math.min(0, pointer.y);
            }
            else if (target === BottomRight) {
                pointer.x = Math.max(0, pointer.x);
                pointer.y = Math.max(0, pointer.y);
            }
            else if (target === BottomLeft) {
                pointer.x = Math.min(0, pointer.x);
                pointer.y = Math.max(0, pointer.y);
            }
            else if (target === TopLeft) {
                pointer.x = Math.min(0, pointer.x);
                pointer.y = Math.min(0, pointer.y);
            }
            // calculate the translation pointer, then get its length, now create a new pointer based on the aspect ratio, and scale it based on the original (limited) length
            const pointerLength = vectorLength(pointer);
            const pointerLengthMin = vectorLength(vectorCreate(interactionBounds.inner.width, interactionBounds.inner.height));
            const pointerLengthMax = vectorLength(vectorCreate(interactionBounds.outer.width, interactionBounds.outer.height));
            const pointerLengthLimited = clamp(pointerLength, pointerLengthMin, pointerLengthMax);
            const pointerAspectRatio = vectorCreate(aspectRatio, 1);
            const pointerScaled = vectorMultiply(vectorNormalize(pointerAspectRatio), pointerLengthLimited);
            if (target === TopRight) {
                r = anchorPosition.x + pointerScaled.x;
                t = anchorPosition.y - pointerScaled.y;
            }
            else if (target === BottomRight) {
                r = anchorPosition.x + pointerScaled.x;
                b = anchorPosition.y + pointerScaled.y;
            }
            else if (target === BottomLeft) {
                l = anchorPosition.x - pointerScaled.x;
                b = anchorPosition.y + pointerScaled.y;
            }
            else if (target === TopLeft) {
                l = anchorPosition.x - pointerScaled.x;
                t = anchorPosition.y - pointerScaled.y;
            }
        }
    }
    return rectCreate(l, t, r - l, b - t);
};
const getInteractionBounds = (anchor, dir, bounds, options) => {
    const { aspectRatio, minSize, maxSize } = options;
    const translateToRight = dir === Right || dir === TopRight || dir === BottomRight;
    const translateToLeft = dir === Left || dir === BottomLeft || dir === TopLeft;
    const translateToTop = dir === Top || dir === TopRight || dir === TopLeft;
    const translateToBottom = dir === Bottom || dir === BottomRight || dir === BottomLeft;
    const translateHorizontally = dir === Left || dir === Right;
    const translateVertically = dir === Top || dir === Bottom;
    // limit bounds based on anchor and direction
    const limitedBounds = rectClone(bounds);
    if (translateToRight) {
        limitedBounds.x = anchor.x;
        limitedBounds.width -= anchor.x;
    }
    else if (translateToLeft) {
        limitedBounds.width = anchor.x;
    }
    if (translateToBottom) {
        limitedBounds.y = anchor.y;
        limitedBounds.height -= anchor.y;
    }
    else if (translateToTop) {
        limitedBounds.height = anchor.y;
    }
    // limit max size based on bounds
    const maxSizeLimitedToBounds = rectCreateFromDimensions(Math.min(limitedBounds.width, maxSize.width), Math.min(limitedBounds.height, maxSize.height));
    if (aspectRatio) {
        // limit height
        if (translateHorizontally) {
            const verticalSpace = Math.min(anchor.y, bounds.height - anchor.y);
            maxSizeLimitedToBounds.height = Math.min(verticalSpace * 2, maxSizeLimitedToBounds.height);
        }
        // limit width
        else if (translateVertically) {
            const horizontalSpace = Math.min(anchor.x, bounds.width - anchor.x);
            maxSizeLimitedToBounds.width = Math.min(horizontalSpace * 2, maxSizeLimitedToBounds.width);
        }
    }
    const maxSizeLimited = aspectRatio
        ? sizeCreateFromRect(rectContainRect(maxSizeLimitedToBounds, aspectRatio))
        : maxSizeLimitedToBounds;
    // limit min size
    const minSizeLimited = aspectRatio
        ? sizeCreateFromRect(rectCoverRect(rectCreateFromSize(minSize), aspectRatio))
        : minSize;
    let l, r, t, b;
    // set bounds
    if (translateToRight)
        l = anchor.x;
    else if (translateToLeft)
        r = anchor.x;
    if (translateToBottom)
        t = anchor.y;
    else if (translateToTop)
        b = anchor.y;
    // inner
    if (translateToRight) {
        r = l + minSizeLimited.width;
    }
    else if (translateToLeft) {
        l = r - minSizeLimited.width;
    }
    if (translateToBottom) {
        b = t + minSizeLimited.height;
    }
    else if (translateToTop) {
        t = b - minSizeLimited.height;
    }
    if (translateHorizontally) {
        t = anchor.y - minSizeLimited.height * 0.5;
        b = anchor.y + minSizeLimited.height * 0.5;
    }
    else if (translateVertically) {
        l = anchor.x - minSizeLimited.width * 0.5;
        r = anchor.x + minSizeLimited.width * 0.5;
    }
    const inner = rectCreateFromPoints(vectorCreate(l, t), vectorCreate(r, b));
    // outer
    if (translateToRight) {
        r = l + maxSizeLimited.width;
    }
    else if (translateToLeft) {
        l = r - maxSizeLimited.width;
    }
    if (translateToBottom) {
        b = t + maxSizeLimited.height;
    }
    else if (translateToTop) {
        t = b - maxSizeLimited.height;
    }
    if (translateHorizontally) {
        t = anchor.y - maxSizeLimited.height * 0.5;
        b = anchor.y + maxSizeLimited.height * 0.5;
    }
    else if (translateVertically) {
        l = anchor.x - maxSizeLimited.width * 0.5;
        r = anchor.x + maxSizeLimited.width * 0.5;
    }
    const outer = rectCreateFromPoints(vectorCreate(l, t), vectorCreate(r, b));
    return {
        inner,
        outer,
    };
};

var applyRectDirectionTranslation = (rect, transform, options = {}) => {
    // the transforms to apply
    const { target, translate } = transform;
    // the transform requirements
    const { aspectRatio } = options;
    // get anchor coordinates and x,y position we need this for relative scaling of the view rectangle
    const anchor = DirectionInversionTable[target];
    const anchorDirectionCoordinates = DirectionCoordinateTable[anchor];
    const anchorPosition = vectorAdd(rectClone(rect), vectorCreate(anchorDirectionCoordinates[0] * rect.width, anchorDirectionCoordinates[1] * rect.height));
    // get coordinate of direction
    const targetDirectionCoordinates = DirectionCoordinateTable[target];
    const targetPosition = vectorAdd(rectClone(rect), vectorCreate(targetDirectionCoordinates[0] * rect.width, targetDirectionCoordinates[1] * rect.height));
    // bools to determine which direction the interaction is moving in
    const [translateToRight, translateToLeft, translateToTop, translateToBottom, translateHorizontally, translateVertically, translateAxis,] = getTranslationInfo(target);
    let tx = translate.x;
    let ty = translate.y;
    if (translateHorizontally)
        ty = 0;
    else if (translateVertically)
        tx = 0;
    // current bounds
    let [t, r, b, l] = rectToBounds(rect);
    // update view bounds with anchor based on translation direction (one side is always locked)
    if (translateToRight)
        l = anchorPosition.x;
    else if (translateToLeft)
        r = anchorPosition.x;
    if (translateToBottom)
        t = anchorPosition.y;
    else if (translateToTop)
        b = anchorPosition.y;
    // update view bounds with interaction limits
    if (translateToRight) {
        r = targetPosition.x + tx;
    }
    else if (translateToLeft) {
        l = targetPosition.x + tx;
    }
    if (translateToBottom) {
        b = targetPosition.y + ty;
    }
    else if (translateToTop) {
        t = targetPosition.y + ty;
    }
    // if aspect ratio is set we need to scale both axis
    if (aspectRatio) {
        // if translating over horizontal or vertical axis we need to update the other axis based on the aspect ratio as well
        if (translateAxis) {
            let dx = r - l;
            let dy = b - t;
            if (translateHorizontally) {
                dy = dx / aspectRatio;
                t = anchorPosition.y - dy * 0.5;
                b = anchorPosition.y + dy * 0.5;
            }
            else if (translateVertically) {
                dx = dy * aspectRatio;
                l = anchorPosition.x - dx * 0.5;
                r = anchorPosition.x + dx * 0.5;
            }
        }
        // we're translating one of the corners in both the x and y direction, need to make sure it conforms to aspect ratio
        else {
            const pointer = vectorCreate(targetPosition.x + tx - anchorPosition.x, targetPosition.y + ty - anchorPosition.y);
            // translations cannot be inverted, limited by anchor position
            if (target === TopRight) {
                pointer.x = Math.max(0, pointer.x);
                pointer.y = Math.min(0, pointer.y);
            }
            else if (target === BottomRight) {
                pointer.x = Math.max(0, pointer.x);
                pointer.y = Math.max(0, pointer.y);
            }
            else if (target === BottomLeft) {
                pointer.x = Math.min(0, pointer.x);
                pointer.y = Math.max(0, pointer.y);
            }
            else if (target === TopLeft) {
                pointer.x = Math.min(0, pointer.x);
                pointer.y = Math.min(0, pointer.y);
            }
            // calculate the translation pointer, then get its length, now create a new pointer based on the aspect ratio, and scale it based on the original (limited) length
            const pointerLength = vectorLength(pointer);
            const pointerAspectRatio = vectorCreate(aspectRatio, 1);
            const pointerScaled = vectorMultiply(vectorNormalize(pointerAspectRatio), pointerLength);
            if (target === TopRight) {
                r = anchorPosition.x + pointerScaled.x;
                t = anchorPosition.y - pointerScaled.y;
            }
            else if (target === BottomRight) {
                r = anchorPosition.x + pointerScaled.x;
                b = anchorPosition.y + pointerScaled.y;
            }
            else if (target === BottomLeft) {
                l = anchorPosition.x - pointerScaled.x;
                b = anchorPosition.y + pointerScaled.y;
            }
            else if (target === TopLeft) {
                l = anchorPosition.x - pointerScaled.x;
                t = anchorPosition.y - pointerScaled.y;
            }
        }
    }
    return rectCreate(l, t, r - l, b - t);
};

var radToDeg = (rad) => rad * 180 / Math.PI;

/* src/native/ui/plugins/crop/components/ImageRotator.svelte generated by Svelte v3.29.4 */

function create_fragment$l(ctx) {
	let div;
	let rangeinput;
	let current;

	rangeinput = new RangeInput({
			props: {
				elasticity: /*elasticity*/ ctx[5],
				min: /*min*/ ctx[7],
				max: /*max*/ ctx[8],
				value: /*value*/ ctx[12],
				valueMin: /*valueMin*/ ctx[0],
				valueMax: /*valueMax*/ ctx[1],
				labelReset: /*labelReset*/ ctx[6],
				base: /*center*/ ctx[9],
				valueLabel: `${Math.round(radToDeg(/*value*/ ctx[12]))}°`,
				oninputstart: /*oninputstart*/ ctx[2],
				oninputmove: /*func*/ ctx[14],
				oninputend: /*func_1*/ ctx[15]
			}
		});

	return {
		c() {
			div = element("div");
			create_component(rangeinput.$$.fragment);
			attr(div, "class", "DokaImageRotator");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			mount_component(rangeinput, div, null);
			current = true;
		},
		p(ctx, [dirty]) {
			const rangeinput_changes = {};
			if (dirty & /*elasticity*/ 32) rangeinput_changes.elasticity = /*elasticity*/ ctx[5];
			if (dirty & /*min*/ 128) rangeinput_changes.min = /*min*/ ctx[7];
			if (dirty & /*max*/ 256) rangeinput_changes.max = /*max*/ ctx[8];
			if (dirty & /*value*/ 4096) rangeinput_changes.value = /*value*/ ctx[12];
			if (dirty & /*valueMin*/ 1) rangeinput_changes.valueMin = /*valueMin*/ ctx[0];
			if (dirty & /*valueMax*/ 2) rangeinput_changes.valueMax = /*valueMax*/ ctx[1];
			if (dirty & /*labelReset*/ 64) rangeinput_changes.labelReset = /*labelReset*/ ctx[6];
			if (dirty & /*center*/ 512) rangeinput_changes.base = /*center*/ ctx[9];
			if (dirty & /*value*/ 4096) rangeinput_changes.valueLabel = `${Math.round(radToDeg(/*value*/ ctx[12]))}°`;
			if (dirty & /*oninputstart*/ 4) rangeinput_changes.oninputstart = /*oninputstart*/ ctx[2];
			if (dirty & /*oninputmove, sign, turns*/ 3080) rangeinput_changes.oninputmove = /*func*/ ctx[14];
			if (dirty & /*oninputend, sign, turns*/ 3088) rangeinput_changes.oninputend = /*func_1*/ ctx[15];
			rangeinput.$set(rangeinput_changes);
		},
		i(local) {
			if (current) return;
			transition_in(rangeinput.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(rangeinput.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			destroy_component(rangeinput);
		}
	};
}

const MARGIN = 1e-9;

function instance$l($$self, $$props, $$invalidate) {
	const HALF_PI = Math.PI / 2;
	const QUARTER_PI = Math.PI / 4;
	let { rotation } = $$props;
	let { valueMin } = $$props;
	let { valueMax } = $$props;
	let { oninputstart = noop } = $$props;
	let { oninputmove = noop } = $$props;
	let { oninputend = noop } = $$props;
	let { elasticity = 0 } = $$props;
	let { labelReset = undefined } = $$props;
	const func = value => oninputmove(sign * turns + value);
	const func_1 = value => oninputend(sign * turns + value);

	$$self.$$set = $$props => {
		if ("rotation" in $$props) $$invalidate(13, rotation = $$props.rotation);
		if ("valueMin" in $$props) $$invalidate(0, valueMin = $$props.valueMin);
		if ("valueMax" in $$props) $$invalidate(1, valueMax = $$props.valueMax);
		if ("oninputstart" in $$props) $$invalidate(2, oninputstart = $$props.oninputstart);
		if ("oninputmove" in $$props) $$invalidate(3, oninputmove = $$props.oninputmove);
		if ("oninputend" in $$props) $$invalidate(4, oninputend = $$props.oninputend);
		if ("elasticity" in $$props) $$invalidate(5, elasticity = $$props.elasticity);
		if ("labelReset" in $$props) $$invalidate(6, labelReset = $$props.labelReset);
	};

	let min;
	let max;
	let center;
	let sign;
	let turns;
	let value;

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*min, max*/ 384) {
			 $$invalidate(9, center = min + (max - min) * 0.5);
		}

		if ($$self.$$.dirty & /*rotation*/ 8192) {
			 $$invalidate(10, sign = Math.sign(rotation));
		}

		if ($$self.$$.dirty & /*rotation*/ 8192) {
			 $$invalidate(11, turns = Math.round(Math.abs(rotation) / HALF_PI) * HALF_PI);
		}

		if ($$self.$$.dirty & /*rotation, sign, turns*/ 11264) {
			 $$invalidate(12, value = rotation - sign * turns);
		}
	};

	 $$invalidate(7, min = -QUARTER_PI + MARGIN);
	 $$invalidate(8, max = QUARTER_PI - MARGIN);

	return [
		valueMin,
		valueMax,
		oninputstart,
		oninputmove,
		oninputend,
		elasticity,
		labelReset,
		min,
		max,
		center,
		sign,
		turns,
		value,
		rotation,
		func,
		func_1
	];
}

class ImageRotator extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$l, create_fragment$l, safe_not_equal, {
			rotation: 13,
			valueMin: 0,
			valueMax: 1,
			oninputstart: 2,
			oninputmove: 3,
			oninputend: 4,
			elasticity: 5,
			labelReset: 6
		});
	}
}

/* src/native/ui/plugins/crop/components/ImageInfo.svelte generated by Svelte v3.29.4 */

function create_fragment$m(ctx) {
	let div;
	let p;
	let t0;
	let t1;
	let t2;

	return {
		c() {
			div = element("div");
			p = element("p");
			t0 = text(/*width*/ ctx[0]);
			t1 = text(" × ");
			t2 = text(/*height*/ ctx[1]);
			attr(div, "class", "DokaImageInfo");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			append(div, p);
			append(p, t0);
			append(p, t1);
			append(p, t2);
		},
		p(ctx, [dirty]) {
			if (dirty & /*width*/ 1) set_data(t0, /*width*/ ctx[0]);
			if (dirty & /*height*/ 2) set_data(t2, /*height*/ ctx[1]);
		},
		i: noop$1,
		o: noop$1,
		d(detaching) {
			if (detaching) detach(div);
		}
	};
}

function instance$m($$self, $$props, $$invalidate) {
	let { width } = $$props;
	let { height } = $$props;

	$$self.$$set = $$props => {
		if ("width" in $$props) $$invalidate(0, width = $$props.width);
		if ("height" in $$props) $$invalidate(1, height = $$props.height);
	};

	return [width, height];
}

class ImageInfo extends SvelteComponent {
	constructor(options) {
		super();
		init(this, options, instance$m, create_fragment$m, safe_not_equal, { width: 0, height: 1 });
	}
}

var getSelectionPresetOptionIcon = (value, options = {}) => {
    const { width = 24, height = 24, bounds = 16, radius = 3 } = options;
    let aspectRatio = isArray(value) ? getAspectRatio(value[0], value[1]) : value;
    let aspectRatioDefined = !!aspectRatio;
    aspectRatio = aspectRatioDefined ? aspectRatio : 1;
    let x;
    let y;
    let w;
    let h;
    w = aspectRatio > 1 ? bounds : aspectRatio * bounds;
    h = w / aspectRatio;
    x = Math.round((width - w) * 0.5);
    y = Math.round((height - h) * 0.5);
    const fill = aspectRatioDefined ? 'currentColor' : 'none';
    const stroke = aspectRatioDefined ? 'none' : 'currentColor';
    const strokeWidth = width / 16;
    const strokeDashArray = [width / 12, width / 6].join(' ');
    return `<rect fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-dasharray="${strokeDashArray}" x="${x}" y="${y}" width="${w}" height="${h}" rx="${radius}"/>`;
};

/* src/native/ui/plugins/crop/index.svelte generated by Svelte v3.29.4 */

function create_if_block_12$1(ctx) {
	let switch_instance;
	let switch_instance_anchor;
	let current;
	var switch_value = Button;

	function switch_props(ctx) {
		return {
			props: {
				labelClass: "DokaToolbarContentWide",
				label: /*locale*/ ctx[8].cropLabelButtonRotateLeft,
				icon: /*locale*/ ctx[8].cropIconButtonRotateLeft,
				onclick: /*func*/ ctx[111]
			}
		};
	}

	if (switch_value) {
		switch_instance = new switch_value(switch_props(ctx));
	}

	return {
		c() {
			if (switch_instance) create_component(switch_instance.$$.fragment);
			switch_instance_anchor = empty();
		},
		m(target, anchor) {
			if (switch_instance) {
				mount_component(switch_instance, target, anchor);
			}

			insert(target, switch_instance_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const switch_instance_changes = {};
			if (dirty[0] & /*locale*/ 256) switch_instance_changes.label = /*locale*/ ctx[8].cropLabelButtonRotateLeft;
			if (dirty[0] & /*locale*/ 256) switch_instance_changes.icon = /*locale*/ ctx[8].cropIconButtonRotateLeft;
			if (dirty[0] & /*$imageRotation*/ 2048) switch_instance_changes.onclick = /*func*/ ctx[111];

			if (switch_value !== (switch_value = Button)) {
				if (switch_instance) {
					group_outros();
					const old_component = switch_instance;

					transition_out(old_component.$$.fragment, 1, 0, () => {
						destroy_component(old_component, 1);
					});

					check_outros();
				}

				if (switch_value) {
					switch_instance = new switch_value(switch_props(ctx));
					create_component(switch_instance.$$.fragment);
					transition_in(switch_instance.$$.fragment, 1);
					mount_component(switch_instance, switch_instance_anchor.parentNode, switch_instance_anchor);
				} else {
					switch_instance = null;
				}
			} else if (switch_value) {
				switch_instance.$set(switch_instance_changes);
			}
		},
		i(local) {
			if (current) return;
			if (switch_instance) transition_in(switch_instance.$$.fragment, local);
			current = true;
		},
		o(local) {
			if (switch_instance) transition_out(switch_instance.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(switch_instance_anchor);
			if (switch_instance) destroy_component(switch_instance, detaching);
		}
	};
}

// (1157:12) {#if cropEnableButtonRotateRight}
function create_if_block_11$1(ctx) {
	let switch_instance;
	let switch_instance_anchor;
	let current;
	var switch_value = Button;

	function switch_props(ctx) {
		return {
			props: {
				labelClass: "DokaToolbarContentWide",
				label: /*locale*/ ctx[8].cropLabelButtonRotateRight,
				icon: /*locale*/ ctx[8].cropIconButtonRotateRight,
				onclick: /*func_1*/ ctx[112]
			}
		};
	}

	if (switch_value) {
		switch_instance = new switch_value(switch_props(ctx));
	}

	return {
		c() {
			if (switch_instance) create_component(switch_instance.$$.fragment);
			switch_instance_anchor = empty();
		},
		m(target, anchor) {
			if (switch_instance) {
				mount_component(switch_instance, target, anchor);
			}

			insert(target, switch_instance_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const switch_instance_changes = {};
			if (dirty[0] & /*locale*/ 256) switch_instance_changes.label = /*locale*/ ctx[8].cropLabelButtonRotateRight;
			if (dirty[0] & /*locale*/ 256) switch_instance_changes.icon = /*locale*/ ctx[8].cropIconButtonRotateRight;
			if (dirty[0] & /*$imageRotation*/ 2048) switch_instance_changes.onclick = /*func_1*/ ctx[112];

			if (switch_value !== (switch_value = Button)) {
				if (switch_instance) {
					group_outros();
					const old_component = switch_instance;

					transition_out(old_component.$$.fragment, 1, 0, () => {
						destroy_component(old_component, 1);
					});

					check_outros();
				}

				if (switch_value) {
					switch_instance = new switch_value(switch_props(ctx));
					create_component(switch_instance.$$.fragment);
					transition_in(switch_instance.$$.fragment, 1);
					mount_component(switch_instance, switch_instance_anchor.parentNode, switch_instance_anchor);
				} else {
					switch_instance = null;
				}
			} else if (switch_value) {
				switch_instance.$set(switch_instance_changes);
			}
		},
		i(local) {
			if (current) return;
			if (switch_instance) transition_in(switch_instance.$$.fragment, local);
			current = true;
		},
		o(local) {
			if (switch_instance) transition_out(switch_instance.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(switch_instance_anchor);
			if (switch_instance) destroy_component(switch_instance, detaching);
		}
	};
}

// (1163:12) {#if cropEnableButtonFlipHorizontal}
function create_if_block_10$1(ctx) {
	let switch_instance;
	let switch_instance_anchor;
	let current;
	var switch_value = Button;

	function switch_props(ctx) {
		return {
			props: {
				labelClass: "DokaToolbarContentWide",
				label: /*locale*/ ctx[8].cropLabelButtonFlipHorizontal,
				icon: /*locale*/ ctx[8].cropIconButtonFlipHorizontal,
				onclick: /*func_2*/ ctx[113]
			}
		};
	}

	if (switch_value) {
		switch_instance = new switch_value(switch_props(ctx));
	}

	return {
		c() {
			if (switch_instance) create_component(switch_instance.$$.fragment);
			switch_instance_anchor = empty();
		},
		m(target, anchor) {
			if (switch_instance) {
				mount_component(switch_instance, target, anchor);
			}

			insert(target, switch_instance_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const switch_instance_changes = {};
			if (dirty[0] & /*locale*/ 256) switch_instance_changes.label = /*locale*/ ctx[8].cropLabelButtonFlipHorizontal;
			if (dirty[0] & /*locale*/ 256) switch_instance_changes.icon = /*locale*/ ctx[8].cropIconButtonFlipHorizontal;
			if (dirty[0] & /*$imageRotation, $imageFlipY, $imageFlipX*/ 14336) switch_instance_changes.onclick = /*func_2*/ ctx[113];

			if (switch_value !== (switch_value = Button)) {
				if (switch_instance) {
					group_outros();
					const old_component = switch_instance;

					transition_out(old_component.$$.fragment, 1, 0, () => {
						destroy_component(old_component, 1);
					});

					check_outros();
				}

				if (switch_value) {
					switch_instance = new switch_value(switch_props(ctx));
					create_component(switch_instance.$$.fragment);
					transition_in(switch_instance.$$.fragment, 1);
					mount_component(switch_instance, switch_instance_anchor.parentNode, switch_instance_anchor);
				} else {
					switch_instance = null;
				}
			} else if (switch_value) {
				switch_instance.$set(switch_instance_changes);
			}
		},
		i(local) {
			if (current) return;
			if (switch_instance) transition_in(switch_instance.$$.fragment, local);
			current = true;
		},
		o(local) {
			if (switch_instance) transition_out(switch_instance.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(switch_instance_anchor);
			if (switch_instance) destroy_component(switch_instance, detaching);
		}
	};
}

// (1174:12) {#if cropEnableButtonFlipVertical}
function create_if_block_9$1(ctx) {
	let switch_instance;
	let switch_instance_anchor;
	let current;
	var switch_value = Button;

	function switch_props(ctx) {
		return {
			props: {
				labelClass: "DokaToolbarContentWide",
				label: /*locale*/ ctx[8].cropLabelButtonFlipVertical,
				icon: /*locale*/ ctx[8].cropIconButtonFlipVertical,
				onclick: /*func_3*/ ctx[114]
			}
		};
	}

	if (switch_value) {
		switch_instance = new switch_value(switch_props(ctx));
	}

	return {
		c() {
			if (switch_instance) create_component(switch_instance.$$.fragment);
			switch_instance_anchor = empty();
		},
		m(target, anchor) {
			if (switch_instance) {
				mount_component(switch_instance, target, anchor);
			}

			insert(target, switch_instance_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const switch_instance_changes = {};
			if (dirty[0] & /*locale*/ 256) switch_instance_changes.label = /*locale*/ ctx[8].cropLabelButtonFlipVertical;
			if (dirty[0] & /*locale*/ 256) switch_instance_changes.icon = /*locale*/ ctx[8].cropIconButtonFlipVertical;
			if (dirty[0] & /*$imageRotation, $imageFlipX, $imageFlipY*/ 14336) switch_instance_changes.onclick = /*func_3*/ ctx[114];

			if (switch_value !== (switch_value = Button)) {
				if (switch_instance) {
					group_outros();
					const old_component = switch_instance;

					transition_out(old_component.$$.fragment, 1, 0, () => {
						destroy_component(old_component, 1);
					});

					check_outros();
				}

				if (switch_value) {
					switch_instance = new switch_value(switch_props(ctx));
					create_component(switch_instance.$$.fragment);
					transition_in(switch_instance.$$.fragment, 1);
					mount_component(switch_instance, switch_instance_anchor.parentNode, switch_instance_anchor);
				} else {
					switch_instance = null;
				}
			} else if (switch_value) {
				switch_instance.$set(switch_instance_changes);
			}
		},
		i(local) {
			if (current) return;
			if (switch_instance) transition_in(switch_instance.$$.fragment, local);
			current = true;
		},
		o(local) {
			if (switch_instance) transition_out(switch_instance.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(switch_instance_anchor);
			if (switch_instance) destroy_component(switch_instance, detaching);
		}
	};
}

// (1185:12) {#if shouldRenderPresetSelect && cropSelectPresetOptions}
function create_if_block_8$1(ctx) {
	let switch_instance;
	let switch_instance_anchor;
	let current;
	var switch_value = Dropdown;

	function switch_props(ctx) {
		return {
			props: {
				detailPanel: { viewBounds: /*viewBounds*/ ctx[15] },
				icon: /*getSelectionPresetLabelIcon*/ ctx[95](/*$selectedPresetIndex*/ ctx[16]),
				label: /*locale*/ ctx[8].cropLabelSelectPreset,
				labelClass: "DokaToolbarContentWide",
				options: /*cropSelectPresetOptions*/ ctx[6],
				selectedIndex: /*$selectedPresetIndex*/ ctx[16],
				onchange: /*func_4*/ ctx[115],
				optionMapper: /*func_5*/ ctx[116]
			}
		};
	}

	if (switch_value) {
		switch_instance = new switch_value(switch_props(ctx));
	}

	return {
		c() {
			if (switch_instance) create_component(switch_instance.$$.fragment);
			switch_instance_anchor = empty();
		},
		m(target, anchor) {
			if (switch_instance) {
				mount_component(switch_instance, target, anchor);
			}

			insert(target, switch_instance_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const switch_instance_changes = {};
			if (dirty[0] & /*viewBounds*/ 32768) switch_instance_changes.detailPanel = { viewBounds: /*viewBounds*/ ctx[15] };
			if (dirty[0] & /*$selectedPresetIndex*/ 65536) switch_instance_changes.icon = /*getSelectionPresetLabelIcon*/ ctx[95](/*$selectedPresetIndex*/ ctx[16]);
			if (dirty[0] & /*locale*/ 256) switch_instance_changes.label = /*locale*/ ctx[8].cropLabelSelectPreset;
			if (dirty[0] & /*cropSelectPresetOptions*/ 64) switch_instance_changes.options = /*cropSelectPresetOptions*/ ctx[6];
			if (dirty[0] & /*$selectedPresetIndex*/ 65536) switch_instance_changes.selectedIndex = /*$selectedPresetIndex*/ ctx[16];
			if (dirty[0] & /*$imageCropAspectRatio, $imageOutputSize, isImageSelectionCentered*/ 917504) switch_instance_changes.onchange = /*func_4*/ ctx[115];
			if (dirty[0] & /*$imageSize, $imageRotation, $imageCropMinSize*/ 3147776) switch_instance_changes.optionMapper = /*func_5*/ ctx[116];

			if (switch_value !== (switch_value = Dropdown)) {
				if (switch_instance) {
					group_outros();
					const old_component = switch_instance;

					transition_out(old_component.$$.fragment, 1, 0, () => {
						destroy_component(old_component, 1);
					});

					check_outros();
				}

				if (switch_value) {
					switch_instance = new switch_value(switch_props(ctx));
					create_component(switch_instance.$$.fragment);
					transition_in(switch_instance.$$.fragment, 1);
					mount_component(switch_instance, switch_instance_anchor.parentNode, switch_instance_anchor);
				} else {
					switch_instance = null;
				}
			} else if (switch_value) {
				switch_instance.$set(switch_instance_changes);
			}
		},
		i(local) {
			if (current) return;
			if (switch_instance) transition_in(switch_instance.$$.fragment, local);
			current = true;
		},
		o(local) {
			if (switch_instance) transition_out(switch_instance.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(switch_instance_anchor);
			if (switch_instance) destroy_component(switch_instance, detaching);
		}
	};
}

// (1229:12) {#if cropEnableButtonToggleCropLimit}
function create_if_block_7$2(ctx) {
	let switch_instance;
	let switch_instance_anchor;
	let current;
	var switch_value = Dropdown;

	function switch_props(ctx) {
		return {
			props: {
				detailPanel: { viewBounds: /*viewBounds*/ ctx[15] },
				icon: /*getCropBoundaryLabelIcon*/ ctx[96](/*$imageCropLimitToImage*/ ctx[22]),
				label: /*locale*/ ctx[8].cropLabelCropBoundary,
				labelClass: "DokaToolbarContentWide",
				onchange: /*func_6*/ ctx[117],
				options: [
					[
						true,
						/*locale*/ ctx[8].cropLabelCropBoundaryEdge,
						{
							icon: /*getCropBoundaryLabelIcon*/ ctx[96](true)
						}
					],
					[
						false,
						/*locale*/ ctx[8].cropLabelCropBoundaryNone,
						{
							icon: /*getCropBoundaryLabelIcon*/ ctx[96](false)
						}
					]
				]
			}
		};
	}

	if (switch_value) {
		switch_instance = new switch_value(switch_props(ctx));
	}

	return {
		c() {
			if (switch_instance) create_component(switch_instance.$$.fragment);
			switch_instance_anchor = empty();
		},
		m(target, anchor) {
			if (switch_instance) {
				mount_component(switch_instance, target, anchor);
			}

			insert(target, switch_instance_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const switch_instance_changes = {};
			if (dirty[0] & /*viewBounds*/ 32768) switch_instance_changes.detailPanel = { viewBounds: /*viewBounds*/ ctx[15] };
			if (dirty[0] & /*$imageCropLimitToImage*/ 4194304) switch_instance_changes.icon = /*getCropBoundaryLabelIcon*/ ctx[96](/*$imageCropLimitToImage*/ ctx[22]);
			if (dirty[0] & /*locale*/ 256) switch_instance_changes.label = /*locale*/ ctx[8].cropLabelCropBoundary;
			if (dirty[0] & /*$imageCropLimitToImage*/ 4194304) switch_instance_changes.onchange = /*func_6*/ ctx[117];

			if (dirty[0] & /*locale*/ 256) switch_instance_changes.options = [
				[
					true,
					/*locale*/ ctx[8].cropLabelCropBoundaryEdge,
					{
						icon: /*getCropBoundaryLabelIcon*/ ctx[96](true)
					}
				],
				[
					false,
					/*locale*/ ctx[8].cropLabelCropBoundaryNone,
					{
						icon: /*getCropBoundaryLabelIcon*/ ctx[96](false)
					}
				]
			];

			if (switch_value !== (switch_value = Dropdown)) {
				if (switch_instance) {
					group_outros();
					const old_component = switch_instance;

					transition_out(old_component.$$.fragment, 1, 0, () => {
						destroy_component(old_component, 1);
					});

					check_outros();
				}

				if (switch_value) {
					switch_instance = new switch_value(switch_props(ctx));
					create_component(switch_instance.$$.fragment);
					transition_in(switch_instance.$$.fragment, 1);
					mount_component(switch_instance, switch_instance_anchor.parentNode, switch_instance_anchor);
				} else {
					switch_instance = null;
				}
			} else if (switch_value) {
				switch_instance.$set(switch_instance_changes);
			}
		},
		i(local) {
			if (current) return;
			if (switch_instance) transition_in(switch_instance.$$.fragment, local);
			current = true;
		},
		o(local) {
			if (switch_instance) transition_out(switch_instance.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(switch_instance_anchor);
			if (switch_instance) destroy_component(switch_instance, detaching);
		}
	};
}

// (1150:8) <Toolbar>
function create_default_slot_3$3(ctx) {
	let t0;
	let t1;
	let t2;
	let t3;
	let t4;
	let if_block5_anchor;
	let current;
	let if_block0 = /*cropEnableButtonRotateLeft*/ ctx[2] && create_if_block_12$1(ctx);
	let if_block1 = /*cropEnableButtonRotateRight*/ ctx[3] && create_if_block_11$1(ctx);
	let if_block2 = /*cropEnableButtonFlipHorizontal*/ ctx[4] && create_if_block_10$1(ctx);
	let if_block3 = /*cropEnableButtonFlipVertical*/ ctx[5] && create_if_block_9$1(ctx);
	let if_block4 = /*shouldRenderPresetSelect*/ ctx[14] && /*cropSelectPresetOptions*/ ctx[6] && create_if_block_8$1(ctx);
	let if_block5 = /*cropEnableButtonToggleCropLimit*/ ctx[7] && create_if_block_7$2(ctx);

	return {
		c() {
			if (if_block0) if_block0.c();
			t0 = space();
			if (if_block1) if_block1.c();
			t1 = space();
			if (if_block2) if_block2.c();
			t2 = space();
			if (if_block3) if_block3.c();
			t3 = space();
			if (if_block4) if_block4.c();
			t4 = space();
			if (if_block5) if_block5.c();
			if_block5_anchor = empty();
		},
		m(target, anchor) {
			if (if_block0) if_block0.m(target, anchor);
			insert(target, t0, anchor);
			if (if_block1) if_block1.m(target, anchor);
			insert(target, t1, anchor);
			if (if_block2) if_block2.m(target, anchor);
			insert(target, t2, anchor);
			if (if_block3) if_block3.m(target, anchor);
			insert(target, t3, anchor);
			if (if_block4) if_block4.m(target, anchor);
			insert(target, t4, anchor);
			if (if_block5) if_block5.m(target, anchor);
			insert(target, if_block5_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			if (/*cropEnableButtonRotateLeft*/ ctx[2]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty[0] & /*cropEnableButtonRotateLeft*/ 4) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_12$1(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(t0.parentNode, t0);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (/*cropEnableButtonRotateRight*/ ctx[3]) {
				if (if_block1) {
					if_block1.p(ctx, dirty);

					if (dirty[0] & /*cropEnableButtonRotateRight*/ 8) {
						transition_in(if_block1, 1);
					}
				} else {
					if_block1 = create_if_block_11$1(ctx);
					if_block1.c();
					transition_in(if_block1, 1);
					if_block1.m(t1.parentNode, t1);
				}
			} else if (if_block1) {
				group_outros();

				transition_out(if_block1, 1, 1, () => {
					if_block1 = null;
				});

				check_outros();
			}

			if (/*cropEnableButtonFlipHorizontal*/ ctx[4]) {
				if (if_block2) {
					if_block2.p(ctx, dirty);

					if (dirty[0] & /*cropEnableButtonFlipHorizontal*/ 16) {
						transition_in(if_block2, 1);
					}
				} else {
					if_block2 = create_if_block_10$1(ctx);
					if_block2.c();
					transition_in(if_block2, 1);
					if_block2.m(t2.parentNode, t2);
				}
			} else if (if_block2) {
				group_outros();

				transition_out(if_block2, 1, 1, () => {
					if_block2 = null;
				});

				check_outros();
			}

			if (/*cropEnableButtonFlipVertical*/ ctx[5]) {
				if (if_block3) {
					if_block3.p(ctx, dirty);

					if (dirty[0] & /*cropEnableButtonFlipVertical*/ 32) {
						transition_in(if_block3, 1);
					}
				} else {
					if_block3 = create_if_block_9$1(ctx);
					if_block3.c();
					transition_in(if_block3, 1);
					if_block3.m(t3.parentNode, t3);
				}
			} else if (if_block3) {
				group_outros();

				transition_out(if_block3, 1, 1, () => {
					if_block3 = null;
				});

				check_outros();
			}

			if (/*shouldRenderPresetSelect*/ ctx[14] && /*cropSelectPresetOptions*/ ctx[6]) {
				if (if_block4) {
					if_block4.p(ctx, dirty);

					if (dirty[0] & /*shouldRenderPresetSelect, cropSelectPresetOptions*/ 16448) {
						transition_in(if_block4, 1);
					}
				} else {
					if_block4 = create_if_block_8$1(ctx);
					if_block4.c();
					transition_in(if_block4, 1);
					if_block4.m(t4.parentNode, t4);
				}
			} else if (if_block4) {
				group_outros();

				transition_out(if_block4, 1, 1, () => {
					if_block4 = null;
				});

				check_outros();
			}

			if (/*cropEnableButtonToggleCropLimit*/ ctx[7]) {
				if (if_block5) {
					if_block5.p(ctx, dirty);

					if (dirty[0] & /*cropEnableButtonToggleCropLimit*/ 128) {
						transition_in(if_block5, 1);
					}
				} else {
					if_block5 = create_if_block_7$2(ctx);
					if_block5.c();
					transition_in(if_block5, 1);
					if_block5.m(if_block5_anchor.parentNode, if_block5_anchor);
				}
			} else if (if_block5) {
				group_outros();

				transition_out(if_block5, 1, 1, () => {
					if_block5 = null;
				});

				check_outros();
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);
			transition_in(if_block1);
			transition_in(if_block2);
			transition_in(if_block3);
			transition_in(if_block4);
			transition_in(if_block5);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			transition_out(if_block1);
			transition_out(if_block2);
			transition_out(if_block3);
			transition_out(if_block4);
			transition_out(if_block5);
			current = false;
		},
		d(detaching) {
			if (if_block0) if_block0.d(detaching);
			if (detaching) detach(t0);
			if (if_block1) if_block1.d(detaching);
			if (detaching) detach(t1);
			if (if_block2) if_block2.d(detaching);
			if (detaching) detach(t2);
			if (if_block3) if_block3.d(detaching);
			if (detaching) detach(t3);
			if (if_block4) if_block4.d(detaching);
			if (detaching) detach(t4);
			if (if_block5) if_block5.d(detaching);
			if (detaching) detach(if_block5_anchor);
		}
	};
}

// (1149:4) <div slot="header">
function create_header_slot(ctx) {
	let div;
	let toolbar;
	let current;

	toolbar = new Toolbar({
			props: {
				$$slots: { default: [create_default_slot_3$3] },
				$$scope: { ctx }
			}
		});

	return {
		c() {
			div = element("div");
			create_component(toolbar.$$.fragment);
			attr(div, "slot", "header");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			mount_component(toolbar, div, null);
			current = true;
		},
		p(ctx, dirty) {
			const toolbar_changes = {};

			if (dirty[0] & /*viewBounds, $imageCropLimitToImage, locale, cropEnableButtonToggleCropLimit, $selectedPresetIndex, cropSelectPresetOptions, $imageCropAspectRatio, $imageOutputSize, isImageSelectionCentered, $imageSize, $imageRotation, $imageCropMinSize, shouldRenderPresetSelect, $imageFlipX, $imageFlipY, cropEnableButtonFlipVertical, cropEnableButtonFlipHorizontal, cropEnableButtonRotateRight, cropEnableButtonRotateLeft*/ 8387068 | dirty[5] & /*$$scope*/ 16384) {
				toolbar_changes.$$scope = { dirty, ctx };
			}

			toolbar.$set(toolbar_changes);
		},
		i(local) {
			if (current) return;
			transition_in(toolbar.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(toolbar.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			destroy_component(toolbar);
		}
	};
}

// (1266:12) {#if shouldRenderImageSelection && shouldRenderImageSelectionRecenterButton}
function create_if_block_6$2(ctx) {
	let switch_instance;
	let switch_instance_anchor;
	let current;
	var switch_value = Button;

	function switch_props(ctx) {
		return {
			props: {
				label: /*locale*/ ctx[8].cropLabelButtonRecenter,
				icon: /*locale*/ ctx[8].cropIconButtonRecenter,
				onclick: /*handleRecenterAction*/ ctx[90],
				class: "DokaButtonCenter",
				disabled: !/*canCenter*/ ctx[26],
				hideLabel: true,
				style: `opacity: ${/*$recenterOpacity*/ ctx[39]}; transform: translate3d(${/*$recenterOffset*/ ctx[40].x}px, ${/*$recenterOffset*/ ctx[40].y}px, 0)`
			}
		};
	}

	if (switch_value) {
		switch_instance = new switch_value(switch_props(ctx));
	}

	return {
		c() {
			if (switch_instance) create_component(switch_instance.$$.fragment);
			switch_instance_anchor = empty();
		},
		m(target, anchor) {
			if (switch_instance) {
				mount_component(switch_instance, target, anchor);
			}

			insert(target, switch_instance_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const switch_instance_changes = {};
			if (dirty[0] & /*locale*/ 256) switch_instance_changes.label = /*locale*/ ctx[8].cropLabelButtonRecenter;
			if (dirty[0] & /*locale*/ 256) switch_instance_changes.icon = /*locale*/ ctx[8].cropIconButtonRecenter;
			if (dirty[0] & /*canCenter*/ 67108864) switch_instance_changes.disabled = !/*canCenter*/ ctx[26];
			if (dirty[1] & /*$recenterOpacity, $recenterOffset*/ 768) switch_instance_changes.style = `opacity: ${/*$recenterOpacity*/ ctx[39]}; transform: translate3d(${/*$recenterOffset*/ ctx[40].x}px, ${/*$recenterOffset*/ ctx[40].y}px, 0)`;

			if (switch_value !== (switch_value = Button)) {
				if (switch_instance) {
					group_outros();
					const old_component = switch_instance;

					transition_out(old_component.$$.fragment, 1, 0, () => {
						destroy_component(old_component, 1);
					});

					check_outros();
				}

				if (switch_value) {
					switch_instance = new switch_value(switch_props(ctx));
					create_component(switch_instance.$$.fragment);
					transition_in(switch_instance.$$.fragment, 1);
					mount_component(switch_instance, switch_instance_anchor.parentNode, switch_instance_anchor);
				} else {
					switch_instance = null;
				}
			} else if (switch_value) {
				switch_instance.$set(switch_instance_changes);
			}
		},
		i(local) {
			if (current) return;
			if (switch_instance) transition_in(switch_instance.$$.fragment, local);
			current = true;
		},
		o(local) {
			if (switch_instance) transition_out(switch_instance.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(switch_instance_anchor);
			if (switch_instance) destroy_component(switch_instance, detaching);
		}
	};
}

// (1271:12) {#if shouldRenderImageSelection}
function create_if_block_5$2(ctx) {
	let rectmanipulator;
	let current;

	rectmanipulator = new RectManipulator({
			props: {
				rect: /*imageSelectionRectOffset*/ ctx[29],
				visible: /*$isActive*/ ctx[31],
				style: /*cropImageSelectionCornerStyle*/ ctx[1]
			}
		});

	rectmanipulator.$on("resizestart", /*handleSelectionGrab*/ ctx[73]);
	rectmanipulator.$on("resizemove", /*handleSelectionDrag*/ ctx[74]);
	rectmanipulator.$on("resizeend", /*handleSelectionRelease*/ ctx[75]);

	return {
		c() {
			create_component(rectmanipulator.$$.fragment);
		},
		m(target, anchor) {
			mount_component(rectmanipulator, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const rectmanipulator_changes = {};
			if (dirty[0] & /*imageSelectionRectOffset*/ 536870912) rectmanipulator_changes.rect = /*imageSelectionRectOffset*/ ctx[29];
			if (dirty[1] & /*$isActive*/ 1) rectmanipulator_changes.visible = /*$isActive*/ ctx[31];
			if (dirty[0] & /*cropImageSelectionCornerStyle*/ 2) rectmanipulator_changes.style = /*cropImageSelectionCornerStyle*/ ctx[1];
			rectmanipulator.$set(rectmanipulator_changes);
		},
		i(local) {
			if (current) return;
			transition_in(rectmanipulator.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(rectmanipulator.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(rectmanipulator, detaching);
		}
	};
}

// (1283:8) {#if shouldRenderInfoIndicator}
function create_if_block_4$2(ctx) {
	let imageinfo;
	let current;

	imageinfo = new ImageInfo({
			props: {
				width: Math.round(/*$imageCropRect*/ ctx[23].width),
				height: Math.round(/*$imageCropRect*/ ctx[23].height)
			}
		});

	return {
		c() {
			create_component(imageinfo.$$.fragment);
		},
		m(target, anchor) {
			mount_component(imageinfo, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const imageinfo_changes = {};
			if (dirty[0] & /*$imageCropRect*/ 8388608) imageinfo_changes.width = Math.round(/*$imageCropRect*/ ctx[23].width);
			if (dirty[0] & /*$imageCropRect*/ 8388608) imageinfo_changes.height = Math.round(/*$imageCropRect*/ ctx[23].height);
			imageinfo.$set(imageinfo_changes);
		},
		i(local) {
			if (current) return;
			transition_in(imageinfo.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(imageinfo.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(imageinfo, detaching);
		}
	};
}

// (1248:4) <div slot="main">
function create_main_slot(ctx) {
	let div0;
	let div1;
	let t0;
	let measurable_action;
	let interactable_action;
	let t1;
	let current;
	let mounted;
	let dispose;
	let if_block0 = /*shouldRenderImageSelection*/ ctx[28] && /*shouldRenderImageSelectionRecenterButton*/ ctx[30] && create_if_block_6$2(ctx);
	let if_block1 = /*shouldRenderImageSelection*/ ctx[28] && create_if_block_5$2(ctx);
	let if_block2 = /*shouldRenderInfoIndicator*/ ctx[27] && create_if_block_4$2(ctx);

	return {
		c() {
			div0 = element("div");
			div1 = element("div");
			if (if_block0) if_block0.c();
			t0 = space();
			if (if_block1) if_block1.c();
			t1 = space();
			if (if_block2) if_block2.c();
			attr(div1, "class", "DokaStage");
			attr(div0, "slot", "main");
		},
		m(target, anchor) {
			insert(target, div0, anchor);
			append(div0, div1);
			if (if_block0) if_block0.m(div1, null);
			append(div1, t0);
			if (if_block1) if_block1.m(div1, null);
			append(div0, t1);
			if (if_block2) if_block2.m(div0, null);
			current = true;

			if (!mounted) {
				dispose = [
					listen(div1, "measure", /*measure_handler_1*/ ctx[110]),
					action_destroyer(measurable_action = measurable.call(null, div1)),
					listen(div1, "wheel", /*handleWheel*/ ctx[89]),
					listen(div1, "interactionstart", /*handleImageDragStart*/ ctx[79]),
					listen(div1, "interactionupdate", /*handleImageDrag*/ ctx[80]),
					listen(div1, "interactionrelease", /*handleImageDragRelease*/ ctx[82]),
					listen(div1, "interactionend", /*handleImageDragEnd*/ ctx[81]),
					action_destroyer(interactable_action = interactable.call(null, div1, {
						drag: true,
						pinch: true,
						inertia: true,
						matchTarget: true,
						getEventPosition: /*interactable_function*/ ctx[118]
					}))
				];

				mounted = true;
			}
		},
		p(ctx, dirty) {
			if (/*shouldRenderImageSelection*/ ctx[28] && /*shouldRenderImageSelectionRecenterButton*/ ctx[30]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty[0] & /*shouldRenderImageSelection, shouldRenderImageSelectionRecenterButton*/ 1342177280) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_6$2(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(div1, t0);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (/*shouldRenderImageSelection*/ ctx[28]) {
				if (if_block1) {
					if_block1.p(ctx, dirty);

					if (dirty[0] & /*shouldRenderImageSelection*/ 268435456) {
						transition_in(if_block1, 1);
					}
				} else {
					if_block1 = create_if_block_5$2(ctx);
					if_block1.c();
					transition_in(if_block1, 1);
					if_block1.m(div1, null);
				}
			} else if (if_block1) {
				group_outros();

				transition_out(if_block1, 1, 1, () => {
					if_block1 = null;
				});

				check_outros();
			}

			if (interactable_action && is_function(interactable_action.update) && dirty[0] & /*$rootRect*/ 33554432) interactable_action.update.call(null, {
				drag: true,
				pinch: true,
				inertia: true,
				matchTarget: true,
				getEventPosition: /*interactable_function*/ ctx[118]
			});

			if (/*shouldRenderInfoIndicator*/ ctx[27]) {
				if (if_block2) {
					if_block2.p(ctx, dirty);

					if (dirty[0] & /*shouldRenderInfoIndicator*/ 134217728) {
						transition_in(if_block2, 1);
					}
				} else {
					if_block2 = create_if_block_4$2(ctx);
					if_block2.c();
					transition_in(if_block2, 1);
					if_block2.m(div0, null);
				}
			} else if (if_block2) {
				group_outros();

				transition_out(if_block2, 1, 1, () => {
					if_block2 = null;
				});

				check_outros();
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);
			transition_in(if_block1);
			transition_in(if_block2);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			transition_out(if_block1);
			transition_out(if_block2);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div0);
			if (if_block0) if_block0.d();
			if (if_block1) if_block1.d();
			if (if_block2) if_block2.d();
			mounted = false;
			run_all(dispose);
		}
	};
}

// (1291:4) {#if shouldRenderFooter}
function create_if_block$4(ctx) {
	let t;
	let tabpanels;
	let current;
	let if_block = /*shouldRenderTabs*/ ctx[34] && create_if_block_3$2(ctx);

	const tabpanels_spread_levels = [
		{ class: "DokaControlPanels" },
		{ panelClass: "DokaControlPanel" },
		{ panels: /*panels*/ ctx[37] },
		/*tabsConfig*/ ctx[35]
	];

	let tabpanels_props = {
		$$slots: {
			default: [
				create_default_slot_1$3,
				({ panel }) => ({ 167: panel }),
				({ panel }) => [0, 0, 0, 0, 0, panel ? 4096 : 0]
			]
		},
		$$scope: { ctx }
	};

	for (let i = 0; i < tabpanels_spread_levels.length; i += 1) {
		tabpanels_props = assign(tabpanels_props, tabpanels_spread_levels[i]);
	}

	tabpanels = new TabPanels({ props: tabpanels_props });

	return {
		c() {
			if (if_block) if_block.c();
			t = space();
			create_component(tabpanels.$$.fragment);
		},
		m(target, anchor) {
			if (if_block) if_block.m(target, anchor);
			insert(target, t, anchor);
			mount_component(tabpanels, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			if (/*shouldRenderTabs*/ ctx[34]) {
				if (if_block) {
					if_block.p(ctx, dirty);

					if (dirty[1] & /*shouldRenderTabs*/ 8) {
						transition_in(if_block, 1);
					}
				} else {
					if_block = create_if_block_3$2(ctx);
					if_block.c();
					transition_in(if_block, 1);
					if_block.m(t.parentNode, t);
				}
			} else if (if_block) {
				group_outros();

				transition_out(if_block, 1, 1, () => {
					if_block = null;
				});

				check_outros();
			}

			const tabpanels_changes = (dirty[1] & /*panels, tabsConfig*/ 80)
			? get_spread_update(tabpanels_spread_levels, [
					tabpanels_spread_levels[0],
					tabpanels_spread_levels[1],
					dirty[1] & /*panels*/ 64 && { panels: /*panels*/ ctx[37] },
					dirty[1] & /*tabsConfig*/ 16 && get_spread_object(/*tabsConfig*/ ctx[35])
				])
			: {};

			if (dirty[0] & /*$imageRotation, locale, imageZoomLevelMin*/ 16779520 | dirty[1] & /*$imageRotationRange, tabs, $imageZoomLevelRange, $imageZoomLevel*/ 7200 | dirty[5] & /*$$scope, panel*/ 20480) {
				tabpanels_changes.$$scope = { dirty, ctx };
			}

			tabpanels.$set(tabpanels_changes);
		},
		i(local) {
			if (current) return;
			transition_in(if_block);
			transition_in(tabpanels.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(if_block);
			transition_out(tabpanels.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (if_block) if_block.d(detaching);
			if (detaching) detach(t);
			destroy_component(tabpanels, detaching);
		}
	};
}

// (1293:8) {#if shouldRenderTabs}
function create_if_block_3$2(ctx) {
	let tablist;
	let current;

	const tablist_spread_levels = [
		{ class: "DokaControlList" },
		{ tabs: /*tabs*/ ctx[36] },
		/*tabsConfig*/ ctx[35]
	];

	let tablist_props = {
		$$slots: {
			default: [
				create_default_slot_2$3,
				({ tab }) => ({ 168: tab }),
				({ tab }) => [0, 0, 0, 0, 0, tab ? 8192 : 0]
			]
		},
		$$scope: { ctx }
	};

	for (let i = 0; i < tablist_spread_levels.length; i += 1) {
		tablist_props = assign(tablist_props, tablist_spread_levels[i]);
	}

	tablist = new TabList({ props: tablist_props });
	tablist.$on("select", /*select_handler*/ ctx[119]);

	return {
		c() {
			create_component(tablist.$$.fragment);
		},
		m(target, anchor) {
			mount_component(tablist, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const tablist_changes = (dirty[1] & /*tabs, tabsConfig*/ 48)
			? get_spread_update(tablist_spread_levels, [
					tablist_spread_levels[0],
					dirty[1] & /*tabs*/ 32 && { tabs: /*tabs*/ ctx[36] },
					dirty[1] & /*tabsConfig*/ 16 && get_spread_object(/*tabsConfig*/ ctx[35])
				])
			: {};

			if (dirty[5] & /*$$scope, tab*/ 24576) {
				tablist_changes.$$scope = { dirty, ctx };
			}

			tablist.$set(tablist_changes);
		},
		i(local) {
			if (current) return;
			transition_in(tablist.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(tablist.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(tablist, detaching);
		}
	};
}

// (1294:8) <TabList class="DokaControlList" {tabs} {...tabsConfig} on:select={({ detail }) => transformSelected = detail} let:tab={tab}>
function create_default_slot_2$3(ctx) {
	let span;
	let t_value = /*tab*/ ctx[168].label + "";
	let t;

	return {
		c() {
			span = element("span");
			t = text(t_value);
		},
		m(target, anchor) {
			insert(target, span, anchor);
			append(span, t);
		},
		p(ctx, dirty) {
			if (dirty[5] & /*tab*/ 8192 && t_value !== (t_value = /*tab*/ ctx[168].label + "")) set_data(t, t_value);
		},
		d(detaching) {
			if (detaching) detach(span);
		}
	};
}

// (1310:43) 
function create_if_block_2$4(ctx) {
	let rangeinput;
	let current;

	rangeinput = new RangeInput({
			props: {
				elasticity: /*elasticityMultiplier*/ ctx[50] * /*rangeInputElasticity*/ ctx[51],
				base: imageZoomLevelBase,
				min: /*imageZoomLevelMin*/ ctx[24],
				max: imageZoomLevelMax,
				valueMin: /*$imageZoomLevelRange*/ ctx[42][0],
				valueMax: /*$imageZoomLevelRange*/ ctx[42][1],
				value: /*$imageZoomLevel*/ ctx[43],
				labelReset: /*locale*/ ctx[8].labelReset,
				valueLabel: `${Math.round(/*$imageZoomLevel*/ ctx[43] * 100)}%`,
				oninputstart: /*handleResizeStart*/ ctx[86],
				oninputmove: /*handleResizeMove*/ ctx[87],
				oninputend: /*handleResizeEnd*/ ctx[88]
			}
		});

	return {
		c() {
			create_component(rangeinput.$$.fragment);
		},
		m(target, anchor) {
			mount_component(rangeinput, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const rangeinput_changes = {};
			if (dirty[0] & /*imageZoomLevelMin*/ 16777216) rangeinput_changes.min = /*imageZoomLevelMin*/ ctx[24];
			if (dirty[1] & /*$imageZoomLevelRange*/ 2048) rangeinput_changes.valueMin = /*$imageZoomLevelRange*/ ctx[42][0];
			if (dirty[1] & /*$imageZoomLevelRange*/ 2048) rangeinput_changes.valueMax = /*$imageZoomLevelRange*/ ctx[42][1];
			if (dirty[1] & /*$imageZoomLevel*/ 4096) rangeinput_changes.value = /*$imageZoomLevel*/ ctx[43];
			if (dirty[0] & /*locale*/ 256) rangeinput_changes.labelReset = /*locale*/ ctx[8].labelReset;
			if (dirty[1] & /*$imageZoomLevel*/ 4096) rangeinput_changes.valueLabel = `${Math.round(/*$imageZoomLevel*/ ctx[43] * 100)}%`;
			rangeinput.$set(rangeinput_changes);
		},
		i(local) {
			if (current) return;
			transition_in(rangeinput.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(rangeinput.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(rangeinput, detaching);
		}
	};
}

// (1300:12) {#if panel === tabs[0].id }
function create_if_block_1$6(ctx) {
	let imagerotator;
	let current;

	imagerotator = new ImageRotator({
			props: {
				elasticity: /*elasticityMultiplier*/ ctx[50] * /*rangeInputElasticity*/ ctx[51],
				rotation: /*$imageRotation*/ ctx[11],
				labelReset: /*locale*/ ctx[8].labelReset,
				valueMin: /*$imageRotationRange*/ ctx[41][0],
				valueMax: /*$imageRotationRange*/ ctx[41][1],
				oninputstart: /*handleRotateStart*/ ctx[76],
				oninputmove: /*handleRotateMove*/ ctx[77],
				oninputend: /*handleRotateEnd*/ ctx[78]
			}
		});

	return {
		c() {
			create_component(imagerotator.$$.fragment);
		},
		m(target, anchor) {
			mount_component(imagerotator, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const imagerotator_changes = {};
			if (dirty[0] & /*$imageRotation*/ 2048) imagerotator_changes.rotation = /*$imageRotation*/ ctx[11];
			if (dirty[0] & /*locale*/ 256) imagerotator_changes.labelReset = /*locale*/ ctx[8].labelReset;
			if (dirty[1] & /*$imageRotationRange*/ 1024) imagerotator_changes.valueMin = /*$imageRotationRange*/ ctx[41][0];
			if (dirty[1] & /*$imageRotationRange*/ 1024) imagerotator_changes.valueMax = /*$imageRotationRange*/ ctx[41][1];
			imagerotator.$set(imagerotator_changes);
		},
		i(local) {
			if (current) return;
			transition_in(imagerotator.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(imagerotator.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(imagerotator, detaching);
		}
	};
}

// (1299:8) <TabPanels class="DokaControlPanels" panelClass="DokaControlPanel" {panels} {...tabsConfig} let:panel>
function create_default_slot_1$3(ctx) {
	let current_block_type_index;
	let if_block;
	let if_block_anchor;
	let current;
	const if_block_creators = [create_if_block_1$6, create_if_block_2$4];
	const if_blocks = [];

	function select_block_type(ctx, dirty) {
		if (/*panel*/ ctx[167] === /*tabs*/ ctx[36][0].id) return 0;
		if (/*panel*/ ctx[167] === /*tabs*/ ctx[36][1].id) return 1;
		return -1;
	}

	if (~(current_block_type_index = select_block_type(ctx))) {
		if_block = if_blocks[current_block_type_index] = if_block_creators[current_block_type_index](ctx);
	}

	return {
		c() {
			if (if_block) if_block.c();
			if_block_anchor = empty();
		},
		m(target, anchor) {
			if (~current_block_type_index) {
				if_blocks[current_block_type_index].m(target, anchor);
			}

			insert(target, if_block_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			let previous_block_index = current_block_type_index;
			current_block_type_index = select_block_type(ctx);

			if (current_block_type_index === previous_block_index) {
				if (~current_block_type_index) {
					if_blocks[current_block_type_index].p(ctx, dirty);
				}
			} else {
				if (if_block) {
					group_outros();

					transition_out(if_blocks[previous_block_index], 1, 1, () => {
						if_blocks[previous_block_index] = null;
					});

					check_outros();
				}

				if (~current_block_type_index) {
					if_block = if_blocks[current_block_type_index];

					if (!if_block) {
						if_block = if_blocks[current_block_type_index] = if_block_creators[current_block_type_index](ctx);
						if_block.c();
					}

					transition_in(if_block, 1);
					if_block.m(if_block_anchor.parentNode, if_block_anchor);
				} else {
					if_block = null;
				}
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block);
			current = true;
		},
		o(local) {
			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			if (~current_block_type_index) {
				if_blocks[current_block_type_index].d(detaching);
			}

			if (detaching) detach(if_block_anchor);
		}
	};
}

// (1290:4) <div slot="footer" style={footerStyle}>
function create_footer_slot(ctx) {
	let div;
	let current;
	let if_block = /*shouldRenderFooter*/ ctx[33] && create_if_block$4(ctx);

	return {
		c() {
			div = element("div");
			if (if_block) if_block.c();
			attr(div, "slot", "footer");
			attr(div, "style", /*footerStyle*/ ctx[38]);
		},
		m(target, anchor) {
			insert(target, div, anchor);
			if (if_block) if_block.m(div, null);
			current = true;
		},
		p(ctx, dirty) {
			if (/*shouldRenderFooter*/ ctx[33]) {
				if (if_block) {
					if_block.p(ctx, dirty);

					if (dirty[1] & /*shouldRenderFooter*/ 4) {
						transition_in(if_block, 1);
					}
				} else {
					if_block = create_if_block$4(ctx);
					if_block.c();
					transition_in(if_block, 1);
					if_block.m(div, null);
				}
			} else if (if_block) {
				group_outros();

				transition_out(if_block, 1, 1, () => {
					if_block = null;
				});

				check_outros();
			}

			if (!current || dirty[1] & /*footerStyle*/ 128) {
				attr(div, "style", /*footerStyle*/ ctx[38]);
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block);
			current = true;
		},
		o(local) {
			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			if (if_block) if_block.d();
		}
	};
}

// (1148:0) <Util hasHeader={shouldRenderToolbar} on:measure bind:root>
function create_default_slot$5(ctx) {
	let t0;
	let t1;

	return {
		c() {
			t0 = space();
			t1 = space();
		},
		m(target, anchor) {
			insert(target, t0, anchor);
			insert(target, t1, anchor);
		},
		p: noop$1,
		i: noop$1,
		o: noop$1,
		d(detaching) {
			if (detaching) detach(t0);
			if (detaching) detach(t1);
		}
	};
}

function create_fragment$n(ctx) {
	let util;
	let updating_root;
	let current;

	function util_root_binding(value) {
		/*util_root_binding*/ ctx[120].call(null, value);
	}

	let util_props = {
		hasHeader: /*shouldRenderToolbar*/ ctx[32],
		$$slots: {
			default: [create_default_slot$5],
			footer: [create_footer_slot],
			main: [create_main_slot],
			header: [create_header_slot]
		},
		$$scope: { ctx }
	};

	if (/*root*/ ctx[10] !== void 0) {
		util_props.root = /*root*/ ctx[10];
	}

	util = new Util({ props: util_props });
	binding_callbacks.push(() => bind(util, "root", util_root_binding));
	util.$on("measure", /*measure_handler*/ ctx[121]);

	return {
		c() {
			create_component(util.$$.fragment);
		},
		m(target, anchor) {
			mount_component(util, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const util_changes = {};
			if (dirty[1] & /*shouldRenderToolbar*/ 2) util_changes.hasHeader = /*shouldRenderToolbar*/ ctx[32];

			if (dirty[0] & /*$imageRotation, locale, imageZoomLevelMin, transformSelected, $imageCropRect, shouldRenderInfoIndicator, $rootRect, imageSelectionRectOffset, cropImageSelectionCornerStyle, shouldRenderImageSelection, canCenter, shouldRenderImageSelectionRecenterButton, viewBounds, $imageCropLimitToImage, cropEnableButtonToggleCropLimit, $selectedPresetIndex, cropSelectPresetOptions, $imageCropAspectRatio, $imageOutputSize, isImageSelectionCentered, $imageSize, $imageCropMinSize, shouldRenderPresetSelect, $imageFlipX, $imageFlipY, cropEnableButtonFlipVertical, cropEnableButtonFlipHorizontal, cropEnableButtonRotateRight, cropEnableButtonRotateLeft*/ 2147482622 | dirty[1] & /*footerStyle, panels, tabsConfig, $imageRotationRange, tabs, $imageZoomLevelRange, $imageZoomLevel, shouldRenderTabs, shouldRenderFooter, $isActive, $recenterOpacity, $recenterOffset*/ 8189 | dirty[5] & /*$$scope*/ 16384) {
				util_changes.$$scope = { dirty, ctx };
			}

			if (!updating_root && dirty[0] & /*root*/ 1024) {
				updating_root = true;
				util_changes.root = /*root*/ ctx[10];
				add_flush_callback(() => updating_root = false);
			}

			util.$set(util_changes);
		},
		i(local) {
			if (current) return;
			transition_in(util.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(util.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(util, detaching);
		}
	};
}

const imageZoomLevelMax = 1;
const imageZoomLevelBase = 0;

function instance$n($$self, $$props, $$invalidate) {
	let $imageRotation;
	let $imageFlipY;
	let $imageFlipX;
	let $selectedPresetIndex;
	let $imageCropAspectRatio;
	let $imageOutputSize;
	let $imageSize;
	let $imageCropMinSize;
	let $imageCropLimitToImage;
	let $isInteracting;
	let $imageSelectionRectSnapshot;
	let $imageSelectionRect;
	let $presentationScalar;
	let $imageCropMaxSize;
	let $imageSelectionRectIntent;
	let $utilRect;
	let $imageCropRectOrigin;
	let $imageCropRect;
	let $imageCropRectIntent;
	let $imageCropRange;
	let $rootRect;
	let $stageRect;
	let $imageCropRangeAspectRatio;
	let $imageSelectionRectPresentation;
	let $env;
	let $imageScalar;

	let $isActive,
		$$unsubscribe_isActive = noop$1,
		$$subscribe_isActive = () => ($$unsubscribe_isActive(), $$unsubscribe_isActive = subscribe(isActive, $$value => $$invalidate(31, $isActive = $$value)), isActive);

	let $imagePreviewModifiers;
	let $footerOffset;
	let $recenterOpacity;
	let $recenterOffset;
	let $imageRotationRange;
	let $imageZoomLevelRange;
	let $imageZoomLevel;
	$$self.$$.on_destroy.push(() => $$unsubscribe_isActive());
	const name = "crop";
	let { isActive } = $$props;
	$$subscribe_isActive();
	let { stores } = $$props;
	let { cropImageSelectionCornerStyle = "circle" } = $$props; // 'circle', 'hook'
	let { cropAutoCenterImageSelectionTimeout = undefined } = $$props;
	let { cropEnableZoomMatchImageAspectRatio = true } = $$props;
	let { cropEnableRotationInput = true } = $$props;
	let { cropEnableZoomInput = true } = $$props;
	let { cropEnableImageSelection = true } = $$props;
	let { cropEnableInfoIndicator = false } = $$props;
	let { cropEnableZoomTowardsWheelPosition = true } = $$props;
	let { cropEnableLimitWheelInputToCropSelection = true } = $$props;
	let { cropEnableCenterImageSelection = true } = $$props;
	let { cropEnableButtonRotateLeft = true } = $$props;
	let { cropEnableButtonRotateRight = false } = $$props;
	let { cropEnableButtonFlipHorizontal = true } = $$props;
	let { cropEnableButtonFlipVertical = false } = $$props;
	let { cropSelectPresetOptions = undefined } = $$props;
	let { cropEnableSelectPreset = true } = $$props;
	let { cropEnableButtonToggleCropLimit = false } = $$props;
	let { locale = {} } = $$props;
	let { tools = [] } = $$props;

	const { history, env, isInteracting, rootRect, stageRect, utilRect, elasticityMultiplier, rangeInputElasticity, presentationScalar, // effect filtering
	imagePreviewModifiers, // crop selection
	imageFlipX, imageFlipY, imageRotation, imageRotationRange, imageOutputSize, imageSelectionRect, imageSelectionRectSnapshot, imageSelectionRectIntent, imageSelectionRectPresentation, imageCropRectIntent, imageCropRectOrigin, imageCropRect, imageCropMinSize, imageCropMaxSize, imageCropRange, imageCropAspectRatio, imageCropRectAspectRatio, imageCropLimitToImage, imageSize, imageScalar } = stores; // the actual limited rectangle
	// used to calculate rectangle while dragging
	// can be used to set set intended rectangle
	// readonly

	component_subscribe($$self, env, value => $$invalidate(149, $env = value));
	component_subscribe($$self, isInteracting, value => $$invalidate(129, $isInteracting = value));
	component_subscribe($$self, rootRect, value => $$invalidate(25, $rootRect = value));
	component_subscribe($$self, stageRect, value => $$invalidate(139, $stageRect = value));
	component_subscribe($$self, utilRect, value => $$invalidate(135, $utilRect = value));
	component_subscribe($$self, presentationScalar, value => $$invalidate(132, $presentationScalar = value));
	component_subscribe($$self, imagePreviewModifiers, value => $$invalidate(152, $imagePreviewModifiers = value));
	component_subscribe($$self, imageFlipX, value => $$invalidate(13, $imageFlipX = value));
	component_subscribe($$self, imageFlipY, value => $$invalidate(12, $imageFlipY = value));
	component_subscribe($$self, imageRotation, value => $$invalidate(11, $imageRotation = value));
	component_subscribe($$self, imageRotationRange, value => $$invalidate(41, $imageRotationRange = value));
	component_subscribe($$self, imageOutputSize, value => $$invalidate(18, $imageOutputSize = value));
	component_subscribe($$self, imageSelectionRect, value => $$invalidate(131, $imageSelectionRect = value));
	component_subscribe($$self, imageSelectionRectSnapshot, value => $$invalidate(130, $imageSelectionRectSnapshot = value));
	component_subscribe($$self, imageSelectionRectIntent, value => $$invalidate(134, $imageSelectionRectIntent = value));
	component_subscribe($$self, imageSelectionRectPresentation, value => $$invalidate(143, $imageSelectionRectPresentation = value));
	component_subscribe($$self, imageCropRectIntent, value => $$invalidate(137, $imageCropRectIntent = value));
	component_subscribe($$self, imageCropRectOrigin, value => $$invalidate(136, $imageCropRectOrigin = value));
	component_subscribe($$self, imageCropRect, value => $$invalidate(23, $imageCropRect = value));
	component_subscribe($$self, imageCropMinSize, value => $$invalidate(21, $imageCropMinSize = value));
	component_subscribe($$self, imageCropMaxSize, value => $$invalidate(133, $imageCropMaxSize = value));
	component_subscribe($$self, imageCropRange, value => $$invalidate(138, $imageCropRange = value));
	component_subscribe($$self, imageCropAspectRatio, value => $$invalidate(17, $imageCropAspectRatio = value));
	component_subscribe($$self, imageCropLimitToImage, value => $$invalidate(22, $imageCropLimitToImage = value));
	component_subscribe($$self, imageSize, value => $$invalidate(20, $imageSize = value));
	component_subscribe($$self, imageScalar, value => $$invalidate(151, $imageScalar = value));

	//
	// resizing crop
	//
	let presentationScalarSnapshot;

	let imageSelectionRectMinSize;
	let imageSelectionRectMaxSize;

	const handleSelectionGrab = () => {
		// now interacting
		set_store_value(isInteracting, $isInteracting = true, $isInteracting);

		// we remember the current view rect and crop rect, because that is the crop rect we use as a starting point to transform while dragging
		set_store_value(imageSelectionRectSnapshot, $imageSelectionRectSnapshot = rectClone($imageSelectionRect), $imageSelectionRectSnapshot);

		// remember current scalar so we can update min and max size correctly
		presentationScalarSnapshot = $presentationScalar;

		imageSelectionRectMinSize = sizeScale(sizeClone($imageCropMinSize), presentationScalarSnapshot);
		imageSelectionRectMaxSize = sizeScale(sizeClone($imageCropMaxSize), presentationScalarSnapshot);
	};

	const handleSelectionDrag = ({ detail }) => {
		const { boundsLimited, boundsIntent } = translateSelection(detail.direction, detail.translation);

		// update actual image selection rectangle
		set_store_value(imageSelectionRectIntent, $imageSelectionRectIntent = boundsIntent, $imageSelectionRectIntent); // intent needs to be updated first because has no listeners attached

		set_store_value(imageSelectionRect, $imageSelectionRect = boundsLimited, $imageSelectionRect);
	};

	const handleSelectionRelease = ({ detail }) => {
		const { boundsLimited } = translateSelection(detail.direction, detail.translation);

		// we're no longer interacting with the image selection, we need to set this here so the image selection presentation is animated when applying the new bounds below
		set_store_value(isInteracting, $isInteracting = false, $isInteracting);

		// no more intent as we're finalizing the selection, so before setting the final selection, we set this to undefined | intent needs to be updated first because has no listeners attached
		set_store_value(imageSelectionRectIntent, $imageSelectionRectIntent = undefined, $imageSelectionRectIntent);

		// confirm the limited rect if actually made a change
		if (vectorLength(detail.translation)) {
			set_store_value(imageSelectionRect, $imageSelectionRect = boundsLimited, $imageSelectionRect);
			history.write();
		}

		// need to set this to undefined after setting the final rect, the snapshot is used to calculate the crop rect transform
		set_store_value(imageSelectionRectSnapshot, $imageSelectionRectSnapshot = undefined, $imageSelectionRectSnapshot);
	};

	const translateSelection = (target, translate) => {
		// - selection may grow to max bounds (util bounds)
		// - the image preview(!) is scaled to fit the selection, actual image size is not affected
		// - we need to make sure the selection adheres to the aspect ratio of the min size
		const directionTranslation = { target, translate };

		let rectIntended = applyRectDirectionTranslation($imageSelectionRectSnapshot, directionTranslation, { aspectRatio: $imageCropAspectRatio });
		let cropAspectRatioLimited;

		// size in crop rect
		const cropSize = sizeCreateFromRect(rectDivide(rectClone(rectIntended), $presentationScalar));

		const imagePolygon = getImagePolygon($imageSize, $imageRotation);
		const imageCenter = quadCentroid(imagePolygon);

		// if one of the edges is small and not both are smaller we need to correct
		if (cropSize.width < $imageCropMinSize.width || cropSize.height < $imageCropMinSize.height) {
			// if moving towards the center of the crop, it can't exceed bounds
			const translateUp = translate.y < 0;

			const translateRight = translate.x > 0;
			const translateLeft = translate.x < 0;
			const translateDown = translate.y > 0;
			const couldExceedBounds = target === "t" && translateUp || target === "r" && translateRight || target === "b" && translateDown || target === "l" && translateLeft || target === "tr" && (translateRight || translateUp) || target === "tl" && (translateLeft || translateUp) || target === "br" && (translateRight || translateDown) || target === "bl" && (translateLeft || translateDown);

			// need the aspect ratio of the crop to determine if it violates min size
			const cropAspectRatio = rectAspectRatio(cropSize);

			// find the maximum size for the current aspect ratio
			const cropSizeMax = getMaxSizeInRect($imageSize, $imageRotation, cropAspectRatio);

			if (couldExceedBounds && (cropSizeMax.width < $imageCropMinSize.width || cropSizeMax.height < $imageCropMinSize.height)) {
				if ($imageRotation !== 0) {
					const sign = Math.sign($imageRotation);
					const turns = Math.round(Math.abs($imageRotation) / HALF_PI) * HALF_PI;
					const value = $imageRotation - sign * turns;
					const imageIsRotated = turns / HALF_PI % 2 === 1;
					const imageWidth = imageIsRotated ? $imageSize.height : $imageSize.width;
					const imageHeight = imageIsRotated ? $imageSize.width : $imageSize.height;
					const r = Math.abs(value);
					const sin = Math.sin(r);
					const cos = Math.cos(r);

					if (cropSize.width < $imageCropMinSize.width) {
						// width doesn't fit, let's limit the width to the min size
						cropSize.width = $imageCropMinSize.width;

						// And now calculate the height
						//  - rotation = .15
						//  - image size = 384 x 288
						//  - crop size width = 200
						// height = 288 - (Math.sin(.15) * 200) / Math.cos(.15)
						const w = cos * cropSize.width + sin * cropSize.height;

						const h = sin * cropSize.width + cos * cropSize.height;
						const dx = imageWidth - w;
						const dy = imageHeight - h;

						if (dx < dy) {
							cropSize.height = (imageWidth - cos * cropSize.width) / sin;
						} else if (dy < dx) {
							cropSize.height = (imageHeight - sin * cropSize.width) / cos;
						}
					}

					if (cropSize.height < $imageCropMinSize.height) {
						// height doesn't fit, let's limit the height to the min size
						cropSize.height = $imageCropMinSize.height;

						// And now calculate the width
						//  - rotation = .15
						//  - image size = 384 x 288
						//  - crop size height = 200
						const w = cos * cropSize.width + sin * cropSize.height;

						const h = sin * cropSize.width + cos * cropSize.height;
						const dx = imageWidth - w;
						const dy = imageHeight - h;

						if (dx < dy) {
							// (384 - (Math.sin(.15) * 250)) / Math.cos(.15)
							cropSize.width = (imageWidth - sin * cropSize.height) / cos;
						} else if (dy < dx) {
							// (288 - (Math.cos(.15) * 250)) / Math.sin(.15)
							cropSize.width = (imageHeight - cos * cropSize.height) / sin;
						}
					}
				} else {
					if (cropSize.width < $imageCropMinSize.width) {
						cropSize.width = $imageCropMinSize.width;
						cropSize.height = $imageSize.height;
					}

					if (cropSize.height < $imageCropMinSize.height) {
						cropSize.height = $imageCropMinSize.height;
						cropSize.width = $imageSize.width;
					}
				}

				// we now have a corrected size, let's calculate the new aspect ratio and use that to limit the translation
				cropAspectRatioLimited = rectAspectRatio(cropSize);
			}
		}

		if (cropAspectRatioLimited) {
			rectIntended = applyRectDirectionTranslation($imageSelectionRectSnapshot, directionTranslation, {
				aspectRatio: cropAspectRatioLimited || $imageCropAspectRatio
			});
		}

		let rectLimited = limitRectDirectionTranslation($imageSelectionRectSnapshot, directionTranslation, $utilRect, {
			aspectRatio: $imageCropAspectRatio || cropAspectRatioLimited,
			minSize: imageSelectionRectMinSize,
			maxSize: imageSelectionRectMaxSize
		});

		return {
			boundsLimited: rectLimited,
			boundsIntent: rectIntended
		};
	};

	//
	// rotating
	//
	const handleRotateStart = () => {
		// now interacting with view
		set_store_value(isInteracting, $isInteracting = true, $isInteracting);

		// we need to know the origin of the crop so we can "shrink" the image to fit the crop rect while rotating
		set_store_value(imageCropRectOrigin, $imageCropRectOrigin = rectClone($imageCropRect), $imageCropRectOrigin);
	};

	const handleRotateMove = value => {
		set_store_value(imageRotation, $imageRotation = value, $imageRotation); // will auto validate
	};

	const handleRotateEnd = value => {
		// we're done interacting
		set_store_value(isInteracting, $isInteracting = false, $isInteracting);

		// apply our final rotation value
		set_store_value(imageRotation, $imageRotation = value, $imageRotation);

		history.write();

		// done, so we no longer need to "shrink" the image
		set_store_value(imageCropRectOrigin, $imageCropRectOrigin = undefined, $imageCropRectOrigin);
	};

	//
	// moving
	//
	let interactionCropRect = undefined;

	let interactionCropRectForce = undefined;

	const handleImageDragStart = () => {
		interactionCropRectForce = undefined;
		set_store_value(isInteracting, $isInteracting = true, $isInteracting);
		interactionCropRect = rectClone($imageCropRect);
	};

	const handleImageDrag = ({ detail }) => manipulateImage(detail);

	const handleImageDragEnd = ({ detail }) => {
		// done interacting
		set_store_value(isInteracting, $isInteracting = false, $isInteracting);

		// apply translation to the crop rect only if did make changes
		if (vectorLength(detail.translation) > 0 || detail.scalar !== 0) {
			manipulateImage(detail);
			history.write();
		}

		// no intent (needs to be set before crop rect is updated)
		set_store_value(imageCropRectIntent, $imageCropRectIntent = undefined, $imageCropRectIntent);

		// now done
		interactionCropRect = undefined;
	};

	const handleImageDragRelease = ({ detail }) => {
		interactionCropRectForce = detail.translation;
		set_store_value(isInteracting, $isInteracting = false, $isInteracting);
	};

	const manipulateImage = ({ translation, scalar }) => {
		const imageSelectionRectZoomFactor = Math.min($imageSelectionRect.width / $imageCropRect.width, $imageSelectionRect.height / $imageCropRect.height);
		const scaledTranslation = vectorMultiply(vectorClone(translation), 1 / imageSelectionRectZoomFactor);

		// while we're interacting we apply changes to the original crop rectangle, after we apply a force
		let cropIntent;

		if (!interactionCropRectForce) {
			cropIntent = rectTranslate(rectClone(interactionCropRect), vectorInvert(vectorClone(scaledTranslation)));

			if (typeof scalar !== undefined) {
				rectScale(cropIntent, 1 - scalar);
			}
		} else {
			// we apply the force to the existing crop rect so animation is not borked
			const forceTranslation = vectorSubtract(vectorClone(interactionCropRectForce), translation);

			interactionCropRectForce = translation;
			cropIntent = rectTranslate(rectClone($imageCropRect), forceTranslation);
		}

		// update crop rect
		set_store_value(imageCropRectIntent, $imageCropRectIntent = cropIntent, $imageCropRectIntent); // auto calculates an elastic effect if bounds exceeded (needs to be set before crop rect is updated)

		set_store_value(imageCropRect, $imageCropRect = cropIntent, $imageCropRect); // auto limits to bounds if needed
	};

	const calculateZoomLevel = (imageSize, size) => {
		return 1 - 1 / Math.min(imageSize.width / size.width, imageSize.height / size.height);
	};

	const imageCropRangeAspectRatio = derived([imageCropRange, imageCropRect], ([$imageCropRange, $imageCropRect], set) => {
		if (!$imageCropRect) return;
		const [minSize, maxSize] = $imageCropRange;
		const aspectRatio = rectAspectRatio($imageCropRect);

		set([
			sizeCreateFromRect(rectApply(rectCoverRect(minSize, aspectRatio), fixPrecision)),
			sizeCreateFromRect(rectApply(rectContainRect(maxSize, aspectRatio), fixPrecision))
		]);
	});

	component_subscribe($$self, imageCropRangeAspectRatio, value => $$invalidate(140, $imageCropRangeAspectRatio = value));

	// this is the max value range that can be set (triggers white range indicator bar)
	const imageZoomLevelRange = derived(
		[
			imageSize,
			imageCropLimitToImage,
			imageCropMinSize,
			imageCropMaxSize,
			imageCropRange,
			imageCropRectAspectRatio
		],
		([
				$imageSize,
				$imageCropLimitToImage,
				$imageCropMinSize,
				$imageCropMaxSize,
				$imageCropRange,
				$imageCropRectAspectRatio
			], set) => {
			if (!$imageSize) return;
			const rangeMinSize = $imageCropRange[0];
			const rangeMaxSize = $imageCropRange[1];
			let minZoom;
			let maxZoom;

			// can't zoom out
			if ($imageCropLimitToImage) {
				minZoom = calculateZoomLevel($imageSize, rangeMaxSize);
				maxZoom = Math.min(rangeMinSize.width / $imageCropMinSize.width, rangeMinSize.height / $imageCropMinSize.height);
			} else {
				maxZoom = 1;
				minZoom = -1;
			}

			const range = [fixPrecision(minZoom), fixPrecision(maxZoom)];
			set(range);
		}
	);

	component_subscribe($$self, imageZoomLevelRange, value => $$invalidate(42, $imageZoomLevelRange = value));

	const imageZoomLevel = derived([imageSize, imageCropRect, imageCropRange, imageCropLimitToImage], ([$imageSize, $imageCropRect, $imageCropRange, $imageCropLimitToImage], set) => {
		// need to check if this value is set, could be that it's empty while loading a new image
		if (!$imageSize || !$imageCropRect) return set(0);

		let z;
		const rangeMinSize = $imageCropRange[0];
		const rangeMaxSize = $imageCropRange[1];
		const currentCropWidth = $imageCropRect.width;
		const currentCropHeight = $imageCropRect.height;
		const currentCropAspectRatio = rectAspectRatio($imageCropRect);
		const imageCropMaxRect = rectContainRect($imageSize, currentCropAspectRatio);

		if (currentCropWidth <= imageCropMaxRect.width || currentCropHeight <= imageCropMaxRect.height) {
			// zoomed in
			const w = imageCropMaxRect.width - rangeMinSize.width;

			const h = imageCropMaxRect.height - rangeMinSize.height;
			const r = rectCoverRect({ width: w, height: h }, currentCropAspectRatio);
			z = 1 - Math.min((currentCropWidth - rangeMinSize.width) / w, (currentCropHeight - rangeMinSize.height) / h);
		} else {
			// zoomed out
			const w = rangeMaxSize.width - imageCropMaxRect.width;

			const h = rangeMaxSize.height - imageCropMaxRect.height;
			const r = rectContainRect({ width: w, height: h }, currentCropAspectRatio);
			z = -Math.min((currentCropWidth - imageCropMaxRect.width) / r.width, (currentCropHeight - imageCropMaxRect.height) / r.height);
		}

		set(z);
	});

	component_subscribe($$self, imageZoomLevel, value => $$invalidate(43, $imageZoomLevel = value));

	const snapshotCropRect = () => {
		interactionCropRect = rectClone($imageCropRect);
	};

	const resizeImage = zoom => {
		const aspectRatio = rectAspectRatio(interactionCropRect);
		let targetWidth;
		let targetHeight;
		let r;
		const imageCropMaxRect = rectContainRect($imageSize, aspectRatio);

		if (zoom >= 0) {
			// zoom in
			const rangeWidth = imageCropMaxRect.width - $imageCropRange[0].width;

			const rangeHeight = imageCropMaxRect.height - $imageCropRange[0].height;
			targetWidth = imageCropMaxRect.width - rangeWidth * zoom;
			targetHeight = imageCropMaxRect.height - rangeHeight * zoom;
			r = rectCoverRect({ width: targetWidth, height: targetHeight }, aspectRatio);
		} else {
			// zoom out
			const rangeWidth = $imageCropRange[1].width - imageCropMaxRect.width;

			const rangeHeight = $imageCropRange[1].height - imageCropMaxRect.height;
			targetWidth = imageCropMaxRect.width + rangeWidth * -zoom;
			targetHeight = imageCropMaxRect.height + rangeHeight * -zoom;
			r = rectContainRect({ width: targetWidth, height: targetHeight }, aspectRatio);
		}

		targetWidth = r.width;
		targetHeight = r.height;
		const targetX = interactionCropRect.x + interactionCropRect.width * 0.5 - targetWidth * 0.5;
		const targetY = interactionCropRect.y + interactionCropRect.height * 0.5 - targetHeight * 0.5;

		set_store_value(
			imageCropRect,
			$imageCropRect = {
				x: targetX,
				y: targetY,
				width: targetWidth,
				height: targetHeight
			},
			$imageCropRect
		);
	};

	const handleResizeStart = () => {
		// now interacting with view
		set_store_value(isInteracting, $isInteracting = true, $isInteracting);

		snapshotCropRect();
	};

	const handleResizeMove = value => {
		// value range from 0 (no zoom) to 1 (100% zoom, 1x1 pixel)
		resizeImage(value);
	};

	const handleResizeEnd = value => {
		resizeImage(value);
		history.write();

		// we're done interacting
		set_store_value(isInteracting, $isInteracting = false, $isInteracting);

		// now done
		interactionCropRect = undefined;
	};

	//
	// resizing with wheel
	//
	let zoomHistoryTimeoutId;

	const handleWheel = e => {
		const stageWheelPosition = getEventPositionInStage(e, $rootRect, $stageRect);

		// only block input if wheel is used within image rectangle
		if (cropEnableLimitWheelInputToCropSelection && !rectContainsPoint($imageSelectionRect, stageWheelPosition)) return;

		// now interacting
		set_store_value(isInteracting, $isInteracting = true, $isInteracting);

		// don't run default actions, prevent other actions from running
		e.preventDefault();

		e.stopPropagation();

		// get current crop rect
		const currentCropRect = rectClone($imageCropRect);

		// convert wheel delta to scalar
		const delta = getWheelDelta(e);

		const scalar = 1 + delta / 100;

		// if already zoomed in, block further zoom in instructions
		const isMinSize = Math.min($imageCropRect.width / $imageCropMinSize.width, $imageCropRect.height / $imageCropMinSize.height) === 1;

		// if is fully zoomed out and trying to zoom out more and crop shape is free, fit image aspect ratio
		if (cropEnableZoomMatchImageAspectRatio) {
			const imageIsRotatedSideways = isRotatedSideways($imageRotation);

			const imageIsFullyZoomedOut = imageIsRotatedSideways
			? Math.max($imageCropRect.width / $imageSize.height, $imageCropRect.height / $imageSize.width) === 1
			: Math.max($imageCropRect.width / $imageSize.width, $imageCropRect.height / $imageSize.height) === 1;

			const canCropFreely = $imageCropAspectRatio === undefined;

			if (canCropFreely && imageIsFullyZoomedOut && delta > 0 && isImageSelectionCentered) {
				set_store_value(isInteracting, $isInteracting = false, $isInteracting);

				const newCropRect = imageIsRotatedSideways
				? rectCreateFromSize({
						height: $imageSize.width,
						width: $imageSize.height
					})
				: rectCreateFromSize($imageSize);

				// no change, exit
				if (rectEqual(currentCropRect, newCropRect)) return;

				// if we were previously zooming in we need to clear the timeout to prevent two history entries
				clearTimeout(zoomHistoryTimeoutId);

				// test if crop rect in history is same as new crop rect, this is possible when zooming in and out real quick
				if (rectEqual(history.state.crop, newCropRect)) return;

				// store new crop rect
				set_store_value(imageCropRect, $imageCropRect = newCropRect, $imageCropRect);

				history.write();
				return;
			}
		}

		// by default when zooming, zoom from center of crop rectangle
		let origin = rectCenter($imageCropRect);

		// when zooming in, zoom in on the part of the image below the mouse cursor
		if (cropEnableZoomTowardsWheelPosition && delta < 0 && !isMinSize) {
			const selectionOffset = vectorSubtract(vectorClone(stageWheelPosition), $imageSelectionRect);
			const imageSelectionScale = Math.min($imageSelectionRect.width / $imageCropRect.width, $imageSelectionRect.height / $imageCropRect.height);

			// if wheel is inside image selection rectangle (slightly expanded version), we zoom based on position in the rectangle, if not, we use the selection center
			const expandedImageSelection = rectScale(rectClone($imageSelectionRect), 1.1);

			origin = rectContainsPoint(expandedImageSelection, stageWheelPosition)
			? vectorAdd(rectClone($imageCropRect), vectorMultiply(selectionOffset, 1 / imageSelectionScale))
			: origin;
		}

		let newCropRect = rectScale(rectClone($imageCropRect), scalar, origin);

		// determin if is new crop rect exceeds min or max size, if so, limit
		if (!sizeContains($imageCropRangeAspectRatio[1], newCropRect)) {
			// exceeds max size, limit to max size and position at new crop rect center
			newCropRect = rectCreateWithCenter(rectCenter(newCropRect), $imageCropRangeAspectRatio[1]);
		}

		if (!sizeContains(newCropRect, $imageCropRangeAspectRatio[0])) {
			// exceeds min size, limit to min size and position at new crop rect center
			newCropRect = rectCreateWithCenter(rectCenter(newCropRect), $imageCropRangeAspectRatio[0]);
		}

		// no change, exit
		if (rectEqual(currentCropRect, newCropRect, fixPrecision)) {
			set_store_value(isInteracting, $isInteracting = false, $isInteracting);
			return;
		}

		// attempt to update the rectangle, we use fixPrecision so the width and height are nice integers when at max zoom (this makes sure the `isMinSize` variable is set to true at this point)
		set_store_value(imageCropRect, $imageCropRect = rectApply(newCropRect, v => fixPrecision(v, 5)), $imageCropRect);

		// done!
		set_store_value(isInteracting, $isInteracting = false, $isInteracting);

		// write history timer
		clearTimeout(zoomHistoryTimeoutId);

		zoomHistoryTimeoutId = setTimeout(
			() => {
				history.write();
			},
			500
		);
	};

	//
	// recenter
	//
	// the 'measure' event triggers the editor to center and scale up the crop
	const dispatch = createEventDispatcher();

	const handleRecenterAction = () => {
		dispatch("measure", rectClone($utilRect));
	};

	// auto recenter after timeout
	let cropAutoCenterImageSelectionTimeoutId;

	// animations
	const recenterOpacity = spring(0, { precision: 0.0001 });

	component_subscribe($$self, recenterOpacity, value => $$invalidate(39, $recenterOpacity = value));
	const recenterOffset = spring();
	component_subscribe($$self, recenterOffset, value => $$invalidate(40, $recenterOffset = value));
	const footerOffset = spring(0);
	component_subscribe($$self, footerOffset, value => $$invalidate(155, $footerOffset = value));

	//
	// crop selection presets
	//
	const selectedPresetIndex = derived([imageCropAspectRatio, imageOutputSize], ([$cropAspectRatio, $imageOutputSize], set) => {
		if (!cropSelectPresetOptions) return;
		const options = flattenOptions(cropSelectPresetOptions);

		const matchedOptionValue = [...options].// to value
		map(option => option[0]).// sort sizes first
		sort((a, b) => {
			if (isArray(a[0]) && !isArray(b[0])) return 1;
			return -1;
		}).// match value
		find(value => {
			if (isArray(value) && $imageOutputSize) {
				// size + aspect ratio
				const [width, height] = value;

				const outputSizeMatches = $imageOutputSize.width === width && $imageOutputSize.height === height;
				const aspectRatioMatches = $cropAspectRatio === getAspectRatio(width, height);
				return outputSizeMatches && aspectRatioMatches;
			}

			// aspect ratio
			return value === $cropAspectRatio;
		});

		const index = options.// to value
		map(option => option[0]).// find index of matching value
		findIndex(value => isArray(value)
		? arrayEqual(value, matchedOptionValue)
		: value === matchedOptionValue);

		set(index);
	});

	component_subscribe($$self, selectedPresetIndex, value => $$invalidate(16, $selectedPresetIndex = value));

	const getSelectionPresetLabelIcon = selectedIndex => {
		if (!cropSelectPresetOptions) return;
		const selectedValue = flattenOptions(cropSelectPresetOptions)[selectedIndex][0];

		const aspectRatio = !selectedValue
		? undefined
		: isArray(selectedValue)
			? getAspectRatio(selectedValue[0], selectedValue[1])
			: selectedValue;

		const [a, b, c] = !aspectRatio
		? [0.2, 0.3, 0.4]
		: [
				aspectRatio < 1 ? 1 : 0.3,
				aspectRatio === 1 ? 0.85 : 0.5,
				aspectRatio > 1 ? 1 : 0.3
			];

		return `<g fill="currentColor">
        <rect opacity="${a}" x="2" y="4" width="10" height="18" rx="1"/>
        <rect opacity="${b}" x="4" y="8" width="14" height="14" rx="1"/>
        <rect opacity="${c}" x="6" y="12" width="17" height="10" rx="1"/>
    </g>`;
	};

	//
	// crop boundary
	//
	const getCropBoundaryLabelIcon = isBoundToImage => {
		const [a, b, c, d] = isBoundToImage ? [0.3, 1, 0, 0] : [0, 0, 0.3, 1];

		return `<g fill="currentColor">
        <rect opacity="${a}" x="2" y="3" width="20" height="20" rx="1"/>
        <rect opacity="${b}" x="7" y="8" width="10" height="10" rx="1"/>
        <rect opacity="${c}" x="4" y="8" width="14" height="14" rx="1"/>
        <rect opacity="${d}" x="12" y="4" width="10" height="10" rx="1"/>
    </g>`;
	};

	const cropUniqueId = `crop-${getUniqueId()}`;
	let transformInitial = cropEnableRotationInput ? "rotation" : "zoom";
	let transformToolInitial = cropUniqueId + "-" + transformInitial;
	let transformSelected = transformToolInitial;
	let root = undefined;

	function measure_handler_1(event) {
		bubble($$self, event);
	}

	const func = () => {
		set_store_value(imageRotation, $imageRotation -= Math.PI / 2, $imageRotation);
		history.write();
	};

	const func_1 = () => {
		set_store_value(imageRotation, $imageRotation += Math.PI / 2, $imageRotation);
		history.write();
	};

	const func_2 = () => {
		if (isRotatedSideways($imageRotation)) {
			set_store_value(imageFlipY, $imageFlipY = !$imageFlipY, $imageFlipY);
		} else {
			set_store_value(imageFlipX, $imageFlipX = !$imageFlipX, $imageFlipX);
		}

		history.write();
	};

	const func_3 = () => {
		if (isRotatedSideways($imageRotation)) {
			set_store_value(imageFlipX, $imageFlipX = !$imageFlipX, $imageFlipX);
		} else {
			set_store_value(imageFlipY, $imageFlipY = !$imageFlipY, $imageFlipY);
		}

		history.write();
	};

	const func_4 = ({ value }) => {
		if (isArray(value)) {
			set_store_value(imageCropAspectRatio, $imageCropAspectRatio = getAspectRatio(value[0], value[1]), $imageCropAspectRatio);
			set_store_value(imageOutputSize, $imageOutputSize = sizeCreateFromArray(value), $imageOutputSize);
		} else {
			set_store_value(imageCropAspectRatio, $imageCropAspectRatio = value, $imageCropAspectRatio);
		}

		if (isImageSelectionCentered) {
			handleRecenterAction();
		}

		history.write();
	};

	const func_5 = option => {
		// if no aspect ratio found we enable the option by default
		let disabled = false;

		// get aspect ratio for this option
		const optionAspectRatio = isArray(option.value)
		? option.value[0] / option.value[1]
		: option.value;

		// can be undefined in which case we don't need to check anything
		if (optionAspectRatio) {
			const maxCropSize = getMaxSizeInRect($imageSize, $imageRotation, optionAspectRatio);
			disabled = maxCropSize.width < $imageCropMinSize.width || maxCropSize.height < $imageCropMinSize.height;
		}

		// add icon for this option
		option.icon = getSelectionPresetOptionIcon(option.value, { bounds: 14 });

		return { ...option, disabled };
	};

	const func_6 = ({ value }) => {
		set_store_value(imageCropLimitToImage, $imageCropLimitToImage = value, $imageCropLimitToImage);
		history.write();
	};

	const interactable_function = e => getEventPositionInViewport(e);
	const select_handler = ({ detail }) => $$invalidate(9, transformSelected = detail);

	function util_root_binding(value) {
		root = value;
		$$invalidate(10, root);
	}

	function measure_handler(event) {
		bubble($$self, event);
	}

	$$self.$$set = $$props => {
		if ("isActive" in $$props) $$subscribe_isActive($$invalidate(0, isActive = $$props.isActive));
		if ("stores" in $$props) $$invalidate(99, stores = $$props.stores);
		if ("cropImageSelectionCornerStyle" in $$props) $$invalidate(1, cropImageSelectionCornerStyle = $$props.cropImageSelectionCornerStyle);
		if ("cropAutoCenterImageSelectionTimeout" in $$props) $$invalidate(100, cropAutoCenterImageSelectionTimeout = $$props.cropAutoCenterImageSelectionTimeout);
		if ("cropEnableZoomMatchImageAspectRatio" in $$props) $$invalidate(101, cropEnableZoomMatchImageAspectRatio = $$props.cropEnableZoomMatchImageAspectRatio);
		if ("cropEnableRotationInput" in $$props) $$invalidate(102, cropEnableRotationInput = $$props.cropEnableRotationInput);
		if ("cropEnableZoomInput" in $$props) $$invalidate(103, cropEnableZoomInput = $$props.cropEnableZoomInput);
		if ("cropEnableImageSelection" in $$props) $$invalidate(104, cropEnableImageSelection = $$props.cropEnableImageSelection);
		if ("cropEnableInfoIndicator" in $$props) $$invalidate(105, cropEnableInfoIndicator = $$props.cropEnableInfoIndicator);
		if ("cropEnableZoomTowardsWheelPosition" in $$props) $$invalidate(106, cropEnableZoomTowardsWheelPosition = $$props.cropEnableZoomTowardsWheelPosition);
		if ("cropEnableLimitWheelInputToCropSelection" in $$props) $$invalidate(107, cropEnableLimitWheelInputToCropSelection = $$props.cropEnableLimitWheelInputToCropSelection);
		if ("cropEnableCenterImageSelection" in $$props) $$invalidate(108, cropEnableCenterImageSelection = $$props.cropEnableCenterImageSelection);
		if ("cropEnableButtonRotateLeft" in $$props) $$invalidate(2, cropEnableButtonRotateLeft = $$props.cropEnableButtonRotateLeft);
		if ("cropEnableButtonRotateRight" in $$props) $$invalidate(3, cropEnableButtonRotateRight = $$props.cropEnableButtonRotateRight);
		if ("cropEnableButtonFlipHorizontal" in $$props) $$invalidate(4, cropEnableButtonFlipHorizontal = $$props.cropEnableButtonFlipHorizontal);
		if ("cropEnableButtonFlipVertical" in $$props) $$invalidate(5, cropEnableButtonFlipVertical = $$props.cropEnableButtonFlipVertical);
		if ("cropSelectPresetOptions" in $$props) $$invalidate(6, cropSelectPresetOptions = $$props.cropSelectPresetOptions);
		if ("cropEnableSelectPreset" in $$props) $$invalidate(109, cropEnableSelectPreset = $$props.cropEnableSelectPreset);
		if ("cropEnableButtonToggleCropLimit" in $$props) $$invalidate(7, cropEnableButtonToggleCropLimit = $$props.cropEnableButtonToggleCropLimit);
		if ("locale" in $$props) $$invalidate(8, locale = $$props.locale);
		if ("tools" in $$props) $$invalidate(97, tools = $$props.tools);
	};

	let imageZoomLevelMin;
	let imageSelectionOffset;
	let imageSelectionCenter;
	let imageSelectionCenteredRect;
	let isImageSelectionDisplayed;
	let isImageSelectionCentered;
	let isResizingSelection;
	let isMaxSelectionRect;
	let isOverlayMode;
	let canZoomToCenter;
	let canCenter;
	let shouldRenderInfoIndicator;
	let shouldRenderImageSelection;
	let shouldRenderImageSelectionRecenterButton;
	let imageSelectionRectOffset;
	let shouldRenderPresetSelect;
	let hasPlentyVerticalSpace;
	let shouldRenderToolbar;
	let shouldRenderZoomInput;
	let shouldRenderFooter;
	let shouldRenderTabs;
	let tabsConfig;
	let tabs;
	let panels;
	let viewBounds;
	let footerStyle;

	$$self.$$.update = () => {
		if ($$self.$$.dirty[4] & /*$env*/ 33554432) {
			 $$invalidate(148, isOverlayMode = (/overlay/).test($env));
		}

		if ($$self.$$.dirty[3] & /*cropEnableSelectPreset*/ 65536 | $$self.$$.dirty[4] & /*isOverlayMode*/ 16777216) {
			//
			// Transform tabs
			//
			 $$invalidate(14, shouldRenderPresetSelect = cropEnableSelectPreset && !isOverlayMode);
		}

		if ($$self.$$.dirty[0] & /*$rootRect*/ 33554432) {
			 $$invalidate(15, viewBounds = {
				x: 16,
				y: 16,
				width: $rootRect.width - 32,
				height: $rootRect.height - 32
			});
		}

		if ($$self.$$.dirty[4] & /*$utilRect, $imageSelectionRect*/ 2176) {
			// determine if we can center the crop, if we can, show the crop center button
			 $$invalidate(144, imageSelectionCenteredRect = $utilRect && $imageSelectionRect && rectCenterRect($utilRect, $imageSelectionRect));
		}

		if ($$self.$$.dirty[4] & /*$imageSelectionRect, imageSelectionCenteredRect*/ 1048704) {
			 $$invalidate(145, isImageSelectionDisplayed = !!($imageSelectionRect && imageSelectionCenteredRect));
		}

		if ($$self.$$.dirty[4] & /*isImageSelectionDisplayed, $imageSelectionRect, imageSelectionCenteredRect*/ 3145856) {
			 $$invalidate(19, isImageSelectionCentered = isImageSelectionDisplayed && rectEqual($imageSelectionRect, imageSelectionCenteredRect, value => fixPrecision(value, 5)));
		}

		if ($$self.$$.dirty[0] & /*cropEnableButtonRotateLeft, locale, $imageRotation, cropEnableButtonRotateRight, cropEnableButtonFlipHorizontal, $imageFlipY, $imageFlipX, cropEnableButtonFlipVertical, shouldRenderPresetSelect, cropSelectPresetOptions, viewBounds, $selectedPresetIndex, isImageSelectionCentered, $imageSize, $imageCropMinSize, cropEnableButtonToggleCropLimit, $imageCropLimitToImage*/ 7993852) {
			 $$invalidate(97, tools = [
				cropEnableButtonRotateLeft && {
					id: "crop-button-rotate-left",
					component: Button,
					props: {
						label: locale.cropLabelButtonRotateLeft,
						icon: locale.cropIconButtonRotateLeft,
						onclick: () => {
							set_store_value(imageRotation, $imageRotation -= Math.PI / 2, $imageRotation);
							history.write();
						}
					}
				},
				cropEnableButtonRotateRight && {
					id: "crop-button-rotate-right",
					component: Button,
					optional: cropEnableButtonRotateLeft, // if rotate left enabled, rotate right is optional
					props: {
						label: locale.cropLabelButtonRotateRight,
						icon: locale.cropIconButtonRotateRight,
						onclick: () => {
							set_store_value(imageRotation, $imageRotation += Math.PI / 2, $imageRotation);
							history.write();
						}
					}
				},
				cropEnableButtonFlipHorizontal && {
					id: "crop-button-flip-horizontal",
					component: Button,
					props: {
						label: locale.cropLabelButtonFlipHorizontal,
						icon: locale.cropIconButtonFlipHorizontal,
						onclick: () => {
							if (isRotatedSideways($imageRotation)) {
								set_store_value(imageFlipY, $imageFlipY = !$imageFlipY, $imageFlipY);
							} else {
								set_store_value(imageFlipX, $imageFlipX = !$imageFlipX, $imageFlipX);
							}

							history.write();
						}
					}
				},
				cropEnableButtonFlipVertical && {
					id: "crop-button-flip-horizontal",
					component: Button,
					optional: cropEnableButtonFlipHorizontal, // if horizontal flipping enabled, vertical is optional
					props: {
						label: locale.cropLabelButtonFlipVertical,
						icon: locale.cropIconButtonFlipVertical,
						onclick: () => {
							if (isRotatedSideways($imageRotation)) {
								set_store_value(imageFlipX, $imageFlipX = !$imageFlipX, $imageFlipX);
							} else {
								set_store_value(imageFlipY, $imageFlipY = !$imageFlipY, $imageFlipY);
							}

							history.write();
						}
					}
				},
				shouldRenderPresetSelect && cropSelectPresetOptions && {
					id: "crop-select-preset",
					component: Dropdown,
					props: {
						detailPanel: { viewBounds },
						icon: getSelectionPresetLabelIcon($selectedPresetIndex),
						label: locale.cropLabelSelectPreset,
						labelClass: "DokaToolbarContentWide",
						options: cropSelectPresetOptions,
						selectedIndex: $selectedPresetIndex,
						onchange: ({ value }) => {
							if (isArray(value)) {
								set_store_value(imageCropAspectRatio, $imageCropAspectRatio = getAspectRatio(value[0], value[1]), $imageCropAspectRatio);
								set_store_value(imageOutputSize, $imageOutputSize = sizeCreateFromArray(value), $imageOutputSize);
							} else {
								set_store_value(imageCropAspectRatio, $imageCropAspectRatio = value, $imageCropAspectRatio);
							}

							if (isImageSelectionCentered) {
								handleRecenterAction();
							}

							history.write();
						},
						optionMapper: option => {
							// if no aspect ratio found we enable the option by default
							let disabled = false;

							// get aspect ratio for this option
							const optionAspectRatio = isArray(option.value)
							? option.value[0] / option.value[1]
							: option.value;

							// can be undefined in which case we don't need to check anything
							if (optionAspectRatio) {
								const maxCropSize = getMaxSizeInRect($imageSize, $imageRotation, optionAspectRatio);
								disabled = maxCropSize.width < $imageCropMinSize.width || maxCropSize.height < $imageCropMinSize.height;
							}

							// add icon for this option
							option.icon = getSelectionPresetOptionIcon(option.value, { bounds: 14 });

							return { ...option, disabled };
						}
					}
				},
				cropEnableButtonToggleCropLimit && {
					id: "crop-toggle-content-bounds",
					component: Dropdown,
					props: {
						detailPanel: { viewBounds },
						icon: getCropBoundaryLabelIcon($imageCropLimitToImage),
						label: locale.cropLabelCropBoundary,
						labelClass: "DokaToolbarContentWide",
						onchange: ({ value }) => {
							set_store_value(imageCropLimitToImage, $imageCropLimitToImage = value, $imageCropLimitToImage);
							history.write();
						},
						options: [
							[
								true,
								locale.cropLabelCropBoundaryEdge,
								{ icon: getCropBoundaryLabelIcon(true) }
							],
							[
								false,
								locale.cropLabelCropBoundaryNone,
								{ icon: getCropBoundaryLabelIcon(false) }
							]
						]
					}
				}
			].filter(Boolean));
		}

		if ($$self.$$.dirty[0] & /*$imageCropLimitToImage*/ 4194304) {
			 $$invalidate(24, imageZoomLevelMin = $imageCropLimitToImage ? 0 : -1);
		}

		if ($$self.$$.dirty[4] & /*$utilRect, $stageRect*/ 34816) {
			 $$invalidate(141, imageSelectionOffset = $utilRect && vectorCreate(-($stageRect.x - $utilRect.x), -($stageRect.y - $utilRect.y)));
		}

		if ($$self.$$.dirty[4] & /*$imageSelectionRectPresentation, imageSelectionOffset*/ 655360) {
			// normalized crop bounds, we use these to limit the crop interactions to the view
			 $$invalidate(142, imageSelectionCenter = $imageSelectionRectPresentation && vectorCreate(snapToPixel($imageSelectionRectPresentation.x + $imageSelectionRectPresentation.width * 0.5 + imageSelectionOffset.x), snapToPixel($imageSelectionRectPresentation.y + $imageSelectionRectPresentation.height * 0.5 + imageSelectionOffset.y)));
		}

		if ($$self.$$.dirty[4] & /*$imageSelectionRectSnapshot*/ 64) {
			 $$invalidate(146, isResizingSelection = $imageSelectionRectSnapshot != null);
		}

		if ($$self.$$.dirty[4] & /*$utilRect, imageSelectionCenteredRect*/ 1050624) {
			 $$invalidate(147, isMaxSelectionRect = $utilRect && imageSelectionCenteredRect && (imageSelectionCenteredRect.height === $utilRect.height || imageSelectionCenteredRect.width === $utilRect.width));
		}

		if ($$self.$$.dirty[4] & /*isMaxSelectionRect, $presentationScalar, $imageScalar*/ 142606592) {
			 $$invalidate(150, canZoomToCenter = !isMaxSelectionRect && ($presentationScalar < 1 && $imageScalar < 1));
		}

		if ($$self.$$.dirty[0] & /*isImageSelectionCentered*/ 524288 | $$self.$$.dirty[4] & /*isImageSelectionDisplayed, isResizingSelection, canZoomToCenter*/ 73400320) {
			 $$invalidate(26, canCenter = isImageSelectionDisplayed && !isResizingSelection && (!isImageSelectionCentered || canZoomToCenter));
		}

		if ($$self.$$.dirty[0] & /*$imageCropRect*/ 8388608 | $$self.$$.dirty[3] & /*cropEnableInfoIndicator*/ 4096 | $$self.$$.dirty[4] & /*isOverlayMode*/ 16777216) {
			 $$invalidate(27, shouldRenderInfoIndicator = cropEnableInfoIndicator && !!$imageCropRect && !isOverlayMode);
		}

		if ($$self.$$.dirty[4] & /*$imageSelectionRectPresentation, imageSelectionOffset*/ 655360) {
			 $$invalidate(29, imageSelectionRectOffset = $imageSelectionRectPresentation && imageSelectionOffset && {
				x: $imageSelectionRectPresentation.x + imageSelectionOffset.x,
				y: $imageSelectionRectPresentation.y + imageSelectionOffset.y,
				width: $imageSelectionRectPresentation.width,
				height: $imageSelectionRectPresentation.height
			});
		}

		if ($$self.$$.dirty[0] & /*imageSelectionRectOffset*/ 536870912 | $$self.$$.dirty[3] & /*cropEnableImageSelection*/ 2048 | $$self.$$.dirty[4] & /*isOverlayMode*/ 16777216) {
			 $$invalidate(28, shouldRenderImageSelection = cropEnableImageSelection && !!imageSelectionRectOffset && !isOverlayMode);
		}

		if ($$self.$$.dirty[3] & /*cropEnableCenterImageSelection, cropAutoCenterImageSelectionTimeout*/ 32896 | $$self.$$.dirty[4] & /*imageSelectionCenter*/ 262144) {
			 $$invalidate(30, shouldRenderImageSelectionRecenterButton = cropEnableCenterImageSelection && !!imageSelectionCenter && !cropAutoCenterImageSelectionTimeout);
		}

		if ($$self.$$.dirty[0] & /*canCenter*/ 67108864 | $$self.$$.dirty[3] & /*cropAutoCenterImageSelectionTimeout*/ 128 | $$self.$$.dirty[4] & /*$isInteracting, cropAutoCenterImageSelectionTimeoutId*/ 48) {
			 if (canCenter && cropAutoCenterImageSelectionTimeout && !$isInteracting) {
				clearTimeout(cropAutoCenterImageSelectionTimeoutId);
				$$invalidate(128, cropAutoCenterImageSelectionTimeoutId = setTimeout(handleRecenterAction, cropAutoCenterImageSelectionTimeout));
			}
		}

		if ($$self.$$.dirty[4] & /*$isInteracting, cropAutoCenterImageSelectionTimeoutId*/ 48) {
			 if ($isInteracting) {
				clearTimeout(cropAutoCenterImageSelectionTimeoutId);
			}
		}

		if ($$self.$$.dirty[0] & /*canCenter*/ 67108864) {
			 recenterOpacity.set(canCenter ? 1 : 0);
		}

		if ($$self.$$.dirty[4] & /*imageSelectionCenter*/ 262144) {
			 recenterOffset.set(imageSelectionCenter);
		}

		if ($$self.$$.dirty[1] & /*$isActive*/ 1) {
			 footerOffset.set($isActive ? 0 : 20);
		}

		if ($$self.$$.dirty[1] & /*$isActive*/ 1 | $$self.$$.dirty[4] & /*$imagePreviewModifiers*/ 268435456) {
			//
			// enable seeing the image outside of the crop area, and disable the vignette effect
			// 
			 if ($isActive) {
				set_store_value(
					imagePreviewModifiers,
					$imagePreviewModifiers["crop"] = {
						maskOpacity: 0.85,
						maskMarkupOpacity: 0.85
					},
					$imagePreviewModifiers
				);
			} else {
				delete $imagePreviewModifiers["crop"];
			}
		}

		if ($$self.$$.dirty[4] & /*$env*/ 33554432) {
			 $$invalidate(153, hasPlentyVerticalSpace = !(/short/).test($env));
		}

		if ($$self.$$.dirty[4] & /*hasPlentyVerticalSpace*/ 536870912) {
			 $$invalidate(32, shouldRenderToolbar = hasPlentyVerticalSpace);
		}

		if ($$self.$$.dirty[3] & /*cropEnableZoomInput*/ 1024 | $$self.$$.dirty[4] & /*hasPlentyVerticalSpace*/ 536870912) {
			 $$invalidate(154, shouldRenderZoomInput = cropEnableZoomInput && hasPlentyVerticalSpace);
		}

		if ($$self.$$.dirty[3] & /*cropEnableRotationInput*/ 512 | $$self.$$.dirty[4] & /*shouldRenderZoomInput*/ 1073741824) {
			 $$invalidate(33, shouldRenderFooter = cropEnableRotationInput || shouldRenderZoomInput);
		}

		if ($$self.$$.dirty[3] & /*cropEnableRotationInput*/ 512 | $$self.$$.dirty[4] & /*shouldRenderZoomInput*/ 1073741824) {
			 $$invalidate(34, shouldRenderTabs = cropEnableRotationInput && shouldRenderZoomInput);
		}

		if ($$self.$$.dirty[4] & /*shouldRenderZoomInput*/ 1073741824) {
			 if (!shouldRenderZoomInput) {
				$$invalidate(9, transformSelected = transformToolInitial);
			}
		}

		if ($$self.$$.dirty[0] & /*transformSelected*/ 512) {
			 $$invalidate(35, tabsConfig = {
				name: cropUniqueId,
				selected: transformSelected
			});
		}

		if ($$self.$$.dirty[0] & /*locale*/ 256) {
			 $$invalidate(36, tabs = [
				{
					id: cropUniqueId + "-rotation",
					label: locale.cropLabelTabRotation
				},
				{
					id: cropUniqueId + "-zoom",
					label: locale.cropLabelTabZoom
				}
			]);
		}

		if ($$self.$$.dirty[1] & /*tabs*/ 32) {
			 $$invalidate(37, panels = tabs.map(tab => tab.id));
		}

		if ($$self.$$.dirty[5] & /*$footerOffset*/ 1) {
			 $$invalidate(38, footerStyle = $footerOffset
			? `transform: translateY(${$footerOffset}px)`
			: undefined);
		}
	};

	return [
		isActive,
		cropImageSelectionCornerStyle,
		cropEnableButtonRotateLeft,
		cropEnableButtonRotateRight,
		cropEnableButtonFlipHorizontal,
		cropEnableButtonFlipVertical,
		cropSelectPresetOptions,
		cropEnableButtonToggleCropLimit,
		locale,
		transformSelected,
		root,
		$imageRotation,
		$imageFlipY,
		$imageFlipX,
		shouldRenderPresetSelect,
		viewBounds,
		$selectedPresetIndex,
		$imageCropAspectRatio,
		$imageOutputSize,
		isImageSelectionCentered,
		$imageSize,
		$imageCropMinSize,
		$imageCropLimitToImage,
		$imageCropRect,
		imageZoomLevelMin,
		$rootRect,
		canCenter,
		shouldRenderInfoIndicator,
		shouldRenderImageSelection,
		imageSelectionRectOffset,
		shouldRenderImageSelectionRecenterButton,
		$isActive,
		shouldRenderToolbar,
		shouldRenderFooter,
		shouldRenderTabs,
		tabsConfig,
		tabs,
		panels,
		footerStyle,
		$recenterOpacity,
		$recenterOffset,
		$imageRotationRange,
		$imageZoomLevelRange,
		$imageZoomLevel,
		history,
		env,
		isInteracting,
		rootRect,
		stageRect,
		utilRect,
		elasticityMultiplier,
		rangeInputElasticity,
		presentationScalar,
		imagePreviewModifiers,
		imageFlipX,
		imageFlipY,
		imageRotation,
		imageRotationRange,
		imageOutputSize,
		imageSelectionRect,
		imageSelectionRectSnapshot,
		imageSelectionRectIntent,
		imageSelectionRectPresentation,
		imageCropRectIntent,
		imageCropRectOrigin,
		imageCropRect,
		imageCropMinSize,
		imageCropMaxSize,
		imageCropRange,
		imageCropAspectRatio,
		imageCropLimitToImage,
		imageSize,
		imageScalar,
		handleSelectionGrab,
		handleSelectionDrag,
		handleSelectionRelease,
		handleRotateStart,
		handleRotateMove,
		handleRotateEnd,
		handleImageDragStart,
		handleImageDrag,
		handleImageDragEnd,
		handleImageDragRelease,
		imageCropRangeAspectRatio,
		imageZoomLevelRange,
		imageZoomLevel,
		handleResizeStart,
		handleResizeMove,
		handleResizeEnd,
		handleWheel,
		handleRecenterAction,
		recenterOpacity,
		recenterOffset,
		footerOffset,
		selectedPresetIndex,
		getSelectionPresetLabelIcon,
		getCropBoundaryLabelIcon,
		tools,
		name,
		stores,
		cropAutoCenterImageSelectionTimeout,
		cropEnableZoomMatchImageAspectRatio,
		cropEnableRotationInput,
		cropEnableZoomInput,
		cropEnableImageSelection,
		cropEnableInfoIndicator,
		cropEnableZoomTowardsWheelPosition,
		cropEnableLimitWheelInputToCropSelection,
		cropEnableCenterImageSelection,
		cropEnableSelectPreset,
		measure_handler_1,
		func,
		func_1,
		func_2,
		func_3,
		func_4,
		func_5,
		func_6,
		interactable_function,
		select_handler,
		util_root_binding,
		measure_handler
	];
}

class Crop extends SvelteComponent {
	constructor(options) {
		super();

		init(
			this,
			options,
			instance$n,
			create_fragment$n,
			safe_not_equal,
			{
				name: 98,
				isActive: 0,
				stores: 99,
				cropImageSelectionCornerStyle: 1,
				cropAutoCenterImageSelectionTimeout: 100,
				cropEnableZoomMatchImageAspectRatio: 101,
				cropEnableRotationInput: 102,
				cropEnableZoomInput: 103,
				cropEnableImageSelection: 104,
				cropEnableInfoIndicator: 105,
				cropEnableZoomTowardsWheelPosition: 106,
				cropEnableLimitWheelInputToCropSelection: 107,
				cropEnableCenterImageSelection: 108,
				cropEnableButtonRotateLeft: 2,
				cropEnableButtonRotateRight: 3,
				cropEnableButtonFlipHorizontal: 4,
				cropEnableButtonFlipVertical: 5,
				cropSelectPresetOptions: 6,
				cropEnableSelectPreset: 109,
				cropEnableButtonToggleCropLimit: 7,
				locale: 8,
				tools: 97
			},
			[-1, -1, -1, -1, -1, -1]
		);
	}

	get name() {
		return this.$$.ctx[98];
	}

	get isActive() {
		return this.$$.ctx[0];
	}

	set isActive(isActive) {
		this.$set({ isActive });
		flush();
	}

	get stores() {
		return this.$$.ctx[99];
	}

	set stores(stores) {
		this.$set({ stores });
		flush();
	}

	get cropImageSelectionCornerStyle() {
		return this.$$.ctx[1];
	}

	set cropImageSelectionCornerStyle(cropImageSelectionCornerStyle) {
		this.$set({ cropImageSelectionCornerStyle });
		flush();
	}

	get cropAutoCenterImageSelectionTimeout() {
		return this.$$.ctx[100];
	}

	set cropAutoCenterImageSelectionTimeout(cropAutoCenterImageSelectionTimeout) {
		this.$set({ cropAutoCenterImageSelectionTimeout });
		flush();
	}

	get cropEnableZoomMatchImageAspectRatio() {
		return this.$$.ctx[101];
	}

	set cropEnableZoomMatchImageAspectRatio(cropEnableZoomMatchImageAspectRatio) {
		this.$set({ cropEnableZoomMatchImageAspectRatio });
		flush();
	}

	get cropEnableRotationInput() {
		return this.$$.ctx[102];
	}

	set cropEnableRotationInput(cropEnableRotationInput) {
		this.$set({ cropEnableRotationInput });
		flush();
	}

	get cropEnableZoomInput() {
		return this.$$.ctx[103];
	}

	set cropEnableZoomInput(cropEnableZoomInput) {
		this.$set({ cropEnableZoomInput });
		flush();
	}

	get cropEnableImageSelection() {
		return this.$$.ctx[104];
	}

	set cropEnableImageSelection(cropEnableImageSelection) {
		this.$set({ cropEnableImageSelection });
		flush();
	}

	get cropEnableInfoIndicator() {
		return this.$$.ctx[105];
	}

	set cropEnableInfoIndicator(cropEnableInfoIndicator) {
		this.$set({ cropEnableInfoIndicator });
		flush();
	}

	get cropEnableZoomTowardsWheelPosition() {
		return this.$$.ctx[106];
	}

	set cropEnableZoomTowardsWheelPosition(cropEnableZoomTowardsWheelPosition) {
		this.$set({ cropEnableZoomTowardsWheelPosition });
		flush();
	}

	get cropEnableLimitWheelInputToCropSelection() {
		return this.$$.ctx[107];
	}

	set cropEnableLimitWheelInputToCropSelection(cropEnableLimitWheelInputToCropSelection) {
		this.$set({ cropEnableLimitWheelInputToCropSelection });
		flush();
	}

	get cropEnableCenterImageSelection() {
		return this.$$.ctx[108];
	}

	set cropEnableCenterImageSelection(cropEnableCenterImageSelection) {
		this.$set({ cropEnableCenterImageSelection });
		flush();
	}

	get cropEnableButtonRotateLeft() {
		return this.$$.ctx[2];
	}

	set cropEnableButtonRotateLeft(cropEnableButtonRotateLeft) {
		this.$set({ cropEnableButtonRotateLeft });
		flush();
	}

	get cropEnableButtonRotateRight() {
		return this.$$.ctx[3];
	}

	set cropEnableButtonRotateRight(cropEnableButtonRotateRight) {
		this.$set({ cropEnableButtonRotateRight });
		flush();
	}

	get cropEnableButtonFlipHorizontal() {
		return this.$$.ctx[4];
	}

	set cropEnableButtonFlipHorizontal(cropEnableButtonFlipHorizontal) {
		this.$set({ cropEnableButtonFlipHorizontal });
		flush();
	}

	get cropEnableButtonFlipVertical() {
		return this.$$.ctx[5];
	}

	set cropEnableButtonFlipVertical(cropEnableButtonFlipVertical) {
		this.$set({ cropEnableButtonFlipVertical });
		flush();
	}

	get cropSelectPresetOptions() {
		return this.$$.ctx[6];
	}

	set cropSelectPresetOptions(cropSelectPresetOptions) {
		this.$set({ cropSelectPresetOptions });
		flush();
	}

	get cropEnableSelectPreset() {
		return this.$$.ctx[109];
	}

	set cropEnableSelectPreset(cropEnableSelectPreset) {
		this.$set({ cropEnableSelectPreset });
		flush();
	}

	get cropEnableButtonToggleCropLimit() {
		return this.$$.ctx[7];
	}

	set cropEnableButtonToggleCropLimit(cropEnableButtonToggleCropLimit) {
		this.$set({ cropEnableButtonToggleCropLimit });
		flush();
	}

	get locale() {
		return this.$$.ctx[8];
	}

	set locale(locale) {
		this.$set({ locale });
		flush();
	}

	get tools() {
		return this.$$.ctx[97];
	}

	set tools(tools) {
		this.$set({ tools });
		flush();
	}
}

// @ts-ignore
var index = { util: ['crop', Crop] };

/* src/native/ui/plugins/filter/index.svelte generated by Svelte v3.29.4 */

function create_option_slot$1(ctx) {
	let div0;
	let div1;
	let measurable_action;
	let option = /*option*/ ctx[61];
	let t0;
	let span;

	let t1_value = (isFunction(/*option*/ ctx[61].label)
	? /*option*/ ctx[61].label(/*locale*/ ctx[2])
	: /*option*/ ctx[61].label) + "";

	let t1;
	let mounted;
	let dispose;

	function measure_handler_1(...args) {
		return /*measure_handler_1*/ ctx[29](/*option*/ ctx[61], ...args);
	}

	const assign_div1 = () => /*div1_binding*/ ctx[30](div1, option);
	const unassign_div1 = () => /*div1_binding*/ ctx[30](null, option);

	return {
		c() {
			div0 = element("div");
			div1 = element("div");
			t0 = space();
			span = element("span");
			t1 = text(t1_value);
			attr(div1, "class", FILTER_PREVIEW_CLASS_NAME);
			attr(div0, "slot", "option");
			attr(div0, "class", "DokaFilterOption");
		},
		m(target, anchor) {
			insert(target, div0, anchor);
			append(div0, div1);
			assign_div1();
			append(div0, t0);
			append(div0, span);
			append(span, t1);

			if (!mounted) {
				dispose = [
					listen(div1, "measure", measure_handler_1),
					action_destroyer(measurable_action = measurable.call(null, div1))
				];

				mounted = true;
			}
		},
		p(new_ctx, dirty) {
			ctx = new_ctx;

			if (option !== /*option*/ ctx[61]) {
				unassign_div1();
				option = /*option*/ ctx[61];
				assign_div1();
			}

			if (dirty[0] & /*locale*/ 4 | dirty[1] & /*option*/ 1073741824 && t1_value !== (t1_value = (isFunction(/*option*/ ctx[61].label)
			? /*option*/ ctx[61].label(/*locale*/ ctx[2])
			: /*option*/ ctx[61].label) + "")) set_data(t1, t1_value);
		},
		d(detaching) {
			if (detaching) detach(div0);
			unassign_div1();
			mounted = false;
			run_all(dispose);
		}
	};
}

// (459:8) <Scrollable              elasticity={elasticityMultiplier * scrollElasticity}              onscroll={offset => tileScrollOffset = offset}              bind:maskFeatherStartOpacity={tileLeftOpacity}             bind:maskFeatherEndOpacity={tileRightOpacity}             bind:maskFeatherSize={tileMargin}             on:measure={e => tileScrollContainerRect = e.detail}>
function create_default_slot_1$4(ctx) {
	let radiogroup;
	let current;

	radiogroup = new RadioGroup({
			props: {
				locale: /*locale*/ ctx[2],
				layout: "row",
				options: /*filterOptions*/ ctx[3],
				selectedIndex: /*selectedFilterIndex*/ ctx[10],
				onchange: /*handleChangeFilter*/ ctx[25],
				$$slots: {
					option: [
						create_option_slot$1,
						({ option }) => ({ 61: option }),
						({ option }) => [0, option ? 1073741824 : 0]
					]
				},
				$$scope: { ctx }
			}
		});

	return {
		c() {
			create_component(radiogroup.$$.fragment);
		},
		m(target, anchor) {
			mount_component(radiogroup, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const radiogroup_changes = {};
			if (dirty[0] & /*locale*/ 4) radiogroup_changes.locale = /*locale*/ ctx[2];
			if (dirty[0] & /*filterOptions*/ 8) radiogroup_changes.options = /*filterOptions*/ ctx[3];
			if (dirty[0] & /*selectedFilterIndex*/ 1024) radiogroup_changes.selectedIndex = /*selectedFilterIndex*/ ctx[10];

			if (dirty[0] & /*locale, tileElements*/ 20 | dirty[1] & /*option*/ 1073741824 | dirty[2] & /*$$scope*/ 1) {
				radiogroup_changes.$$scope = { dirty, ctx };
			}

			radiogroup.$set(radiogroup_changes);
		},
		i(local) {
			if (current) return;
			transition_in(radiogroup.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(radiogroup.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(radiogroup, detaching);
		}
	};
}

// (458:4) <div slot="footer" style={footerStyle} on:transitionend={handleTransitionEnd}>
function create_footer_slot$1(ctx) {
	let div;
	let scrollable;
	let updating_maskFeatherStartOpacity;
	let updating_maskFeatherEndOpacity;
	let updating_maskFeatherSize;
	let current;
	let mounted;
	let dispose;

	function scrollable_maskFeatherStartOpacity_binding(value) {
		/*scrollable_maskFeatherStartOpacity_binding*/ ctx[32].call(null, value);
	}

	function scrollable_maskFeatherEndOpacity_binding(value) {
		/*scrollable_maskFeatherEndOpacity_binding*/ ctx[33].call(null, value);
	}

	function scrollable_maskFeatherSize_binding(value) {
		/*scrollable_maskFeatherSize_binding*/ ctx[34].call(null, value);
	}

	let scrollable_props = {
		elasticity: /*elasticityMultiplier*/ ctx[14] * /*scrollElasticity*/ ctx[15],
		onscroll: /*func*/ ctx[31],
		$$slots: { default: [create_default_slot_1$4] },
		$$scope: { ctx }
	};

	if (/*tileLeftOpacity*/ ctx[5] !== void 0) {
		scrollable_props.maskFeatherStartOpacity = /*tileLeftOpacity*/ ctx[5];
	}

	if (/*tileRightOpacity*/ ctx[6] !== void 0) {
		scrollable_props.maskFeatherEndOpacity = /*tileRightOpacity*/ ctx[6];
	}

	if (/*tileMargin*/ ctx[7] !== void 0) {
		scrollable_props.maskFeatherSize = /*tileMargin*/ ctx[7];
	}

	scrollable = new Scrollable({ props: scrollable_props });
	binding_callbacks.push(() => bind(scrollable, "maskFeatherStartOpacity", scrollable_maskFeatherStartOpacity_binding));
	binding_callbacks.push(() => bind(scrollable, "maskFeatherEndOpacity", scrollable_maskFeatherEndOpacity_binding));
	binding_callbacks.push(() => bind(scrollable, "maskFeatherSize", scrollable_maskFeatherSize_binding));
	scrollable.$on("measure", /*measure_handler_2*/ ctx[35]);

	return {
		c() {
			div = element("div");
			create_component(scrollable.$$.fragment);
			attr(div, "slot", "footer");
			attr(div, "style", /*footerStyle*/ ctx[11]);
		},
		m(target, anchor) {
			insert(target, div, anchor);
			mount_component(scrollable, div, null);
			current = true;

			if (!mounted) {
				dispose = listen(div, "transitionend", /*handleTransitionEnd*/ ctx[23]);
				mounted = true;
			}
		},
		p(ctx, dirty) {
			const scrollable_changes = {};
			if (dirty[0] & /*tileScrollOffset*/ 256) scrollable_changes.onscroll = /*func*/ ctx[31];

			if (dirty[0] & /*locale, filterOptions, selectedFilterIndex, tileElements*/ 1052 | dirty[2] & /*$$scope*/ 1) {
				scrollable_changes.$$scope = { dirty, ctx };
			}

			if (!updating_maskFeatherStartOpacity && dirty[0] & /*tileLeftOpacity*/ 32) {
				updating_maskFeatherStartOpacity = true;
				scrollable_changes.maskFeatherStartOpacity = /*tileLeftOpacity*/ ctx[5];
				add_flush_callback(() => updating_maskFeatherStartOpacity = false);
			}

			if (!updating_maskFeatherEndOpacity && dirty[0] & /*tileRightOpacity*/ 64) {
				updating_maskFeatherEndOpacity = true;
				scrollable_changes.maskFeatherEndOpacity = /*tileRightOpacity*/ ctx[6];
				add_flush_callback(() => updating_maskFeatherEndOpacity = false);
			}

			if (!updating_maskFeatherSize && dirty[0] & /*tileMargin*/ 128) {
				updating_maskFeatherSize = true;
				scrollable_changes.maskFeatherSize = /*tileMargin*/ ctx[7];
				add_flush_callback(() => updating_maskFeatherSize = false);
			}

			scrollable.$set(scrollable_changes);

			if (!current || dirty[0] & /*footerStyle*/ 2048) {
				attr(div, "style", /*footerStyle*/ ctx[11]);
			}
		},
		i(local) {
			if (current) return;
			transition_in(scrollable.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(scrollable.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			destroy_component(scrollable);
			mounted = false;
			dispose();
		}
	};
}

function create_fragment$o(ctx) {
	let util;
	let current;

	util = new Util({
			props: {
				$$slots: { footer: [create_footer_slot$1] },
				$$scope: { ctx }
			}
		});

	util.$on("measure", /*measure_handler*/ ctx[36]);

	return {
		c() {
			create_component(util.$$.fragment);
		},
		m(target, anchor) {
			mount_component(util, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const util_changes = {};

			if (dirty[0] & /*footerStyle, tileScrollOffset, tileLeftOpacity, tileRightOpacity, tileMargin, tileScrollContainerRect, locale, filterOptions, selectedFilterIndex, tileElements*/ 4092 | dirty[2] & /*$$scope*/ 1) {
				util_changes.$$scope = { dirty, ctx };
			}

			util.$set(util_changes);
		},
		i(local) {
			if (current) return;
			transition_in(util.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(util.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(util, detaching);
		}
	};
}

let FILTER_PREVIEW_CLASS_NAME = "DokaFilterPreview";

function instance$o($$self, $$props, $$invalidate) {
	let $imageColorMatrix;
	let $tileRects;
	let $canvasSize;
	let $imageTransforms;

	let $isActive,
		$$unsubscribe_isActive = noop$1,
		$$subscribe_isActive = () => ($$unsubscribe_isActive(), $$unsubscribe_isActive = subscribe(isActive, $$value => $$invalidate(44, $isActive = $$value)), isActive);

	let $utilRect;
	let $stageRect;
	let $imageGamma;

	let $isActiveFraction,
		$$unsubscribe_isActiveFraction = noop$1,
		$$subscribe_isActiveFraction = () => ($$unsubscribe_isActiveFraction(), $$unsubscribe_isActiveFraction = subscribe(isActiveFraction, $$value => $$invalidate(48, $isActiveFraction = $$value)), isActiveFraction);

	let $footerOffset;
	let $imageTiles;
	$$self.$$.on_destroy.push(() => $$unsubscribe_isActive());
	$$self.$$.on_destroy.push(() => $$unsubscribe_isActiveFraction());
	const name = "filter";
	let { isActive } = $$props;
	$$subscribe_isActive();
	let { isActiveFraction } = $$props;
	$$subscribe_isActiveFraction();
	let { stores } = $$props;
	let { locale } = $$props;
	let { filterFunctions } = $$props;
	let { filterOptions } = $$props;

	// connect filter choice to stores
	const { history, imagePreviews, stageRect, utilRect, elasticityMultiplier, scrollElasticity, imageCropRect, imageSize, imageRotation, imageFlipX, imageFlipY, imageGamma, imageColorMatrix } = stores;

	component_subscribe($$self, stageRect, value => $$invalidate(46, $stageRect = value));
	component_subscribe($$self, utilRect, value => $$invalidate(45, $utilRect = value));
	component_subscribe($$self, imageGamma, value => $$invalidate(47, $imageGamma = value));
	component_subscribe($$self, imageColorMatrix, value => $$invalidate(40, $imageColorMatrix = value));

	const getFilterIndex = (imageColorMatrix, filterOptionsFlattened) => {
		if (!imageColorMatrix || !imageColorMatrix["filter"] || !filterOptionsFlattened) return 0;
		const filterColorMatrix = imageColorMatrix["filter"];

		return filterOptionsFlattened.// get id and compare matrices
		findIndex(([id]) => {
			if (!filterFunctions[id]) return false;
			const matrix = filterFunctions[id]();
			return arrayEqual(matrix, filterColorMatrix);
		});
	};

	const tileRects = writable({});
	component_subscribe($$self, tileRects, value => $$invalidate(41, $tileRects = value));
	const handleTileResize = (item, rect) => set_store_value(tileRects, $tileRects[item.value] = rect, $tileRects);

	const canvasSize = derived(tileRects, $tileRects => {
		if (!$tileRects[undefined]) return;
		const tileRectFirst = $tileRects[undefined];
		if ($canvasSize && sizeEqual($canvasSize, tileRectFirst)) return $canvasSize;
		return sizeClone(tileRectFirst);
	});

	component_subscribe($$self, canvasSize, value => $$invalidate(42, $canvasSize = value));

	const imageTransforms = derived(
		[
			isActive,
			canvasSize,
			imageCropRect,
			imageSize,
			imageRotation,
			imageFlipX,
			imageFlipY
		],
		([
				$isActive,
				$canvasSize,
				$imageCropRect,
				$imageSize,
				$imageRotation,
				$imageFlipX,
				$imageFlipY
			], set) => {
			if (!$isActive || !$canvasSize) return $imageTransforms;
			const imageRect = rectCreateFromSize($imageSize);
			const imageCenter = rectCenter(imageRect);

			// get base crop rect so we can correctly apply transforms
			const cropRectBase = getBaseCropRect($imageSize, $imageCropRect, $imageRotation);

			const cropRectBaseCenter = rectCenter(cropRectBase);
			const imageTranslation = vectorSubtract(vectorClone(imageCenter), cropRectBaseCenter);
			const imageOrigin = vectorInvert(vectorClone(imageTranslation));

			// scalar
			const imageScalar = Math.max($canvasSize.width / $imageCropRect.width, $canvasSize.height / $imageCropRect.height);

			// update preview transforms
			set({
				origin: imageOrigin,
				translation: imageTranslation,
				rotation: {
					x: $imageFlipY ? Math.PI : 0,
					y: $imageFlipX ? Math.PI : 0,
					z: $imageRotation
				},
				perspective: vectorCreateEmpty(),
				scale: imageScalar
			});
		}
	);

	component_subscribe($$self, imageTransforms, value => $$invalidate(43, $imageTransforms = value));

	const cloneImageTransforms = imageTransforms => ({
		origin: vectorClone(imageTransforms.origin),
		translation: vectorClone(imageTransforms.translation),
		rotation: { ...imageTransforms.rotation },
		perspective: vectorClone(imageTransforms.perspective),
		scale: imageTransforms.scale
	});

	//
	// Footer
	//
	const footerOffset = spring(0);

	component_subscribe($$self, footerOffset, value => $$invalidate(49, $footerOffset = value));
	let tileCornerRadius;
	const tileElements = {};

	const handleTransitionEnd = e => {
		if (e.target.className !== FILTER_PREVIEW_CLASS_NAME) return;

		$$invalidate(37, tileCornerRadius = Object.keys(tileElements).reduce(
			(prev, curr) => {
				const element = tileElements[curr];
				const style = getComputedStyle(element);

				const corners = ["top-left", "top-right", "bottom-left", "bottom-right"].map(corner => style.getPropertyValue(`border-${corner}-radius`)).map(unitToPixels).// this better aligns WebGL rounded corner with css rounded corner
				map(v => v * 1.25);

				prev[curr] = corners;
				return prev;
			},
			{}
		));
	};

	let tileLeftOpacity; // set by Scrollable
	let tileRightOpacity; // set by Scrollable
	let tileMargin; // set by Scrollable
	let tileScrollOffset = { x: 0, y: 0 };
	let tileScrollContainerRect;
	let tileWrapperOffset;
	const imageTiles = writable([]);
	component_subscribe($$self, imageTiles, value => $$invalidate(50, $imageTiles = value));

	// clones an image tile for use in canvas
	const cloneImageTile = tile => {
		const clone = {
			// doesn't clone everything but should be enough
			...tile,
			offset: { ...tile.offset },
			mask: { ...tile.mask }
		};

		clone.opacity = $isActiveFraction;
		clone.offset.y += $footerOffset;
		clone.mask.y += $footerOffset;
		return clone;
	};

	const handleChangeFilter = ({ value }) => {
		set_store_value(
			imageColorMatrix,
			$imageColorMatrix = {
				...$imageColorMatrix,
				"filter": isFunction(filterFunctions[value])
				? filterFunctions[value]()
				: undefined
			},
			$imageColorMatrix
		);

		history.write();
	};

	const measure_handler_1 = (option, e) => handleTileResize(option, e.detail);

	function div1_binding($$value, option) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			tileElements[option.value] = $$value;
			$$invalidate(4, tileElements);
		});
	}

	const func = offset => $$invalidate(8, tileScrollOffset = offset);

	function scrollable_maskFeatherStartOpacity_binding(value) {
		tileLeftOpacity = value;
		$$invalidate(5, tileLeftOpacity);
	}

	function scrollable_maskFeatherEndOpacity_binding(value) {
		tileRightOpacity = value;
		$$invalidate(6, tileRightOpacity);
	}

	function scrollable_maskFeatherSize_binding(value) {
		tileMargin = value;
		$$invalidate(7, tileMargin);
	}

	const measure_handler_2 = e => $$invalidate(9, tileScrollContainerRect = e.detail);

	function measure_handler(event) {
		bubble($$self, event);
	}

	$$self.$$set = $$props => {
		if ("isActive" in $$props) $$subscribe_isActive($$invalidate(0, isActive = $$props.isActive));
		if ("isActiveFraction" in $$props) $$subscribe_isActiveFraction($$invalidate(1, isActiveFraction = $$props.isActiveFraction));
		if ("stores" in $$props) $$invalidate(27, stores = $$props.stores);
		if ("locale" in $$props) $$invalidate(2, locale = $$props.locale);
		if ("filterFunctions" in $$props) $$invalidate(28, filterFunctions = $$props.filterFunctions);
		if ("filterOptions" in $$props) $$invalidate(3, filterOptions = $$props.filterOptions);
	};

	let filterOptionsFlattened;
	let selectedFilterIndex;
	let footerStyle;

	$$self.$$.update = () => {
		if ($$self.$$.dirty[0] & /*filterOptions*/ 8) {
			 $$invalidate(39, filterOptionsFlattened = flattenOptions(filterOptions));
		}

		if ($$self.$$.dirty[1] & /*$imageColorMatrix, filterOptionsFlattened*/ 768) {
			 $$invalidate(10, selectedFilterIndex = getFilterIndex($imageColorMatrix, filterOptionsFlattened));
		}

		if ($$self.$$.dirty[1] & /*$isActive*/ 8192) {
			 footerOffset.set($isActive ? 0 : 20);
		}

		if ($$self.$$.dirty[1] & /*$isActive, $utilRect, $stageRect*/ 57344) {
			 if ($isActive && $utilRect && $stageRect) {
				const tileTopOffset = $stageRect.y + $stageRect.height + $utilRect.y;

				$$invalidate(38, tileWrapperOffset = {
					x: $stageRect.x - $utilRect.x,
					y: tileTopOffset
				});
			}
		}

		if ($$self.$$.dirty[0] & /*tileScrollOffset, tileScrollContainerRect, tileMargin, tileLeftOpacity, tileRightOpacity, filterFunctions*/ 268436448 | $$self.$$.dirty[1] & /*$imageTransforms, tileWrapperOffset, tileCornerRadius, filterOptionsFlattened, $tileRects, $imageColorMatrix, $imageGamma*/ 71616) {
			 if ($imageTransforms && tileWrapperOffset && tileScrollOffset && tileScrollContainerRect && tileCornerRadius) {
				const boundsX = tileWrapperOffset.x + tileScrollContainerRect.x;
				const offsetX = boundsX + tileScrollOffset.x;
				const offsetY = tileWrapperOffset.y;
				const containerLeft = tileScrollContainerRect.x + tileWrapperOffset.x;
				const containerRight = containerLeft + tileScrollContainerRect.width;

				imageTiles.set(filterOptionsFlattened.map(([id], i) => {
					const tileRect = $tileRects[id];

					// test if is outside of view
					const tileLeft = tileScrollOffset.x + tileRect.x;

					const tileRight = tileLeft + tileRect.width;
					if (tileRight < 0 || tileLeft > tileScrollContainerRect.width) return false;
					const x = offsetX + tileRect.x;
					const y = offsetY + tileRect.y;
					const preview = cloneImageTransforms($imageTransforms);
					preview.offset = vectorCreate(tileRect.width * 0.5 + x, tileRect.height * 0.5 + y);
					let clipX = 0;
					let clipWidth = 0;
					preview.maskOpacity = 1;
					preview.mask = rectCreate(x + clipX, y, tileRect.width + clipWidth, tileRect.height);
					preview.maskFeather = [1, 0, 1, 0, 1, containerRight, 1, containerRight];

					if (tileLeft < tileMargin && tileLeftOpacity < 1) {
						preview.maskFeather[0] = tileLeftOpacity;
						preview.maskFeather[1] = containerLeft;
						preview.maskFeather[2] = 1;
						preview.maskFeather[3] = containerLeft + tileMargin;
					}

					if (tileRight > tileScrollContainerRect.width - tileMargin && tileRightOpacity < 1) {
						preview.maskFeather[4] = tileRightOpacity;
						preview.maskFeather[5] = containerRight - tileMargin;
						preview.maskFeather[6] = 1;
						preview.maskFeather[7] = containerRight;
					}

					preview.maskCornerRadius = tileCornerRadius[id];
					let colorMatrices = $imageColorMatrix && Object.keys($imageColorMatrix).filter(name => name != "filter").map(name => $imageColorMatrix[name]) || [];

					if (isFunction(filterFunctions[id])) {
						colorMatrices.push(filterFunctions[id]());
					}

					preview.colorMatrix = colorMatrices.length
					? getColorMatrixFromColorMatrices(colorMatrices)
					: undefined;

					preview.gamma = $imageGamma;
					return preview;
				}).filter(Boolean));
			}
		}

		if ($$self.$$.dirty[1] & /*$isActive, $imageTiles, $footerOffset, $isActiveFraction*/ 925696) {
			// Will update `imagePreviews` store when
			// - $isActiveFraction is updated
			// - $imageTiles is updated
			// - $footerOffset is updated
			 if ($isActive && $imageTiles) {

				// clone tiles
				imagePreviews.set($imageTiles.map(cloneImageTile));
			} else {
				imagePreviews.set([]);
			}
		}

		if ($$self.$$.dirty[1] & /*$footerOffset*/ 262144) {
			 $$invalidate(11, footerStyle = $footerOffset
			? `transform: translateY(${$footerOffset}px)`
			: undefined);
		}
	};

	return [
		isActive,
		isActiveFraction,
		locale,
		filterOptions,
		tileElements,
		tileLeftOpacity,
		tileRightOpacity,
		tileMargin,
		tileScrollOffset,
		tileScrollContainerRect,
		selectedFilterIndex,
		footerStyle,
		stageRect,
		utilRect,
		elasticityMultiplier,
		scrollElasticity,
		imageGamma,
		imageColorMatrix,
		tileRects,
		handleTileResize,
		canvasSize,
		imageTransforms,
		footerOffset,
		handleTransitionEnd,
		imageTiles,
		handleChangeFilter,
		name,
		stores,
		filterFunctions,
		measure_handler_1,
		div1_binding,
		func,
		scrollable_maskFeatherStartOpacity_binding,
		scrollable_maskFeatherEndOpacity_binding,
		scrollable_maskFeatherSize_binding,
		measure_handler_2,
		measure_handler
	];
}

class Filter extends SvelteComponent {
	constructor(options) {
		super();

		init(
			this,
			options,
			instance$o,
			create_fragment$o,
			safe_not_equal,
			{
				name: 26,
				isActive: 0,
				isActiveFraction: 1,
				stores: 27,
				locale: 2,
				filterFunctions: 28,
				filterOptions: 3
			},
			[-1, -1, -1]
		);
	}

	get name() {
		return this.$$.ctx[26];
	}

	get isActive() {
		return this.$$.ctx[0];
	}

	set isActive(isActive) {
		this.$set({ isActive });
		flush();
	}

	get isActiveFraction() {
		return this.$$.ctx[1];
	}

	set isActiveFraction(isActiveFraction) {
		this.$set({ isActiveFraction });
		flush();
	}

	get stores() {
		return this.$$.ctx[27];
	}

	set stores(stores) {
		this.$set({ stores });
		flush();
	}

	get locale() {
		return this.$$.ctx[2];
	}

	set locale(locale) {
		this.$set({ locale });
		flush();
	}

	get filterFunctions() {
		return this.$$.ctx[28];
	}

	set filterFunctions(filterFunctions) {
		this.$set({ filterFunctions });
		flush();
	}

	get filterOptions() {
		return this.$$.ctx[3];
	}

	set filterOptions(filterOptions) {
		this.$set({ filterOptions });
		flush();
	}
}

// @ts-ignore
var index$1 = { util: ['filter', Filter] };

/* src/native/ui/plugins/finetune/index.svelte generated by Svelte v3.29.4 */

function create_default_slot_3$4(ctx) {
	let span;
	let t_value = /*tab*/ ctx[35].label + "";
	let t;

	return {
		c() {
			span = element("span");
			t = text(t_value);
		},
		m(target, anchor) {
			insert(target, span, anchor);
			append(span, t);
		},
		p(ctx, dirty) {
			if (dirty[1] & /*tab*/ 16 && t_value !== (t_value = /*tab*/ ctx[35].label + "")) set_data(t, t_value);
		},
		d(detaching) {
			if (detaching) detach(span);
		}
	};
}

// (152:8) <Scrollable elasticity={elasticityMultiplier * scrollElasticity} class="DokaControlListScroller">
function create_default_slot_2$4(ctx) {
	let tablist;
	let current;
	const tablist_spread_levels = [{ class: "DokaControlList" }, { tabs: /*tabs*/ ctx[1] }, /*tabsConfig*/ ctx[3]];

	let tablist_props = {
		$$slots: {
			default: [
				create_default_slot_3$4,
				({ tab }) => ({ 35: tab }),
				({ tab }) => [0, tab ? 16 : 0]
			]
		},
		$$scope: { ctx }
	};

	for (let i = 0; i < tablist_spread_levels.length; i += 1) {
		tablist_props = assign(tablist_props, tablist_spread_levels[i]);
	}

	tablist = new TabList({ props: tablist_props });
	tablist.$on("select", /*select_handler*/ ctx[17]);

	return {
		c() {
			create_component(tablist.$$.fragment);
		},
		m(target, anchor) {
			mount_component(tablist, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const tablist_changes = (dirty[0] & /*tabs, tabsConfig*/ 10)
			? get_spread_update(tablist_spread_levels, [
					tablist_spread_levels[0],
					dirty[0] & /*tabs*/ 2 && { tabs: /*tabs*/ ctx[1] },
					dirty[0] & /*tabsConfig*/ 8 && get_spread_object(/*tabsConfig*/ ctx[3])
				])
			: {};

			if (dirty[1] & /*$$scope, tab*/ 48) {
				tablist_changes.$$scope = { dirty, ctx };
			}

			tablist.$set(tablist_changes);
		},
		i(local) {
			if (current) return;
			transition_in(tablist.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(tablist.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(tablist, detaching);
		}
	};
}

// (158:8) <TabPanels class="DokaControlPanels" panelClass="DokaControlPanel" {panels} {...tabsConfig} let:panel>
function create_default_slot_1$5(ctx) {
	let rangeinput;
	let current;
	const rangeinput_spread_levels = [/*$rangeInputConfig*/ ctx[5][/*panel*/ ctx[34]]];
	let rangeinput_props = {};

	for (let i = 0; i < rangeinput_spread_levels.length; i += 1) {
		rangeinput_props = assign(rangeinput_props, rangeinput_spread_levels[i]);
	}

	rangeinput = new RangeInput({ props: rangeinput_props });

	return {
		c() {
			create_component(rangeinput.$$.fragment);
		},
		m(target, anchor) {
			mount_component(rangeinput, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const rangeinput_changes = (dirty[0] & /*$rangeInputConfig*/ 32 | dirty[1] & /*panel*/ 8)
			? get_spread_update(rangeinput_spread_levels, [get_spread_object(/*$rangeInputConfig*/ ctx[5][/*panel*/ ctx[34]])])
			: {};

			rangeinput.$set(rangeinput_changes);
		},
		i(local) {
			if (current) return;
			transition_in(rangeinput.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(rangeinput.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(rangeinput, detaching);
		}
	};
}

// (150:4) <div slot="footer" style={footerStyle}>
function create_footer_slot$2(ctx) {
	let div;
	let scrollable;
	let t;
	let tabpanels;
	let current;

	scrollable = new Scrollable({
			props: {
				elasticity: /*elasticityMultiplier*/ ctx[8] * /*scrollElasticity*/ ctx[7],
				class: "DokaControlListScroller",
				$$slots: { default: [create_default_slot_2$4] },
				$$scope: { ctx }
			}
		});

	const tabpanels_spread_levels = [
		{ class: "DokaControlPanels" },
		{ panelClass: "DokaControlPanel" },
		{ panels: /*panels*/ ctx[4] },
		/*tabsConfig*/ ctx[3]
	];

	let tabpanels_props = {
		$$slots: {
			default: [
				create_default_slot_1$5,
				({ panel }) => ({ 34: panel }),
				({ panel }) => [0, panel ? 8 : 0]
			]
		},
		$$scope: { ctx }
	};

	for (let i = 0; i < tabpanels_spread_levels.length; i += 1) {
		tabpanels_props = assign(tabpanels_props, tabpanels_spread_levels[i]);
	}

	tabpanels = new TabPanels({ props: tabpanels_props });

	return {
		c() {
			div = element("div");
			create_component(scrollable.$$.fragment);
			t = space();
			create_component(tabpanels.$$.fragment);
			attr(div, "slot", "footer");
			attr(div, "style", /*footerStyle*/ ctx[6]);
		},
		m(target, anchor) {
			insert(target, div, anchor);
			mount_component(scrollable, div, null);
			append(div, t);
			mount_component(tabpanels, div, null);
			current = true;
		},
		p(ctx, dirty) {
			const scrollable_changes = {};

			if (dirty[0] & /*tabs, tabsConfig, tabSelected*/ 14 | dirty[1] & /*$$scope*/ 32) {
				scrollable_changes.$$scope = { dirty, ctx };
			}

			scrollable.$set(scrollable_changes);

			const tabpanels_changes = (dirty[0] & /*panels, tabsConfig*/ 24)
			? get_spread_update(tabpanels_spread_levels, [
					tabpanels_spread_levels[0],
					tabpanels_spread_levels[1],
					dirty[0] & /*panels*/ 16 && { panels: /*panels*/ ctx[4] },
					dirty[0] & /*tabsConfig*/ 8 && get_spread_object(/*tabsConfig*/ ctx[3])
				])
			: {};

			if (dirty[0] & /*$rangeInputConfig*/ 32 | dirty[1] & /*$$scope, panel*/ 40) {
				tabpanels_changes.$$scope = { dirty, ctx };
			}

			tabpanels.$set(tabpanels_changes);

			if (!current || dirty[0] & /*footerStyle*/ 64) {
				attr(div, "style", /*footerStyle*/ ctx[6]);
			}
		},
		i(local) {
			if (current) return;
			transition_in(scrollable.$$.fragment, local);
			transition_in(tabpanels.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(scrollable.$$.fragment, local);
			transition_out(tabpanels.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			destroy_component(scrollable);
			destroy_component(tabpanels);
		}
	};
}

function create_fragment$p(ctx) {
	let util;
	let current;

	util = new Util({
			props: {
				$$slots: { footer: [create_footer_slot$2] },
				$$scope: { ctx }
			}
		});

	util.$on("measure", /*measure_handler*/ ctx[18]);

	return {
		c() {
			create_component(util.$$.fragment);
		},
		m(target, anchor) {
			mount_component(util, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const util_changes = {};

			if (dirty[0] & /*footerStyle, panels, tabsConfig, $rangeInputConfig, tabs, tabSelected*/ 126 | dirty[1] & /*$$scope*/ 32) {
				util_changes.$$scope = { dirty, ctx };
			}

			util.$set(util_changes);
		},
		i(local) {
			if (current) return;
			transition_in(util.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(util.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(util, detaching);
		}
	};
}

function instance$p($$self, $$props, $$invalidate) {
	let $rangeInputConfig;
	let $panelValues;

	let $isActive,
		$$unsubscribe_isActive = noop$1,
		$$subscribe_isActive = () => ($$unsubscribe_isActive(), $$unsubscribe_isActive = subscribe(isActive, $$value => $$invalidate(21, $isActive = $$value)), isActive);

	let $footerOffset;
	$$self.$$.on_destroy.push(() => $$unsubscribe_isActive());
	const name = "finetune";
	let { stores } = $$props;
	let { isActive } = $$props;
	$$subscribe_isActive();
	let { locale = {} } = $$props;
	let { finetuneControlConfiguration } = $$props;
	let { finetuneOptions } = $$props;
	const { history, scrollElasticity, elasticityMultiplier, rangeInputElasticity, imageColorMatrix, imageConvolutionMatrix, imageGamma, imageVignette, imageNoise } = stores;

	const imageEffectStores = {
		imageColorMatrix,
		imageConvolutionMatrix,
		imageGamma,
		imageVignette,
		imageNoise
	};

	//
	// Tabs
	//
	const uid = `doka-finetune-${getUniqueId()}`;

	//
	// Range inputs
	//
	const panelValues = writable({});

	component_subscribe($$self, panelValues, value => $$invalidate(20, $panelValues = value));
	const rangeInputConfig = writable({});
	component_subscribe($$self, rangeInputConfig, value => $$invalidate(5, $rangeInputConfig = value));

	const updateRangeInputState = () => {
		set_store_value(
			rangeInputConfig,
			$rangeInputConfig = Object.keys($panelValues).reduce(
				(config, id) => {
					const { base, min, max, getStore, setValue = (store, v) => store.set(v) } = finetuneControlConfiguration[id];
					const store = getStore(imageEffectStores);
					const value = $panelValues[id] != null ? $panelValues[id] : base;

					config[id] = {
						base,
						min,
						max,
						value,
						valueLabel: Math.round(100 * value),
						oninputmove: v => {
							setValue(store, v);
						},
						oninputend: v => {
							setValue(store, v);
							history.write();
						},
						elasticity: elasticityMultiplier * rangeInputElasticity,
						labelReset: locale.labelReset
					};

					return config;
				},
				{}
			),
			$rangeInputConfig
		);
	};

	// need to unsubscribe when redrawing vie
	let unsubs = [];

	const subscribePanelStores = config => {
		if (unsubs) unsubs.forEach(unsub => unsub());

		unsubs = panels.map(id => {
			const { getStore, getValue = passthrough } = config[id];
			const store = getStore(imageEffectStores);

			return store.subscribe(value => {
				const currentValue = value != null ? getValue(value) : value;
				set_store_value(panelValues, $panelValues = { ...$panelValues, [id]: currentValue }, $panelValues);
			});
		});
	};

	//
	// Footer
	//
	const footerOffset = spring(0);

	component_subscribe($$self, footerOffset, value => $$invalidate(22, $footerOffset = value));
	const select_handler = ({ detail }) => $$invalidate(2, tabSelected = detail);

	function measure_handler(event) {
		bubble($$self, event);
	}

	$$self.$$set = $$props => {
		if ("stores" in $$props) $$invalidate(13, stores = $$props.stores);
		if ("isActive" in $$props) $$subscribe_isActive($$invalidate(0, isActive = $$props.isActive));
		if ("locale" in $$props) $$invalidate(14, locale = $$props.locale);
		if ("finetuneControlConfiguration" in $$props) $$invalidate(15, finetuneControlConfiguration = $$props.finetuneControlConfiguration);
		if ("finetuneOptions" in $$props) $$invalidate(16, finetuneOptions = $$props.finetuneOptions);
	};

	let tabs;
	let tabSelected;
	let tabsConfig;
	let panels;
	let footerStyle;

	$$self.$$.update = () => {
		if ($$self.$$.dirty[0] & /*finetuneOptions, locale*/ 81920) {
			 $$invalidate(1, tabs = finetuneOptions
			? finetuneOptions.map(([id, label]) => ({
					id,
					label: isFunction(label) ? label(locale) : label
				}))
			: []);
		}

		if ($$self.$$.dirty[0] & /*tabs*/ 2) {
			 $$invalidate(2, tabSelected = tabs.length && tabs[0].id);
		}

		if ($$self.$$.dirty[0] & /*tabSelected*/ 4) {
			 $$invalidate(3, tabsConfig = { name: uid, selected: tabSelected });
		}

		if ($$self.$$.dirty[0] & /*tabs*/ 2) {
			 $$invalidate(4, panels = tabs.map(tab => tab.id));
		}

		if ($$self.$$.dirty[0] & /*finetuneControlConfiguration*/ 32768) {
			 finetuneControlConfiguration && subscribePanelStores(finetuneControlConfiguration);
		}

		if ($$self.$$.dirty[0] & /*finetuneControlConfiguration, $panelValues*/ 1081344) {
			 finetuneControlConfiguration && $panelValues && updateRangeInputState();
		}

		if ($$self.$$.dirty[0] & /*$isActive*/ 2097152) {
			 footerOffset.set($isActive ? 0 : 20);
		}

		if ($$self.$$.dirty[0] & /*$footerOffset*/ 4194304) {
			 $$invalidate(6, footerStyle = $footerOffset
			? `transform: translateY(${$footerOffset}px)`
			: undefined);
		}
	};

	return [
		isActive,
		tabs,
		tabSelected,
		tabsConfig,
		panels,
		$rangeInputConfig,
		footerStyle,
		scrollElasticity,
		elasticityMultiplier,
		panelValues,
		rangeInputConfig,
		footerOffset,
		name,
		stores,
		locale,
		finetuneControlConfiguration,
		finetuneOptions,
		select_handler,
		measure_handler
	];
}

class Finetune extends SvelteComponent {
	constructor(options) {
		super();

		init(
			this,
			options,
			instance$p,
			create_fragment$p,
			safe_not_equal,
			{
				name: 12,
				stores: 13,
				isActive: 0,
				locale: 14,
				finetuneControlConfiguration: 15,
				finetuneOptions: 16
			},
			[-1, -1]
		);
	}

	get name() {
		return this.$$.ctx[12];
	}

	get stores() {
		return this.$$.ctx[13];
	}

	set stores(stores) {
		this.$set({ stores });
		flush();
	}

	get isActive() {
		return this.$$.ctx[0];
	}

	set isActive(isActive) {
		this.$set({ isActive });
		flush();
	}

	get locale() {
		return this.$$.ctx[14];
	}

	set locale(locale) {
		this.$set({ locale });
		flush();
	}

	get finetuneControlConfiguration() {
		return this.$$.ctx[15];
	}

	set finetuneControlConfiguration(finetuneControlConfiguration) {
		this.$set({ finetuneControlConfiguration });
		flush();
	}

	get finetuneOptions() {
		return this.$$.ctx[16];
	}

	set finetuneOptions(finetuneOptions) {
		this.$set({ finetuneOptions });
		flush();
	}
}

// @ts-ignore
var index$2 = { util: ['finetune', Finetune] };

/* src/native/ui/components/ShapeManipulator.svelte generated by Svelte v3.29.4 */

function get_each_context$5(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[46] = list[i].key;
	child_ctx[47] = list[i].index;
	child_ctx[48] = list[i].translate;
	child_ctx[49] = list[i].scale;
	child_ctx[14] = list[i].rotate;
	child_ctx[50] = list[i].cursor;
	child_ctx[51] = list[i].type;
	return child_ctx;
}

// (274:0) {#if enableResizing}
function create_if_block_1$7(ctx) {
	let each_blocks = [];
	let each_1_lookup = new Map();
	let each_1_anchor;
	let each_value = /*resizeControls*/ ctx[3];
	const get_key = ctx => /*key*/ ctx[46];

	for (let i = 0; i < each_value.length; i += 1) {
		let child_ctx = get_each_context$5(ctx, each_value, i);
		let key = get_key(child_ctx);
		each_1_lookup.set(key, each_blocks[i] = create_each_block$5(key, child_ctx));
	}

	return {
		c() {
			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			each_1_anchor = empty();
		},
		m(target, anchor) {
			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].m(target, anchor);
			}

			insert(target, each_1_anchor, anchor);
		},
		p(ctx, dirty) {
			if (dirty[0] & /*resizeControls, $selectionScale, $selectionOpacity, handleKeyDown, handleKeyUp, handleResizeNudge, resize*/ 6632) {
				const each_value = /*resizeControls*/ ctx[3];
				each_blocks = update_keyed_each(each_blocks, dirty, get_key, 1, ctx, each_value, each_1_lookup, each_1_anchor.parentNode, destroy_block, create_each_block$5, each_1_anchor, get_each_context$5);
			}
		},
		d(detaching) {
			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].d(detaching);
			}

			if (detaching) detach(each_1_anchor);
		}
	};
}

// (275:0) {#each resizeControls as { key, index, translate, scale, rotate, cursor, type }
function create_each_block$5(key_1, ctx) {
	let div;
	let div_tabindex_value;
	let div_data_control_value;
	let div_style_value;
	let nudgeable_action;
	let interactable_action;
	let mounted;
	let dispose;

	function nudge_handler(...args) {
		return /*nudge_handler*/ ctx[17](/*index*/ ctx[47], ...args);
	}

	return {
		key: key_1,
		first: null,
		c() {
			div = element("div");
			attr(div, "role", "button");
			attr(div, "tabindex", div_tabindex_value = /*type*/ ctx[51] === "edge" ? -1 : 0);
			attr(div, "class", "DokaShapeManipulator");
			attr(div, "data-control", div_data_control_value = /*type*/ ctx[51]);

			attr(div, "style", div_style_value = `cursor: ${/*cursor*/ ctx[50]}; transform: translate3d(${/*translate*/ ctx[48].x}px, ${/*translate*/ ctx[48].y}px, 0) rotate(${/*rotate*/ ctx[14]}rad) scale(${/*type*/ ctx[51] === "corner"
			? /*$selectionScale*/ ctx[5]
			: /*scale*/ ctx[49].x}, ${/*type*/ ctx[51] === "corner"
			? /*$selectionScale*/ ctx[5]
			: /*scale*/ ctx[49].y}); opacity: ${/*$selectionOpacity*/ ctx[6]}`);

			this.first = div;
		},
		m(target, anchor) {
			insert(target, div, anchor);

			if (!mounted) {
				dispose = [
					listen(div, "keydown", /*handleKeyDown*/ ctx[7]),
					listen(div, "keyup", /*handleKeyUp*/ ctx[8]),
					listen(div, "nudge", nudge_handler),
					action_destroyer(nudgeable_action = nudgeable.call(null, div)),
					listen(div, "interactionstart", function () {
						if (is_function(/*resize*/ ctx[11]("start", /*index*/ ctx[47]))) /*resize*/ ctx[11]("start", /*index*/ ctx[47]).apply(this, arguments);
					}),
					listen(div, "interactionupdate", function () {
						if (is_function(/*resize*/ ctx[11]("move", /*index*/ ctx[47]))) /*resize*/ ctx[11]("move", /*index*/ ctx[47]).apply(this, arguments);
					}),
					listen(div, "interactionend", function () {
						if (is_function(/*resize*/ ctx[11]("end", /*index*/ ctx[47]))) /*resize*/ ctx[11]("end", /*index*/ ctx[47]).apply(this, arguments);
					}),
					action_destroyer(interactable_action = interactable.call(null, div))
				];

				mounted = true;
			}
		},
		p(new_ctx, dirty) {
			ctx = new_ctx;

			if (dirty[0] & /*resizeControls*/ 8 && div_tabindex_value !== (div_tabindex_value = /*type*/ ctx[51] === "edge" ? -1 : 0)) {
				attr(div, "tabindex", div_tabindex_value);
			}

			if (dirty[0] & /*resizeControls*/ 8 && div_data_control_value !== (div_data_control_value = /*type*/ ctx[51])) {
				attr(div, "data-control", div_data_control_value);
			}

			if (dirty[0] & /*resizeControls, $selectionScale, $selectionOpacity*/ 104 && div_style_value !== (div_style_value = `cursor: ${/*cursor*/ ctx[50]}; transform: translate3d(${/*translate*/ ctx[48].x}px, ${/*translate*/ ctx[48].y}px, 0) rotate(${/*rotate*/ ctx[14]}rad) scale(${/*type*/ ctx[51] === "corner"
			? /*$selectionScale*/ ctx[5]
			: /*scale*/ ctx[49].x}, ${/*type*/ ctx[51] === "corner"
			? /*$selectionScale*/ ctx[5]
			: /*scale*/ ctx[49].y}); opacity: ${/*$selectionOpacity*/ ctx[6]}`)) {
				attr(div, "style", div_style_value);
			}
		},
		d(detaching) {
			if (detaching) detach(div);
			mounted = false;
			run_all(dispose);
		}
	};
}

// (292:0) {#if enableRotating && rotationControlActive}
function create_if_block$5(ctx) {
	let div;
	let div_style_value;
	let nudgeable_action;
	let interactable_action;
	let mounted;
	let dispose;

	return {
		c() {
			div = element("div");
			attr(div, "role", "button");
			attr(div, "tabindex", "0");
			attr(div, "class", "DokaShapeManipulator");
			attr(div, "data-control", "rotate");
			attr(div, "style", div_style_value = `transform: translate3d(${/*rotatorPoint*/ ctx[0].x}px, ${/*rotatorPoint*/ ctx[0].y}px, 0) scale(${/*$selectionScale*/ ctx[5]}, ${/*$selectionScale*/ ctx[5]}); opacity: ${/*$selectionOpacity*/ ctx[6]}`);
		},
		m(target, anchor) {
			insert(target, div, anchor);

			if (!mounted) {
				dispose = [
					listen(div, "keydown", /*handleKeyDown*/ ctx[7]),
					listen(div, "keyup", /*handleKeyUp*/ ctx[8]),
					listen(div, "nudge", /*handleRotateNudge*/ ctx[13]),
					action_destroyer(nudgeable_action = nudgeable.call(null, div)),
					listen(div, "interactionstart", /*rotate*/ ctx[14]("start")),
					listen(div, "interactionupdate", /*rotate*/ ctx[14]("move")),
					listen(div, "interactionend", /*rotate*/ ctx[14]("end")),
					action_destroyer(interactable_action = interactable.call(null, div))
				];

				mounted = true;
			}
		},
		p(ctx, dirty) {
			if (dirty[0] & /*rotatorPoint, $selectionScale, $selectionOpacity*/ 97 && div_style_value !== (div_style_value = `transform: translate3d(${/*rotatorPoint*/ ctx[0].x}px, ${/*rotatorPoint*/ ctx[0].y}px, 0) scale(${/*$selectionScale*/ ctx[5]}, ${/*$selectionScale*/ ctx[5]}); opacity: ${/*$selectionOpacity*/ ctx[6]}`)) {
				attr(div, "style", div_style_value);
			}
		},
		d(detaching) {
			if (detaching) detach(div);
			mounted = false;
			run_all(dispose);
		}
	};
}

function create_fragment$q(ctx) {
	let t;
	let if_block1_anchor;
	let if_block0 = /*enableResizing*/ ctx[1] && create_if_block_1$7(ctx);
	let if_block1 = /*enableRotating*/ ctx[2] && /*rotationControlActive*/ ctx[4] && create_if_block$5(ctx);

	return {
		c() {
			if (if_block0) if_block0.c();
			t = space();
			if (if_block1) if_block1.c();
			if_block1_anchor = empty();
		},
		m(target, anchor) {
			if (if_block0) if_block0.m(target, anchor);
			insert(target, t, anchor);
			if (if_block1) if_block1.m(target, anchor);
			insert(target, if_block1_anchor, anchor);
		},
		p(ctx, dirty) {
			if (/*enableResizing*/ ctx[1]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);
				} else {
					if_block0 = create_if_block_1$7(ctx);
					if_block0.c();
					if_block0.m(t.parentNode, t);
				}
			} else if (if_block0) {
				if_block0.d(1);
				if_block0 = null;
			}

			if (/*enableRotating*/ ctx[2] && /*rotationControlActive*/ ctx[4]) {
				if (if_block1) {
					if_block1.p(ctx, dirty);
				} else {
					if_block1 = create_if_block$5(ctx);
					if_block1.c();
					if_block1.m(if_block1_anchor.parentNode, if_block1_anchor);
				}
			} else if (if_block1) {
				if_block1.d(1);
				if_block1 = null;
			}
		},
		i: noop$1,
		o: noop$1,
		d(detaching) {
			if (if_block0) if_block0.d(detaching);
			if (detaching) detach(t);
			if (if_block1) if_block1.d(detaching);
			if (detaching) detach(if_block1_anchor);
		}
	};
}

function instance$q($$self, $$props, $$invalidate) {
	let $selectionScale;
	let $selectionOpacity;
	const dispatch = createEventDispatcher();

	// which cursor to render
	const EIGTH_PI = QUART_PI * 0.5;

	const N = HALF_PI;
	const N0 = N - EIGTH_PI;
	const N1 = N + EIGTH_PI;
	const S = -HALF_PI;
	const S0 = S - EIGTH_PI;
	const S1 = S + EIGTH_PI;
	const E = PI;
	const E0 = E - EIGTH_PI;
	const E1 = -PI + EIGTH_PI;
	const W0 = EIGTH_PI;
	const W1 = -EIGTH_PI;
	const NW = N - QUART_PI;
	const NW0 = NW - EIGTH_PI;
	const NW1 = NW + EIGTH_PI;
	const NE = PI - QUART_PI;
	const NE0 = NE - EIGTH_PI;
	const NE1 = NE + EIGTH_PI;
	const SE = S - QUART_PI;
	const SE0 = SE + EIGTH_PI;
	const SE1 = SE - EIGTH_PI;
	const SW = S + QUART_PI;
	const SW0 = SW + EIGTH_PI;
	const SW1 = SW - EIGTH_PI;
	let { points = [] } = $$props;
	let { rotatorPoint = undefined } = $$props;
	let { visible = false } = $$props;
	let { enableResizing = true } = $$props;
	let { enableRotating = true } = $$props;

	// // internal
	let shiftKey = false;

	const handleKeyDown = e => shiftKey = e.shiftKey;
	const handleKeyUp = e => shiftKey = false;

	// state
	const selectionScale = spring(0.5, {
		precision: 0.0001,
		stiffness: 0.3,
		damping: 0.7
	});

	component_subscribe($$self, selectionScale, value => $$invalidate(5, $selectionScale = value));
	const selectionOpacity = spring(0, { precision: 0.001 });
	component_subscribe($$self, selectionOpacity, value => $$invalidate(6, $selectionOpacity = value));

	const rotate = type => ({ detail }) => {
		// always make sure we have a translation to work with
		const translation = detail && detail.translation
		? detail.translation
		: vectorCreate(0, 0);

		dispatch(`rotate${type}`, { translation, shiftKey });
	};

	const resize = (type, indexes) => ({ detail }) => {
		// always make sure we have a translation to work with
		const translation = detail && detail.translation
		? detail.translation
		: vectorCreate(0, 0);

		// done transforming the points
		dispatch(`resize${type}`, { indexes, translation, shiftKey });
	};

	// https://en.wikipedia.org/wiki/Radian#/media/File:Degree-Radian_Conversion.svg
	const getDirectionalCursorByAngle = angle => {
		let dir = "";
		const isNorth = angle <= N1 && angle >= N0;
		const isSouth = angle >= S0 && angle <= S1;
		const isEast = angle <= E1 || angle >= E0;
		const isWest = angle >= W1 && angle <= W0;
		const isNorthEast = angle >= NE0 && angle <= NE1;
		const isNorthWest = angle >= NW0 && angle <= NW1;
		const isSouthEast = angle <= SE0 && angle >= SE1;
		const isSouthWest = angle <= SW0 && angle >= SW1;
		if (isNorth || isSouth) dir = "ns";
		if (isEast || isWest) dir = "ew";
		if (isNorthEast || isSouthWest) dir = "nesw";
		if (isNorthWest || isSouthEast) dir = "nwse";
		return `${dir}-resize`;
	};

	// 2 points -> [corner, corner]
	// 3 points => [corner, edge, corner, edge, corner, edge],
	// 4 points => [corner, edge, corner, edge, corner, edge, corner, edge]
	const mapPointsToControls = points => {
		let i = 0;
		const center = vectorCenter(points);
		const out = [];
		const l = points.length;
		const isLine = l === 2;

		for (; i < l; i++) {
			const p0 = points[i - 1] || points[points.length - 1];
			const p1 = points[i];
			const p2 = points[i + 1] || points[0];
			const dir = Math.atan2(p2.y - p1.y, p2.x - p1.x);

			// const angle = Math.atan2(center.y - p1.y, center.x - p1.x);
			const a = vectorNormalize(vectorCreate(p0.x - p1.x, p0.y - p1.y));

			const b = vectorNormalize(vectorCreate(p2.x - p1.x, p2.y - p1.y));
			const cornerVector = vectorCreate(a.x + b.x, a.y + b.y);

			// create corner
			out.push({
				index: [i],
				key: `corner-${i}`,
				type: "corner",
				scale: { x: 1, y: 1 },
				translate: { x: p1.x, y: p1.y },
				angle: undefined,
				rotate: isLine ? 0 : dir,
				cursor: isLine
				? "move"
				: getDirectionalCursorByAngle(Math.atan2(cornerVector.y, cornerVector.x))
			});

			// if only two points, skip edge
			if (isLine) continue;

			const mid = vectorCreate(p1.x + (p2.x - p1.x) * 0.5, p1.y + (p2.y - p1.y) * 0.5);

			// create edge
			out.push({
				index: [i, i + 1 === l ? 0 : i + 1],
				key: `edge-${i}`,
				type: "edge",
				scale: { x: vectorDistance(p1, p2), y: 1 },
				translate: { x: p1.x, y: p1.y },
				angle: dir,
				rotate: dir,
				cursor: getDirectionalCursorByAngle(Math.atan2(center.y - mid.y, center.x - mid.x))
			});
		}

		return out;
	};

	const handleResizeNudge = (indexes, translation) => {
		dispatch(`resizestart`, {
			indexes,
			translation: vectorCreateEmpty()
		});

		dispatch(`resizemove`, { indexes, translation });

		dispatch(`resizeend`, {
			indexes,
			translation: vectorCreateEmpty()
		});
	};

	const handleRotateNudge = ({ detail }) => {
		dispatch(`rotatestart`, { translation: vectorCreateEmpty() });
		dispatch(`rotatemove`, { translation: detail });
		dispatch(`rotateend`, { translation: vectorCreateEmpty() });
	};

	const nudge_handler = (index, { detail }) => handleResizeNudge(index, detail);

	$$self.$$set = $$props => {
		if ("points" in $$props) $$invalidate(15, points = $$props.points);
		if ("rotatorPoint" in $$props) $$invalidate(0, rotatorPoint = $$props.rotatorPoint);
		if ("visible" in $$props) $$invalidate(16, visible = $$props.visible);
		if ("enableResizing" in $$props) $$invalidate(1, enableResizing = $$props.enableResizing);
		if ("enableRotating" in $$props) $$invalidate(2, enableRotating = $$props.enableRotating);
	};

	let resizeControls;
	let rotationControlActive;

	$$self.$$.update = () => {
		if ($$self.$$.dirty[0] & /*visible*/ 65536) {
			 selectionScale.set(visible ? 1 : 0.5);
		}

		if ($$self.$$.dirty[0] & /*visible*/ 65536) {
			 selectionOpacity.set(visible ? 1 : 0);
		}

		if ($$self.$$.dirty[0] & /*points*/ 32768) {
			 $$invalidate(3, resizeControls = mapPointsToControls(points));
		}

		if ($$self.$$.dirty[0] & /*points*/ 32768) {
			 $$invalidate(4, rotationControlActive = points.length > 2);
		}
	};

	return [
		rotatorPoint,
		enableResizing,
		enableRotating,
		resizeControls,
		rotationControlActive,
		$selectionScale,
		$selectionOpacity,
		handleKeyDown,
		handleKeyUp,
		selectionScale,
		selectionOpacity,
		resize,
		handleResizeNudge,
		handleRotateNudge,
		rotate,
		points,
		visible,
		nudge_handler
	];
}

class ShapeManipulator extends SvelteComponent {
	constructor(options) {
		super();

		init(
			this,
			options,
			instance$q,
			create_fragment$q,
			safe_not_equal,
			{
				points: 15,
				rotatorPoint: 0,
				visible: 16,
				enableResizing: 1,
				enableRotating: 2
			},
			[-1, -1]
		);
	}
}

var getEventPositionInEditor = (e, viewOffset) => {
    const positionInViewport = getEventPositionInViewport(e);
    return vectorSubtract(positionInViewport, viewOffset);
};

/* src/native/ui/components/ColorPreview.svelte generated by Svelte v3.29.4 */

function create_fragment$r(ctx) {
	let div;
	let span;
	let div_style_value;

	return {
		c() {
			div = element("div");
			span = element("span");
			attr(div, "class", "DokaColorPreview");
			attr(div, "title", /*title*/ ctx[0]);
			attr(div, "style", div_style_value = `--color:${/*colorValue*/ ctx[1]}`);
		},
		m(target, anchor) {
			insert(target, div, anchor);
			append(div, span);
		},
		p(ctx, [dirty]) {
			if (dirty & /*title*/ 1) {
				attr(div, "title", /*title*/ ctx[0]);
			}

			if (dirty & /*colorValue*/ 2 && div_style_value !== (div_style_value = `--color:${/*colorValue*/ ctx[1]}`)) {
				attr(div, "style", div_style_value);
			}
		},
		i: noop$1,
		o: noop$1,
		d(detaching) {
			if (detaching) detach(div);
		}
	};
}

function instance$r($$self, $$props, $$invalidate) {
	let { color = undefined } = $$props;
	let { title = undefined } = $$props;

	$$self.$$set = $$props => {
		if ("color" in $$props) $$invalidate(2, color = $$props.color);
		if ("title" in $$props) $$invalidate(0, title = $$props.title);
	};

	let colorValue;

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*color*/ 4) {
			 $$invalidate(1, colorValue = color ? colorArrayToRGBA(color) : "transparent");
		}
	};

	return [title, colorValue, color];
}

class ColorPreview extends SvelteComponent {
	constructor(options) {
		super();
		init(this, options, instance$r, create_fragment$r, safe_not_equal, { color: 2, title: 0 });
	}
}

/* src/native/ui/components/ShapeLayoutEditor.svelte generated by Svelte v3.29.4 */

const { Map: Map_1 } = globals;

function get_each_context$6(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[132] = list[i];
	child_ctx[134] = i;
	return child_ctx;
}

// (1635:8) {#each shapeNavList as item, index (item.id) }
function create_each_block$6(key_1, ctx) {
	let li;
	let button;
	let colorpreview;
	let t0;
	let span;
	let t1_value = /*item*/ ctx[132].name + "";
	let t1;
	let button_aria_label_value;
	let t2;
	let current;
	let mounted;
	let dispose;

	colorpreview = new ColorPreview({
			props: { color: /*item*/ ctx[132].color }
		});

	function click_handler(...args) {
		return /*click_handler*/ ctx[94](/*index*/ ctx[134], ...args);
	}

	return {
		key: key_1,
		first: null,
		c() {
			li = element("li");
			button = element("button");
			create_component(colorpreview.$$.fragment);
			t0 = space();
			span = element("span");
			t1 = text(t1_value);
			t2 = space();
			attr(button, "class", "DokaShapeListItem");
			attr(button, "type", "button");
			attr(button, "aria-label", button_aria_label_value = "Select shape " + /*item*/ ctx[132].name);
			this.first = li;
		},
		m(target, anchor) {
			insert(target, li, anchor);
			append(li, button);
			mount_component(colorpreview, button, null);
			append(button, t0);
			append(button, span);
			append(span, t1);
			append(li, t2);
			current = true;

			if (!mounted) {
				dispose = listen(button, "click", click_handler);
				mounted = true;
			}
		},
		p(new_ctx, dirty) {
			ctx = new_ctx;
			const colorpreview_changes = {};
			if (dirty[0] & /*shapeNavList*/ 8388608) colorpreview_changes.color = /*item*/ ctx[132].color;
			colorpreview.$set(colorpreview_changes);
			if ((!current || dirty[0] & /*shapeNavList*/ 8388608) && t1_value !== (t1_value = /*item*/ ctx[132].name + "")) set_data(t1, t1_value);

			if (!current || dirty[0] & /*shapeNavList*/ 8388608 && button_aria_label_value !== (button_aria_label_value = "Select shape " + /*item*/ ctx[132].name)) {
				attr(button, "aria-label", button_aria_label_value);
			}
		},
		i(local) {
			if (current) return;
			transition_in(colorpreview.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(colorpreview.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(li);
			destroy_component(colorpreview);
			mounted = false;
			dispose();
		}
	};
}

// (1646:4) {#if shouldRenderShapeManipulator}
function create_if_block_8$2(ctx) {
	let shapemanipulator;
	let current;

	shapemanipulator = new ShapeManipulator({
			props: {
				visible: true,
				points: /*shapeManipulatorPoints*/ ctx[10],
				rotatorPoint: /*shapeManipulatorRotationPointPosition*/ ctx[11],
				enableResizing: /*allowResizeControls*/ ctx[7],
				enableRotating: /*allowRotateControls*/ ctx[8]
			}
		});

	shapemanipulator.$on("resizestart", /*handleManipulatorResizeGrab*/ ctx[30]);
	shapemanipulator.$on("resizemove", /*handleManipulatorResizeDrag*/ ctx[31]);
	shapemanipulator.$on("resizeend", /*handleManipulatorResizeEnd*/ ctx[32]);
	shapemanipulator.$on("rotatestart", /*handleManipulatorRotateGrab*/ ctx[33]);
	shapemanipulator.$on("rotatemove", /*handleManipulatorRotateDrag*/ ctx[34]);
	shapemanipulator.$on("rotateend", /*handleManipulatorRotateEnd*/ ctx[35]);

	return {
		c() {
			create_component(shapemanipulator.$$.fragment);
		},
		m(target, anchor) {
			mount_component(shapemanipulator, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const shapemanipulator_changes = {};
			if (dirty[0] & /*shapeManipulatorPoints*/ 1024) shapemanipulator_changes.points = /*shapeManipulatorPoints*/ ctx[10];
			if (dirty[0] & /*shapeManipulatorRotationPointPosition*/ 2048) shapemanipulator_changes.rotatorPoint = /*shapeManipulatorRotationPointPosition*/ ctx[11];
			if (dirty[0] & /*allowResizeControls*/ 128) shapemanipulator_changes.enableResizing = /*allowResizeControls*/ ctx[7];
			if (dirty[0] & /*allowRotateControls*/ 256) shapemanipulator_changes.enableRotating = /*allowRotateControls*/ ctx[8];
			shapemanipulator.$set(shapemanipulator_changes);
		},
		i(local) {
			if (current) return;
			transition_in(shapemanipulator.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(shapemanipulator.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(shapemanipulator, detaching);
		}
	};
}

// (1661:4) {#if shouldRenderTextInput}
function create_if_block_7$3(ctx) {
	let div;
	let textarea;
	let t0;
	let button0;
	let t1;
	let button1;
	let current;
	let mounted;
	let dispose;

	button0 = new Button({
			props: {
				onclick: /*handleTextCancel*/ ctx[42],
				label: /*locale*/ ctx[3].shapeLabelInputCancel,
				icon: /*locale*/ ctx[3].shapeIconInputCancel,
				hideLabel: true
			}
		});

	button1 = new Button({
			props: {
				onclick: /*handleTextConfirm*/ ctx[41],
				label: /*locale*/ ctx[3].shapeLabelInputConfirm,
				icon: /*locale*/ ctx[3].shapeIconInputConfirm,
				hideLabel: true
			}
		});

	return {
		c() {
			div = element("div");
			textarea = element("textarea");
			t0 = space();
			create_component(button0.$$.fragment);
			t1 = space();
			create_component(button1.$$.fragment);
			attr(textarea, "style", /*markupTextInputStyle*/ ctx[15]);
			attr(textarea, "spellcheck", "false");
			attr(textarea, "autocorrect", "off");
			attr(textarea, "autocapitalize", "off");
			textarea.value = /*textInputText*/ ctx[13];
			attr(div, "class", "DokaTextInput");
			attr(div, "style", /*markupTextPanelStyle*/ ctx[14]);
		},
		m(target, anchor) {
			insert(target, div, anchor);
			append(div, textarea);
			/*textarea_binding*/ ctx[95](textarea);
			append(div, t0);
			mount_component(button0, div, null);
			append(div, t1);
			mount_component(button1, div, null);
			current = true;

			if (!mounted) {
				dispose = [
					listen(textarea, "keypress", /*handleTextInputAttempt*/ ctx[39]),
					listen(textarea, "keyup", /*handleTextInputKeyUp*/ ctx[40])
				];

				mounted = true;
			}
		},
		p(ctx, dirty) {
			if (!current || dirty[0] & /*markupTextInputStyle*/ 32768) {
				attr(textarea, "style", /*markupTextInputStyle*/ ctx[15]);
			}

			if (!current || dirty[0] & /*textInputText*/ 8192) {
				textarea.value = /*textInputText*/ ctx[13];
			}

			const button0_changes = {};
			if (dirty[0] & /*locale*/ 8) button0_changes.label = /*locale*/ ctx[3].shapeLabelInputCancel;
			if (dirty[0] & /*locale*/ 8) button0_changes.icon = /*locale*/ ctx[3].shapeIconInputCancel;
			button0.$set(button0_changes);
			const button1_changes = {};
			if (dirty[0] & /*locale*/ 8) button1_changes.label = /*locale*/ ctx[3].shapeLabelInputConfirm;
			if (dirty[0] & /*locale*/ 8) button1_changes.icon = /*locale*/ ctx[3].shapeIconInputConfirm;
			button1.$set(button1_changes);

			if (!current || dirty[0] & /*markupTextPanelStyle*/ 16384) {
				attr(div, "style", /*markupTextPanelStyle*/ ctx[14]);
			}
		},
		i(local) {
			if (current) return;
			transition_in(button0.$$.fragment, local);
			transition_in(button1.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(button0.$$.fragment, local);
			transition_out(button1.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			/*textarea_binding*/ ctx[95](null);
			destroy_component(button0);
			destroy_component(button1);
			mounted = false;
			run_all(dispose);
		}
	};
}

// (1674:4) {#if $markupControlsOpacity > 0}
function create_if_block$6(ctx) {
	let div2;
	let div1;
	let div0;
	let t0;
	let t1;
	let t2;
	let t3;
	let current;
	let if_block0 = /*allowShapeFlip*/ ctx[16] && create_if_block_5$3(ctx);
	let if_block1 = /*allowShapeReorder*/ ctx[19] && create_if_block_4$3(ctx);
	let if_block2 = /*allowShapeDuplicate*/ ctx[17] && create_if_block_3$3(ctx);
	let if_block3 = /*allowShapeRemove*/ ctx[18] && create_if_block_2$5(ctx);
	let if_block4 = /*allowShapeInput*/ ctx[20] && create_if_block_1$8(ctx);

	return {
		c() {
			div2 = element("div");
			div1 = element("div");
			div0 = element("div");
			if (if_block0) if_block0.c();
			t0 = space();
			if (if_block1) if_block1.c();
			t1 = space();
			if (if_block2) if_block2.c();
			t2 = space();
			if (if_block3) if_block3.c();
			t3 = space();
			if (if_block4) if_block4.c();
			attr(div0, "class", "DokaShapeControlsGroup");
			attr(div1, "class", "DokaShapeControlsInner");
			attr(div2, "class", "DokaShapeControls");
			attr(div2, "style", /*markupControlsStyle*/ ctx[21]);
		},
		m(target, anchor) {
			insert(target, div2, anchor);
			append(div2, div1);
			append(div1, div0);
			if (if_block0) if_block0.m(div0, null);
			append(div0, t0);
			if (if_block1) if_block1.m(div0, null);
			append(div0, t1);
			if (if_block2) if_block2.m(div0, null);
			append(div0, t2);
			if (if_block3) if_block3.m(div0, null);
			append(div1, t3);
			if (if_block4) if_block4.m(div1, null);
			current = true;
		},
		p(ctx, dirty) {
			if (/*allowShapeFlip*/ ctx[16]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty[0] & /*allowShapeFlip*/ 65536) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_5$3(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(div0, t0);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (/*allowShapeReorder*/ ctx[19]) {
				if (if_block1) {
					if_block1.p(ctx, dirty);

					if (dirty[0] & /*allowShapeReorder*/ 524288) {
						transition_in(if_block1, 1);
					}
				} else {
					if_block1 = create_if_block_4$3(ctx);
					if_block1.c();
					transition_in(if_block1, 1);
					if_block1.m(div0, t1);
				}
			} else if (if_block1) {
				group_outros();

				transition_out(if_block1, 1, 1, () => {
					if_block1 = null;
				});

				check_outros();
			}

			if (/*allowShapeDuplicate*/ ctx[17]) {
				if (if_block2) {
					if_block2.p(ctx, dirty);

					if (dirty[0] & /*allowShapeDuplicate*/ 131072) {
						transition_in(if_block2, 1);
					}
				} else {
					if_block2 = create_if_block_3$3(ctx);
					if_block2.c();
					transition_in(if_block2, 1);
					if_block2.m(div0, t2);
				}
			} else if (if_block2) {
				group_outros();

				transition_out(if_block2, 1, 1, () => {
					if_block2 = null;
				});

				check_outros();
			}

			if (/*allowShapeRemove*/ ctx[18]) {
				if (if_block3) {
					if_block3.p(ctx, dirty);

					if (dirty[0] & /*allowShapeRemove*/ 262144) {
						transition_in(if_block3, 1);
					}
				} else {
					if_block3 = create_if_block_2$5(ctx);
					if_block3.c();
					transition_in(if_block3, 1);
					if_block3.m(div0, null);
				}
			} else if (if_block3) {
				group_outros();

				transition_out(if_block3, 1, 1, () => {
					if_block3 = null;
				});

				check_outros();
			}

			if (/*allowShapeInput*/ ctx[20]) {
				if (if_block4) {
					if_block4.p(ctx, dirty);

					if (dirty[0] & /*allowShapeInput*/ 1048576) {
						transition_in(if_block4, 1);
					}
				} else {
					if_block4 = create_if_block_1$8(ctx);
					if_block4.c();
					transition_in(if_block4, 1);
					if_block4.m(div1, null);
				}
			} else if (if_block4) {
				group_outros();

				transition_out(if_block4, 1, 1, () => {
					if_block4 = null;
				});

				check_outros();
			}

			if (!current || dirty[0] & /*markupControlsStyle*/ 2097152) {
				attr(div2, "style", /*markupControlsStyle*/ ctx[21]);
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);
			transition_in(if_block1);
			transition_in(if_block2);
			transition_in(if_block3);
			transition_in(if_block4);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			transition_out(if_block1);
			transition_out(if_block2);
			transition_out(if_block3);
			transition_out(if_block4);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div2);
			if (if_block0) if_block0.d();
			if (if_block1) if_block1.d();
			if (if_block2) if_block2.d();
			if (if_block3) if_block3.d();
			if (if_block4) if_block4.d();
		}
	};
}

// (1678:16) {#if allowShapeFlip}
function create_if_block_5$3(ctx) {
	let button;
	let t;
	let if_block_anchor;
	let current;

	button = new Button({
			props: {
				onclick: /*handleFlipX*/ ctx[43],
				label: /*locale*/ ctx[3].shapeTitleButtonFlipHorizontal,
				icon: /*locale*/ ctx[3].shapeIconButtonFlipHorizontal,
				hideLabel: true
			}
		});

	let if_block = /*enableButtonFlipVertical*/ ctx[2] && create_if_block_6$3(ctx);

	return {
		c() {
			create_component(button.$$.fragment);
			t = space();
			if (if_block) if_block.c();
			if_block_anchor = empty();
		},
		m(target, anchor) {
			mount_component(button, target, anchor);
			insert(target, t, anchor);
			if (if_block) if_block.m(target, anchor);
			insert(target, if_block_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const button_changes = {};
			if (dirty[0] & /*locale*/ 8) button_changes.label = /*locale*/ ctx[3].shapeTitleButtonFlipHorizontal;
			if (dirty[0] & /*locale*/ 8) button_changes.icon = /*locale*/ ctx[3].shapeIconButtonFlipHorizontal;
			button.$set(button_changes);

			if (/*enableButtonFlipVertical*/ ctx[2]) {
				if (if_block) {
					if_block.p(ctx, dirty);

					if (dirty[0] & /*enableButtonFlipVertical*/ 4) {
						transition_in(if_block, 1);
					}
				} else {
					if_block = create_if_block_6$3(ctx);
					if_block.c();
					transition_in(if_block, 1);
					if_block.m(if_block_anchor.parentNode, if_block_anchor);
				}
			} else if (if_block) {
				group_outros();

				transition_out(if_block, 1, 1, () => {
					if_block = null;
				});

				check_outros();
			}
		},
		i(local) {
			if (current) return;
			transition_in(button.$$.fragment, local);
			transition_in(if_block);
			current = true;
		},
		o(local) {
			transition_out(button.$$.fragment, local);
			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			destroy_component(button, detaching);
			if (detaching) detach(t);
			if (if_block) if_block.d(detaching);
			if (detaching) detach(if_block_anchor);
		}
	};
}

// (1680:20) {#if enableButtonFlipVertical}
function create_if_block_6$3(ctx) {
	let button;
	let current;

	button = new Button({
			props: {
				onclick: /*handleFlipY*/ ctx[44],
				label: /*locale*/ ctx[3].shapeIconButtonFlipVertical,
				icon: /*locale*/ ctx[3].shapeTitleButtonFlipVertical,
				hideLabel: true
			}
		});

	return {
		c() {
			create_component(button.$$.fragment);
		},
		m(target, anchor) {
			mount_component(button, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const button_changes = {};
			if (dirty[0] & /*locale*/ 8) button_changes.label = /*locale*/ ctx[3].shapeIconButtonFlipVertical;
			if (dirty[0] & /*locale*/ 8) button_changes.icon = /*locale*/ ctx[3].shapeTitleButtonFlipVertical;
			button.$set(button_changes);
		},
		i(local) {
			if (current) return;
			transition_in(button.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(button.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(button, detaching);
		}
	};
}

// (1684:16) {#if allowShapeReorder}
function create_if_block_4$3(ctx) {
	let button;
	let current;

	button = new Button({
			props: {
				onclick: /*handleMoveToFrontActiveMarkup*/ ctx[46],
				label: /*locale*/ ctx[3].shapeTitleButtonMoveToFront,
				icon: /*locale*/ ctx[3].shapeIconButtonMoveToFront,
				hideLabel: true
			}
		});

	return {
		c() {
			create_component(button.$$.fragment);
		},
		m(target, anchor) {
			mount_component(button, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const button_changes = {};
			if (dirty[0] & /*locale*/ 8) button_changes.label = /*locale*/ ctx[3].shapeTitleButtonMoveToFront;
			if (dirty[0] & /*locale*/ 8) button_changes.icon = /*locale*/ ctx[3].shapeIconButtonMoveToFront;
			button.$set(button_changes);
		},
		i(local) {
			if (current) return;
			transition_in(button.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(button.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(button, detaching);
		}
	};
}

// (1687:16) {#if allowShapeDuplicate}
function create_if_block_3$3(ctx) {
	let button;
	let current;

	button = new Button({
			props: {
				onclick: /*handleDuplicateActiveMarkup*/ ctx[47],
				label: /*locale*/ ctx[3].shapeTitleButtonDuplicate,
				icon: /*locale*/ ctx[3].shapeIconButtonDuplicate,
				hideLabel: true
			}
		});

	return {
		c() {
			create_component(button.$$.fragment);
		},
		m(target, anchor) {
			mount_component(button, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const button_changes = {};
			if (dirty[0] & /*locale*/ 8) button_changes.label = /*locale*/ ctx[3].shapeTitleButtonDuplicate;
			if (dirty[0] & /*locale*/ 8) button_changes.icon = /*locale*/ ctx[3].shapeIconButtonDuplicate;
			button.$set(button_changes);
		},
		i(local) {
			if (current) return;
			transition_in(button.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(button.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(button, detaching);
		}
	};
}

// (1690:16) {#if allowShapeRemove}
function create_if_block_2$5(ctx) {
	let button;
	let current;

	button = new Button({
			props: {
				onclick: /*handleRemoveActiveMarkup*/ ctx[45],
				label: /*locale*/ ctx[3].shapeTitleButtonRemove,
				icon: /*locale*/ ctx[3].shapeIconButtonRemove,
				hideLabel: true
			}
		});

	return {
		c() {
			create_component(button.$$.fragment);
		},
		m(target, anchor) {
			mount_component(button, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const button_changes = {};
			if (dirty[0] & /*locale*/ 8) button_changes.label = /*locale*/ ctx[3].shapeTitleButtonRemove;
			if (dirty[0] & /*locale*/ 8) button_changes.icon = /*locale*/ ctx[3].shapeIconButtonRemove;
			button.$set(button_changes);
		},
		i(local) {
			if (current) return;
			transition_in(button.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(button.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(button, detaching);
		}
	};
}

// (1694:12) {#if allowShapeInput}
function create_if_block_1$8(ctx) {
	let div;
	let button;
	let current;

	button = new Button({
			props: {
				onclick: /*handleEditTextActiveMarkup*/ ctx[38],
				label: /*locale*/ ctx[3].shapeLabelInputText
			}
		});

	return {
		c() {
			div = element("div");
			create_component(button.$$.fragment);
			attr(div, "class", "DokaShapeControlsGroup");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			mount_component(button, div, null);
			current = true;
		},
		p(ctx, dirty) {
			const button_changes = {};
			if (dirty[0] & /*locale*/ 8) button_changes.label = /*locale*/ ctx[3].shapeLabelInputText;
			button.$set(button_changes);
		},
		i(local) {
			if (current) return;
			transition_in(button.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(button.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			destroy_component(button);
		}
	};
}

function create_fragment$s(ctx) {
	let div;
	let nav;
	let ul;
	let each_blocks = [];
	let each_1_lookup = new Map_1();
	let t0;
	let t1;
	let t2;
	let measurable_action;
	let nudgeable_action;
	let interactable_action;
	let current;
	let mounted;
	let dispose;
	let each_value = /*shapeNavList*/ ctx[23];
	const get_key = ctx => /*item*/ ctx[132].id;

	for (let i = 0; i < each_value.length; i += 1) {
		let child_ctx = get_each_context$6(ctx, each_value, i);
		let key = get_key(child_ctx);
		each_1_lookup.set(key, each_blocks[i] = create_each_block$6(key, child_ctx));
	}

	let if_block0 = /*shouldRenderShapeManipulator*/ ctx[9] && create_if_block_8$2(ctx);
	let if_block1 = /*shouldRenderTextInput*/ ctx[12] && create_if_block_7$3(ctx);
	let if_block2 = /*$markupControlsOpacity*/ ctx[22] > 0 && create_if_block$6(ctx);

	return {
		c() {
			div = element("div");
			nav = element("nav");
			ul = element("ul");

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			t0 = space();
			if (if_block0) if_block0.c();
			t1 = space();
			if (if_block1) if_block1.c();
			t2 = space();
			if (if_block2) if_block2.c();
			attr(nav, "class", "DokaShapeList");
			attr(nav, "data-visible", /*showShapeList*/ ctx[6]);
			attr(div, "class", "DokaShapeEditor");
			attr(div, "tabindex", "0");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			append(div, nav);
			append(nav, ul);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].m(ul, null);
			}

			append(div, t0);
			if (if_block0) if_block0.m(div, null);
			append(div, t1);
			if (if_block1) if_block1.m(div, null);
			append(div, t2);
			if (if_block2) if_block2.m(div, null);
			current = true;

			if (!mounted) {
				dispose = [
					listen(nav, "focusin", /*handleFocusIn*/ ctx[50]),
					listen(nav, "focusout", /*handleFocusOut*/ ctx[51]),
					listen(div, "keydown", /*handleKey*/ ctx[36]),
					listen(div, "nudge", /*handleNudge*/ ctx[49]),
					listen(div, "measure", /*measure_handler*/ ctx[93]),
					action_destroyer(measurable_action = measurable.call(null, div)),
					action_destroyer(nudgeable_action = nudgeable.call(null, div)),
					listen(div, "wheel", /*handleWheel*/ ctx[25]),
					listen(div, "interactionstart", /*handleInteractionStart*/ ctx[26]),
					listen(div, "interactionupdate", /*handleInteractionUpdate*/ ctx[27]),
					listen(div, "interactionrelease", /*handleInteractionRelease*/ ctx[28]),
					listen(div, "interactionend", /*handleInteractionEnd*/ ctx[29]),
					action_destroyer(interactable_action = interactable.call(null, div, {
						drag: true,
						pinch: true,
						inertia: true,
						matchTarget: true,
						getEventPosition: /*interactable_function*/ ctx[96]
					}))
				];

				mounted = true;
			}
		},
		p(ctx, dirty) {
			if (dirty[0] & /*shapeNavList, selectShape, markup*/ 8388625) {
				const each_value = /*shapeNavList*/ ctx[23];
				group_outros();
				each_blocks = update_keyed_each(each_blocks, dirty, get_key, 1, ctx, each_value, each_1_lookup, ul, outro_and_destroy_block, create_each_block$6, null, get_each_context$6);
				check_outros();
			}

			if (!current || dirty[0] & /*showShapeList*/ 64) {
				attr(nav, "data-visible", /*showShapeList*/ ctx[6]);
			}

			if (/*shouldRenderShapeManipulator*/ ctx[9]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty[0] & /*shouldRenderShapeManipulator*/ 512) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_8$2(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(div, t1);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (/*shouldRenderTextInput*/ ctx[12]) {
				if (if_block1) {
					if_block1.p(ctx, dirty);

					if (dirty[0] & /*shouldRenderTextInput*/ 4096) {
						transition_in(if_block1, 1);
					}
				} else {
					if_block1 = create_if_block_7$3(ctx);
					if_block1.c();
					transition_in(if_block1, 1);
					if_block1.m(div, t2);
				}
			} else if (if_block1) {
				group_outros();

				transition_out(if_block1, 1, 1, () => {
					if_block1 = null;
				});

				check_outros();
			}

			if (/*$markupControlsOpacity*/ ctx[22] > 0) {
				if (if_block2) {
					if_block2.p(ctx, dirty);

					if (dirty[0] & /*$markupControlsOpacity*/ 4194304) {
						transition_in(if_block2, 1);
					}
				} else {
					if_block2 = create_if_block$6(ctx);
					if_block2.c();
					transition_in(if_block2, 1);
					if_block2.m(div, null);
				}
			} else if (if_block2) {
				group_outros();

				transition_out(if_block2, 1, 1, () => {
					if_block2 = null;
				});

				check_outros();
			}

			if (interactable_action && is_function(interactable_action.update) && dirty[0] & /*rootRect*/ 2) interactable_action.update.call(null, {
				drag: true,
				pinch: true,
				inertia: true,
				matchTarget: true,
				getEventPosition: /*interactable_function*/ ctx[96]
			});
		},
		i(local) {
			if (current) return;

			for (let i = 0; i < each_value.length; i += 1) {
				transition_in(each_blocks[i]);
			}

			transition_in(if_block0);
			transition_in(if_block1);
			transition_in(if_block2);
			current = true;
		},
		o(local) {
			for (let i = 0; i < each_blocks.length; i += 1) {
				transition_out(each_blocks[i]);
			}

			transition_out(if_block0);
			transition_out(if_block1);
			transition_out(if_block2);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].d();
			}

			if (if_block0) if_block0.d();
			if (if_block1) if_block1.d();
			if (if_block2) if_block2.d();
			mounted = false;
			run_all(dispose);
		}
	};
}

const ROTATION_CONTROL_OFFSET = 20;
const MIN_TEXT_MARKUP_WIDTH = 10;

function instance$s($$self, $$props, $$invalidate) {
	let $keysPressedStored;
	let $markupTextPanelOpacity;
	let $markupControlsOpacity;
	let { markup } = $$props;
	let { offset } = $$props;
	let { rotation = 0 } = $$props;
	let { scale } = $$props;
	let { ui } = $$props;
	let { opacity = 1 } = $$props;
	let { parentRect } = $$props;
	let { rootRect } = $$props;
	let { stageRect } = $$props;
	let { oninteractionstart = noop } = $$props;
	let { oninteractionupdate = noop } = $$props;
	let { oninteractionrelease = noop } = $$props;
	let { oninteractionend = noop } = $$props;
	let { onaddshape = noop } = $$props;
	let { onupdateshape = noop } = $$props;
	let { onremoveshape = noop } = $$props;
	let { mapEditorPointToImagePoint } = $$props;
	let { mapImagePointToEditorPoint } = $$props;
	let { enableButtonFlipVertical = false } = $$props;
	let { locale } = $$props;

	//
	// Cache
	//
	const TextSizeCache = new Map();

	//
	// Helpers
	//
	const isPositionInRect = (position, rect, rotation) => {
		if (rotation) return !!polyPointIntersectionConvex(position, rectRotate(rect, rotation, rectCenter(rect)));
		const [t, r, b, l] = rectToBounds(rect);
		return position.x >= l && position.x <= r && (position.y >= t && position.y <= b);
	};

	const isPositionInEllipse = (position, ellipse, rotation) => {
		const rx = ellipse.rx;
		const ry = ellipse.ry;
		const x = ellipse.x;
		const y = ellipse.y;
		const angle = rotation || 0;
		const c = Math.cos(angle);
		const s = Math.sin(angle);
		const rx2 = rx * rx;
		const ry2 = ry * ry;
		const a = c * (position.x - x) + s * (position.y - y);
		const b = s * (position.x - x) - c * (position.y - y);
		const aa = a * a;
		const bb = b * b;
		return aa / rx2 + bb / ry2 <= 1;
	};

	const isPositionNearLine = (position, begin, end, treshold) => {
		const ba = Math.atan2(position.y - begin.y, position.x - begin.x);
		const ea = Math.atan2(position.y - end.y, position.x - end.x);
		if (ba > 0 && ea > 0 || ba < 0 && ea < 0) return false;
		const r = treshold * treshold;
		const d = vectorCreate(end.x - begin.x, end.y - begin.y);
		const f = vectorCreate(begin.x - position.x, begin.y - position.y);
		const a = vectorDot(d, d);
		const b = 2 * vectorDot(f, d);
		const c = vectorDot(f, f) - r;
		const discriminant = b * b - 4 * a * c;
		return discriminant >= 0;
	};

	//
	// Shape creator
	//
	const keysPressedStored = getContext("keysPressed");

	component_subscribe($$self, keysPressedStored, value => $$invalidate(103, $keysPressedStored = value));

	const createShape = (shapeDefault, options = {}) => {
		// state
		let interactionOrigin;

		let interactionRadius;
		let interactionPosition;
		let isEllipse = shapeIsEllipse(shapeDefault);
		let isText = shapeIsText(shapeDefault);
		let isRelative = options.position === "relative";

		if (shapeIsPath(shapeDefault)) {
			return {
				start: e => {
					const { origin } = e.detail;
					interactionRadius = 4;
					interactionOrigin = vectorClone(origin);
					interactionPosition = vectorClone(origin);
					const originPoint = mapEditorPointToImagePoint(origin);

					if (isRelative) {
						originPoint.x = isRelative
						? toPercentage(originPoint.x, parentRect.width)
						: originPoint.x;

						originPoint.y = isRelative
						? toPercentage(originPoint.y, parentRect.height)
						: originPoint.y;
					}

					// add to markup so it's drawn
					addMarkupItemDraft({ ...shapeDefault, points: [originPoint] });
				},
				update: e => {
					const draft = getMarkupItemDraft();
					const { translation } = e.detail;

					// position
					const pointerPosition = vectorCreate(interactionOrigin.x + translation.x, interactionOrigin.y + translation.y);

					// distance between interaction and pointer if it's too close, we don't draw this point
					const dist = vectorDistance(interactionPosition, pointerPosition);

					if (fixPrecision(dist, 5) <= interactionRadius) return;

					// get angle between interaction and pointer
					const angle = vectorAngleBetween(pointerPosition, interactionPosition);

					// move brush towards pointer at angle
					const moveDist = interactionRadius - dist;

					interactionPosition.x += moveDist * Math.cos(angle);
					interactionPosition.y += moveDist * Math.sin(angle);

					// create point
					const point = mapEditorPointToImagePoint(interactionPosition);

					if (point) {
						point.x = isRelative
						? toPercentage(point.x, parentRect.width)
						: point.x;

						point.y = isRelative
						? toPercentage(point.y, parentRect.height)
						: point.y;
					}

					draft.points = draft.points.concat(point);
					syncShapes();
				},
				release: e => e.detail.preventInertia(),
				end: e => {
					if (e.detail.isTap) return discardMarkupItemDraft();
					confirmMarkupItemDraft();
					onaddshape();
				}
			};
		} else if (isEllipse || isText || shapeIsRect(shapeDefault)) {
			return {
				start: e => {
					const { origin } = e.detail;

					// need to remember origin so we can correctly apply interaction translation
					interactionOrigin = vectorClone(origin);

					const mappedOriginPosition = mapEditorPointToImagePoint(interactionOrigin);

					// add to markup so it's drawn
					const draft = {
						...shapeDefault,
						rotation: -rotation,
						x: isRelative
						? toPercentage(mappedOriginPosition.x, parentRect.width)
						: mappedOriginPosition.x,
						y: isRelative
						? toPercentage(mappedOriginPosition.y, parentRect.height)
						: mappedOriginPosition.y
					};

					// remove position
					delete draft.position;

					// hide initially
					draft.opacity = 0;

					// 
					if (isEllipse) {
						draft.rx = isRelative ? toPercentage(0) : 0;
						draft.ry = isRelative ? toPercentage(0) : 0;
					} else {
						draft.width = isRelative ? toPercentage(0) : 0;
						draft.height = isRelative ? toPercentage(0) : 0;
					}

					addMarkupItemDraft(draft);
				},
				update: e => {
					const draft = getMarkupItemDraft();
					draft.opacity = 1;
					const { aspectRatio } = draft;
					let { translation } = e.detail;

					// limit to aspect ratio
					if (aspectRatio) {
						const t = Math.abs(translation.x) * aspectRatio;
						translation.x = translation.x;
						translation.y = t * Math.sign(translation.y);
					}

					// position
					const pointerPosition = vectorCreate(interactionOrigin.x + translation.x, interactionOrigin.y + translation.y);

					const mappedOriginPosition = mapEditorPointToImagePoint(interactionOrigin);
					const mappedPointerPosition = mapEditorPointToImagePoint(pointerPosition);

					const pivot = {
						x: mappedOriginPosition.x + (mappedPointerPosition.x - mappedOriginPosition.x) * 0.5,
						y: mappedOriginPosition.y + (mappedPointerPosition.y - mappedOriginPosition.y) * 0.5
					};

					vectorRotate(mappedOriginPosition, rotation, pivot);
					vectorRotate(mappedPointerPosition, rotation, pivot);
					const l = Math.min(mappedOriginPosition.x, mappedPointerPosition.x);
					const t = Math.min(mappedOriginPosition.y, mappedPointerPosition.y);
					const r = Math.max(mappedOriginPosition.x, mappedPointerPosition.x);
					const b = Math.max(mappedOriginPosition.y, mappedPointerPosition.y);
					let width = r - l;
					let height = b - t;
					let props = {};

					if (isEllipse) {
						props.x = l + width * 0.5;
						props.y = t + height * 0.5;
						props.rx = width * 0.5;
						props.ry = height * 0.5;
					} else {
						props.x = l;
						props.y = t;
						props.width = width;
						props.height = height;
					}

					shapeUpdateProps(draft, props, parentRect);
					syncShapes();
				},
				release: e => {
					e.detail.preventInertia();
				},
				end: e => {
					if (e.detail.isTap) return discardMarkupItemDraft();

					// get draft
					const draft = getMarkupItemDraft();

					draft.opacity = 1;

					// finish markup
					if (!shapeIsText(draft)) {
						confirmMarkupItemDraft();
						onaddshape();
					}

					// select the draft
					selectShape(draft);

					// also enable editing the text
					if (shapeIsText(draft)) editMarkupItem(draft);
				}
			};
		} else if (shapeIsLine(shapeDefault)) {
			return {
				start: e => {
					const { origin } = e.detail;
					const originMapped = mapEditorPointToImagePoint(origin);
					const originSnapped = vectorApply(originMapped, snapToPixel);
					interactionOrigin = vectorClone(origin);

					// add to markup so it's drawn
					addMarkupItemDraft({
						...shapeDefault,
						x1: isRelative
						? toPercentage(originSnapped.x, parentRect.width)
						: originSnapped.x,
						y1: isRelative
						? toPercentage(originSnapped.y, parentRect.height)
						: originSnapped.y,
						x2: isRelative
						? toPercentage(originSnapped.x, parentRect.width)
						: originSnapped.x,
						y2: isRelative
						? toPercentage(originSnapped.y, parentRect.height)
						: originSnapped.y,
						opacity: 0
					});
				},
				update: e => {
					const draft = getMarkupItemDraft();
					const { translation } = e.detail;
					const interactionTarget = vectorAdd(vectorClone(interactionOrigin), translation);

					// shift pressed
					if ($keysPressedStored.includes(16)) {
						const length = vectorDistance(interactionOrigin, interactionTarget);
						const angle = vectorAngleBetween(interactionOrigin, interactionTarget);
						const angleSnapInterval = Math.PI / 4;
						const angleSnapped = angleSnapInterval * Math.round(angle / angleSnapInterval);
						interactionTarget.x = interactionOrigin.x + length * Math.cos(angleSnapped);
						interactionTarget.y = interactionOrigin.y + length * Math.sin(angleSnapped);
					}

					// update line end position
					const point = mapEditorPointToImagePoint(interactionTarget);

					updateMarkupShape(draft, {
						x2: isRelative
						? toPercentage(point.x, parentRect.width)
						: point.x,
						y2: isRelative
						? toPercentage(point.y, parentRect.height)
						: point.y,
						opacity: 1
					});

					syncShapes();
				},
				release: e => e.detail.preventInertia(),
				end: e => {
					if (e.detail.isTap) return discardMarkupItemDraft();

					// get draft
					const draft = getMarkupItemDraft();

					draft.opacity = 1;

					// finish markup
					confirmMarkupItemDraft();

					// select the draft
					selectShape(draft);
				}
			};
		}
	};

	const eraseShape = () => {
		let origin;
		let positionPrevious;

		return {
			start: e => {
				origin = e.detail.origin;
				positionPrevious = origin;
			},
			update: e => {
				const { translation } = e.detail;
				const positionCurrent = vectorCreate(origin.x + translation.x, origin.y + translation.y);
				const shapesFound = getMarkupBetweenPoints(mapEditorPointToImagePoint(positionPrevious), mapEditorPointToImagePoint(positionCurrent));
				removeMarkupItems(shapesFound);
				positionPrevious = vectorClone(positionCurrent);
			},
			release: e => e.detail.preventInertia(),
			end: () => {
				onremoveshape();
			}
		};
	};

	//
	// Mapping coordinates
	//
	const getImagePointWithScreenTranslation = (point, translation) => {
		const pointInScreenSpace = mapImagePointToEditorPoint(point);
		return mapEditorPointToImagePoint(vectorAdd(pointInScreenSpace, translation));
	};

	//
	// Interaction
	//
	// image zoom
	const handleWheel = e => {
		
	}; // TODO: ZOOM image

	// markup manipulate
	const translateShape = (shapeCurrent, shapeOriginComputed, translation) => {
		if (shapeIsLine(shapeCurrent)) {
			const beginImageOffset = getImagePointWithScreenTranslation(shapeLineGetStartPoint(shapeOriginComputed), translation);
			const endImageOffset = getImagePointWithScreenTranslation(shapeLineGetEndPoint(shapeOriginComputed), translation);

			shapeUpdateProps(
				shapeCurrent,
				{
					x1: beginImageOffset.x,
					y1: beginImageOffset.y,
					x2: endImageOffset.x,
					y2: endImageOffset.y
				},
				parentRect
			);
		} else if (shapeIsRect(shapeCurrent) || shapeIsText(shapeCurrent) || shapeIsEllipse(shapeCurrent)) {
			const imagePoint = getImagePointWithScreenTranslation(shapeOriginComputed, translation);
			shapeUpdateProps(shapeCurrent, imagePoint, parentRect);
		}

		syncShapes();
	};

	const resizeMarkup = (shapeCurrent, shapeOriginComputed, indexes, translation, options) => {
		if (shapeIsLine(shapeCurrent)) {
			const [targetIndex] = indexes;

			const snap = $keysPressedStored.includes(16)
			? (origin, target) => {
					const length = vectorDistance(origin, target);
					const angle = vectorAngleBetween(origin, target);
					const angleSnapInterval = Math.PI / 4;
					const angleSnapped = angleSnapInterval * Math.round(angle / angleSnapInterval) - rotation % angleSnapInterval;
					target.x = origin.x + length * Math.cos(angleSnapped);
					target.y = origin.y + length * Math.sin(angleSnapped);
				}
			: (origin, target) => target;

			if (targetIndex === 0) {
				const point = getImagePointWithScreenTranslation(shapeLineGetStartPoint(shapeOriginComputed), translation);
				snap(vectorCreate(shapeOriginComputed.x2, shapeOriginComputed.y2), point);
				shapeUpdateProps(shapeCurrent, { x1: point.x, y1: point.y }, parentRect);
			} else if (targetIndex === 1) {
				const point = getImagePointWithScreenTranslation(shapeLineGetEndPoint(shapeOriginComputed), translation);
				snap(vectorCreate(shapeOriginComputed.x1, shapeOriginComputed.y1), point);
				shapeUpdateProps(shapeCurrent, { x2: point.x, y2: point.y }, parentRect);
			}
		} else if (shapeHasSize(shapeCurrent) || shapeIsEllipse(shapeCurrent)) {
			let shapeOriginRect = shapeIsEllipse(shapeOriginComputed)
			? rectCreateFromEllipse(shapeOriginComputed)
			: rectCreateFromAny(shapeOriginComputed);

			let shapeAspectRatio;

			if (shapeCurrent.aspectRatio) {
				shapeAspectRatio = shapeCurrent.aspectRatio;
			} else if (options.shiftKey) {
				shapeAspectRatio = shapeOriginRect.width / shapeOriginRect.height;
			}

			// current shape
			const rectAligned = rectCreateFromAny(shapeOriginRect);

			const rectAlignedCenterPosition = rectCenter(rectAligned);
			const rectRotation = shapeCurrent.rotation;
			const rectAlignedCorners = rectGetCorners(rectAligned);
			const rectCorners = rectRotate(rectAligned, rectRotation);

			// is translating one corner
			if (indexes.length === 1) {
				// corner
				let cornerIndex = indexes[0];
				const [tl, tr, br, bl] = rectAlignedCorners;
				const screenTargetPosition = mapImagePointToEditorPoint(rectCorners[cornerIndex]);
				vectorAdd(screenTargetPosition, translation);
				const imageTargetPosition = mapEditorPointToImagePoint(screenTargetPosition);
				const imageTargetTranslation = vectorCreate(imageTargetPosition.x - rectCorners[cornerIndex].x, imageTargetPosition.y - rectCorners[cornerIndex].y);
				if (shapeCurrent.flipX) imageTargetTranslation.x = -imageTargetTranslation.x;
				if (shapeCurrent.flipY) imageTargetTranslation.y = -imageTargetTranslation.y;
				const imageTargetTranslationAligned = vectorRotate(vectorClone(imageTargetTranslation), -rectRotation);
				const imageTargetPositionAligned = vectorCreate(rectAlignedCorners[cornerIndex].x + imageTargetTranslationAligned.x, rectAlignedCorners[cornerIndex].y + imageTargetTranslationAligned.y);
				let anchor;
				if (cornerIndex === 0) anchor = br;
				if (cornerIndex === 1) anchor = bl;
				if (cornerIndex === 2) anchor = tl;
				if (cornerIndex === 3) anchor = tr;

				// create an aligned and updated rectangle
				const rectAlignedResized = rectCreateFromPoints(anchor, imageTargetPositionAligned);

				// limit rect
				if (shapeAspectRatio) {
					// get size that adheres to aspect ratio but still fits current rectangle
					const { width, height } = rectContainRect(rectAlignedResized, shapeAspectRatio);

					const [t, r, b, l] = rectToBounds(rectAlignedResized);

					// update size
					rectAlignedResized.width = width;

					rectAlignedResized.height = height;

					// align to anchor
					if (imageTargetPositionAligned.y < anchor.y) {
						rectAlignedResized.y = b - height;
					}

					if (imageTargetPositionAligned.x < anchor.x) {
						rectAlignedResized.x = r - width;
					}
				}

				// rotate the aligned rectangle around it's original pivot point
				const rr = rectRotate(rectAlignedResized, rectRotation, rectAlignedCenterPosition);

				// now calculate the new center and then rotate the tl and br corners around that center to find their new positions
				const rrc = vectorCenter(rr);

				const p1 = vectorRotate(rr[0], -rectRotation, rrc);
				const p2 = vectorRotate(rr[2], -rectRotation, rrc);

				if (shapeCurrent.flipX || shapeCurrent.flipY) {
					vectorsFlip([p1, p2], shapeCurrent.flipX, shapeCurrent.flipY, rectAlignedCenterPosition.x, rectAlignedCenterPosition.y);
				}

				const rectUpdated = rectCreateFromPoints(p1, p2);

				shapeUpdateProps(
					shapeCurrent,
					shapeIsEllipse(shapeCurrent)
					? ellipseCreateFromRect(rectUpdated)
					: rectUpdated,
					parentRect
				);
			} else // is translating two corner points
			{
				// 0-1 -> 2-3
				// 1-2 -> 3-0
				// 2-3 -> 0-1
				// 3-0 -> 1-2
				const [cornerA, cornerB] = indexes.map(index => rectCorners[index]);

				const mid = {
					x: cornerA.x + (cornerB.x - cornerA.x) * 0.5,
					y: cornerA.y + (cornerB.y - cornerA.y) * 0.5
				};

				const [cornerAlignedA, cornerAlignedB] = indexes.map(index => rectAlignedCorners[index]);

				const [cornerAlignedC, cornerAlignedD] = indexes.map(index => {
					const mappedIndex = index + 2;
					if (mappedIndex < 4) return rectAlignedCorners[mappedIndex];
					return rectAlignedCorners[mappedIndex - 4];
				});

				const midAligned = {
					x: cornerAlignedA.x + (cornerAlignedB.x - cornerAlignedA.x) * 0.5,
					y: cornerAlignedA.y + (cornerAlignedB.y - cornerAlignedA.y) * 0.5
				};

				const midAnchorAligned = {
					x: cornerAlignedC.x + (cornerAlignedD.x - cornerAlignedC.x) * 0.5,
					y: cornerAlignedC.y + (cornerAlignedD.y - cornerAlignedC.y) * 0.5
				};

				const screenTargetPosition = mapImagePointToEditorPoint(mid);
				vectorAdd(screenTargetPosition, translation);
				const imageTargetPosition = mapEditorPointToImagePoint(screenTargetPosition);
				const imageTargetTranslation = vectorCreate(imageTargetPosition.x - mid.x, imageTargetPosition.y - mid.y);
				if (shapeCurrent.flipX) imageTargetTranslation.x = -imageTargetTranslation.x;
				if (shapeCurrent.flipY) imageTargetTranslation.y = -imageTargetTranslation.y;
				const imageTargetTranslationAligned = vectorRotate(vectorClone(imageTargetTranslation), -rectRotation);
				const imageTargetPositionAligned = vectorCreate(midAligned.x + imageTargetTranslationAligned.x, midAligned.y + imageTargetTranslationAligned.y);
				const d = vectorSubtract(vectorClone(cornerAlignedA), cornerAlignedB);
				const f = vectorApply(d, v => 1 - Math.abs(Math.sign(v)));
				const t = vectorCreate(imageTargetTranslationAligned.x * f.x, imageTargetTranslationAligned.y * f.y);
				vectorAdd(cornerAlignedA, t);
				vectorAdd(cornerAlignedB, t);
				const rectAlignedResized = rectCreateFromPoints(rectAlignedCorners);

				// limit rect
				if (shapeAspectRatio) {
					let width = rectAlignedResized.width;
					let height = rectAlignedResized.height;

					if (f.y === 0) {
						height = width / shapeAspectRatio;
					} else {
						width = height * shapeAspectRatio;
					}

					// update size
					rectAlignedResized.width = width;

					rectAlignedResized.height = height;

					// align to anchor
					if (f.y === 0) {
						rectAlignedResized.y = midAnchorAligned.y - height * 0.5;
					} else {
						rectAlignedResized.x = midAnchorAligned.x - width * 0.5;
					}
				}

				// rotate the aligned rectangle around it's original pivot point
				const rr = rectRotate(rectAlignedResized, rectRotation, rectAlignedCenterPosition);

				// now calculate the new center and then rotate the tl and br corners around that center to find their new positions
				const rrc = vectorCenter(rr);

				const p1 = vectorRotate(rr[0], -rectRotation, rrc);
				const p2 = vectorRotate(rr[2], -rectRotation, rrc);

				if (shapeCurrent.flipX || shapeCurrent.flipY) {
					vectorsFlip([p1, p2], shapeCurrent.flipX, shapeCurrent.flipY, rectAlignedCenterPosition.x, rectAlignedCenterPosition.y);
				}

				const rectUpdated = rectCreateFromPoints(p1, p2);

				shapeUpdateProps(
					shapeCurrent,
					shapeIsEllipse(shapeCurrent)
					? ellipseCreateFromRect(rectUpdated)
					: rectUpdated,
					parentRect
				);
			}
		}

		syncShapes();
	};

	let rotatorInitialPosition;

	const rotateMarkup = (shapeCurrent, shapeOriginComputed, translation, options) => {
		// calculate angle between translated rotation control and origin
		const shapeRect = getMarkupShapeRect(shapeComputeDisplay(shapeDeepCopy(shapeCurrent), parentRect));

		const shapeCenter = rectCenter(shapeRect);
		const p = getImagePointWithScreenTranslation(rotatorInitialPosition, translation);
		let shapeRotation = vectorAngleBetween(p, shapeCenter) + Math.PI / 2;

		if (options.shiftKey) {
			const rotatorSnapInterval = Math.PI / 16;
			shapeRotation = rotatorSnapInterval * Math.round(shapeRotation / rotatorSnapInterval) - rotation % rotatorSnapInterval;
		}

		shapeUpdateProps(shapeCurrent, { rotation: shapeRotation }, parentRect);
		syncShapes();
	};

	const getMarkupItemDraft = () => {
		if (!markup.length) return;
		const lastMarkupItem = markup[markup.length - 1];
		return lastMarkupItem.isDraft ? lastMarkupItem : undefined;
	};

	const addMarkupItemDraft = (shape, sync = true) => {
		if (getMarkupItemDraft()) return;
		shape = Object.assign(shape, { isDraft: true });
		return addShape(shape, sync);
	};

	const confirmMarkupItemDraft = () => {
		const markupItem = getMarkupItemDraft();
		if (!markupItem) return;
		updateMarkupShapeProperty(markupItem, "isDraft", false);
		return markupItem;
	};

	const discardMarkupItemDraft = () => {
		if (!getMarkupItemDraft()) return;
		$$invalidate(0, markup = markup.slice(0, -1));
	};

	const createMarkupItem = (props = {}) => ({ id: getUniqueId(), ...props });
	const syncShapes = () => $$invalidate(0, markup);

	const addShape = (shape, sync = true) => {
		markup.push(shape);

		// sync markup array
		if (sync) syncShapes();

		return shape;
	};

	const updateMarkupShape = (shape, props, sync = true) => {
		// update markup
		shape = Object.assign(shape, props);

		// sync markup array
		if (!sync) return;

		syncShapes();
	};

	const updateMarkupShapeProperty = (shape, name, value, sync = true) => {
		shape[name] = value;

		// sync markup array
		if (!sync) return;

		syncShapes();
	};

	const updateMarkupItemsShapeProperty = (name, value, sync = true) => {
		markup.forEach(shape => updateMarkupShapeProperty(shape, name, value, false));

		// sync markup array
		if (!sync) return;

		syncShapes();
	};

	const updateMarkupShapeItems = (props, sync = true) => {
		markup.forEach(markupItem => updateMarkupShape(markupItem, props, false));

		// sync markup array
		if (!sync) return;

		syncShapes();
	};

	const getActiveMarkupItem = () => [...markup].reverse().find(shapeIsSelected);
	const hasActiveMarkupItem = () => !!getActiveMarkupItem();

	const removeShape = (shapeToRemove, sync = true) => {
		const markupWithoutShape = markup.filter(shape => shape !== shapeToRemove);
		if (!sync) return;
		$$invalidate(0, markup = markupWithoutShape);
	};

	const removeActiveMarkupItem = () => {
		const activeShape = getActiveMarkupItem();
		if (!activeShape) return;

		// get removable markup items, 
		const removableShapes = markup.filter(shape => shapeCanRemove(shape) && shapeCanSelect(shape));

		// find index of active shape so we can select the next active shape on removal
		const index = removableShapes.findIndex(shape => shape === activeShape);

		// remove the active ship from the shape array
		removeShape(activeShape);

		// if no more markup items, release active shape
		if (removableShapes.length - 1 === 0) return blurShapes();

		// select next active markup item
		const indexNext = index - 1 < 0 ? removableShapes.length - 1 : index - 1;

		selectShape(removableShapes[indexNext]);
	};

	const blurShapes = () => updateMarkupShapeItems({ isSelected: false, isEditing: false });

	const selectShape = (shape, sync = true) => {
		// can't select draft
		if (shape.isDraft) return;

		// remove selected state from other markup
		blurShapes();

		// select
		shapeSelect(shape);

		// sync
		if (!sync) return;

		syncShapes();
	};

	const deselectMarkupItem = markupItem => {
		updateMarkupShape(markupItem, { isSelected: false, isEditing: false });
	};

	const editMarkupItem = markupItem => {
		updateMarkupShape(markupItem, { isSelected: true, isEditing: true });
	};

	const finishEditMarkupItem = markupItem => {
		updateMarkupShape(markupItem, { isSelected: true, isEditing: false });
	};

	const removeMarkupItems = shapesToRemove => {
		$$invalidate(0, markup = markup.filter(shape => !shapesToRemove.includes(shape)));
	};

	const getMarkupShapeRect = shape => {
		// has own rect
		if (shapeHasSize(shape)) return rectCreateFromAny(shape);

		if (shapeIsEllipse(shape)) return rectCreateFromEllipse(shape);

		// text
		const uid = shapeTextUID({
			...shape,
			// will not affect 
			textAlign: 0
		});

		let size = TextSizeCache.get(uid);
		if (size) return rectCreate(shape.x, shape.y, size.width, size.height);

		// assume it's auto text for now
		const measure = h("span", {
			style: `visibility:hidden;pointer-events:none;white-space:pre;position:absolute;left:0;top:0;font-size:${shape.fontSize}px;font-family:${shape.fontFamily}`
		});

		measure.textContent = shape.text;
		document.body.appendChild(measure);

		const height = isFunction(shape.lineHeight)
		? shape.lineHeight(shape.fontSize)
		: shape.lineHeight;

		size = {
			width: Math.max(MIN_TEXT_MARKUP_WIDTH, measure.offsetWidth),
			height: height * shape.text.split("\n").length
		};

		measure.parentNode.removeChild(measure);

		// done!
		TextSizeCache.set(uid, size);

		return rectCreate(shape.x, shape.y, size.width, size.height);
	};

	const getMarkupItemsAtPosition = position => [...markup].// reverse the array
	reverse().// can't select paths
	filter(shapeCanSelect).// find markup near pointer
	filter(shape => {
		// we need to know where the shape will end up
		const computedShape = shapeComputeDisplay(shapeDeepCopy(shape), parentRect);

		// test if clicked in rect
		if (shapeIsRect(computedShape)) {
			// const rect = getMarkupShapeRect(shape);
			return isPositionInRect(position, computedShape, shape.rotation);
		} else if (shapeIsText(computedShape)) {
			const rect = getMarkupShapeRect(computedShape);
			return isPositionInRect(position, rect, shape.rotation);
		} else // test if is ellipse
		if (shapeIsEllipse(computedShape)) {
			return isPositionInEllipse(position, computedShape, shape.rotation);
		} else // test if clicked on line
		if (shapeIsLine(computedShape)) {
			// radius around click position, test if line intersects
			return isPositionNearLine(position, shapeLineGetStartPoint(computedShape), shapeLineGetEndPoint(computedShape), 16);
		}

		return false;
	});

	const getMarkupBetweenPoints = (a, b) => {
		// eraseLine should be in image space
		const eraseLine = lineCreate(a, b);

		// loop over shapes and find intersecting shapes
		return markup.filter(shapeCanErase).filter(shape => !!linePointsIntersection(eraseLine, getMarkupShapePoints(shapeComputeDisplay(shapeDeepCopy(shape), parentRect))));
	};

	//
	// Generic interaction
	//
	let interactionTimer = undefined;

	let interactionTarget = undefined;
	let interactionShape = undefined;
	let interactionShapeOriginComputed = undefined;
	let isInteracting = false;

	const handleInteractionStart = e => {
		const { origin } = e.detail;
		interactionShape = undefined;
		interactionShapeOriginComputed = undefined;
		interactionTarget = undefined;
		clearTimeout(interactionTimer);
		interactionTimer = setTimeout(() => $$invalidate(102, isInteracting = true), 250);

		// test if target is a shape, if not, run interaction handler
		const point = mapEditorPointToImagePoint(vectorClone(origin));

		const targettedMarkupItem = getMarkupItemsAtPosition(point).shift();

		// check if markup was targetted
		if (targettedMarkupItem && shapeIsSelected(targettedMarkupItem)) {
			// clone shape we're interacting with so we can update it properly
			interactionShape = targettedMarkupItem;

			interactionShapeOriginComputed = shapeComputeDisplay(shapeDeepCopy(interactionShape), parentRect);
			return;
		}

		// set target
		interactionTarget = targettedMarkupItem;

		// started interacting
		oninteractionstart(e);
	};

	const handleInteractionUpdate = e => {
		// is interacting with shape
		if (interactionShape) {
			if (!shapeCanMove(interactionShape)) return;
			return translateShape(interactionShape, interactionShapeOriginComputed, e.detail.translation);
		}

		oninteractionupdate(e);
	};

	const handleInteractionRelease = e => {
		clearTimeout(interactionTimer);
		$$invalidate(102, isInteracting = false);

		// test if is text and if we double tapped, if so, switch to text edit mode
		if (interactionShape) {
			if (e.detail.isDoubleTap && shapeIsText(interactionShape) && shapeCanInput(interactionShape) !== false) {
				editMarkupItem(interactionShape);
			}

			return;
		}

		oninteractionrelease(e);
	};

	const handleInteractionEnd = e => {
		// shape remains active so user can resize, rotate
		if (interactionShape) {
			onupdateshape();
			interactionShape = undefined;
			return;
		}

		blurShapes();
		oninteractionend(e);
		if (interactionTarget && e.detail.isTap) selectShape(interactionTarget);
	};

	//
	// Shape manipulation
	//
	const getMarkupShapePoints = shape => {
		let center;
		let points;

		if (shapeIsRect(shape)) {
			center = rectCenter(shape);
			points = rectGetCorners(shape);
			if (shape.flipX || shape.flipY) vectorsFlip(points, shape.flipX, shape.flipY, center.x, center.y);
			points = vectorsRotate(points, shape.rotation, center.x, center.y);
		} else if (shapeIsEllipse(shape)) {
			center = shape;
			points = rectGetCorners(rectCreateFromEllipse(shape));
			if (shape.flipX || shape.flipY) vectorsFlip(points, shape.flipX, shape.flipY, center.x, center.y);
			points = vectorsRotate(points, shape.rotation, center.x, center.y);
		} else if (shapeIsLine(shape)) {
			points = [shapeLineGetStartPoint(shape), shapeLineGetEndPoint(shape)];
			center = vectorCenter(points);
		} else if (shapeIsPath(shape)) {
			points = [...shape.points];
			center = vectorCenter(points);
		} else if (shapeIsText(shape)) {
			const rect = getMarkupShapeRect(shape);
			rect.width = Math.max(MIN_TEXT_MARKUP_WIDTH, rect.width);
			center = rectCenter(rect);
			points = rectGetCorners(rect);
			if (shape.flipX || shape.flipY) vectorsFlip(points, shape.flipX, shape.flipY, center.x, center.y);
			points = vectorsRotate(points, shape.rotation, center.x, center.y);
		}

		return points;
	};

	const getShapeRotationPoint = shape => {
		const points = getMarkupShapePoints(shape);
		let origin;
		let dir;

		if (shape.flipY) {
			origin = vectorCenter([points[0], points[1]]);
			dir = vectorNormalize(vectorCreate(points[1].x - points[2].x, points[1].y - points[2].y));
		} else {
			origin = vectorCenter([points[2], points[3]]);
			dir = vectorNormalize(vectorCreate(points[2].x - points[1].x, points[2].y - points[1].y));
		}

		vectorMultiply(dir, ROTATION_CONTROL_OFFSET / scale);
		return { origin, dir };
	};

	const getShapeRotationPointOnScreen = shape => {
		const markupPoint = getShapeRotationPoint(shape);

		const screenPosition = mapImagePointToEditorPoint({
			x: markupPoint.origin.x + markupPoint.dir.x,
			y: markupPoint.origin.y + markupPoint.dir.y
		});

		const screenOrigin = mapImagePointToEditorPoint(markupPoint.origin);

		return {
			origin: screenOrigin,
			position: screenPosition
		};
	};

	// draw shape manipulator edges
	// let shapeManipulatorOpacity = 0;
	let shapeManipulatorUid = `markup-manipulator-segment`;

	onDestroy(() => {
		// clean up UI elements created by editor
		$$invalidate(52, ui = ui.filter(guide => guide.id !== shapeManipulatorUid));
	});

	const handleManipulatorResizeGrab = e => {
		$$invalidate(102, isInteracting = true);
		interactionShape = activeMarkup;
		interactionShapeOriginComputed = activeMarkupComputed;
	};

	const handleManipulatorResizeDrag = e => {
		const { translation, indexes, shiftKey } = e.detail;
		resizeMarkup(interactionShape, interactionShapeOriginComputed, indexes, translation, { shiftKey });
	};

	const handleManipulatorResizeEnd = e => {
		selectShape(interactionShape);
		interactionShape = undefined;
		$$invalidate(102, isInteracting = false);
		onupdateshape();
	};

	const handleManipulatorRotateGrab = e => {
		rotatorInitialPosition = getShapeRotationPoint(activeMarkupComputed).origin;
		$$invalidate(102, isInteracting = true);
		interactionShape = activeMarkup;
		interactionShapeOriginComputed = activeMarkupComputed;
	};

	const handleManipulatorRotateDrag = e => {
		const { translation, shiftKey } = e.detail;
		rotateMarkup(interactionShape, interactionShapeOriginComputed, translation, { shiftKey });
	};

	const handleManipulatorRotateEnd = () => {
		selectShape(interactionShape);
		interactionShape = undefined;
		$$invalidate(102, isInteracting = false);
		onupdateshape();
	};

	//
	// Keyboard
	//
	const handleKey = e => {
		// only handle key input if a shape has been selected
		if (!hasActiveMarkupItem()) return;

		// get key type
		const { key } = e;

		// if is escape deselect active item
		if ((/escape/i).test(key)) {
			return deselectMarkupItem(activeMarkup);
		}

		// if is tab, select next markup item
		/*
if (/arrow/i.test(key)) {
    const success = e.shiftKey 
        ? selectPreviousMarkupItem(activeMarkup)
        : selectNextMarkupItem(activeMarkup);
    
    if (success) {
        e.preventDefault();
        e.stopPropagation();
    }
}
*/
		// if is remove active
		if ((/backspace/i).test(key) && !(/input|textarea/i).test(e.target.nodeName)) {
			// prevent back navigation on Firefox
			e.preventDefault();

			// remove the item
			removeActiveMarkupItem();

			onremoveshape();
		}
	};

	const markupTextPanelOpacity = spring(0);
	component_subscribe($$self, markupTextPanelOpacity, value => $$invalidate(115, $markupTextPanelOpacity = value));
	let textInput;

	const handleEditTextActiveMarkup = () => {
		editMarkupItem(activeMarkup);
	};

	const handleTextInputAttempt = e => {
		const currentValue = e.target.value;
		const selectionStart = e.target.selectionStart;
		const selectionEnd = e.target.selectionEnd;
		const currentValueStart = currentValue.substring(0, selectionStart);
		const currentValueEnd = currentValue.substring(selectionEnd);
		const intendedValue = currentValueStart + e.key + currentValueEnd;
		const filteredValue = shapeCanInput(activeMarkup, intendedValue);
		if (filteredValue !== intendedValue) e.preventDefault();
	};

	const handleTextInputKeyUp = e => {
		// get key type
		const { key, ctrlKey, altKey } = e;

		// exit single line text elements
		if ((/enter/i).test(key) && shapeIsTextLine(activeMarkup)) return handleTextConfirm();

		// confirm multi line text elements
		if ((/enter/i).test(key) && ctrlKey | altKey) return handleTextConfirm();

		// exit and cancel editing text
		if ((/escape/i).test(key)) return handleTextCancel();
	};

	const handleTextConfirm = () => {
		let wasDraft = activeMarkup.isDraft;
		if (activeMarkup.isDraft) confirmMarkupItemDraft();

		// result of shape can input test, can also be a regex
		const canInput = shapeCanInput(activeMarkup, textInput.value);

		// update the shape with the filtered value
		updateMarkupShape(activeMarkup, {
			text: canInput === true ? textInput.value : canInput
		});

		finishEditMarkupItem(activeMarkup);
		if (wasDraft) onaddshape(); else onupdateshape();
	};

	const handleTextCancel = () => {
		if (activeMarkup.isDraft) discardMarkupItemDraft();
		finishEditMarkupItem(activeMarkup);
	};

	//
	// Controls
	//
	const handleFlipX = e => {
		e.stopPropagation();
		const flipX = activeMarkup.flipX || false;
		updateMarkupShapeProperty(activeMarkup, "flipX", !flipX);
		onupdateshape();
	};

	const handleFlipY = e => {
		e.stopPropagation();
		const flipY = activeMarkup.flipY || false;
		updateMarkupShapeProperty(activeMarkup, "flipY", !flipY);
		onupdateshape();
	};

	const handleRemoveActiveMarkup = e => {
		e.stopPropagation();
		e.target.blur(); // cancels focus of remove button
		removeActiveMarkupItem();
		onremoveshape();
	};

	const handleMoveToFrontActiveMarkup = e => {
		e.stopPropagation();

		// test if is not already at top of stack, if so, exit
		const index = markup.findIndex(shape => shape === activeMarkup);

		if (index === markup.length - 1) return;

		// add to last index
		$$invalidate(0, markup = markup.filter(markupItem => markupItem !== activeMarkup).concat([activeMarkup]));

		onupdateshape();
	};

	const handleDuplicateActiveMarkup = e => {
		e.stopPropagation();

		// create clone
		const clone = shapeDeepCopy(activeMarkup);

		clone.id = undefined;
		const duplicationOffset = vectorCreate(50, -50);

		// offset
		if (shapeIsLine(clone)) {
			const linePosition = shapeGetPropsPixelValues(clone, ["x1", "y1", "x2", "y2"], parentRect);
			linePosition.x1 += duplicationOffset.x;
			linePosition.y1 += duplicationOffset.y;
			linePosition.x2 += duplicationOffset.x;
			linePosition.y2 += duplicationOffset.y;
			shapeUpdateProps(clone, linePosition, parentRect);
		} else {
			const currentPosition = shapeGetPropsPixelValues(clone, ["x", "y"], parentRect);
			currentPosition.x += 50;
			currentPosition.y -= 50;
			shapeUpdateProps(clone, currentPosition, parentRect);
		}

		// add clone
		markup.push(clone);

		// select clone
		selectShape(clone);

		onaddshape();
	};

	const getMarkupControlsAnchorPosition = rect => vectorApply(vectorCreate(rect.x + rect.width * 0.5, rect.y), snapToPixel);

	//
	// show & position markup controls panel
	//
	const markupControlsOpacity = spring(0, { stiffness: 0.2, damping: 0.7 });

	component_subscribe($$self, markupControlsOpacity, value => $$invalidate(22, $markupControlsOpacity = value));

	// handling keyboard focus
	// const handleFocusEditor = (e) => {
	//     const shape = markup[0];
	//     if (!shape) return;
	//     // select first shape but only if navigating by keyboard
	//     if ('focusVisible' in e.target.dataset) {
	//         selectShape(shape)
	//     }
	// }
	const handleNudge = e => {
		const shape = getActiveMarkupItem();
		if (!shape) return;
		interactionShape = shape;
		interactionShapeOriginComputed = shapeComputeDisplay(shapeDeepCopy(interactionShape), parentRect);
		translateShape(interactionShape, interactionShapeOriginComputed, e.detail);
	};

	// shape navigator
	let showShapeList = false;

	const handleFocusIn = e => {
		$$invalidate(6, showShapeList = true);
	};

	const handleFocusOut = ({ relatedTarget }) => {
		// still in list
		if (relatedTarget.classList.contains("shape-selector__button")) return;

		$$invalidate(6, showShapeList = false);
	};

	function measure_handler(event) {
		bubble($$self, event);
	}

	const click_handler = (index, e) => selectShape(markup[index]);

	function textarea_binding($$value) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			textInput = $$value;
			$$invalidate(5, textInput);
		});
	}

	const interactable_function = e => getEventPositionInEditor(e, rootRect);

	$$self.$$set = $$props => {
		if ("markup" in $$props) $$invalidate(0, markup = $$props.markup);
		if ("offset" in $$props) $$invalidate(53, offset = $$props.offset);
		if ("rotation" in $$props) $$invalidate(54, rotation = $$props.rotation);
		if ("scale" in $$props) $$invalidate(55, scale = $$props.scale);
		if ("ui" in $$props) $$invalidate(52, ui = $$props.ui);
		if ("opacity" in $$props) $$invalidate(56, opacity = $$props.opacity);
		if ("parentRect" in $$props) $$invalidate(57, parentRect = $$props.parentRect);
		if ("rootRect" in $$props) $$invalidate(1, rootRect = $$props.rootRect);
		if ("stageRect" in $$props) $$invalidate(58, stageRect = $$props.stageRect);
		if ("oninteractionstart" in $$props) $$invalidate(59, oninteractionstart = $$props.oninteractionstart);
		if ("oninteractionupdate" in $$props) $$invalidate(60, oninteractionupdate = $$props.oninteractionupdate);
		if ("oninteractionrelease" in $$props) $$invalidate(61, oninteractionrelease = $$props.oninteractionrelease);
		if ("oninteractionend" in $$props) $$invalidate(62, oninteractionend = $$props.oninteractionend);
		if ("onaddshape" in $$props) $$invalidate(63, onaddshape = $$props.onaddshape);
		if ("onupdateshape" in $$props) $$invalidate(64, onupdateshape = $$props.onupdateshape);
		if ("onremoveshape" in $$props) $$invalidate(65, onremoveshape = $$props.onremoveshape);
		if ("mapEditorPointToImagePoint" in $$props) $$invalidate(66, mapEditorPointToImagePoint = $$props.mapEditorPointToImagePoint);
		if ("mapImagePointToEditorPoint" in $$props) $$invalidate(67, mapImagePointToEditorPoint = $$props.mapImagePointToEditorPoint);
		if ("enableButtonFlipVertical" in $$props) $$invalidate(2, enableButtonFlipVertical = $$props.enableButtonFlipVertical);
		if ("locale" in $$props) $$invalidate(3, locale = $$props.locale);
	};

	let activeMarkup;
	let activeMarkupComputed;
	let activeMarkupItemIsDraft;
	let shapeProps;
	let shapeActivePoints;
	let allowResizeControls;
	let allowRotateControls;
	let shouldRenderShapeManipulator;
	let shapeActiveScreenPoints;
	let shapeManipulatorPoints;
	let shapeManipulatorRotationPoint;
	let shapeManipulatorRotationPointPosition;
	let shapeOpacity;
	let isTextMarkupSelected;
	let shouldRenderTextInput;
	let textInputText;
	let markupTextPanelSize;
	let markupTextPanelOffset;
	let markupTextPanelStyle;
	let markupTextInputStyle;
	let controlledMarkupItem;
	let allowShapeFlip;
	let allowShapeDuplicate;
	let allowShapeRemove;
	let allowShapeReorder;
	let allowShapeInput;
	let markupControlsAnchorPosition;
	let markupControlsStyle;
	let shapeNavList;

	$$self.$$.update = () => {
		if ($$self.$$.dirty[0] & /*markup*/ 1) {
			 $$invalidate(104, activeMarkup = markup && (getMarkupItemDraft() || getActiveMarkupItem()));
		}

		if ($$self.$$.dirty[1] & /*parentRect*/ 67108864 | $$self.$$.dirty[3] & /*activeMarkup*/ 2048) {
			 $$invalidate(105, activeMarkupComputed = activeMarkup && shapeComputeDisplay(shapeDeepCopy(activeMarkup), parentRect));
		}

		if ($$self.$$.dirty[3] & /*activeMarkup*/ 2048) {
			 $$invalidate(106, activeMarkupItemIsDraft = !!(activeMarkup && shapeIsDraft(activeMarkup)));
		}

		if ($$self.$$.dirty[3] & /*activeMarkup*/ 2048) {
			 $$invalidate(107, shapeProps = activeMarkup || undefined);
		}

		if ($$self.$$.dirty[3] & /*activeMarkup, activeMarkupComputed*/ 6144) {
			 $$invalidate(108, shapeActivePoints = activeMarkup && !shapeIsPath(activeMarkupComputed) && getMarkupShapePoints(activeMarkupComputed) || []);
		}

		if ($$self.$$.dirty[3] & /*activeMarkup*/ 2048) {
			// $: shapeRotation = activeMarkup && activeMarkup.rotation || 0;
			 $$invalidate(7, allowResizeControls = activeMarkup && shapeCanResize(activeMarkup));
		}

		if ($$self.$$.dirty[3] & /*activeMarkup*/ 2048) {
			 $$invalidate(8, allowRotateControls = activeMarkup && shapeCanRotate(activeMarkup));
		}

		if ($$self.$$.dirty[3] & /*activeMarkup, shapeActivePoints*/ 34816) {
			 $$invalidate(9, shouldRenderShapeManipulator = activeMarkup && shapeActivePoints.length > 1);
		}

		if ($$self.$$.dirty[2] & /*mapImagePointToEditorPoint*/ 32 | $$self.$$.dirty[3] & /*shapeActivePoints*/ 32768) {
			 $$invalidate(109, shapeActiveScreenPoints = shapeActivePoints.map(mapImagePointToEditorPoint));
		}

		if ($$self.$$.dirty[1] & /*offset*/ 4194304 | $$self.$$.dirty[3] & /*shapeActiveScreenPoints*/ 65536) {
			 $$invalidate(10, shapeManipulatorPoints = shapeActiveScreenPoints.map(point => vectorCreate(point.x - offset.x, point.y - offset.y)));
		}

		if ($$self.$$.dirty[0] & /*shouldRenderShapeManipulator, allowRotateControls*/ 768 | $$self.$$.dirty[3] & /*activeMarkupComputed*/ 4096) {
			 $$invalidate(110, shapeManipulatorRotationPoint = shouldRenderShapeManipulator && allowRotateControls && getShapeRotationPointOnScreen(activeMarkupComputed));
		}

		if ($$self.$$.dirty[1] & /*offset*/ 4194304 | $$self.$$.dirty[3] & /*shapeManipulatorRotationPoint*/ 131072) {
			 $$invalidate(11, shapeManipulatorRotationPointPosition = shapeManipulatorRotationPoint && vectorCreate(shapeManipulatorRotationPoint.position.x - offset.x, shapeManipulatorRotationPoint.position.y - offset.y));
		}

		if ($$self.$$.dirty[1] & /*opacity*/ 33554432) {
			 $$invalidate(111, shapeOpacity = opacity); // 1.0; // shapeManipulatorOpacity * opacity;
		}

		if ($$self.$$.dirty[0] & /*shapeManipulatorPoints*/ 1024 | $$self.$$.dirty[1] & /*opacity, ui*/ 35651584 | $$self.$$.dirty[3] & /*shapeActiveScreenPoints, shapeOpacity, shapeManipulatorRotationPoint*/ 458752) {
			 if (opacity > 0 && shapeManipulatorPoints.length > 2) {
				// create manipulator outline segments
				const points = [...shapeActiveScreenPoints, shapeActiveScreenPoints[0]];

				const l = points.length;
				const segments = [];
				const strokeColor = [1, 1, 1, shapeOpacity];
				const strokeWidth = 1;

				for (let i = 0; i < l - 1; i++) {
					segments[i] = {
						id: shapeManipulatorUid,
						points: [points[i], points[i + 1]],
						opacity: 1,
						strokeColor,
						strokeWidth
					};
				}

				if (shapeManipulatorRotationPoint) {
					// add rotator line
					segments[segments.length] = {
						id: shapeManipulatorUid,
						opacity: 1,
						points: [
							{
								x: shapeManipulatorRotationPoint.origin.x,
								y: shapeManipulatorRotationPoint.origin.y
							},
							{
								x: shapeManipulatorRotationPoint.position.x,
								y: shapeManipulatorRotationPoint.position.y
							}
						],
						strokeColor,
						strokeWidth
					};
				}

				// remove existing segments, and set new ones
				$$invalidate(52, ui = [...ui.filter(guide => guide.id !== shapeManipulatorUid), ...segments]);
			} else {
				// remove the guides when shape manipulator is no longer visible
				$$invalidate(52, ui = ui.filter(guide => guide.id !== shapeManipulatorUid));
			}
		}

		if ($$self.$$.dirty[3] & /*activeMarkup*/ 2048) {
			//
			// Text input
			//
			 $$invalidate(112, isTextMarkupSelected = activeMarkup && shapeIsText(activeMarkup));
		}

		if ($$self.$$.dirty[3] & /*isTextMarkupSelected, activeMarkup*/ 526336) {
			 $$invalidate(12, shouldRenderTextInput = isTextMarkupSelected && shapeCanInput(activeMarkup) !== false && activeMarkup.isEditing);
		}

		if ($$self.$$.dirty[0] & /*shouldRenderTextInput*/ 4096 | $$self.$$.dirty[3] & /*activeMarkup*/ 2048) {
			 $$invalidate(13, textInputText = shouldRenderTextInput ? activeMarkup.text : "");
		}

		if ($$self.$$.dirty[0] & /*shouldRenderTextInput*/ 4096) {
			 markupTextPanelOpacity.set(shouldRenderTextInput ? 1 : 0);
		}

		if ($$self.$$.dirty[0] & /*shouldRenderTextInput, rootRect*/ 4098) {
			 $$invalidate(113, markupTextPanelSize = shouldRenderTextInput && {
				width: clamp(rootRect.width * 0.5, 280, 360),
				height: clamp(rootRect.height * 0.5, 160, 240)
			});
		}

		if ($$self.$$.dirty[0] & /*shouldRenderTextInput*/ 4096 | $$self.$$.dirty[1] & /*stageRect*/ 134217728 | $$self.$$.dirty[3] & /*markupTextPanelSize*/ 1048576) {
			 $$invalidate(114, markupTextPanelOffset = shouldRenderTextInput && {
				x: stageRect.x + (stageRect.width - stageRect.x * 2) * 0.5 - markupTextPanelSize.width * 0.5,
				y: stageRect.y + (stageRect.height - stageRect.y * 2) * 0.5 - markupTextPanelSize.height * 0.5
			});
		}

		if ($$self.$$.dirty[0] & /*shouldRenderTextInput*/ 4096 | $$self.$$.dirty[3] & /*$markupTextPanelOpacity, markupTextPanelSize, markupTextPanelOffset*/ 7340032) {
			 $$invalidate(14, markupTextPanelStyle = shouldRenderTextInput && `
    opacity: ${$markupTextPanelOpacity};
    width: ${markupTextPanelSize.width}px;
    height: ${markupTextPanelSize.height}px;
    transform: translate(${snapToPixel(markupTextPanelOffset.x)}px, ${snapToPixel(markupTextPanelOffset.y)}px);
`);
		}

		if ($$self.$$.dirty[0] & /*shouldRenderTextInput*/ 4096 | $$self.$$.dirty[3] & /*shapeProps*/ 16384) {
			 $$invalidate(15, markupTextInputStyle = shouldRenderTextInput && `
    white-space: ${isNumber(shapeProps.width) ? "auto" : "nowrap"};
    text-align: ${shapeProps.textAlign || "left"};
    font-family: ${shapeProps.fontFamily || "sans-serif"};
`);
		}

		if ($$self.$$.dirty[0] & /*textInput*/ 32) {
			 if (textInput) {
				textInput.focus();
				textInput.select();
			}
		}

		if ($$self.$$.dirty[3] & /*activeMarkup, activeMarkupItemIsDraft, controlledMarkupItem*/ 8398848) {
			 $$invalidate(116, controlledMarkupItem = activeMarkup && !activeMarkupItemIsDraft
			? activeMarkup
			: controlledMarkupItem);
		}

		if ($$self.$$.dirty[3] & /*controlledMarkupItem*/ 8388608) {
			 $$invalidate(16, allowShapeFlip = controlledMarkupItem && shapeCanFlip(controlledMarkupItem));
		}

		if ($$self.$$.dirty[3] & /*controlledMarkupItem*/ 8388608) {
			 $$invalidate(17, allowShapeDuplicate = controlledMarkupItem && shapeCanDuplicate(controlledMarkupItem));
		}

		if ($$self.$$.dirty[3] & /*controlledMarkupItem*/ 8388608) {
			 $$invalidate(18, allowShapeRemove = controlledMarkupItem && shapeCanRemove(controlledMarkupItem));
		}

		if ($$self.$$.dirty[3] & /*controlledMarkupItem*/ 8388608) {
			 $$invalidate(19, allowShapeReorder = controlledMarkupItem && shapeCanReorder(controlledMarkupItem));
		}

		if ($$self.$$.dirty[3] & /*controlledMarkupItem*/ 8388608) {
			 $$invalidate(20, allowShapeInput = controlledMarkupItem && shapeCanInput(controlledMarkupItem) !== false);
		}

		if ($$self.$$.dirty[0] & /*shouldRenderTextInput*/ 4096 | $$self.$$.dirty[3] & /*activeMarkup, activeMarkupItemIsDraft, isInteracting*/ 10752) {
			 markupControlsOpacity.set(activeMarkup && !activeMarkupItemIsDraft && !isInteracting && !shouldRenderTextInput
			? 1
			: 0);
		}

		if ($$self.$$.dirty[0] & /*shapeManipulatorPoints*/ 1024 | $$self.$$.dirty[3] & /*activeMarkup, activeMarkupItemIsDraft, markupControlsAnchorPosition*/ 16787456) {
			 $$invalidate(117, markupControlsAnchorPosition = activeMarkup && !activeMarkupItemIsDraft
			? getMarkupControlsAnchorPosition(rectCreateFromPoints(shapeManipulatorPoints))
			: markupControlsAnchorPosition);
		}

		if ($$self.$$.dirty[0] & /*$markupControlsOpacity*/ 4194304 | $$self.$$.dirty[3] & /*markupControlsAnchorPosition*/ 16777216) {
			 $$invalidate(21, markupControlsStyle = markupControlsAnchorPosition && `transform: translate(${markupControlsAnchorPosition.x}px, ${markupControlsAnchorPosition.y}px);opacity:${$markupControlsOpacity}`);
		}

		if ($$self.$$.dirty[0] & /*rootRect*/ 2) {
			// if view is resized
			 if (rootRect) {
				blurShapes();
			}
		}

		if ($$self.$$.dirty[0] & /*markup, locale*/ 9) {
			 $$invalidate(23, shapeNavList = markup.filter(shapeCanSelect).filter(shape => !shapeIsDraft(shape)).map(shape => ({
				id: shape.id,
				color: shapeIsText(shape)
				? shape.color
				: shapeIsLine(shape)
					? shape.strokeColor
					: shape.backgroundColor,
				name: shape.name || locale[`shapeLabelTool${capitalizeFirstLetter(shapeGetDescription(shape))}`]
			})));
		}
	};

	return [
		markup,
		rootRect,
		enableButtonFlipVertical,
		locale,
		selectShape,
		textInput,
		showShapeList,
		allowResizeControls,
		allowRotateControls,
		shouldRenderShapeManipulator,
		shapeManipulatorPoints,
		shapeManipulatorRotationPointPosition,
		shouldRenderTextInput,
		textInputText,
		markupTextPanelStyle,
		markupTextInputStyle,
		allowShapeFlip,
		allowShapeDuplicate,
		allowShapeRemove,
		allowShapeReorder,
		allowShapeInput,
		markupControlsStyle,
		$markupControlsOpacity,
		shapeNavList,
		keysPressedStored,
		handleWheel,
		handleInteractionStart,
		handleInteractionUpdate,
		handleInteractionRelease,
		handleInteractionEnd,
		handleManipulatorResizeGrab,
		handleManipulatorResizeDrag,
		handleManipulatorResizeEnd,
		handleManipulatorRotateGrab,
		handleManipulatorRotateDrag,
		handleManipulatorRotateEnd,
		handleKey,
		markupTextPanelOpacity,
		handleEditTextActiveMarkup,
		handleTextInputAttempt,
		handleTextInputKeyUp,
		handleTextConfirm,
		handleTextCancel,
		handleFlipX,
		handleFlipY,
		handleRemoveActiveMarkup,
		handleMoveToFrontActiveMarkup,
		handleDuplicateActiveMarkup,
		markupControlsOpacity,
		handleNudge,
		handleFocusIn,
		handleFocusOut,
		ui,
		offset,
		rotation,
		scale,
		opacity,
		parentRect,
		stageRect,
		oninteractionstart,
		oninteractionupdate,
		oninteractionrelease,
		oninteractionend,
		onaddshape,
		onupdateshape,
		onremoveshape,
		mapEditorPointToImagePoint,
		mapImagePointToEditorPoint,
		createShape,
		eraseShape,
		getMarkupItemDraft,
		addMarkupItemDraft,
		confirmMarkupItemDraft,
		discardMarkupItemDraft,
		createMarkupItem,
		syncShapes,
		addShape,
		updateMarkupShape,
		updateMarkupShapeProperty,
		updateMarkupItemsShapeProperty,
		updateMarkupShapeItems,
		getActiveMarkupItem,
		hasActiveMarkupItem,
		removeShape,
		removeActiveMarkupItem,
		blurShapes,
		deselectMarkupItem,
		editMarkupItem,
		finishEditMarkupItem,
		removeMarkupItems,
		getMarkupShapeRect,
		getMarkupItemsAtPosition,
		getMarkupBetweenPoints,
		measure_handler,
		click_handler,
		textarea_binding,
		interactable_function
	];
}

class ShapeLayoutEditor extends SvelteComponent {
	constructor(options) {
		super();

		init(
			this,
			options,
			instance$s,
			create_fragment$s,
			safe_not_equal,
			{
				markup: 0,
				offset: 53,
				rotation: 54,
				scale: 55,
				ui: 52,
				opacity: 56,
				parentRect: 57,
				rootRect: 1,
				stageRect: 58,
				oninteractionstart: 59,
				oninteractionupdate: 60,
				oninteractionrelease: 61,
				oninteractionend: 62,
				onaddshape: 63,
				onupdateshape: 64,
				onremoveshape: 65,
				mapEditorPointToImagePoint: 66,
				mapImagePointToEditorPoint: 67,
				enableButtonFlipVertical: 2,
				locale: 3,
				createShape: 68,
				eraseShape: 69,
				getMarkupItemDraft: 70,
				addMarkupItemDraft: 71,
				confirmMarkupItemDraft: 72,
				discardMarkupItemDraft: 73,
				createMarkupItem: 74,
				syncShapes: 75,
				addShape: 76,
				updateMarkupShape: 77,
				updateMarkupShapeProperty: 78,
				updateMarkupItemsShapeProperty: 79,
				updateMarkupShapeItems: 80,
				getActiveMarkupItem: 81,
				hasActiveMarkupItem: 82,
				removeShape: 83,
				removeActiveMarkupItem: 84,
				blurShapes: 85,
				selectShape: 4,
				deselectMarkupItem: 86,
				editMarkupItem: 87,
				finishEditMarkupItem: 88,
				removeMarkupItems: 89,
				getMarkupShapeRect: 90,
				getMarkupItemsAtPosition: 91,
				getMarkupBetweenPoints: 92
			},
			[-1, -1, -1, -1, -1]
		);
	}

	get createShape() {
		return this.$$.ctx[68];
	}

	get eraseShape() {
		return this.$$.ctx[69];
	}

	get getMarkupItemDraft() {
		return this.$$.ctx[70];
	}

	get addMarkupItemDraft() {
		return this.$$.ctx[71];
	}

	get confirmMarkupItemDraft() {
		return this.$$.ctx[72];
	}

	get discardMarkupItemDraft() {
		return this.$$.ctx[73];
	}

	get createMarkupItem() {
		return this.$$.ctx[74];
	}

	get syncShapes() {
		return this.$$.ctx[75];
	}

	get addShape() {
		return this.$$.ctx[76];
	}

	get updateMarkupShape() {
		return this.$$.ctx[77];
	}

	get updateMarkupShapeProperty() {
		return this.$$.ctx[78];
	}

	get updateMarkupItemsShapeProperty() {
		return this.$$.ctx[79];
	}

	get updateMarkupShapeItems() {
		return this.$$.ctx[80];
	}

	get getActiveMarkupItem() {
		return this.$$.ctx[81];
	}

	get hasActiveMarkupItem() {
		return this.$$.ctx[82];
	}

	get removeShape() {
		return this.$$.ctx[83];
	}

	get removeActiveMarkupItem() {
		return this.$$.ctx[84];
	}

	get blurShapes() {
		return this.$$.ctx[85];
	}

	get selectShape() {
		return this.$$.ctx[4];
	}

	get deselectMarkupItem() {
		return this.$$.ctx[86];
	}

	get editMarkupItem() {
		return this.$$.ctx[87];
	}

	get finishEditMarkupItem() {
		return this.$$.ctx[88];
	}

	get removeMarkupItems() {
		return this.$$.ctx[89];
	}

	get getMarkupShapeRect() {
		return this.$$.ctx[90];
	}

	get getMarkupItemsAtPosition() {
		return this.$$.ctx[91];
	}

	get getMarkupBetweenPoints() {
		return this.$$.ctx[92];
	}
}

/* src/native/ui/components/ShapeStyleEditor.svelte generated by Svelte v3.29.4 */

function get_each_context$7(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[7] = list[i];
	return child_ctx;
}

// (272:0) {#each activeStyleControls as styleControl (styleControl.id) }
function create_each_block$7(key_1, ctx) {
	let li;
	let span;
	let t0_value = localize(/*styleControl*/ ctx[7].props.title, /*locale*/ ctx[0]) + "";
	let t0;
	let t1;
	let switch_instance;
	let t2;
	let current;
	const switch_instance_spread_levels = [/*styleControl*/ ctx[7].props];
	var switch_value = /*styleControl*/ ctx[7].component;

	function switch_props(ctx) {
		let switch_instance_props = {};

		for (let i = 0; i < switch_instance_spread_levels.length; i += 1) {
			switch_instance_props = assign(switch_instance_props, switch_instance_spread_levels[i]);
		}

		return { props: switch_instance_props };
	}

	if (switch_value) {
		switch_instance = new switch_value(switch_props());
	}

	return {
		key: key_1,
		first: null,
		c() {
			li = element("li");
			span = element("span");
			t0 = text(t0_value);
			t1 = space();
			if (switch_instance) create_component(switch_instance.$$.fragment);
			t2 = space();
			attr(span, "class", "DokaShapeStyleLabel");
			attr(li, "class", "DokaShapeStyle");
			this.first = li;
		},
		m(target, anchor) {
			insert(target, li, anchor);
			append(li, span);
			append(span, t0);
			append(li, t1);

			if (switch_instance) {
				mount_component(switch_instance, li, null);
			}

			append(li, t2);
			current = true;
		},
		p(ctx, dirty) {
			if ((!current || dirty & /*activeStyleControls, locale*/ 3) && t0_value !== (t0_value = localize(/*styleControl*/ ctx[7].props.title, /*locale*/ ctx[0]) + "")) set_data(t0, t0_value);

			const switch_instance_changes = (dirty & /*activeStyleControls*/ 2)
			? get_spread_update(switch_instance_spread_levels, [get_spread_object(/*styleControl*/ ctx[7].props)])
			: {};

			if (switch_value !== (switch_value = /*styleControl*/ ctx[7].component)) {
				if (switch_instance) {
					group_outros();
					const old_component = switch_instance;

					transition_out(old_component.$$.fragment, 1, 0, () => {
						destroy_component(old_component, 1);
					});

					check_outros();
				}

				if (switch_value) {
					switch_instance = new switch_value(switch_props());
					create_component(switch_instance.$$.fragment);
					transition_in(switch_instance.$$.fragment, 1);
					mount_component(switch_instance, li, t2);
				} else {
					switch_instance = null;
				}
			} else if (switch_value) {
				switch_instance.$set(switch_instance_changes);
			}
		},
		i(local) {
			if (current) return;
			if (switch_instance) transition_in(switch_instance.$$.fragment, local);
			current = true;
		},
		o(local) {
			if (switch_instance) transition_out(switch_instance.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(li);
			if (switch_instance) destroy_component(switch_instance);
		}
	};
}

function create_fragment$t(ctx) {
	let ul;
	let each_blocks = [];
	let each_1_lookup = new Map();
	let current;
	let each_value = /*activeStyleControls*/ ctx[1];
	const get_key = ctx => /*styleControl*/ ctx[7].id;

	for (let i = 0; i < each_value.length; i += 1) {
		let child_ctx = get_each_context$7(ctx, each_value, i);
		let key = get_key(child_ctx);
		each_1_lookup.set(key, each_blocks[i] = create_each_block$7(key, child_ctx));
	}

	return {
		c() {
			ul = element("ul");

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			attr(ul, "class", "DokaShapeStyleEditor");
		},
		m(target, anchor) {
			insert(target, ul, anchor);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].m(ul, null);
			}

			current = true;
		},
		p(ctx, [dirty]) {
			if (dirty & /*activeStyleControls, localize, locale*/ 3) {
				const each_value = /*activeStyleControls*/ ctx[1];
				group_outros();
				each_blocks = update_keyed_each(each_blocks, dirty, get_key, 1, ctx, each_value, each_1_lookup, ul, outro_and_destroy_block, create_each_block$7, null, get_each_context$7);
				check_outros();
			}
		},
		i(local) {
			if (current) return;

			for (let i = 0; i < each_value.length; i += 1) {
				transition_in(each_blocks[i]);
			}

			current = true;
		},
		o(local) {
			for (let i = 0; i < each_blocks.length; i += 1) {
				transition_out(each_blocks[i]);
			}

			current = false;
		},
		d(detaching) {
			if (detaching) detach(ul);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].d();
			}
		}
	};
}

function instance$t($$self, $$props, $$invalidate) {
	let { controls = {} } = $$props;
	let { shape = undefined } = $$props;
	let { detailPanel = undefined } = $$props;
	let { onchange } = $$props;
	let { locale } = $$props;

	// finds the controls needed to style the selected shape
	const getShapeControls = (shape, detailPanel) => {
		const shapeId = shape.id || "tool";

		const activeControls = Object.keys(controls).filter(prop => shape.hasOwnProperty(prop) && shapeCanStyle(shape, prop)).map(prop => {
			const currentValue = shape[prop];
			const [component, props] = controls[prop];

			const options = isFunction(props.options)
			? props.options(shape)
			: props.options;

			return {
				id: `${shapeId}_${prop}`,
				component,
				props: {
					...props,
					// set the options prop
					options,
					// defaults
					locale,
					value: currentValue,
					optionLabelClass: "DokaButtonLabel",
					onchange: detail => {
						let value = detail;

						// if receives event, need the 'value' property
						if (isObject(detail) && !isArray(detail)) value = detail.value;

						// allow custom changes
						if (props.onchange) {
							props.onchange(value, shape);
						}

						// internal change
						onchange(prop, value);
					},
					// for rendering detail panels
					detailPanel
				}
			};
		});

		return activeControls;
	};

	$$self.$$set = $$props => {
		if ("controls" in $$props) $$invalidate(2, controls = $$props.controls);
		if ("shape" in $$props) $$invalidate(3, shape = $$props.shape);
		if ("detailPanel" in $$props) $$invalidate(4, detailPanel = $$props.detailPanel);
		if ("onchange" in $$props) $$invalidate(5, onchange = $$props.onchange);
		if ("locale" in $$props) $$invalidate(0, locale = $$props.locale);
	};

	let activeStyleControls;

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*shape, detailPanel*/ 24) {
			 $$invalidate(1, activeStyleControls = shape && detailPanel && detailPanel.viewBounds && shapeCanStyle(shape)
			? getShapeControls(shape, detailPanel)
			: []);
		}
	};

	return [locale, activeStyleControls, controls, shape, detailPanel, onchange];
}

class ShapeStyleEditor extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$t, create_fragment$t, safe_not_equal, {
			controls: 2,
			shape: 3,
			detailPanel: 4,
			onchange: 5,
			locale: 0
		});
	}
}

/* src/native/ui/components/DragButton.svelte generated by Svelte v3.29.4 */

function create_fragment$u(ctx) {
	let button;
	let mounted;
	let dispose;

	return {
		c() {
			button = element("button");
			attr(button, "class", "DokaDragButton");
			attr(button, "title", /*title*/ ctx[1]);
		},
		m(target, anchor) {
			insert(target, button, anchor);
			button.innerHTML = /*html*/ ctx[0];
			/*button_binding*/ ctx[7](button);

			if (!mounted) {
				dispose = listen(button, "pointerdown", /*handleDown*/ ctx[3]);
				mounted = true;
			}
		},
		p(ctx, [dirty]) {
			if (dirty & /*html*/ 1) button.innerHTML = /*html*/ ctx[0];
			if (dirty & /*title*/ 2) {
				attr(button, "title", /*title*/ ctx[1]);
			}
		},
		i: noop$1,
		o: noop$1,
		d(detaching) {
			if (detaching) detach(button);
			/*button_binding*/ ctx[7](null);
			mounted = false;
			dispose();
		}
	};
}

function instance$u($$self, $$props, $$invalidate) {
	let { html } = $$props;
	let { title } = $$props;
	let { onclick } = $$props;
	let { ondrag = noop } = $$props;
	let { ondrop = noop } = $$props;
	let element;
	const isOverButton = e => vectorDistanceSquared(downPosition, vectorCreate(e.pageX, e.pageY)) < 256;
	let downPosition;

	const handleDown = e => {
		downPosition = vectorCreate(e.pageX, e.pageY);
		document.documentElement.addEventListener("pointermove", handleMove);
		document.documentElement.addEventListener("pointerup", handleUp);
	};

	const handleUp = e => {
		document.documentElement.removeEventListener("pointermove", handleMove);
		document.documentElement.removeEventListener("pointerup", handleUp);
		const upPosition = vectorCreate(e.pageX, e.pageY);

		// must have moved enough distance to drop
		if (vectorDistanceSquared(downPosition, upPosition) < 32) return onclick(e);

		// cant drop in button
		if (isOverButton(e)) return;

		// was dragging
		ondrop(e);
	};

	const handleMove = e => {
		// must have moved enough distance from button
		if (isOverButton(e)) return;

		// dragging out of button
		ondrag(e);
	};

	function button_binding($$value) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			element = $$value;
			$$invalidate(2, element);
		});
	}

	$$self.$$set = $$props => {
		if ("html" in $$props) $$invalidate(0, html = $$props.html);
		if ("title" in $$props) $$invalidate(1, title = $$props.title);
		if ("onclick" in $$props) $$invalidate(4, onclick = $$props.onclick);
		if ("ondrag" in $$props) $$invalidate(5, ondrag = $$props.ondrag);
		if ("ondrop" in $$props) $$invalidate(6, ondrop = $$props.ondrop);
	};

	return [html, title, element, handleDown, onclick, ondrag, ondrop, button_binding];
}

class DragButton extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$u, create_fragment$u, safe_not_equal, {
			html: 0,
			title: 1,
			onclick: 4,
			ondrag: 5,
			ondrop: 6
		});
	}
}

var isSVG = (str) => /<svg /.test(str);

var isImage = (file) => /^image/.test(file.type);

/* src/native/ui/components/ShapePresetsPalette.svelte generated by Svelte v3.29.4 */

function get_each_context_1$2(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[25] = list[i];
	return child_ctx;
}

function get_each_context$8(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[25] = list[i];
	return child_ctx;
}

// (222:4) {#if enableSelectImage}
function create_if_block_2$6(ctx) {
	let form;
	let input;
	let t0;
	let label;
	let t1;
	let current;
	let mounted;
	let dispose;
	let if_block0 = /*locale*/ ctx[0].shapeIconButtonSelectSticker && create_if_block_4$4(ctx);
	let if_block1 = /*locale*/ ctx[0].shapeLabelButtonSelectSticker && create_if_block_3$4(ctx);

	return {
		c() {
			form = element("form");
			input = element("input");
			t0 = space();
			label = element("label");
			if (if_block0) if_block0.c();
			t1 = space();
			if (if_block1) if_block1.c();
			attr(input, "id", /*inputName*/ ctx[8]);
			attr(input, "type", "file");
			attr(input, "accept", "image/*");
			attr(label, "for", /*inputName*/ ctx[8]);
			attr(form, "class", "DokaShapePresetsSelect");
		},
		m(target, anchor) {
			insert(target, form, anchor);
			append(form, input);
			append(form, t0);
			append(form, label);
			if (if_block0) if_block0.m(label, null);
			append(label, t1);
			if (if_block1) if_block1.m(label, null);
			/*form_binding*/ ctx[13](form);
			current = true;

			if (!mounted) {
				dispose = listen(input, "input", /*handleFileInput*/ ctx[9]);
				mounted = true;
			}
		},
		p(ctx, dirty) {
			if (/*locale*/ ctx[0].shapeIconButtonSelectSticker) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty & /*locale*/ 1) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_4$4(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(label, t1);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (/*locale*/ ctx[0].shapeLabelButtonSelectSticker) {
				if (if_block1) {
					if_block1.p(ctx, dirty);
				} else {
					if_block1 = create_if_block_3$4(ctx);
					if_block1.c();
					if_block1.m(label, null);
				}
			} else if (if_block1) {
				if_block1.d(1);
				if_block1 = null;
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(form);
			if (if_block0) if_block0.d();
			if (if_block1) if_block1.d();
			/*form_binding*/ ctx[13](null);
			mounted = false;
			dispose();
		}
	};
}

// (226:12) {#if locale.shapeIconButtonSelectSticker}
function create_if_block_4$4(ctx) {
	let icon;
	let current;

	icon = new Icon({
			props: {
				width: 16,
				height: 16,
				$$slots: { default: [create_default_slot$6] },
				$$scope: { ctx }
			}
		});

	return {
		c() {
			create_component(icon.$$.fragment);
		},
		m(target, anchor) {
			mount_component(icon, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const icon_changes = {};

			if (dirty & /*$$scope, locale*/ 1073741825) {
				icon_changes.$$scope = { dirty, ctx };
			}

			icon.$set(icon_changes);
		},
		i(local) {
			if (current) return;
			transition_in(icon.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(icon.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(icon, detaching);
		}
	};
}

// (227:12) <Icon width={16} height={16}>
function create_default_slot$6(ctx) {
	let g;
	let raw_value = /*locale*/ ctx[0].shapeIconButtonSelectSticker + "";

	return {
		c() {
			g = svg_element("g");
		},
		m(target, anchor) {
			insert(target, g, anchor);
			g.innerHTML = raw_value;
		},
		p(ctx, dirty) {
			if (dirty & /*locale*/ 1 && raw_value !== (raw_value = /*locale*/ ctx[0].shapeIconButtonSelectSticker + "")) g.innerHTML = raw_value;		},
		d(detaching) {
			if (detaching) detach(g);
		}
	};
}

// (229:12) {#if locale.shapeLabelButtonSelectSticker}
function create_if_block_3$4(ctx) {
	let span;
	let t_value = /*locale*/ ctx[0].shapeLabelButtonSelectSticker + "";
	let t;

	return {
		c() {
			span = element("span");
			t = text(t_value);
		},
		m(target, anchor) {
			insert(target, span, anchor);
			append(span, t);
		},
		p(ctx, dirty) {
			if (dirty & /*locale*/ 1 && t_value !== (t_value = /*locale*/ ctx[0].shapeLabelButtonSelectSticker + "")) set_data(t, t_value);
		},
		d(detaching) {
			if (detaching) detach(span);
		}
	};
}

// (236:4) {#if presets.length}
function create_if_block$7(ctx) {
	let ul;
	let each_blocks = [];
	let each_1_lookup = new Map();
	let current;
	let each_value = /*presetsMapped*/ ctx[4];
	const get_key = ctx => /*item*/ ctx[25].id;

	for (let i = 0; i < each_value.length; i += 1) {
		let child_ctx = get_each_context$8(ctx, each_value, i);
		let key = get_key(child_ctx);
		each_1_lookup.set(key, each_blocks[i] = create_each_block$8(key, child_ctx));
	}

	return {
		c() {
			ul = element("ul");

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			attr(ul, "class", "DokaShapePresetsList");
		},
		m(target, anchor) {
			insert(target, ul, anchor);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].m(ul, null);
			}

			current = true;
		},
		p(ctx, dirty) {
			if (dirty & /*presetsMapped, handlePresetClick, handlePresetDrag, handlePresetDrop*/ 240) {
				const each_value = /*presetsMapped*/ ctx[4];
				group_outros();
				each_blocks = update_keyed_each(each_blocks, dirty, get_key, 1, ctx, each_value, each_1_lookup, ul, outro_and_destroy_block, create_each_block$8, null, get_each_context$8);
				check_outros();
			}
		},
		i(local) {
			if (current) return;

			for (let i = 0; i < each_value.length; i += 1) {
				transition_in(each_blocks[i]);
			}

			current = true;
		},
		o(local) {
			for (let i = 0; i < each_blocks.length; i += 1) {
				transition_out(each_blocks[i]);
			}

			current = false;
		},
		d(detaching) {
			if (detaching) detach(ul);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].d();
			}
		}
	};
}

// (255:8) {:else}
function create_else_block$2(ctx) {
	let li;
	let dragbutton;
	let t;
	let current;

	function func_3(...args) {
		return /*func_3*/ ctx[17](/*item*/ ctx[25], ...args);
	}

	function func_4(...args) {
		return /*func_4*/ ctx[18](/*item*/ ctx[25], ...args);
	}

	function func_5(...args) {
		return /*func_5*/ ctx[19](/*item*/ ctx[25], ...args);
	}

	dragbutton = new DragButton({
			props: {
				onclick: func_3,
				ondrag: func_4,
				ondrop: func_5,
				title: /*item*/ ctx[25].title,
				html: /*item*/ ctx[25].thumb
			}
		});

	return {
		c() {
			li = element("li");
			create_component(dragbutton.$$.fragment);
			t = space();
			attr(li, "class", "DokaShapePreset");
		},
		m(target, anchor) {
			insert(target, li, anchor);
			mount_component(dragbutton, li, null);
			append(li, t);
			current = true;
		},
		p(new_ctx, dirty) {
			ctx = new_ctx;
			const dragbutton_changes = {};
			if (dirty & /*presetsMapped*/ 16) dragbutton_changes.onclick = func_3;
			if (dirty & /*presetsMapped*/ 16) dragbutton_changes.ondrag = func_4;
			if (dirty & /*presetsMapped*/ 16) dragbutton_changes.ondrop = func_5;
			if (dirty & /*presetsMapped*/ 16) dragbutton_changes.title = /*item*/ ctx[25].title;
			if (dirty & /*presetsMapped*/ 16) dragbutton_changes.html = /*item*/ ctx[25].thumb;
			dragbutton.$set(dragbutton_changes);
		},
		i(local) {
			if (current) return;
			transition_in(dragbutton.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(dragbutton.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(li);
			destroy_component(dragbutton);
		}
	};
}

// (240:8) {#if item.items}
function create_if_block_1$9(ctx) {
	let li;
	let ul;
	let each_blocks = [];
	let each_1_lookup = new Map();
	let t;
	let current;
	let each_value_1 = /*item*/ ctx[25].items;
	const get_key = ctx => /*item*/ ctx[25].id;

	for (let i = 0; i < each_value_1.length; i += 1) {
		let child_ctx = get_each_context_1$2(ctx, each_value_1, i);
		let key = get_key(child_ctx);
		each_1_lookup.set(key, each_blocks[i] = create_each_block_1$2(key, child_ctx));
	}

	return {
		c() {
			li = element("li");
			ul = element("ul");

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			t = space();
			attr(li, "class", "DokaShapePresetsGroup");
		},
		m(target, anchor) {
			insert(target, li, anchor);
			append(li, ul);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].m(ul, null);
			}

			append(li, t);
			current = true;
		},
		p(ctx, dirty) {
			if (dirty & /*handlePresetClick, presetsMapped, handlePresetDrag, handlePresetDrop*/ 240) {
				const each_value_1 = /*item*/ ctx[25].items;
				group_outros();
				each_blocks = update_keyed_each(each_blocks, dirty, get_key, 1, ctx, each_value_1, each_1_lookup, ul, outro_and_destroy_block, create_each_block_1$2, null, get_each_context_1$2);
				check_outros();
			}
		},
		i(local) {
			if (current) return;

			for (let i = 0; i < each_value_1.length; i += 1) {
				transition_in(each_blocks[i]);
			}

			current = true;
		},
		o(local) {
			for (let i = 0; i < each_blocks.length; i += 1) {
				transition_out(each_blocks[i]);
			}

			current = false;
		},
		d(detaching) {
			if (detaching) detach(li);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].d();
			}
		}
	};
}

// (243:12) {#each item.items as item (item.id)}
function create_each_block_1$2(key_1, ctx) {
	let li;
	let dragbutton;
	let t;
	let current;

	function func(...args) {
		return /*func*/ ctx[14](/*item*/ ctx[25], ...args);
	}

	function func_1(...args) {
		return /*func_1*/ ctx[15](/*item*/ ctx[25], ...args);
	}

	function func_2(...args) {
		return /*func_2*/ ctx[16](/*item*/ ctx[25], ...args);
	}

	dragbutton = new DragButton({
			props: {
				onclick: func,
				ondrag: func_1,
				ondrop: func_2,
				title: /*item*/ ctx[25].title,
				html: /*item*/ ctx[25].thumb
			}
		});

	return {
		key: key_1,
		first: null,
		c() {
			li = element("li");
			create_component(dragbutton.$$.fragment);
			t = space();
			attr(li, "class", "DokaShapePreset");
			this.first = li;
		},
		m(target, anchor) {
			insert(target, li, anchor);
			mount_component(dragbutton, li, null);
			append(li, t);
			current = true;
		},
		p(new_ctx, dirty) {
			ctx = new_ctx;
			const dragbutton_changes = {};
			if (dirty & /*presetsMapped*/ 16) dragbutton_changes.onclick = func;
			if (dirty & /*presetsMapped*/ 16) dragbutton_changes.ondrag = func_1;
			if (dirty & /*presetsMapped*/ 16) dragbutton_changes.ondrop = func_2;
			if (dirty & /*presetsMapped*/ 16) dragbutton_changes.title = /*item*/ ctx[25].title;
			if (dirty & /*presetsMapped*/ 16) dragbutton_changes.html = /*item*/ ctx[25].thumb;
			dragbutton.$set(dragbutton_changes);
		},
		i(local) {
			if (current) return;
			transition_in(dragbutton.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(dragbutton.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(li);
			destroy_component(dragbutton);
		}
	};
}

// (239:8) {#each presetsMapped as item (item.id)}
function create_each_block$8(key_1, ctx) {
	let first;
	let current_block_type_index;
	let if_block;
	let if_block_anchor;
	let current;
	const if_block_creators = [create_if_block_1$9, create_else_block$2];
	const if_blocks = [];

	function select_block_type(ctx, dirty) {
		if (/*item*/ ctx[25].items) return 0;
		return 1;
	}

	current_block_type_index = select_block_type(ctx);
	if_block = if_blocks[current_block_type_index] = if_block_creators[current_block_type_index](ctx);

	return {
		key: key_1,
		first: null,
		c() {
			first = empty();
			if_block.c();
			if_block_anchor = empty();
			this.first = first;
		},
		m(target, anchor) {
			insert(target, first, anchor);
			if_blocks[current_block_type_index].m(target, anchor);
			insert(target, if_block_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			let previous_block_index = current_block_type_index;
			current_block_type_index = select_block_type(ctx);

			if (current_block_type_index === previous_block_index) {
				if_blocks[current_block_type_index].p(ctx, dirty);
			} else {
				group_outros();

				transition_out(if_blocks[previous_block_index], 1, 1, () => {
					if_blocks[previous_block_index] = null;
				});

				check_outros();
				if_block = if_blocks[current_block_type_index];

				if (!if_block) {
					if_block = if_blocks[current_block_type_index] = if_block_creators[current_block_type_index](ctx);
					if_block.c();
				}

				transition_in(if_block, 1);
				if_block.m(if_block_anchor.parentNode, if_block_anchor);
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block);
			current = true;
		},
		o(local) {
			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(first);
			if_blocks[current_block_type_index].d(detaching);
			if (detaching) detach(if_block_anchor);
		}
	};
}

function create_fragment$v(ctx) {
	let div;
	let t;
	let current;
	let if_block0 = /*enableSelectImage*/ ctx[2] && create_if_block_2$6(ctx);
	let if_block1 = /*presets*/ ctx[1].length && create_if_block$7(ctx);

	return {
		c() {
			div = element("div");
			if (if_block0) if_block0.c();
			t = space();
			if (if_block1) if_block1.c();
			attr(div, "class", "DokaShapePresetsPalette");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			if (if_block0) if_block0.m(div, null);
			append(div, t);
			if (if_block1) if_block1.m(div, null);
			current = true;
		},
		p(ctx, [dirty]) {
			if (/*enableSelectImage*/ ctx[2]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty & /*enableSelectImage*/ 4) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_2$6(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(div, t);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (/*presets*/ ctx[1].length) {
				if (if_block1) {
					if_block1.p(ctx, dirty);

					if (dirty & /*presets*/ 2) {
						transition_in(if_block1, 1);
					}
				} else {
					if_block1 = create_if_block$7(ctx);
					if_block1.c();
					transition_in(if_block1, 1);
					if_block1.m(div, null);
				}
			} else if (if_block1) {
				group_outros();

				transition_out(if_block1, 1, 1, () => {
					if_block1 = null;
				});

				check_outros();
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);
			transition_in(if_block1);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			transition_out(if_block1);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			if (if_block0) if_block0.d();
			if (if_block1) if_block1.d();
		}
	};
}

function instance$v($$self, $$props, $$invalidate) {
	let { locale } = $$props;
	let { presets } = $$props;
	let { enableSelectImage = true } = $$props;
	let { onaddpreset = noop } = $$props;
	let { ondragpreset = noop } = $$props;
	let { ondroppreset = noop } = $$props;
	const isPresetGroup = item => isArray(item) && isString(item[0]) && isArray(item[1]);

	const getPresetThumb = (value, alt = "") => {
		// could be svg
		if (isSVG(value)) return value;

		// test if is emoji
		if (isEmoji(value)) return getEmojiSVG(value, alt);

		// must be URL
		return `<img src="${value}" alt="${alt}"/>`;
	};

	const getAltFromSrc = src => getFilenameWithoutExtension(getFilenameFromURL(src));

	const mapPreset = item => {
		let id = item;
		let src;
		let shape;
		let shapeProps;
		let thumb;
		let alt;

		// if item is a string, it's either an emoji or a URL
		if (isString(item)) {
			// is emoji
			if (isEmoji(item)) {
				src = item;
				alt = item;
				thumb = getPresetThumb(src, alt);
			} else // must be URL
			{
				src = item;
				alt = getAltFromSrc(src);
				thumb = getPresetThumb(src, alt);
			}
		} else {
			// set custom thumbnail if defined
			src = item.src;

			alt = item.alt || (isString(src)
			? getAltFromSrc(src)
			: isString(item.thumb)
				? getAltFromSrc(item.thumb)
				: undefined);

			thumb = getPresetThumb(item.thumb || src, alt);
			shape = item.shape;
			shapeProps = { ...item };
			delete shapeProps.src;
			delete shapeProps.alt;
			delete shapeProps.thumb;
			delete shapeProps.shape;
			delete shapeProps.id;
		}

		return {
			id,
			src,
			thumb,
			shape,
			shapeProps,
			alt,
			title: alt
		};
	};

	const mapPresets = items => items.map(item => {
		if (isPresetGroup(item)) {
			return {
				id: item,
				label: item[0],
				items: mapPresets(item[1])
			};
		}

		return mapPreset(item);
	});

	const handlePresetClick = (preset, e) => onaddpreset(preset);

	const handlePresetDrag = (preset, e) => {
		ondragpreset(preset, e);
	};

	const handlePresetDrop = (preset, e) => {
		ondroppreset(preset, e);
	};

	const inputName = `doka-preset-select-${getUniqueId()}`;

	//
	// handle file input
	//
	let formFileInput;

	const handleFileInput = e => {
		// only deal with image files
		Array.from(e.target.files).filter(isImage).forEach(onaddpreset);

		// reset input when done
		formFileInput.reset();
	};

	function form_binding($$value) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			formFileInput = $$value;
			$$invalidate(3, formFileInput);
		});
	}

	const func = (item, e) => handlePresetClick(item.id);
	const func_1 = (item, e) => handlePresetDrag(item.id, e);
	const func_2 = (item, e) => handlePresetDrop(item.id, e);
	const func_3 = (item, e) => handlePresetClick(item.id);
	const func_4 = (item, e) => handlePresetDrag(item.id, e);
	const func_5 = (item, e) => handlePresetDrop(item.id, e);

	$$self.$$set = $$props => {
		if ("locale" in $$props) $$invalidate(0, locale = $$props.locale);
		if ("presets" in $$props) $$invalidate(1, presets = $$props.presets);
		if ("enableSelectImage" in $$props) $$invalidate(2, enableSelectImage = $$props.enableSelectImage);
		if ("onaddpreset" in $$props) $$invalidate(10, onaddpreset = $$props.onaddpreset);
		if ("ondragpreset" in $$props) $$invalidate(11, ondragpreset = $$props.ondragpreset);
		if ("ondroppreset" in $$props) $$invalidate(12, ondroppreset = $$props.ondroppreset);
	};

	let presetsMapped;

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*presets*/ 2) {
			 $$invalidate(4, presetsMapped = mapPresets(presets));
		}
	};

	return [
		locale,
		presets,
		enableSelectImage,
		formFileInput,
		presetsMapped,
		handlePresetClick,
		handlePresetDrag,
		handlePresetDrop,
		inputName,
		handleFileInput,
		onaddpreset,
		ondragpreset,
		ondroppreset,
		form_binding,
		func,
		func_1,
		func_2,
		func_3,
		func_4,
		func_5
	];
}

class ShapePresetsPalette extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$v, create_fragment$v, safe_not_equal, {
			locale: 0,
			presets: 1,
			enableSelectImage: 2,
			onaddpreset: 10,
			ondragpreset: 11,
			ondroppreset: 12
		});
	}
}

const getFilesFromEvent = (e) => {
    const { items, files } = e.dataTransfer;
    if (items) {
        return Array.from(items)
            .filter((item) => item.kind === 'file')
            .map((item) => item.getAsFile());
    }
    return Array.from(files) || [];
};
var dropable = (node) => {
    const handleDragOver = (e) => {
        // need to be prevent default to allow drop
        e.preventDefault();
    };
    const handleDrop = (e) => {
        e.preventDefault();
        node.dispatchEvent(new CustomEvent('dropfiles', {
            detail: {
                event: e,
                files: getFilesFromEvent(e).filter((file) => isImage(file)),
            },
        }));
    };
    node.addEventListener('drop', handleDrop);
    node.addEventListener('dragover', handleDragOver);
    // remove method
    return {
        destroy() {
            node.removeEventListener('drop', handleDrop);
            node.removeEventListener('dragover', handleDragOver);
        },
    };
};

/* src/native/ui/components/ShapeUtil.svelte generated by Svelte v3.29.4 */

function create_if_block_3$5(ctx) {
	let shapelayouteditor;
	let updating_markup;
	let updating_ui;
	let current;

	function shapelayouteditor_markup_binding(value) {
		/*shapelayouteditor_markup_binding*/ ctx[56].call(null, value);
	}

	function shapelayouteditor_ui_binding(value) {
		/*shapelayouteditor_ui_binding*/ ctx[57].call(null, value);
	}

	let shapelayouteditor_props = {
		locale: /*locale*/ ctx[4],
		parentRect: /*$parentRect*/ ctx[26],
		rootRect: /*$rootRect*/ ctx[18],
		stageRect: /*$stageRect*/ ctx[21],
		offset: /*markupOffset*/ ctx[20],
		scale: /*$presentationScalar*/ ctx[29],
		rotation: /*imageRotation*/ ctx[13],
		opacity: /*$isActiveFraction*/ ctx[30],
		enableButtonFlipVertical: /*enableButtonFlipVertical*/ ctx[8],
		mapEditorPointToImagePoint: /*mapScreenPointToImagePoint*/ ctx[11],
		mapImagePointToEditorPoint: /*mapImagePointToScreenPoint*/ ctx[12],
		oninteractionstart: /*handleInteractionStart*/ ctx[41],
		oninteractionupdate: /*handleInteractionUpdate*/ ctx[42],
		oninteractionrelease: /*handleInteractionRelease*/ ctx[43],
		oninteractionend: /*handleInteractionEnd*/ ctx[44],
		onaddshape: /*handleMarkupUpdate*/ ctx[50],
		onupdateshape: /*handleMarkupUpdate*/ ctx[50],
		onremoveshape: /*handleMarkupUpdate*/ ctx[50]
	};

	if (/*$shapes*/ ctx[22] !== void 0) {
		shapelayouteditor_props.markup = /*$shapes*/ ctx[22];
	}

	if (/*$imageOverlayMarkup*/ ctx[28] !== void 0) {
		shapelayouteditor_props.ui = /*$imageOverlayMarkup*/ ctx[28];
	}

	shapelayouteditor = new ShapeLayoutEditor({ props: shapelayouteditor_props });
	/*shapelayouteditor_binding*/ ctx[55](shapelayouteditor);
	binding_callbacks.push(() => bind(shapelayouteditor, "markup", shapelayouteditor_markup_binding));
	binding_callbacks.push(() => bind(shapelayouteditor, "ui", shapelayouteditor_ui_binding));
	shapelayouteditor.$on("measure", /*measure_handler_1*/ ctx[58]);

	return {
		c() {
			create_component(shapelayouteditor.$$.fragment);
		},
		m(target, anchor) {
			mount_component(shapelayouteditor, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const shapelayouteditor_changes = {};
			if (dirty[0] & /*locale*/ 16) shapelayouteditor_changes.locale = /*locale*/ ctx[4];
			if (dirty[0] & /*$parentRect*/ 67108864) shapelayouteditor_changes.parentRect = /*$parentRect*/ ctx[26];
			if (dirty[0] & /*$rootRect*/ 262144) shapelayouteditor_changes.rootRect = /*$rootRect*/ ctx[18];
			if (dirty[0] & /*$stageRect*/ 2097152) shapelayouteditor_changes.stageRect = /*$stageRect*/ ctx[21];
			if (dirty[0] & /*markupOffset*/ 1048576) shapelayouteditor_changes.offset = /*markupOffset*/ ctx[20];
			if (dirty[0] & /*$presentationScalar*/ 536870912) shapelayouteditor_changes.scale = /*$presentationScalar*/ ctx[29];
			if (dirty[0] & /*imageRotation*/ 8192) shapelayouteditor_changes.rotation = /*imageRotation*/ ctx[13];
			if (dirty[0] & /*$isActiveFraction*/ 1073741824) shapelayouteditor_changes.opacity = /*$isActiveFraction*/ ctx[30];
			if (dirty[0] & /*enableButtonFlipVertical*/ 256) shapelayouteditor_changes.enableButtonFlipVertical = /*enableButtonFlipVertical*/ ctx[8];
			if (dirty[0] & /*mapScreenPointToImagePoint*/ 2048) shapelayouteditor_changes.mapEditorPointToImagePoint = /*mapScreenPointToImagePoint*/ ctx[11];
			if (dirty[0] & /*mapImagePointToScreenPoint*/ 4096) shapelayouteditor_changes.mapImagePointToEditorPoint = /*mapImagePointToScreenPoint*/ ctx[12];

			if (!updating_markup && dirty[0] & /*$shapes*/ 4194304) {
				updating_markup = true;
				shapelayouteditor_changes.markup = /*$shapes*/ ctx[22];
				add_flush_callback(() => updating_markup = false);
			}

			if (!updating_ui && dirty[0] & /*$imageOverlayMarkup*/ 268435456) {
				updating_ui = true;
				shapelayouteditor_changes.ui = /*$imageOverlayMarkup*/ ctx[28];
				add_flush_callback(() => updating_ui = false);
			}

			shapelayouteditor.$set(shapelayouteditor_changes);
		},
		i(local) {
			if (current) return;
			transition_in(shapelayouteditor.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(shapelayouteditor.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			/*shapelayouteditor_binding*/ ctx[55](null);
			destroy_component(shapelayouteditor, detaching);
		}
	};
}

// (336:4) <div slot="main" style="cursor: crosshair" use:dropable on:dropfiles={handleDropFiles}>
function create_main_slot$1(ctx) {
	let div;
	let dropable_action;
	let current;
	let mounted;
	let dispose;
	let if_block = /*shouldRenderShapeEditor*/ ctx[19] && create_if_block_3$5(ctx);

	return {
		c() {
			div = element("div");
			if (if_block) if_block.c();
			attr(div, "slot", "main");
			set_style(div, "cursor", "crosshair");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			if (if_block) if_block.m(div, null);
			current = true;

			if (!mounted) {
				dispose = [
					action_destroyer(dropable_action = dropable.call(null, div)),
					listen(div, "dropfiles", /*handleDropFiles*/ ctx[49])
				];

				mounted = true;
			}
		},
		p(ctx, dirty) {
			if (/*shouldRenderShapeEditor*/ ctx[19]) {
				if (if_block) {
					if_block.p(ctx, dirty);

					if (dirty[0] & /*shouldRenderShapeEditor*/ 524288) {
						transition_in(if_block, 1);
					}
				} else {
					if_block = create_if_block_3$5(ctx);
					if_block.c();
					transition_in(if_block, 1);
					if_block.m(div, null);
				}
			} else if (if_block) {
				group_outros();

				transition_out(if_block, 1, 1, () => {
					if_block = null;
				});

				check_outros();
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block);
			current = true;
		},
		o(local) {
			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			if (if_block) if_block.d();
			mounted = false;
			run_all(dispose);
		}
	};
}

// (375:12) {#if shouldRenderPresets}
function create_if_block_2$7(ctx) {
	let shapepresetspalette;
	let current;

	shapepresetspalette = new ShapePresetsPalette({
			props: {
				locale: /*locale*/ ctx[4],
				presets: /*shapePresets*/ ctx[10],
				enableSelectImage: /*enablePresetSelectImage*/ ctx[9],
				onaddpreset: /*handleAddPreset*/ ctx[48],
				ondragpreset: /*handleDragPreset*/ ctx[46],
				ondroppreset: /*handleDropPreset*/ ctx[47]
			}
		});

	return {
		c() {
			create_component(shapepresetspalette.$$.fragment);
		},
		m(target, anchor) {
			mount_component(shapepresetspalette, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const shapepresetspalette_changes = {};
			if (dirty[0] & /*locale*/ 16) shapepresetspalette_changes.locale = /*locale*/ ctx[4];
			if (dirty[0] & /*shapePresets*/ 1024) shapepresetspalette_changes.presets = /*shapePresets*/ ctx[10];
			if (dirty[0] & /*enablePresetSelectImage*/ 512) shapepresetspalette_changes.enableSelectImage = /*enablePresetSelectImage*/ ctx[9];
			shapepresetspalette.$set(shapepresetspalette_changes);
		},
		i(local) {
			if (current) return;
			transition_in(shapepresetspalette.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(shapepresetspalette.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(shapepresetspalette, detaching);
		}
	};
}

// (367:8) <Scrollable class="DokaShapeStyles" scrollAutoCancel={shouldRenderPresets} elasticity={elasticityMultiplier * scrollElasticity} on:measure={e => markupShapeEditorScrollRect = e.detail}>
function create_default_slot_4(ctx) {
	let shapestyleeditor;
	let t;
	let if_block_anchor;
	let current;

	shapestyleeditor = new ShapeStyleEditor({
			props: {
				locale: /*locale*/ ctx[4],
				shape: /*markupShapeSelected*/ ctx[23],
				onchange: /*handleUpdateSelectedMarkupShape*/ ctx[45],
				controls: /*shapeControls*/ ctx[7],
				detailPanel: /*detailPanelProps*/ ctx[24]
			}
		});

	let if_block = /*shouldRenderPresets*/ ctx[25] && create_if_block_2$7(ctx);

	return {
		c() {
			create_component(shapestyleeditor.$$.fragment);
			t = space();
			if (if_block) if_block.c();
			if_block_anchor = empty();
		},
		m(target, anchor) {
			mount_component(shapestyleeditor, target, anchor);
			insert(target, t, anchor);
			if (if_block) if_block.m(target, anchor);
			insert(target, if_block_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const shapestyleeditor_changes = {};
			if (dirty[0] & /*locale*/ 16) shapestyleeditor_changes.locale = /*locale*/ ctx[4];
			if (dirty[0] & /*markupShapeSelected*/ 8388608) shapestyleeditor_changes.shape = /*markupShapeSelected*/ ctx[23];
			if (dirty[0] & /*shapeControls*/ 128) shapestyleeditor_changes.controls = /*shapeControls*/ ctx[7];
			if (dirty[0] & /*detailPanelProps*/ 16777216) shapestyleeditor_changes.detailPanel = /*detailPanelProps*/ ctx[24];
			shapestyleeditor.$set(shapestyleeditor_changes);

			if (/*shouldRenderPresets*/ ctx[25]) {
				if (if_block) {
					if_block.p(ctx, dirty);

					if (dirty[0] & /*shouldRenderPresets*/ 33554432) {
						transition_in(if_block, 1);
					}
				} else {
					if_block = create_if_block_2$7(ctx);
					if_block.c();
					transition_in(if_block, 1);
					if_block.m(if_block_anchor.parentNode, if_block_anchor);
				}
			} else if (if_block) {
				group_outros();

				transition_out(if_block, 1, 1, () => {
					if_block = null;
				});

				check_outros();
			}
		},
		i(local) {
			if (current) return;
			transition_in(shapestyleeditor.$$.fragment, local);
			transition_in(if_block);
			current = true;
		},
		o(local) {
			transition_out(shapestyleeditor.$$.fragment, local);
			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			destroy_component(shapestyleeditor, detaching);
			if (detaching) detach(t);
			if (if_block) if_block.d(detaching);
			if (detaching) detach(if_block_anchor);
		}
	};
}

// (386:8) {#if tools.length}
function create_if_block$8(ctx) {
	let scrollable;
	let current;

	scrollable = new Scrollable({
			props: {
				class: "DokaControlListScroller",
				elasticity: /*elasticityMultiplier*/ ctx[34] * /*scrollElasticity*/ ctx[35],
				$$slots: { default: [create_default_slot_1$6] },
				$$scope: { ctx }
			}
		});

	return {
		c() {
			create_component(scrollable.$$.fragment);
		},
		m(target, anchor) {
			mount_component(scrollable, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const scrollable_changes = {};

			if (dirty[0] & /*locale, tools, toolActive*/ 81 | dirty[2] & /*$$scope*/ 2097152) {
				scrollable_changes.$$scope = { dirty, ctx };
			}

			scrollable.$set(scrollable_changes);
		},
		i(local) {
			if (current) return;
			transition_in(scrollable.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(scrollable.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(scrollable, detaching);
		}
	};
}

// (398:20) {#if option.icon}
function create_if_block_1$a(ctx) {
	let icon;
	let current;

	icon = new Icon({
			props: {
				$$slots: { default: [create_default_slot_3$5] },
				$$scope: { ctx }
			}
		});

	return {
		c() {
			create_component(icon.$$.fragment);
		},
		m(target, anchor) {
			mount_component(icon, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const icon_changes = {};

			if (dirty[0] & /*locale*/ 16 | dirty[2] & /*$$scope, option*/ 3145728) {
				icon_changes.$$scope = { dirty, ctx };
			}

			icon.$set(icon_changes);
		},
		i(local) {
			if (current) return;
			transition_in(icon.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(icon.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(icon, detaching);
		}
	};
}

// (399:20) <Icon>
function create_default_slot_3$5(ctx) {
	let g;

	let raw_value = (isFunction(/*option*/ ctx[82].icon)
	? /*option*/ ctx[82].icon(/*locale*/ ctx[4])
	: /*option*/ ctx[82].icon) + "";

	return {
		c() {
			g = svg_element("g");
		},
		m(target, anchor) {
			insert(target, g, anchor);
			g.innerHTML = raw_value;
		},
		p(ctx, dirty) {
			if (dirty[0] & /*locale*/ 16 | dirty[2] & /*option*/ 1048576 && raw_value !== (raw_value = (isFunction(/*option*/ ctx[82].icon)
			? /*option*/ ctx[82].icon(/*locale*/ ctx[4])
			: /*option*/ ctx[82].icon) + "")) g.innerHTML = raw_value;		},
		d(detaching) {
			if (detaching) detach(g);
		}
	};
}

// (397:16) <div slot="option">
function create_option_slot$2(ctx) {
	let div;
	let t0;
	let span;

	let t1_value = (isFunction(/*option*/ ctx[82].label)
	? /*option*/ ctx[82].label(/*locale*/ ctx[4])
	: /*option*/ ctx[82].label) + "";

	let t1;
	let current;
	let if_block = /*option*/ ctx[82].icon && create_if_block_1$a(ctx);

	return {
		c() {
			div = element("div");
			if (if_block) if_block.c();
			t0 = space();
			span = element("span");
			t1 = text(t1_value);
			attr(div, "slot", "option");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			if (if_block) if_block.m(div, null);
			append(div, t0);
			append(div, span);
			append(span, t1);
			current = true;
		},
		p(ctx, dirty) {
			if (/*option*/ ctx[82].icon) {
				if (if_block) {
					if_block.p(ctx, dirty);

					if (dirty[2] & /*option*/ 1048576) {
						transition_in(if_block, 1);
					}
				} else {
					if_block = create_if_block_1$a(ctx);
					if_block.c();
					transition_in(if_block, 1);
					if_block.m(div, t0);
				}
			} else if (if_block) {
				group_outros();

				transition_out(if_block, 1, 1, () => {
					if_block = null;
				});

				check_outros();
			}

			if ((!current || dirty[0] & /*locale*/ 16 | dirty[2] & /*option*/ 1048576) && t1_value !== (t1_value = (isFunction(/*option*/ ctx[82].label)
			? /*option*/ ctx[82].label(/*locale*/ ctx[4])
			: /*option*/ ctx[82].label) + "")) set_data(t1, t1_value);
		},
		i(local) {
			if (current) return;
			transition_in(if_block);
			current = true;
		},
		o(local) {
			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			if (if_block) if_block.d();
		}
	};
}

// (387:8) <Scrollable class="DokaControlListScroller" elasticity={elasticityMultiplier * scrollElasticity}>
function create_default_slot_1$6(ctx) {
	let radiogroup;
	let current;

	radiogroup = new RadioGroup({
			props: {
				locale: /*locale*/ ctx[4],
				class: "DokaControlList",
				optionClass: "DokaControlListOption",
				layout: "row",
				options: /*tools*/ ctx[6],
				selectedIndex: /*tools*/ ctx[6].findIndex(/*func*/ ctx[61]),
				onchange: /*updateActiveTool*/ ctx[40],
				$$slots: {
					option: [
						create_option_slot$2,
						({ option }) => ({ 82: option }),
						({ option }) => [0, 0, option ? 1048576 : 0]
					]
				},
				$$scope: { ctx }
			}
		});

	return {
		c() {
			create_component(radiogroup.$$.fragment);
		},
		m(target, anchor) {
			mount_component(radiogroup, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const radiogroup_changes = {};
			if (dirty[0] & /*locale*/ 16) radiogroup_changes.locale = /*locale*/ ctx[4];
			if (dirty[0] & /*tools*/ 64) radiogroup_changes.options = /*tools*/ ctx[6];
			if (dirty[0] & /*tools, toolActive*/ 65) radiogroup_changes.selectedIndex = /*tools*/ ctx[6].findIndex(/*func*/ ctx[61]);

			if (dirty[0] & /*locale*/ 16 | dirty[2] & /*$$scope, option*/ 3145728) {
				radiogroup_changes.$$scope = { dirty, ctx };
			}

			radiogroup.$set(radiogroup_changes);
		},
		i(local) {
			if (current) return;
			transition_in(radiogroup.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(radiogroup.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(radiogroup, detaching);
		}
	};
}

// (363:4) <div slot="footer" style={footerStyle}>
function create_footer_slot$3(ctx) {
	let div0;
	let div1;
	let t0;
	let scrollable;
	let t1;
	let current;

	scrollable = new Scrollable({
			props: {
				class: "DokaShapeStyles",
				scrollAutoCancel: /*shouldRenderPresets*/ ctx[25],
				elasticity: /*elasticityMultiplier*/ ctx[34] * /*scrollElasticity*/ ctx[35],
				$$slots: { default: [create_default_slot_4] },
				$$scope: { ctx }
			}
		});

	scrollable.$on("measure", /*measure_handler_2*/ ctx[60]);
	let if_block = /*tools*/ ctx[6].length && create_if_block$8(ctx);

	return {
		c() {
			div0 = element("div");
			div1 = element("div");
			t0 = space();
			create_component(scrollable.$$.fragment);
			t1 = space();
			if (if_block) if_block.c();
			attr(div0, "slot", "footer");
			attr(div0, "style", /*footerStyle*/ ctx[27]);
		},
		m(target, anchor) {
			insert(target, div0, anchor);
			append(div0, div1);
			/*div1_binding*/ ctx[59](div1);
			append(div0, t0);
			mount_component(scrollable, div0, null);
			append(div0, t1);
			if (if_block) if_block.m(div0, null);
			current = true;
		},
		p(ctx, dirty) {
			const scrollable_changes = {};
			if (dirty[0] & /*shouldRenderPresets*/ 33554432) scrollable_changes.scrollAutoCancel = /*shouldRenderPresets*/ ctx[25];

			if (dirty[0] & /*locale, shapePresets, enablePresetSelectImage, shouldRenderPresets, markupShapeSelected, shapeControls, detailPanelProps*/ 58721936 | dirty[2] & /*$$scope*/ 2097152) {
				scrollable_changes.$$scope = { dirty, ctx };
			}

			scrollable.$set(scrollable_changes);

			if (/*tools*/ ctx[6].length) {
				if (if_block) {
					if_block.p(ctx, dirty);

					if (dirty[0] & /*tools*/ 64) {
						transition_in(if_block, 1);
					}
				} else {
					if_block = create_if_block$8(ctx);
					if_block.c();
					transition_in(if_block, 1);
					if_block.m(div0, null);
				}
			} else if (if_block) {
				group_outros();

				transition_out(if_block, 1, 1, () => {
					if_block = null;
				});

				check_outros();
			}

			if (!current || dirty[0] & /*footerStyle*/ 134217728) {
				attr(div0, "style", /*footerStyle*/ ctx[27]);
			}
		},
		i(local) {
			if (current) return;
			transition_in(scrollable.$$.fragment, local);
			transition_in(if_block);
			current = true;
		},
		o(local) {
			transition_out(scrollable.$$.fragment, local);
			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div0);
			/*div1_binding*/ ctx[59](null);
			destroy_component(scrollable);
			if (if_block) if_block.d();
		}
	};
}

// (334:0) <Util on:measure>
function create_default_slot$7(ctx) {
	let t;

	return {
		c() {
			t = space();
		},
		m(target, anchor) {
			insert(target, t, anchor);
		},
		p: noop$1,
		i: noop$1,
		o: noop$1,
		d(detaching) {
			if (detaching) detach(t);
		}
	};
}

function create_fragment$w(ctx) {
	let util;
	let current;

	util = new Util({
			props: {
				$$slots: {
					default: [create_default_slot$7],
					footer: [create_footer_slot$3],
					main: [create_main_slot$1]
				},
				$$scope: { ctx }
			}
		});

	util.$on("measure", /*measure_handler*/ ctx[62]);

	return {
		c() {
			create_component(util.$$.fragment);
		},
		m(target, anchor) {
			mount_component(util, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const util_changes = {};

			if (dirty[0] & /*footerStyle, locale, tools, toolActive, shouldRenderPresets, markupShapeEditorScrollRect, shapePresets, enablePresetSelectImage, markupShapeSelected, shapeControls, detailPanelProps, styleDetailPanelPortal, $parentRect, $rootRect, $stageRect, markupOffset, $presentationScalar, imageRotation, $isActiveFraction, enableButtonFlipVertical, mapScreenPointToImagePoint, mapImagePointToScreenPoint, markupEditor, $shapes, $imageOverlayMarkup, shouldRenderShapeEditor*/ 2147467217 | dirty[2] & /*$$scope*/ 2097152) {
				util_changes.$$scope = { dirty, ctx };
			}

			util.$set(util_changes);
		},
		i(local) {
			if (current) return;
			transition_in(util.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(util.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(util, detaching);
		}
	};
}

function instance$w($$self, $$props, $$invalidate) {
	let $rootRect;

	let $isActive,
		$$unsubscribe_isActive = noop$1,
		$$subscribe_isActive = () => ($$unsubscribe_isActive(), $$unsubscribe_isActive = subscribe(isActive, $$value => $$invalidate(65, $isActive = $$value)), isActive);

	let $imagePreviewModifiers;

	let $isVisible,
		$$unsubscribe_isVisible = noop$1,
		$$subscribe_isVisible = () => ($$unsubscribe_isVisible(), $$unsubscribe_isVisible = subscribe(isVisible, $$value => $$invalidate(68, $isVisible = $$value)), isVisible);

	let $utilRect;
	let $stageRect;

	let $shapes,
		$$unsubscribe_shapes = noop$1,
		$$subscribe_shapes = () => ($$unsubscribe_shapes(), $$unsubscribe_shapes = subscribe(shapes, $$value => $$invalidate(22, $shapes = $$value)), shapes);

	let $imageCropRect;

	let $parentRect,
		$$unsubscribe_parentRect = noop$1,
		$$subscribe_parentRect = () => ($$unsubscribe_parentRect(), $$unsubscribe_parentRect = subscribe(parentRect, $$value => $$invalidate(26, $parentRect = $$value)), parentRect);

	let $footerOffset;
	let $imageOverlayMarkup;
	let $presentationScalar;

	let $isActiveFraction,
		$$unsubscribe_isActiveFraction = noop$1,
		$$subscribe_isActiveFraction = () => ($$unsubscribe_isActiveFraction(), $$unsubscribe_isActiveFraction = subscribe(isActiveFraction, $$value => $$invalidate(30, $isActiveFraction = $$value)), isActiveFraction);

	$$self.$$.on_destroy.push(() => $$unsubscribe_isActive());
	$$self.$$.on_destroy.push(() => $$unsubscribe_isVisible());
	$$self.$$.on_destroy.push(() => $$unsubscribe_shapes());
	$$self.$$.on_destroy.push(() => $$unsubscribe_parentRect());
	$$self.$$.on_destroy.push(() => $$unsubscribe_isActiveFraction());
	let { isActive } = $$props;
	$$subscribe_isActive();
	let { isActiveFraction } = $$props;
	$$subscribe_isActiveFraction();
	let { isVisible } = $$props;
	$$subscribe_isVisible();
	let { stores } = $$props;
	let { locale = {} } = $$props;
	let { shapes = [] } = $$props;
	$$subscribe_shapes();
	let { tools = [] } = $$props;
	let { toolShapes = [] } = $$props;
	let { toolActive = "sharpie" } = $$props;
	let { shapeControls = [] } = $$props;
	let { enableButtonFlipVertical = false } = $$props;
	let { enablePresetSelectImage = true } = $$props;
	let { shapePresets = [] } = $$props;
	let { utilKey } = $$props;
	let { mapScreenPointToImagePoint } = $$props;
	let { mapImagePointToScreenPoint } = $$props;
	let { imageRotation = 0 } = $$props;
	let { parentRect } = $$props;
	$$subscribe_parentRect();
	const { history, rootRect, stageRect, utilRect, elasticityMultiplier, scrollElasticity, imageOverlayMarkup, imagePreviewModifiers, imageCropRect, presentationScalar } = stores;
	component_subscribe($$self, rootRect, value => $$invalidate(18, $rootRect = value));
	component_subscribe($$self, stageRect, value => $$invalidate(21, $stageRect = value));
	component_subscribe($$self, utilRect, value => $$invalidate(69, $utilRect = value));
	component_subscribe($$self, imageOverlayMarkup, value => $$invalidate(28, $imageOverlayMarkup = value));
	component_subscribe($$self, imagePreviewModifiers, value => $$invalidate(66, $imagePreviewModifiers = value));
	component_subscribe($$self, imageCropRect, value => $$invalidate(75, $imageCropRect = value));
	component_subscribe($$self, presentationScalar, value => $$invalidate(29, $presentationScalar = value));
	const updateActiveTool = ({ value }) => $$invalidate(0, toolActive = value);
	const mapScreenEventToImagePoint = e => mapScreenPointToImagePoint(getEventPositionInEditor(e, $rootRect));

	// A reference to the markup editor, we need this so we can call exported functions
	let markupEditor;

	// the current tool styles
	let markupToolStyles = {};

	//
	// Create new markup
	//
	let interactionHandler;

	const handleInteractionStart = e => {
		if (toolShapes[toolActive]) {
			const [shape, options] = toolShapes[toolActive];
			interactionHandler = markupEditor.createShape({ ...shape, ...markupToolStylesComputed }, options);
		} else if (toolActive === "eraser") {
			interactionHandler = markupEditor.eraseShape();
		} else {
			interactionHandler = undefined;
		}

		if (!interactionHandler) return;
		interactionHandler.start(e);
	};

	const handleInteractionUpdate = e => {
		if (!interactionHandler) return;
		interactionHandler.update(e);
	};

	const handleInteractionRelease = e => {
		if (!interactionHandler) return;
		interactionHandler.release(e);
	};

	const handleInteractionEnd = e => {
		if (!interactionHandler) return;
		interactionHandler.end(e);
		interactionHandler = undefined;
	};

	function handleUpdateSelectedMarkupShape(prop, value) {
		// remember style for when creating or styling other element
		$$invalidate(63, markupToolStyles[prop] = value, markupToolStyles);

		// it's possible we're only updating default styles
		if (!markupSelected) return;

		// update element
		markupEditor.updateMarkupShapeProperty(markupSelected, prop, value);

		// update markup style
		handleMarkupUpdate();
	}

	//
	// render style detail panels here
	//
	let markupShapeEditorScrollRect;

	let styleDetailPanelPortal;

	const createPreset = (preset, position) => {
		// position defaults to crop center
		if (!position) position = rectCenter($imageCropRect);

		// adjust offset if parent rect is image crop rect
		position.x -= $parentRect.x || 0;

		position.y -= $parentRect.y || 0;

		// calculate new center if image is rotated
		if (imageRotation !== 0) {
			const size = {
				width: $parentRect.width,
				height: $parentRect.height
			};

			sizeRotate(size, imageRotation);
			const dx = ($parentRect.width - Math.abs(size.width)) * 0.5;
			const dy = ($parentRect.height - Math.abs(size.height)) * 0.5;
			position.x += dx;
			position.y += dy;
		}

		// reposition if there's already shapes at position
		const shapesAlreadyAtPosition = markupEditor.getMarkupItemsAtPosition(position);

		if (shapesAlreadyAtPosition.length) {
			const size = Math.min($imageCropRect.width, $imageCropRect.height) * 0.1;
			position.x += Math.round(-size + Math.random() * size * 2);
			position.y += Math.round(-size + Math.random() * size * 2);
		}

		// create shape
		const shape = shapeCreateFromPreset(preset, $imageCropRect, (error, shape) => {
			// something went wrong, exit!
			if (error) {
				markupEditor.removeShape(shape);
				return console.error(error);
			}

			// reposition shape
			shapeContain(shape, $imageCropRect);

			shapeCenter(shape, position);

			// select if is last shape
			if ($shapes[$shapes.length - 1] === shape) {
				markupEditor.selectShape(shape);
			}
		});

		// de-rotate shape
		shape.rotation = imageRotation !== 0 ? -imageRotation : 0;

		// shape update position
		if (shape.width && shape.height) {
			shapeContain(shape, $imageCropRect);
			shapeCenter(shape, position);
		}

		return shape;
	};

	const addPreset = (preset, position) => {
		const shape = createPreset(preset, position);
		markupEditor.addShape(shape);
		history.write();
		return shape;
	};

	const handleDragPreset = (preset, e) => {
		const position = mapScreenEventToImagePoint(e);

		// create or update if is inside image
		const draft = markupEditor.getMarkupItemDraft();

		const inCropRect = rectContainsPoint($imageCropRect, {
			// adjust offset if parent rect is image crop rect
			x: position.x + ($parentRect.x || 0),
			y: position.y + ($parentRect.y || 0)
		});

		// remove draft if not dragging inside image
		if (draft && !inCropRect) markupEditor.discardMarkupItemDraft();

		// stop here if we're not in the crop rect
		if (!inCropRect) return;

		// create draft shape
		if (!draft) {
			const shape = createPreset(preset, position);
			shapeMakeDraft(shape);
			markupEditor.addShape(shape);
			return;
		}

		// update existing draft
		markupEditor.updateMarkupShape(draft, {
			x: position.x - draft.width * 0.5,
			y: position.y - draft.height * 0.5
		});
	};

	const handleDropPreset = (preset, e) => {
		const position = mapScreenEventToImagePoint(e);

		// adjust offset if parent rect is image crop rect
		if (!rectContainsPoint($imageCropRect, {
			x: position.x + ($parentRect.x || 0),
			y: position.y + ($parentRect.y || 0)
		})) {
			markupEditor.discardMarkupItemDraft();
		} else {
			const shape = markupEditor.confirmMarkupItemDraft();
			markupEditor.selectShape(shape);

			// update history
			history.write();
		}
	};

	const handleAddPreset = preset => addPreset(preset);
	const handleAddFiles = (files, position) => files.forEach(file => addPreset(file, position));
	const handleDropFiles = e => handleAddFiles(e.detail.files, mapScreenEventToImagePoint(e.detail.event));

	//
	// History
	//
	const handleMarkupUpdate = () => {
		history.write();
	};

	//
	// Footer style
	//
	const footerOffset = spring(0);

	component_subscribe($$self, footerOffset, value => $$invalidate(76, $footerOffset = value));

	function shapelayouteditor_binding($$value) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			markupEditor = $$value;
			$$invalidate(15, markupEditor);
		});
	}

	function shapelayouteditor_markup_binding(value) {
		$shapes = value;
		shapes.set($shapes);
	}

	function shapelayouteditor_ui_binding(value) {
		$imageOverlayMarkup = value;
		imageOverlayMarkup.set($imageOverlayMarkup);
	}

	function measure_handler_1(event) {
		bubble($$self, event);
	}

	function div1_binding($$value) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			styleDetailPanelPortal = $$value;
			$$invalidate(17, styleDetailPanelPortal);
		});
	}

	const measure_handler_2 = e => $$invalidate(16, markupShapeEditorScrollRect = e.detail);
	const func = option => option[0] === toolActive;

	function measure_handler(event) {
		bubble($$self, event);
	}

	$$self.$$set = $$props => {
		if ("isActive" in $$props) $$subscribe_isActive($$invalidate(1, isActive = $$props.isActive));
		if ("isActiveFraction" in $$props) $$subscribe_isActiveFraction($$invalidate(2, isActiveFraction = $$props.isActiveFraction));
		if ("isVisible" in $$props) $$subscribe_isVisible($$invalidate(3, isVisible = $$props.isVisible));
		if ("stores" in $$props) $$invalidate(52, stores = $$props.stores);
		if ("locale" in $$props) $$invalidate(4, locale = $$props.locale);
		if ("shapes" in $$props) $$subscribe_shapes($$invalidate(5, shapes = $$props.shapes));
		if ("tools" in $$props) $$invalidate(6, tools = $$props.tools);
		if ("toolShapes" in $$props) $$invalidate(53, toolShapes = $$props.toolShapes);
		if ("toolActive" in $$props) $$invalidate(0, toolActive = $$props.toolActive);
		if ("shapeControls" in $$props) $$invalidate(7, shapeControls = $$props.shapeControls);
		if ("enableButtonFlipVertical" in $$props) $$invalidate(8, enableButtonFlipVertical = $$props.enableButtonFlipVertical);
		if ("enablePresetSelectImage" in $$props) $$invalidate(9, enablePresetSelectImage = $$props.enablePresetSelectImage);
		if ("shapePresets" in $$props) $$invalidate(10, shapePresets = $$props.shapePresets);
		if ("utilKey" in $$props) $$invalidate(54, utilKey = $$props.utilKey);
		if ("mapScreenPointToImagePoint" in $$props) $$invalidate(11, mapScreenPointToImagePoint = $$props.mapScreenPointToImagePoint);
		if ("mapImagePointToScreenPoint" in $$props) $$invalidate(12, mapImagePointToScreenPoint = $$props.mapImagePointToScreenPoint);
		if ("imageRotation" in $$props) $$invalidate(13, imageRotation = $$props.imageRotation);
		if ("parentRect" in $$props) $$subscribe_parentRect($$invalidate(14, parentRect = $$props.parentRect));
	};

	let shouldRenderShapeEditor;
	let markupOffset;
	let markupBounds;
	let markupShapeKeys;
	let markupSelected;
	let markupToolShape;
	let markupToolStylesComputed;
	let markupShapeSelected;
	let styleDetailPanelOffset;
	let detailPanelProps;
	let shouldRenderPresets;
	let footerStyle;

	$$self.$$.update = () => {
		if ($$self.$$.dirty[1] & /*utilKey*/ 8388608 | $$self.$$.dirty[2] & /*$isActive, $imagePreviewModifiers*/ 24) {
			//
			// enable seeing the markup outlines outside of the crop area
			// 
			 if ($isActive) {
				set_store_value(imagePreviewModifiers, $imagePreviewModifiers[utilKey] = { maskMarkupOpacity: 0.85 }, $imagePreviewModifiers);
			} else {
				delete $imagePreviewModifiers[utilKey];
			}
		}

		if ($$self.$$.dirty[2] & /*$isVisible*/ 64) {
			 $$invalidate(19, shouldRenderShapeEditor = $isVisible);
		}

		if ($$self.$$.dirty[0] & /*$stageRect*/ 2097152 | $$self.$$.dirty[2] & /*$utilRect*/ 128) {
			 $$invalidate(20, markupOffset = $utilRect && vectorCreate($stageRect.x - $utilRect.x, $stageRect.y - $utilRect.y));
		}

		if ($$self.$$.dirty[0] & /*$rootRect*/ 262144 | $$self.$$.dirty[2] & /*$utilRect*/ 128) {
			 $$invalidate(70, markupBounds = $utilRect && {
				x: 16,
				y: 16,
				width: $rootRect.width - 32,
				height: $rootRect.height - 32
			});
		}

		if ($$self.$$.dirty[0] & /*toolActive, markupEditor*/ 32769) {
			// if tool is changed, deselect markup items
			 if (toolActive && markupEditor) markupEditor.blurShapes();
		}

		if ($$self.$$.dirty[0] & /*shapeControls*/ 128) {
			 $$invalidate(71, markupShapeKeys = Object.keys(shapeControls));
		}

		if ($$self.$$.dirty[0] & /*$shapes*/ 4194304) {
			 $$invalidate(72, markupSelected = ($shapes.filter(shapeIsSelected) || [])[0]);
		}

		if ($$self.$$.dirty[0] & /*toolActive*/ 1 | $$self.$$.dirty[1] & /*toolShapes*/ 4194304 | $$self.$$.dirty[2] & /*$isActive*/ 8) {
			 $$invalidate(73, markupToolShape = $isActive && (toolShapes[toolActive]
			? shapeFormat(shapeDeepCopy(toolShapes[toolActive][0]))
			: {}));
		}

		if ($$self.$$.dirty[2] & /*markupToolShape, markupShapeKeys, markupToolStyles*/ 2562) {
			 $$invalidate(67, markupToolStylesComputed = markupToolShape && Object.keys(markupToolShape).reduce(
				(prev, key) => {
					// skip props that can't be styled
					if (!markupShapeKeys.includes(key)) return prev;

					// apply this style
					prev[key] = markupToolStyles[key] || markupToolShape[key];

					return prev;
				},
				{}
			));
		}

		if ($$self.$$.dirty[2] & /*markupSelected, markupToolStylesComputed*/ 1056) {
			 $$invalidate(23, markupShapeSelected = markupSelected || markupToolStylesComputed);
		}

		if ($$self.$$.dirty[0] & /*markupShapeEditorScrollRect, markupOffset*/ 1114112 | $$self.$$.dirty[2] & /*$isActive*/ 8) {
			 $$invalidate(74, styleDetailPanelOffset = $isActive && markupShapeEditorScrollRect && markupOffset && {
				x: markupShapeEditorScrollRect.x + markupOffset.x,
				y: markupShapeEditorScrollRect.y + markupOffset.y
			});
		}

		if ($$self.$$.dirty[0] & /*styleDetailPanelPortal, markupOffset*/ 1179648 | $$self.$$.dirty[2] & /*$isActive, markupBounds, styleDetailPanelOffset*/ 4360) {
			 $$invalidate(24, detailPanelProps = $isActive && {
				portal: styleDetailPanelPortal,
				viewBounds: markupBounds,
				viewOffset: styleDetailPanelOffset,
				portalOffset: markupOffset
			});
		}

		if ($$self.$$.dirty[0] & /*toolActive, shapePresets, enablePresetSelectImage*/ 1537 | $$self.$$.dirty[2] & /*$isActive*/ 8) {
			//
			// presets
			//
			 $$invalidate(25, shouldRenderPresets = $isActive && toolActive === "preset" && (shapePresets.length > 0 || enablePresetSelectImage));
		}

		if ($$self.$$.dirty[2] & /*$isActive*/ 8) {
			 footerOffset.set($isActive ? 0 : 20);
		}

		if ($$self.$$.dirty[2] & /*$footerOffset*/ 16384) {
			 $$invalidate(27, footerStyle = $footerOffset
			? `transform: translateY(${$footerOffset}px)`
			: undefined);
		}
	};

	return [
		toolActive,
		isActive,
		isActiveFraction,
		isVisible,
		locale,
		shapes,
		tools,
		shapeControls,
		enableButtonFlipVertical,
		enablePresetSelectImage,
		shapePresets,
		mapScreenPointToImagePoint,
		mapImagePointToScreenPoint,
		imageRotation,
		parentRect,
		markupEditor,
		markupShapeEditorScrollRect,
		styleDetailPanelPortal,
		$rootRect,
		shouldRenderShapeEditor,
		markupOffset,
		$stageRect,
		$shapes,
		markupShapeSelected,
		detailPanelProps,
		shouldRenderPresets,
		$parentRect,
		footerStyle,
		$imageOverlayMarkup,
		$presentationScalar,
		$isActiveFraction,
		rootRect,
		stageRect,
		utilRect,
		elasticityMultiplier,
		scrollElasticity,
		imageOverlayMarkup,
		imagePreviewModifiers,
		imageCropRect,
		presentationScalar,
		updateActiveTool,
		handleInteractionStart,
		handleInteractionUpdate,
		handleInteractionRelease,
		handleInteractionEnd,
		handleUpdateSelectedMarkupShape,
		handleDragPreset,
		handleDropPreset,
		handleAddPreset,
		handleDropFiles,
		handleMarkupUpdate,
		footerOffset,
		stores,
		toolShapes,
		utilKey,
		shapelayouteditor_binding,
		shapelayouteditor_markup_binding,
		shapelayouteditor_ui_binding,
		measure_handler_1,
		div1_binding,
		measure_handler_2,
		func,
		measure_handler
	];
}

class ShapeUtil extends SvelteComponent {
	constructor(options) {
		super();

		init(
			this,
			options,
			instance$w,
			create_fragment$w,
			safe_not_equal,
			{
				isActive: 1,
				isActiveFraction: 2,
				isVisible: 3,
				stores: 52,
				locale: 4,
				shapes: 5,
				tools: 6,
				toolShapes: 53,
				toolActive: 0,
				shapeControls: 7,
				enableButtonFlipVertical: 8,
				enablePresetSelectImage: 9,
				shapePresets: 10,
				utilKey: 54,
				mapScreenPointToImagePoint: 11,
				mapImagePointToScreenPoint: 12,
				imageRotation: 13,
				parentRect: 14
			},
			[-1, -1, -1]
		);
	}

	get isActive() {
		return this.$$.ctx[1];
	}

	set isActive(isActive) {
		this.$set({ isActive });
		flush();
	}

	get isActiveFraction() {
		return this.$$.ctx[2];
	}

	set isActiveFraction(isActiveFraction) {
		this.$set({ isActiveFraction });
		flush();
	}

	get isVisible() {
		return this.$$.ctx[3];
	}

	set isVisible(isVisible) {
		this.$set({ isVisible });
		flush();
	}

	get stores() {
		return this.$$.ctx[52];
	}

	set stores(stores) {
		this.$set({ stores });
		flush();
	}

	get locale() {
		return this.$$.ctx[4];
	}

	set locale(locale) {
		this.$set({ locale });
		flush();
	}

	get shapes() {
		return this.$$.ctx[5];
	}

	set shapes(shapes) {
		this.$set({ shapes });
		flush();
	}

	get tools() {
		return this.$$.ctx[6];
	}

	set tools(tools) {
		this.$set({ tools });
		flush();
	}

	get toolShapes() {
		return this.$$.ctx[53];
	}

	set toolShapes(toolShapes) {
		this.$set({ toolShapes });
		flush();
	}

	get toolActive() {
		return this.$$.ctx[0];
	}

	set toolActive(toolActive) {
		this.$set({ toolActive });
		flush();
	}

	get shapeControls() {
		return this.$$.ctx[7];
	}

	set shapeControls(shapeControls) {
		this.$set({ shapeControls });
		flush();
	}

	get enableButtonFlipVertical() {
		return this.$$.ctx[8];
	}

	set enableButtonFlipVertical(enableButtonFlipVertical) {
		this.$set({ enableButtonFlipVertical });
		flush();
	}

	get enablePresetSelectImage() {
		return this.$$.ctx[9];
	}

	set enablePresetSelectImage(enablePresetSelectImage) {
		this.$set({ enablePresetSelectImage });
		flush();
	}

	get shapePresets() {
		return this.$$.ctx[10];
	}

	set shapePresets(shapePresets) {
		this.$set({ shapePresets });
		flush();
	}

	get utilKey() {
		return this.$$.ctx[54];
	}

	set utilKey(utilKey) {
		this.$set({ utilKey });
		flush();
	}

	get mapScreenPointToImagePoint() {
		return this.$$.ctx[11];
	}

	set mapScreenPointToImagePoint(mapScreenPointToImagePoint) {
		this.$set({ mapScreenPointToImagePoint });
		flush();
	}

	get mapImagePointToScreenPoint() {
		return this.$$.ctx[12];
	}

	set mapImagePointToScreenPoint(mapImagePointToScreenPoint) {
		this.$set({ mapImagePointToScreenPoint });
		flush();
	}

	get imageRotation() {
		return this.$$.ctx[13];
	}

	set imageRotation(imageRotation) {
		this.$set({ imageRotation });
		flush();
	}

	get parentRect() {
		return this.$$.ctx[14];
	}

	set parentRect(parentRect) {
		this.$set({ parentRect });
		flush();
	}
}

var _mapImagePointToScreenPoint = (imagePoint, canvasSize, imageSize, imageOrigin, imageTranslation, imageRotation, imageScalar, imageFlipX, imageFlipY) => {
    const mappedPoint = vectorClone(imagePoint);
    const imageCenterX = imageSize.width * 0.5;
    const imageCenterY = imageSize.height * 0.5;
    const canvasCenterX = canvasSize.width * 0.5;
    const canvasCenterY = canvasSize.height * 0.5;
    const imageOffsetX = imageTranslation.x + imageOrigin.x;
    const imageOffsetY = imageTranslation.y + imageOrigin.y;
    if (imageFlipX)
        mappedPoint.x = imageSize.width - mappedPoint.x;
    if (imageFlipY)
        mappedPoint.y = imageSize.height - mappedPoint.y;
    // rotate around image center based on if image is rotated
    const c = Math.cos(imageRotation);
    const s = Math.sin(imageRotation);
    mappedPoint.x -= imageCenterX;
    mappedPoint.y -= imageCenterY;
    const rx = mappedPoint.x * c - mappedPoint.y * s;
    const ry = mappedPoint.x * s + mappedPoint.y * c;
    mappedPoint.x = imageCenterX + rx;
    mappedPoint.y = imageCenterY + ry;
    // position based on screen transforms
    mappedPoint.x *= imageScalar;
    mappedPoint.y *= imageScalar;
    mappedPoint.x += canvasCenterX;
    mappedPoint.y += canvasCenterY;
    mappedPoint.x += imageOffsetX;
    mappedPoint.y += imageOffsetY;
    mappedPoint.x -= imageCenterX * imageScalar;
    mappedPoint.y -= imageCenterY * imageScalar;
    const tx = (imageTranslation.x - imageOffsetX) * imageScalar;
    const ty = (imageTranslation.y - imageOffsetY) * imageScalar;
    const rtx = tx * c - ty * s;
    const rty = tx * s + ty * c;
    mappedPoint.x += rtx;
    mappedPoint.y += rty;
    return mappedPoint;
};

var _mapScreenPointToImagePoint = (screenPoint, canvasSize, imageSize, imageOrigin, imageTranslation, imageRotation, imageScalar, imageFlipX, imageFlipY) => {
    const mappedPoint = vectorClone(screenPoint);
    const imageCenter = sizeCenter(imageSize);
    const canvasCenter = sizeCenter(canvasSize);
    const imageOffset = vectorCreate(imageTranslation.x + imageOrigin.x, imageTranslation.y + imageOrigin.y);
    const c = Math.cos(imageRotation);
    const s = Math.sin(imageRotation);
    mappedPoint.x -= canvasCenter.x;
    mappedPoint.y -= canvasCenter.y;
    const tx = (imageTranslation.x - imageOffset.x) * imageScalar;
    const ty = (imageTranslation.y - imageOffset.y) * imageScalar;
    const rtx = tx * c - ty * s;
    const rty = tx * s + ty * c;
    mappedPoint.x -= rtx;
    mappedPoint.y -= rty;
    mappedPoint.x -= imageOffset.x;
    mappedPoint.y -= imageOffset.y;
    mappedPoint.x /= imageScalar;
    mappedPoint.y /= imageScalar;
    const rx = mappedPoint.x * c + mappedPoint.y * s;
    const ry = mappedPoint.x * s - mappedPoint.y * c;
    mappedPoint.x = rx;
    mappedPoint.y = -ry;
    mappedPoint.x += imageCenter.x;
    mappedPoint.y += imageCenter.y;
    if (imageFlipX)
        mappedPoint.x = imageSize.width - mappedPoint.x;
    if (imageFlipY)
        mappedPoint.y = imageSize.height - mappedPoint.y;
    return mappedPoint;
};

/* src/native/ui/plugins/annotate/index.svelte generated by Svelte v3.29.4 */

function create_fragment$x(ctx) {
	let shapeutil;
	let current;

	shapeutil = new ShapeUtil({
			props: {
				stores: /*stores*/ ctx[3],
				locale: /*locale*/ ctx[4],
				isActive: /*isActive*/ ctx[0],
				isActiveFraction: /*isActiveFraction*/ ctx[1],
				isVisible: /*isVisible*/ ctx[2],
				utilKey: "annotate",
				imageRotation: /*$imageRotation*/ ctx[12],
				shapes: /*imageAnnotation*/ ctx[14],
				tools: /*annotateTools*/ ctx[5],
				toolShapes: /*annotateToolShapes*/ ctx[6],
				toolActive: /*annotateActiveTool*/ ctx[8],
				shapeControls: /*annotateShapeControls*/ ctx[7],
				shapePresets: /*annotatePresets*/ ctx[11],
				enableButtonFlipVertical: /*annotateEnableButtonFlipVertical*/ ctx[9],
				parentRect: /*imageSize*/ ctx[15],
				enablePresetSelectImage: /*annotateEnableSelectImagePreset*/ ctx[10],
				mapScreenPointToImagePoint: /*mapScreenPointToImagePoint*/ ctx[20],
				mapImagePointToScreenPoint: /*mapImagePointToScreenPoint*/ ctx[21]
			}
		});

	shapeutil.$on("measure", /*measure_handler*/ ctx[23]);

	return {
		c() {
			create_component(shapeutil.$$.fragment);
		},
		m(target, anchor) {
			mount_component(shapeutil, target, anchor);
			current = true;
		},
		p(ctx, [dirty]) {
			const shapeutil_changes = {};
			if (dirty & /*stores*/ 8) shapeutil_changes.stores = /*stores*/ ctx[3];
			if (dirty & /*locale*/ 16) shapeutil_changes.locale = /*locale*/ ctx[4];
			if (dirty & /*isActive*/ 1) shapeutil_changes.isActive = /*isActive*/ ctx[0];
			if (dirty & /*isActiveFraction*/ 2) shapeutil_changes.isActiveFraction = /*isActiveFraction*/ ctx[1];
			if (dirty & /*isVisible*/ 4) shapeutil_changes.isVisible = /*isVisible*/ ctx[2];
			if (dirty & /*$imageRotation*/ 4096) shapeutil_changes.imageRotation = /*$imageRotation*/ ctx[12];
			if (dirty & /*annotateTools*/ 32) shapeutil_changes.tools = /*annotateTools*/ ctx[5];
			if (dirty & /*annotateToolShapes*/ 64) shapeutil_changes.toolShapes = /*annotateToolShapes*/ ctx[6];
			if (dirty & /*annotateActiveTool*/ 256) shapeutil_changes.toolActive = /*annotateActiveTool*/ ctx[8];
			if (dirty & /*annotateShapeControls*/ 128) shapeutil_changes.shapeControls = /*annotateShapeControls*/ ctx[7];
			if (dirty & /*annotatePresets*/ 2048) shapeutil_changes.shapePresets = /*annotatePresets*/ ctx[11];
			if (dirty & /*annotateEnableButtonFlipVertical*/ 512) shapeutil_changes.enableButtonFlipVertical = /*annotateEnableButtonFlipVertical*/ ctx[9];
			if (dirty & /*annotateEnableSelectImagePreset*/ 1024) shapeutil_changes.enablePresetSelectImage = /*annotateEnableSelectImagePreset*/ ctx[10];
			shapeutil.$set(shapeutil_changes);
		},
		i(local) {
			if (current) return;
			transition_in(shapeutil.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(shapeutil.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(shapeutil, detaching);
		}
	};
}

function instance$x($$self, $$props, $$invalidate) {
	let $rootRect;
	let $imageSize;
	let $imageTransforms;
	let $imageFlipX;
	let $imageFlipY;
	let $imageRotation;
	const name = "annotate";
	let { isActive } = $$props;
	let { isActiveFraction } = $$props;
	let { isVisible } = $$props;
	let { stores } = $$props;
	let { locale = {} } = $$props;
	let { annotateTools } = $$props;
	let { annotateToolShapes } = $$props;
	let { annotateShapeControls } = $$props;
	let { annotateActiveTool = "sharpie" } = $$props;
	let { annotateEnableButtonFlipVertical = false } = $$props;
	let { annotateEnableSelectImagePreset = false } = $$props;
	let { annotatePresets = [] } = $$props;

	// connect filter choice to stores
	const { rootRect, imageAnnotation, imageSize, imageTransforms, imageRotation, imageFlipX, imageFlipY } = stores;

	component_subscribe($$self, rootRect, value => $$invalidate(24, $rootRect = value));
	component_subscribe($$self, imageSize, value => $$invalidate(25, $imageSize = value));
	component_subscribe($$self, imageTransforms, value => $$invalidate(26, $imageTransforms = value));
	component_subscribe($$self, imageRotation, value => $$invalidate(12, $imageRotation = value));
	component_subscribe($$self, imageFlipX, value => $$invalidate(27, $imageFlipX = value));
	component_subscribe($$self, imageFlipY, value => $$invalidate(28, $imageFlipY = value));

	//
	// Mapping coordinates
	//
	const mapScreenPointToImagePoint = point => _mapScreenPointToImagePoint(point, $rootRect, $imageSize, $imageTransforms.origin, $imageTransforms.translation, $imageTransforms.rotation.z, $imageTransforms.scale, $imageFlipX, $imageFlipY);

	const mapImagePointToScreenPoint = point => _mapImagePointToScreenPoint(point, $rootRect, $imageSize, $imageTransforms.origin, $imageTransforms.translation, $imageTransforms.rotation.z, $imageTransforms.scale, $imageFlipX, $imageFlipY);

	function measure_handler(event) {
		bubble($$self, event);
	}

	$$self.$$set = $$props => {
		if ("isActive" in $$props) $$invalidate(0, isActive = $$props.isActive);
		if ("isActiveFraction" in $$props) $$invalidate(1, isActiveFraction = $$props.isActiveFraction);
		if ("isVisible" in $$props) $$invalidate(2, isVisible = $$props.isVisible);
		if ("stores" in $$props) $$invalidate(3, stores = $$props.stores);
		if ("locale" in $$props) $$invalidate(4, locale = $$props.locale);
		if ("annotateTools" in $$props) $$invalidate(5, annotateTools = $$props.annotateTools);
		if ("annotateToolShapes" in $$props) $$invalidate(6, annotateToolShapes = $$props.annotateToolShapes);
		if ("annotateShapeControls" in $$props) $$invalidate(7, annotateShapeControls = $$props.annotateShapeControls);
		if ("annotateActiveTool" in $$props) $$invalidate(8, annotateActiveTool = $$props.annotateActiveTool);
		if ("annotateEnableButtonFlipVertical" in $$props) $$invalidate(9, annotateEnableButtonFlipVertical = $$props.annotateEnableButtonFlipVertical);
		if ("annotateEnableSelectImagePreset" in $$props) $$invalidate(10, annotateEnableSelectImagePreset = $$props.annotateEnableSelectImagePreset);
		if ("annotatePresets" in $$props) $$invalidate(11, annotatePresets = $$props.annotatePresets);
	};

	return [
		isActive,
		isActiveFraction,
		isVisible,
		stores,
		locale,
		annotateTools,
		annotateToolShapes,
		annotateShapeControls,
		annotateActiveTool,
		annotateEnableButtonFlipVertical,
		annotateEnableSelectImagePreset,
		annotatePresets,
		$imageRotation,
		rootRect,
		imageAnnotation,
		imageSize,
		imageTransforms,
		imageRotation,
		imageFlipX,
		imageFlipY,
		mapScreenPointToImagePoint,
		mapImagePointToScreenPoint,
		name,
		measure_handler
	];
}

class Annotate extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$x, create_fragment$x, safe_not_equal, {
			name: 22,
			isActive: 0,
			isActiveFraction: 1,
			isVisible: 2,
			stores: 3,
			locale: 4,
			annotateTools: 5,
			annotateToolShapes: 6,
			annotateShapeControls: 7,
			annotateActiveTool: 8,
			annotateEnableButtonFlipVertical: 9,
			annotateEnableSelectImagePreset: 10,
			annotatePresets: 11
		});
	}

	get name() {
		return this.$$.ctx[22];
	}

	get isActive() {
		return this.$$.ctx[0];
	}

	set isActive(isActive) {
		this.$set({ isActive });
		flush();
	}

	get isActiveFraction() {
		return this.$$.ctx[1];
	}

	set isActiveFraction(isActiveFraction) {
		this.$set({ isActiveFraction });
		flush();
	}

	get isVisible() {
		return this.$$.ctx[2];
	}

	set isVisible(isVisible) {
		this.$set({ isVisible });
		flush();
	}

	get stores() {
		return this.$$.ctx[3];
	}

	set stores(stores) {
		this.$set({ stores });
		flush();
	}

	get locale() {
		return this.$$.ctx[4];
	}

	set locale(locale) {
		this.$set({ locale });
		flush();
	}

	get annotateTools() {
		return this.$$.ctx[5];
	}

	set annotateTools(annotateTools) {
		this.$set({ annotateTools });
		flush();
	}

	get annotateToolShapes() {
		return this.$$.ctx[6];
	}

	set annotateToolShapes(annotateToolShapes) {
		this.$set({ annotateToolShapes });
		flush();
	}

	get annotateShapeControls() {
		return this.$$.ctx[7];
	}

	set annotateShapeControls(annotateShapeControls) {
		this.$set({ annotateShapeControls });
		flush();
	}

	get annotateActiveTool() {
		return this.$$.ctx[8];
	}

	set annotateActiveTool(annotateActiveTool) {
		this.$set({ annotateActiveTool });
		flush();
	}

	get annotateEnableButtonFlipVertical() {
		return this.$$.ctx[9];
	}

	set annotateEnableButtonFlipVertical(annotateEnableButtonFlipVertical) {
		this.$set({ annotateEnableButtonFlipVertical });
		flush();
	}

	get annotateEnableSelectImagePreset() {
		return this.$$.ctx[10];
	}

	set annotateEnableSelectImagePreset(annotateEnableSelectImagePreset) {
		this.$set({ annotateEnableSelectImagePreset });
		flush();
	}

	get annotatePresets() {
		return this.$$.ctx[11];
	}

	set annotatePresets(annotatePresets) {
		this.$set({ annotatePresets });
		flush();
	}
}

// @ts-ignore
var index$3 = { util: ['annotate', Annotate] };

/* src/native/ui/plugins/decorate/index.svelte generated by Svelte v3.29.4 */

function create_fragment$y(ctx) {
	let shapeutil;
	let current;

	shapeutil = new ShapeUtil({
			props: {
				stores: /*stores*/ ctx[3],
				locale: /*locale*/ ctx[4],
				isActive: /*isActive*/ ctx[0],
				isActiveFraction: /*isActiveFraction*/ ctx[1],
				isVisible: /*isVisible*/ ctx[2],
				utilKey: "decorate",
				shapes: /*imageDecoration*/ ctx[13],
				tools: /*decorateTools*/ ctx[5],
				toolShapes: /*decorateToolShapes*/ ctx[6],
				toolActive: /*decorateActiveTool*/ ctx[7],
				shapeControls: /*decorateShapeControls*/ ctx[8],
				shapePresets: /*decoratePresets*/ ctx[11],
				enableButtonFlipVertical: /*decorateEnableButtonFlipVertical*/ ctx[9],
				parentRect: /*imageCropRect*/ ctx[12],
				enablePresetSelectImage: /*decorateEnableSelectImagePreset*/ ctx[10],
				mapScreenPointToImagePoint: /*mapScreenPointToImagePoint*/ ctx[16],
				mapImagePointToScreenPoint: /*mapImagePointToScreenPoint*/ ctx[17]
			}
		});

	shapeutil.$on("measure", /*measure_handler*/ ctx[19]);

	return {
		c() {
			create_component(shapeutil.$$.fragment);
		},
		m(target, anchor) {
			mount_component(shapeutil, target, anchor);
			current = true;
		},
		p(ctx, [dirty]) {
			const shapeutil_changes = {};
			if (dirty & /*stores*/ 8) shapeutil_changes.stores = /*stores*/ ctx[3];
			if (dirty & /*locale*/ 16) shapeutil_changes.locale = /*locale*/ ctx[4];
			if (dirty & /*isActive*/ 1) shapeutil_changes.isActive = /*isActive*/ ctx[0];
			if (dirty & /*isActiveFraction*/ 2) shapeutil_changes.isActiveFraction = /*isActiveFraction*/ ctx[1];
			if (dirty & /*isVisible*/ 4) shapeutil_changes.isVisible = /*isVisible*/ ctx[2];
			if (dirty & /*decorateTools*/ 32) shapeutil_changes.tools = /*decorateTools*/ ctx[5];
			if (dirty & /*decorateToolShapes*/ 64) shapeutil_changes.toolShapes = /*decorateToolShapes*/ ctx[6];
			if (dirty & /*decorateActiveTool*/ 128) shapeutil_changes.toolActive = /*decorateActiveTool*/ ctx[7];
			if (dirty & /*decorateShapeControls*/ 256) shapeutil_changes.shapeControls = /*decorateShapeControls*/ ctx[8];
			if (dirty & /*decoratePresets*/ 2048) shapeutil_changes.shapePresets = /*decoratePresets*/ ctx[11];
			if (dirty & /*decorateEnableButtonFlipVertical*/ 512) shapeutil_changes.enableButtonFlipVertical = /*decorateEnableButtonFlipVertical*/ ctx[9];
			if (dirty & /*decorateEnableSelectImagePreset*/ 1024) shapeutil_changes.enablePresetSelectImage = /*decorateEnableSelectImagePreset*/ ctx[10];
			shapeutil.$set(shapeutil_changes);
		},
		i(local) {
			if (current) return;
			transition_in(shapeutil.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(shapeutil.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(shapeutil, detaching);
		}
	};
}

function instance$y($$self, $$props, $$invalidate) {
	let $imageSelectionRectPresentation;
	let $presentationScalar;
	const name = "decorate";
	let { isActive } = $$props;
	let { isActiveFraction } = $$props;
	let { isVisible } = $$props;
	let { stores } = $$props;
	let { locale = {} } = $$props;
	let { decorateTools } = $$props;
	let { decorateToolShapes } = $$props;
	let { decorateActiveTool = "sharpie" } = $$props;
	let { decorateShapeControls } = $$props;
	let { decorateEnableButtonFlipVertical = false } = $$props;
	let { decorateEnableSelectImagePreset = false } = $$props;
	let { decoratePresets = [] } = $$props;
	const { imageCropRect, imageDecoration, imageSelectionRectPresentation, presentationScalar } = stores;
	component_subscribe($$self, imageSelectionRectPresentation, value => $$invalidate(20, $imageSelectionRectPresentation = value));
	component_subscribe($$self, presentationScalar, value => $$invalidate(21, $presentationScalar = value));

	const mapScreenPointToImagePoint = screenPoint => {
		const mappedPoint = vectorClone(screenPoint);
		mappedPoint.x -= $imageSelectionRectPresentation.x;
		mappedPoint.y -= $imageSelectionRectPresentation.y;
		mappedPoint.x /= $presentationScalar;
		mappedPoint.y /= $presentationScalar;
		return mappedPoint;
	};

	const mapImagePointToScreenPoint = imagePoint => {
		const mappedPoint = vectorClone(imagePoint);
		mappedPoint.x *= $presentationScalar;
		mappedPoint.y *= $presentationScalar;
		mappedPoint.x += $imageSelectionRectPresentation.x;
		mappedPoint.y += $imageSelectionRectPresentation.y;
		return mappedPoint;
	};

	function measure_handler(event) {
		bubble($$self, event);
	}

	$$self.$$set = $$props => {
		if ("isActive" in $$props) $$invalidate(0, isActive = $$props.isActive);
		if ("isActiveFraction" in $$props) $$invalidate(1, isActiveFraction = $$props.isActiveFraction);
		if ("isVisible" in $$props) $$invalidate(2, isVisible = $$props.isVisible);
		if ("stores" in $$props) $$invalidate(3, stores = $$props.stores);
		if ("locale" in $$props) $$invalidate(4, locale = $$props.locale);
		if ("decorateTools" in $$props) $$invalidate(5, decorateTools = $$props.decorateTools);
		if ("decorateToolShapes" in $$props) $$invalidate(6, decorateToolShapes = $$props.decorateToolShapes);
		if ("decorateActiveTool" in $$props) $$invalidate(7, decorateActiveTool = $$props.decorateActiveTool);
		if ("decorateShapeControls" in $$props) $$invalidate(8, decorateShapeControls = $$props.decorateShapeControls);
		if ("decorateEnableButtonFlipVertical" in $$props) $$invalidate(9, decorateEnableButtonFlipVertical = $$props.decorateEnableButtonFlipVertical);
		if ("decorateEnableSelectImagePreset" in $$props) $$invalidate(10, decorateEnableSelectImagePreset = $$props.decorateEnableSelectImagePreset);
		if ("decoratePresets" in $$props) $$invalidate(11, decoratePresets = $$props.decoratePresets);
	};

	return [
		isActive,
		isActiveFraction,
		isVisible,
		stores,
		locale,
		decorateTools,
		decorateToolShapes,
		decorateActiveTool,
		decorateShapeControls,
		decorateEnableButtonFlipVertical,
		decorateEnableSelectImagePreset,
		decoratePresets,
		imageCropRect,
		imageDecoration,
		imageSelectionRectPresentation,
		presentationScalar,
		mapScreenPointToImagePoint,
		mapImagePointToScreenPoint,
		name,
		measure_handler
	];
}

class Decorate extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$y, create_fragment$y, safe_not_equal, {
			name: 18,
			isActive: 0,
			isActiveFraction: 1,
			isVisible: 2,
			stores: 3,
			locale: 4,
			decorateTools: 5,
			decorateToolShapes: 6,
			decorateActiveTool: 7,
			decorateShapeControls: 8,
			decorateEnableButtonFlipVertical: 9,
			decorateEnableSelectImagePreset: 10,
			decoratePresets: 11
		});
	}

	get name() {
		return this.$$.ctx[18];
	}

	get isActive() {
		return this.$$.ctx[0];
	}

	set isActive(isActive) {
		this.$set({ isActive });
		flush();
	}

	get isActiveFraction() {
		return this.$$.ctx[1];
	}

	set isActiveFraction(isActiveFraction) {
		this.$set({ isActiveFraction });
		flush();
	}

	get isVisible() {
		return this.$$.ctx[2];
	}

	set isVisible(isVisible) {
		this.$set({ isVisible });
		flush();
	}

	get stores() {
		return this.$$.ctx[3];
	}

	set stores(stores) {
		this.$set({ stores });
		flush();
	}

	get locale() {
		return this.$$.ctx[4];
	}

	set locale(locale) {
		this.$set({ locale });
		flush();
	}

	get decorateTools() {
		return this.$$.ctx[5];
	}

	set decorateTools(decorateTools) {
		this.$set({ decorateTools });
		flush();
	}

	get decorateToolShapes() {
		return this.$$.ctx[6];
	}

	set decorateToolShapes(decorateToolShapes) {
		this.$set({ decorateToolShapes });
		flush();
	}

	get decorateActiveTool() {
		return this.$$.ctx[7];
	}

	set decorateActiveTool(decorateActiveTool) {
		this.$set({ decorateActiveTool });
		flush();
	}

	get decorateShapeControls() {
		return this.$$.ctx[8];
	}

	set decorateShapeControls(decorateShapeControls) {
		this.$set({ decorateShapeControls });
		flush();
	}

	get decorateEnableButtonFlipVertical() {
		return this.$$.ctx[9];
	}

	set decorateEnableButtonFlipVertical(decorateEnableButtonFlipVertical) {
		this.$set({ decorateEnableButtonFlipVertical });
		flush();
	}

	get decorateEnableSelectImagePreset() {
		return this.$$.ctx[10];
	}

	set decorateEnableSelectImagePreset(decorateEnableSelectImagePreset) {
		this.$set({ decorateEnableSelectImagePreset });
		flush();
	}

	get decoratePresets() {
		return this.$$.ctx[11];
	}

	set decoratePresets(decoratePresets) {
		this.$set({ decoratePresets });
		flush();
	}
}

// @ts-ignore
var index$4 = { util: ['decorate', Decorate] };

/* src/native/ui/plugins/sticker/index.svelte generated by Svelte v3.29.4 */

function create_fragment$z(ctx) {
	let shapeutil;
	let current;

	shapeutil = new ShapeUtil({
			props: {
				stores: /*stores*/ ctx[3],
				locale: /*locale*/ ctx[4],
				isActive: /*isActive*/ ctx[0],
				isActiveFraction: /*isActiveFraction*/ ctx[1],
				isVisible: /*isVisible*/ ctx[2],
				utilKey: "sticker",
				shapeControls: /*stickerShapeControls*/ ctx[8],
				shapePresets: /*stickers*/ ctx[5],
				shapes: /*stickerStickToImage*/ ctx[6]
				? /*imageAnnotation*/ ctx[15]
				: /*imageDecoration*/ ctx[16],
				toolActive: "preset",
				imageRotation: /*stickerStickToImage*/ ctx[6]
				? /*$imageRotation*/ ctx[10]
				: 0,
				parentRect: /*stickerStickToImage*/ ctx[6]
				? /*imageSize*/ ctx[17]
				: /*imageCropRect*/ ctx[13],
				enablePresetSelectImage: /*stickerEnableSelectImage*/ ctx[7],
				enableButtonFlipVertical: /*stickersEnableButtonFlipVertical*/ ctx[9],
				mapScreenPointToImagePoint: /*mapScreenPointToImagePoint*/ ctx[22],
				mapImagePointToScreenPoint: /*mapImagePointToScreenPoint*/ ctx[23]
			}
		});

	shapeutil.$on("measure", /*measure_handler*/ ctx[25]);

	return {
		c() {
			create_component(shapeutil.$$.fragment);
		},
		m(target, anchor) {
			mount_component(shapeutil, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const shapeutil_changes = {};
			if (dirty[0] & /*stores*/ 8) shapeutil_changes.stores = /*stores*/ ctx[3];
			if (dirty[0] & /*locale*/ 16) shapeutil_changes.locale = /*locale*/ ctx[4];
			if (dirty[0] & /*isActive*/ 1) shapeutil_changes.isActive = /*isActive*/ ctx[0];
			if (dirty[0] & /*isActiveFraction*/ 2) shapeutil_changes.isActiveFraction = /*isActiveFraction*/ ctx[1];
			if (dirty[0] & /*isVisible*/ 4) shapeutil_changes.isVisible = /*isVisible*/ ctx[2];
			if (dirty[0] & /*stickerShapeControls*/ 256) shapeutil_changes.shapeControls = /*stickerShapeControls*/ ctx[8];
			if (dirty[0] & /*stickers*/ 32) shapeutil_changes.shapePresets = /*stickers*/ ctx[5];

			if (dirty[0] & /*stickerStickToImage*/ 64) shapeutil_changes.shapes = /*stickerStickToImage*/ ctx[6]
			? /*imageAnnotation*/ ctx[15]
			: /*imageDecoration*/ ctx[16];

			if (dirty[0] & /*stickerStickToImage, $imageRotation*/ 1088) shapeutil_changes.imageRotation = /*stickerStickToImage*/ ctx[6]
			? /*$imageRotation*/ ctx[10]
			: 0;

			if (dirty[0] & /*stickerStickToImage*/ 64) shapeutil_changes.parentRect = /*stickerStickToImage*/ ctx[6]
			? /*imageSize*/ ctx[17]
			: /*imageCropRect*/ ctx[13];

			if (dirty[0] & /*stickerEnableSelectImage*/ 128) shapeutil_changes.enablePresetSelectImage = /*stickerEnableSelectImage*/ ctx[7];
			if (dirty[0] & /*stickersEnableButtonFlipVertical*/ 512) shapeutil_changes.enableButtonFlipVertical = /*stickersEnableButtonFlipVertical*/ ctx[9];
			shapeutil.$set(shapeutil_changes);
		},
		i(local) {
			if (current) return;
			transition_in(shapeutil.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(shapeutil.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(shapeutil, detaching);
		}
	};
}

function instance$z($$self, $$props, $$invalidate) {
	let $rootRect;
	let $imageSize;
	let $imageTransforms;
	let $imageFlipX;
	let $imageFlipY;
	let $imageSelectionRectPresentation;
	let $presentationScalar;
	let $imageRotation;
	const name = "sticker";
	let { isActive } = $$props;
	let { isActiveFraction } = $$props;
	let { isVisible } = $$props;
	let { stores } = $$props;
	let { locale = {} } = $$props;
	let { stickers = [] } = $$props;
	let { stickerStickToImage = false } = $$props;
	let { stickerEnableSelectImage = true } = $$props;
	let { stickerShapeControls = [] } = $$props;
	let { stickersEnableButtonFlipVertical = false } = $$props;

	// connect filter choice to stores
	const { presentationScalar, rootRect, imageCropRect, imageSelectionRectPresentation, imageAnnotation, imageDecoration, imageSize, imageTransforms, imageRotation, imageFlipX, imageFlipY } = stores;

	component_subscribe($$self, presentationScalar, value => $$invalidate(32, $presentationScalar = value));
	component_subscribe($$self, rootRect, value => $$invalidate(26, $rootRect = value));
	component_subscribe($$self, imageSelectionRectPresentation, value => $$invalidate(31, $imageSelectionRectPresentation = value));
	component_subscribe($$self, imageSize, value => $$invalidate(27, $imageSize = value));
	component_subscribe($$self, imageTransforms, value => $$invalidate(28, $imageTransforms = value));
	component_subscribe($$self, imageRotation, value => $$invalidate(10, $imageRotation = value));
	component_subscribe($$self, imageFlipX, value => $$invalidate(29, $imageFlipX = value));
	component_subscribe($$self, imageFlipY, value => $$invalidate(30, $imageFlipY = value));

	//
	// Mapping coordinates
	//
	const mapScreenPointToImagePoint = stickerStickToImage
	? point => _mapScreenPointToImagePoint(point, $rootRect, $imageSize, $imageTransforms.origin, $imageTransforms.translation, $imageTransforms.rotation.z, $imageTransforms.scale, $imageFlipX, $imageFlipY)
	: point => {
			const mappedPoint = vectorClone(point);
			mappedPoint.x -= $imageSelectionRectPresentation.x;
			mappedPoint.y -= $imageSelectionRectPresentation.y;
			mappedPoint.x /= $presentationScalar;
			mappedPoint.y /= $presentationScalar;
			return mappedPoint;
		};

	const mapImagePointToScreenPoint = stickerStickToImage
	? point => _mapImagePointToScreenPoint(point, $rootRect, $imageSize, $imageTransforms.origin, $imageTransforms.translation, $imageTransforms.rotation.z, $imageTransforms.scale, $imageFlipX, $imageFlipY)
	: point => {
			const mappedPoint = vectorClone(point);
			mappedPoint.x *= $presentationScalar;
			mappedPoint.y *= $presentationScalar;
			mappedPoint.x += $imageSelectionRectPresentation.x;
			mappedPoint.y += $imageSelectionRectPresentation.y;
			return mappedPoint;
		};

	function measure_handler(event) {
		bubble($$self, event);
	}

	$$self.$$set = $$props => {
		if ("isActive" in $$props) $$invalidate(0, isActive = $$props.isActive);
		if ("isActiveFraction" in $$props) $$invalidate(1, isActiveFraction = $$props.isActiveFraction);
		if ("isVisible" in $$props) $$invalidate(2, isVisible = $$props.isVisible);
		if ("stores" in $$props) $$invalidate(3, stores = $$props.stores);
		if ("locale" in $$props) $$invalidate(4, locale = $$props.locale);
		if ("stickers" in $$props) $$invalidate(5, stickers = $$props.stickers);
		if ("stickerStickToImage" in $$props) $$invalidate(6, stickerStickToImage = $$props.stickerStickToImage);
		if ("stickerEnableSelectImage" in $$props) $$invalidate(7, stickerEnableSelectImage = $$props.stickerEnableSelectImage);
		if ("stickerShapeControls" in $$props) $$invalidate(8, stickerShapeControls = $$props.stickerShapeControls);
		if ("stickersEnableButtonFlipVertical" in $$props) $$invalidate(9, stickersEnableButtonFlipVertical = $$props.stickersEnableButtonFlipVertical);
	};

	return [
		isActive,
		isActiveFraction,
		isVisible,
		stores,
		locale,
		stickers,
		stickerStickToImage,
		stickerEnableSelectImage,
		stickerShapeControls,
		stickersEnableButtonFlipVertical,
		$imageRotation,
		presentationScalar,
		rootRect,
		imageCropRect,
		imageSelectionRectPresentation,
		imageAnnotation,
		imageDecoration,
		imageSize,
		imageTransforms,
		imageRotation,
		imageFlipX,
		imageFlipY,
		mapScreenPointToImagePoint,
		mapImagePointToScreenPoint,
		name,
		measure_handler
	];
}

class Sticker extends SvelteComponent {
	constructor(options) {
		super();

		init(
			this,
			options,
			instance$z,
			create_fragment$z,
			safe_not_equal,
			{
				name: 24,
				isActive: 0,
				isActiveFraction: 1,
				isVisible: 2,
				stores: 3,
				locale: 4,
				stickers: 5,
				stickerStickToImage: 6,
				stickerEnableSelectImage: 7,
				stickerShapeControls: 8,
				stickersEnableButtonFlipVertical: 9
			},
			[-1, -1]
		);
	}

	get name() {
		return this.$$.ctx[24];
	}

	get isActive() {
		return this.$$.ctx[0];
	}

	set isActive(isActive) {
		this.$set({ isActive });
		flush();
	}

	get isActiveFraction() {
		return this.$$.ctx[1];
	}

	set isActiveFraction(isActiveFraction) {
		this.$set({ isActiveFraction });
		flush();
	}

	get isVisible() {
		return this.$$.ctx[2];
	}

	set isVisible(isVisible) {
		this.$set({ isVisible });
		flush();
	}

	get stores() {
		return this.$$.ctx[3];
	}

	set stores(stores) {
		this.$set({ stores });
		flush();
	}

	get locale() {
		return this.$$.ctx[4];
	}

	set locale(locale) {
		this.$set({ locale });
		flush();
	}

	get stickers() {
		return this.$$.ctx[5];
	}

	set stickers(stickers) {
		this.$set({ stickers });
		flush();
	}

	get stickerStickToImage() {
		return this.$$.ctx[6];
	}

	set stickerStickToImage(stickerStickToImage) {
		this.$set({ stickerStickToImage });
		flush();
	}

	get stickerEnableSelectImage() {
		return this.$$.ctx[7];
	}

	set stickerEnableSelectImage(stickerEnableSelectImage) {
		this.$set({ stickerEnableSelectImage });
		flush();
	}

	get stickerShapeControls() {
		return this.$$.ctx[8];
	}

	set stickerShapeControls(stickerShapeControls) {
		this.$set({ stickerShapeControls });
		flush();
	}

	get stickersEnableButtonFlipVertical() {
		return this.$$.ctx[9];
	}

	set stickersEnableButtonFlipVertical(stickersEnableButtonFlipVertical) {
		this.$set({ stickersEnableButtonFlipVertical });
		flush();
	}
}

// @ts-ignore
var index$5 = { util: ['sticker', Sticker] };

var toInt = (str) => parseInt(`${str}`.replace(/\D/g, ''), 10);

/* src/native/ui/plugins/resize/index.svelte generated by Svelte v3.29.4 */

function create_default_slot_2$5(ctx) {
	let g;

	let raw_value = (isString(/*locale*/ ctx[1].resizeIconButtonMaintainAspectRatio)
	? /*locale*/ ctx[1].resizeIconButtonMaintainAspectRatio
	: /*locale*/ ctx[1].resizeIconButtonMaintainAspectRatio(/*maintainAspectRatio*/ ctx[2], /*$iconActiveFraction*/ ctx[6])) + "";

	return {
		c() {
			g = svg_element("g");
		},
		m(target, anchor) {
			insert(target, g, anchor);
			g.innerHTML = raw_value;
		},
		p(ctx, dirty) {
			if (dirty & /*locale, maintainAspectRatio, $iconActiveFraction*/ 70 && raw_value !== (raw_value = (isString(/*locale*/ ctx[1].resizeIconButtonMaintainAspectRatio)
			? /*locale*/ ctx[1].resizeIconButtonMaintainAspectRatio
			: /*locale*/ ctx[1].resizeIconButtonMaintainAspectRatio(/*maintainAspectRatio*/ ctx[2], /*$iconActiveFraction*/ ctx[6])) + "")) g.innerHTML = raw_value;		},
		d(detaching) {
			if (detaching) detach(g);
		}
	};
}

// (275:12) <Button type="submit" class="implicit">
function create_default_slot_1$7(ctx) {
	let t;

	return {
		c() {
			t = text("Save");
		},
		m(target, anchor) {
			insert(target, t, anchor);
		},
		d(detaching) {
			if (detaching) detach(t);
		}
	};
}

// (248:4) <form slot="footer" style={footerStyle} on:submit|preventDefault={update}>
function create_footer_slot$4(ctx) {
	let form;
	let div4;
	let fieldset;
	let legend;
	let t0_value = /*locale*/ ctx[1].resizeLabelFormCaption + "";
	let t0;
	let t1;
	let div3;
	let div0;
	let label0;
	let t2_value = /*locale*/ ctx[1].resizeLabelInputWidth + "";
	let t2;
	let label0_for_value;
	let label0_title_value;
	let label0_aria_label_value;
	let t3;
	let input0;
	let input0_id_value;
	let t4;
	let div1;
	let input1;
	let input1_id_value;
	let t5;
	let label1;
	let icon;
	let label1_for_value;
	let label1_title_value;
	let t6;
	let div2;
	let label2;
	let t7_value = /*locale*/ ctx[1].resizeLabelInputHeight + "";
	let t7;
	let label2_for_value;
	let label2_title_value;
	let label2_aria_label_value;
	let t8;
	let input2;
	let input2_id_value;
	let t9;
	let button;
	let current;
	let mounted;
	let dispose;

	icon = new Icon({
			props: {
				$$slots: { default: [create_default_slot_2$5] },
				$$scope: { ctx }
			}
		});

	button = new Button({
			props: {
				type: "submit",
				class: "implicit",
				$$slots: { default: [create_default_slot_1$7] },
				$$scope: { ctx }
			}
		});

	return {
		c() {
			form = element("form");
			div4 = element("div");
			fieldset = element("fieldset");
			legend = element("legend");
			t0 = text(t0_value);
			t1 = space();
			div3 = element("div");
			div0 = element("div");
			label0 = element("label");
			t2 = text(t2_value);
			t3 = space();
			input0 = element("input");
			t4 = space();
			div1 = element("div");
			input1 = element("input");
			t5 = space();
			label1 = element("label");
			create_component(icon.$$.fragment);
			t6 = space();
			div2 = element("div");
			label2 = element("label");
			t7 = text(t7_value);
			t8 = space();
			input2 = element("input");
			t9 = space();
			create_component(button.$$.fragment);
			attr(legend, "class", "implicit");
			attr(label0, "for", label0_for_value = "doka-width-" + /*formId*/ ctx[11]);
			attr(label0, "title", label0_title_value = /*locale*/ ctx[1].resizeTitleInputWidth);
			attr(label0, "aria-label", label0_aria_label_value = /*locale*/ ctx[1].resizeTitleInputWidth);
			attr(input0, "id", input0_id_value = "doka-width-" + /*formId*/ ctx[11]);
			attr(input0, "type", "text");
			attr(input0, "inputmode", "numeric");
			attr(input0, "pattern", "[0-9]*");
			attr(input0, "autocomplete", "off");
			attr(div0, "class", "DokaInputDimension");
			attr(input1, "class", "implicit");
			attr(input1, "id", input1_id_value = "doka-maintainAspectRatio-" + /*formId*/ ctx[11]);
			attr(input1, "type", "checkbox");
			attr(label1, "for", label1_for_value = "doka-maintainAspectRatio-" + /*formId*/ ctx[11]);
			attr(label1, "title", label1_title_value = /*locale*/ ctx[1].resizeTitleButtonMaintainAspectRatio);
			attr(label2, "for", label2_for_value = "doka-height-" + /*formId*/ ctx[11]);
			attr(label2, "title", label2_title_value = /*locale*/ ctx[1].resizeTitleInputHeight);
			attr(label2, "aria-label", label2_aria_label_value = /*locale*/ ctx[1].resizeTitleInputHeight);
			attr(input2, "id", input2_id_value = "doka-height-" + /*formId*/ ctx[11]);
			attr(input2, "type", "text");
			attr(input2, "inputmode", "numeric");
			attr(input2, "pattern", "[0-9]*");
			attr(input2, "autocomplete", "off");
			attr(div2, "class", "DokaInputDimension");
			attr(div3, "class", "DokaFieldsetInner");
			attr(div4, "class", "DokaFormInner");
			attr(form, "slot", "footer");
			attr(form, "style", /*footerStyle*/ ctx[5]);
		},
		m(target, anchor) {
			insert(target, form, anchor);
			append(form, div4);
			append(div4, fieldset);
			append(fieldset, legend);
			append(legend, t0);
			append(fieldset, t1);
			append(fieldset, div3);
			append(div3, div0);
			append(div0, label0);
			append(label0, t2);
			append(div0, t3);
			append(div0, input0);
			set_input_value(input0, /*width*/ ctx[3]);
			append(div3, t4);
			append(div3, div1);
			append(div1, input1);
			input1.checked = /*maintainAspectRatio*/ ctx[2];
			append(div1, t5);
			append(div1, label1);
			mount_component(icon, label1, null);
			append(div3, t6);
			append(div3, div2);
			append(div2, label2);
			append(label2, t7);
			append(div2, t8);
			append(div2, input2);
			set_input_value(input2, /*height*/ ctx[4]);
			append(div4, t9);
			mount_component(button, div4, null);
			current = true;

			if (!mounted) {
				dispose = [
					listen(input0, "input", /*input0_input_handler*/ ctx[18]),
					listen(input1, "change", /*input1_change_handler*/ ctx[19]),
					listen(input2, "input", /*input2_input_handler*/ ctx[20]),
					listen(form, "submit", prevent_default(/*update*/ ctx[12]))
				];

				mounted = true;
			}
		},
		p(ctx, dirty) {
			if ((!current || dirty & /*locale*/ 2) && t0_value !== (t0_value = /*locale*/ ctx[1].resizeLabelFormCaption + "")) set_data(t0, t0_value);
			if ((!current || dirty & /*locale*/ 2) && t2_value !== (t2_value = /*locale*/ ctx[1].resizeLabelInputWidth + "")) set_data(t2, t2_value);

			if (!current || dirty & /*locale*/ 2 && label0_title_value !== (label0_title_value = /*locale*/ ctx[1].resizeTitleInputWidth)) {
				attr(label0, "title", label0_title_value);
			}

			if (!current || dirty & /*locale*/ 2 && label0_aria_label_value !== (label0_aria_label_value = /*locale*/ ctx[1].resizeTitleInputWidth)) {
				attr(label0, "aria-label", label0_aria_label_value);
			}

			if (dirty & /*width*/ 8 && input0.value !== /*width*/ ctx[3]) {
				set_input_value(input0, /*width*/ ctx[3]);
			}

			if (dirty & /*maintainAspectRatio*/ 4) {
				input1.checked = /*maintainAspectRatio*/ ctx[2];
			}

			const icon_changes = {};

			if (dirty & /*$$scope, locale, maintainAspectRatio, $iconActiveFraction*/ 536870982) {
				icon_changes.$$scope = { dirty, ctx };
			}

			icon.$set(icon_changes);

			if (!current || dirty & /*locale*/ 2 && label1_title_value !== (label1_title_value = /*locale*/ ctx[1].resizeTitleButtonMaintainAspectRatio)) {
				attr(label1, "title", label1_title_value);
			}

			if ((!current || dirty & /*locale*/ 2) && t7_value !== (t7_value = /*locale*/ ctx[1].resizeLabelInputHeight + "")) set_data(t7, t7_value);

			if (!current || dirty & /*locale*/ 2 && label2_title_value !== (label2_title_value = /*locale*/ ctx[1].resizeTitleInputHeight)) {
				attr(label2, "title", label2_title_value);
			}

			if (!current || dirty & /*locale*/ 2 && label2_aria_label_value !== (label2_aria_label_value = /*locale*/ ctx[1].resizeTitleInputHeight)) {
				attr(label2, "aria-label", label2_aria_label_value);
			}

			if (dirty & /*height*/ 16 && input2.value !== /*height*/ ctx[4]) {
				set_input_value(input2, /*height*/ ctx[4]);
			}

			const button_changes = {};

			if (dirty & /*$$scope*/ 536870912) {
				button_changes.$$scope = { dirty, ctx };
			}

			button.$set(button_changes);

			if (!current || dirty & /*footerStyle*/ 32) {
				attr(form, "style", /*footerStyle*/ ctx[5]);
			}
		},
		i(local) {
			if (current) return;
			transition_in(icon.$$.fragment, local);
			transition_in(button.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(icon.$$.fragment, local);
			transition_out(button.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(form);
			destroy_component(icon);
			destroy_component(button);
			mounted = false;
			run_all(dispose);
		}
	};
}

function create_fragment$A(ctx) {
	let util;
	let current;

	util = new Util({
			props: {
				$$slots: { footer: [create_footer_slot$4] },
				$$scope: { ctx }
			}
		});

	util.$on("measure", /*measure_handler*/ ctx[21]);

	return {
		c() {
			create_component(util.$$.fragment);
		},
		m(target, anchor) {
			mount_component(util, target, anchor);
			current = true;
		},
		p(ctx, [dirty]) {
			const util_changes = {};

			if (dirty & /*$$scope, footerStyle, height, locale, maintainAspectRatio, $iconActiveFraction, width*/ 536871038) {
				util_changes.$$scope = { dirty, ctx };
			}

			util.$set(util_changes);
		},
		i(local) {
			if (current) return;
			transition_in(util.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(util.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(util, detaching);
		}
	};
}

function instance$A($$self, $$props, $$invalidate) {
	let $imageCropRectAspectRatio;
	let $imageOutputSize;
	let $imageCropAspectRatio;

	let $isActive,
		$$unsubscribe_isActive = noop$1,
		$$subscribe_isActive = () => ($$unsubscribe_isActive(), $$unsubscribe_isActive = subscribe(isActive, $$value => $$invalidate(26, $isActive = $$value)), isActive);

	let $footerOffset;
	let $iconActiveFraction;
	$$self.$$.on_destroy.push(() => $$unsubscribe_isActive());
	const name = "resize";
	let { isActive } = $$props;
	$$subscribe_isActive();
	let { stores } = $$props;
	let { locale = {} } = $$props;
	let { resizeMinSize = sizeCreate(1, 1) } = $$props;
	let { resizeMaxSize = sizeCreate(9999, 9999) } = $$props;

	// offset
	const iconActiveFraction = spring(0, { stiffness: 0.15, damping: 0.3 });

	component_subscribe($$self, iconActiveFraction, value => $$invalidate(6, $iconActiveFraction = value));
	const { imageCropRectAspectRatio, imageCropAspectRatio, imageOutputSize, history } = stores;
	component_subscribe($$self, imageCropRectAspectRatio, value => $$invalidate(23, $imageCropRectAspectRatio = value));
	component_subscribe($$self, imageCropAspectRatio, value => $$invalidate(25, $imageCropAspectRatio = value));
	component_subscribe($$self, imageOutputSize, value => $$invalidate(24, $imageOutputSize = value));
	const formId = getUniqueId();
	let maintainAspectRatio = false;
	let width = undefined;
	let height = undefined;
	let updateTimeout;

	const update = () => {
		// no values set, exit
		if (!width || !height) return;

		// get curent values so we can check if they were changed (if not, no need to write history or update)
		const { width: currentWidth, height: currentHeight } = $imageOutputSize || {};

		// no need to update
		if (currentWidth === width && currentHeight === height) return;

		set_store_value(imageCropAspectRatio, $imageCropAspectRatio = width / height, $imageCropAspectRatio);
		set_store_value(imageOutputSize, $imageOutputSize = sizeCreate(width, height), $imageOutputSize);
		history.write();
	};

	imageOutputSize.subscribe(size => {
		if (!size) {
			$$invalidate(3, width = undefined);
			$$invalidate(4, height = undefined);
		} else {
			$$invalidate(3, width = size.width);
			$$invalidate(4, height = size.height);
		}
	});

	imageCropAspectRatio.subscribe(cropAspectRatio => {
		// if aspect ratio is set, we need to maintain the aspect ratio
		$$invalidate(2, maintainAspectRatio = cropAspectRatio !== undefined);

		// exit if no dimensions supplied
		if (!width || !height) return;

		// get a
		const sizeAspectRatio = width / height;

		// reset size if no aspect selected
		if (!cropAspectRatio) {
			// test if the aspect ratio of the crop is different from the size, if so, reset inputs
			if ($imageCropRectAspectRatio !== sizeAspectRatio) {
				$$invalidate(3, width = "");
				$$invalidate(4, height = "");
			}

			return;
		}

		// if the crop aspect ratio has changed, we need to update the fields
		if (cropAspectRatio !== sizeAspectRatio) {
			if (cropAspectRatio < sizeAspectRatio) {
				$$invalidate(4, height = Math.round(width / cropAspectRatio));
			} else {
				$$invalidate(3, width = Math.round(height * cropAspectRatio));
			}
		}
	});

	//
	// Footer
	//
	const footerOffset = spring(0);

	component_subscribe($$self, footerOffset, value => $$invalidate(27, $footerOffset = value));

	function input0_input_handler() {
		width = this.value;
		((((($$invalidate(3, width), $$invalidate(16, resizeMinSize)), $$invalidate(17, resizeMaxSize)), $$invalidate(4, height)), $$invalidate(2, maintainAspectRatio)), $$invalidate(23, $imageCropRectAspectRatio));
	}

	function input1_change_handler() {
		maintainAspectRatio = this.checked;
		$$invalidate(2, maintainAspectRatio);
	}

	function input2_input_handler() {
		height = this.value;
		((((($$invalidate(4, height), $$invalidate(16, resizeMinSize)), $$invalidate(17, resizeMaxSize)), $$invalidate(3, width)), $$invalidate(2, maintainAspectRatio)), $$invalidate(23, $imageCropRectAspectRatio));
	}

	function measure_handler(event) {
		bubble($$self, event);
	}

	$$self.$$set = $$props => {
		if ("isActive" in $$props) $$subscribe_isActive($$invalidate(0, isActive = $$props.isActive));
		if ("stores" in $$props) $$invalidate(15, stores = $$props.stores);
		if ("locale" in $$props) $$invalidate(1, locale = $$props.locale);
		if ("resizeMinSize" in $$props) $$invalidate(16, resizeMinSize = $$props.resizeMinSize);
		if ("resizeMaxSize" in $$props) $$invalidate(17, resizeMaxSize = $$props.resizeMaxSize);
	};

	let footerStyle;

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*maintainAspectRatio*/ 4) {
			 iconActiveFraction.set(maintainAspectRatio ? 1 : 0);
		}

		if ($$self.$$.dirty & /*width, resizeMinSize, resizeMaxSize*/ 196616) {
			 if (width) {
				const widthValue = toInt(width);
				if (Number.isNaN(widthValue)) $$invalidate(3, width = undefined); else $$invalidate(3, width = clamp(widthValue, resizeMinSize.width, resizeMaxSize.width));
			}
		}

		if ($$self.$$.dirty & /*height, resizeMinSize, resizeMaxSize*/ 196624) {
			 if (height) {
				const heightValue = toInt(height);
				if (Number.isNaN(heightValue)) $$invalidate(4, height = undefined);
				$$invalidate(4, height = clamp(heightValue, resizeMinSize.height, resizeMaxSize.height));
			}
		}

		if ($$self.$$.dirty & /*width, height, maintainAspectRatio, $imageCropRectAspectRatio*/ 8388636) {
			 if ((width || height) && maintainAspectRatio) {
				const isEditingWidth = document.activeElement.id === `doka-width-${formId}`;

				if (isEditingWidth) {
					$$invalidate(4, height = Math.round(width / $imageCropRectAspectRatio));
				} else {
					$$invalidate(3, width = Math.round(height * $imageCropRectAspectRatio));
				}
			}
		}

		if ($$self.$$.dirty & /*width, height, updateTimeout*/ 4194328) {
			 if (width || height) {
				clearTimeout(updateTimeout);
				$$invalidate(22, updateTimeout = setTimeout(update, 1000));
			}
		}

		if ($$self.$$.dirty & /*$isActive*/ 67108864) {
			 footerOffset.set($isActive ? 0 : 20);
		}

		if ($$self.$$.dirty & /*$footerOffset*/ 134217728) {
			 $$invalidate(5, footerStyle = $footerOffset
			? `transform: translateY(${$footerOffset}px)`
			: undefined);
		}
	};

	return [
		isActive,
		locale,
		maintainAspectRatio,
		width,
		height,
		footerStyle,
		$iconActiveFraction,
		iconActiveFraction,
		imageCropRectAspectRatio,
		imageCropAspectRatio,
		imageOutputSize,
		formId,
		update,
		footerOffset,
		name,
		stores,
		resizeMinSize,
		resizeMaxSize,
		input0_input_handler,
		input1_change_handler,
		input2_input_handler,
		measure_handler
	];
}

class Resize extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$A, create_fragment$A, safe_not_equal, {
			name: 14,
			isActive: 0,
			stores: 15,
			locale: 1,
			resizeMinSize: 16,
			resizeMaxSize: 17
		});
	}

	get name() {
		return this.$$.ctx[14];
	}

	get isActive() {
		return this.$$.ctx[0];
	}

	set isActive(isActive) {
		this.$set({ isActive });
		flush();
	}

	get stores() {
		return this.$$.ctx[15];
	}

	set stores(stores) {
		this.$set({ stores });
		flush();
	}

	get locale() {
		return this.$$.ctx[1];
	}

	set locale(locale) {
		this.$set({ locale });
		flush();
	}

	get resizeMinSize() {
		return this.$$.ctx[16];
	}

	set resizeMinSize(resizeMinSize) {
		this.$set({ resizeMinSize });
		flush();
	}

	get resizeMaxSize() {
		return this.$$.ctx[17];
	}

	set resizeMaxSize(resizeMaxSize) {
		this.$set({ resizeMaxSize });
		flush();
	}
}

// @ts-ignore
var index$6 = { util: ['resize', Resize] };

const cropSelectPresetOptionsDefault = [
    [
        'Crop',
        [
            [undefined, 'Custom'],
            [1, 'Square'],
            [1.5, 'Landscape'],
            [0.7, 'Portrait'],
        ],
    ],
    [
        'Size',
        [
            [[180, 180], 'Profile Picture'],
            [[1200, 600], 'Header Image'],
            [[800, 400], 'Timeline Photo'],
        ],
    ],
];
var defaults = {
    cropSelectPresetOptions: cropSelectPresetOptionsDefault,
};

const brightness = {
    base: 0,
    min: -0.25,
    max: 0.25,
    getStore: ({ imageColorMatrix }) => imageColorMatrix,
    getValue: (store) => {
        if (!store.brightness)
            return;
        return store.brightness[4];
    },
    setValue: (store, v) => store.update((matrices) => ({
        // clone existing matrices
        ...matrices,
        // prettier-ignore
        brightness: [
            1, 0, 0, 0, v,
            0, 1, 0, 0, v,
            0, 0, 1, 0, v,
            0, 0, 0, 1, 0
        ],
    })),
};
const contrast = {
    base: 1,
    min: 0.5,
    max: 1.5,
    getStore: ({ imageColorMatrix }) => imageColorMatrix,
    getValue: (store) => {
        if (!store.contrast)
            return;
        return store.contrast[0];
    },
    setValue: (store, v) => store.update((matrices) => ({
        // clone existing matrices
        ...matrices,
        // prettier-ignore
        contrast: [
            v, 0, 0, 0, .5 * (1 - v),
            0, v, 0, 0, .5 * (1 - v),
            0, 0, v, 0, .5 * (1 - v),
            0, 0, 0, 1, 0
        ],
    })),
};
const saturation = {
    base: 1,
    min: 0,
    max: 2,
    getStore: ({ imageColorMatrix }) => imageColorMatrix,
    getValue: (store) => {
        if (!store.saturation)
            return;
        return (store.saturation[0] - 0.213) / 0.787;
    },
    setValue: (store, v) => store.update((matrices) => ({
        ...matrices,
        // prettier-ignore
        saturation: [
            .213 + .787 * v, .715 - .715 * v, .072 - .072 * v, 0, 0,
            .213 - .213 * v, .715 + .285 * v, .072 - .072 * v, 0, 0,
            .213 - .213 * v, .715 - .715 * v, .072 + .928 * v, 0, 0,
            0, 0, 0, 1, 0
        ],
    })),
};
const exposure = {
    base: 1,
    min: 0.5,
    max: 1.5,
    getStore: ({ imageColorMatrix }) => imageColorMatrix,
    getValue: (store) => {
        if (!store.exposure)
            return;
        return store.exposure[0];
    },
    setValue: (store, v) => store.update((matrices) => ({
        ...matrices,
        // prettier-ignore
        exposure: [
            v, 0, 0, 0, 0,
            0, v, 0, 0, 0,
            0, 0, v, 0, 0,
            0, 0, 0, 1, 0
        ],
    })),
};
const gamma = {
    base: 1,
    min: 0.15,
    max: 4,
    getStore: ({ imageGamma }) => imageGamma,
};
const vignette = {
    base: 0,
    min: -1,
    max: 1,
    getStore: ({ imageVignette }) => imageVignette,
};
const clarity = {
    base: 0,
    min: -1,
    max: 1,
    getStore: ({ imageConvolutionMatrix }) => imageConvolutionMatrix,
    getValue: (store) => {
        if (!store.clarity)
            return;
        if (store.clarity[0] === 0) {
            return store.clarity[1] / -1;
        }
        else {
            return store.clarity[1] / -2;
        }
    },
    setValue: (store, v) => {
        store.update((matrices) => ({
            ...matrices,
            // prettier-ignore
            clarity: v >= 0
                ? [0, -1 * v, 0,
                    -1 * v, 1 + 4 * v, -1 * v,
                    0, -1 * v, 0
                ]
                : [-1 * v, -2 * v, -1 * v,
                    -2 * v, 1 + -3 * v, -2 * v,
                    -1 * v, -2 * v, -1 * v
                ],
        }));
    },
};
/*
TODO: fix inverse fn in getValue
export const hue = {
    base: 0,
    min: 0,
    max: Math.PI,
    getStore: ({ imageColorMatrix }) => imageColorMatrix,
    getValue: (store) => {
        if (!store.hue) return;

        const v = store.hue[1];

        const o = -(Math.acos((0.715 - v) / Math.sqrt(2) / 0.715) - Math.PI / 4);

        return o;
    },
    setValue: (store, v: number) => {
        store.update((matrices) => {
            // const value = v * (Math.PI);

            const cos = Math.cos(v);
            const sin = Math.sin(v);

            const a00 = 0.213 + cos * 0.787 - sin * 0.213;
            const a01 = 0.715 - cos * 0.715 - sin * 0.715;
            const a02 = 0.072 - cos * 0.072 + sin * 0.928;
            const a10 = 0.213 - cos * 0.213 + sin * 0.143;
            const a11 = 0.715 + cos * 0.285 + sin * 0.14;
            const a12 = 0.072 - cos * 0.072 - sin * 0.283;
            const a20 = 0.213 - cos * 0.213 - sin * 0.787;
            const a21 = 0.715 - cos * 0.715 + sin * 0.715;
            const a22 = 0.072 + cos * 0.928 + sin * 0.072;

            return {
                ...matrices,

                // prettier-ignore
                hue: [
                    a00, a01, a02, 0, 0,
                    a10, a11, a12, 0, 0,
                    a20, a21, a22, 0, 0,
                      0,   0,   0, 1, 0,
                ]
            };
        });
    },
};
*/
const finetuneControlConfigurationDefault = {
    gamma,
    brightness,
    contrast,
    saturation,
    exposure,
    clarity,
    vignette,
};
const finetuneOptionsDefault = [
    ['brightness', (locale) => locale.finetuneLabelBrightness],
    ['contrast', (locale) => locale.finetuneLabelContrast],
    ['saturation', (locale) => locale.finetuneLabelSaturation],
    ['exposure', (locale) => locale.finetuneLabelExposure],
    ['gamma', (locale) => locale.finetuneLabelGamma],
    ['clarity', (locale) => locale.finetuneLabelClarity],
    ['vignette', (locale) => locale.finetuneLabelVignette],
];
var defaults$1 = {
    finetuneControlConfiguration: finetuneControlConfigurationDefault,
    finetuneOptions: finetuneOptionsDefault,
};

const pastel = () => 
// prettier-ignore
[
    0.75, 0.25, 0.25, 0, 0,
    0.25, 0.75, 0.25, 0, 0,
    0.25, 0.25, 0.75, 0, 0,
    0, 0, 0, 1, 0
];
const chrome = () => 
// prettier-ignore
[
    1.398, -0.316, 0.065, -0.273, 0.201,
    -0.051, 1.278, -0.080, -0.273, 0.201,
    -0.051, 0.119, 1.151, -0.290, 0.215,
    0, 0, 0, 1, 0
];
const fade$1 = () => 
// prettier-ignore
[
    1.073, -0.015, 0.092, -0.115, -0.017,
    0.107, 0.859, 0.184, -0.115, -0.017,
    0.015, 0.077, 1.104, -0.115, -0.017,
    0, 0, 0, 1, 0
];
const warm = () => 
// prettier-ignore
[
    1.06, 0, 0, 0, 0,
    0, 1.01, 0, 0, 0,
    0, 0, 0.93, 0, 0,
    0, 0, 0, 1, 0,
];
const cold = () => 
// prettier-ignore
[
    1.1, 0, 0, 0, -.1,
    0, 1.1, 0, 0, -.1,
    0, 0, 1.2, 0, -.1,
    0, 0, 0, 1, 0,
];
const monoDefault = () => 
// prettier-ignore
[
    0.212, 0.715, 0.114, 0, 0,
    0.212, 0.715, 0.114, 0, 0,
    0.212, 0.715, 0.114, 0, 0,
    0, 0, 0, 1, 0
];
const monoNoir = () => 
// prettier-ignore
[
    0.15, 1.3, -0.25, 0.1, -0.2,
    0.15, 1.3, -0.25, 0.1, -0.2,
    0.15, 1.3, -0.25, 0.1, -0.2,
    0, 0, 0, 1, 0
];
const monoWash = () => 
// prettier-ignore
[
    0.163, 0.518, 0.084, -0.010, 0.208,
    0.163, 0.529, 0.082, -0.020, 0.210,
    0.171, 0.529, 0.084, 0.000, 0.214,
    0.000, 0.000, 0.000, 1.000, 0.000,
];
const monoStark = () => 
// prettier-ignore
[
    0.338, 0.991, 0.117, 0.093, -0.196,
    0.302, 1.049, 0.096, 0.078, -0.196,
    0.286, 1.016, 0.146, 0.101, -0.196,
    0.000, 0.000, 0.000, 1.000, 0.000,
];
const sepiaDefault = () => 
// prettier-ignore
[
    0.393, 0.768, 0.188, 0, 0,
    0.349, 0.685, 0.167, 0, 0,
    0.272, 0.533, 0.130, 0, 0,
    0, 0, 0, 1, 0,
];
const sepiaBlues = () => 
// prettier-ignore
[
    0.289, 0.620, 0.185, 0.000, 0.077,
    0.257, 0.566, 0.163, 0.000, 0.115,
    0.200, 0.430, 0.128, 0.000, 0.188,
    0.000, 0.000, 0.000, 1.000, 0.000,
];
const sepiaRust = () => 
// prettier-ignore
[
    0.269, 0.764, 0.172, 0.050, 0.100,
    0.239, 0.527, 0.152, 0.000, 0.176,
    0.186, 0.400, 0.119, 0.000, 0.159,
    0.000, 0.000, 0.000, 1.000, 0.000,
];
const sepiaColor = () => 
// prettier-ignore
[
    0.547, 0.764, 0.134, 0.000, -0.147,
    0.281, 0.925, 0.120, 0.000, -0.135,
    0.225, 0.558, 0.330, 0.000, -0.113,
    0.000, 0.000, 0.000, 1.000, 0.000,
];
//
// default filter set
//
const filterFunctionsDefault = {
    chrome,
    fade: fade$1,
    pastel,
    cold,
    warm,
    monoDefault,
    monoWash,
    monoNoir,
    monoStark,
    sepiaDefault,
    sepiaRust,
    sepiaBlues,
    sepiaColor,
};
const filterOptionsDefault = [
    ['Default', [[undefined, (locale) => locale.labelDefault]]],
    [
        'Classic',
        [
            ['chrome', (locale) => locale.filterLabelChrome],
            ['fade', (locale) => locale.filterLabelFade],
            ['cold', (locale) => locale.filterLabelCold],
            ['warm', (locale) => locale.filterLabelWarm],
            ['pastel', (locale) => locale.filterLabelPastel],
        ],
    ],
    [
        'Monochrome',
        [
            ['monoDefault', (locale) => locale.filterLabelMonoDefault],
            ['monoNoir', (locale) => locale.filterLabelMonoNoir],
            ['monoStark', (locale) => locale.filterLabelMonoStark],
            ['monoWash', (locale) => locale.filterLabelMonoWash],
        ],
    ],
    [
        'Sepia',
        [
            ['sepiaDefault', (locale) => locale.filterLabelSepiaDefault],
            ['sepiaRust', (locale) => locale.filterLabelSepiaRust],
            ['sepiaBlues', (locale) => locale.filterLabelSepiaBlues],
            ['sepiaColor', (locale) => locale.filterLabelSepiaColor],
        ],
    ],
];
var defaults$2 = {
    filterFunctions: filterFunctionsDefault,
    filterOptions: filterOptionsDefault,
};

var defaults$3 = {};

/* src/native/ui/components/Slider.svelte generated by Svelte v3.29.4 */

function create_fragment$B(ctx) {
	let div3;
	let input_1;
	let nudgeable_action;
	let t0;
	let div0;
	let t1;
	let div2;
	let div1;
	let div2_style_value;
	let div3_class_value;
	let mounted;
	let dispose;

	return {
		c() {
			div3 = element("div");
			input_1 = element("input");
			t0 = space();
			div0 = element("div");
			t1 = space();
			div2 = element("div");
			div1 = element("div");
			attr(input_1, "type", "range");
			attr(input_1, "id", /*id*/ ctx[4]);
			attr(input_1, "min", /*min*/ ctx[1]);
			attr(input_1, "max", /*max*/ ctx[2]);
			attr(input_1, "step", /*step*/ ctx[3]);
			input_1.value = /*value*/ ctx[0];
			attr(div0, "class", "DokaSliderTrack");
			attr(div0, "style", /*trackStyle*/ ctx[5]);
			attr(div1, "class", "DokaSliderKnob");
			attr(div1, "style", /*knobStyle*/ ctx[6]);
			attr(div2, "class", "DokaSliderKnobController");
			attr(div2, "style", div2_style_value = `transform: translateX(${/*position*/ ctx[9]}%)`);
			attr(div3, "class", div3_class_value = arrayJoin(["DokaSlider", /*klass*/ ctx[7]]));
		},
		m(target, anchor) {
			insert(target, div3, anchor);
			append(div3, input_1);
			/*input_1_binding*/ ctx[14](input_1);
			append(div3, t0);
			append(div3, div0);
			append(div3, t1);
			append(div3, div2);
			append(div2, div1);

			if (!mounted) {
				dispose = [
					listen(input_1, "pointerdown", /*handlePointerDown*/ ctx[12]),
					listen(input_1, "input", /*handleInput*/ ctx[10]),
					listen(input_1, "nudge", /*handleNudge*/ ctx[11]),
					action_destroyer(nudgeable_action = nudgeable.call(null, input_1))
				];

				mounted = true;
			}
		},
		p(ctx, [dirty]) {
			if (dirty & /*id*/ 16) {
				attr(input_1, "id", /*id*/ ctx[4]);
			}

			if (dirty & /*min*/ 2) {
				attr(input_1, "min", /*min*/ ctx[1]);
			}

			if (dirty & /*max*/ 4) {
				attr(input_1, "max", /*max*/ ctx[2]);
			}

			if (dirty & /*step*/ 8) {
				attr(input_1, "step", /*step*/ ctx[3]);
			}

			if (dirty & /*value*/ 1) {
				input_1.value = /*value*/ ctx[0];
			}

			if (dirty & /*trackStyle*/ 32) {
				attr(div0, "style", /*trackStyle*/ ctx[5]);
			}

			if (dirty & /*knobStyle*/ 64) {
				attr(div1, "style", /*knobStyle*/ ctx[6]);
			}

			if (dirty & /*position*/ 512 && div2_style_value !== (div2_style_value = `transform: translateX(${/*position*/ ctx[9]}%)`)) {
				attr(div2, "style", div2_style_value);
			}

			if (dirty & /*klass*/ 128 && div3_class_value !== (div3_class_value = arrayJoin(["DokaSlider", /*klass*/ ctx[7]]))) {
				attr(div3, "class", div3_class_value);
			}
		},
		i: noop$1,
		o: noop$1,
		d(detaching) {
			if (detaching) detach(div3);
			/*input_1_binding*/ ctx[14](null);
			mounted = false;
			run_all(dispose);
		}
	};
}

function instance$B($$self, $$props, $$invalidate) {
	let { min = 0 } = $$props;
	let { max = 100 } = $$props;
	let { step = 1 } = $$props;
	let { id = undefined } = $$props;
	let { value = 0 } = $$props;
	let { trackStyle = undefined } = $$props;
	let { knobStyle = undefined } = $$props;
	let { onchange = undefined } = $$props;
	let { class: klass = undefined } = $$props;
	let input;
	let inputWidth;
	let inputOffsetX;
	let pageOffsetX;

	const setValueByOffsetX = (offsetX, inputWidth) => {
		$$invalidate(0, value = clamp(min + offsetX / inputWidth * range, min, max));
		onchange(value);
	};

	const handleInput = e => {
		// already handled by pointer events
		if (inputWidth) return;

		$$invalidate(0, value = parseFloat(e.target.value));
		onchange(value);
	};

	const handleNudge = e => {
		const width = input.offsetWidth;
		const offset = value / range * width;
		setValueByOffsetX(offset + e.detail.x, width);
	};

	const handlePointerDown = e => {
		e.stopPropagation();
		inputWidth = input.offsetWidth;
		inputOffsetX = e.offsetX;
		pageOffsetX = e.pageX;
		setValueByOffsetX(inputOffsetX, inputWidth);
		document.documentElement.addEventListener("pointermove", handlePointerMove);
		document.documentElement.addEventListener("pointerup", handlePointerUp);
	};

	const handlePointerMove = e => {
		const dx = e.pageX - pageOffsetX;
		setValueByOffsetX(inputOffsetX + dx, inputWidth);
	};

	const handlePointerUp = e => {
		inputWidth = undefined;
		document.documentElement.removeEventListener("pointermove", handlePointerMove);
		document.documentElement.removeEventListener("pointerup", handlePointerUp);
		onchange(value);
	};

	function input_1_binding($$value) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			input = $$value;
			$$invalidate(8, input);
		});
	}

	$$self.$$set = $$props => {
		if ("min" in $$props) $$invalidate(1, min = $$props.min);
		if ("max" in $$props) $$invalidate(2, max = $$props.max);
		if ("step" in $$props) $$invalidate(3, step = $$props.step);
		if ("id" in $$props) $$invalidate(4, id = $$props.id);
		if ("value" in $$props) $$invalidate(0, value = $$props.value);
		if ("trackStyle" in $$props) $$invalidate(5, trackStyle = $$props.trackStyle);
		if ("knobStyle" in $$props) $$invalidate(6, knobStyle = $$props.knobStyle);
		if ("onchange" in $$props) $$invalidate(13, onchange = $$props.onchange);
		if ("class" in $$props) $$invalidate(7, klass = $$props.class);
	};

	let range;
	let position;

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*max, min*/ 6) {
			 $$invalidate(18, range = max - min);
		}

		if ($$self.$$.dirty & /*value, range*/ 262145) {
			 $$invalidate(9, position = value / range * 100);
		}
	};

	return [
		value,
		min,
		max,
		step,
		id,
		trackStyle,
		knobStyle,
		klass,
		input,
		position,
		handleInput,
		handleNudge,
		handlePointerDown,
		onchange,
		input_1_binding
	];
}

class Slider extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$B, create_fragment$B, safe_not_equal, {
			min: 1,
			max: 2,
			step: 3,
			id: 4,
			value: 0,
			trackStyle: 5,
			knobStyle: 6,
			onchange: 13,
			class: 7
		});
	}
}

var RGBToHSV = (r, g, b) => {
    let v = Math.max(r, g, b), n = v - Math.min(r, g, b);
    let h = n && (v == r ? (g - b) / n : v == g ? 2 + (b - r) / n : 4 + (r - g) / n);
    return [(60 * (h < 0 ? h + 6 : h)) / 360, v && n / v, v];
};

var HSVToRGB = (h, s, v) => {
    let r, g, b;
    const i = Math.floor(h * 6);
    const f = h * 6 - i;
    const p = v * (1 - s);
    const q = v * (1 - f * s);
    const t = v * (1 - (1 - f) * s);
    switch (i % 6) {
        case 0:
            (r = v), (g = t), (b = p);
            break;
        case 1:
            (r = q), (g = v), (b = p);
            break;
        case 2:
            (r = p), (g = v), (b = t);
            break;
        case 3:
            (r = p), (g = q), (b = v);
            break;
        case 4:
            (r = t), (g = p), (b = v);
            break;
        case 5:
            (r = v), (g = p), (b = q);
            break;
    }
    return [r, g, b];
};

/* src/native/ui/components/ColorPicker.svelte generated by Svelte v3.29.4 */

function create_if_block_4$5(ctx) {
	let span;
	let t;

	return {
		c() {
			span = element("span");
			t = text(/*label*/ ctx[1]);
		},
		m(target, anchor) {
			insert(target, span, anchor);
			append(span, t);
		},
		p(ctx, dirty) {
			if (dirty[0] & /*label*/ 2) set_data(t, /*label*/ ctx[1]);
		},
		d(detaching) {
			if (detaching) detach(span);
		}
	};
}

// (314:4) <span slot="label" class="DokaButtonLabel">
function create_label_slot$1(ctx) {
	let span;
	let colorpreview;
	let t;
	let current;

	colorpreview = new ColorPreview({
			props: {
				color: /*value*/ ctx[5],
				title: localize(/*title*/ ctx[8], /*locale*/ ctx[10])
			}
		});

	let if_block = !/*hidePresetLabel*/ ctx[9] && create_if_block_4$5(ctx);

	return {
		c() {
			span = element("span");
			create_component(colorpreview.$$.fragment);
			t = space();
			if (if_block) if_block.c();
			attr(span, "slot", "label");
			attr(span, "class", "DokaButtonLabel");
		},
		m(target, anchor) {
			insert(target, span, anchor);
			mount_component(colorpreview, span, null);
			append(span, t);
			if (if_block) if_block.m(span, null);
			current = true;
		},
		p(ctx, dirty) {
			const colorpreview_changes = {};
			if (dirty[0] & /*value*/ 32) colorpreview_changes.color = /*value*/ ctx[5];
			if (dirty[0] & /*title, locale*/ 1280) colorpreview_changes.title = localize(/*title*/ ctx[8], /*locale*/ ctx[10]);
			colorpreview.$set(colorpreview_changes);

			if (!/*hidePresetLabel*/ ctx[9]) {
				if (if_block) {
					if_block.p(ctx, dirty);
				} else {
					if_block = create_if_block_4$5(ctx);
					if_block.c();
					if_block.m(span, null);
				}
			} else if (if_block) {
				if_block.d(1);
				if_block = null;
			}
		},
		i(local) {
			if (current) return;
			transition_in(colorpreview.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(colorpreview.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(span);
			destroy_component(colorpreview);
			if (if_block) if_block.d();
		}
	};
}

// (326:8) {#if enablePicker}
function create_if_block_2$8(ctx) {
	let div3;
	let div2;
	let div1;
	let div0;
	let div0_style_value;
	let nudgeable_action;
	let div1_style_value;
	let div2_style_value;
	let t0;
	let slider;
	let t1;
	let current;
	let mounted;
	let dispose;

	slider = new Slider({
			props: {
				class: "DokaHuePicker",
				knobStyle: `background-color:${/*valueAsRGBAFullySaturated*/ ctx[20]}`,
				onchange: /*updateHue*/ ctx[25],
				value: /*hue*/ ctx[15],
				min: 0,
				max: 1,
				step: 0.001
			}
		});

	let if_block = /*enableOpacity*/ ctx[12] && create_if_block_3$6(ctx);

	return {
		c() {
			div3 = element("div");
			div2 = element("div");
			div1 = element("div");
			div0 = element("div");
			t0 = space();
			create_component(slider.$$.fragment);
			t1 = space();
			if (if_block) if_block.c();
			attr(div0, "role", "button");
			attr(div0, "class", "DokaPickerKnob");
			attr(div0, "tabindex", "0");
			attr(div0, "style", div0_style_value = `background-color:${/*valueAsRGBAFullyOpaque*/ ctx[19]};`);
			attr(div1, "class", "DokaPickerKnobController");
			attr(div1, "style", div1_style_value = `transform:translate(${/*sx*/ ctx[22]}%,${/*sy*/ ctx[23]}%)`);
			attr(div2, "class", "DokaSaturationPicker");
			attr(div2, "style", div2_style_value = `background-color: ${/*valueAsRGBAFullySaturated*/ ctx[20]}`);
			attr(div3, "class", "DokaPicker");
		},
		m(target, anchor) {
			insert(target, div3, anchor);
			append(div3, div2);
			append(div2, div1);
			append(div1, div0);
			/*div2_binding*/ ctx[30](div2);
			append(div3, t0);
			mount_component(slider, div3, null);
			append(div3, t1);
			if (if_block) if_block.m(div3, null);
			current = true;

			if (!mounted) {
				dispose = [
					listen(div0, "nudge", /*handleNudge*/ ctx[28]),
					action_destroyer(nudgeable_action = nudgeable.call(null, div0)),
					listen(div2, "pointerdown", /*handlePointerDown*/ ctx[27])
				];

				mounted = true;
			}
		},
		p(ctx, dirty) {
			if (!current || dirty[0] & /*valueAsRGBAFullyOpaque*/ 524288 && div0_style_value !== (div0_style_value = `background-color:${/*valueAsRGBAFullyOpaque*/ ctx[19]};`)) {
				attr(div0, "style", div0_style_value);
			}

			if (!current || dirty[0] & /*sx, sy*/ 12582912 && div1_style_value !== (div1_style_value = `transform:translate(${/*sx*/ ctx[22]}%,${/*sy*/ ctx[23]}%)`)) {
				attr(div1, "style", div1_style_value);
			}

			if (!current || dirty[0] & /*valueAsRGBAFullySaturated*/ 1048576 && div2_style_value !== (div2_style_value = `background-color: ${/*valueAsRGBAFullySaturated*/ ctx[20]}`)) {
				attr(div2, "style", div2_style_value);
			}

			const slider_changes = {};
			if (dirty[0] & /*valueAsRGBAFullySaturated*/ 1048576) slider_changes.knobStyle = `background-color:${/*valueAsRGBAFullySaturated*/ ctx[20]}`;
			if (dirty[0] & /*hue*/ 32768) slider_changes.value = /*hue*/ ctx[15];
			slider.$set(slider_changes);

			if (/*enableOpacity*/ ctx[12]) {
				if (if_block) {
					if_block.p(ctx, dirty);

					if (dirty[0] & /*enableOpacity*/ 4096) {
						transition_in(if_block, 1);
					}
				} else {
					if_block = create_if_block_3$6(ctx);
					if_block.c();
					transition_in(if_block, 1);
					if_block.m(div3, null);
				}
			} else if (if_block) {
				group_outros();

				transition_out(if_block, 1, 1, () => {
					if_block = null;
				});

				check_outros();
			}
		},
		i(local) {
			if (current) return;
			transition_in(slider.$$.fragment, local);
			transition_in(if_block);
			current = true;
		},
		o(local) {
			transition_out(slider.$$.fragment, local);
			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div3);
			/*div2_binding*/ ctx[30](null);
			destroy_component(slider);
			if (if_block) if_block.d();
			mounted = false;
			run_all(dispose);
		}
	};
}

// (346:12) {#if enableOpacity}
function create_if_block_3$6(ctx) {
	let slider;
	let current;

	slider = new Slider({
			props: {
				class: "DokaOpacityPicker",
				knobStyle: `background-color: ${/*valueAsRGBA*/ ctx[17]}`,
				trackStyle: `background-image: linear-gradient(to right, ${/*valueAsRGBAFullyTransparent*/ ctx[18]}, ${/*valueAsRGBAFullyOpaque*/ ctx[19]})`,
				onchange: /*updateOpacity*/ ctx[26],
				value: /*opacity*/ ctx[16],
				min: 0,
				max: 1,
				step: 0.001
			}
		});

	return {
		c() {
			create_component(slider.$$.fragment);
		},
		m(target, anchor) {
			mount_component(slider, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const slider_changes = {};
			if (dirty[0] & /*valueAsRGBA*/ 131072) slider_changes.knobStyle = `background-color: ${/*valueAsRGBA*/ ctx[17]}`;
			if (dirty[0] & /*valueAsRGBAFullyTransparent, valueAsRGBAFullyOpaque*/ 786432) slider_changes.trackStyle = `background-image: linear-gradient(to right, ${/*valueAsRGBAFullyTransparent*/ ctx[18]}, ${/*valueAsRGBAFullyOpaque*/ ctx[19]})`;
			if (dirty[0] & /*opacity*/ 65536) slider_changes.value = /*opacity*/ ctx[16];
			slider.$set(slider_changes);
		},
		i(local) {
			if (current) return;
			transition_in(slider.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(slider.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(slider, detaching);
		}
	};
}

// (359:8) {#if enablePresets}
function create_if_block$9(ctx) {
	let radiogroup;
	let current;

	radiogroup = new RadioGroup({
			props: {
				label: "Presets",
				class: arrayJoin([
					"DokaColorPresets",
					/*hidePresetLabel*/ ctx[9]
					? "DokaColorPresetsGrid"
					: "DokaColorPresetsList"
				]),
				hideLabel: false,
				name: /*name*/ ctx[2],
				value: /*value*/ ctx[5],
				optionGroupClass: "DokaDropdownOptionGroup",
				optionClass: "DokaDropdownOption",
				options: /*options*/ ctx[3].map(/*func*/ ctx[31]),
				selectedIndex: /*selectedIndex*/ ctx[4],
				optionMapper: /*optionMapper*/ ctx[7],
				optionLabelClass: /*optionLabelClass*/ ctx[6],
				onchange: /*func_1*/ ctx[32],
				$$slots: {
					default: [
						create_default_slot_1$8,
						({ option }) => ({ 45: option }),
						({ option }) => [0, option ? 16384 : 0]
					],
					option: [
						create_option_slot$3,
						({ option }) => ({ 45: option }),
						({ option }) => [0, option ? 16384 : 0]
					],
					group: [
						create_group_slot$1,
						({ option }) => ({ 45: option }),
						({ option }) => [0, option ? 16384 : 0]
					]
				},
				$$scope: { ctx }
			}
		});

	return {
		c() {
			create_component(radiogroup.$$.fragment);
		},
		m(target, anchor) {
			mount_component(radiogroup, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const radiogroup_changes = {};

			if (dirty[0] & /*hidePresetLabel*/ 512) radiogroup_changes.class = arrayJoin([
				"DokaColorPresets",
				/*hidePresetLabel*/ ctx[9]
				? "DokaColorPresetsGrid"
				: "DokaColorPresetsList"
			]);

			if (dirty[0] & /*name*/ 4) radiogroup_changes.name = /*name*/ ctx[2];
			if (dirty[0] & /*value*/ 32) radiogroup_changes.value = /*value*/ ctx[5];
			if (dirty[0] & /*options, locale*/ 1032) radiogroup_changes.options = /*options*/ ctx[3].map(/*func*/ ctx[31]);
			if (dirty[0] & /*selectedIndex*/ 16) radiogroup_changes.selectedIndex = /*selectedIndex*/ ctx[4];
			if (dirty[0] & /*optionMapper*/ 128) radiogroup_changes.optionMapper = /*optionMapper*/ ctx[7];
			if (dirty[0] & /*optionLabelClass*/ 64) radiogroup_changes.optionLabelClass = /*optionLabelClass*/ ctx[6];

			if (dirty[0] & /*hidePresetLabel*/ 512 | dirty[1] & /*$$scope, option*/ 49152) {
				radiogroup_changes.$$scope = { dirty, ctx };
			}

			radiogroup.$set(radiogroup_changes);
		},
		i(local) {
			if (current) return;
			transition_in(radiogroup.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(radiogroup.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(radiogroup, detaching);
		}
	};
}

// (375:12) <span slot="group">
function create_group_slot$1(ctx) {
	let span;
	let t_value = /*option*/ ctx[45].label + "";
	let t;

	return {
		c() {
			span = element("span");
			t = text(t_value);
			attr(span, "slot", "group");
		},
		m(target, anchor) {
			insert(target, span, anchor);
			append(span, t);
		},
		p(ctx, dirty) {
			if (dirty[1] & /*option*/ 16384 && t_value !== (t_value = /*option*/ ctx[45].label + "")) set_data(t, t_value);
		},
		d(detaching) {
			if (detaching) detach(span);
		}
	};
}

// (380:16) {#if !hidePresetLabel}
function create_if_block_1$b(ctx) {
	let span;
	let t_value = /*option*/ ctx[45].label + "";
	let t;

	return {
		c() {
			span = element("span");
			t = text(t_value);
			attr(span, "class", "DokaButtonLabel");
		},
		m(target, anchor) {
			insert(target, span, anchor);
			append(span, t);
		},
		p(ctx, dirty) {
			if (dirty[1] & /*option*/ 16384 && t_value !== (t_value = /*option*/ ctx[45].label + "")) set_data(t, t_value);
		},
		d(detaching) {
			if (detaching) detach(span);
		}
	};
}

// (378:12) <span slot="option">
function create_option_slot$3(ctx) {
	let span;
	let colorpreview;
	let t;
	let current;

	colorpreview = new ColorPreview({
			props: {
				title: /*option*/ ctx[45].label,
				color: /*option*/ ctx[45].value
			}
		});

	let if_block = !/*hidePresetLabel*/ ctx[9] && create_if_block_1$b(ctx);

	return {
		c() {
			span = element("span");
			create_component(colorpreview.$$.fragment);
			t = space();
			if (if_block) if_block.c();
			attr(span, "slot", "option");
		},
		m(target, anchor) {
			insert(target, span, anchor);
			mount_component(colorpreview, span, null);
			append(span, t);
			if (if_block) if_block.m(span, null);
			current = true;
		},
		p(ctx, dirty) {
			const colorpreview_changes = {};
			if (dirty[1] & /*option*/ 16384) colorpreview_changes.title = /*option*/ ctx[45].label;
			if (dirty[1] & /*option*/ 16384) colorpreview_changes.color = /*option*/ ctx[45].value;
			colorpreview.$set(colorpreview_changes);

			if (!/*hidePresetLabel*/ ctx[9]) {
				if (if_block) {
					if_block.p(ctx, dirty);
				} else {
					if_block = create_if_block_1$b(ctx);
					if_block.c();
					if_block.m(span, null);
				}
			} else if (if_block) {
				if_block.d(1);
				if_block = null;
			}
		},
		i(local) {
			if (current) return;
			transition_in(colorpreview.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(colorpreview.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(span);
			destroy_component(colorpreview);
			if (if_block) if_block.d();
		}
	};
}

// (361:8) <RadioGroup              label="Presets"             class={arrayJoin(['DokaColorPresets', hidePresetLabel ? 'DokaColorPresetsGrid' : 'DokaColorPresetsList'])}             hideLabel={false}             {name}             {value}             optionGroupClass="DokaDropdownOptionGroup"             optionClass="DokaDropdownOption"             options={options.map(([color, label]) => [color, isFunction(label) ? label(locale) : label])}             {selectedIndex}             {optionMapper}             {optionLabelClass}             onchange={(detail) => updateColorArray(detail.value)}             let:option>
function create_default_slot_1$8(ctx) {
	let t;

	return {
		c() {
			t = space();
		},
		m(target, anchor) {
			insert(target, t, anchor);
		},
		p: noop$1,
		i: noop$1,
		o: noop$1,
		d(detaching) {
			if (detaching) detach(t);
		}
	};
}

// (323:4) <div slot="details">
function create_details_slot$1(ctx) {
	let div;
	let t;
	let current;
	let if_block0 = /*enablePicker*/ ctx[14] && create_if_block_2$8(ctx);
	let if_block1 = /*enablePresets*/ ctx[13] && create_if_block$9(ctx);

	return {
		c() {
			div = element("div");
			if (if_block0) if_block0.c();
			t = space();
			if (if_block1) if_block1.c();
			attr(div, "slot", "details");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			if (if_block0) if_block0.m(div, null);
			append(div, t);
			if (if_block1) if_block1.m(div, null);
			current = true;
		},
		p(ctx, dirty) {
			if (/*enablePicker*/ ctx[14]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty[0] & /*enablePicker*/ 16384) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_2$8(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(div, t);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (/*enablePresets*/ ctx[13]) {
				if (if_block1) {
					if_block1.p(ctx, dirty);

					if (dirty[0] & /*enablePresets*/ 8192) {
						transition_in(if_block1, 1);
					}
				} else {
					if_block1 = create_if_block$9(ctx);
					if_block1.c();
					transition_in(if_block1, 1);
					if_block1.m(div, null);
				}
			} else if (if_block1) {
				group_outros();

				transition_out(if_block1, 1, 1, () => {
					if_block1 = null;
				});

				check_outros();
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);
			transition_in(if_block1);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			transition_out(if_block1);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			if (if_block0) if_block0.d();
			if (if_block1) if_block1.d();
		}
	};
}

// (312:0) <Details {...detailPanel} class={arrayJoin(['DokaColorPicker', klass])} toggleClass="DokaColorPickerToggle">
function create_default_slot$8(ctx) {
	let t;

	return {
		c() {
			t = space();
		},
		m(target, anchor) {
			insert(target, t, anchor);
		},
		p: noop$1,
		i: noop$1,
		o: noop$1,
		d(detaching) {
			if (detaching) detach(t);
		}
	};
}

function create_fragment$C(ctx) {
	let details;
	let current;

	const details_spread_levels = [
		/*detailPanel*/ ctx[11],
		{
			class: arrayJoin(["DokaColorPicker", /*klass*/ ctx[0]])
		},
		{ toggleClass: "DokaColorPickerToggle" }
	];

	let details_props = {
		$$slots: {
			default: [create_default_slot$8],
			details: [create_details_slot$1],
			label: [create_label_slot$1]
		},
		$$scope: { ctx }
	};

	for (let i = 0; i < details_spread_levels.length; i += 1) {
		details_props = assign(details_props, details_spread_levels[i]);
	}

	details = new Details({ props: details_props });

	return {
		c() {
			create_component(details.$$.fragment);
		},
		m(target, anchor) {
			mount_component(details, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const details_changes = (dirty[0] & /*detailPanel, klass*/ 2049)
			? get_spread_update(details_spread_levels, [
					dirty[0] & /*detailPanel*/ 2048 && get_spread_object(/*detailPanel*/ ctx[11]),
					dirty[0] & /*klass*/ 1 && {
						class: arrayJoin(["DokaColorPicker", /*klass*/ ctx[0]])
					},
					details_spread_levels[2]
				])
			: {};

			if (dirty[0] & /*hidePresetLabel, name, value, options, locale, selectedIndex, optionMapper, optionLabelClass, enablePresets, valueAsRGBA, valueAsRGBAFullyTransparent, valueAsRGBAFullyOpaque, opacity, enableOpacity, valueAsRGBAFullySaturated, hue, input, sx, sy, enablePicker, label, title*/ 16775166 | dirty[1] & /*$$scope*/ 32768) {
				details_changes.$$scope = { dirty, ctx };
			}

			details.$set(details_changes);
		},
		i(local) {
			if (current) return;
			transition_in(details.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(details.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(details, detaching);
		}
	};
}

function instance$C($$self, $$props, $$invalidate) {
	let { class: klass = undefined } = $$props;
	let { label = undefined } = $$props;
	let { name = undefined } = $$props;
	let { options = [] } = $$props;
	let { selectedIndex = -1 } = $$props;
	let { value = undefined } = $$props;
	let { optionLabelClass = undefined } = $$props;
	let { optionMapper = undefined } = $$props;
	let { onchange = undefined } = $$props;
	let { title = undefined } = $$props;
	let { hidePresetLabel = true } = $$props;
	let { locale = undefined } = $$props;
	let { detailPanel = {} } = $$props;
	let { enableOpacity = true } = $$props;
	let { enablePresets = true } = $$props;
	let { enablePicker = true } = $$props;

	// inputs
	let hue;

	let saturation;
	let brightness;
	let opacity;

	// shortcuts
	let valueAsRGB;

	let valueAsRGBA;
	let valueAsRGBAFullyTransparent;
	let valueAsRGBAFullyOpaque;
	let valueAsRGBAFullySaturated;

	const updateProps = (color, syncControls) => {
		valueAsRGB = [color[0], color[1], color[2]];

		if (syncControls) {
			let valueAsHSV = RGBToHSV(...valueAsRGB);
			$$invalidate(15, hue = valueAsHSV[0]);
			$$invalidate(33, saturation = valueAsHSV[1]);
			$$invalidate(34, brightness = valueAsHSV[2]);
			$$invalidate(16, opacity = isNumber(color[3]) ? color[3] : 1);
		}

		$$invalidate(17, valueAsRGBA = colorArrayToRGBA(color));
		$$invalidate(18, valueAsRGBAFullyTransparent = colorArrayToRGBA([...valueAsRGB, 0]));
		$$invalidate(19, valueAsRGBAFullyOpaque = colorArrayToRGBA([...valueAsRGB, 1]));
		$$invalidate(20, valueAsRGBAFullySaturated = colorArrayToRGBA(HSVToRGB(hue, 1, 1)));
	};

	value && updateProps(value, true);

	const update = () => {
		const rgb = HSVToRGB(hue, saturation, brightness);
		const rgba = [...rgb, opacity];
		updateProps(rgba);
		onchange(rgba);
	};

	const updateColorArray = arr => {
		const rgba = arr.length === 3 ? [...arr, 1] : arr;
		updateProps(rgba, true);
		onchange(rgba);
	};

	const updateHue = h => {
		$$invalidate(15, hue = h);
		if (opacity === 0) $$invalidate(16, opacity = 1);
		update();
	};

	const updateSaturationAndBrightness = (s, b) => {
		$$invalidate(33, saturation = s);
		$$invalidate(34, brightness = b);
		if (opacity === 0) $$invalidate(16, opacity = 1);
		update();
	};

	const updateOpacity = o => {
		$$invalidate(16, opacity = o);
		update();
	};

	const setValueByOffset = (offset, inputSize) => {
		const x = clamp(offset.x / inputSize.width, 0, 1);
		const y = clamp(offset.y / inputSize.height, 0, 1);
		updateSaturationAndBrightness(x, 1 - y);
	};

	let input;
	let inputSize;
	let inputOffset;
	let inputPageOffset;

	const handlePointerDown = e => {
		e.stopPropagation();
		inputSize = sizeCreate(input.offsetWidth, input.offsetHeight);
		inputOffset = vectorCreateFromPointerEventOffset(e);
		inputPageOffset = vectorCreateFromPointerEvent(e);
		setValueByOffset(inputOffset, inputSize);
		document.documentElement.addEventListener("pointermove", handlePointerMove);
		document.documentElement.addEventListener("pointerup", handlePointerUp);
	};

	const handlePointerMove = e => {
		const d = vectorSubtract(vectorCreateFromPointerEvent(e), inputPageOffset);
		setValueByOffset(vectorAdd(vectorClone(inputOffset), d), inputSize);
	};

	const handlePointerUp = e => {
		inputSize = undefined;
		document.documentElement.removeEventListener("pointermove", handlePointerMove);
		document.documentElement.removeEventListener("pointerup", handlePointerUp);
	};

	const handleNudge = e => {
		inputSize = sizeCreate(input.offsetWidth, input.offsetHeight);
		const x = sx / 100 * inputSize.width;
		const y = sy / 100 * inputSize.height;
		setValueByOffset({ x: x + e.detail.x, y: y + e.detail.y }, inputSize);
	};

	function div2_binding($$value) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			input = $$value;
			$$invalidate(21, input);
		});
	}

	const func = ([color, label]) => [color, isFunction(label) ? label(locale) : label];
	const func_1 = detail => updateColorArray(detail.value);

	$$self.$$set = $$props => {
		if ("class" in $$props) $$invalidate(0, klass = $$props.class);
		if ("label" in $$props) $$invalidate(1, label = $$props.label);
		if ("name" in $$props) $$invalidate(2, name = $$props.name);
		if ("options" in $$props) $$invalidate(3, options = $$props.options);
		if ("selectedIndex" in $$props) $$invalidate(4, selectedIndex = $$props.selectedIndex);
		if ("value" in $$props) $$invalidate(5, value = $$props.value);
		if ("optionLabelClass" in $$props) $$invalidate(6, optionLabelClass = $$props.optionLabelClass);
		if ("optionMapper" in $$props) $$invalidate(7, optionMapper = $$props.optionMapper);
		if ("onchange" in $$props) $$invalidate(29, onchange = $$props.onchange);
		if ("title" in $$props) $$invalidate(8, title = $$props.title);
		if ("hidePresetLabel" in $$props) $$invalidate(9, hidePresetLabel = $$props.hidePresetLabel);
		if ("locale" in $$props) $$invalidate(10, locale = $$props.locale);
		if ("detailPanel" in $$props) $$invalidate(11, detailPanel = $$props.detailPanel);
		if ("enableOpacity" in $$props) $$invalidate(12, enableOpacity = $$props.enableOpacity);
		if ("enablePresets" in $$props) $$invalidate(13, enablePresets = $$props.enablePresets);
		if ("enablePicker" in $$props) $$invalidate(14, enablePicker = $$props.enablePicker);
	};

	let sx;
	let sy;

	$$self.$$.update = () => {
		if ($$self.$$.dirty[1] & /*saturation*/ 4) {
			 $$invalidate(22, sx = saturation * 100);
		}

		if ($$self.$$.dirty[1] & /*brightness*/ 8) {
			 $$invalidate(23, sy = 100 - brightness * 100);
		}
	};

	return [
		klass,
		label,
		name,
		options,
		selectedIndex,
		value,
		optionLabelClass,
		optionMapper,
		title,
		hidePresetLabel,
		locale,
		detailPanel,
		enableOpacity,
		enablePresets,
		enablePicker,
		hue,
		opacity,
		valueAsRGBA,
		valueAsRGBAFullyTransparent,
		valueAsRGBAFullyOpaque,
		valueAsRGBAFullySaturated,
		input,
		sx,
		sy,
		updateColorArray,
		updateHue,
		updateOpacity,
		handlePointerDown,
		handleNudge,
		onchange,
		div2_binding,
		func,
		func_1
	];
}

class ColorPicker extends SvelteComponent {
	constructor(options) {
		super();

		init(
			this,
			options,
			instance$C,
			create_fragment$C,
			safe_not_equal,
			{
				class: 0,
				label: 1,
				name: 2,
				options: 3,
				selectedIndex: 4,
				value: 5,
				optionLabelClass: 6,
				optionMapper: 7,
				onchange: 29,
				title: 8,
				hidePresetLabel: 9,
				locale: 10,
				detailPanel: 11,
				enableOpacity: 12,
				enablePresets: 13,
				enablePicker: 14
			},
			[-1, -1]
		);
	}
}

// @ts-ignore
// tools
const toolSharpie = [
    {
        points: [],
        disableErase: false,
        strokeWidth: '0.5%',
        strokeColor: [1, 0.2549, 0.2118],
    },
    {
        position: 'relative',
    },
];
const toolRectangle = [
    {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        strokeColor: [1, 1, 1, 0],
        backgroundColor: [1, 0.2549, 0.2118],
    },
    {
        position: 'relative',
    },
];
const toolEllipse = [
    {
        x: 0,
        y: 0,
        rx: 0,
        ry: 0,
        strokeColor: [1, 1, 1, 0],
        backgroundColor: [1, 0.2549, 0.2118],
    },
    {
        position: 'relative',
    },
];
const toolLine = [
    {
        // type
        x1: 0,
        y1: 0,
        x2: 0,
        y2: 0,
        strokeColor: [1, 0.2549, 0.2118],
        strokeWidth: '1%',
        disableErase: false,
    },
    {
        position: 'relative',
    },
];
const toolArrow = [
    {
        // type
        x1: 0,
        y1: 0,
        x2: 0,
        y2: 0,
        strokeColor: [1, 0.2549, 0.2118],
        strokeWidth: '1%',
        lineEnd: 'arrow-solid',
        disableErase: false,
    },
    {
        position: 'relative',
    },
];
const toolText = [
    {
        x: 0,
        y: 0,
        color: [1, 0.2549, 0.2118],
        fontSize: '2%',
        text: 'Text',
    },
    {
        position: 'relative',
    },
];
// styles
const colors = [
    [[1, 1, 1, 0], (locale) => locale.shapeTitleColorTransparent],
    [[1, 1, 1], (locale) => locale.shapeTitleColorWhite],
    [[0.8667, 0.8667, 0.8667], (locale) => locale.shapeTitleColorSilver],
    [[0.6667, 0.6667, 0.6667], (locale) => locale.shapeTitleColorGray],
    [[0, 0, 0], (locale) => locale.shapeTitleColorBlack],
    [[0, 0.1216, 0.2471], (locale) => locale.shapeTitleColorNavy],
    [[0, 0.4549, 0.851], (locale) => locale.shapeTitleColorBlue],
    [[0.498, 0.8588, 1], (locale) => locale.shapeTitleColorAqua],
    [[0.2235, 0.8, 0.8], (locale) => locale.shapeTitleColorTeal],
    [[0.2392, 0.6, 0.4392], (locale) => locale.shapeTitleColorOlive],
    [[0.1804, 0.8, 0.251], (locale) => locale.shapeTitleColorGreen],
    [[1, 0.8627, 0], (locale) => locale.shapeTitleColorYellow],
    [[1, 0.5216, 0.1059], (locale) => locale.shapeTitleColorOrange],
    [[1, 0.2549, 0.2118], (locale) => locale.shapeTitleColorRed],
    [[0.5216, 0.0784, 0.2941], (locale) => locale.shapeTitleColorMaroon],
    [[0.9412, 0.0706, 0.7451], (locale) => locale.shapeTitleColorFuchsia],
    [[0.6941, 0.051, 0.7882], (locale) => locale.shapeTitleColorPurple],
];
const fontSizesRelative = [
    ['2%', (locale) => locale.labelSizeExtraSmall],
    ['4%', (locale) => locale.labelSizeSmall],
    ['8%', (locale) => locale.labelSizeMediumSmall],
    ['10%', (locale) => locale.labelSizeMedium],
    ['15%', (locale) => locale.labelSizeMediumLarge],
    ['20%', (locale) => locale.labelSizeLarge],
    ['25%', (locale) => locale.labelSizeExtraLarge],
];
const strokeWidthsRelative = [
    ['0.25%', (locale) => locale.labelSizeExtraSmall],
    ['0.5%', (locale) => locale.labelSizeSmall],
    ['1%', (locale) => locale.labelSizeMediumSmall],
    ['1.75%', (locale) => locale.labelSizeMedium],
    ['2.5%', (locale) => locale.labelSizeMediumLarge],
    ['3.5%', (locale) => locale.labelSizeLarge],
    ['5%', (locale) => locale.labelSizeExtraLarge],
];
const fontFamilies = [
    [`Helvetica, Arial, Verdana, 'Droid Sans', sans-serif`, 'Sans Serif'],
    [`'Arial Black', 'Avenir-Black', 'Arial Bold'`, 'Black'],
    [`'Arial Narrow', 'Futura-CondensedMedium'`, 'Narrow'],
    [`'Trebuchet MS'`, 'Humanist'],
    [`Georgia, 'Avenir-Black', 'Times New Roman', 'Droid Serif', serif`, 'Serif'],
    [`Palatino`, 'Old-Style'],
    [`'Times New Roman', 'TimesNewRomanPSMT'`, 'Transitional'],
    [`Menlo, Monaco, 'Lucida Console', monospace`, 'Monospaced'],
    [`'Courier New', monospace`, 'Slab Serif'],
];
const lineStyles = [
    [
        'bar',
        (locale) => locale.shapeTitleLineDecorationBar,
        {
            icon: '<g stroke="currentColor" stroke-linecap="round" stroke-width=".125em"><path d="M5,12 H16"/><path d="M16,8 V16"/></g>',
        },
    ],
    [
        'arrow',
        (locale) => locale.shapeTitleLineDecorationArrow,
        {
            icon: `<g stroke="currentColor" stroke-linecap="round" stroke-width=".125em">
            <path d="M5,12 H16 M13,7 l6,5 l-6,5" fill="none"/>
        </g>`,
        },
    ],
    [
        'arrow-solid',
        (locale) => locale.shapeTitleLineDecorationArrowSolid,
        {
            icon: `<g stroke="currentColor" stroke-linecap="round" stroke-width=".125em">
            <path d="M5,12 H16"/>
            <path d="M13,7 l6,5 l-6,5z" fill="currentColor"/>
        </g>`,
        },
    ],
    [
        'circle',
        (locale) => locale.shapeTitleLineDecorationCircle,
        {
            icon: '<g stroke="currentColor" stroke-linecap="round"><path stroke-width=".125em" d="M5,12 H12"/><circle fill="none" stroke-width=".125em" cx="16" cy="12" r="4"/></g>',
        },
    ],
    [
        'circle-solid',
        (locale) => locale.shapeTitleLineDecorationCircleSolid,
        {
            icon: '<g stroke="currentColor" stroke-linecap="round"><path stroke-width=".125em" d="M5,12 H12"/><circle fill="currentColor" cx="16" cy="12" r="4"/></g>',
        },
    ],
    [
        'square',
        (locale) => locale.shapeTitleLineDecorationSquare,
        {
            icon: '<g stroke="currentColor" stroke-linecap="round"><path stroke-width=".125em" d="M5,12 H12"/><rect fill="none" stroke-width=".125em" x="12" y="8" width="8" height="8"/></g>',
        },
    ],
    [
        'square-solid',
        (locale) => locale.shapeTitleLineDecorationSquareSolid,
        {
            icon: '<g stroke="currentColor" stroke-linecap="round"><path stroke-width=".125em" d="M5,12 H12"/><rect fill="currentColor" x="12" y="8" width="8" height="8"/></g>',
        },
    ],
];
const shapeControlDefaults = {
    // shape background
    backgroundColor: [
        ColorPicker,
        {
            title: (locale) => locale.shapeTitleBackgroundColor,
            options: colors,
        },
    ],
    // line/outline
    strokeColor: [
        ColorPicker,
        {
            title: (locale) => locale.shapeTitleStrokeColor,
            class: 'DokaColorPickerStroke',
            options: colors,
            onchange: (value, shape) => {
                // already has outline
                if (shape.strokeWidth > 0 && shape.strokeWidth !== '0%')
                    return;
                // set outline to first stroke width
                shape.strokeWidth = strokeWidthsRelative[2][0];
            },
        },
    ],
    strokeWidth: [
        Dropdown,
        {
            title: (locale) => locale.shapeTitleStrokeWidth,
            options: (shape) => {
                if (hasProp(shape, 'backgroundColor')) {
                    return [
                        [undefined, (locale) => locale.shapeLabelStrokeNone],
                        ...strokeWidthsRelative,
                    ];
                }
                return [...strokeWidthsRelative];
            },
        },
    ],
    // line
    lineStart: [
        Dropdown,
        {
            title: (locale) => locale.shapeTitleLineStart,
            options: [
                [
                    undefined,
                    (locale) => locale.labelNone,
                    {
                        icon: `<g stroke="currentColor" stroke-linecap="round" stroke-width=".125em">
                        <path d="M5,12 H14"/>
                    </g>`,
                    },
                ],
                ...lineStyles,
            ],
            optionIconStyle: `transform: scaleX(-1)`,
        },
    ],
    lineEnd: [
        Dropdown,
        {
            title: (locale) => locale.shapeTitleLineEnd,
            options: [
                [
                    undefined,
                    (locale) => locale.labelNone,
                    {
                        icon: `<g stroke="currentColor" stroke-linecap="round" stroke-width=".125em">
                        <path d="M5,12 H14"/>
                    </g>`,
                    },
                ],
                ...lineStyles,
            ],
        },
    ],
    // text
    color: [
        ColorPicker,
        {
            title: (locale) => locale.shapeTitleTextColor,
            options: colors,
        },
    ],
    fontFamily: [
        Dropdown,
        {
            title: (locale) => locale.shapeTitleFontFamily,
            optionLabelStyle: (value) => `font-family: ${value}`,
            options: [[undefined, (locale) => locale.labelDefault], ...fontFamilies],
            optionFilter: (fontFamilyOption) => {
                // don't filter if not in browser context
                if (!isBrowser())
                    return true;
                // get font stack
                const [stack] = fontFamilyOption;
                // allow if undefined
                if (!stack)
                    return true;
                // filter out fonts that aren't available
                return stack
                    .split(',')
                    .map((name) => name.trim())
                    .some((name) => {
                    // @ts-ignore
                    return document.fonts.check(`16px ${name}`);
                });
            },
        },
    ],
    /* temporary disabled
    lineHeight: [
        Dropdown,
        {
            title: (locale) => locale.shapeTitleLineHeight,
            options: [[undefined, (locale) => locale.labelAuto], ...fontSizes],
        },
    ],
    */
    fontSize: [
        Dropdown,
        {
            title: (locale) => locale.shapeTitleFontSize,
            options: [[undefined, (locale) => locale.labelDefault], ...fontSizesRelative],
        },
    ],
    textAlign: [
        RadioGroup,
        {
            title: (locale) => locale.shapeTitleTextAlign,
            options: [
                [
                    'left',
                    (locale) => locale.shapeTitleTextAlignLeft,
                    {
                        hideLabel: true,
                        icon: (locale) => locale.shapeIconTextAlignLeft,
                    },
                ],
                [
                    'center',
                    (locale) => locale.shapeTitleTextAlignCenter,
                    {
                        hideLabel: true,
                        icon: (locale) => locale.shapeIconTextAlignCenter,
                    },
                ],
                [
                    'right',
                    (locale) => locale.shapeTitleTextAlignRight,
                    {
                        hideLabel: true,
                        icon: (locale) => locale.shapeIconTextAlignRight,
                    },
                ],
            ],
        },
    ],
};
const toolShapeDefaults = {
    sharpie: toolSharpie,
    line: toolLine,
    arrow: toolArrow,
    rectangle: toolRectangle,
    ellipse: toolEllipse,
    text: toolText,
};
const toolDefaults = [
    [
        'sharpie',
        (locale) => locale.shapeLabelToolSharpie,
        {
            icon: (locale) => locale.shapeIconToolSharpie,
        },
    ],
    [
        'eraser',
        (locale) => locale.shapeLabelToolEraser,
        {
            icon: (locale) => locale.shapeIconToolEraser,
        },
    ],
    [
        'rectangle',
        (locale) => locale.shapeLabelToolRectangle,
        {
            icon: (locale) => locale.shapeIconToolRectangle,
        },
    ],
    [
        'ellipse',
        (locale) => locale.shapeLabelToolEllipse,
        {
            icon: (locale) => locale.shapeIconToolEllipse,
        },
    ],
    [
        'arrow',
        (locale) => locale.shapeLabelToolArrow,
        {
            icon: (locale) => locale.shapeIconToolArrow,
        },
    ],
    [
        'line',
        (locale) => locale.shapeLabelToolLine,
        {
            icon: (locale) => locale.shapeIconToolLine,
        },
    ],
    [
        'text',
        (locale) => locale.shapeLabelToolText,
        {
            icon: (locale) => locale.shapeIconToolText,
        },
    ],
];

var defaults$4 = {
    annotateTools: toolDefaults,
    annotateToolShapes: toolShapeDefaults,
    annotateShapeControls: shapeControlDefaults,
    annotateEnableSelectImagePreset: false,
};

var defaults$5 = {
    decorateTools: toolDefaults,
    decorateToolShapes: toolShapeDefaults,
    decorateShapeControls: shapeControlDefaults,
    decorateEnableSelectImagePreset: false,
};

var defaults$6 = {
    stickerEnableSelectImage: true,
    stickerShapeControls: shapeControlDefaults,
};

var en_gb$1 = {
    cropLabel: 'Crop',
    cropIcon: '<g stroke-width=".125em" stroke="currentColor" fill="none"><path d="M23 17H9a2 2 0 0 1-2-2v-5m0-3V1"/><path d="M1 7h14a2 2 0 0 1 2 2v7m0 4v3"/></g>',
    cropIconButtonRecenter: `<path stroke="currentColor" fill="none" stroke-width="2" stroke-linejoin="bevel" d="M1.5 7.5v-6h6M1.5 16.5v6h6M22.5 16.5v6h-6M22.5 7.5v-6h-6"/><circle cx="12" cy="12" r="3.5" fill="currentColor" stroke="none"/>`,
    cropIconButtonRotateLeft: '<g stroke="none" fill="currentColor"><path fill="none" d="M-1-1h582v402H-1z"/><rect x="3" rx="1" height="12" width="12" y="9"/><path d="M15 5h-1a5 5 0 015 5 1 1 0 002 0 7 7 0 00-7-7h-1.374l.747-.747A1 1 0 0011.958.84L9.603 3.194a1 1 0 000 1.415l2.355 2.355a1 1 0 001.415-1.414l-.55-.55H15z"/></g>',
    cropIconButtonRotateRight: '<g stroke="none" fill="currentColor"><path fill="none" d="M-1-1h582v402H-1z"/><path d="M11.177 5H10a5 5 0 00-5 5 1 1 0 01-2 0 7 7 0 017-7h1.374l-.747-.747A1 1 0 0112.042.84l2.355 2.355a1 1 0 010 1.415l-2.355 2.354a1 1 0 01-1.415-1.414l.55-.55z"/><rect rx="1" height="12" width="12" y="9" x="9"/></g>',
    cropIconButtonFlipVertical: '<g stroke="none" fill="currentColor"><path d="M19.993 12.143H7a1 1 0 0 1-1-1V5.994a1 1 0 0 1 1.368-.93l12.993 5.15a1 1 0 0 1-.368 1.93z"/><path d="M19.993 14a1 1 0 0 1 .368 1.93L7.368 21.078A1 1 0 0 1 6 20.148V15a1 1 0 0 1 1-1h12.993z" opacity=".6"/></g>',
    cropIconButtonFlipHorizontal: '<g stroke="none" fill="currentColor"><path d="M11.93 7.007V20a1 1 0 0 1-1 1H5.78a1 1 0 0 1-.93-1.368l5.15-12.993a1 1 0 0 1 1.929.368z"/><path d="M14 7.007V20a1 1 0 0 0 1 1h5.149a1 1 0 0 0 .93-1.368l-5.15-12.993A1 1 0 0 0 14 7.007z" opacity=".6"/></g>',
    cropLabelButtonRecenter: 'Recenter',
    cropLabelButtonRotateLeft: 'Rotate left',
    cropLabelButtonRotateRight: 'Rotate right',
    cropLabelButtonFlipHorizontal: 'Flip horizontal',
    cropLabelButtonFlipVertical: 'Flip vertical',
    cropLabelSelectPreset: 'Crop shape',
    cropLabelCropBoundary: 'Crop boundary',
    cropLabelCropBoundaryEdge: 'Edge of image',
    cropLabelCropBoundaryNone: 'None',
    cropLabelTabRotation: 'Rotation',
    cropLabelTabZoom: 'Zoom',
};

var en_gb$2 = {
    filterLabel: 'Filter',
    filterIcon: '<g stroke-width=".125em" stroke="currentColor" fill="none"><path d="M18.347 9.907a6.5 6.5 0 1 0-1.872 3.306M3.26 11.574a6.5 6.5 0 1 0 2.815-1.417"/><path d="M10.15 17.897A6.503 6.503 0 0 0 16.5 23a6.5 6.5 0 1 0-6.183-8.51"/></g>',
    filterLabelChrome: 'Chrome',
    filterLabelFade: 'Fade',
    filterLabelCold: 'Cold',
    filterLabelWarm: 'Warm',
    filterLabelPastel: 'Pastel',
    filterLabelMonoDefault: 'Mono',
    filterLabelMonoNoir: 'Noir',
    filterLabelMonoWash: 'Wash',
    filterLabelMonoStark: 'Stark',
    filterLabelSepiaDefault: 'Sepia',
    filterLabelSepiaBlues: 'Blues',
    filterLabelSepiaRust: 'Rust',
    filterLabelSepiaColor: 'Color',
};

var en_gb$3 = {
    finetuneLabel: 'Finetune',
    finetuneIcon: '<g stroke-width=".125em" stroke="currentColor" fill="none"><path d="M4 1v5.5m0 3.503V23M12 1v10.5m0 3.5v8M20 1v15.5m0 3.5v3M2 7h4M10 12h4M18 17h4"/></g>',
    finetuneLabelBrightness: 'Brightness',
    finetuneLabelContrast: 'Contrast',
    finetuneLabelSaturation: 'Saturation',
    finetuneLabelExposure: 'Exposure',
    finetuneLabelGamma: 'Gamma',
    finetuneLabelClarity: 'Clarity',
    finetuneLabelVignette: 'Vignette',
};

var en_gb$4 = {
    resizeLabel: 'Resize',
    resizeIcon: '<g stroke-width=".125em" stroke="currentColor" fill="none"><rect x="2" y="12" width="10" height="10" rx="2"/><path d="M4 11.5V4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-5.5"/><path d="M14 10l3.365-3.365M14 6h4v4"/></g>',
    resizeLabelFormCaption: 'Image output size',
    resizeLabelInputWidth: 'w',
    resizeTitleInputWidth: 'Width',
    resizeLabelInputHeight: 'h',
    resizeTitleInputHeight: 'Height',
    resizeTitleButtonMaintainAspectRatio: 'Maintain aspectratio',
    resizeIconButtonMaintainAspectRatio: (active, activeFraction) => `
        <defs>
            <mask id="mask1" x="0" y="0" width="24" height="24" >
                <rect x="0" y="0" width="24" height="10" fill="#fff" stroke="none"/>
            </mask>
        </defs>
        <g fill="none" fill-rule="evenodd">
            <g  mask="url(#mask1)">
                <path transform="translate(0 ${(activeFraction - 1) * 3})" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" d="M9.401 10.205v-.804a2.599 2.599 0 0 1 5.198 0V17"/>
            </g>
            <rect fill="currentColor" x="7" y="10" width="10" height="7" rx="1.5"/>
        </g>
    `,
};

var en_gb$5 = {
    decorateLabel: 'Decorate',
    decorateIcon: '<g fill="none" fill-rule="evenodd"><path stroke="currentColor" stroke-width=".125em" stroke-linecap="round" stroke-linejoin="round" d="M12 18.5l-6.466 3.4 1.235-7.2-5.23-5.1 7.228-1.05L12 2l3.233 6.55 7.229 1.05-5.231 5.1 1.235 7.2z"/></g>',
};

var en_gb$6 = {
    annotateLabel: 'Annotate',
    annotateIcon: '<g stroke-width=".125em" stroke="currentColor" fill="none"><path d="M17.086 2.914a2.828 2.828 0 1 1 4 4l-14.5 14.5-5.5 1.5 1.5-5.5 14.5-14.5z"/></g>',
};

var en_gb$7 = {
    stickerLabel: 'Sticker',
    stickerIcon: '<g fill="none" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor" stroke-width=".125em"><path d="M12 22c2.773 0 1.189-5.177 3-7 1.796-1.808 7-.25 7-3 0-5.523-4.477-10-10-10S2 6.477 2 12s4.477 10 10 10z"/><path d="M20 17c-3 3-5 5-8 5"/></g>',
};

/* Deprecated, remove in v8, use `createDefaultImageOrienter` instead */
const imageOrienter = {
    read: getImageOrientationFromFile,
    apply: orientImageData,
};

const createDefaultImageOrienter = () => ({
    read: getImageOrientationFromFile,
    apply: orientImageData,
});
const createEditor = createImageEditor;
const getEditorProps$1 = () => getEditorProps().concat(getEditorViewProps());

const processImage = (src, options) => {
    const { processImage } = createImageEditor();
    return processImage(src, options);
};

export { appendEditor, appendEditors, blobToFile, colorStringToColorArray, ShapeEditor as component_shape_editor_locale_en_gb, createDefaultImageOrienter, createDefaultImageReader, createDefaultImageWriter, createEditor, customElement as defineCustomElements, degToRad, dispatchEditorEvents, getEditorProps$1 as getEditorProps, imageOrienter, isModernBrowser as isSupported, legacyDataToImageState, en_gb as locale_en_gb, openEditor, overlayEditor, index$3 as plugin_annotate, defaults$4 as plugin_annotate_defaults, en_gb$6 as plugin_annotate_locale_en_gb, index as plugin_crop, defaults as plugin_crop_defaults, en_gb$1 as plugin_crop_locale_en_gb, index$4 as plugin_decorate, defaults$5 as plugin_decorate_defaults, en_gb$5 as plugin_decorate_locale_en_gb, index$1 as plugin_filter, defaults$2 as plugin_filter_defaults, en_gb$2 as plugin_filter_locale_en_gb, index$2 as plugin_finetune, defaults$1 as plugin_finetune_defaults, en_gb$3 as plugin_finetune_locale_en_gb, index$6 as plugin_resize, defaults$3 as plugin_resize_defaults, en_gb$4 as plugin_resize_locale_en_gb, index$5 as plugin_sticker, defaults$6 as plugin_sticker_defaults, en_gb$7 as plugin_sticker_locale_en_gb, processImage, setEditorViewPlugins as setPlugins };
